import React, { useState, useEffect, useRef, useCallback, useLayoutEffect} from 'react';
import ReactDOM from 'react-dom';
import {
  LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea,
  Tooltip, ResponsiveContainer, Legend, BarChart, Bar,
  ComposedChart, Area, CartesianGrid, Cell
} from 'recharts';
import { resolveMetric, colorFor } from './utils/chartConfig';
import { computeTrend, ckdEpiEgfr, egfrStability, evalMarkerClauses } from './drawerEngine';
import { privacyPack, US_STATES } from './privacyCopy';
import {
  setLocale, onLocaleChange, T, fmtDate, fmtDateNumeric, deIsoDatesLocale,
  fmtWeight, fmtWeightDelta, fmtHeight, weightUnit, weightInputToKg, fmtDateShort,
  displayMarker, displayRange, convertPanelsPayload, convertFlagsPayload,
  unitSystem, setUnitSystem, unitsToggleAvailable, isImperial,
} from './locale';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const AUTH_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : '';

// ── P0.2 (KAL-222) — SURFACE THE SERVER'S REASON, NOT JUST ITS STATUS CODE ──────────
// Every verb below threw `new Error("API " + status)` WITHOUT EVER READING THE BODY, so a
// server that had explained itself precisely was reduced to "API 403". Measured: the
// underwriting consent gate returns
//   403 {"error":"consent gate","detail":{"reason":"no insurance consent on record"}}
// and the report rendered "Report unavailable: API 403". The stronger string existed the
// whole time and the client discarded it.
//
// Reads the body once, prefers detail.reason (the specific cause), falls back to error, then
// to the status line. Never throws while building the message - a parse failure must not
// replace a real error with a different one.
async function _apiError(r) {
  let msg = '';
  try {
    const b = await r.json();
    msg = (b && b.detail && b.detail.reason) || (b && b.error) || (b && b.message) || '';
    if (msg && b && b.detail && b.detail.needs_reconsent) msg += ' (re-consent required)';
  } catch (e) { /* non-JSON body: fall through to the status line */ }
  const err = new Error(msg || `API ${r.status}`);
  err.status = r.status;          // KAL-322 — retry decisions need the code, not just the text
  return err;
}

// ── A7 / KAL-241: NOTHING WAITS FOREVER ──────────────────────────────────────────────
// `fetch` has NO timeout. A request that never resolves leaves a skeleton on screen
// indefinitely, which is exactly what NPL's Today screen did: `/api/today/v2/metrics` is fast
// in itself (median 264 ms measured) but was QUEUED behind a saturated single worker, and the
// client had no opinion about how long it was prepared to wait. The user sees three empty
// cards and no explanation, forever.
//
// This is KAL-232's lesson generalised. KAL-232 was a non-200 rendered as "logged out"; this is
// a PENDING request rendered as "loading". Both turn a server problem into a screen that lies
// about what is happening. A slow or failed request must produce a stated outcome.
//
// 20 s is deliberately generous — well past any healthy response, short enough that a person
// has not yet given up and reloaded.
const LOAD_TIMEOUT_MS = 20000;

function withTimeout(promise, ms = LOAD_TIMEOUT_MS, label = 'request') {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      const e = new Error(`Timed out after ${Math.round(ms / 1000)}s`);
      e.isTimeout = true;
      e.label = label;
      reject(e);
    }, ms);
    promise.then(v => { clearTimeout(t); resolve(v); },
                 e => { clearTimeout(t); reject(e); });
  });
}

// ── KAL-262 — THE TIMEOUT BELONGS IN THE HELPER, NOT AT 287 CALL SITES ───────────────────────
// KAL-241 added `withTimeout` to fix a hang, and applied it to THREE call sites. It never swept
// the class. Audited 23 Aug 2026 across frontend/src: **287 unbounded network awaits against 3
// bounded** - 179 through `api.*`, 63 through the ops helper, 45 raw `fetch`. Same shape as the
// FEATURES_GLOBAL sweep: one instance found, class left in place.
//
// WHAT AN UNBOUNDED AWAIT COSTS, measured on NPL 23 Aug: `runSync` hung on step 1 of 6 and the
// global sync bar sat at "refresh step 1 of 6 · 0%" for four minutes. Because the bar reports
// `busy` from that same lifecycle, and `busy` gates the click handler, the "Sync now" button
// silently ignored every press - the reported "unclickable, then acts when clicked".
//
// A REQUEST-BOUNDED ABORT, not just a rejected promise: `withTimeout` alone leaves the socket
// open, so a saturated single-worker gunicorn (NPL runs `-w 1 --threads 4`) keeps the thread.
// AbortController releases it, which is the difference between recovering and queueing.
//
// `upload` is DELIBERATELY EXEMPT and stays unbounded - see the note on it below.
const _abortableFetch = async (url, opts = {}, ms = LOAD_TIMEOUT_MS, label = 'request') => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...opts, signal: ctrl.signal });
  } catch (e) {
    if (e && e.name === 'AbortError') {
      const to = new Error(`Timed out after ${Math.round(ms / 1000)}s`);
      to.isTimeout = true;
      to.label = label;
      throw to;
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
};

// ── KAL-322 — RETRY INSTEAD OF A 20 s DEAD END ──────────────────────────────────────────────
// A timeout or a 503 (the heavy-endpoint gate, KAL-318) retries after 2 s, 5 s, 10 s. The caller
// gets onRetry(n) to say "Still loading, retrying" and every timeout is reported server-side
// (/api/client-error), so a slow load is a fact in error_events rather than a screenshot. The
// per-load trace (_loadTrace) feeds the load beacon (KAL-321).
const RETRY_DELAYS_MS = [2000, 5000, 10000];
// KAL-349 — ONE definition of "this failure is the server being busy, not the answer being no".
// A timeout or a 502/503/504 is TRANSIENT: the request never got an answer, so the surface must
// say so and try again rather than present a permanent failure. Measured on NPL 21 Sep 2026
// 06:28-06:31Z (Tim's Pixel 8, right after a Health-Connect sync): every request in that window
// ran to the client's 20 s abort - /api/active-minutes 30.0 s, /api/collect 32.3 s, and even the
// static /favicon_npl.png 33.4 s - while the same month request served in 0.19 s at 06:30:25Z.
const _isTransient = (e) => !!(e && (e.isTimeout || e.status === 503 || e.status === 502 || e.status === 504));
const _loadTrace = {
  api: [], retries: 0, timeouts: 0,
  reset() { this.api = []; this.retries = 0; this.timeouts = 0; },
  note(path, ms, status) { if (this.api.length < 24) this.api.push({ path: String(path).slice(0, 80), ms: Math.round(ms), status }); },
};
async function apiGetWithRetry(ep, { delays = RETRY_DELAYS_MS, onRetry, label } = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    const t0 = performance.now();
    try {
      const v = await api.get(ep);
      _loadTrace.note(ep, performance.now() - t0, 200);
      return v;
    } catch (e) {
      lastErr = e;
      const status = (e && e.status) || (e && e.isTimeout ? 'timeout' : 'error');
      _loadTrace.note(ep, performance.now() - t0, status);
      if (e && e.isTimeout) { _loadTrace.timeouts += 1; try { reportClientError(`timeout ${ep} (attempt ${attempt + 1})`, null, label || ep); } catch (_) {} }
      const retryable = _isTransient(e);
      if (!retryable || attempt === delays.length) throw e;
      _loadTrace.retries += 1;
      if (onRetry) { try { onRetry(attempt + 1); } catch (_) {} }
      await new Promise(r => setTimeout(r, delays[attempt]));
    }
  }
  throw lastErr;
}

// ── KAL-322 — LAST-KNOWN SUMMARIES IN IndexedDB (localStorage fallback) ───────────────────────
// Keyed per profile (window.__kalpaProfileKey, set on /auth/user) so a shared phone never shows
// one account's yesterday to another. Read on mount, written after every successful fetch.
const _kv = (() => {
  const DB = 'kalpa-cache', STORE = 'kv';
  let dbp = null;
  const open = () => {
    if (dbp) return dbp;
    dbp = new Promise((resolve, reject) => {
      try {
        if (typeof indexedDB === 'undefined') { reject(new Error('no indexedDB')); return; }
        const req = indexedDB.open(DB, 1);
        req.onupgradeneeded = () => { try { req.result.createObjectStore(STORE); } catch (_) {} };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error('idb open failed'));
      } catch (e) { reject(e); }
    });
    return dbp;
  };
  const ls = (op, k, v) => { try { if (op === 'get') { const s = localStorage.getItem('kv:' + k); return s ? JSON.parse(s) : null; } localStorage.setItem('kv:' + k, JSON.stringify(v)); return true; } catch (_) { return null; } };
  return {
    async get(k) { try { const db = await open(); return await new Promise((res, rej) => { const r = db.transaction(STORE).objectStore(STORE).get(k); r.onsuccess = () => res(r.result || null); r.onerror = () => rej(r.error); }); } catch (_) { return ls('get', k); } },
    async set(k, v) { try { const db = await open(); return await new Promise((res, rej) => { const tx = db.transaction(STORE, 'readwrite'); tx.objectStore(STORE).put(v, k); tx.oncomplete = () => res(true); tx.onerror = () => rej(tx.error); }); } catch (_) { return ls('set', k, v); } },
  };
})();
function _fmtAsOf(iso) {
  try { const d = new Date(iso); const now = new Date();
    const hm = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toDateString() === now.toDateString() ? hm : `${formatDate(iso.slice(0, 10))} ${hm}`;
  } catch (_) { return ''; }
}

// ── KAL-321 — LOAD BEACON: what this load cost, sent AFTER content is ready, never awaited ───
function sendLoadBeacon(extra) {
  try {
    const nav = performance.getEntriesByType('navigation')[0] || {};
    const res = performance.getEntriesByType('resource') || [];
    const main = res.find(r => /\/static\/js\/main\.[a-f0-9]+\.js/.test(r.name));
    const bundle = main ? main.name.split('/').pop() : ((document.querySelector('script[src*="/static/js/main."]') || {}).src || '').split('/').pop();
    const conn = (navigator.connection || {});
    const body = {
      screen: extra.screen || 'today',
      platform: isInNativeApp() ? ('webview-' + nativePlatform()) : detectPlatform(),   // KAL-326: never the UA inside a shell
      bundle, bundle_bytes: main ? Math.round(main.decodedBodySize ?? main.encodedBodySize ?? 0) : null,
      bundle_cached: main ? (main.transferSize === 0) : null,
      cold: main ? (main.transferSize > 0) : null,
      sw_state: (navigator.serviceWorker && navigator.serviceWorker.controller) ? navigator.serviceWorker.controller.state : 'none',
      ttfb_ms: nav.responseStart != null ? Math.round(nav.responseStart - (nav.requestStart ?? 0)) : null,
      index_bytes: nav.decodedBodySize || null,
      dcl_ms: nav.domContentLoadedEventEnd ? Math.round(nav.domContentLoadedEventEnd) : null,
      content_ready_ms: extra.content_ready_ms,
      first_api_ok_ms: (_loadTrace.api.find(a => a.status === 200) || {}).ms || null,
      api: _loadTrace.api, timeouts: extra.timeouts ?? 0, retries: extra.retries ?? 0,
      from_cache: !!extra.from_cache, network: conn.effectiveType || '',
    };
    fetch(`${API_BASE}/telemetry/load`, { method: 'POST', credentials: 'include', keepalive: true,
      headers: { 'Content-Type': 'application/json', ..._TZ_HEADER }, body: JSON.stringify(body) }).catch(() => {});
  } catch (_) { /* telemetry never breaks the screen */ }
}

// KAL-299 — the browser's IANA zone rides on every API call, exactly as the native app's
// does (X-Kalpa-Timezone, api.ts). The server records it as the user's device_tz and files
// their day boundary and sleep dates in it. Read once; never throws.
const _TZ_HEADER = (() => {
  try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; return tz ? { 'X-Kalpa-Timezone': tz } : {}; }
  catch (e) { return {}; }
})();
const api = {
  get: async (ep) => { const r = await _abortableFetch(`${API_BASE}${ep}`, {credentials:'include', headers: _TZ_HEADER}, LOAD_TIMEOUT_MS, `GET ${ep}`); if (!r.ok) throw await _apiError(r); return r.json(); },
  post: async (ep, data) => { const r = await _abortableFetch(`${API_BASE}${ep}`, { method:'POST', headers:{'Content-Type':'application/json', ..._TZ_HEADER}, body:JSON.stringify(data), credentials:'include' }, LOAD_TIMEOUT_MS, `POST ${ep}`); if (!r.ok) throw await _apiError(r); return r.json(); },
  put: async (ep, data) => { const r = await _abortableFetch(`${API_BASE}${ep}`, { method:'PUT', headers:{'Content-Type':'application/json', ..._TZ_HEADER}, body:JSON.stringify(data), credentials:'include' }, LOAD_TIMEOUT_MS, `PUT ${ep}`); if (!r.ok) throw await _apiError(r); return r.json(); },
  del: async (ep, data) => { const r = await _abortableFetch(`${API_BASE}${ep}`, { method:'DELETE', headers: data?{'Content-Type':'application/json'}:undefined, body: data?JSON.stringify(data):undefined, credentials:'include' }, LOAD_TIMEOUT_MS, `DELETE ${ep}`); if (!r.ok) throw await _apiError(r); return r.json(); },
  // ⚠ NO TIMEOUT, ON PURPOSE. This carries document and genetics uploads through the parse
  // pipeline, which legitimately runs 140-190s and has been measured at 470s (which is why NPL
  // alone runs gunicorn `--timeout 600`). A 20s bound here would abort real work that is
  // succeeding. Uploads report progress by their own UI; they are not the silent-hang class.
  upload: async (ep, fd) => { const r = await fetch(`${API_BASE}${ep}`, { method:'POST', body:fd, credentials:'include' }); if (!r.ok) throw await _apiError(r); return r.json(); }
};

// ── Native-app detection (the RN WebView bridge, NOT a UA sniff) ────────────────
function isInNativeApp() {
  return typeof window !== 'undefined' && !!(window.ReactNativeWebView && window.ReactNativeWebView.postMessage);
}
// KAL-326 — WHICH OS IS THIS WEBVIEW ON? NEVER THE USER-AGENT. Both native shells pin ONE fixed
// Android Chrome UA on iOS and Android alike (npl-kalpa-mobile App.tsx:2095, kalpa-mobile
// App.tsx:648), so /iPhone/.test(navigator.userAgent) is FALSE on an iPhone inside the app. That
// is how Benjamin's iPhone (build 13) was resolved as Android and handed the APK on 6 Sep 2026.
// WKWebView's custom UA changes navigator.userAgent ONLY: navigator.platform still says
// "iPhone"/"iPad", and window.webkit.messageHandlers exists only in WKWebView. Outside the app,
// detectPlatform() (UA + the iPadOS "Macintosh with touch" rule) is right and is kept.
function nativePlatform() {
  try {
    // KAL-328 — a shell that names its OS (window.__kalpaNativePlatform, injected before content
    // load by the native app from build 14 / versionCode 37) is authoritative; inference below is
    // the fallback for older shells and plain browsers.
    if (typeof window !== 'undefined' && (window.__kalpaNativePlatform === 'ios' || window.__kalpaNativePlatform === 'android')) return window.__kalpaNativePlatform;
    if (typeof navigator === 'undefined') return 'desktop';
    const plat = String(navigator.platform || '');
    const touch = (navigator.maxTouchPoints ?? 0) > 1;
    const wk = typeof window !== 'undefined' && !!(window.webkit && window.webkit.messageHandlers);
    if (/iPhone|iPad|iPod/i.test(plat) || (/Mac/i.test(plat) && touch) || wk) return 'ios';
    if (isInNativeApp()) return 'android';           // a shell that is not WebKit is the Android one
    const d = detectPlatform();
    return d === 'native' ? 'android' : d;
  } catch (_) { return 'desktop'; }
}
// The header the server reads FIRST (version_status.resolve_platform). Sent on the version-status
// call and as the `?platform=` hint on the APK link (an <a download> cannot send headers).
function platformHeader() {
  const p = nativePlatform();
  return (p === 'ios' || p === 'android') ? { 'X-Kalpa-Platform': p } : {};
}

// ── Client-error telemetry → /api/client-error (error_events) ───────────────────
// Fire-and-forget, deduped, NEVER throws. Makes web/WebView JS errors AND dropped
// feedback observable server-side (previously /api/client-error had no web caller).
const _clientErrSeen = new Set();
function reportClientError(message, stack, screen) {
  try {
    const msg = String(message || '').slice(0, 1000);
    if (!msg) return;
    const key = msg + '|' + String(screen || '');
    if (_clientErrSeen.has(key)) return;            // dedupe identical errors
    _clientErrSeen.add(key);
    if (_clientErrSeen.size > 50) _clientErrSeen.clear();  // bound memory
    fetch(`${API_BASE}/client-error`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', keepalive: true,
      body: JSON.stringify({
        message: msg,
        stack: String(stack || '').slice(0, 4000),
        screen: (typeof window !== 'undefined' && (window.location.hash || window.location.pathname)) || String(screen || ''),
        platform: isInNativeApp() ? 'webview-web' : 'web',
        app_version: '8.0',
      }),
    }).catch(() => {});   // never throw — telemetry must not break the app
  } catch (e) { /* never throw */ }
}
if (typeof window !== 'undefined' && !window.__kalpaErrHooked) {
  window.__kalpaErrHooked = true;   // attach once, at bundle load (before React mounts)
  window.addEventListener('error', (e) => {
    const m = e && (e.message || (e.error && e.error.message));
    if (m) reportClientError(m, e.error && e.error.stack, 'window.onerror');  // ignore resource-load errors (no message)
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e && e.reason;
    const m = r && (r.message || (typeof r === 'string' ? r : ''));
    if (m) reportClientError('unhandledrejection: ' + m, r && r.stack, 'unhandledrejection');
  });
}

// ── Usage telemetry (product analytics) → /api/collect (usage_events) ──────────
// PRIVACY PLANE (Usage-Analytics Build Spec, 16 Jul 2026): this path NEVER carries
// biomarker values/names, medication names, document contents/filenames, chat text,
// any health-record field, genetics, or free text. It emits controlled route names
// + coarse controlled property values only. The *_viewed events are PAGE-level:
// they record that a page was opened, never what was on it. The server re-strips
// any non-whitelisted property key as defence in depth (see usage_analytics.py).
// Fire-and-forget, buffered, NEVER throws — telemetry must not break the app.
const USAGE_APP_VERSION = '8.0';
const USAGE_EVENT_WHITELIST = new Set([
  'session_start', 'route_view', 'app_opened', 'login_succeeded', 'document_uploaded',
  'chat_message_sent', 'feedback_submitted', 'thumb_rated', 'fitness_synced',
  'biomarker_viewed', 'medicine_viewed', 'longevity_viewed', 'vault_viewed',
  'nudge_sent', 'nudge_opened',
]);
function _usagePlatform() {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent || navigator.vendor || '';
  if (isInNativeApp()) {
    if (/iPad|iPhone|iPod/i.test(ua) || /Macintosh/i.test(ua)) return 'ios_webview';
    return 'android_webview';   // native RN wrapper on Android (the dominant NPL app)
  }
  return 'web';
}
function _usageAnonId() {
  // Pseudonymous per-device id (NOT an underwriting key). Persists in localStorage;
  // falls back to null when storage is blocked (private mode) — then the event is
  // purely anonymous and server-side session identity is the logged-in profile only.
  try {
    let id = window.localStorage.getItem('kalpa_uid');
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'a-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      window.localStorage.setItem('kalpa_uid', id);
    }
    return id;
  } catch (e) { return null; }
}
const usageAnalytics = (typeof window !== 'undefined') ? (() => {
  const buf = [];
  let currentRoute = null;
  let lastRoute = null;
  const FLUSH_MS = 12000;
  // P2: dedicated CDP / CC-verification profiles carry localStorage
  // kalpa_e2e=1 (set once via ?e2e=1, or directly). Such a session NEVER posts
  // telemetry, so end-to-end verification runs write no usage_events rows. Real
  // users never carry this marker. Server /api/collect drops it too (backstop).
  const _isE2E = (() => {
    try {
      if (/[?&]e2e=1(&|$)/.test(window.location.search || '')) window.localStorage.setItem('kalpa_e2e', '1');
      return window.localStorage.getItem('kalpa_e2e') === '1';
    } catch (e) { return false; }
  })();
  const flush = (useBeacon) => {
    if (_isE2E || !buf.length) return;
    const batch = buf.splice(0, buf.length);
    let body;
    try { body = JSON.stringify(batch); } catch (e) { return; }
    try {
      if (useBeacon && navigator.sendBeacon
          && navigator.sendBeacon('/api/collect', new Blob([body], { type: 'application/json' }))) {
        return;
      }
      fetch('/api/collect', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', keepalive: true, body,
      }).catch(() => {});
    } catch (e) { /* never throw */ }
  };
  const track = (event, props) => {
    if (_isE2E) return;                                // P2: test profile emits nothing
    try {
      if (!USAGE_EVENT_WHITELIST.has(event)) return;   // client-side taxonomy guard
      buf.push({
        event, ts: new Date().toISOString(), route: currentRoute,
        platform: _usagePlatform(), app_version: USAGE_APP_VERSION,
        anonymous_id: _usageAnonId(),
        properties: (props && typeof props === 'object') ? props : undefined,
      });
      if (buf.length >= 25) flush(false);   // safety cap between 12s timers
    } catch (e) { /* never throw */ }
  };
  const trackRouteView = (route) => {
    const r = (route || '').split('?')[0];
    if (!r) return;
    currentRoute = r;
    if (r === lastRoute) return;             // dedupe consecutive identical routes
    lastRoute = r;
    track('route_view', { to_route: r });
    // Derive PAGE-level feature-view events (page opened only — never any value).
    const derived = {
      longevity: 'longevity_viewed', 'longevity-flags': 'biomarker_viewed',
      vault: 'vault_viewed', medicines: 'medicine_viewed',
    }[r];
    if (derived) track(derived);
  };
  if (!window.__kalpaUsageHooked && !_isE2E) {
    window.__kalpaUsageHooked = true;
    setInterval(() => flush(false), FLUSH_MS);          // spec: 12s batch timer
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
  }
  return { track, flush, trackRouteView };
})() : { track: () => {}, flush: () => {}, trackRouteView: () => {} };

// ── Feedback delivery: retry-with-backoff + IN-MEMORY queue (no browser storage) ──
// A dropped feedback message is never silently lost: it retries (1s/2s/4s), and if still
// failing it queues in memory and flushes on the next successful send / next feedback
// open; a final failure is reported to /api/client-error for a server trace. Never throws.
const _feedbackQueue = [];
async function _postWithRetry(ep, payload, delays = [1000, 2000, 4000]) {
  let lastErr;
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try { return await api.post(ep, payload); }
    catch (e) { lastErr = e; if (attempt < delays.length) await new Promise(r => setTimeout(r, delays[attempt])); }
  }
  throw lastErr;
}
async function flushFeedbackQueue() {
  if (!_feedbackQueue.length) return;
  const pending = _feedbackQueue.splice(0, _feedbackQueue.length);
  for (const item of pending) {
    try { await api.post(item.ep, item.payload); }
    catch (e) { _feedbackQueue.push(item); }   // still failing → keep for the next flush
  }
}
async function sendFeedback(ep, payload) {
  try { const res = await _postWithRetry(ep, payload); flushFeedbackQueue(); return { ok: true, res }; }
  catch (e) {
    _feedbackQueue.push({ ep, payload });
    reportClientError(`feedback POST failed after retries (queued): ${ep} — ${(e && e.message) || e}`, e && e.stack, 'feedback');
    return { ok: false, queued: true };
  }
}

// ── Central date formatter — DD-MM-YYYY EVERYWHERE (single source of truth) ──
// Every user-facing date render in the app routes through this. No ad-hoc
// toLocaleDateString / ISO slice / long-form / slashes anywhere else.
// Accepts a Date, a ms timestamp, or a string (bare 'YYYY-MM-DD' or full ISO).
// Returns 'DD-MM-YYYY' (dashes, zero-padded), or '' for null/blank input.
// A bare 'YYYY-MM-DD' is reformatted purely as a string so a UTC parse can never
// roll the day back in +05:45 (Nepal) — the day the lab printed is the day shown.
// RUN11.1 §1 — sitewide date format = "DD MMM YYYY" (e.g. "03 Jul 2026"). Supersedes the earlier
// DD-MM-YYYY (ambiguous for UK/Nepal/US readers). Single helper; nothing hand-formats dates elsewhere.
// LOCALE (2d): the day/month/year assembly moved into locale.js so ONE place decides
// "06 Jul 2023" (en-GB) vs "Jul 6, 2023" (en-US). formatDate stays THE name every caller
// uses — it is now a thin delegate, and on en-GB it returns the identical string it always
// did. The UTC-rollback guard for bare 'YYYY-MM-DD' moved with it, unchanged.
function formatDate(input) {
  return fmtDate(input);
}

// 2f — render a marker's value+unit through the clinical display layer. DISPLAY ONLY:
// storage, the API payload and every scoring input are untouched, and outside US this is
// typography alone. Returns a string so it can drop straight into JSX.
function mkVal(name, value, unit, opts) {
  const r = displayMarker(name, value, unit);
  if (r.value == null || r.value === '') return '';
  const u = r.unit ? ` ${r.unit}` : '';
  if (r.converted && !(opts && opts.bare)) {
    return `${r.value}${u}`;      // the converted figure IS the figure; no dual display
  }
  return `${r.value}${u}`;
}

// ── DURATION: ONE canonical formatter (2b) ──────────────────────────────────
// Four near-identical implementations existed (_fmtHm, _hrsMin, _fmtDur, plus an inline
// one), and separately the Today sleep card rendered the raw decimal `duration_hrs` as
// "6.1h". A duration is now formatted in exactly one place and every other helper
// delegates here, so "6h 6m" cannot drift into "6.1h" on one screen and "6h 06m" on
// another. Total minutes are rounded BEFORE the split, so 6.999h -> "7h 0m", never
// "6h 60m". Display-only: stored values and API payloads are untouched.
function fmtDurationMin(mins) {
  if (mins == null || !Number.isFinite(Number(mins))) return '—';
  const m = Math.round(Number(mins));
  const neg = m < 0 ? '-' : '';
  const a = Math.abs(m);
  if (a < 60) return `${neg}${a}m`;
  return `${neg}${Math.floor(a / 60)}h ${a % 60}m`;
}
function fmtDurationHours(hours) {
  if (hours == null || !Number.isFinite(Number(hours))) return '—';
  return fmtDurationMin(Number(hours) * 60);
}
// Date + time, for audit/last-sync/event lines. Date is DD-MM-YYYY; time is 24h HH:MM.
// D3 — ONE timestamp rule for the whole product: STORE UTC, RENDER IN THE VIEWER'S BROWSER
// TIMEZONE, ALWAYS WITH AN EXPLICIT ZONE LABEL.
//
// Two separate defects this fixes:
//
// 1. BARE UTC PARSED AS LOCAL. The server writes datetime.utcnow().isoformat(), which has NO
//    zone suffix — e.g. "2026-08-08T14:26:20.218002". JavaScript parses a suffix-less ISO
//    date-TIME as LOCAL time, so a UTC instant was being displayed as though its wall-clock
//    reading were already local: wrong by the viewer's whole UTC offset. The consent "Granted"
//    row on the Applicant Risk Report was doing exactly this. _asUtcDate pins those to UTC.
//    (Strings that DO carry a zone — "…Z", "+05:45" — are left alone and parse correctly.)
//
// 2. NO ZONE LABEL. "Aug 8, 2026, 2:26 PM" is ambiguous on an underwriting artifact that may be
//    read in another country. Every rendered timestamp now names its zone.
//
// NEVER DERIVE THE ZONE FROM THE PROFILE'S STATE. 13+ US states span two zones, so a state is
// not a timezone. Intl reads the viewer's actual browser zone, which is the only correct source.
// Audit STORAGE stays UTC and is untouched by any of this.
function _asUtcDate(input) {
  if (input instanceof Date) return input;
  if (input == null || input === '') return null;
  const s = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(s)) {
    return new Date(s.replace(' ', 'T') + 'Z');   // bare = server UTC
  }
  return new Date(s);
}

function _localeTag() {
  try {
    return (typeof FEATURES_GLOBAL !== 'undefined' && FEATURES_GLOBAL.language) || 'en-GB';
  } catch (e) { return 'en-GB'; }
}

// The canonical user-facing timestamp. Use this everywhere a stored instant is shown.
function formatDateTimeZoned(input) {
  const d = _asUtcDate(input);
  if (!d || isNaN(d.getTime())) return input == null ? '' : String(input);
  try {
    return new Intl.DateTimeFormat(_localeTag(), {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(d);
  } catch (e) {
    // Intl should never fail, but a timestamp is not worth throwing a report away for.
    return `${formatDate(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
}

// Date-only render of a stored instant. Same UTC-pinning fix as above: without it a UTC
// timestamp late in the day renders as the PREVIOUS day for viewers behind UTC.
function formatDateFromInstant(input) {
  const d = _asUtcDate(input);
  if (!d || isNaN(d.getTime())) return input == null ? '' : String(input);
  return formatDate(d);
}

// LEGACY renderer for NAIVE, ALREADY-LOCAL timestamps. Deliberately NOT routed through
// formatDateTimeZoned.
//
// Not every stored timestamp is a UTC instant. Fitbit returns sleep and workout start_time as
// DEVICE-LOCAL wall-clock ("2026-08-09T01:05:00.000" is a 1:05am bedtime, not 1:05 UTC).
// Pinning those to UTC and re-rendering in the viewer's zone would shift every bedtime and
// workout by the viewer's whole offset — inventing a defect while fixing a different one.
// So this stays as it was, and formatDateTimeZoned is applied ONLY where the value is genuinely
// a server-generated UTC instant.
function formatDateTime(input) {
  if (input == null || input === '') return '';
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return String(input);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${formatDate(d)} ${hh}:${mi}`;
}
// Rewrite any ISO dates (YYYY-MM-DD) embedded in a SERVER-generated string to
// DD-MM-YYYY, so advice text / "Based on your" source chips (which the backend
// builds with ISO dates) also read in the one sitewide format. Leaves everything
// else untouched. Null-safe.
// LOCALE (2d): delegates to the same locale pack as formatDate, so an ISO date rewritten
// out of a server string reads in whichever form the instance's language selects. Identity
// on en-GB.
function deIsoDates(text) {
  return deIsoDatesLocale(text);
}

// The server computes the greeting time-of-day from datetime.now() on the VM, which is UTC —
// so a Nepal user at 16:50 NPT (11:05 UTC) wrongly saw "Good morning". Re-derive the
// "Good morning/afternoon/evening" prefix from the BROWSER's local clock (new Date().getHours())
// so it is correct in ANY timezone, preserving the ", <name>" the server appended. Same
// client-clock principle the masthead date already uses (formatDate(new Date())). Falls back to
// the server string unchanged if it isn't the expected shape.
function localGreeting(serverGreeting) {
  const s = String(serverGreeting || '');
  const h = new Date().getHours();
  const tod = h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
  const m = s.match(/^Good\s+(?:morning|afternoon|evening)\b(.*)$/i);
  return m ? `Good ${tod}${m[1]}` : s;
}

// RUN11.1 §3 — deterministic, meaningful DISPLAY title for a document, derived from its own
// fields ("NPL · Lab Report · 03 Jul 2026"). NEVER surfaces a status string ("Self-uploaded
// document (marker commit pending your review)") or a raw drive filename as the title. Derived
// at render so it applies to NEW and EXISTING rows alike; the original filename is preserved
// (shown as the hover tooltip). Falls back to the filename when there aren't ≥2 meaningful parts.
const _DOC_TYPE_LABELS = { lab_report: 'Lab Report', medical_document: 'Medical Document',
  blood_test: 'Blood Test', medical_image: 'Medical Image', prescription: 'Prescription' };
function _docTypeLabel(t) {
  if (!t) return '';
  return _DOC_TYPE_LABELS[t] || String(t).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function _docDisplayTitle({ provider, docType, date, filename, id } = {}) {
  const shortProv = (provider || '').trim().split(/\s+/)[0];
  const parts = [shortProv, _docTypeLabel(docType), formatDate(date)].filter(x => x && String(x).length);
  if (parts.length >= 2) return parts.join(' · ');
  return filename || (id != null ? `Document ${id}` : 'Document');
}

// -- Touch gesture hooks -------------------------------------------------------
function usePullToRefresh(onRefresh, containerRef) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  // KAL-110 — TRUE ONLY FOR A REFRESH THIS GESTURE STARTED. The Today banner used to render on
  // the screen's generic `updating` flag, which is also set by the mount-time cache
  // revalidation and by every autonomous sync settlement — so "Refreshing…" appeared on app
  // open with no pull. A user-initiated pull is the only thing that earns a spinner; a
  // background refresh is info-level and stays silent behind the last-received line.
  const [pullRefreshing, setPullRefreshing] = useState(false);
  const startY = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchStart = (e) => { if (el.scrollTop === 0) startY.current = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      if (!startY.current) return;
      const dist = e.touches[0].clientY - startY.current;
      if (dist > 0 && el.scrollTop === 0) {
        setPulling(true);
        setPullDistance(Math.min(dist, 120));
      }
    };
    const onTouchEnd = () => {
      if (pullDistance > 60 && onRefresh) {
        setPullRefreshing(true);
        Promise.resolve().then(onRefresh)
          .catch((e) => console.warn('[sync] pull-to-refresh failed:', e))
          .finally(() => setPullRefreshing(false));
      }
      setPulling(false); setPullDistance(0); startY.current = 0;
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [containerRef, pullDistance, onRefresh]);

  return { pulling, pullDistance, pullRefreshing };
}

// ── Global sync-status strip + pull-to-refresh (Step 2) ───────────────────────
// A slim, NON-BLOCKING status strip below the header on data screens. Fed by
// (a) the native bridge's sync-lifecycle events (window.__kalpaSyncStatus, wired in
// App) and (b) pull-to-refresh. States: syncing | loading | ready | error. Persists
// "Updated {relative} ago" when idle. Never a notch/toast, never reads as an error
// while merely syncing.
const _SYNC_EVT = 'kalpa:sync-status';
const _SYNC_PROG_EVT = 'kalpa:sync-progress';
const _NATIVE_FINISHED_EVT = 'kalpa:native-sync-finished';
const _EMPTY_SYNC_PROGRESS = Object.freeze({ received: 0, total: null, label: null, indeterminate: false, scope: null });

// One process-wide snapshot is the sole source for lifecycle, progress and receipt time.
// Previously every useSyncStatus() call owned a private timestamp fetch while GlobalSyncBar
// separately owned progress. That allowed three individually valid values to describe three
// different moments (notably: error text + a syncing spinner + an old receipt timestamp).
let _syncSnapshot = { state: 'idle', lastSynced: null, progress: _EMPTY_SYNC_PROGRESS, error: null };
const _syncSubscribers = new Set();
let _lastReceivedRequest = 0;
let _latestLastReceivedPromise = Promise.resolve(null);
let _initialLastReceivedRequested = false;
let _activeScreenRefetch = null;
let _syncInFlight = null;
let _lastNativeTerminalAt = 0;
let _lastLegacyDataUpdateAt = 0;
let _syncSessionKey;
let _syncLifecycleEpoch = 0;

const _syncBusy = (state = _syncSnapshot.state) => state === 'syncing' || state === 'loading';
function _publishSyncSnapshot(patch) {
  const next = { ..._syncSnapshot, ...patch };
  if (!_syncBusy(next.state)) next.progress = _EMPTY_SYNC_PROGRESS;
  _syncSnapshot = next;
  _syncSubscribers.forEach(fn => {
    try { fn(); } catch (e) { console.error('[sync] status subscriber failed:', e); }
  });
}

function emitSyncStatus(state, extra = {}) {
  // Every new sync start permanently invalidates any older asynchronous receipt
  // settlement. A current `_syncInFlight` check alone is insufficient: the newer
  // run can finish before the older promise resumes, making the pointer null again.
  if (state === 'syncing') ++_syncLifecycleEpoch;
  const patch = { state, error: state === 'error' ? (extra.error || 'sync_failed') : null };
  // lastSynced means "last data received by the server", never "this client finished".
  // Only a no-store /last-received response is allowed to update it.
  if (extra.authoritative && Object.prototype.hasOwnProperty.call(extra, 'lastSynced')) {
    patch.lastSynced = extra.lastSynced || null;
  }
  _publishSyncSnapshot(patch);
  try {
    window.dispatchEvent(new CustomEvent(_SYNC_EVT, { detail: { state, ...extra } }));
  } catch (e) {
    console.error('[sync] could not dispatch lifecycle event:', e);
  }
}

function emitSyncProgress(received, total, label = null, indeterminate = false, scope = 'refresh') {
  const detail = { received, total, label, indeterminate, scope };
  if (_syncBusy()) {
    _publishSyncSnapshot({ progress: detail });
  } else {
    // A late native progress callback must never resurrect a spinner after ready/error.
    console.warn(`[sync] ignored late ${scope || 'unknown'} progress while state=${_syncSnapshot.state}`);
  }
  try {
    window.dispatchEvent(new CustomEvent(_SYNC_PROG_EVT, { detail }));
  } catch (e) {
    console.error('[sync] could not dispatch progress event:', e);
  }
}

function _readAuthoritativeLastReceived(reason) {
  const request = ++_lastReceivedRequest;
  let work;
  work = (async () => {
    try {
      // KAL-262
    const r = await _abortableFetch(`${API_BASE}/fitness/last-received`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (!data || !Object.prototype.hasOwnProperty.call(data, 'last_data_received')) {
        throw new Error('response missing last_data_received');
      }
      const value = data.last_data_received || null;
      if (request !== _lastReceivedRequest) {
        console.info(`[sync] awaiting newer last-received response (${reason})`);
        return _latestLastReceivedPromise === work ? _syncSnapshot.lastSynced : _latestLastReceivedPromise;
      }
      _publishSyncSnapshot({ lastSynced: value });
      return value;
    } catch (e) {
      if (request !== _lastReceivedRequest) {
        console.info(`[sync] ignored failed superseded last-received response (${reason})`);
        return _latestLastReceivedPromise === work ? _syncSnapshot.lastSynced : _latestLastReceivedPromise;
      }
      console.error(`[sync] authoritative last-received failed (${reason}):`, e);
      reportClientError(`last-received failed (${reason}): ${e.message || e}`, e.stack, 'sync-status');
      throw e;
    }
  })();
  _latestLastReceivedPromise = work;
  return work;
}

function _ensureInitialLastReceived(reason = 'initial status') {
  if (_initialLastReceivedRequested) return;
  _initialLastReceivedRequested = true;
  void _readAuthoritativeLastReceived(reason).catch((e) => {
    console.warn('[sync] initial receipt time remains unavailable after logged failure:', e.message || e);
  });
}

function _setSyncSession(sessionKey) {
  const key = sessionKey || null;
  if (_syncSessionKey === key) return;
  _syncSessionKey = key;
  ++_syncLifecycleEpoch;
  ++_lastReceivedRequest; // invalidate any response owned by the previous authenticated user
  _initialLastReceivedRequested = false;
  _publishSyncSnapshot({ state: 'idle', lastSynced: null, progress: _EMPTY_SYNC_PROGRESS, error: null });
  if (key && _syncSubscribers.size) _ensureInitialLastReceived('authenticated user changed');
}

async function _refreshActiveScreen(reason, refetch = _activeScreenRefetch) {
  if (typeof refetch !== 'function') return;
  try {
    await refetch();
  } catch (e) {
    console.error(`[sync] active-screen refresh failed (${reason}):`, e);
    reportClientError(`active-screen refresh failed (${reason}): ${e.message || e}`, e.stack, 'sync-status');
    throw e;
  }
}

function _refreshActiveScreenInBackground(reason, refetch = _activeScreenRefetch) {
  const started = Date.now();
  void _refreshActiveScreen(reason, refetch).then(() => {
    // Some page refetch callbacks intentionally own their own error UI and resolve
    // after handling a failure, so "settled" is the only honest generic claim here.
    console.info(`[sync] background screen refresh callback settled (${reason}) in ${Date.now() - started}ms`);
  }).catch((e) => {
    // The phone upload and server receipt are already confirmed. Keep that truthful
    // success state, but make a display-refresh failure loud and independently visible
    // in telemetry rather than turning it into a second, ambiguous sync failure.
    console.warn(`[sync] data was received but the background screen refresh rejected (${reason}):`, e);
  });
}

async function _refreshDataAndReceipt(reason, {
  terminal = true,
  dataEvent = null,
  receiptFirst = false,
} = {}) {
  const ownsLifecycle = terminal && !_syncInFlight;
  const lifecycleEpoch = ownsLifecycle ? ++_syncLifecycleEpoch : null;
  const stillOwnsLifecycle = () => ownsLifecycle
    && lifecycleEpoch === _syncLifecycleEpoch
    && !_syncInFlight;
  const canPublishDataEvent = () => !terminal || stillOwnsLifecycle();
  if (receiptFirst) {
    let succeeded = false;
    try {
      // Native has already uploaded every batch. Confirm the server receipt first so
      // the global sync surface can finish at phone step 36 instead of exposing the
      // current screen's potentially slow query as an indeterminate second sync.
      const lastReceived = await _readAuthoritativeLastReceived(reason);
      if (stillOwnsLifecycle()) emitSyncStatus('ready', { authoritative: true, lastSynced: lastReceived });
      succeeded = true;
    } catch (e) {
      if (stillOwnsLifecycle()) emitSyncStatus('error', { error: e.message || String(e) });
    } finally {
      // A failed receipt-status request must not leave the actual screen stale after
      // a successful native upload. Conversely, an older autonomous completion must
      // never ACK or overwrite a newer interactive sync that started while we waited.
      if (canPublishDataEvent()) {
        if (dataEvent) {
          try { window.dispatchEvent(new Event(dataEvent)); }
          catch (e) { console.error(`[sync] could not dispatch ${dataEvent}:`, e); }
        }
        _refreshActiveScreenInBackground(reason);
      } else {
        console.info(`[sync] suppressed stale native settlement after a newer sync took ownership${dataEvent ? ` (${dataEvent})` : ''}`);
      }
    }
    return succeeded;
  }
  if (ownsLifecycle) {
    emitSyncStatus('loading');
    emitSyncProgress(0, null, 'Refreshing your data…', true, 'refresh');
  }
  try {
    await _refreshActiveScreen(reason);
    const lastReceived = await _readAuthoritativeLastReceived(reason);
    if (dataEvent && canPublishDataEvent()) {
      try { window.dispatchEvent(new Event(dataEvent)); }
      catch (e) { console.error(`[sync] could not dispatch ${dataEvent}:`, e); }
    }
    if (stillOwnsLifecycle()) emitSyncStatus('ready', { authoritative: true, lastSynced: lastReceived });
    return true;
  } catch (e) {
    if (stillOwnsLifecycle()) emitSyncStatus('error', { error: e.message || String(e) });
    return false;
  }
}

function _normaliseNativeFinished(value, lastSynced, errorCode) {
  if (value && typeof value === 'object') return value;
  return { status: value, lastSynced, errorCode };
}

function _handleNativeFinished(value, lastSynced, errorCode) {
  const detail = _normaliseNativeFinished(value, lastSynced, errorCode);
  const status = detail.status === 'ready' ? 'ready' : 'error';
  const terminal = { ...detail, status };
  _lastNativeTerminalAt = Date.now();
  if (status === 'error') {
    console.error(`[sync] native sync failed${detail.errorCode ? ` (${detail.errorCode})` : ''}`);
    emitSyncStatus('error', { error: detail.errorCode || 'native_sync_failed' });
  }
  try {
    window.dispatchEvent(new CustomEvent(_NATIVE_FINISHED_EVT, { detail: terminal }));
  } catch (e) {
    console.error('[sync] could not dispatch native terminal event:', e);
  }
  if (status === 'ready' && !_syncInFlight) {
    // An autonomous login/foreground/background sync has no outer web run. Confirm
    // receipt immediately, settle the visible 36-step phone plan, then refresh the
    // current screen in the background. Never emit kalpa:refetch: that used to start
    // a second force_sync feedback loop.
    void _refreshDataAndReceipt('native completion', {
      terminal: true,
      dataEvent: 'kalpa:native-synced',
      receiptFirst: true,
    });
  }
}

function _acceptNativeProgress(event) {
  const d = (event && event.detail) || {};
  // Native timing marks an individual record read/enrichment as `status:error`
  // before the outer Health Connect loop deliberately catches it and continues.
  // Only the explicitly recoverable phases may continue; unknown/permission/upload
  // errors fail closed until native classifies them. Treating Vo2Max/other optional-
  // type failures as terminal produced a false "Couldn't sync" while uploads continued.
  const recoverableError = d.status === 'error'
    && ['read', 'enrichment', 'backfill'].includes(d.phase);
  if (recoverableError) {
    console.warn(`[sync] native ${d.phase || 'progress'} error is non-terminal; sync continues: ${d.label || d.errorCode || 'unknown'}`);
    return;
  }
  if (d.status === 'error') {
    _lastNativeTerminalAt = Date.now();
    console.error(`[sync] native progress reported terminal error: ${d.label || d.errorCode || 'unknown'}`);
    emitSyncStatus('error', { error: d.errorCode || d.label || 'native_sync_failed' });
    return;
  }
  if (typeof d.done !== 'number' || typeof d.total !== 'number') return;
  if (!_syncInFlight && Date.now() - _lastLegacyDataUpdateAt < 1500
      && _syncSnapshot.progress.scope === 'refresh') {
    console.warn('[sync] ignored late v25 phone progress after legacy data refresh began');
    return;
  }
  if (!_syncBusy()) {
    console.warn(`[sync] ignored late phone progress while state=${_syncSnapshot.state}`);
    return;
  }
  emitSyncProgress(d.done, d.total, d.label || 'Reading from your phone', false, 'phone');
}

function _handleLegacyNativeDataUpdate() {
  // v25 used this same hook for BOTH interactive sync completion and historical
  // backfill. It is therefore a data notification, never a terminal ACK. During
  // an interactive run the subsequent lifecycle-ready compatibility fallback
  // settles the waiter; outside one, refresh data + receipt exactly once here.
  if (_syncInFlight) {
    console.info('[sync] legacy data update received during interactive sync; outer refresh owns it');
    return;
  }
  _lastLegacyDataUpdateAt = Date.now();
  usageAnalytics.track('fitness_synced', { source: 'native_legacy_data' });
  void _refreshDataAndReceipt('legacy native data update', {
    terminal: true,
    dataEvent: 'kalpa:native-data-updated',
    receiptFirst: true,
  });
}

function _acceptNativeLifecycle(state, lastSynced, errorCode) {
  if (state === 'ready') {
    // Native sends lifecycle ready immediately before/after its terminal hook. Never render
    // that client timestamp as server receipt time; wait briefly for the terminal hook. The
    // fallback preserves old APKs which emitted lifecycle only.
    if (Date.now() - _lastNativeTerminalAt < 1500) return;
    // v25 calls legacy __kalpaRefreshFitness for autonomous syncs and historical
    // backfill alike. That hook already owns the data/receipt refresh when there is
    // no interactive waiter, so its adjacent lifecycle-ready must not start it twice.
    if (!_syncInFlight && Date.now() - _lastLegacyDataUpdateAt < 1500) return;
    const terminalSeen = _lastNativeTerminalAt;
    setTimeout(() => {
      if (_lastNativeTerminalAt === terminalSeen && _syncBusy()) {
        console.warn('[sync] native ready arrived without terminal hook — using compatibility fallback');
        _handleNativeFinished({ status: 'ready', lastSynced, source: 'lifecycle-fallback' });
      }
    }, 250);
    return;
  }
  if ((state === 'syncing' || state === 'loading') && _syncSnapshot.state === 'error'
      && Date.now() - _lastNativeTerminalAt < 1500) {
    console.warn(`[sync] ignored late native ${state} after terminal error`);
    return;
  }
  if (state === 'error') _lastNativeTerminalAt = Date.now();
  emitSyncStatus(state, state === 'error' ? { error: errorCode || 'native_sync_failed' } : {});
}

function useSyncStatus() {
  const [snapshot, setSnapshot] = useState(_syncSnapshot);
  useEffect(() => {
    const update = () => setSnapshot(_syncSnapshot);
    _syncSubscribers.add(update);
    update();
    // App's authenticated-session effect owns the first request. This gate prevents a
    // guaranteed 401 + false client-error telemetry while /auth/user is still resolving.
    if (_syncSessionKey) _ensureInitialLastReceived();
    return () => _syncSubscribers.delete(update);
  }, []);
  return snapshot;
}
// Fire a global sync: tell native to re-read Health Connect (force_sync) + re-fetch
// this screen's server data. Native settles visibly once server receipt is confirmed;
// its potentially slow screen query then refreshes in the background. Browser-only
// syncs retain measured refresh progress. Every failure remains loud.
// Pull-to-refresh and "Sync now" share the staged native + refresh run. kalpa:refetch is kept
// intentionally separate below: it refreshes already-received server data and never posts
// force_sync back to the phone.
function triggerGlobalSync(refetch) {
  return runSync({ refetch });
}
// Wire pull-to-refresh (on the shared .main-content scroller) + a global-retry
// listener to a screen's `refetch`. Returns {pulling, pullDistance} for the pull UI.
function useScreenSync(refetch) {
  const ref = useRef(null);
  useEffect(() => { ref.current = document.querySelector('.main-content'); });
  useEffect(() => {
    _activeScreenRefetch = refetch;
    // kalpa:refetch is deliberately DATA-ONLY. Native completion used to emit it,
    // which called runSync(), posted force_sync, and created the observed second run.
    const onRefetch = () => {
      Promise.resolve().then(refetch).catch((e) => {
        console.error('[sync] kalpa:refetch data refresh failed:', e);
        reportClientError(`kalpa:refetch failed: ${e.message || e}`, e.stack, 'sync-status');
      });
    };
    window.addEventListener('kalpa:refetch', onRefetch);
    return () => {
      window.removeEventListener('kalpa:refetch', onRefetch);
      if (_activeScreenRefetch === refetch) _activeScreenRefetch = null;
    };
  }, [refetch]);
  // pullRefreshing passes through: only a real pull reaches triggerGlobalSync from here.
  return usePullToRefresh(() => triggerGlobalSync(refetch), ref);
}
function _relSync(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime(); if (isNaN(t)) return null;
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) {
    const hours = Math.floor(s / 3600);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  const d = Math.floor(s / 86400); return d === 1 ? 'yesterday' : `${d} days ago`;
}
// MEASURED sync (real progress, not indeterminate): determine the expected total FIRST
// (Content-Length of the per-user payload), then stream the body and report bytes-received / total
// as it arrives. Also nudges the native bridge to re-read Health Connect. Emits progress on the
// shared sync event so the persistent left-panel bar fills for real. Never fabricates a total.
// The MEASURED total = the number of the user's real data DATASETS to re-pull (known up-front,
// gzip-immune — Content-Length is stripped from gzipped bodies, so a byte-bar would sit indeterminate).
// Each entry is a real GET the app already relies on; the bar advances by one as each resolves →
// genuinely measured "N of M datasets", never fake. Missing/failed datasets still advance the bar
// (a 4xx is a completed unit of work, not a hang) so it always settles.
const _SYNC_DATASETS = [
  { label: 'Today', path: '/today/summary' },
  { label: 'Scores', path: '/scores/all' },
  { label: 'Fitness activities', path: '/fitness/activities' },
  { label: 'Blood panels', path: '/clinical-timeline/panels' },
  { label: 'Last sync', path: '/fitness/last-received' },
];
// ── KAL-261 — THE LOCAL DATE, AND WHY toISOString() IS NOT IT ─────────────────────────────────
// `new Date().toISOString().slice(0,10)` is the **UTC** date. It was being compared against
// `as_of_date`, which is the server's INSTANCE-LOCAL day, and any mismatch printed a banner.
// Two consequences, both measured:
//   • NPL, 23 Aug 2026 19:25 BST: as_of_date 2026-08-24 (Kathmandu) vs UTC 2026-08-23 -> banner
//     fired and read "Data from 24 Aug 2026" directly above "No data today - last 23 Aug 2026".
//   • UK/US: between 00:00 and 01:00 BST the UTC date is still YESTERDAY, so the banner fires on
//     a perfectly current payload. Latent on all four instances, nightly, for one hour.
// A local calendar date must be built from local parts. Never from an ISO string.
// KAL-268 — the human name of the day boundary, for the approved copy "…(Nepal time)".
// Derived from the `timezone` FLAG the server already serves (FEATURES_GLOBAL.timezone), never
// from the browser: the whole point of the sentence is that the SERVER's day differs from the
// reader's. Returns '' when the zone is unknown or matches the reader's own, so the parenthetical
// appears only when it carries information.
const _TZ_HUMAN = {
  'Asia/Kathmandu': 'Nepal time',
  'Europe/London': 'UK time',
  'America/New_York': 'US Eastern time',
  'America/Chicago': 'US Central time',
  'America/Denver': 'US Mountain time',
  'America/Los_Angeles': 'US Pacific time',
};
function _tzShortLabel() {
  try {
    const tz = (typeof FEATURES_GLOBAL !== 'undefined' && FEATURES_GLOBAL.timezone) || '';
    if (!tz) return '';
    let mine = '';
    try { mine = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { mine = ''; }
    if (mine && mine === tz) return '';          // same zone — the parenthetical says nothing
    return _TZ_HUMAN[tz] || tz.split('/').pop().replace(/_/g, ' ') + ' time';
  } catch (e) { return ''; }
}

function _localISODate(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const _hasNativeBridge = () => !!(window.ReactNativeWebView && window.ReactNativeWebView.postMessage);

// Ask native to re-read Health Connect and wait for an explicit terminal outcome. v26 reports
// kalpa:native-sync-finished; native-synced and lifecycle:error remain fallbacks for old APKs.
// A timeout/error is terminal — continuing into "ready" after an unconfirmed phone read was the
// source of the five-minute zombie spinner and a false successful status.
function _awaitNativeSync(timeoutMs = 240000) {
  return new Promise((resolve) => {
    if (!_hasNativeBridge()) return resolve({ status: 'ready', reason: 'no-bridge' });
    let settled = false;
    const cleanup = () => {
      window.removeEventListener(_NATIVE_FINISHED_EVT, onFinished);
      window.removeEventListener('kalpa:native-synced', onLegacySynced);
      window.removeEventListener(_SYNC_EVT, onLifecycle);
    };
    const finish = (outcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(outcome);
    };
    const onFinished = (event) => {
      const d = (event && event.detail) || {};
      finish({ ...d, status: d.status === 'ready' ? 'ready' : 'error', reason: 'terminal-event' });
    };
    const onLegacySynced = () => finish({ status: 'ready', reason: 'legacy-native-synced' });
    const onLifecycle = (event) => {
      const d = (event && event.detail) || {};
      if (d.state === 'error') finish({ status: 'error', errorCode: d.error || 'native_sync_failed', reason: 'lifecycle-error' });
    };
    const timer = setTimeout(() => {
      console.error(`[sync] native did not report a terminal result within ${timeoutMs}ms`);
      finish({ status: 'error', errorCode: 'native_sync_timeout', reason: 'timeout' });
    }, timeoutMs);
    window.addEventListener(_NATIVE_FINISHED_EVT, onFinished);
    window.addEventListener('kalpa:native-synced', onLegacySynced);
    window.addEventListener(_SYNC_EVT, onLifecycle);
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'force_sync' }));
    } catch (e) {
      console.error('[sync] force_sync postMessage threw:', e);
      finish({ status: 'error', errorCode: 'bridge_error', reason: 'postMessage-threw' });
    }
  });
}

// ONE interactive sync driver for the button and pull-to-refresh. Native completion is a separate,
// data-only path and therefore cannot feed back into force_sync.
async function runSync({ refetch } = {}) {
  if (_syncInFlight) {
    console.info('[sync] already in flight — joining the running sync');
    return _syncInFlight;
  }
  _syncInFlight = (async () => {
    const t0 = (window.performance && performance.now) ? performance.now() : Date.now();
    const now = () => ((window.performance && performance.now) ? performance.now() : Date.now());
    const timings = [];
    let mark = t0;
    const lap = (stage) => { const t = now(); timings.push({ stage, ms: Math.round(t - mark) }); mark = t; };

    const native = _hasNativeBridge();
    const stages = _SYNC_DATASETS.map(d => ({ label: d.label, path: d.path, kind: 'fetch' }))
      .concat([{ label: 'Finishing up', kind: 'finish' }]);
    const total = stages.length;
    emitSyncStatus('syncing');
    let done = 0;
    let lastReceived = _syncSnapshot.lastSynced;
    const failures = [];
    let fatal = null;
    try {
      if (native) {
        // Phone and web refresh are two real plans with different denominators. Name the scope
        // instead of presenting 36 and 7 as though they were the same sequence.
        emitSyncProgress(0, null, 'Reading from your phone', true, 'phone');
        const outcome = await _awaitNativeSync();
        console.info(`[sync] native read finished: ${outcome.status}/${outcome.reason || 'unknown'}`);
        lap('Reading from your phone');
        if (outcome.status !== 'ready') {
          throw new Error(outcome.errorCode || 'native_sync_failed');
        }
      }

      if (native) {
        // The first four refresh GETs were downloaded with cache:no-store and then
        // discarded; the final active-screen query did the real UI update and could
        // take 15–20 seconds. For native, confirm the authoritative receipt now, keep
        // the visible plan at Phone sync complete (36/36), and refresh that one screen
        // after the status has honestly settled.
        try {
          lastReceived = await _readAuthoritativeLastReceived('interactive native sync');
          lap('Confirm server receipt');
        } catch (e) {
          console.error('[sync] native receipt confirmation failed:', e);
          failures.push(`receipt confirmation: ${e.message || e}`);
        }
        // Refresh whichever screen is current NOW, not the screen that initiated an
        // 86-second sync and may since have unmounted. Receipt failure is terminal for
        // the global timestamp, but must not prevent the successfully uploaded data
        // from reaching the visible screen.
        _refreshActiveScreenInBackground('interactive native completion');
      } else {
        emitSyncStatus('loading');
        for (const st of stages) {
          emitSyncProgress(done, total, st.label, false, 'refresh');
          if (st.kind === 'fetch') {
            try {
              if (st.path === '/fitness/last-received') {
                lastReceived = await _readAuthoritativeLastReceived('interactive sync');
              } else {
                // KAL-262 / F3 — THE UNBOUNDED AWAIT THAT FROZE THE SYNC BAR. This is the
                // request that hung on 23 Aug; every other loader in the app got a bound in
                // KAL-241 and this one did not. A stage that times out is now a FAILURE that is
                // named and counted, which is what the loop below already knows how to report.
                const r = await _abortableFetch(`${API_BASE}${st.path}`, { credentials: 'include', cache: 'no-store' }, LOAD_TIMEOUT_MS, st.path);
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                await r.arrayBuffer();
              }
            } catch (e) {
              console.error(`[sync] ${st.path} failed:`, e);
              failures.push(`${st.path}: ${e.message || e}`);
            }
          } else {
            const refresh = typeof refetch === 'function' ? refetch : _activeScreenRefetch;
            if (typeof refresh === 'function') {
              try { await refresh(); }
              catch (e) {
                console.error('[sync] final screen refresh failed:', e);
                failures.push(`screen refresh: ${e.message || e}`);
              }
            }
          }
          done += 1;
          lap(st.label);
          emitSyncProgress(done, total, st.label, false, 'refresh');
        }
      }
      if (failures.length) throw new Error(failures.join(' | '));
    } catch (e) {
      fatal = e;
      console.error('[sync] run failed:', e);
    }

    // B2 instrumentation: per-stage wall clock, readable from the console and from CDP.
    const totalMs = Math.round(now() - t0);
    window.__kalpaLastSyncTimings = { totalMs, stages: timings, at: new Date().toISOString() };
    console.info(`[sync] total ${totalMs}ms — ` + timings.map(x => `${x.stage} ${x.ms}ms`).join(' | '));

    if (fatal) {
      emitSyncStatus('error', { error: fatal.message || String(fatal) });
      return false;
    }
    // Lifecycle + authoritative receipt time settle in the same snapshot. Progress is cleared
    // by the same transition, so ready/error can never retain a spinner or stale step count.
    emitSyncStatus('ready', { authoritative: true, lastSynced: lastReceived });
    try { window.dispatchEvent(new Event('kalpa:native-synced')); }
    catch (e) { console.error('[sync] could not dispatch completed-data event:', e); }
    return true;
  })().finally(() => { _syncInFlight = null; });
  return _syncInFlight;
}
async function measuredSync() { return runSync(); }
/* _fmtKB removed with S5: it existed only for the abandoned byte-counting sync bar. The bar is
   measured in DATASETS because Content-Length is stripped from the gzipped responses, so a
   byte total was never obtainable and the helper had no remaining caller. */

// ── S5 — ONE sync status surface for the whole app ────────────────────────────────────────────
// There were THREE, and none of them was right on a phone:
//   1. SidebarSyncPanel   — had the measured progress bar, lived in the left nav, and CSS hid it
//                           outright below 480px (and capped it to 96px in the bottom bar below
//                           768px). So the users who actually wait for a sync — Bimal, Pawan, Tim
//                           on Android — were the only ones who never saw a progress bar.
//   2. SyncStatusStrip    — a thin line INSIDE <main>, gated on a hard-coded list of five views,
//                           so it vanished on Documents/My Genome and had no progress bar at all.
//   3. FitnessPage's own  — a local "↻ Syncing…" line, a third opinion on the same state.
// This is the one component. It is rendered ONCE, ABOVE the router (before the currentView
// switch, ungated), so a sync started on Today survives Today → Fitness → Health Record →
// Documents without unmounting and losing its progress. It is sticky at the top of the content
// column, which on mobile begins BELOW .mobile-header — and that header already reserves
// env(safe-area-inset-top) — so the bar can never land under a notch or camera cutout. It also
// reserves right-hand room for the fixed 💬 Feedback pill (top-right, zIndex 1150) so the two
// never overlap; that is measured in verification, not assumed.
// States are the existing emitSyncStatus lifecycle, unchanged: syncing | loading | ready | error.
function GlobalSyncBar() {
  const { state, lastSynced, progress: prog } = useSyncStatus();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 30000);   // keep "X ago" honest
    return () => clearInterval(id);
  }, []);
  const busy = state === 'syncing' || state === 'loading';
  // KAL-262 — "Sync now appears unclickable, then acts when clicked" is explained here, and the
  // guard below is NOT where it lives. While `busy` this component renders the busy row only:
  // spans, no button. So there is nothing to click, and a press lands on the row where "Sync
  // now" was a moment ago. That is correct behaviour for a sync already in flight - what was
  // wrong is how LONG the bar could stay busy. Before the KAL-262 bound, a single hung stage held
  // it there indefinitely (measured: 4 minutes, still going). With every stage bounded the busy
  // window is now bounded too, which is what makes this row honest rather than inert.
  // The `!busy` guard is kept as belt-and-braces for callers reached from elsewhere; runSync's
  // own `_syncInFlight` join is the real re-entrancy guard.
  const doSync = () => { if (!busy) measuredSync(); };
  const rel = _relSync(lastSynced);
  // Idle with no sync history at all → render NOTHING rather than invent a state. (This is why
  // the bar is absent for a brand-new account, and that is correct.)
  if (!busy && state !== 'error' && !rel) return null;

  // Indeterminate = busy with nothing measurable to report. That covers BOTH the native Health
  // Connect read (which flags itself) AND every other emitter that reports state but no progress:
  // triggerFitbitSync (the Fitbit-direct path UK/US use) and the page-load "a sync was already in
  // flight" case. Deriving it from `!prog.total` rather than trusting a flag means a future
  // emitter cannot reintroduce the inert bar. Measured on UK before this: a Fitbit background sync
  // held the bar busy for 10s+ with fill width 0 and a generic label — a bar that reported nothing.
  const indet = busy && (!!prog.indeterminate || !prog.total);
  let pct = null;
  if (busy && prog.total && !indet) pct = Math.min(100, Math.round((prog.received / prog.total) * 100));
  // KAL-262 — DO NOT PRINT A DETERMINATE "0%". Measured on NPL 23 Aug: the bar read
  // "refresh step 1 of 6 · 0%" for four minutes. Every part of that was technically true and the
  // whole was a lie - it looks like a stalled measurement when it is simply the state before the
  // first stage returns. Zero progress carries no information a reader can act on, so the
  // percentage appears only once something has actually completed; the step counter still moves.
  const pctShown = (pct != null && prog.received > 0) ? pct : null;
  const phoneComplete = busy && prog.scope === 'phone' && !!prog.total
    && prog.received >= prog.total;
  const tone = busy ? 'busy' : state === 'error' ? 'error' : 'ok';

  return (
    <div className={`gsb gsb-${tone}`} role="status" aria-live="polite"
      data-testid="global-sync-bar" data-sync-state={busy ? 'busy' : state}>
      <div className="gsb-main">
        {busy ? (
          <>
            <span className={`gsb-icon${phoneComplete ? '' : ' gsb-spin'}`} aria-hidden="true">
              {phoneComplete ? '✓' : '↻'}
            </span>
            {/* The LIVE stage name, not a frozen "starting…". Every entry point now drives the
                same runSync(), so this text changes as the sync actually moves through its real
                steps; the fallback is only for the first tick before the first stage is announced. */}
            <span className="gsb-label" data-testid="gsb-stage">
              {prog.label || (state === 'syncing' ? 'Syncing with your phone…' : 'Updating your data…')}
            </span>
            <span className="gsb-count" data-testid="gsb-count">
              {prog.total
                ? `${prog.scope === 'phone' ? 'phone' : 'refresh'} step ${Math.min(prog.received + 1, prog.total)} of ${prog.total}`
                : ''}
            </span>
            {/* No percentage while a stage is unmeasurable — a number there would be invented.
                And none at 0 either (KAL-262): see pctShown. */}
            {pctShown != null && <span className="gsb-pct">{pctShown}%</span>}
          </>
        ) : state === 'error' ? (
          <>
            <span className="gsb-icon" aria-hidden="true">⚠</span>
            <button type="button" className="gsb-label gsb-action" onClick={doSync}>
              Couldn’t sync — tap to retry
            </button>
            {rel && <span className="gsb-count">showing data from {rel}</span>}
          </>
        ) : (
          <>
            <span className="gsb-icon" aria-hidden="true">✓</span>
            {/* HONESTY FIX (3 Aug 2026). This said "Up to date · just now". `rel` is derived from
                /api/fitness/last-received, which is the last time ANY metric was written — and
                heart-rate writes land constantly, so it is almost always "just now". It was
                therefore printing "Up to date" over a 14-hour-stale sleep figure. The timestamp is
                a RECEIPT time, not a completeness claim, so it now says exactly that. Per-metric
                freshness lives in /api/fitness/today provenance.fields[*].last_recorded_date and is
                the right place to disclose an individual stale metric. */}
            <button type="button" className="gsb-label gsb-action" onClick={doSync}
                    title="Last time any health data reached Kalpa. Tap to sync now.">
              {rel === 'just now' ? 'Last data received just now' : `Last data received ${rel}`}
            </button>
            <span className="gsb-count gsb-cta">Sync now</span>
          </>
        )}
      </div>
      {/* The bar itself is always in the layout while busy so the row does not jump height as
          the label changes; the FILL is what moves. */}
      {busy && (
        <div className="gsb-bar" data-testid="gsb-bar"
          data-indeterminate={indet ? '1' : '0'} data-pct={pctShown != null ? pctShown : ''}
          role="progressbar" aria-valuemin={0} aria-valuemax={100}
          {...(pctShown != null ? { 'aria-valuenow': pctShown } : {})}>
          {/* ONE value drives the fill. The old version set width:8% and then animated
              translateX(-100%)->translateX(400%) on it, so the moving element crossed only ~40%
              of the track while the 8% was itself arbitrary — a fill and a pulse that agreed with
              each other about nothing. Determinate: width == pct, no motion. Indeterminate: the
              element spans the FULL track and the SHIMMER moves inside it, so the pulse can never
              travel further than the fill it belongs to. */}
          {/* KAL-262 — a determinate fill of width 0 is an empty track: indistinguishable from
              a broken bar. Before the first stage completes the bar is honestly INDETERMINATE. */}
          <div className={`gsb-bar-fill${(indet || pctShown == null) ? ' gsb-bar-indet' : ''}`}
            data-testid="gsb-fill"
            style={{ width: ((indet || pctShown == null) ? 100 : pctShown) + '%' }} />
        </div>
      )}
      {busy && indet && (
        <div className="gsb-sub">
          {prog.scope === 'provider'
            ? 'Fitbit is updating in the background…'
            : prog.scope === 'refresh'
              ? 'Updating this screen with the latest server data…'
              : 'This can take a few minutes — your phone is re-reading its records.'}
        </div>
      )}
    </div>
  );
}

// -- useIsMobile hook -----------------------------------------------------------
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [breakpoint]);
  return isMobile;
}

// -- isMobileBrowser -----------------------------------------------------------
// Reliable mobile-browser detection for the "Download Android app" button:
// user-agent FIRST (Android/iOS/mobile tokens), then a touch + narrow-viewport
// fallback (covers iPadOS 13+ which masquerades as "Macintosh"). NOT a bare CSS
// width check — a narrow desktop window must NOT count as mobile (a PC can't
// install an APK). Hoisted function declaration, safe to call from any render.
function isMobileBrowser() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  const uaMobile = /Android|iPhone|iPad|iPod|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile|Mobile/i.test(ua);
  const iPadOS = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  const touchNarrow = (navigator.maxTouchPoints || 0) > 0 && window.innerWidth <= 900;
  return uaMobile || iPadOS || touchNarrow;
}
// Hook variant: re-evaluates on resize/orientationchange so rotate/resize
// toggles visibility without needing a data-driven re-render.
function useIsMobileBrowser() {
  const [mob, setMob] = useState(isMobileBrowser);
  useEffect(() => {
    const h = () => setMob(isMobileBrowser());
    window.addEventListener('resize', h);
    window.addEventListener('orientationchange', h);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('orientationchange', h); };
  }, []);
  return mob;
}

// -- RUN V — device-conditional app install CTA -------------------------------
// TestFlight public join link (live). Single source of truth — future changes are one
// line here. While it was the "TODO_TIM" placeholder the iOS button rendered disabled;
// now that a real URL is set, tfReady is true and iOS visitors get the live button.
const TESTFLIGHT_URL = "https://testflight.apple.com/join/hzXZYmbR";
const APK_URL = "/app/npl-health.apk"; // https://health.nationalpathlabs.com/app/npl-health.apk
// Fallback only. The REAL URL comes from /api/app/version-status, which derives it from the APK
// actually being served — so shipping a new APK moves the link without touching this file. This
// constant only matters if that call fails.
const INTERNATIONAL_APK_URL = "/downloads/kalpa-international-1.2.1.apk";

// App-icon chip on the install card + button. Branded per instance: the Kalpa sunburst on
// UK/US, NPL's own icon on NPL. Shared by the card header and the button so the two can never
// disagree about which product they are offering.
//
// MUST BE A FUNCTION, NOT A CONST. FEATURES_GLOBAL is a module-level object whose branding is
// filled in later by the features fetch, and its DEFAULT is instance:'kalpa'. A const evaluated
// at module load would therefore resolve to the Kalpa mark on NPL as well, and never re-evaluate.
// KAL-329 — the product's name, from the branding flag, for every install/update surface. Same
// reasoning as appIconUrl(): a FUNCTION, because FEATURES_GLOBAL.branding fills in after load.
function productName() {
  const b = (typeof FEATURES_GLOBAL !== 'undefined' && FEATURES_GLOBAL.branding) || {};
  return b.instance === 'npl' ? (b.app_name || 'NPL Health') : 'Kalpa';
}
function appIconUrl() {
  const npl = typeof FEATURES_GLOBAL !== 'undefined'
    && FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.instance === 'npl';
  return npl ? "/npl_app_icon.png?v=1" : "/kalpa_mark.png?v=3";
}

// platform = 'android' | 'ios' | 'desktop'. UA first; iPadOS 13+ reports a
// "Macintosh" UA, so a Mac UA WITH touch points is treated as iPad (iOS). Never
// relies on a single UA substring. Android = /Android/i; everything else that
// isn't iOS is desktop/other.
// P4 — captured at script-eval time, BEFORE the app root strips location.search
// on mount (missing the bounce marker would silent-retry forever).
const GSILENT_AT_LOAD = (() => {
  try { return new URLSearchParams(window.location.search).get('gsilent'); }
  catch (e) { return null; }
})();

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  // §O — the native app renders this web frontend inside a React-Native WebView,
  // which injects window.ReactNativeWebView. Detect it first so the onboarding gate
  // treats the platform as 'native' (hides the web-only connect_device card).
  if (typeof window !== 'undefined' && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) return 'native';
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/Android/i.test(ua)) return 'android';
  const isIOS = /iPad|iPhone|iPod/i.test(ua) || (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  return isIOS ? 'ios' : 'desktop';
}
function usePlatform() {
  const [p, setP] = useState(detectPlatform);
  useEffect(() => {
    const h = () => setP(detectPlatform());
    window.addEventListener('resize', h);
    window.addEventListener('orientationchange', h);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('orientationchange', h); };
  }, []);
  return p;
}

// Android → APK; iOS/iPadOS → TestFlight (disabled until the link exists);
// desktop/other → BOTH + an "open on your phone" line. Themed (§C tokens) so
// contrast holds in light AND dark. No analytics on the buttons (privacy-clean).
// ── VERSION-AWARE INSTALL / UPDATE BUTTON (international instances only) ──────────────────────
// Three states, driven by the version comparison ALONE (never by UA, never by a hard-coded
// version string):
//   never_seen       -> "Download app"
//   update_available -> "Update app"
//   current          -> render NOTHING (and no empty container, so no gap is left behind)
// `latest` is derived server-side from the APK actually being served; `seen` is what this user's
// native client last reported. Until the mobile client sends X-Kalpa-App-Version the server
// records nothing, every user is `never_seen`, and the button simply always shows — the correct
// fallback, and exactly the state Tim is in today. See CODEX_SPEC_app_version_header.md.
function useAppVersionStatus(enabled) {
  const [status, setStatus] = React.useState(null);
  React.useEffect(() => {
    if (!enabled) return;
    let alive = true;
    (async () => {
      try {
        // KAL-262
    const r = await _abortableFetch(`${API_BASE}/app/version-status`, { credentials: 'include' }, LOAD_TIMEOUT_MS, 'version-status');
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setStatus(j);
      } catch (e) { /* button falls back to never_seen — never blocks the page */ }
    })();
    return () => { alive = false; };
  }, [enabled]);
  return status;
}

// Which platform lines this UA should be offered, in order. Desktop gets both, compactly.
function _installOrder(platform) {
  return platform === 'ios' ? ['ios'] : platform === 'android' ? ['android'] : ['android', 'ios'];
}

// ── KAL-320 — THE VERSION-AWARE UPDATE CARD, ONE COMPONENT, TWO HOMES ─────────────────────────
// History: the 9 Aug card (AppInstallCTA) had two mounts — the onboarding wizard and the legacy
// TodayFeed. 7ac2581 (23 Aug) deleted TodayFeed as unreachable code and with it the only
// Today-adjacent mount; Profile never had one. This component reads the ONE server resolver
// (/api/version-status) and is mounted on Today (above the fold, only when not up to date) and
// on Profile inside the Connections manager (always: "You have N · Latest M").
// Copy is the 9 Aug copy: "Download app" / "Update app · Version {latest} available. You have
// {seen}." / nothing. Android: APK + one line that Chrome will ask permission to install.
// iOS: TestFlight. Desktop: compact link on Profile only. Blocked: a modal on Today.
// The 60 s memo is keyed on the identity the server resolves the verdict from — the UA and the
// viewing profile — so a UA change (a debug override, a view-as switch) can never serve the
// previous identity's verdict. Measured 6 Sep 2026: a desktop answer was reused for an Android
// load 40 s later and the Today card stayed hidden.
let _versionStatusCache = { at: 0, key: '', data: null };
function useVersionStatus() {
  const key = (typeof navigator !== 'undefined' ? navigator.userAgent : '') + '|' + ((typeof window !== 'undefined' && window.__kalpaProfileKey) || '');
  const [vs, setVs] = useState(_versionStatusCache.key === key ? _versionStatusCache.data : null);
  useEffect(() => {
    let alive = true;
    let timer = null;
    const surface = isInNativeApp() ? 'app' : 'browser';
    // KAL-330 — ONE silent fetch was the whole card. On 7 Sep 2026 06:45:27Z (NPL, Tim's Pixel
    // on v36, latest 37) this request timed out at 20 s because both gunicorn workers were disk-
    // stalled behind the hourly stream-health sweep that ran INSIDE the phone's own ingest
    // request; the catch below swallowed it and nothing re-asked until the sync finished
    // (06:47:55Z). So "Update available" appeared to be gated behind "Uploading device totals ·
    // 94%" — it was not: same server, same stall, one fetch with no retry. Now: bounded retries
    // (3 s, 10 s, 30 s — deliberately slower than the KAL-322 page loaders so a stalled server is
    // not hammered), one named console line per failure, and a client-error report when every
    // attempt has failed. The server side of the stall is fixed in stream_health.sweep_throttled.
    const UPD_RETRY_MS = [3000, 10000, 30000];
    let retryTimer = null;
    const fetchNow = (attempt = 0) => {
      _abortableFetch(`${API_BASE}/version-status?surface=${surface}`, { credentials: 'include', headers: { ..._TZ_HEADER, ...platformHeader() } }, LOAD_TIMEOUT_MS, 'version-status')
        .then(r => { if (!r.ok) { const e = new Error(`HTTP ${r.status}`); e.status = r.status; throw e; } return r.json(); })
        .then(j => { if (!alive || !j) return; _versionStatusCache = { at: Date.now(), key, data: j }; setVs(j); })
        .catch((e) => {
          if (!alive) return;
          const why = (e && e.isTimeout) ? `timed out after ${Math.round(LOAD_TIMEOUT_MS / 1000)}s` : ((e && e.message) || 'failed');
          if (attempt < UPD_RETRY_MS.length) {
            console.warn(`[update-card] /api/version-status ${why}; retry ${attempt + 1}/${UPD_RETRY_MS.length} in ${UPD_RETRY_MS[attempt] / 1000}s`);
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => { if (alive) fetchNow(attempt + 1); }, UPD_RETRY_MS[attempt]);
            return;
          }
          console.error(`[update-card] /api/version-status ${why} after ${attempt + 1} attempts — card cannot render until the next visibility change or sync`);
          try { reportClientError(`version-status ${why} after ${attempt + 1} attempts`, null, 'update-card'); } catch (_) {}
        });
    };
    if (_versionStatusCache.data && _versionStatusCache.key === key && Date.now() - _versionStatusCache.at < 60000) setVs(_versionStatusCache.data);
    else fetchNow();
    // KAL-295b — the verdict changes exactly when the user comes BACK: the phone installed the
    // update and foregrounded the app, whose native hello (push/register, onboarding/state)
    // records the new build. Re-ask on visibility→visible, 1.5 s later so that hello lands first,
    // and again when the native side reports a sync. The 60 s memo would otherwise keep showing
    // "Update available" for a build the server already knows about.
    const refetch = () => { if (timer) clearTimeout(timer); timer = setTimeout(() => { if (alive) fetchNow(); }, 1500); };
    const onVis = () => { if (document.visibilityState === 'visible') refetch(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('kalpa:native-synced', refetch);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      if (retryTimer) clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('kalpa:native-synced', refetch);
    };
  }, []);
  return vs;
}
function UpdateCard({ placement }) {
  const vs = useVersionStatus();
  const [dismissed, setDismissed] = useState(() => {
    // KAL-379 — dismissal of the install/update card is remembered for 24 HOURS (was: this session).
    try { const raw = localStorage.getItem('upd_dismiss_until'); return raw != null && Number(raw) > Date.now(); } catch (_) { return false; }
  });
  const [snoozed, setSnoozed] = useState(() => {
    try { const raw = localStorage.getItem('upd_snooze_until'); return raw != null && Number(raw) > Date.now(); } catch (_) { return false; }
  });
  const [blockedKeyClosed, setBlockedKeyClosed] = useState(() => {
    try { return sessionStorage.getItem('upd_blocked_closed') || ''; } catch (_) { return ''; }
  });
  if (!vs || !vs.for_viewer) return null;
  const v = vs.for_viewer;
  const blockedKey = `${v.platform}|${v.seen}|${v.min_supported}|${v.latest}`;
  const blockedDismissed = blockedKeyClosed === blockedKey;
  const dismissBlocked = () => {
    try { sessionStorage.setItem('upd_blocked_closed', blockedKey); } catch (_) {}
    setBlockedKeyClosed(blockedKey);
  };
  const isDesktop = v.platform === 'desktop';
  const seen = v.seen || 'an older version';
  const latest = v.latest;
  // KAL-326 — the APK is offered ONLY to a resolved Android viewer, and carries the platform as a
  // query hint so /app/*.apk can refuse an Apple client even without headers. iOS gets
  // TestFlight, never the APK; an UNRESOLVED platform renders every labelled link (below).
  const withHint = (u) => (u && u.startsWith('/') && u.endsWith('.apk') && !u.includes('?')) ? `${u}?platform=android` : u;
  const hrefFor = () => (v.platform === 'android' ? withHint(v.action_url || APK_URL)
    : v.platform === 'ios' ? (v.action_url || TESTFLIGHT_URL) : null);
  // KAL-380 — on an instance with a Play listing the server's first Android link is the STORE
  // (kind 'play'): opened as a link, never downloaded, with the store's own hint copy.
  const primary = (v.action_urls || [])[0] || null;
  const viaPlay = v.platform === 'android' && !!primary && primary.kind === 'play';
  const actionProps = v.platform === 'android' && !viaPlay
    ? { href: hrefFor(), download: true }
    : { href: hrefFor(), target: '_blank', rel: 'noopener noreferrer' };
  const updateLabel = v.platform === 'ios' ? 'Update in TestFlight' : viaPlay ? 'Install from Google Play' : 'Update app';
  const androidHint = viaPlay ? (primary.hint || 'Uninstall the old app first, then install the app from Google Play.')
    : 'Chrome will ask you to allow installing from this site the first time.';
  const unresolved = v.platform !== 'android' && v.platform !== 'ios' && !isDesktop;
  // Every link the server offered, labelled; iOS carries the per-app link + the public join link.
  const allLinks = (v.action_urls || []).map((u, i) => (
    <a key={i} className={i === 0 ? 'profile-connect-btn upd-btn' : 'upd-link'}
       href={u.kind === 'apk' ? withHint(u.url) : u.url}
       {...(u.kind === 'apk' ? { download: true } : { target: '_blank', rel: 'noopener noreferrer' })}>
      {u.platform === 'android' && unresolved ? 'Android · ' : u.platform === 'ios' && unresolved ? 'iPhone · ' : ''}{u.label}
    </a>
  ));
  const secondaryLinks = allLinks.slice(1);
  if (v.state === 'blocked') {
    // Today gets the modal; Profile shows the line + button so support can point at it.
    // KAL-363 — the modal is ALWAYS closable. On 28 Sep 2026 a wrong server verdict (Kalpa UK,
    // current app, "pre-v27") left it covering Today with no way out: an update could not clear
    // it and neither could a relaunch. Closing it is remembered for this app session and for
    // THIS verdict only, so a different verdict (or the next launch) shows it again, and the
    // Profile line + button stay put regardless.
    if (placement === 'today') {
      if (blockedDismissed) return null;
      return <UpdateBlockedModal v={v} actionProps={actionProps} links={unresolved ? allLinks : secondaryLinks} unresolved={unresolved} onClose={dismissBlocked} viaPlay={viaPlay} androidHint={androidHint} />;
    }
  }
  if (placement === 'profile') {
    if (isDesktop) {
      return (
        <div className="upd-line" data-testid="upd-profile">
          <span>The {productName()} app installs on a phone.</span>
          <a className="upd-link" href={TESTFLIGHT_URL} target="_blank" rel="noopener noreferrer">Get the app</a>
        </div>
      );
    }
    return (
      <div className={`upd-line ${v.state === 'up_to_date' || v.state === 'unknown' ? '' : 'is-behind'}`} data-testid="upd-profile">
        <span>
          {v.state === 'not_installed' ? 'App not installed on this phone.'
            : v.state === 'unknown' ? 'App installed'
            : v.state === 'up_to_date' ? (v.seen ? `You have ${seen} · Up to date` : 'App installed')
            : `You have ${seen}${latest ? ` · Latest ${latest}` : ''}`}
        </span>
        {v.state !== 'up_to_date' && v.state !== 'unknown' && unresolved && allLinks}
        {v.state !== 'up_to_date' && v.state !== 'unknown' && !unresolved && (
          <a className="profile-connect-btn upd-btn" {...actionProps}>
            {v.state === 'not_installed' ? (v.platform === 'ios' ? 'Get on TestFlight' : 'Download app') : updateLabel}
          </a>
        )}
        {v.state !== 'up_to_date' && v.state !== 'unknown' && !unresolved && secondaryLinks}
        {v.state !== 'up_to_date' && v.state !== 'unknown' && v.platform === 'android' && (
          <span className="upd-hint">{androidHint}</span>
        )}
      </div>
    );
  }
  // Today, above the fold: only when there is something to do.
  // KAL-379 — 'unknown' (app bridge present, no reported build, no native rows) shows nothing.
  if (isDesktop || v.state === 'up_to_date' || v.state === 'none' || v.state === 'unknown') return null;
  if (v.state === 'not_installed' && dismissed) return null;
  if (v.state === 'behind' && snoozed) return null;
  const dismiss = () => {
    try {
      if (v.state === 'not_installed') { localStorage.setItem('upd_dismiss_until', String(Date.now() + 24 * 3600 * 1000)); setDismissed(true); }
      else { localStorage.setItem('upd_snooze_until', String(Date.now() + 24 * 3600 * 1000)); setSnoozed(true); }
    } catch (_) { setDismissed(true); setSnoozed(true); }
  };
  return (
    <div className="upd-card" data-testid="upd-today" role="status">
      <div className="upd-card-body">
        <div className="upd-title">{v.state === 'not_installed' ? `Get the ${productName()} app` : 'Update available'}</div>
        <div className="upd-sub">
          {v.state === 'not_installed'
            ? 'Your watch data syncs through the app on this phone.'
            : `Version ${latest} available. You have ${seen}.`}
        </div>
        {v.platform === 'android' && <div className="upd-hint">{androidHint}</div>}
      </div>
      <div className="upd-actions">
        {unresolved ? allLinks : (
          <a className="profile-connect-btn upd-btn" {...actionProps}>
            {v.state === 'not_installed' ? (v.platform === 'ios' ? 'Get on TestFlight' : 'Download') : updateLabel}
          </a>
        )}
        {!unresolved && secondaryLinks}
        <button type="button" className="upd-dismiss" onClick={dismiss} aria-label="Dismiss">×</button>
      </div>
    </div>
  );
}
function UpdateBlockedModal({ v, actionProps, links, unresolved, onClose, viaPlay, androidHint }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="upd-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="upd-modal-title" data-testid="upd-blocked">
      <div className="upd-modal">
        <button type="button" className="upd-dismiss upd-modal-close" onClick={onClose} aria-label="Close" data-testid="upd-blocked-close">×</button>
        <h3 id="upd-modal-title">This version of the app needs updating</h3>
        <p>
          You have version {v.seen || 'an older version'}. Versions before {v.min_supported} stop your sleep and
          heart-rate history being kept, so {productName()} cannot continue until the app is updated.
        </p>
        <p>The latest version is {v.latest}. Updating takes about a minute.</p>
        {!unresolved && (
          <a className="profile-connect-btn upd-btn" {...actionProps}>
            {v.platform === 'ios' ? 'Update in TestFlight' : viaPlay ? 'Install from Google Play' : 'Download the update'}
          </a>
        )}
        {links}
        {v.platform === 'android' && <p className="upd-hint">{androidHint || 'Chrome will ask you to allow installing from this site the first time.'}</p>}
      </div>
    </div>
  );
}

function AppInstallCTA() {
  const platform = usePlatform();
  const isNPL = typeof FEATURES_GLOBAL !== 'undefined' && FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.instance === 'npl';
  const isInternational = !!(typeof FEATURES_GLOBAL !== 'undefined' && FEATURES_GLOBAL.ready && !isNPL && (FEATURES_GLOBAL.country === 'UK' || FEATURES_GLOBAL.country === 'US'));
  // KAL-315 — version-aware on EVERY instance. This hook was enabled for UK/US only, so on NPL the
  // card was a static download button: a pilot user on v23 could never learn that v35 existed
  // (measured 6 Sep 2026: three real Android pilots on pre-v27, latest 35). The server has
  // answered `compare: build, latest: 35` for NPL since KAL-295; nothing here asked it.
  const vs = useAppVersionStatus(!!(typeof FEATURES_GLOBAL !== 'undefined' && FEATURES_GLOBAL.ready));
  const androidUrl = isInternational ? INTERNATIONAL_APK_URL : APK_URL;
  const androidLabel = isInternational ? 'Download Android app (internal testing)' : `Download ${productName()} (Android)`;
  const tfReady = isNPL && TESTFLIGHT_URL && TESTFLIGHT_URL !== 'TODO_TIM';
  const btn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, textDecoration: 'none',
    padding: '12px 16px', borderRadius: 10, fontWeight: 600, fontSize: 14, border: 'none',
    background: 'var(--npl-indigo, #0D156A)', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF',
    cursor: 'pointer', marginBottom: 8, width: '100%', boxSizing: 'border-box',
  };
  const disabled = {
    ...btn, background: 'var(--bg-card-raised)', color: 'var(--text-secondary)',
    WebkitTextFillColor: 'var(--text-secondary)', cursor: 'not-allowed',
  };
  const hint = { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '2px 0 6px', textAlign: 'center' };

  // ── INTERNATIONAL (Kalpa UK / Kalpa US) — version-aware. NPL never reaches this block and its
  // rendering below is untouched.
  if (isInternational) {
    const sub = { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, margin: '0 0 10px', textAlign: 'center' };
    // Default to never_seen until the status call answers: showing the button to someone who
    // already has the app is a small annoyance; hiding it from someone who does not have it is
    // the failure that matters.
    const st = (p) => (vs && vs[p] && vs[p].state) || 'never_seen';
    const line = (p) => {
      const s = st(p);
      if (s === 'current') return null;                      // nothing rendered, no empty box
      const isAndroid = p === 'android';
      const info = (vs && vs[p]) || {};
      const label = s === 'update_available' ? 'Update app' : 'Download app';
      const subline = s === 'update_available'
        ? `Version ${info.latest} available — you have ${info.seen}`
        : (isAndroid ? 'Android · internal testing build' : 'iOS · via TestFlight');
      const href = isAndroid ? (info.url || androidUrl) : TESTFLIGHT_URL;
      const extra = isAndroid ? { download: true } : { target: '_blank', rel: 'noopener noreferrer' };
      return (
        <div key={p}>
          <a href={href} style={btn} {...extra}>
            {isAndroid && (
              // Kalpa sunburst, not the NPL app icon — this branch is UK/US only. The mark ships
              // on a white field, which is exactly the app-icon treatment the rounded chip wants
              // and holds contrast in light AND dark. NPL's own icon is used on its path below.
              <img src={appIconUrl()} alt="" aria-hidden="true"
                   style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'cover' }} />
            )}
            {label}
          </a>
          <div style={sub}>{subline}</div>
        </div>
      );
    };
    // Lead with the platform the user is actually on; desktop shows both compactly.
    const rendered = _installOrder(platform).map(line).filter(Boolean);
    if (!rendered.length) return null;                       // every platform current -> nothing
    return <div data-testid="install-versioned">{rendered}</div>;
  }

  // KAL-315 — NPL, version-aware: the same three states as the international block.
  //   never_seen       -> "Download NPL Health" (this user has never opened the app)
  //   update_available -> "Update app · Version {latest} available — you have {seen}"
  //   current          -> nothing (no empty box)
  const npState = (p) => (vs && vs[p] && vs[p].state) || 'never_seen';
  const npInfo = (p) => (vs && vs[p]) || {};
  const androidState = npState('android');
  const androidBtn = androidState === 'current' ? null : (
    <div>
      <a href={`${npInfo('android').url || androidUrl}?platform=android`} download style={btn}>
        <img src="/npl_app_icon.png?v=1" alt="" aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'cover' }} />
        {androidState === 'update_available' ? 'Update app' : androidLabel}
      </a>
      {androidState === 'update_available' && (
        <div style={hint}>Version {npInfo('android').latest} available — you have {npInfo('android').seen}</div>
      )}
    </div>
  );
  const iosState = npState('ios');
  const iosHref = npInfo('ios').url || TESTFLIGHT_URL;
  const iosBtn = iosState === 'current' ? null : (tfReady || npInfo('ios').url)
    ? (
      <div>
        <a href={iosHref} target="_blank" rel="noopener noreferrer" style={btn}>
          {iosState === 'update_available' ? 'Update app (TestFlight)' : 'Install on iPhone (TestFlight)'}
        </a>
        {iosState === 'update_available' && (
          <div style={hint}>Build {npInfo('ios').latest} available — you have {npInfo('ios').seen}</div>
        )}
      </div>
    )
    : <button type="button" disabled style={disabled}>{isInternational ? 'iOS via TestFlight invitation' : 'TestFlight link coming'}</button>;
  // KAL-326 — 'native' used to mean Android here, so an iPhone inside the app was offered the APK.
  const resolved = platform === 'native' ? nativePlatform() : platform;
  if (resolved === 'android') return androidBtn ? <div data-testid="install-npl">{androidBtn}</div> : null;
  if (resolved === 'ios') return iosBtn ? <div data-testid="install-npl">{iosBtn}</div> : null;
  // ── S9 — DESKTOP SHOWS NEITHER PHONE-ONLY BUTTON. ────────────────────────────────────────────
  // It used to render BOTH the Android APK and the iPhone TestFlight button on desktop. That was
  // deliberate (with an "open on your phone" line underneath) but it is wrong in practice: a PC
  // cannot install either, and on a phone one of the two is always the wrong platform — a Nepali
  // pilot user on Android being invited into a TestFlight flow they cannot complete is a support
  // call in the market where hand-holding is hardest.
  // CHOSEN over a QR code: a QR needs a new dependency, and this run is not the place to add one.
  // Showing the host as TEXT is better than hiding the block outright — a desktop user can still
  // read where to go, which hiding would take away. Host comes from the live origin, so it is
  // instance-correct on NPL / Kalpa UK / Kalpa US without a per-instance string.
  const host = (typeof window !== 'undefined' && window.location && window.location.host) || '';
  return (
    <div data-testid="install-desktop">
      <div style={hint}>
        The Kalpa app installs on a phone. Open{host ? ` ${host} ` : ' this page '}on your
        Android or iPhone to install it.
      </div>
    </div>
  );
}

// -- Personalized info lookups for drill-down views ----------------------------
const METRIC_INFO = {
  'Weight': (avg, latest) => {
    const bmiEst = (parseFloat(latest) / ((1.76) ** 2)).toFixed(1);
    return `Your weight averages ${avg}kg (est. BMI ${bmiEst}). For longevity, maintaining a stable weight in the healthy BMI range (18.5\u201324.9) is associated with better outcomes. Weight stability matters as much as any single reading.`;
  },
  'Resting Heart Rate': (avg) => {
    const a = parseFloat(avg);
    const assessment = a < 60 ? 'excellent cardiovascular fitness' : a < 70 ? 'good cardiovascular fitness' : a < 80 ? 'average' : 'elevated \u2014 consider more aerobic exercise';
    return `Your resting HR averages ${avg}bpm, indicating ${assessment} for age 55. Lower resting HR is consistently associated with better longevity outcomes.`;
  },
  'Sleep Duration': (avg) => {
    const a = parseFloat(avg);
    const assessment = a >= 7 && a <= 8.5 ? 'within the optimal 7\u20138.5 hour range' : a < 7 ? 'below the recommended 7\u20138.5 hours' : 'above 8.5 hours';
    return `Your sleep averages ${avg} hours \u2014 ${assessment}. Research links 7\u20138.5 hours with lowest all-cause mortality. Sleep quality (deep + REM) matters as much as duration.`;
  },
  'Daily Steps': (avg) => {
    const a = parseFloat(String(avg).replace(/,/g, ''));
    const assessment = a >= 10000 ? 'excellent activity' : a >= 7500 ? 'good \u2014 significant mortality reduction' : a >= 5000 ? 'moderate activity' : 'below recommended levels';
    return `You average ${avg} daily steps \u2014 ${assessment}. Research shows 8,000\u201310,000 steps/day provides near-maximum longevity benefit with diminishing returns above 12,000.`;
  },
  'HRV (RMSSD)': (avg) => {
    const a = parseFloat(avg);
    const assessment = a >= 40 ? 'above average for age 55' : a >= 25 ? 'good autonomic function' : a >= 15 ? 'below average \u2014 stress management and sleep may help' : 'low \u2014 worth discussing with your doctor';
    return `Your HRV averages ${avg}ms \u2014 ${assessment}. Higher HRV reflects stronger parasympathetic tone and is associated with better longevity outcomes. HRV naturally declines with age.`;
  },
  'Calories Burned': (avg) => `Your daily calorie burn averages ${avg}cal. For a 55-year-old male at ~80kg, 2,000\u20132,500cal total daily expenditure is typical. Higher values reflect more active days.`,
  'Floors Climbed': (avg) => `You average ${avg} floors per day. Regular stair climbing is associated with reduced cardiovascular mortality. Even 3\u20135 floors daily offers measurable health benefit.`,
  'Active Zone Minutes': (avg) => {
    const weeklyEst = (parseFloat(avg) * 7).toFixed(0);
    const met = parseFloat(weeklyEst) >= 150;
    return `You average ${avg} active zone minutes daily (~${weeklyEst}/week). ${met ? 'You meet' : 'This is below'} the WHO guideline of 150+ minutes/week of moderate-to-vigorous activity.`;
  },
  'SpO2': (avg) => {
    const a = parseFloat(avg);
    const assessment = a >= 95 ? 'normal range' : a >= 90 ? 'borderline \u2014 worth monitoring' : 'below normal';
    return `Your SpO2 averages ${avg}% \u2014 ${assessment}. Normal blood oxygen is 95\u2013100%. Consistent readings below 95% during wakefulness may warrant evaluation.`;
  },
};

const MARKER_INFO = {
  'Ferritin': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v < 30 ? 'very low \u2014 iron depletion likely' : v < 60 ? 'below the longevity-optimal range (50\u2013150)' : v <= 150 ? 'within the longevity-optimal range' : 'elevated \u2014 may indicate inflammation';
    return `Your ferritin is ${value}. This is ${assessment}. As a pescatarian, iron absorption may be lower. Pairing iron-rich foods with vitamin C improves absorption.`;
  },
  'Vitamin D': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v < 20 ? 'deficient' : v < 40 ? 'below the longevity-optimal range (40\u201380)' : v <= 80 ? 'within the longevity-optimal range' : 'elevated \u2014 monitor for toxicity';
    return `Your vitamin D is ${value}. This is ${assessment}. Given your melanoma history (limiting sun exposure), supplementation is important. The Endocrine Society recommends 40\u201380 nmol/L for optimal health.`;
  },
  'eGFR': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v >= 90 ? 'normal kidney function' : v >= 60 ? 'mildly decreased \u2014 common after age 50' : 'moderately decreased \u2014 monitor closely';
    return `Your eGFR is ${value}. This indicates ${assessment}. eGFR naturally declines ~1 mL/min/year after 40. Hydration, limiting NSAIDs, and healthy blood pressure support kidney function.`;
  },
  'RDW': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v <= 13.0 ? 'normal' : v <= 13.5 ? 'at the longevity threshold' : v <= 14.5 ? 'mildly elevated' : 'elevated';
    return `Your RDW is ${value}%. This is ${assessment}. Research (Patel et al. 2009, NHANES) links RDW above 13.5% with increased all-cause mortality. It can reflect iron deficiency, B12/folate issues, or chronic inflammation.`;
  },
  'HDL': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v >= 60 ? 'in the longevity-optimal range (above 60)' : v >= 40 ? 'adequate but room for improvement' : 'low \u2014 increased cardiovascular risk';
    return `Your HDL is ${value}. This is ${assessment}. Aerobic exercise, omega-3s (good for pescatarians), and moderate alcohol intake support HDL. The Framingham study links higher HDL with lower cardiovascular mortality.`;
  },
  'LDL': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v < 70 ? 'excellent for cardiovascular longevity' : v < 100 ? 'at or near the longevity target' : v < 130 ? 'borderline high' : 'elevated';
    return `Your LDL is ${value}. This is ${assessment}. With rosuvastatin therapy, LDL should trend downward. Longevity research supports LDL below 100 mg/dL (2.6 mmol/L).`;
  },
  'Triglycerides': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v <= 75 ? 'within the longevity-optimal range' : v <= 150 ? 'within lab normal but above longevity target' : 'elevated';
    return `Your triglycerides are ${value}. This is ${assessment}. The Copenhagen studies link fasting triglycerides below 75 with lowest cardiovascular risk. Omega-3s, reduced refined carbs, and exercise help.`;
  },
  'HbA1c': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v <= 5.4 ? 'within the longevity-optimal range' : v <= 5.6 ? 'normal' : v <= 6.4 ? 'pre-diabetic range' : 'diabetic range';
    return `Your HbA1c is ${value}%. This is ${assessment}. Longevity research targets HbA1c below 5.4% for optimal metabolic health. Diet, exercise, and sleep quality all influence this marker.`;
  },
  'Glucose': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v >= 70 && v <= 90 ? 'within the longevity-optimal range (70\u201390)' : v < 70 ? 'low' : v <= 100 ? 'normal but above longevity target' : 'elevated';
    return `Your fasting glucose is ${value}. This is ${assessment}. Post-meal glucose spikes matter as much as fasting values. Regular exercise and adequate sleep are the strongest levers for glucose control.`;
  },
  'CRP': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v < 1.0 ? 'low inflammation \u2014 longevity-optimal' : v < 3.0 ? 'moderate inflammation' : 'elevated systemic inflammation';
    return `Your CRP is ${value}. This indicates ${assessment}. The CANTOS trial established inflammation as a causal cardiovascular risk factor. Anti-inflammatory diet, omega-3s, and regular exercise lower CRP.`;
  },
  'TSH': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v >= 0.5 && v <= 2.5 ? 'in the optimal range' : v < 0.4 ? 'suppressed' : v <= 4.5 ? 'within lab normal but above longevity-optimal' : 'elevated';
    return `Your TSH is ${value}. This is ${assessment}. Optimal thyroid function (TSH 0.5\u20132.5) supports metabolism, energy, and cardiovascular health.`;
  },
  'Homocysteine': (value, status) => {
    const v = parseFloat(value) || 0;
    const assessment = v <= 10 ? 'within the longevity-optimal range' : v <= 15 ? 'mildly elevated' : 'elevated \u2014 cardiovascular risk factor';
    return `Your homocysteine is ${value}. This is ${assessment}. B12, folate, and B6 help lower homocysteine. As a pescatarian, B12 intake may need supplementation.`;
  },
};

// -- MetricDetailView (Fitbit drill-down) --------------------------------------
function MetricDetailView({ metric, onClose, onAskAI }) {
  // Back-button parity: this full-screen drill-down pushes #metric-detail so
  // hardware/browser back closes it and returns to the Fitness page.
  const closeModal = useModalHashRoute('#metric-detail', onClose);
  const [range, setRange] = useState('90d');
  const ranges = { '30d': 30, '90d': 90, '1y': 365, 'all': 99999 };
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - ranges[range]);
  const filtered = (metric.data || []).filter(d => new Date(d.date) >= cutoff);
  // KAL-266 — was `.filter(v => v > 0)` with `: 0` fallbacks: it dropped genuine rest days
  // from the mean AND reported "0" for a metric with no readings at all. Exclude null,
  // include 0; an empty series yields null, not zero.
  const vals = filtered.map(d => (d.value == null ? null : Number(d.value)))
                       .filter(v => v != null && Number.isFinite(v));
  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  const mn = vals.length ? Math.min(...vals) : null;
  const mx = vals.length ? Math.max(...vals) : 0;
  const latest = vals.length ? vals[vals.length - 1] : 0;
  const recent7 = vals.slice(-7), prev7 = vals.slice(-14, -7);
  const rAvg = recent7.length ? recent7.reduce((a, b) => a + b, 0) / recent7.length : 0;
  const pAvg = prev7.length ? prev7.reduce((a, b) => a + b, 0) / prev7.length : 0;
  const trendPct = pAvg ? ((rAvg - pAvg) / pAvg * 100).toFixed(1) : '0.0';
  const yearTicks = (() => {
    if (filtered.length < 2) return [];
    const s = new Date(filtered[0].date).getFullYear(), e = new Date(filtered[filtered.length - 1].date).getFullYear();
    const t = []; for (let y = s; y <= e; y++) t.push(new Date(y, 0, 1).getTime()); return t;
  })();
  const fmt = v => typeof v === 'number' ? (v >= 100 ? Math.round(v).toLocaleString('en-GB') : v.toFixed(1)) : v;
  return (
    <div className="detail-view">
      <div className="detail-header">
        <button className="detail-back" onClick={closeModal}>&larr; Back</button>
        <h3>{autoGlossary(metric.label)}</h3>
      </div>
      <div className="detail-range-bar">
        {Object.keys(ranges).map(r => (
          <button key={r} className={`range-btn ${range === r ? 'active' : ''}`} onClick={() => setRange(r)}>
            {r === 'all' ? 'All' : r}
          </button>
        ))}
      </div>
      <div className="detail-stats-row">
        <div className="detail-stat"><span className="ds-label">Latest</span><span className="ds-value" style={{ color: metric.color }}>{fmt(latest)}</span></div>
        <div className="detail-stat"><span className="ds-label">Average</span><span className="ds-value">{fmt(avg)}</span></div>
        <div className="detail-stat"><span className="ds-label">Min</span><span className="ds-value">{fmt(mn)}</span></div>
        <div className="detail-stat"><span className="ds-label">Max</span><span className="ds-value">{fmt(mx)}</span></div>
      </div>
      <div className="detail-trend">
        Trend: {trendPct > 0 ? '\u2191' : trendPct < 0 ? '\u2193' : '\u2192'} {Math.abs(trendPct)}% (7-day vs prior 7-day) &middot; {filtered.length} data points
      </div>
      {METRIC_INFO[metric.label] && (
        <div className="detail-info-box">
          <div className="detail-info-icon">i</div>
          <p>{autoGlossary(METRIC_INFO[metric.label](fmt(avg), fmt(latest)))}</p>
        </div>
      )}
      <div className="detail-chart">
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={filtered} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
            <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
              tick={{ fontSize: 10, fill: '#8B6914' }} tickFormatter={ts => ts ? new Date(ts).getFullYear().toString() : ''}
              ticks={yearTicks} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#8B6914' }} width={40} />
            <Tooltip formatter={v => [`${fmt(v)} ${metric.unit}`, metric.label]}
              labelFormatter={ts => ts ? formatDate(ts) : ''}
              contentStyle={{ fontSize: '11px', padding: '4px 8px', background: 'var(--bg-card, #112240)', color: 'var(--text-primary, #e2e8f0)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px' }} />
            <Line type="monotone" dataKey="value" stroke={metric.color} strokeWidth={2}
              dot={filtered.length <= 90 ? { r: 2, fill: metric.color } : false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <button className="detail-ask-ai" onClick={() => onAskAI(`Analyze my ${metric.label} trends. Latest: ${fmt(latest)} ${metric.unit}, average over ${range}: ${fmt(avg)} ${metric.unit}, min: ${fmt(mn)}, max: ${fmt(mx)}. What does this mean for my health and longevity?`)}>
        {'\uD83E\uDDE0'} Ask AI about {metric.label}
      </button>
    </div>
  );
}

// -- MarkerDetailView (blood marker drill-down) --------------------------------
// Phase 2 — multi-source, contraindication-screened "What you can do" for one
// biomarker. Calls /api/marker-advice, which returns either LLM advice WITH the
// data sources that informed it, or (when only one data source exists or the
// advice can't be safely screened) falls back to numbers-only — in which case
// we show a quiet note rather than any advice (the chart/numbers are already on
// screen). This replaces the old static per-marker tip strings.
function MarkerAdvice({ marker, displayName, onAskAI }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    // KAL-278 (item 7) — this call runs an LLM server-side and was measured at ~26s on live
    // NPL, during which the only feedback was "Analysing your data…" with no way to know it
    // was still working, and on failure the component rendered NULL — the spinner vanished
    // into nothing, which reads as "stuck" (Bimal's report). Now: a 45s ceiling (the LLM path
    // is legitimately slow; the api helper's 20s abort would kill real successes), and every
    // terminal state SAYS something.
    withTimeout(api.get(`/marker-advice?marker=${encodeURIComponent(marker)}`), 45000, 'marker-advice')
      .then(d => { if (alive) setState({ loading: false, data: d }); })
      .catch(e => { if (alive) setState({ loading: false, error: e.message, timedOut: !!e.isTimeout }); });
    return () => { alive = false; };
  }, [marker]);

  if (state.loading) return (
    <div className="marker-advice" style={{margin:'14px 0', fontSize:13, color:'var(--text-secondary)'}}>{T('Analysing your data…')}</div>
  );
  if (state.error) return (
    <div className="marker-advice" style={{margin:'14px 0', fontSize:12.5, color:'var(--text-dim, #8a9bb0)'}}>
      {state.timedOut ? T('The analysis is taking longer than usual — your numbers above are complete without it.')
                      : T('Analysis unavailable right now — your numbers above are complete without it.')}
    </div>
  );
  if (!state.data) return null;
  const d = state.data;

  if (d.mode !== 'advice') {
    return (
      <div className="marker-advice" style={{margin:'14px 0', padding:'10px 12px', background:'var(--secondary-bg)', borderRadius:8, fontSize:12.5, color:'var(--text-secondary)'}}>
        Showing your numbers and trend above. {d.fallback_reason || 'Detailed guidance isn’t available for this marker.'}
      </div>
    );
  }

  return (
    <div className="marker-advice" style={{margin:'14px 0', padding:'12px 14px', background:'var(--secondary-bg)', border:'1px solid var(--border-color)', borderRadius:10}}>
      <div style={{fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em'}}>What you can do</div>
      <div style={{fontSize:13.5, color:'var(--text-primary)', lineHeight:1.5}}>{autoGlossary(deIsoDates(d.advice))}</div>
      {Array.isArray(d.sources) && d.sources.length > 0 && (
        <div style={{marginTop:10}}>
          <div style={{fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-dim)', marginBottom:5}}>Based on your</div>
          <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
            {d.sources.map((s, i) => (
              <span key={i} style={{fontSize:11.5, background:'rgba(127,145,223,0.12)', color:'var(--accent-violet, #818CF8)', borderRadius:999, padding:'3px 9px'}}>{deIsoDates(s)}</span>
            ))}
          </div>
        </div>
      )}
      {onAskAI && (
        <button onClick={() => onAskAI(T(`About my ${displayName || marker}: ${d.advice}\n\nCan you explain this and what to prioritise, grounded in my data?`))}
          style={{marginTop:12, background:'transparent', border:'1px solid var(--accent-violet, #818CF8)', color:'var(--accent-violet, #818CF8)', borderRadius:8, padding:'6px 12px', fontSize:12.5, fontWeight:600, cursor:'pointer'}}>
          Ask Kalpa about this
        </button>
      )}
      <div style={{marginTop:12}}>
        <AiThumbs surface="marker_advice" contextRef={marker} />
      </div>
    </div>
  );
}

function MarkerDetailView({ marker, onClose, onAskAI, onDeleted }) {
  // Back-button parity: pushes #marker-detail so back closes the marker
  // drill-down and returns to the Health Record timeline.
  const closeModal = useModalHashRoute('#marker-detail', onClose);
  const { displayName, history, unit, refLow, refHigh, optimalLow, optimalHigh, optimalSource, status, latest, name } = marker;
  const [range, setRange] = useState('all');
  // Stage 6c — per-reading delete (confirm first).
  const [pendingReading, setPendingReading] = useState(null);
  const [deletingReading, setDeletingReading] = useState(false);
  const [delErr, setDelErr] = useState(null);
  const doDeleteReading = async () => {
    const h = pendingReading; if (!h) return;
    setDeletingReading(true); setDelErr(null);
    try {
      await api.del('/clinical-timeline/marker', { marker_name: name, date: h.date });
      setPendingReading(null);
      if (onDeleted) onDeleted();  // parent reloads the Health Record + closes this view
    } catch (e) { setDelErr(e.message); setDeletingReading(false); }
  };
  const markerRanges = { '1y': 365, '2y': 730, '5y': 1825, 'all': 99999 };
  const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - markerRanges[range]);
  const allChartData = (history || []).map(h => ({ date: h.date, ts: new Date(h.date).getTime(), value: h.value }));
  const chartData = allChartData.filter(d => new Date(d.date) >= cutoff);
  const filteredHistory = (history || []).filter(h => new Date(h.date) >= cutoff);
  const vals = chartData.map(d => d.value);
  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  const mn = vals.length ? Math.min(...vals) : 0, mx = vals.length ? Math.max(...vals) : 0;
  const pad = (mx - mn) * 0.2 || (mx * 0.1) || 1;
  // KAL-278 (item 7) — ROUND THE DOMAIN AND FORMAT THE TICKS. `mn - pad` in IEEE-754 produced
  // 1.1067999999999998 and 6.659199999999999 as the domain ENDS, Recharts printed them raw,
  // and the 40px axis clipped them to their trailing digits: Bimal's screenshot read
  // "›99999 / 5.1068 / ›99998". Reproduced pixel-for-pixel on his CRP view (n=2), where the
  // domain ends become ticks. Ticks now pass through one formatter, so no tick can ever be
  // wider than its meaningful digits.
  const _fmtTick = (v) => {
    if (v == null || !Number.isFinite(v)) return '';
    const a = Math.abs(v);
    return a >= 100 ? Math.round(v).toLocaleString('en-GB')
         : a >= 10 ? v.toFixed(1).replace(/\.0$/, '')
         : Number(v.toFixed(2)).toString();
  };
  // RUN11.6 §2 — suppress the Kalpa optimal band on NPL (lab-printed reference only); Kalpa keeps it.
  const _isNPLmc = (typeof FEATURES_GLOBAL !== 'undefined') && FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.instance === 'npl';
  const hasOpt = !_isNPLmc && (optimalLow != null || optimalHigh != null);
  const yMin = Math.min(mn - pad, refLow != null ? refLow * 0.9 : mn - pad, hasOpt && optimalLow != null ? optimalLow * 0.9 : mn - pad);
  const yMax = Math.max(mx + pad, refHigh != null ? refHigh * 1.1 : mx + pad, hasOpt && optimalHigh != null ? optimalHigh * 1.1 : mx + pad);
  const statusColors = { normal: '#22c55e', high: '#ef4444', low: '#f59e0b', high_borderline: '#f59e0b', low_borderline: '#f59e0b', high_good: '#22c55e', low_good: '#22c55e', data_error: '#9ca3af' };
  // Present-tense, direction-aware, lay-friendly badge copy (5F, C0) — states
  // good/bad plainly in plain language, no clinical jargon.
  const STATUS_LABEL = { normal: 'In your typical range', high: 'Above typical range', low: 'Below typical range', high_borderline: 'Slightly above typical range', low_borderline: 'Slightly below typical range', high_good: 'High · beneficial', low_good: 'Low · beneficial', data_error: 'Worth re-checking' };
  return (
    <div className="detail-view">
      <div className="detail-header">
        <button className="detail-back" onClick={closeModal}>&larr; Back</button>
        <h3>{autoGlossary(displayName)}</h3>
        <span className={`marker-status-badge ${status}`}>{STATUS_LABEL[status] || status}</span>
      </div>
      <div className="detail-range-bar">
        {Object.keys(markerRanges).map(r => (
          <button key={r} className={`range-btn ${range === r ? 'active' : ''}`} onClick={() => setRange(r)}>
            {r === 'all' ? 'All' : r}
          </button>
        ))}
      </div>
      <div className="detail-ref-ranges">
        {refLow != null && refHigh != null && <span><GlossaryTerm term="reference range">Lab range</GlossaryTerm>: {refLow}&ndash;{refHigh} {unit}</span>}
        {hasOpt && <span className="optimal-range"><GlossaryTerm term="longevity target">Longevity target</GlossaryTerm>: {optimalLow != null ? optimalLow : '<'}{optimalLow != null && optimalHigh != null ? '\u2013' : ''}{optimalHigh != null ? optimalHigh : '+'} {unit}</span>}
        {hasOpt && optimalSource && <span style={{ fontStyle: 'italic', fontSize: '11px' }}>({optimalSource})</span>}
      </div>
      {/* RUN11.6 §2 — MARKER_INFO copy is Kalpa longevity-optimal framed (e.g. Ferritin
          "below the longevity-optimal range (50–150)"), conflicting with NPL's lab-printed
          range. Suppress it on NPL; Kalpa-main keeps it. */}
      {!_isNPLmc && MARKER_INFO[name] && (
        <div className="detail-info-box">
          <div className="detail-info-icon">i</div>
          <p>{autoGlossary(MARKER_INFO[name](latest?.value ?? '--', status))}</p>
        </div>
      )}
      <div className="detail-stats-row">
        <div className="detail-stat"><span className="ds-label">Latest</span><span className="ds-value" style={{ color: statusColors[status] }}>{latest?.value ?? '--'}</span></div>
        <div className="detail-stat"><span className="ds-label">Average</span><span className="ds-value">{avg.toFixed(1)}</span></div>
        <div className="detail-stat"><span className="ds-label">Min</span><span className="ds-value">{mn.toFixed(1)}</span></div>
        <div className="detail-stat"><span className="ds-label">Max</span><span className="ds-value">{mx.toFixed(1)}</span></div>
      </div>
      <div className="detail-chart">
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
            {refLow != null && refHigh != null && <ReferenceArea y1={refLow} y2={refHigh} fill="#22c55e" fillOpacity={0.08} stroke="none" />}
            {hasOpt && optimalLow != null && optimalHigh != null && <ReferenceArea y1={optimalLow} y2={optimalHigh} fill="#3b82f6" fillOpacity={0.10} stroke="none" />}
            {hasOpt && optimalLow != null && optimalHigh == null && <ReferenceLine y={optimalLow} stroke="#3b82f6" strokeDasharray="4 2" strokeWidth={1} />}
            {hasOpt && optimalHigh != null && optimalLow == null && <ReferenceLine y={optimalHigh} stroke="#3b82f6" strokeDasharray="4 2" strokeWidth={1} />}
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#8B6914' }} axisLine={false} tickLine={false} tickFormatter={formatDate} />
            <YAxis domain={[Number(yMin.toFixed(2)), Number(yMax.toFixed(2))]}
              tickFormatter={_fmtTick} tick={{ fontSize: 10, fill: '#8B6914' }} width={40} />
            <Tooltip formatter={v => [`${v} ${unit}`, displayName]}
              contentStyle={{ fontSize: '11px', padding: '4px 8px', background: 'var(--bg-card, #112240)', color: 'var(--text-primary, #e2e8f0)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px' }} />
            <Line type="monotone" dataKey="value" stroke={statusColors[status]} strokeWidth={2}
              dot={{ r: 4, fill: statusColors[status] }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="detail-history-table">
        <h4>History ({filteredHistory.length} readings{range !== 'all' ? ` in ${range}` : ''})</h4>
        {filteredHistory.slice().reverse().map((h, i) => (
          <div key={i} className="history-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{formatDate(h.date)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{h.value} {unit}</span>
              <button onClick={() => { setDelErr(null); setPendingReading(h); }} title="Delete this reading" aria-label="Delete reading"
                style={{ background: 'none', border: 'none', color: 'var(--text-dim, #94a3b8)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>🗑</button>
            </span>
          </div>
        ))}
      </div>

      {/* Stage 6c — confirm deleting a single saved reading */}
      {pendingReading && (
        <div onClick={() => !deletingReading && setPendingReading(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: 'var(--secondary-bg, #10182b)', border: '1px solid var(--border-color, #334155)', borderRadius: 12, padding: 20, maxWidth: 400, width: '100%' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #f1f5f9)', marginBottom: 10 }}>Delete this reading?</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary, #cbd5e1)', lineHeight: 1.5, marginBottom: 14 }}>
              Permanently remove <strong>{displayName} {pendingReading.value} {unit}</strong> from <strong>{formatDate(pendingReading.date)}</strong>. This cannot be undone.
            </div>
            {delErr && <div style={{ fontSize: 12.5, color: '#f87171', marginBottom: 10 }}>Delete failed: {delErr}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button disabled={deletingReading} onClick={() => setPendingReading(null)}
                      style={{ fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border-color, #334155)', background: 'transparent', color: 'var(--text-secondary, #cbd5e1)', cursor: 'pointer' }}>Cancel</button>
              <button disabled={deletingReading} onClick={doDeleteReading}
                      style={{ fontSize: 13, fontWeight: 700, padding: '9px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: deletingReading ? 'wait' : 'pointer' }}>
                {deletingReading ? 'Deleting…' : 'Delete reading'}
              </button>
            </div>
          </div>
        </div>
      )}
      <MarkerAdvice marker={name || displayName} displayName={displayName} onAskAI={onAskAI} />
      <button className="detail-ask-ai" onClick={() => onAskAI(`My ${displayName} (${name}) is ${latest?.value} ${unit} (status: ${status}). History: ${(history || []).map(h => `${h.date}: ${h.value}`).join(', ')}. Is this optimal for longevity? What should I do to improve it?`)}>
        {'\uD83E\uDDE0'} Ask AI about {displayName}
      </button>
    </div>
  );
}

// -- VaultThumbnail (lazy-loaded document preview) -----------------------------
function VaultThumbnail({ file }) {
  const [thumb, setThumb] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loaded) {
        setLoaded(true);
        api.get(`/vault/thumbnail/${encodeURIComponent(file.path)}`)
          .then(data => { if (data.thumbnail) setThumb(data); })
          .catch(() => {});
      }
    }, { rootMargin: '100px' });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [file.path, loaded]);
  return (
    <div ref={ref} className="vault-thumbnail">
      {thumb ? <img src={`data:${thumb.mime};base64,${thumb.thumbnail}`} alt="" /> :
        <div className="vault-card-icon">{file.is_image ? '\uD83D\uDDBC\uFE0F' : '\uD83D\uDCC4'}</div>}
    </div>
  );
}

// -- Login Page ----------------------------------------------------------------
// E.164 helper: the national part of `phone` for country code `cc` ('977' -> '+9779812345678'
// yields '9812345678'). One definition, used by the input, its validation and its hint, so they
// can never disagree about where the country code ends.
function natOf(phone, cc) {
  const p = String(phone || '');
  const pre = `+${cc}`;
  return p.startsWith(pre) ? p.slice(pre.length) : p.replace(/^\+/, '');
}

function LoginPage() {
  useFeatures(); // ensure FEATURES_GLOBAL is hydrated (login is pre-auth — fine, /api/features is public)
  const isNPL = FEATURES_GLOBAL.branding.instance === 'npl';
  const logoSrc = isNPL ? (FEATURES_GLOBAL.branding.logo_url || '/static/npl_logo.png') : '/kalpa_logo.png';
  const appName = isNPL ? (FEATURES_GLOBAL.branding.app_name || 'NPL Health') : 'Kalpa';
  // USER-MGMT build P3: hidden staff/WF entry. On NPL the pilot login (/) is
  // OTP-only with NO Google button; /wf surfaces "Sign in with Google"
  // (gated server-side by the google_allowlist — non-listed emails get 403).
  const isWf = typeof window !== 'undefined' && window.location.pathname === '/wf';
  // STAFF-IN-APP FIX: the native app is this SAME page in a RN WebView that loads
  // the site root — it has no URL bar, so staff could never reach /wf and saw
  // OTP-only. A discreet "Staff sign-in" link now reveals the SAME Google entry on
  // BOTH surfaces. The default view stays OTP-only (pilots never see Google unless
  // they deliberately tap the staff link), and the boundary remains SERVER-SIDE:
  // google_allowlist rejects non-listed emails with 403 and creates no account.
  const [staffMode, setStaffMode] = useState(false);
  const [gMsg, setGMsg] = useState(null);          // silent-flow bounce message
  const [silentBusy, setSilentBusy] = useState(false);
  const showGoogle = !isNPL || isWf || staffMode;
  // KAL-208 — whether the phone/OTP form is OFFERED. `isNPL` is evaluated FIRST and stands
  // alone, so NPL's pilot login renders bit-identically whatever the flag says: NPL's login
  // predates this flag and must never be made to depend on it. On a branding=kalpa instance
  // the form was previously unreachable (this block was `isNPL && !isWf`), which is why the
  // seeded +1 demo accounts — phone identifiers, unroutable @synthetic.kalpa.invalid
  // addresses, so no Google path either — had NO way in at all on UK/US.
  //
  // The flag grants NO new backend capability: /api/auth/request-otp and /api/auth/verify-otp
  // are public and ungated on every instance and already accepted these credentials. It
  // decides only whether a human is shown the field. Demo affordance; out after 19 Aug 2026
  // (KAL-208).
  const showOtp = isNPL || FEATURES_GLOBAL.otp_login;
  // P4 — SILENT staff auto-login. A device that has used the staff Google entry
  // carries a local flag; on the next visit we try /auth/login?silent=1
  // (prompt=none): live Google session + allow-list match → straight to logged-in,
  // no button. Clean bounces come back as ?gsilent=failed (no live session — show
  // the staff view, flag kept) or ?gsilent=denied (live session NOT on the
  // allow-list — clear the flag so it never loops). Pilots never set the flag, so
  // their default OTP-only view is untouched.
  useEffect(() => {
    if (!isNPL) return;
    // GSILENT_AT_LOAD is captured at MODULE scope: the app root strips
    // location.search on mount, so reading it live here could miss the bounce
    // marker and re-attempt forever. Belt-and-braces: sessionStorage allows ONE
    // silent attempt per tab session (a fresh visit/tab retries).
    const gs = GSILENT_AT_LOAD;
    const flagged = (() => { try { return localStorage.getItem('kalpa_staff_google') === '1'; } catch (e) { return false; } })();
    const tried = (() => { try { return sessionStorage.getItem('kalpa_gsilent_tried') === '1'; } catch (e) { return false; } })();
    if (gs === 'denied') {
      try { localStorage.removeItem('kalpa_staff_google'); } catch (e) { /* ignore */ }
      setStaffMode(true);
      setGMsg(T('That Google account is not on the staff allow-list — sign in with an authorised account, or use OTP.'));
    } else if (gs === 'failed') {
      setStaffMode(true);   // staff device, no live Google session — show the entry, no loop
    } else if (flagged && !isWf && !tried) {
      try { sessionStorage.setItem('kalpa_gsilent_tried', '1'); } catch (e) { /* ignore */ }
      setSilentBusy(true);
      window.location.replace(`${AUTH_BASE}/auth/login?silent=1`);
    }
  }, [isNPL, isWf]);

  // OTP login state. `phone` stays the single source of truth in E.164 ('+9779812345678');
  // `cc` is the country-code digits, DEFAULT '977' so the Nepal pilot UX is unchanged. Before this,
  // the country code was a hard-coded non-editable '+977' label AND the onChange unconditionally
  // re-prefixed +977, so a +1 number could not be typed at all: '+15551234567' became
  // '+9771555123456'. Seeded +1 demo accounts were therefore impossible to reach through the UI.
  // KAL-208 — the DEFAULT country code follows the instance's `country` flag rather than
  // being hard-coded to Nepal. On NPL this still resolves to '977', so the pilot UX is
  // unchanged; on US it opens on '1' instead of making the operator edit '977' to '1' as the
  // first act of every login. Editable either way — the flag picks the default, not the value.
  const [cc, setCc] = useState(
    ({ NP: '977', UK: '44', US: '1' })[FEATURES_GLOBAL.country] || '977');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const phoneRef = useRef(null);
  const otpRef = useRef(null);
  // ...and re-apply the country default ONCE when /api/features lands. The initialiser above
  // runs on first paint, when FEATURES_GLOBAL still holds its literal defaults (country:'UK')
  // because the fetch is async — so without this, US would open on '44' and stay there, since
  // a useState initialiser never runs again. Guarded by a ref and by "the user has not typed",
  // so it can never overwrite something Tim has entered.
  //
  // Declared AFTER the state it reads, deliberately: the same file already carries a comment
  // (App.js:24448) about an effect that referenced a `const` in its temporal dead zone and
  // threw at runtime. Reading `phone` from a closure defined above its declaration would be
  // legal here, but it is the identical shape and not worth repeating.
  const ccSynced = useRef(false);
  useEffect(() => {
    if (ccSynced.current || !FEATURES_GLOBAL.ready) return;
    ccSynced.current = true;
    const want = ({ NP: '977', UK: '44', US: '1' })[FEATURES_GLOBAL.country];
    if (want && !phone) setCc(want);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FEATURES_GLOBAL.ready, FEATURES_GLOBAL.country]);

  // ── KAL-211 — EMAIL + PASSWORD as the first step ──────────────────────────────────
  // Composes with the OTP form rather than replacing it: password proves who you are, the
  // code proves you are there. Its own flag, so it can be switched off and leave a working
  // OTP login rather than no login at all.
  //
  // The server does NOT return the phone after the password step (that would make an
  // unauthenticated response an identifier oracle), so from here on the client posts no
  // phone at all and the session supplies it.
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const pwMode = !!FEATURES_GLOBAL.password_login;

  const submitPassword = async () => {
    setError(null);
    if (!email.trim() || !pw) { setError('Enter your email and password.'); return; }
    setPwBusy(true);
    try {
      const r = await fetch(`${API_BASE}/auth/password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ email: email.trim(), password: pw }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || 'Those details do not match an account.');
      // Ask for the code immediately — the server knows which number, we do not.
      const r2 = await fetch(`${API_BASE}/auth/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({}),
      });
      const d2 = await r2.json().catch(() => ({}));
      if (!r2.ok || !d2.success) throw new Error(d2.error || 'Could not send your code.');
      setPw('');                      // never keep the password in state past this point
      setStep('otp');
      setTimeout(() => { try { otpRef.current && otpRef.current.focus(); } catch (e) { /* ignore */ } }, 60);
    } catch (e) {
      setError(e.message);
    } finally {
      setPwBusy(false);
    }
  };

  const requestOtp = async () => {
    setError(null);
    // Length is validated PER COUNTRY. Nepal keeps its exact-10 rule: guard the SHORT-entry
    // failure mode, where a 7-digit number passes a loose check, reaches the server and comes
    // back "not enrolled" — so require the full 10 digits with a length-specific message. Other
    // country codes get a permissive E.164 national range (ITU allows up to 15 digits total),
    // because we cannot know every national numbering plan and must not invent one.
    const nat = natOf(phone, cc);
    if (cc === '977') {
      if (nat.length !== 10) { setError('Enter your full 10-digit mobile number (e.g. 98XXXXXXXX)'); return; }
    } else if (nat.length < 4 || nat.length > 15 - cc.length) {
      setError(`Enter the full number after +${cc} (4\u2013${15 - cc.length} digits)`); return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/auth/request-otp`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        credentials: 'include', body: JSON.stringify({ phone }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || 'Could not send code');
      setStep('otp');
    } catch (e) {
      setError(e.message);
    }
    setSending(false);
  };

  // Accepts an explicit code (auto-submit / WebOTP fill) so we never read a
  // stale `otp` state value the same tick it was set.
  const verifyOtp = async (codeArg) => {
    const code = (typeof codeArg === 'string' ? codeArg : otp);
    setError(null);
    if (!code || code.length < 4) { setError('Enter the code from your SMS'); return; }
    if (verifying) return; // guard double-submit (auto-submit + button/Enter)
    setVerifying(true);
    try {
      const r = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: 'POST', headers: {'Content-Type': 'application/json'},
        // KAL-211 — in password mode the client has never been told the phone, so it sends
        // none and the server reads it from the session marker it armed itself.
        credentials: 'include',
        body: JSON.stringify(pwMode ? { otp: code } : { phone, otp: code }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || 'Invalid code');
      // Usage telemetry — flush synchronously before the reload drops the buffer.
      usageAnalytics.track('login_succeeded', { method: 'otp' });
      usageAnalytics.flush(true);
      // Force a reload so the App component re-checks auth and renders the
      // post-login chrome.
      window.location.reload();
    } catch (e) {
      setError(e.message);
      setVerifying(false); // only re-enable on failure; success reloads the page
    }
  };

  // OTP UX (best practice): auto-focus the active field on step change so the
  // user never has to tap into it; WebOTP auto-reads the SMS code on Android
  // Chrome; autocomplete="one-time-code" covers iOS SMS autofill.
  useEffect(() => {
    if (!isNPL) return;
    const t = setTimeout(() => {
      if (step === 'phone') phoneRef.current && phoneRef.current.focus();
      else if (step === 'otp') otpRef.current && otpRef.current.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [step, isNPL]);

  useEffect(() => {
    if (!isNPL || step !== 'otp') return;
    if (typeof window === 'undefined' || !('OTPCredential' in window) || !navigator.credentials) return;
    const ac = new AbortController();
    navigator.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then(c => {
        if (c && c.code) {
          const v = String(c.code).replace(/\D/g, '').slice(0, 6);
          setOtp(v);
          if (v.length === 6) verifyOtp(v); // auto-submit the SMS-read code
        }
      })
      .catch(() => { /* user dismissed / unsupported — silent; manual entry still works */ });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isNPL]);

  // P4 — mid-silent-redirect: show a calm one-liner instead of a flickering form.
  if (silentBusy) {
    return (
      <div className="login-page">
        <div className="login-card">
          <p className="login-hint">Signing you in with Google…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src={logoSrc} alt={appName} style={{height: isNPL ? '64px' : '100px', width:'auto', maxWidth:'100%', objectFit:'contain'}} />
        </div>
        <p className="login-tagline">{isNPL ? `${appName} · powered by Kalpa` : 'Health Analytics'}</p>

        {showOtp && !isWf && (
          <div className="npl-login-otp" style={{margin:'10px 0 16px', display:'flex', flexDirection:'column', gap:8}}>
            {/* KAL-211 — the first step is email+password where the flag is on, and the phone
                field where it is not. Rendered as a BRANCH on the existing 'phone' step rather
                than a new step value, because `step` would have to be initialised from
                FEATURES_GLOBAL and that hydrates after first paint — the same trap the country
                code hit above. */}
            {step === 'phone' && pwMode && (
              <>
                <label className="login-field-label" htmlFor="kal-email">Email</label>
                <input
                  id="kal-email"
                  type="email"
                  className="login-text-input"
                  placeholder="you@company.com"
                  value={email}
                  autoComplete="username"
                  autoFocus
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !pwBusy) submitPassword(); }}
                />
                <label className="login-field-label" htmlFor="kal-pw">Password</label>
                <input
                  id="kal-pw"
                  type="password"
                  className="login-text-input"
                  placeholder="Enter your password"
                  value={pw}
                  autoComplete="current-password"
                  onChange={e => setPw(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !pwBusy) submitPassword(); }}
                />
                <button
                  onClick={submitPassword}
                  disabled={pwBusy}
                  style={{background:'var(--accent)', color:'var(--on-accent)', border:'none', borderRadius:8, padding:'10px 14px', minHeight:44, fontSize:14, fontWeight:600, cursor:'pointer'}}
                >{pwBusy ? 'Checking…' : 'Continue'}</button>
                {/* There is no reset flow and this must not imply one. It says what is
                    actually true rather than offering a link that goes nowhere. */}
                <button type="button" className="login-forgot" onClick={() => setForgot(v => !v)}>
                  Forgot password?
                </button>
                {forgot && (
                  <div style={{fontSize:12, color:'var(--text-secondary)', lineHeight:1.5}}>
                    Passwords for this account are issued by your administrator. Contact your
                    Kalpa contact to have one re-issued.
                  </div>
                )}
              </>
            )}
            {step === 'phone' && !pwMode && (
              <>
                {/* The country code DEFAULTS to +977 (the Nepal pilot) but is EDITABLE, so a
                    non-Nepal number — e.g. a seeded +1 demo account — can actually be entered.
                    It was previously a hard-coded label with an onChange that re-prefixed +977
                    unconditionally, which made any other country code impossible to type. The
                    national part still strips spaces/dashes/'+', a pasted duplicate of the
                    country code, and a trunk-prefix leading zero. */}
                <div className="login-phone-row">
                  {/* KAL-127 — the '+' is a flex ITEM, and it used to inherit the default
                      flex-shrink:1 inside a row with overflow:hidden. On a narrow screen the
                      row squeezed it below its own glyph width and the parent clipped it, so
                      Bimal saw "977" / "-977". flex-shrink:0 is the fix; the class moves the
                      whole row into App.css, which is the CAUSE — this block was styled
                      inline, independently of every other input in the app, which is also why
                      it missed the KAL-121 contrast fix two days ago. */}
                  <span className="login-cc-plus" aria-hidden="true">+</span>
                  <input
                    type="tel"
                    value={cc}
                    onChange={e => {
                      const d = e.target.value.replace(/\D/g, '').slice(0, 4) || '';
                      const nat = natOf(phone, cc);
                      setCc(d);
                      setPhone(d && nat ? `+${d}${nat}` : '');
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && !sending) requestOtp(); }}
                    className="login-cc-input"
                    inputMode="numeric"
                    maxLength={4}
                    aria-label="Country code, digits only (default 977 for Nepal)"
                  />
                  <input
                    ref={phoneRef}
                    type="tel"
                    placeholder={cc === '977' ? 'e.g. 9812345678' : 'e.g. 5551234567'}
                    value={natOf(phone, cc)}
                    onChange={e => {
                      // Robust construction — always yields the correct E.164 number
                      // regardless of how the user types it:
                      let d = e.target.value.replace(/\D/g, '');       // drop spaces / dashes / '+'
                      if (cc && d.length > 4 && d.startsWith(cc)) d = d.slice(cc.length); // pasted country code
                      d = d.replace(/^0+/, '');                         // trunk-prefix leading zero(s)
                      d = d.slice(0, cc === '977' ? 10 : Math.max(4, 15 - cc.length));
                      setPhone(d ? `+${cc}${d}` : '');
                    }}
                    onKeyDown={e => { if (e.key === 'Enter' && !sending) requestOtp(); }}
                    className="login-phone-input"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={15}
                    autoFocus
                    aria-label={cc === '977' ? 'Nepal phone number, 10 digits' : `Phone number after +${cc}`}
                  />
                </div>
                <div style={{fontSize:12, color:'var(--text-secondary)', margin:'-2px 2px 0'}}>
                  {cc === '977'
                    ? <>Enter your 10-digit mobile number (starts 97 or 98) — no leading zero.</>
                    : <>Entering <b>+{cc}</b> — change the code on the left for another country. Number will be sent as <code>{phone || `+${cc}\u2026`}</code>.</>}
                </div>
                <button
                  onClick={requestOtp}
                  disabled={sending}
                  style={{background:'var(--accent)', color:'var(--on-accent)', border:'none', borderRadius:8, padding:'10px 14px', minHeight:44, fontSize:14, fontWeight:600, cursor:'pointer'}}
                >{sending ? 'Sending…' : 'Send code'}</button>
              </>
            )}
            {step === 'otp' && (
              <>
                {/* KAL-211 — in password mode the client was never told the number, so it
                    cannot echo it. Saying "your registered mobile" is the honest form. */}
                <div style={{fontSize:12, color:'var(--text-secondary)', marginBottom:2}}>
                  {pwMode ? 'We sent a code to your registered mobile number'
                          : `Code sent to ${phone}`}
                </div>
                <input
                  ref={otpRef}
                  type="text"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(v);
                    if (v.length === 6) verifyOtp(v); // auto-submit on full code (zero extra taps)
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') verifyOtp(); }}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  style={{padding:'10px 12px', border:'1px solid var(--border-color)', borderRadius:8,
                    fontSize:18, letterSpacing:'0.3em', textAlign:'center',
                    /* KAL-121 — this field declared NEITHER colour NOR background, so it
                       rendered in the UA's default box with the card's inherited light text.
                       This is the field Bimal could not read. */
                    background:'var(--bg-input)', color:'var(--text-primary)',
                    boxShadow:'0 0 0 1000px var(--bg-input) inset', WebkitBoxShadow:'0 0 0 1000px var(--bg-input) inset', 
                    WebkitTextFillColor:'var(--text-primary)', caretColor:'var(--text-primary)'}}
                />
                <button
                  onClick={verifyOtp}
                  disabled={verifying}
                  style={{background:'var(--accent)', color:'var(--on-accent)', border:'none', borderRadius:8, padding:'10px 14px', minHeight:44, fontSize:14, fontWeight:600, cursor:'pointer'}}
                >{verifying ? 'Verifying…' : 'Verify & sign in'}</button>
                <button
                  onClick={() => { setStep('phone'); setOtp(''); setError(null); setPw(''); }}
                  style={{background:'none', border:'none', color:'var(--text-dim)', fontSize:12, cursor:'pointer'}}
                >{pwMode ? 'Start again' : 'Change number'}</button>
              </>
            )}
            {error && <div style={{color:'var(--status-flagged)', fontSize:12}}>{error}</div>}
          </div>
        )}

        {/* Google sign-in: Kalpa (main) always; NPL only via /wf OR the discreet
            staff link below (needed in the app WebView, which can't navigate to
            /wf). The pilot DEFAULT view never shows it. Server-side the NPL
            google_allowlist is the boundary — a non-listed email gets 403 and no
            account is ever created (the old data-leak vector stays closed). */}
        {showGoogle && (
          <>
            <button className="google-login-btn" onClick={() => {
              // Mark this device as a staff-Google device so future visits
              // silent-auth straight in (cleared on an allow-list denial).
              try { localStorage.setItem('kalpa_staff_google', '1'); } catch (e) { /* ignore */ }
              window.location.href = `${AUTH_BASE}/auth/login`;
            }}>
              <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
              Sign in with Google
            </button>
            {isNPL
              ? <p className="login-hint">{T('Staff / WF access only — use your authorised Google account.')}</p>
              : <>
                  <p className="login-hint">Sign in with your Google account</p>
                  <p className="login-hint-sub">New to Kalpa? Your account is created on first sign-in.</p>
                </>}
            {gMsg && <p className="login-hint" style={{ color: 'var(--status-flagged, #F59E0B)' }}>{gMsg}</p>}
          </>
        )}
        {/* Discreet staff entry — reveals the Google option (same as /wf) on both
            web and the app WebView. Pilots who tap it are still rejected by the
            server allow-list; their OTP flow is untouched. */}
        {isNPL && !showGoogle && (
          <button type="button" className="login-staff-link" onClick={() => setStaffMode(true)}>
            Staff sign-in
          </button>
        )}
        <p className="login-footer">{isNPL ? 'Health insights for everyday wellness' : 'Increase longevity through data and knowledge'}</p>
      </div>
    </div>
  );
}

// -- Medical Vault (GCS Image Browser) -----------------------------------------
const DOC_TYPE_COLORS = {
  // Canonical title-cased categories (server now returns these — Parts 3/9).
  'Lab Report': '#112240', 'Consultation Note': '#818CF8', 'Prescription': '#F59E0B',
  'Referral': '#C9973A', 'Discharge Summary': '#ef4444', 'Ultrasound': '#8B6914',
  'Medical Image': '#8B6914', 'Screening Report': '#5EC8D8', 'Health Check': '#5EC8D8',
  'Genetic Panel': '#A78BFA', 'Other': '#8B6914',
  // Legacy snake_case (pre-canonicalisation) kept as a harmless fallback.
  blood_test: '#112240', lab_report: '#112240', consultation_note: '#818CF8',
  prescription: '#F59E0B', referral: '#C9973A', discharge_summary: '#ef4444',
  ultrasound: '#8B6914', xray: '#8B6914', radiology: '#8B6914',
  medical_document: '#8B6914', medical_image: '#8B6914',
};

// Run 5 §B — ONE shared document-list renderer used by BOTH the Health Record
// (#vault, self-reported docs) and Lab Upload (#staff-lab-upload, lab-verified docs).
// Purely presentational: each screen builds its own normalised `rows` (so the data
// logic — and the working delete/soft-delete/undo flow on the vault side — stays
// intact) and passes them here. Provenance itself is split SERVER-SIDE; this
// component only renders whatever the server already scoped. Consistent chips,
// Recently-Deleted panel, provenance tag, status pill, Preview and delete affordances.
// RUN6 §4 — ONE identical document-management component for BOTH #vault (self-reported) and
// #staff-lab-upload (lab-verified). Sortable table with named column headers, right-aligned
// data columns (default report-date newest-first), per-row Preview + soft-delete, and the
// Recently-Deleted / undo panel. ONLY the data differs (server-side provenance split); the
// controls are identical. Each row carries: key, badge, title, filename, date (display),
// sortDate (ISO for sorting), provider, source, statusText/statusKey, note, onPreview, onDelete.
// RUN10 §4 — per-row kebab "⋯" overflow menu (research: 3+ actions → overflow, not inline buttons).
// Menu items render only when the row supplies the handler: Preview, Analyse, Delete.
// RUN10 §5 — ONE shared multi-file upload-progress component for BOTH paths. Shows every file
// of N with its per-file state (queued → parsing → ready/failed), so a 2-file upload can never
// show only 1 with no idea which is processing. data-testid="upload-progress".
function MultiFileProgress({ items }) {
  if (!items || !items.length) return null;
  const lbl = { pending: 'Queued', parsing: 'Parsing…', analysed: 'Ready to review', failed: 'Failed' };
  return (
    <div className="multifile-progress" data-testid="upload-progress">
      {items.map((it, i) => (
        <div key={i} className="multifile-row">
          <span className="multifile-name" title={it.name}>{it.name}</span>
          <span className={`multifile-state st-${it.state}`}>{lbl[it.state] || it.state}</span>
        </div>
      ))}
    </div>
  );
}
function RowKebab({ onPreview, onAnalyse, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [open]);
  const act = (fn) => (e) => { e.stopPropagation(); setOpen(false); if (fn) fn(); };
  if (!onPreview && !onAnalyse && !onDelete) return null;
  return (
    <span className="docmgr-kebab-wrap" ref={ref}>
      <button className="docmgr-kebab" aria-label="Row actions" aria-haspopup="menu" aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>⋯</button>
      {open && (
        <div className="docmgr-kebab-menu" role="menu">
          {onPreview && <button role="menuitem" onClick={act(onPreview)}>Preview</button>}
          {onAnalyse && <button role="menuitem" onClick={act(onAnalyse)}>{T('Analyse')}</button>}
          {onDelete && <button role="menuitem" className="danger" onClick={act(onDelete)}>Delete</button>}
        </div>
      )}
    </span>
  );
}
function SharedDocList({
  rows, filters, activeFilter, onFilter,
  recentlyDeleted, showDeleted, onToggleDeleted, onRestore, daysLeft,
  emptyText, uploadSlot, showSource = true,
}) {
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');   // report-date newest-first by default
  const onSort = (k) => {
    if (k === sortKey) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(k); setSortDir(k === 'date' ? 'desc' : 'asc'); }
  };
  const _cmp = (a, b) => {
    let av, bv;
    if (sortKey === 'date') { av = a.sortDate || a.date || ''; bv = b.sortDate || b.date || ''; }
    else if (sortKey === 'title') { av = (a.title || '').toLowerCase(); bv = (b.title || '').toLowerCase(); }
    else if (sortKey === 'provider') { av = (a.provider || '').toLowerCase(); bv = (b.provider || '').toLowerCase(); }
    else if (sortKey === 'source') { av = a.source || ''; bv = b.source || ''; }
    else if (sortKey === 'status') { av = a.statusText || ''; bv = b.statusText || ''; }
    else { av = a.type || (a.badge && a.badge.label) || ''; bv = b.type || (b.badge && b.badge.label) || ''; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  };
  const sorted = [...rows].sort(_cmp);
  const arrow = (k) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const SortableTh = ({ k, label, align, w }) => (
    <th className={`docmgr-th docmgr-${align || 'left'}`} style={w ? { width: w } : undefined} aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="docmgr-sort" onClick={() => onSort(k)}>{label}{arrow(k)}</button>
    </th>
  );
  return (
    <>
      {filters && filters.length > 0 && (
        <div className="vault-filters">
          {filters.map(f => (
            <button key={f.key} className={`vault-filter-btn ${activeFilter === f.key ? 'active' : ''}`} onClick={() => onFilter && onFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {uploadSlot}

      {recentlyDeleted && recentlyDeleted.length > 0 && (
        <div className="vault-recently-deleted">
          <button className="vault-rd-toggle" onClick={() => onToggleDeleted && onToggleDeleted()}>
            🗑 Recently Deleted ({recentlyDeleted.length}) {showDeleted ? '▲' : '▼'}
          </button>
          {showDeleted && (
            <div className="vault-rd-list">
              {recentlyDeleted.map(d => (
                <div key={d.id} className="vault-rd-row">
                  <div className="vault-rd-main">
                    <div className="vault-rd-name">{d.filename || d.doc_type || `Document ${d.id}`}</div>
                    <div className="vault-rd-meta">{formatDate(d.draw_date) || ''} · {(daysLeft ? daysLeft(d.deleted_at) : 30)} day{(daysLeft ? daysLeft(d.deleted_at) : 30) === 1 ? '' : 's'} left</div>
                  </div>
                  <button className="vault-rd-restore" onClick={() => onRestore && onRestore(d.id)}>Restore</button>
                </div>
              ))}
              <div className="vault-rd-note">Removed documents are kept for 30 days, then cleared. Restore anytime before then.</div>
            </div>
          )}
        </div>
      )}

      <div className="docmgr-tablewrap">
        <table className="docmgr-table">
          <thead>
            <tr>
              {/* RUN10 §4 — table-layout:fixed: Document (no width) absorbs remaining space + truncates;
                  the rest are fixed-narrow so the table fits the container (no horizontal scroll). */}
              {/* RUN10.2 §1 — Type widened 54→92 so "Lab Report" fits without truncation; the
                  inline-block badge (CSS) clips within this width so it can never overlap Document. */}
              <th className="docmgr-th docmgr-left docmgr-typecell" style={{ width: 92 }}>Type</th>
              <SortableTh k="title" label="Document" align="left" />
              {/* RUN11.1 §2 — Date 78→100 fits "DD MMM YYYY"; Lab 58→84 fits "NPL"/"Bluecrest"
                  (extra-long provider names still ellipsis+tooltip). Width taken from the flex
                  Document column, which truncates + shows a full-name tooltip. */}
              <SortableTh k="date" label="Date" align="right" w={100} />
              <SortableTh k="provider" label="Lab" align="right" w={84} />
              {showSource && <SortableTh k="source" label="Source" align="right" w={92} />}
              <SortableTh k="status" label="Status" align="right" w={106} />
              <th className="docmgr-th docmgr-right" style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.key} className="docmgr-row">
                <td className="docmgr-left docmgr-typecell">
                  {/* RUN10.1 §3 — badge text colour is theme-aware (var(--text-primary), CSS) for
                      WCAG-AA contrast; brand colour is a bg tint + border for coding only. */}
                  {r.badge && (
                    <span className="vault-type-badge" title={r.badge.label} style={{ background: (r.badge.color || '#8B6914') + '2E', borderColor: (r.badge.color || '#8B6914') + '66' }}>
                      {r.badge.label}
                    </span>
                  )}
                </td>
                <td className="docmgr-left docmgr-doccell">
                  {/* RUN10 §4 — single-line ellipsis truncation (never wrap) + full-name tooltip. */}
                  <div className="vault-list-title docmgr-trunc" title={r.filename || r.title}>{r.title}</div>
                  {r.note && <div style={{ color: '#F59E0B', fontSize: 11 }}>{r.note}</div>}
                </td>
                <td className="docmgr-right docmgr-data" title={formatDate(r.date) || ''}>{formatDate(r.date) || '—'}</td>
                {/* RUN11.1 §2 — show a SHORT lab label (first token: "Bluecrest Wellness"→"Bluecrest",
                    "NPLRC LALITPUR"→"NPLRC") so it fits the column un-truncated; full name in tooltip. */}
                <td className="docmgr-right docmgr-data" title={r.provider || ''}>{(r.provider || '').trim().split(/\s+/)[0] || '—'}</td>
                {showSource && (
                  <td className="docmgr-right docmgr-data">
                    {r.source && (
                      <span className={`prov-tag prov-${r.source === 'Lab-verified' ? 'lab' : 'self'}`}>
                        {r.source === 'Lab-verified' ? '🛡 Lab-verified' : '👤 Self-reported'}
                      </span>
                    )}
                  </td>
                )}
                <td className="docmgr-right docmgr-data">
                  {r.statusText && <span className={`status-pill status-${(r.statusKey || '').toLowerCase()}`}>{r.statusText}</span>}
                </td>
                <td className="docmgr-right docmgr-actions">
                  {/* RUN10 §4 — kebab overflow menu (Preview / Analyse / Delete). Makes Pending
                      actionable (Analyse re-parses into the shared review) instead of delete-only. */}
                  <RowKebab onPreview={r.onPreview} onAnalyse={r.onAnalyse} onDelete={r.onDelete} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sorted.length === 0 && <div className="vault-empty">{emptyText || 'No documents match this filter'}</div>}
      </div>
    </>
  );
}

function MedicalVault({ onBack }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);        // real byte-upload % (4A)
  const [uploadPhase, setUploadPhase] = useState(null);  // 'uploading' | 'analysing'
  const [uploadMsg, setUploadMsg] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);  // Stage 6b: doc awaiting delete confirm
  const [deleting, setDeleting] = useState(false);
  // Section C — document management wired to the live /api/documents endpoints.
  const [docMeta, setDocMeta] = useState({});     // doc id -> {source,status,marker_count,deleted,deleted_at}
  const [undoDoc, setUndoDoc] = useState(null);   // {id,filename} for the undo toast (soft-delete)
  const [showDeleted, setShowDeleted] = useState(false);  // Recently Deleted (30-day) view
  const undoTimer = useRef(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  // RUN10 §2 — #vault now uses the SAME parse→review→commit flow as staff Lab Upload.
  const [panels, setPanels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [progressFiles, setProgressFiles] = useState([]);  // RUN10 §5 — per-file upload state
  const { pulling, pullDistance } = usePullToRefresh(() => loadFiles(), scrollRef);

  const loadFiles = useCallback(async () => {
    setLoading(true); setError(null);
    let fileList = [];
    try {
      const data = await api.get('/vault/files');
      fileList = data.files || [];
    } catch (e) { setError(e.message); }
    // Section C — enrich with the live management list (provenance/status/deleted).
    let docs = [];
    try { docs = (await api.get('/documents')) || []; }
    catch (e) { console.warn('[documents] meta load failed', e); }
    // RUN10.1 §1 — DE-DUPE. medical_documents has no UNIQUE constraint, so re-uploads/re-parses
    // create multiple rows per file → the list showed the same doc twice, and a stray un-committed
    // parse-stub (Pending/Failed) appeared next to the committed (Verified) row. Build meta keyed by
    // FILENAME, keeping the BEST row (Verified>Parsed>Pending>Failed, then most markers), so one
    // document = one row with the correct status.
    const RANK = { Verified: 4, Parsed: 3, Pending: 2, Duplicate: 1, Failed: 1, Deleted: 0 };
    const metaByName = {};
    docs.forEach(d => {
      if (d.deleted) return;
      const k = d.filename; const cur = metaByName[k];
      const dr = RANK[d.status] || 0, cr = cur ? (RANK[cur.status] || 0) : -1;
      if (!cur || dr > cr || (dr === cr && (d.marker_count || 0) > (cur.marker_count || 0))) metaByName[k] = d;
    });
    setDocMeta(metaByName);   // keyed by FILENAME (was id)
    // Merge any DB doc (by filename) the GCS listing hasn't surfaced yet (RUN9 lag fix).
    const haveNames = new Set(fileList.map(f => f.filename).filter(Boolean));
    Object.values(metaByName).forEach(d => {
      if (haveNames.has(d.filename)) return;
      haveNames.add(d.filename);
      fileList.push({
        path: d.source_path || null, filename: d.filename, indexed: (d.marker_count || 0) > 0,
        index_data: { id: d.id, doc_type: d.doc_type, summary: null, doc_date: d.draw_date, provider: d.provider },
      });
    });
    // Final safety: one row per filename (GCS listing itself is one blob per name).
    const seenN = new Set();
    fileList = fileList.filter(f => { const k = f.filename; if (seenN.has(k)) return false; seenN.add(k); return true; });
    setFiles(fileList);
    setLoading(false);
  }, []);

  // Section C — plain-language parse status for the card pill.
  // RUN10.1 §2 — CONCISE status labels that fit the Status column without truncation.
  const _docStatusText = (m) => {
    if (!m) return null;
    const n = m.marker_count || 0;
    switch (m.status) {
      case 'Verified': return `Verified · ${n}`;
      case 'Parsed':   return `Parsed · ${n}`;
      case 'Pending':  return 'Processing';
      case 'Duplicate':return 'Duplicate';
      case 'Failed':   return "Couldn't read";
      default:         return m.status || null;
    }
  };
  // Section C — reversible soft-delete + undo toast (wired to the live audited backend).
  const softDeleteDoc = async (file) => {
    const id = file.index_data && file.index_data.id;
    if (!id) return;
    setFiles(prev => prev.filter(f => !(f.index_data && f.index_data.id === id)));  // optimistic
    setUndoDoc({ id, filename: file.filename });
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => setUndoDoc(null), 7000);
    try {
      await api.post(`/documents/${id}/soft-delete`, {});
      try { localStorage.removeItem('kalpa_today_cache_v1'); window.dispatchEvent(new Event('kalpa:labs-updated')); } catch (e2) {}
      loadFiles();
    } catch (e) { setError(`Remove failed: ${e.message}`); setUndoDoc(null); loadFiles(); }
  };
  const undoDelete = async () => {
    if (!undoDoc) return;
    const id = undoDoc.id; setUndoDoc(null);
    if (undoTimer.current) clearTimeout(undoTimer.current);
    try { await api.post(`/documents/${id}/restore`, {}); }
    catch (e) { setError(`Undo failed: ${e.message}`); }
    loadFiles();
  };
  const restoreDoc = async (id) => {
    try { await api.post(`/documents/${id}/restore`, {}); }
    catch (e) { setError(`Restore failed: ${e.message}`); }
    loadFiles();
  };
  const _daysLeft = (deleted_at) => {
    if (!deleted_at) return 30;
    try { const d = Math.floor((Date.now() - new Date(deleted_at.replace(' ', 'T')).getTime()) / 86400000); return Math.max(0, 30 - d); }
    catch (e) { return 30; }
  };
  const recentlyDeleted = Object.values(docMeta).filter(d => d && d.deleted);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  // RUN10 §2 — shared review-panel row handlers (identical to StaffLabUpload).
  const updateRow = (pi, ri, k, v) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, rows: p.rows.map((r, x) => x === ri ? { ...r, [k]: v } : r) } : p));
  const removeRow = (pi, ri) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, rows: p.rows.filter((_, x) => x !== ri) } : p));
  const addRow = (pi) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, rows: [...p.rows, { name: '', value: '', unit: '', date: p.test_date, plausible: true, warn: null, needs_review: false }] } : p));
  const setPanelDate = (pi, v) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, test_date: v } : p));
  const removePanel = (pi) => setPanels(ps => ps.filter((_, j) => j !== pi));
  const totalMarkers = panels.reduce((n, p) => n + p.rows.filter(r => (r.name || '').trim() && String(r.value).trim() !== '').length, 0);

  // Shared SSE reader for the vault parse-stream (used by a fresh file upload AND the kebab
  // "Analyse" re-parse of a stored doc). Returns a review panel.
  const _vaultParse = async (fd, rep, fallbackName) => {
    const resp = await fetch('/api/vault/parse-stream', { method: 'POST', body: fd, credentials: 'include' });
    if (!resp.ok) {
      // A1 — surface the server's specific per-file rejection (magic-byte / size /
      // type) rather than a bare HTTP code; uploadFiles collects it per file.
      let msg = `HTTP ${resp.status}`;
      try { const j = await resp.json(); if (j && j.error) msg = j.error; } catch (e) {}
      throw new Error(msg);
    }
    if (!resp.body) throw new Error(`HTTP ${resp.status}`);
    const reader = resp.body.getReader(); const dec = new TextDecoder();
    let buf = '', final = null, streamErr = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
        const dl = chunk.split('\n').find(l => l.startsWith('data:'));
        if (!dl) continue;
        let evt; try { evt = JSON.parse(dl.slice(5).trim()); } catch { continue; }
        if (evt.phase === 'done') final = evt.result;
        else if (evt.phase === 'error') streamErr = evt.error;
        else setProgress(`${rep} — ${LAB_PHASE_LABELS[evt.phase] || evt.phase}`);
      }
    }
    if (streamErr) throw new Error(streamErr);
    if (!final) throw new Error('No result received from the parser');
    const d = final;
    return {
      id: `${fallbackName}-${Date.now()}`, filename: d.filename || fallbackName, test_date: d.test_date || '',
      method: d.method, read_failed: d.read_failed, warning: d.warning,
      // A3 — doc-intel: non-blood documents carry a kind + interpretation so the
      // review shows their summary, not an empty marker grid. blood → kind 'blood'.
      kind: d.kind || 'blood', doc_type: d.doc_type || null,
      interpretation: d.interpretation || null, needs_review: !!d.needs_review,
      needs_manual: !!d.needs_manual,
      rows: (d.markers || []).map(m => ({ name: m.name, value: m.value, unit: m.unit, date: m.date || d.test_date, plausible: m.plausible !== false, warn: m.warn, needs_review: !!m.needs_review, review_reason: m.review_reason, reference_range: m.reference_range })),  // RUN11.4 §1
      bp: d.bp || [],
    };
  };
  const parseFileVault = async (file, rep) => {
    const fd = new FormData(); fd.append('file', file);
    return _vaultParse(fd, rep, file.name);
  };
  // RUN10 §4 — kebab "Analyse": re-parse an ALREADY-stored doc (by GCS path) into the SAME review.
  const reanalyseDoc = async (file) => {
    if (!file.path) return;
    setBusy(true); setUploading(true); setError(null); setUploadMsg(null); setProgress('Re-analysing…');
    try {
      const fd = new FormData(); fd.append('path', file.path);
      const panel = await _vaultParse(fd, T('Re-analysing'), file.filename || 'document');
      setPanels([panel]);
    } catch (e) { setError(T(`Analyse failed: ${e.message}`)); }
    finally { setBusy(false); setUploading(false); setProgress(''); setUploadPhase(null); }
  };

  // RUN10 §2 — multi-file: parse each file into a review panel (shared progress), THEN review,
  // THEN commit as self-reported. Replaces the old silent auto-save.
  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true); setUploading(true); setError(null); setUploadMsg(null); setUploadPhase('ingest');
    setProgressFiles(list.map(f => ({ name: f.name, state: 'pending' })));
    const mark = (i, state) => setProgressFiles(pf => pf.map((x, j) => j === i ? { ...x, state } : x));
    const out = []; const errs = [];
    for (let i = 0; i < list.length; i++) {
      const rep = `Report ${i + 1} of ${list.length}`;
      setProgress(`${rep} — Starting…`); mark(i, 'parsing');
      try { out.push(await parseFileVault(list[i], rep)); mark(i, 'analysed'); }
      catch (e) { errs.push(`${list[i].name}: ${e.message}`); mark(i, 'failed'); }
    }
    setPanels(out); setProgress(''); setProgressFiles([]);
    if (errs.length) setError('Some files could not be parsed: ' + errs.join('; '));
    // Usage telemetry — activation event. Count only (no filenames/contents).
    if (out.length) usageAnalytics.track('document_uploaded', { source: 'vault', count: out.length });
    setBusy(false); setUploading(false); setUploadPhase(null);
  };

  const doCommitVault = async () => {
    setBusy(true); setError(null);
    let mk = 0, bp = 0, filesDone = 0, docsDone = 0; const errs = [];
    for (let pi = 0; pi < panels.length; pi++) {
      const p = panels[pi];
      setProgress(`Saving ${pi + 1}/${panels.length}: ${p.filename}…`);
      // A3 — non-blood documents (imaging / clinical_note / other) are already
      // persisted with their correct type + interpretation at parse time; there
      // are no markers to commit, so confirm just counts them as saved.
      if (p.kind && p.kind !== 'blood') { docsDone++; continue; }
      const markers = p.rows.filter(r => (r.name || '').trim() && String(r.value).trim() !== '')
        .map(r => ({ name: r.name.trim(), value: r.value, unit: (r.unit || '').trim(), date: r.date || p.test_date, reference_range: r.reference_range || '' }));  // RUN11.4 §1 — carry the lab's printed range to commit
      if (!markers.length && !(p.bp || []).length) continue;
      try {
        const d = await api.post('/vault/commit', { markers, bp: p.bp || [], test_date: p.test_date, filename: p.filename });
        mk += d.markers_saved || 0; bp += d.bp_saved || 0; filesDone++;
      } catch (e) { errs.push(`${p.filename}: ${e.message}`); }
    }
    setProgress('');
    if (errs.length) { setError('Some reports failed: ' + errs.join('; ')); }
    else {
      const parts = [];
      if (filesDone) parts.push(`${filesDone} report${filesDone === 1 ? '' : 's'} · ${mk} marker${mk === 1 ? '' : 's'}`);
      if (docsDone) parts.push(`${docsDone} document${docsDone === 1 ? '' : 's'}`);
      setUploadMsg(`✓ ${parts.join(' + ') || 'Nothing'} saved to your record`);
      setPanels([]); setFiles([]);
    }
    await loadFiles();
    try { localStorage.removeItem('kalpa_today_cache_v1'); window.dispatchEvent(new Event('kalpa:labs-updated')); } catch (e) {}
    setBusy(false);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length) uploadFiles(files);
  };

  const viewFile = async (file) => {
    setSelectedFile(file); setFileContent(null);
    try {
      const data = await api.get(`/vault/file/${encodeURIComponent(file.path)}`);
      setFileContent(data);
    } catch (e) { setFileContent({ error: e.message }); }
  };

  const analyzeFile = async (path) => {
    setAnalyzing(true);
    try {
      const profileMatch = path.match(/^Profiles\/([^/]+)\//);
      const fileProfile = profileMatch ? profileMatch[1] : null;
      const result = await api.post('/vault/analyze', { path, profile: fileProfile });
      await loadFiles();
      if (selectedFile && selectedFile.path === path) {
        setSelectedFile(prev => ({ ...prev, indexed: true,
          index_data: { doc_type: result.doc_type, doc_date: result.doc_date,
                        provider: result.provider, summary: result.summary } }));
      }
    } catch (e) { setError(e.message); }
    finally { setAnalyzing(false); }
  };

  // Stage 6b — delete a document AND its linked markers (confirmed first).
  const doDeleteDocument = async () => {
    const f = pendingDelete;
    if (!f?.index_data?.id) { setPendingDelete(null); return; }
    setDeleting(true);
    try {
      const res = await api.del('/vault/document', { document_id: f.index_data.id });
      setPendingDelete(null);
      await loadFiles();
      setUploadMsg(`✓ Removed ${f.filename}${res.markers_deleted ? ` and its ${res.markers_deleted} linked marker${res.markers_deleted === 1 ? '' : 's'}` : ''}.`);
      try { localStorage.removeItem('kalpa_today_cache_v1'); window.dispatchEvent(new Event('kalpa:labs-updated')); } catch (e) {}
    } catch (e) { setError(`Delete failed: ${e.message}`); }
    finally { setDeleting(false); }
  };

  const docTypes = [...new Set(files.filter(f => f.index_data).map(f => f.index_data.doc_type))];
  const filtered = files.filter(f => {
    // Section C — hide soft-deleted docs from the main list (they live in Recently
    // Deleted). /vault/files is a GCS listing unaware of the DB deleted_at, so filter here.
    const m = docMeta[f.index_data && f.index_data.id];
    if (m && m.deleted) return false;
    if (filter === 'all') return true;
    if (filter === 'indexed') return f.indexed;
    if (filter === 'unindexed') return !f.indexed;
    return f.index_data && f.index_data.doc_type === filter;
  });

  if (loading) return <div className="vault-page"><LoadingSpinner message="Loading Documents..." /></div>;

  return (
    <div className="vault-page" ref={scrollRef}>
      {pulling && <div className="pull-refresh-indicator" style={{opacity: pullDistance/60}}>
        {pullDistance > 60 ? 'Release to refresh...' : 'Pull to refresh...'}
      </div>}
      {/* Documents lives nested under Health Record — offer a way back to it
          on every surface (web, mobile web, native WebView). */}
      {onBack && (
        <button onClick={onBack}
          style={{background:'none',border:'none',color:'var(--accent-violet, #818CF8)',cursor:'pointer',fontSize:'0.85rem',padding:'4px 0',marginBottom:'4px'}}>
          {'←'} Health Record
        </button>
      )}
      <div className="page-header">
        <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Documents</h2>
        <span style={{fontSize:'0.75rem',color:'#8B6914'}}>{files.length} document{files.length !== 1 ? 's' : ''}</span>
      </div>

      {/* RUN10 §2 — after parse, the SHARED review/flags UI (same component as staff Lab Upload);
          commit writes SELF-REPORTED. Upload zone hidden while reviewing. */}
      {panels.length > 0 && (
        <DocReviewPanels
          panels={panels} updateRow={updateRow} removeRow={removeRow} addRow={addRow}
          setPanelDate={setPanelDate} removePanel={removePanel}
          busy={busy} progress={progress} doCommit={doCommitVault}
          onCancel={() => { setPanels([]); setFiles([]); }}
          reviewName={null} totalMarkers={totalMarkers}
          commitLabel={`Confirm & save ${panels.length} item${panels.length === 1 ? '' : 's'} as self-reported`} />
      )}

      {/* Upload zone (hidden while reviewing) */}
      {panels.length === 0 && (
      <div
        className={`vault-upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" style={{display:'none'}}
          onChange={e => { const fs = e.target.files; if (fs && fs.length) uploadFiles(fs); e.target.value = ''; }} />
        {uploading ? (() => {
          // Live 7-phase stepper driven by REAL SSE events (uploadPhase = current key).
          const _i = Math.max(0, LAB_PHASE_SEQUENCE.indexOf(uploadPhase));
          const _pct = Math.round(((_i + 1) / LAB_PHASE_SEQUENCE.length) * 100);
          return (
          <div className="vault-upload-progress">
            <div className="vault-upload-label">{LAB_PHASE_LABELS[uploadPhase] || 'Working…'}</div>
            <div className="vault-progress-track">
              <div className="vault-progress-fill" style={{ width: `${_pct}%` }} />
            </div>
            <div className="vault-upload-hint">Step {_i + 1} of {LAB_PHASE_SEQUENCE.length} · {T('analysing your document live')}</div>
          </div>
          );
        })() : (
          <><div className="vault-upload-icon" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 15V4" /><path d="M7.5 8.5 12 4l4.5 4.5" /><path d="M5 15v2.5A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5V15" />
            </svg>
          </div>
          <div className="vault-upload-label">Drop files or tap to upload</div>
          <div className="vault-upload-hint">PDF, JPG, PNG · you can select multiple files · reviewed before saving</div></>
        )}
      </div>
      )}

      {/* RUN10 §5 — shared per-file multi-upload progress. */}
      {progressFiles.length > 0 && <MultiFileProgress items={progressFiles} />}

      {error && <div className="vault-error">{error} <button className="notif-dismiss" onClick={() => setError(null)}>&times;</button></div>}
      {uploadMsg && <div className="vault-upload-success" style={{background:'rgba(34,197,94,0.16)', color:'#86efac', border:'1px solid rgba(34,197,94,0.45)', padding:'9px 12px', borderRadius:8, fontSize:13, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10}}>{uploadMsg} <button className="notif-dismiss" onClick={() => setUploadMsg(null)} style={{background:'none', border:'none', color:'inherit', cursor:'pointer', fontSize:18, lineHeight:1}}>&times;</button></div>}

      {/* Filters + Recently-Deleted + Doc list — via the shared renderer (Run 5 §B). */}
      {/* RUN10 §4 — #vault is self-reported ONLY (server-filtered), so the Source column is
          redundant here too; dropping it also helps the table fit (no horizontal scroll). */}
      <SharedDocList
        showSource={false}
        filters={['all', 'indexed', 'unindexed', ...docTypes].map(f => ({
          key: f, label: f === 'all' ? 'All' : f === 'indexed' ? 'Indexed' : f === 'unindexed' ? 'Pending' : f.replace(/_/g, ' '),
        }))}
        activeFilter={filter}
        onFilter={setFilter}
        recentlyDeleted={recentlyDeleted}
        showDeleted={showDeleted}
        onToggleDeleted={() => setShowDeleted(s => !s)}
        onRestore={restoreDoc}
        daysLeft={_daysLeft}
        rows={filtered.map(file => {
          const dtype = file.index_data?.doc_type || 'medical_document';
          const color = DOC_TYPE_COLORS[dtype] || '#8B6914';
          // A3 — date fallback chain: content doc_date → GCS upload date, so an
          // undated imaging file (e.g. a dental x-ray) still gets "{Type} · {Date}"
          // and never falls back to a raw filename with no metadata.
          const dispDate = file.index_data?.doc_date || file.created || null;
          // RUN11.1 §3 — derived meaningful title (NOT the junk summary string).
          const title = _docDisplayTitle({ provider: file.index_data?.provider, docType: file.index_data?.doc_type, date: dispDate, filename: file.filename });
          // RUN10.1 §1 — meta keyed by FILENAME (best-status row), so status is correct and a
          // duplicate/stub row can't override it. Delete targets the best row's id.
          const m = docMeta[file.filename];
          const delId = (m && m.id) || (file.index_data && file.index_data.id);
          return {
            key: file.path || file.filename,
            badge: { label: dtype.replace(/_/g, ' '), color },
            title, filename: file.filename,
            date: dispDate, sortDate: dispDate, provider: file.index_data?.provider,
            source: m ? m.source : null,
            statusText: m ? _docStatusText(m) : null,
            statusKey: m ? m.status : '',
            note: (!m && !file.indexed) ? T('Not yet analysed') : null,
            onPreview: file.path ? () => viewFile(file) : null,   // RUN9 §4 — no path (GCS lag) → no preview yet
            onAnalyse: file.path ? () => reanalyseDoc(file) : null,   // RUN10 §4 — re-parse into the review
            onDelete: delId ? () => softDeleteDoc({ ...file, index_data: { ...file.index_data, id: delId } }) : null,
          };
        })}
      />

      {/* Stage 6b — delete confirmation stating the document AND its N linked markers */}
      {pendingDelete && (
        <div onClick={() => !deleting && setPendingDelete(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: 'var(--secondary-bg, #10182b)', border: '1px solid var(--border-color, #334155)', borderRadius: 12, padding: 20, maxWidth: 420, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #f1f5f9)', marginBottom: 10 }}>Delete this document?</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary, #cbd5e1)', lineHeight: 1.5, marginBottom: 16 }}>
              This permanently removes <strong>{pendingDelete.filename}</strong>
              {(pendingDelete.index_data?.linked_marker_count > 0)
                ? <> and the <strong>{pendingDelete.index_data.linked_marker_count} marker{pendingDelete.index_data.linked_marker_count === 1 ? '' : 's'}</strong> extracted from it.</>
                : <> . No saved markers are linked to it.</>}
              {' '}This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button disabled={deleting} onClick={() => setPendingDelete(null)}
                      style={{ fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border-color, #334155)', background: 'transparent', color: 'var(--text-secondary, #cbd5e1)', cursor: 'pointer' }}>Cancel</button>
              <button disabled={deleting} onClick={doDeleteDocument}
                      style={{ fontSize: 13, fontWeight: 700, padding: '9px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: deleting ? 'wait' : 'pointer' }}>
                {deleting ? 'Deleting…' : 'Delete document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section C — undo toast for the reversible, audited soft-delete (5–8s). */}
      {undoDoc && (
        <div className="vault-undo-toast">
          <span>Removed from your documents.</span>
          <button className="vault-undo-btn" onClick={undoDelete}>Undo</button>
        </div>
      )}

      {/* Preview modal — Phase A: history-routed (see VaultPreviewModal) so the
          Android hardware Back / browser Back closes the preview and returns to
          the Documents list rather than over-popping to Health Record. */}
      {selectedFile && (
        <VaultPreviewModal
          file={selectedFile}
          content={fileContent}
          analyzing={analyzing}
          onAnalyze={analyzeFile}
          onClose={() => setSelectedFile(null)}
        />
      )}
    </div>
  );
}

// Documents file-preview overlay. Routed through useModalHashRoute('#vault-doc')
// so hardware/browser Back closes JUST the preview (popstate → up one level) and
// lands back on the Documents list — the same proven pattern the metric/activity
// drawers use. The X, Done and backdrop all call the same canonical close
// handler so the URL hash never drifts out of sync.
function VaultPreviewModal({ file, content, analyzing, onAnalyze, onClose }) {
  const close = useModalHashRoute('#vault-doc', onClose);
  return (
    <div className="modal-overlay" onClick={close}>
      <div className="vault-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="vault-detail-header">
          <h3>{file.filename}</h3>
          <button className="notif-close-btn" onClick={close}>&times;</button>
        </div>
        {content && content.type === 'image' && (
          <div className="vault-image-container">
            <img src={`data:${content.mime};base64,${content.data}`} alt={file.filename} />
          </div>
        )}
        {content && content.type === 'pdf' && (
          <div className="vault-pdf-text"><pre>{content.text}</pre></div>
        )}
        {file.index_data && (
          <div className="vault-analysis-section">
            <h4>Analysis</h4>
            <div className="vault-meta-row">
              <span>Type: {file.index_data.doc_type.replace(/_/g, ' ')}</span>
              <span>Date: {formatDate(file.index_data.doc_date) || 'Unknown'}</span>
              {file.index_data.provider && <span>Provider: {file.index_data.provider}</span>}
            </div>
            <p className="vault-summary">{file.index_data.summary}</p>
          </div>
        )}
        <div className="vault-detail-actions">
          {file.path && !file.indexed && (
            <button className="action-btn" onClick={() => onAnalyze(file.path)} disabled={analyzing}>
              {analyzing ? T('Analysing...') : T('Analyse & Import')}
            </button>
          )}
          <button className="action-btn secondary" onClick={close}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── (#1377) THE canonical moderate/vigorous zone definition ──────────────────
// ONE source for how Kalpa defines a moderate and a vigorous minute, so every surface that
// shows an AZM figure can state the SAME rule and the number stops being unfalsifiable.
// Transcribed from the code that actually computes it — backend/active_minutes.py:
//   _ZONE_FATBURN_FRAC 0.50 / _ZONE_CARDIO_FRAC 0.70 / _ZONE_PEAK_FRAC 0.85 (fractions of max HR)
//   _max_hr_from_dob() → 220 − age, _MAX_HR_FALLBACK 185 when the DOB is absent
//   MODERATE_PT_PER_MIN 1 / VIGOROUS_PT_PER_MIN 2  (peak is still ×2, same as cardio)
//   STEPS_SEDENTARY_FLOOR 4000 / STEPS_PER_MODERATE_MIN 100
//   day_points() = MAX(zone points, step points) — NOT their sum (commit 2e81ab5)
// If any of those constants change, change this copy in the SAME commit. Deliberately describes
// only what the code implements — no guideline is quoted that the engine does not apply.
const AZM_ZONE_DEF_SHORT =
  'Moderate = 50–69% of your max HR (counts ×1) · Vigorous = 70%+ (counts ×2) · max HR ≈ 220 − your age';
const AZM_ZONE_DEF_LONG =
  'Active Zone Minutes measure time spent in an elevated heart-rate zone. The zone boundaries are '
  + 'set from your age-predicted maximum heart rate (max HR ≈ 220 − your age; where a date of birth '
  + 'isn’t on file, 185 bpm is assumed). '
  + 'Moderate = the fat-burn zone, 50–69% of your max HR — counts ×1. '
  + 'Vigorous = the cardio/peak zone, 70% of your max HR or above — counts ×2 (the NHS '
  + 'moderate/vigorous doubling rule). Where your device reports its own zone minutes, those are '
  + 'used as recorded. Brisk walking is counted too: steps above 4,000 in a day convert to moderate '
  + 'minutes at about 100 steps per minute. Each day counts whichever is HIGHER — the heart-rate '
  + 'zone minutes or the step estimate — never the two added together, so a brisk walk that your '
  + 'heart rate already picked up is not counted twice. The weekly window resets every Monday, and '
  + '150 minutes a week is the target. '
  + 'Separately, your watch may report its own “active minutes” total — that is the device’s own '
  + 'tally, added without the ×2 and without heart-rate zones, so the two figures can differ.';

// (#1377) The zone FRACTIONS, in one place, transcribed from backend/active_minutes.py
// (_ZONE_FATBURN_FRAC / _ZONE_CARDIO_FRAC / _ZONE_PEAK_FRAC). Everything in the UI that draws or
// counts a heart-rate zone reads these, so the boundaries the user is SHOWN are the boundaries the
// engine COUNTS. (Before this, HR_ZONES carried its own map — "Light 50–60%" then "Fat Burn
// 60–70%" — which told the user moderate started at 60% while the engine started counting at 50%.)
const AZM_ZONE_FATBURN_FRAC = 0.50;   // ≥50% max HR → moderate (fat-burn) begins
const AZM_ZONE_CARDIO_FRAC  = 0.70;   // ≥70% max HR → vigorous (cardio) begins
const AZM_ZONE_PEAK_FRAC    = 0.85;   // ≥85% max HR → peak (still vigorous, still ×2)
// The engine's own max-HR resolution, mirrored: _max_hr_from_dob() → 220 − age, with
// _MAX_HR_FALLBACK 185 when no usable date of birth is on file. NEVER hard-code an age-specific
// bpm: the activity views used a literal 165 ( = "age 55"), which silently mis-stated the zones —
// and therefore the zone minutes — for every user who isn't 55.
const AZM_MAX_HR_FALLBACK = 185;
// {bpm, fromAge} — fromAge is what lets a caption say "220 − your age" ONLY when that is what
// happened; on the fallback it must say "assumed", never claim a derivation it didn't do.
function azmMaxHrInfoFromAge(age) {
  const a = Number(age);
  if (!isFinite(a) || a < 5 || a > 110) return { bpm: AZM_MAX_HR_FALLBACK, fromAge: false };
  return { bpm: Math.round(220 - a), fromAge: true };
}
function azmMaxHrFromAge(age) {
  return azmMaxHrInfoFromAge(age).bpm;
}
// Zone minutes for ONE session's heart-rate samples, by the SAME rule as backend
// active_minutes._series_zone_minutes() + day_cardio_points(): fat-burn (50–69% of max HR) =
// moderate ×1, cardio/peak (≥70%) = vigorous ×2. Client-side we only have evenly-spaced samples,
// so each sample owns duration/nSamples minutes (the backend uses real per-sample gaps, capped at
// 90s); the BOUNDARIES and the ×2 doubling — the part the displayed definition promises — are
// identical. Returns null when there is nothing to compute from, never a fabricated 0.
function azmZoneMinutesFromHr(hr, durationMin, maxHr) {
  const samples = (Array.isArray(hr) ? hr : []).filter(s => s && typeof s.bpm === 'number' && isFinite(s.bpm));
  const mins = Number(durationMin);
  const mx = (isFinite(Number(maxHr)) && Number(maxHr) > 0) ? Number(maxHr) : AZM_MAX_HR_FALLBACK;
  if (samples.length < 2 || !isFinite(mins) || mins <= 0) return null;
  const perSample = mins / samples.length;
  const modLo = AZM_ZONE_FATBURN_FRAC * mx;
  const vigLo = AZM_ZONE_CARDIO_FRAC * mx;
  let mod = 0, vig = 0;
  for (const s of samples) {
    if (s.bpm >= vigLo) vig += perSample;
    else if (s.bpm >= modLo) mod += perSample;
  }
  const moderate = Math.round(mod);
  const vigorous = Math.round(vig);
  return { moderate, vigorous, points: moderate + vigorous * 2, maxHr: Math.round(mx) };
}

// (#1377) THE OTHER "active minutes" in this UI — a DIFFERENT quantity, so it needs its own
// definition rather than the zone one. fitbit_activity.active_minutes is written by
// fitbit_sync.py as fairlyActiveMinutes + veryActiveMinutes: the device's own MET-based tally,
// SUMMED (no ×2) with no heart-rate zone in it. /api/fitness/today and /api/today/summary serve
// that column, so the Vitals pill, the Today activity strip and the Fitness "Active min" tile all
// show it — NOT active_minutes.day_points(). Stating the zone rule beside those figures would be
// stating a rule the code does not apply to them.
const DEVICE_ACTIVE_MIN_DEF_SHORT =
  'As your device reports it — its moderate + vigorous minutes added together (no ×2 doubling, no '
  + 'heart-rate zones). Not the same figure as Active Zone Minutes.';

// -- GlossaryTooltip (global term definitions) --------------------------------

const GLOSSARY = {
  // Genetics fundamentals
  'variant': { short: 'A position in your DNA that differs from the reference sequence.', long: 'Most variants are harmless. They\'re what makes each person genetically unique. Only a small fraction have known health associations.' },
  'vcf': { short: 'A standard file format listing positions in your DNA that differ from the reference sequence.', long: 'VCF (Variant Call Format) files are produced by DNA sequencing pipelines and contain your genetic variants with quality scores.' },
  'snp': { short: 'A single-letter change in your DNA at one specific position.', long: 'SNPs (Single Nucleotide Polymorphisms) are the most common type of genetic variation. Most have no noticeable effect, but some influence health traits.' },
  'maf': { short: 'Minor allele frequency \u2014 how common this variant is in the population.', long: 'A MAF of 5% means about 1 in 20 people carry this variant. Rarer variants (MAF < 1%) are more likely to have noticeable effects.' },
  'heterozygous': { short: 'You inherited one copy of this variant from one parent, and the standard version from the other.', long: 'Think of it like having one blue sock and one red sock \u2014 you carry both versions. The effect depends on whether one copy is enough to matter.' },
  'homozygous': { short: 'You inherited the same version of this variant from both parents.', long: 'Having two copies often means a stronger effect than having just one. This is true for both beneficial and potentially concerning variants.' },
  'genotype': { short: 'The specific pair of DNA letters you carry at a particular position.', long: 'For example, "AG" means you have an A from one parent and a G from the other. "AA" means both parents gave you the same version.' },
  'rsid': { short: 'A unique identifier (like rs429358) assigned to a known genetic variant.', long: 'rsIDs are used by researchers worldwide to reference specific positions in the genome. They make it possible to look up published research about a variant.' },
  'exome': { short: 'The ~1.5% of your genome that codes for proteins \u2014 the functional building blocks of your body.', long: 'Exome sequencing reads only protein-coding genes but captures the majority of known disease-causing variants at a fraction of the cost of whole-genome sequencing.' },
  'allele': { short: 'One version of a gene or variant at a specific position in your DNA.', long: 'You inherit two alleles for each position \u2014 one from each parent. The combination determines your genotype.' },
  'chromosome': { short: 'A long strand of DNA carrying thousands of genes. Humans have 23 pairs.', long: 'Chromosomes 1\u201322 are numbered by size (1 is largest). The 23rd pair (X/Y) determines biological sex.' },
  'gene': { short: 'A section of DNA containing instructions for making a specific protein.', long: 'Humans have about 20,000 protein-coding genes. Variants within or near genes can affect how much protein is made or how well it works.' },
  'clinvar': { short: 'A public database linking genetic variants to their clinical significance.', long: 'ClinVar is maintained by the NCBI and aggregates reports from genetic testing laboratories and researchers worldwide.' },
  'gwas': { short: 'Genome-wide association study \u2014 research that scans entire genomes to find variants linked to traits.', long: 'GWAS studies compare the DNA of thousands of people with and without a condition to identify statistically significant genetic associations.' },
  'regulatory region': { short: 'DNA near a gene that controls when and how much protein the gene makes.', long: 'Like a dimmer switch for genes \u2014 variants in regulatory regions can turn genes up or down without changing the protein itself.' },
  'splice site': { short: 'A position where your cell cuts and joins gene segments to make the final protein blueprint.', long: 'Variants at splice sites can cause genes to produce shorter, longer, or misshapen proteins.' },
  'chromatin': { short: 'The packaging structure that wraps your DNA around proteins to fit inside cell nuclei.', long: 'How tightly DNA is packaged affects which genes are active. Open chromatin means genes are accessible; closed chromatin means they\'re silenced.' },
  'transcription factor': { short: 'A protein that attaches to DNA to switch genes on or off.', long: 'Variants can create or destroy sites where transcription factors bind, changing gene activity in specific tissues.' },
  'eqtl': { short: 'A variant that affects how much of a particular gene\'s protein is produced.', long: 'eQTL (expression quantitative trait locus) variants explain how non-coding DNA changes can still affect your biology by altering gene expression levels.' },
  'reference genome': { short: 'The standard human DNA sequence used as a comparison baseline (GRCh38).', long: 'It\'s a composite sequence assembled from multiple individuals. Your variants are the positions where your DNA differs from this reference.' },
  'base pair': { short: 'A single unit of DNA \u2014 one rung on the DNA ladder. Your genome has about 3 billion.', long: 'The four DNA letters (A, T, C, G) pair up: A with T, C with G. A base pair refers to one of these paired positions.' },
  // Blood markers & biomarkers
  'ldl': { short: 'Low-density lipoprotein \u2014 the "bad" cholesterol that can build up in artery walls.', long: 'LDL particles carry cholesterol into artery walls. Optimal LDL is below 100 mg/dL; below 70 for high-risk individuals. Statins, diet, and exercise all lower LDL.' },
  'hdl': { short: 'High-density lipoprotein \u2014 the "good" cholesterol that removes cholesterol from arteries.', long: 'HDL acts as a cholesterol vacuum cleaner, carrying it back to the liver for disposal. Levels above 60 mg/dL are considered protective. Exercise is the most effective way to raise HDL.' },
  'triglycerides': { short: 'A type of fat in your blood that your body uses for energy. High levels increase cardiovascular risk.', long: 'After eating, your body converts unused calories to triglycerides and stores them in fat cells. Optimal levels are below 75 mg/dL for longevity. Diet, exercise, and omega-3s help lower them.' },
  'apob': { short: 'Apolipoprotein B \u2014 a more precise measure of harmful cholesterol particles than LDL alone.', long: 'Every LDL particle has exactly one ApoB molecule, so ApoB count equals the number of particles that can enter artery walls. Many cardiologists consider it a better risk predictor than LDL-C.' },
  'lp(a)': { short: 'Lipoprotein(a) \u2014 a cholesterol particle largely set from birth and stable over life.', long: 'Lp(a) levels are largely fixed from birth and not lowered by statins or lifestyle. Levels above 50 nmol/L increase cardiovascular risk. PCSK9 inhibitors can reduce Lp(a) by ~25%.' },
  'apolipoprotein': { short: 'A protein that wraps around lipid (fat) particles to transport them through the bloodstream.', long: 'Different apolipoproteins (ApoA, ApoB, ApoE) have specific roles in cholesterol transport. Your APOE genotype determines which version you produce.' },
  'hba1c': { short: 'Glycated haemoglobin \u2014 your average blood sugar over the past 2-3 months.', long: 'HbA1c measures the percentage of red blood cells with sugar attached. Below 5.4% is optimal for longevity; 5.7-6.4% indicates pre-diabetes; above 6.5% indicates diabetes.' },
  'glucose': { short: 'Blood sugar \u2014 the primary energy source for your cells.', long: 'Fasting glucose between 70-90 mg/dL is optimal. Higher levels can indicate insulin resistance, often years before diabetes develops. Exercise and diet are powerful regulators.' },
  'insulin resistance': { short: 'A condition where your cells respond poorly to insulin, requiring more insulin to manage blood sugar.', long: 'Insulin resistance is the root cause of type 2 diabetes and metabolic syndrome. It develops gradually, driven by excess body fat (especially visceral) and inactivity.' },
  'crp': { short: 'C-reactive protein \u2014 a blood marker of inflammation throughout your body.', long: 'CRP is produced by the liver in response to inflammation. Below 1.0 mg/L is optimal. Chronic elevation is linked to cardiovascular disease, autoimmune conditions, and accelerated aging. IL-6 gene variants can influence baseline levels.' },
  'inflammation': { short: 'Your immune system\'s response to injury or threat. Chronic low-grade inflammation accelerates aging.', long: 'Acute inflammation heals injuries, but chronic inflammation damages tissues over time. It\'s measured by CRP, IL-6, and other markers. Anti-inflammatory lifestyle factors include exercise, sleep, omega-3s, and stress management.' },
  'cytokine': { short: 'A signalling molecule that immune cells use to communicate \u2014 some promote inflammation, others reduce it.', long: 'IL-6 and TNF-alpha are pro-inflammatory cytokines; IL-10 is anti-inflammatory. Their balance shapes your baseline inflammatory tone.' },
  'homocysteine': { short: 'An amino acid that, when elevated, damages blood vessel walls and increases cardiovascular risk.', long: 'Homocysteine is normally recycled using folate and B12; when that recycling is impaired, levels can rise. Optimal is below 10 \u00b5mol/L. B vitamins (folate, B12, B6) help lower it.' },
  'folate': { short: 'Vitamin B9 \u2014 essential for DNA repair, cell division, and recycling homocysteine.', long: 'Folate is critical for methylation, a process that regulates gene expression. People with MTHFR variants may need methylfolate (the active form) rather than folic acid.' },
  'methylation': { short: 'A biochemical process affecting how the body uses folate and B12.', long: 'Methylation adds a methyl group (-CH3) to molecules in the body. It supports detoxification, neurotransmitter production, and homocysteine metabolism, and relies on folate and B12.' },
  'ferritin': { short: 'A protein that stores iron in your body. Levels reflect total iron stores.', long: 'Optimal ferritin is 50-150 ng/mL. Too low causes fatigue and anaemia; too high (as in hemochromatosis from HFE variants) causes organ damage. Regular blood donation can lower excess ferritin.' },
  // Fitness & cardiac metrics
  'hrv': { short: 'Heart rate variability \u2014 the variation in time between heartbeats, a marker of autonomic nervous system health.', long: 'Higher HRV generally indicates better cardiovascular fitness and stress resilience. It\'s measured as RMSSD (root mean square of successive differences). Exercise, sleep, and stress management all improve HRV.' },
  'resting heart rate': { short: 'Your heart rate when completely at rest. Lower is generally better for longevity.', long: 'Fit individuals typically have resting HR of 50-60 bpm. Above 75 bpm is associated with increased cardiovascular risk. Exercise is the most effective way to lower resting HR.' },
  'vo2max': { short: 'Maximum oxygen uptake during exercise \u2014 the single best predictor of cardiovascular longevity.', long: 'VO2max measures how efficiently your body uses oxygen during intense exercise. It declines ~10% per decade but exercise can slow this dramatically. Being in the top 25% for your age adds years to life.' },
  'rmssd': { short: 'Root mean square of successive differences \u2014 the standard measurement of heart rate variability.', long: 'RMSSD captures beat-to-beat variation in heart rate. Higher values (typically 20-100+ ms) indicate better parasympathetic (rest-and-digest) nervous system function.' },
  // Pharmacogenomics
  'metaboliser': { short: 'How fast your body breaks down a medication, determined by your CYP enzyme gene variants.', long: 'Poor metabolisers break down drugs slowly (risk of side effects); rapid/ultra-rapid metabolisers clear drugs too fast (reduced effectiveness). CYP2D6, CYP2C19, CYP2C9, and CYP3A5 are the most important drug-metabolising enzymes.' },
  'pharmacogenomics': { short: 'The study of how your genes affect your response to medications.', long: 'Your CYP enzyme variants determine how fast you metabolise ~75% of commonly prescribed drugs. This information can help your doctor choose the right drug and dose for you.' },
  'drug metabolism': { short: 'The process by which your liver enzymes break down medications into inactive forms for elimination.', long: 'The cytochrome P450 (CYP) enzyme family handles most drug metabolism. Genetic variants in CYP genes can make you a poor, intermediate, normal, or ultra-rapid metaboliser of specific drugs.' },
  'cyp enzyme': { short: 'Cytochrome P450 enzymes in your liver that break down most medications.', long: 'The CYP family includes CYP2D6 (codeine, antidepressants), CYP2C19 (PPIs, clopidogrel), CYP2C9 (warfarin, NSAIDs), and CYP3A5 (statins, immunosuppressants). Your genotype determines enzyme activity.' },
  // Cardiovascular terms
  'thrombosis': { short: 'Formation of a blood clot inside a blood vessel, which can block blood flow.', long: 'Venous thrombosis (deep vein thrombosis, pulmonary embolism) and arterial thrombosis (heart attack, stroke) are the two main types. Risk rises with immobility, inflammation, and some clotting conditions.' },
  'nitric oxide': { short: 'A molecule that relaxes blood vessels and improves blood flow. Produced by the NOS3 gene.', long: 'Nitric oxide dilates arteries, lowers blood pressure, and prevents plaque buildup. NOS3 variants can reduce production. Exercise, beetroot, and leafy greens boost nitric oxide levels.' },
  'endothelial function': { short: 'The health of the inner lining of your blood vessels, which controls blood flow and clotting.', long: 'Endothelial cells produce nitric oxide and regulate vascular tone. Dysfunction is an early step in atherosclerosis. NOS3 variants, inflammation, and high blood pressure all impair endothelial function.' },
  // Sleep & circadian
  'circadian rhythm': { short: 'Your body\'s internal 24-hour clock that regulates sleep, hormones, and metabolism.', long: 'Core clock genes (PER2, CLOCK, BMAL1) create a feedback loop that cycles every ~24 hours. Disruption (shift work, jet lag) is linked to metabolic disease, cancer risk, and accelerated aging.' },
  'chronotype': { short: 'Your natural sleep-timing tendency, partly innate.', long: 'Some people are naturally earlier (morning lark) or later (night owl). Aligning your schedule with your chronotype improves sleep quality, mood, and metabolic health.' },
  'adenosine': { short: 'A molecule that accumulates while you\'re awake and creates the pressure to sleep.', long: 'Adenosine builds up in your brain during waking hours, creating "sleep pressure." ADA gene variants affect how fast adenosine is cleared, influencing deep sleep depth. Caffeine blocks adenosine receptors.' },
  // Nutrition & metabolism
  'hemochromatosis': { short: 'A condition causing the body to absorb too much iron, often running in families.', long: 'Iron builds up in the liver, heart, and pancreas over years. Affects ~1 in 200 Northern Europeans. Treatment is simple: regular blood donation or phlebotomy.' },
  'lactase persistence': { short: 'The ability to digest dairy into adulthood, determined by a variant near the LCT gene.', long: 'Most mammals lose lactase production after weaning. The MCM6 variant that maintains it evolved independently in pastoral populations. About 65% of the global population is lactose intolerant.' },
  'mitochondria': { short: 'The energy-producing structures inside your cells. Their efficiency declines with age.', long: 'Mitochondria convert food into ATP (cellular energy). PPARGC1A (PGC-1\u03b1) is the master regulator of mitochondrial production. Exercise is the most powerful stimulus for making new mitochondria.' },
  'fast-twitch': { short: 'Muscle fibres optimised for power and speed. Used in sprinting and weightlifting.', long: 'Fast-twitch (Type II) fibres contract quickly but fatigue faster. ACTN3 produces alpha-actinin-3 exclusively in these fibres. The R577X variant (TT genotype) eliminates this protein, shifting muscles toward endurance.' },
  'risk allele': { short: 'The version of a variant associated with higher disease risk in research studies.', long: 'Carrying a risk allele doesn\'t guarantee disease \u2014 it shifts probability. Most health traits involve many risk alleles, each with a small effect. Lifestyle often outweighs individual genetic risk.' },
  'protective allele': { short: 'The version of a variant associated with reduced disease risk or better health outcomes.', long: 'Protective alleles can offset risk from other variants. For example, APOE e2 is protective for cholesterol, while FOXO3 longevity variants activate cellular repair pathways.' },
  // Longevity score terms
  'biological age': { short: 'How old your body appears based on biomarkers and fitness \u2014 compared to your actual (chronological) age.', long: 'Unlike chronological age (years since birth), biological age reflects your body\'s actual condition. It\'s calculated from blood markers and cardiovascular fitness. You can lower it through lifestyle changes.' },
  'chronological age': { short: 'Your actual age in years since birth.', long: 'Chronological age is fixed \u2014 it simply counts calendar time. Biological age can be higher or lower, reflecting how well your body is aging relative to the average person your age.' },
  'phenoage': { short: 'A biological age formula using 9 routine blood markers, validated on 9,926 people in the NHANES study.', long: 'Developed by Morgan Levine in 2018. PhenoAge uses albumin, creatinine, glucose, CRP, lymphocytes, MCV, RDW, alkaline phosphatase, and white blood cells to estimate biological age. It predicts mortality better than chronological age alone.' },
  'scoring layers': { short: 'The three components that make up your composite biological age score.', long: 'PhenoAge (50% weight) measures blood biomarker aging. Cardiovascular (30%) assesses heart and lipid health. Fitness (20%) includes sleep, steps, HRV, and resting heart rate.' },
  'mortality risk': { short: 'The estimated probability of dying within a given time period, based on your biomarkers.', long: '10-year mortality risk is derived from your PhenoAge calculation. A lower biological age than chronological age means lower risk. This is a statistical estimate, not a diagnosis \u2014 individual outcomes vary widely.' },
  'albumin': { short: 'A protein made by your liver. Higher levels suggest good nutrition and liver function.', long: 'Albumin is the most abundant protein in blood. It transports hormones, vitamins, and drugs. Low albumin (<3.5 g/dL) is associated with accelerated aging. Optimal is 4.0\u20134.5 g/dL. Good nutrition and liver health maintain levels.' },
  'creatinine': { short: 'A waste product from muscle metabolism, filtered by your kidneys. Used to estimate kidney function.', long: 'Your kidneys filter creatinine from blood. Higher levels may indicate reduced kidney function (lower eGFR). Normal range is 0.7\u20131.2 mg/dL for men. Exercise and muscle mass also affect levels.' },
  'lymphocytes': { short: 'White blood cells that fight infections and form your immune memory (T cells, B cells).', long: 'Lymphocytes include T cells (cell-mediated immunity), B cells (antibody production), and NK cells (cancer surveillance). The percentage decreases with age. Higher lymphocyte percentage is associated with younger biological age.' },
  'mcv': { short: 'Mean corpuscular volume \u2014 the average size of your red blood cells.', long: 'MCV helps identify nutritional deficiencies. High MCV (macrocytosis) suggests B12 or folate deficiency. Low MCV (microcytosis) suggests iron deficiency. Normal range is 80\u2013100 fL. Optimal for longevity is toward the lower end of normal.' },
  'rdw': { short: 'Red cell distribution width \u2014 measures how much your red blood cells vary in size.', long: 'Higher variation (elevated RDW) is a strong predictor of mortality, even in otherwise healthy people. Optimal is below 13%. Elevated RDW may indicate inflammation, nutritional deficiency, or bone marrow stress.' },
  'alp': { short: 'Alkaline phosphatase \u2014 an enzyme found in liver, bones, and other tissues.', long: 'ALP is used in PhenoAge as a marker of liver and bone health. Elevated levels may indicate liver disease, bone turnover, or bile duct issues. Optimal is in the lower half of the normal range (40\u201380 U/L).' },
  'wbc': { short: 'White blood cell count \u2014 a measure of your immune system\'s overall activity level.', long: 'WBC counts reflect immune activation. Elevated WBC (even within normal range) is associated with chronic inflammation and faster aging. Optimal for longevity is toward the lower end: 4\u20136 \u00d710\u00B3/\u00B5L. Very low counts may indicate immune suppression.' },
  'nhanes': { short: 'National Health and Nutrition Examination Survey \u2014 a major US study that provided the data to develop PhenoAge.', long: 'NHANES collects health data from thousands of Americans through interviews, physical exams, and lab tests. PhenoAge was validated on NHANES III (9,926 participants). It\'s one of the most cited health datasets in the world.' },
  // Pathway-specific terms
  'mthfr': { short: 'A gene that converts folate to its active form (5-MTHF). Common variants reduce this conversion.', long: 'MTHFR C677T (rs1801133) reduces enzyme activity by ~35% (heterozygous) or ~70% (homozygous). This can raise homocysteine levels and affect DNA methylation. Supplementing with methylfolate bypasses the reduced enzyme.' },
  'apoe': { short: 'A gene that determines how your body handles cholesterol. The e4 variant increases cardiovascular and Alzheimer\'s risk.', long: 'APOE has three versions: e2 (protective, linked to longevity), e3 (most common, neutral), and e4 (increases LDL and Alzheimer\'s risk). About 25% of people carry at least one e4 allele.' },
  'foxo3': { short: 'The most replicated human longevity gene. Activates cellular repair and stress defence pathways.', long: 'FOXO3 regulates genes involved in antioxidant defence, DNA repair, and programmed cell death. The G allele of rs2802292 is associated with 2.7x odds of reaching 100 (Willcox 2008). Found in every centenarian study.' },
  'nf-kb': { short: 'A protein complex that activates inflammatory genes. Chronic activation accelerates aging.', long: 'NF-\u03BAB is a master regulator of inflammation. It\'s activated by stress, infection, and oxidative damage. Chronic NF-\u03BAB activation drives "inflammaging" \u2014 the low-grade inflammation that accelerates biological aging.' },
  'il-6': { short: 'Interleukin-6 \u2014 a pro-inflammatory signalling molecule. Higher levels accelerate aging.', long: 'IL-6 is released during inflammation and infection. Chronically elevated IL-6 is linked to heart disease, diabetes, and frailty. Your IL6 gene variant affects baseline production. Exercise paradoxically releases IL-6 acutely but lowers chronic levels.' },
  'il-10': { short: 'Interleukin-10 \u2014 an anti-inflammatory signalling molecule that helps resolve inflammation.', long: 'IL-10 acts as a brake on the immune system, preventing excessive inflammation. The GG genotype at rs1800896 produces more IL-10, associated with better inflammatory balance and longevity.' },
  'vdr': { short: 'Vitamin D receptor \u2014 the protein that lets your cells respond to vitamin D.', long: 'VDR is found in nearly every cell type. When activated by vitamin D, it switches on genes involved in bone health, immunity, and cancer protection. The FokI variant (rs2228570) affects how active the receptor is.' },
  'sam': { short: 'S-adenosylmethionine \u2014 the universal methyl donor used to switch genes on and off throughout your body.', long: 'SAM is produced from methionine and donates methyl groups to DNA, proteins, and neurotransmitters. It\'s the endpoint of the methylation cycle. Adequate folate and B12 are needed to maintain SAM levels.' },
  // Vital profile & units
  'systolic': { short: 'The top number in a blood pressure reading \u2014 the pressure when your heart beats.', long: 'Systolic pressure reflects the force on artery walls during heartbeats. Optimal is below 120 mmHg. 120\u2013129 is elevated; 130+ is high. It tends to rise with age as arteries stiffen.' },
  'diastolic': { short: 'The bottom number in a blood pressure reading \u2014 the pressure between heartbeats.', long: 'Diastolic pressure reflects the baseline pressure in your arteries when your heart is resting between beats. Optimal is below 80 mmHg. Consistently above 90 is high blood pressure.' },
  'mmhg': { short: 'Millimetres of mercury \u2014 the standard unit for measuring blood pressure.', long: 'An inherited unit from early mercury-column instruments. A reading of "120/80 mmHg" means systolic pressure pushes a mercury column 120mm high, diastolic 80mm.' },
  'bmi': { short: 'Body Mass Index \u2014 a simple ratio of weight to height used to classify underweight, normal, overweight, and obesity.', long: 'BMI = weight(kg) / height(m)\u00B2. Normal range is 18.5\u201324.9. It doesn\'t distinguish muscle from fat, so athletes may show "overweight" despite being lean. Waist circumference is a better predictor of metabolic risk.' },
  'bpm': { short: 'Beats per minute \u2014 the unit for measuring heart rate.', long: 'Resting heart rate is measured in bpm. Athletic adults typically have 50\u201360 bpm; average is 60\u201380 bpm. Above 75 bpm at rest is associated with increased cardiovascular risk.' },
  'spo2': { short: 'Blood oxygen saturation \u2014 the percentage of haemoglobin carrying oxygen. Normal is 95\u2013100%.', long: 'SpO2 is measured by a pulse oximeter (or Fitbit sensor) using light absorption through your finger or wrist. Below 94% may indicate respiratory issues. Values during sleep often dip slightly.' },
  'deep sleep': { short: 'The most restorative sleep stage \u2014 when tissue repair, growth hormone release, and memory consolidation occur.', long: 'Also called slow-wave sleep (N3). Adults typically get 1\u20132 hours per night, mostly in the first half. It decreases with age. Exercise and cool sleeping conditions can increase deep sleep.' },
  'light sleep': { short: 'The transitional sleep stage between wakefulness and deep sleep. Takes up about 50% of total sleep.', long: 'Light sleep (N1 and N2) is when your body starts to relax. Heart rate and breathing slow. You\'re easily awakened. It serves as a bridge to deeper, more restorative stages.' },
  'rem sleep': { short: 'Rapid Eye Movement sleep \u2014 when most vivid dreaming occurs. Critical for emotional processing and learning.', long: 'REM sleep increases through the night, with the longest periods toward morning. Adults need about 90\u2013120 minutes per night. REM is essential for creativity, emotional regulation, and memory consolidation.' },
  // (#1377) Was: "earned when your heart rate exceeds 40% of your heart rate RESERVE" — that is
  // Fitbit's own definition and the engine does NOT implement it. Kalpa's engine works in
  // %-of-max-HR (50% / 70%), and also counts brisk-walking steps, which the old copy never
  // mentioned. Now reads from the single AZM_ZONE_DEF_LONG source so the glossary can never
  // contradict the drawer, the ring, or the number itself.
  'active zone minutes': { short: 'Minutes spent in an elevated heart-rate zone: moderate counts ×1, vigorous ×2. Brisk-walking steps count too.', long: AZM_ZONE_DEF_LONG },
  'floors climbed': { short: 'Fitbit metric tracking elevation gain, measured using a barometric altimeter. One floor \u2248 3 metres (10 feet).', long: 'Stair climbing is a good measure of functional fitness and vertical activity. It engages different muscle groups than flat walking and provides cardiovascular benefits.' },
  // Clinical timeline terms
  'egfr': { short: 'Estimated glomerular filtration rate \u2014 measures how well your kidneys filter waste from blood.', long: 'eGFR is calculated from creatinine, age, and sex. Normal is >90 mL/min. 60\u201389 is mildly reduced; below 60 indicates chronic kidney disease. It naturally declines ~1 point/year after age 40. Hydration, exercise, and blood pressure control help preserve kidney function.' },
  'tsh': { short: 'Thyroid stimulating hormone \u2014 controls how much thyroid hormone your body produces.', long: 'TSH is produced by the pituitary gland. Optimal is 0.5\u20132.5 mIU/L. High TSH suggests hypothyroidism (underactive thyroid); low TSH suggests hyperthyroidism. Thyroid health affects metabolism, energy, weight, and heart rate.' },
  'alt': { short: 'Alanine aminotransferase \u2014 a liver enzyme. Elevated levels may indicate liver inflammation or damage.', long: 'ALT is released when liver cells are injured. Optimal is <35 U/L. Elevated by alcohol, medications (especially statins, paracetamol), fatty liver, and hepatitis. ALT is more liver-specific than AST.' },
  'ast': { short: 'Aspartate aminotransferase \u2014 an enzyme found in liver, heart, and muscles.', long: 'AST is elevated by liver damage, heart damage, or intense exercise. Normal is <35 U/L. AST is less liver-specific than ALT because it\'s also found in muscle tissue. The AST/ALT ratio can help distinguish causes.' },
  'longevity target': { short: 'A reference range reflecting research on optimal biomarker levels for healthy ageing \u2014 tighter than standard lab "normal" ranges.', long: 'Longevity targets are based on studies of centenarians and long-lived populations. Standard lab ranges just mean "not obviously sick." Longevity ranges are stricter because average \u2260 optimal. For example, LDL <70 mg/dL vs lab normal <100.' },
  'reference range': { short: 'The standard "normal" range for a blood marker, based on the healthy population tested by the lab.', long: 'Lab reference ranges represent the middle 95% of healthy people tested. They vary by lab and method. Shown as the grey shaded area on charts. Values outside the range aren\'t necessarily dangerous but warrant attention.' },
  // Pharmacogenomics specific genes
  'cyp2d6': { short: 'A liver enzyme that metabolises ~25% of all drugs, including antidepressants, pain medications, and beta-blockers.', long: 'CYP2D6 has the most variant alleles of any CYP gene. Poor metabolisers accumulate drugs (risk of side effects); ultra-rapid metabolisers clear drugs too fast (reduced effectiveness for most drugs, but dangerous for codeine which gets converted faster to morphine).' },
  'cyp2c19': { short: 'A liver enzyme that metabolises PPIs (acid reflux drugs), antidepressants, and the blood thinner clopidogrel.', long: 'CYP2C19 poor metabolisers should avoid clopidogrel (Plavix) as it won\'t be activated properly, reducing antiplatelet effect. Rapid metabolisers may need higher PPI doses. The *2 allele (rs4244285) is the most common loss-of-function variant.' },
  'cyp3a5': { short: 'A liver enzyme that metabolises ~50% of all drugs, especially statins and immunosuppressants.', long: 'CYP3A5 and its close relative CYP3A4 together metabolise more drugs than any other enzyme family. Variants significantly affect statin clearance. CYP3A5 expressers (carriers of CYP3A5*1) may need higher doses of tacrolimus after organ transplant.' },
  // Genome pathway terms
  '9p21': { short: 'A chromosome 9 region containing the strongest known genetic risk factor for coronary artery disease.', long: 'The 9p21.3 locus near the CDKN2B gene has been confirmed in dozens of GWAS studies across all ethnicities. Risk allele carriers have ~25% increased CAD risk per copy. The mechanism involves vascular smooth muscle cell regulation, not traditional cholesterol pathways.' },
  'cdkn2b': { short: 'A tumour suppressor gene near the 9p21 heart disease risk region. It regulates cell growth in blood vessels.', long: 'CDKN2B (cyclin-dependent kinase inhibitor 2B) controls cell cycle arrest. Variants near this gene affect vascular smooth muscle proliferation and plaque stability. Also linked to type 2 diabetes and some cancers.' },
  'cad': { short: 'Coronary artery disease \u2014 the buildup of plaque in the arteries that supply blood to your heart.', long: 'CAD is the leading cause of death worldwide. It develops over decades as cholesterol, inflammation, and calcium accumulate in coronary arteries. Risk factors include genetics (APOE, 9p21), high LDL, smoking, high blood pressure, and diabetes.' },
  'cad risk': { short: 'Your genetic predisposition to coronary artery disease, based on known risk variants.', long: 'CAD risk is polygenic \u2014 influenced by many variants, each adding a small amount. The 9p21/CDKN2B region is the strongest single genetic risk factor. Lifestyle factors (diet, exercise, not smoking) can substantially offset genetic risk.' },
  'mtr': { short: 'Methionine synthase \u2014 helps recycle homocysteine back to methionine using vitamin B12.', long: 'MTR works with MTHFR to convert homocysteine back to methionine using B12 as a cofactor. The A2756G variant (rs1805087) can affect B12 utilisation and homocysteine levels.' },
  'pathway': { short: 'A biological system where multiple genes work together to carry out a function (e.g. cholesterol processing, inflammation).', long: 'Pathways show how your genes interact within a system. Variants in multiple genes along the same pathway can have cumulative effects. Understanding pathways helps identify which lifestyle changes have the biggest impact.' },
  'cyp2r1': { short: 'An enzyme that performs the first activation step for vitamin D in your liver.', long: 'CYP2R1 converts vitamin D3 (cholecalciferol from sun or supplements) to 25-hydroxyvitamin D \u2014 the form measured in blood tests. Variants at rs10741657 can reduce this conversion, meaning you need more sun or supplementation.' },
  'dhcr7': { short: 'An enzyme that controls how much cholesterol precursor is available for vitamin D production in your skin.', long: 'DHCR7 (7-dehydrocholesterol reductase) competes with vitamin D synthesis. Variants at rs12785878 affect the balance \u2014 some versions divert more precursor toward cholesterol and away from vitamin D production.' },
  // Longevity score additional
  'offset': { short: 'The difference between your biological age and chronological age. Negative = younger (good!).', long: 'For example, \u22122 years means your body appears 2 years younger than your calendar age. Offset changes with lifestyle improvements. PhenoAge offset correlates with disease risk and disability in large studies.' },
  'wellness category': { short: 'A grouping of genetic findings by health domain \u2014 cardiovascular, metabolism, fitness, longevity, etc.', long: 'Wellness categories organise your genome findings by body system. This helps spot patterns and focus on areas where your genetics suggest the most opportunity for lifestyle optimisation.' },
  'blood pressure': { short: 'The force of blood pushing against your artery walls, measured as systolic/diastolic (e.g. 120/80 mmHg).', long: 'Blood pressure varies throughout the day. Optimal is below 120/80 mmHg. High blood pressure (hypertension) is the leading modifiable risk factor for heart disease and stroke. It usually has no symptoms.' },
  'calories burned': { short: 'Total energy expenditure estimated by Fitbit, including basal metabolism and activity.', long: 'Your body burns most calories just staying alive (basal metabolic rate). Fitbit adds activity calories based on heart rate and movement. Total daily expenditure varies by body size, muscle mass, and activity level.' },
  'steps': { short: 'Daily step count tracked by your Fitbit. A common target is 10,000 steps/day.', long: 'Research shows mortality risk decreases significantly up to about 7,500\u201310,000 steps/day, with diminishing returns above that. Even 4,000 steps/day provides meaningful health benefits compared to being sedentary.' },
  // Clinical abbreviations & terms that surface in insight + flag text. Keys are
  // lower-cased (autoGlossary/injectHtmlGlossary match case-insensitively); the
  // matched text keeps its original case on screen. Word-boundary matching in
  // autoGlossary keeps short codes like AST/ALT from matching inside words.
  'ast': { short: 'Aspartate aminotransferase \u2014 a liver (and muscle) enzyme measured in blood.', long: 'AST rises when liver or muscle cells are stressed. It\u2019s usually read alongside ALT; the AST/ALT ratio helps interpret the pattern. Mildly raised values are common and worth monitoring with your GP.' },
  'alt': { short: 'Alanine aminotransferase \u2014 a liver enzyme measured in blood.', long: 'ALT is fairly specific to the liver, so a raised value points toward liver-cell stress. It\u2019s a routine part of a liver panel and is best interpreted alongside AST, GGT and ALP.' },
  'ggt': { short: 'Gamma-glutamyl transferase \u2014 a liver/bile-duct enzyme.', long: 'GGT helps clarify whether a raised ALP comes from the liver or bone, and is sensitive to alcohol and certain medications. It\u2019s one marker in a wider liver picture.' },
  'alp': { short: 'Alkaline phosphatase \u2014 an enzyme from liver, bile ducts and bone.', long: 'ALP can rise with bile-flow or bone turnover changes. Pairing it with GGT helps localise the source. Levels also shift naturally in growth and pregnancy.' },
  'bilirubin': { short: 'A yellow pigment from the normal breakdown of red blood cells, cleared by the liver.', long: 'Bilirubin is part of a liver panel. Patterns (total vs direct) help indicate how the liver and bile ducts are handling it. Mild fluctuations are common.' },
  'iop': { short: 'Intraocular pressure \u2014 the fluid pressure inside your eye.', long: 'IOP is measured during an eye exam. Higher readings are one factor optometrists monitor over time; it\u2019s interpreted alongside the optic nerve and visual-field checks.' },
  'egfr': { short: 'Estimated glomerular filtration rate \u2014 an estimate of how well your kidneys filter blood.', long: 'eGFR is calculated from creatinine, age and sex. It\u2019s the headline number for kidney function and is best read as a trend over time rather than a single value.' },
  'creatinine': { short: 'A waste product from muscle that healthy kidneys filter out of the blood.', long: 'Creatinine is used to estimate eGFR. Levels are influenced by muscle mass and hydration as well as kidney function, so it\u2019s interpreted in context.' },
  'bun': { short: 'Blood urea nitrogen \u2014 a waste product that reflects kidney function and hydration.', long: 'BUN is read alongside creatinine and eGFR. The BUN/creatinine ratio can hint at hydration status. It\u2019s one part of a kidney panel.' },
  'uric acid': { short: 'A waste product from the breakdown of purines in food and cells.', long: 'Higher uric acid is associated with gout and is influenced by diet, alcohol and kidney clearance. It\u2019s a wellness marker worth tracking as a trend.' },
  'tsh': { short: 'Thyroid-stimulating hormone \u2014 the brain\u2019s signal that sets thyroid activity.', long: 'TSH is the first-line thyroid screen. It moves opposite to thyroid output, so a higher TSH suggests an under-active pattern and a lower TSH an over-active one; free T4/T3 add detail.' },
  'psa': { short: 'Prostate-specific antigen \u2014 a protein made by the prostate, measured in blood.', long: 'PSA is tracked over time as a wellness marker for prostate health in men. A single value matters less than the trend; discuss interpretation with your GP.' },
  'albumin': { short: 'The main protein in blood, made by the liver.', long: 'Albumin reflects liver synthesis and nutrition status, and helps interpret other results. It\u2019s a routine part of a metabolic panel.' },
  'cortisol': { short: 'The body\u2019s main stress hormone, made by the adrenal glands.', long: 'Cortisol follows a daily rhythm (highest in the morning). It influences metabolism, blood sugar and the stress response; timing of the sample matters for interpretation.' },
  'wbc': { short: 'White blood cell count \u2014 immune cells that defend against infection.', long: 'WBC is part of a full blood count. It rises and falls with infection, inflammation and stress, and is read alongside the differential (the breakdown by cell type).' },
  'rbc': { short: 'Red blood cell count \u2014 the cells that carry oxygen around your body.', long: 'RBC is read with haemoglobin and the red-cell indices (MCV, MCH) to characterise oxygen-carrying capacity.' },
  'mcv': { short: 'Mean corpuscular volume \u2014 the average size of your red blood cells.', long: 'MCV helps classify the pattern of a low haemoglobin (smaller vs larger cells), which points toward different nutritional or other causes worth discussing with your GP.' },
  'mch': { short: 'Mean corpuscular haemoglobin \u2014 the average amount of haemoglobin per red blood cell.', long: 'MCH is read alongside MCV and haemoglobin as part of the red-cell indices in a full blood count.' },
  'rdw': { short: 'Red cell distribution width \u2014 how much your red blood cells vary in size.', long: 'A wider RDW means more variation in cell size. It\u2019s read with MCV to help interpret the red-cell picture as a trend.' },
};

function GlossaryTerm({ term, children }) {
  const [showTip, setShowTip] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Fixed-position coords computed from the term's rect at open time. The old
  // tooltip was position:absolute inside an INLINE span, so when the term
  // wrapped or sat in a transformed ancestor the anchor collapsed and the
  // popup floated detached to the top-left and never dismissed on touch (8B).
  const [pos, setPos] = useState(null);
  const termRef = useRef(null);
  const tipRef = useRef(null);
  const closeTimer = useRef(null);
  const entry = GLOSSARY[term.toLowerCase()];

  const close = useCallback(() => { setShowTip(false); setExpanded(false); }, []);
  const openTip = useCallback(() => {
    const el = termRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxw = Math.min(260, window.innerWidth - 16);
    let left = r.left + r.width / 2 - maxw / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - maxw - 8)); // clamp to viewport
    const above = r.top > 140;                                        // flip below if no room
    setPos({ left, top: above ? Math.round(r.top - 8) : Math.round(r.bottom + 8), maxw, above });
    setShowTip(true);
  }, []);
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(close, 140); };

  // Robust dismissal: outside tap/click, scroll, resize, Escape (8B). Without
  // this a touch-opened tooltip never closed (onMouseLeave doesn't fire on
  // touch).
  useEffect(() => {
    if (!showTip) return undefined;
    const onDown = (e) => {
      if (termRef.current && termRef.current.contains(e.target)) return;
      if (tipRef.current && tipRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [showTip, close]);
  useEffect(() => () => cancelClose(), []);

  if (!entry) return <>{children}</>;

  const tipStyle = pos ? {
    position: 'fixed', top: pos.top, left: pos.left, right: 'auto', bottom: 'auto',
    transform: pos.above ? 'translateY(-100%)' : 'none', maxWidth: pos.maxw,
    zIndex: 4000,
  } : { display: 'none' };

  // PORTAL the tooltip to <body>. Rendered inline, a position:fixed popup is
  // positioned relative to the nearest TRANSFORMED ancestor, not the viewport —
  // and Today insight cards carry a transform (.today-stagger animation), so the
  // popup's viewport coords resolved against that offset ancestor and rendered
  // far off-screen (CDP: computed top 615px but painted at 1108px, off-screen).
  // Portaling to body removes it from the transformed subtree so the fixed coords
  // are truly viewport-relative — the same reason the chat GlossaryPopover (which
  // mounts at the app root) already worked. zIndex bumped to 4000 to match it.
  const tip = showTip && pos ? ReactDOM.createPortal(
    <span
      className="glossary-tooltip"
      ref={tipRef}
      style={tipStyle}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <span className="glossary-short">{T(entry.short)}</span>
      {entry.long && !expanded && <button className="glossary-expand" onClick={e => { e.stopPropagation(); setExpanded(true); }}>+</button>}
      {expanded && <span className="glossary-long">{T(entry.long)}</span>}
    </span>, document.body) : null;

  return (
    <span
      className="glossary-term"
      ref={termRef}
      onMouseEnter={openTip}
      onMouseLeave={scheduleClose}
      onClick={(e) => { e.stopPropagation(); showTip ? close() : openTip(); }}
    >
      {children}
      {tip}
    </span>
  );
}

// Auto-glossary: scan text for known terms, wrap in GlossaryTerm components
const _glossaryTermsSorted = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length);
function autoGlossary(text) {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  let remaining = text;
  const usedTerms = new Set();
  while (remaining.length > 0) {
    let earliest = null;
    for (const term of _glossaryTermsSorted) {
      if (usedTerms.has(term)) continue;
      const idx = remaining.toLowerCase().indexOf(term);
      if (idx !== -1 && (!earliest || idx < earliest.idx)) {
        // Check word boundary (don't match inside longer words)
        const before = idx > 0 ? remaining[idx - 1] : ' ';
        const after = idx + term.length < remaining.length ? remaining[idx + term.length] : ' ';
        if (/\w/.test(before) || /\w/.test(after)) continue;
        earliest = { term, idx };
      }
    }
    // LOCALE (2d): term MATCHING runs against the ORIGINAL (en-GB) text so the glossary keys
    // still resolve, and only the DISPLAY segments are localised — both the plain runs and the
    // matched term's visible label. Doing it the other way round would break lookups (the key
    // 'metaboliser' would never match a localised "metabolizer").
    if (!earliest) { parts.push(T(remaining)); break; }
    if (earliest.idx > 0) parts.push(T(remaining.slice(0, earliest.idx)));
    const matched = remaining.slice(earliest.idx, earliest.idx + earliest.term.length);
    parts.push(<GlossaryTerm key={`g-${earliest.term}-${parts.length}`} term={earliest.term}>{T(matched)}</GlossaryTerm>);
    usedTerms.add(earliest.term);
    remaining = remaining.slice(earliest.idx + earliest.term.length);
  }
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// -- Markdown Renderer -------------------------------------------------------
function renderMarkdown(text) {
  if (!text) return '';
  // Extract fenced code blocks before HTML escaping so backticks aren't mangled
  const fences = [];
  text = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    const idx = fences.length;
    const esc = code.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').trimEnd();
    fences.push(`<pre><code${lang ? ` class="language-${lang}"` : ''}>${esc}</code></pre>`);
    return `\x00FENCE${idx}\x00`;
  });
  let h = text
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/^### (.+)$/gm,'<h3>$1</h3>')
    .replace(/^## (.+)$/gm,'<h2>$1</h2>')
    .replace(/^# (.+)$/gm,'<h1>$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g,'<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,'<em>$1</em>')
    .replace(/`([^`]+)`/g,'<code>$1</code>')
    .replace(/^&gt; (.+)$/gm,'<blockquote>$1</blockquote>')
    .replace(/^[\s]*[-*] (.+)$/gm,'<li>$1</li>')
    .replace(/^[\s]*\d+\. (.+)$/gm,'<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g,'<ul>$1</ul>')
    .replace(/^---+$/gm,'<hr/>')
    .replace(/\n\n/g,'</p><p>')
    .replace(/\n/g,'<br/>');
  h = '<p>'+h+'</p>';
  h = h.replace(/<p><\/p>/g,'').replace(/<p><h/g,'<h').replace(/<\/h(\d)><\/p>/g,'</h$1>');
  h = h.replace(/<p><ul>/g,'<ul>').replace(/<\/ul><\/p>/g,'</ul>');
  h = h.replace(/<p><hr\/><\/p>/g,'<hr/>').replace(/<p><blockquote>/g,'<blockquote>').replace(/<\/blockquote><\/p>/g,'</blockquote>');
  // Restore fenced code blocks
  h = h.replace(/\x00FENCE(\d+)\x00/g, (_, i) => fences[parseInt(i, 10)] || '');
  return h;
}

function formatAge(ageFloat) {
  if (!ageFloat || isNaN(ageFloat)) return '--';
  const totalMonths = Math.round(ageFloat * 12);
  const yy = Math.floor(totalMonths / 12);
  const mm = totalMonths % 12;
  return mm > 0 ? `${yy}y ${mm}m` : `${yy}y`;
}

// Convert a fractional-year value (e.g. 54.7 → "54y 8m", -1.33 → "1y 4m") to
// a y/m display. Sign is dropped — the caller applies the younger/older label.
// Shared by the Today feed hero/delta chip and the Longevity page so both
// derive their display from the SAME helper and never diverge.
function fmtYearsMonths(years) {
  if (years == null || isNaN(years)) return '—';
  const abs = Math.abs(years);
  const wholeYears = Math.floor(abs);
  let months = Math.round((abs - wholeYears) * 12);
  let displayYears = wholeYears;
  if (months === 12) { displayYears += 1; months = 0; }
  if (displayYears === 0) return `${months}m`;
  if (months === 0)       return `${displayYears}y`;
  return `${displayYears}y ${months}m`;
}

// §B — Inject TAPPABLE glossary spans into an HTML string (for
// dangerouslySetInnerHTML contexts, e.g. chat markdown). Two bugs in the old
// version are fixed:
//   (1) RAW-HTML LEAK — it regex-replaced across the WHOLE html, so after it
//       injected  <span title="…a formula … in the NHANES study.">PhenoAge</span>
//       a later term ("NHANES"/"biological age") matched the definition text
//       INSIDE that title attribute and got wrapped again, corrupting the tag
//       and leaking  NHANES study">PhenoAge  as visible text.
//   (2) NOT TAPPABLE — title="…" is a hover-only native tooltip; on touch it
//       never opens. We now emit data-gloss="term" (definition NOT in the DOM
//       attribute) and let the single <GlossaryPopover/> host show the popup on
//       tap (mobile-friendly, matches the GlossaryTerm popup used in React prose).
// Robustness: only scan the TEXT segments between tags, and stash each injected
// span behind a private-use placeholder so no later term can match inside our
// own markup.
function injectHtmlGlossary(html) {
  if (!html) return html;
  const segs = html.split(/(<[^>]+>)/);   // odd indices = tags, even = text
  const used = new Set();
  for (let i = 0; i < segs.length; i += 2) {
    let seg = segs[i];
    if (!seg) continue;
    const spans = [];
    for (const term of _glossaryTermsSorted) {
      if (used.has(term)) continue;
      if (!GLOSSARY[term]) continue;
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\b(${escaped})\\b`, 'i');
      const m = seg.match(re);
      if (m) {
        const ph = `${spans.length}`;   // placeholder — no matchable letters
        // LOCALE (2d): the visible label is localised; the data-gloss KEY is NOT — the key
        // must keep matching GLOSSARY, which is authored in en-GB.
        spans.push(`<span class="auto-glossary" data-gloss="${term}">${T(m[1])}</span>`);
        seg = seg.replace(re, ph);
        used.add(term);
      }
    }
    // Localise the plain runs BEFORE the spans are substituted back in, so T never sees
    // (and never rewrites) our own markup or the data-gloss attribute. The placeholders
    // are private-use codepoints around bare digits, which no spelling rule can match.
    seg = T(seg);
    if (spans.length) seg = seg.replace(/(\d+)/g, (_, n) => spans[+n]);
    segs[i] = seg;
  }
  return segs.join('');
}

// §B — ONE app-level popover host. A single capture-phase click delegate turns
// EVERY .auto-glossary[data-gloss] span (chat, and any future HTML-rendered prose)
// into a tap-to-define control, using the same .glossary-tooltip UI as the React
// GlossaryTerm. Mounted once in <App/>; future surfaces inherit for free.
function GlossaryPopover() {
  const [st, setSt] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target && e.target.closest && e.target.closest('.auto-glossary[data-gloss]');
      if (!el) return;
      const term = (el.getAttribute('data-gloss') || '').toLowerCase();
      if (!GLOSSARY[term]) return;
      e.stopPropagation();
      const r = el.getBoundingClientRect();
      const maxw = Math.min(260, window.innerWidth - 16);
      let left = r.left + r.width / 2 - maxw / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - maxw - 8));
      const above = r.top > 140;
      setExpanded(false);
      setSt({ term, left, top: above ? Math.round(r.top - 8) : Math.round(r.bottom + 8), above, maxw });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
  useEffect(() => {
    if (!st) return undefined;
    const close = () => setSt(null);
    const onDown = (e) => {
      if (e.target && e.target.closest && (e.target.closest('.glossary-tooltip') || e.target.closest('.auto-glossary'))) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [st]);
  if (!st) return null;
  const entry = GLOSSARY[st.term];
  if (!entry) return null;
  // right:'auto' + bottom:'auto' NEUTRALISE the .glossary-tooltip CSS `bottom: calc(100% + 8px)`
  // (that rule is for the inline, position:absolute GlossaryTerm variant). Without them this
  // FIXED popover had BOTH top and bottom set, so its box collapsed to a degenerate height — the
  // opaque background never painted and the definition rendered as a TRANSPARENT overlay on the
  // chat answer, unreadable. Portaled to <body> (exactly like GlossaryTerm) so it also escapes the
  // chat drawer's transformed stacking context and its fixed coords stay viewport-relative.
  return ReactDOM.createPortal(
    <span className="glossary-tooltip" style={{ position: 'fixed', left: st.left, top: st.top,
      right: 'auto', bottom: 'auto', transform: st.above ? 'translateY(-100%)' : 'none',
      maxWidth: st.maxw, zIndex: 4000 }}>
      <span className="glossary-short">{T(entry.short)}</span>
      {entry.long && !expanded && <button className="glossary-expand" onClick={e => { e.stopPropagation(); setExpanded(true); }}>+</button>}
      {expanded && <span className="glossary-long">{T(entry.long)}</span>}
    </span>, document.body);
}

// Per-instance feature flags + branding. Fetched once from /api/features on
// mount (public endpoint, no auth needed) so the login screen can render
// branded chrome. Callers read FEATURES_GLOBAL.<flag> synchronously after
// first paint; safe defaults below mean any flag-gated chrome is hidden
// until the real config arrives. Module-level cache + listener pattern
// avoids threading the flag through a context provider.
const FEATURES_GLOBAL = {
  gamification: false,
  order_lab: false,
  order_eligible: false,     // KAL-303 per-USER: set from /api/profile; hides every order affordance for a non-Nepal user
  retest_panel: false,
  // KAL-270 — THE THIRD TIME THIS EXACT FAULT HAS SHIPPED, and this file warns about it twice
  // in the comments below. `timezone` had NO default here and NO hydration line, so
  // FEATURES_GLOBAL.timezone was permanently `undefined` while /api/features correctly served
  // "Asia/Kathmandu" at BOTH the top level and inside `features`. The consequence was small and
  // silent: `_tzShortLabel()` returned '' and the Tim-approved copy rendered as
  // "No data yet for 24 Aug 2026 · last received 23 Aug 2026" with the "(Nepal time)"
  // parenthetical missing — the one part of the sentence that explains WHY the date differs
  // from the reader's own. A flag with no default is a flag whose pre-hydration behaviour is an
  // accident; a flag with no hydration never has any other behaviour at all.
  timezone: '',
  // KAL-253 CHECK 2 — hydrated at /api/features but had NO default here, so it was
  // `undefined` until hydration. Falsy, so it failed CLOSED and nothing broke — but that is
  // the same shape as KAL-249, where the key was in neither place and stayed undefined
  // forever. A flag with no default is a flag whose pre-hydration behaviour is an accident.
  underwriter_role: false,
  // Part C (KAL-247/248). ⚠ THIS LINE AND ITS HYDRATION BELOW WERE MISSING, so
  // FEATURES_GLOBAL.lab_presentation_v2 was permanently `undefined` and every Part C surface
  // was dark on EVERY instance including master-test — while /api/features correctly served
  // true and the AST test correctly verified the gating. Only rendering the page found it.
  lab_presentation_v2: false,
  genetics: true,
  insurance: true,
  nepali_language: false,
  location: true,
  two_loop: false,
  insurance_export: false,   // underwriting plane — OFF until the server says otherwise
  cycle_ui_v2: false,        // KAL-132 monthly-cycle UI v2 — OFF until the server says otherwise
  today_v2: false,           // KAL-142 Today v2 (B4) — OFF until the server says otherwise
  connection_health: false,  // KAL-287 stalled-stream remediation card — OFF until the server says otherwise
  hc_diagnostic: false,      // KAL-297 staff-triggered Health Connect sleep read
  otp_login: false,          // KAL-208 phone/OTP login form on a non-NPL instance — OFF until the server says otherwise
  uw_jurisdiction_selector: false, // KAL-210 regulatory-pack switch — master-test parity testing ONLY
  password_login: false,     // KAL-211 email+password step in front of the OTP form
  // 8-flag additions — theme is INDEPENDENT of branding; wearable_route + country explicit.
  theme: 'kalpa',            // 'kalpa' | 'npl' — visual theme + dark-toggle availability
  country: 'UK',             // 'UK' | 'US' | 'NP'
  language: 'en-GB',         // 'en-GB' | 'en-US' — spelling + date form (locale.js)
  wearable_route: 'fitbit_direct',
  branding: {
    instance: 'kalpa',       // branding flag value (identity: logo/name/title/seed)
    theme: 'kalpa',
    app_name: 'Kalpa',
    powered_by: '',
    logo_url: '',
    primary_colour: '#1B3A5C',
    support_email: 'support@kalpa.health',
  },
  ready: false,
};
const FEATURES_LISTENERS = new Set();
let FEATURES_FETCH_STARTED = false;

// LOCALE (2d) — re-render on a UNITS TOGGLE. The language/country flags only ever change
// on a page load, but the user's metric/imperial choice changes mid-session, and the
// components that render a weight or a height are not all flag-aware. Any component that
// displays a localised quantity subscribes with this and re-renders when the choice flips.
function useLocale() {
  const [, force] = useState(0);
  useEffect(() => onLocaleChange(() => force(n => n + 1)), []);
  return { unitSystem: unitSystem(), imperial: isImperial() };
}

function useFeatures() {
  const [, force] = useState(0);
  useEffect(() => {
    const tick = () => force(n => n + 1);
    FEATURES_LISTENERS.add(tick);
    if (!FEATURES_FETCH_STARTED) {
      FEATURES_FETCH_STARTED = true;
      fetch(`${API_BASE}/features`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d && typeof d === 'object') {
            const f = (d.features && typeof d.features === 'object') ? d.features : d;
            FEATURES_GLOBAL.gamification    = !!f.gamification;
            FEATURES_GLOBAL.order_lab       = !!f.order_lab;
            FEATURES_GLOBAL.retest_panel    = !!f.retest_panel;   // KAL-237
            FEATURES_GLOBAL.lab_presentation_v2 = !!f.lab_presentation_v2;   // KAL-247/248
            FEATURES_GLOBAL.genetics        = f.genetics !== false; // default on
            FEATURES_GLOBAL.insurance       = f.insurance !== false; // default on
            FEATURES_GLOBAL.nepali_language = !!f.nepali_language;
            FEATURES_GLOBAL.location        = f.location !== false; // default on
            FEATURES_GLOBAL.two_loop        = !!f.two_loop; // two-loop Today hero
            // KAL-141 — FEATURES_GLOBAL IS AN EXPLICIT PER-KEY ALLOW-LIST, NOT A SPREAD OF THE
            // PAYLOAD. Registering a flag in flags.py and emitting it from /api/features is only
            // two thirds of the wiring; without this line the key is `undefined` on the client,
            // which is falsy, so the gated surface silently renders its OLD branch. That is
            // exactly how KAL-132 shipped "working" — every server-side check passed (the flag
            // read true, the payload carried it, the binary discriminated across the fleet) while
            // the browser never saw it. A flag is not wired until the CLIENT is shown to change.
            FEATURES_GLOBAL.cycle_ui_v2     = !!f.cycle_ui_v2; // KAL-132 monthly-cycle UI v2
            FEATURES_GLOBAL.today_v2        = !!f.today_v2;    // KAL-142 Today v2 (B4)
            FEATURES_GLOBAL.connection_health = !!f.connection_health; // KAL-287 stalled-stream card
            FEATURES_GLOBAL.hc_diagnostic = !!f.hc_diagnostic;         // KAL-297 diagnostic trigger
            FEATURES_GLOBAL.insurance_export = !!f.insurance_export; // underwriting plane (4a)
            FEATURES_GLOBAL.underwriter_role = !!f.underwriter_role; // KAL-200 sandbox
            FEATURES_GLOBAL.otp_login       = !!f.otp_login; // KAL-208 login form (leg 3 of 3)
            FEATURES_GLOBAL.uw_jurisdiction_selector = !!f.uw_jurisdiction_selector; // KAL-210
            FEATURES_GLOBAL.password_login  = !!f.password_login; // KAL-211
            FEATURES_GLOBAL.theme           = f.theme || (d.branding && d.branding.theme) || 'kalpa';
            FEATURES_GLOBAL.country         = f.country || 'UK';
            FEATURES_GLOBAL.language        = f.language || 'en-GB';
            FEATURES_GLOBAL.wearable_route  = f.wearable_route || 'fitbit_direct';
            // KAL-270 — hydrate from either shape: /api/features serves `timezone` at the top
            // level AND inside `features`, and reading only one of them is how this stayed
            // undefined. Never the browser's zone: the whole point of the label is that the
            // SERVER's day differs from the reader's.
            FEATURES_GLOBAL.timezone        = d.timezone || f.timezone || '';
            // LOCALE (2d) — hand the two locale-bearing flags to the copy/format layer
            // BEFORE any listener re-renders, so the first flagged paint is already correct
            // (dates, units and spelling all read from locale.js, never from FEATURES_GLOBAL).
            setLocale(FEATURES_GLOBAL.language, FEATURES_GLOBAL.country);
            if (d.branding && typeof d.branding === 'object') {
              Object.assign(FEATURES_GLOBAL.branding, d.branding);
            }
          }
          // THEME switch — driven by the THEME flag, INDEPENDENT of branding. Setting
          // data-instance activates the [data-instance="npl"] CSS token overrides in App.css
          // (the attribute name is legacy; it now reflects the theme flag, not the instance).
          // Set on BOTH <html> and <body>; when theme=npl also apply the saved dark/light mode.
          if (typeof document !== 'undefined') {
            const theme = FEATURES_GLOBAL.theme || 'kalpa';
            document.body.setAttribute('data-instance', theme);
            document.documentElement.setAttribute('data-instance', theme);
            try { localStorage.setItem('kalpa_theme_flag', theme); } catch (e) { /* ignore */ }
            _nplApplyTheme(_nplGetTheme());   // apply saved light/dark for ALL themes (core dark-toggle)
            // <title> + FAVICON derive from the BRANDING flag (favicon was missed in normalisation).
            try {
              const bf = (FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.instance) || 'kalpa';
              const nm = (FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.app_name) || 'Kalpa';
              if (nm) document.title = nm;
              localStorage.setItem('kalpa_branding_flag', bf);
              const fi = document.getElementById('favicon'), ai = document.getElementById('apple-icon');
              if (bf === 'npl') {
                if (fi) fi.href = '/favicon_npl.png';
                if (ai) ai.href = '/static/npl_logo_square.png';
              } else {
                if (fi) fi.href = '/kalpa_favicon.png';
                if (ai) ai.href = '/kalpa_apple_touch.png';
              }
            } catch (e) { /* ignore */ }
          }
          // Belt-and-suspenders: reflect the genetics flag onto <html> so the
          // CSS net (html[data-genetics="off"] .genetic-surface { display:none })
          // strips ANY genetic surface even if an individual flag check is ever
          // forgotten. NPL is genetics:false → 'off'.
          if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-genetics', FEATURES_GLOBAL.genetics ? 'on' : 'off');
            // Belt for the index.html bootstrap: mark the native wrapper so the
            // header drops the status-bar inset the native SafeAreaView already adds.
            if (window.ReactNativeWebView) document.documentElement.setAttribute('data-native', '1');
          }
          FEATURES_GLOBAL.ready = true;
          FEATURES_LISTENERS.forEach(fn => fn());
        })
        .catch(() => {
          FEATURES_GLOBAL.ready = true;
          FEATURES_LISTENERS.forEach(fn => fn());
        });
    }
    return () => FEATURES_LISTENERS.delete(tick);
  }, []);
  return FEATURES_GLOBAL;
}

// Hash-route nav for modal overlays: pushes a sentinel hash when the modal
// opens and listens for popstate so the Android hardware back button (and
// browser back button) closes the modal instead of exiting the page. The X
// button calls history.back() too, so both paths run the same close logic.
//
// Usage:
//   const closeRef = useModalHashRoute('#activity-detail', onClose);
//   <button onClick={closeRef}>×</button>
//
// The returned function is the canonical "close this modal" handler — call
// it from the X button and any backdrop click. Don't call onClose directly
// or the URL hash will get out of sync.
function useModalHashRoute(hash, onClose, active = true) {
  const onCloseRef = useRef(onClose);
  // Did THIS hook instance push a history entry? Determines whether the
  // close handler should call history.back() (yes, pop our entry) or
  // replaceState (no entry to pop — modal was opened from a fresh load
  // where the URL already had the modal hash, e.g. user refreshed while
  // an activity detail was open).
  const pushedRef = useRef(false);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    // `active` gates the push/listen for overlays that stay MOUNTED while
    // visually closed (the Ask Kalpa drawer animates via an `open` prop; the
    // NPL consent screen renders in a mandatory first-run mode too). Such
    // callers pass active=open so we only own a history entry while shown.
    // Mount-gated callers (rendered only when present) omit it → always true.
    if (!active) return;
    pushedRef.current = false;
    if (window.location.hash !== hash) {
      window.history.pushState({ kalpaModal: hash }, '', hash);
      pushedRef.current = true;
    }
    const onPop = () => {
      if (window.location.hash !== hash) {
        // User navigated away from our hash → close the modal.
        onCloseRef.current && onCloseRef.current();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // IMPORTANT: do NOT call history.back() here. See earlier commit
      // a31ee31 — calling back() in cleanup races with tab-nav and
      // bounces the user back to the previous tab. The parent's hash
      // useEffect overwrites any stale modal hash on next view change.
    };
  }, [hash, active]);
  // Canonical close handler: drives unmount via history.back so X button
  // and hardware back button take the same code path — but only when we
  // actually pushed an entry. If we didn't (fresh-load refresh case),
  // there's no entry to pop without exiting the page, so we replaceState
  // to clear the hash and call onClose directly.
  return useCallback(() => {
    if (window.location.hash === hash) {
      if (pushedRef.current) {
        window.history.back();
      } else {
        const path = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', path);
        onCloseRef.current && onCloseRef.current();
      }
    } else {
      onCloseRef.current && onCloseRef.current();
    }
  }, [hash]);
}

// Strip ===CHART_START===…===CHART_END=== and ===IMAGE_START===…===IMAGE_END===
// blocks (and any orphan ===CHART_START=== without a matching end) from a
// content string. Charts are surfaced separately via msg.charts so the raw
// JSON spec must never reach the rendered markdown — otherwise it leaks
// into the Details toggle as raw text. Used in MarkdownContent.
function stripChartAndImageBlocks(text) {
  if (!text) return '';
  return text
    .replace(/===CHART_START===[\s\S]*?===CHART_END===/g, '')
    .replace(/===IMAGE_START===[\s\S]*?===IMAGE_END===/g, '')
    .replace(/===CHART_START===[\s\S]*$/g, '')   // orphan start, no end
    .replace(/===IMAGE_START===[\s\S]*$/g, '');
}

// Parse ORDER_PROMPT lines emitted by the AI when ORDER_LAB_ENABLED is on.
// Format: "ORDER_PROMPT: {test_name} | {reason} | {test_code}"
// Returns { stripped, prompts: [...] } so the caller can render
// OrderPromptCard for each prompt and display the stripped text. Resolves
// each test against NPL_TEST_CATALOGUE_FE for the price.
const NPL_TEST_CATALOGUE_FE = {
  // mirror of backend NPL_TEST_CATALOGUE — frontend uses this when the
  // AI omits the price (which it always will — only the backend knows).
  'BC047A': { test_name: 'Vitamin D (25-OH)',      price_npr: 1800, chart_key: 'vitamin_d' },
  'BC112':  { test_name: 'Ferritin',               price_npr: 1200, chart_key: 'ferritin' },
  'BC089':  { test_name: 'Vitamin B12',            price_npr: 1500, chart_key: 'b12' },
  'BC031':  { test_name: 'Lipid Panel',            price_npr: 1400, chart_key: 'lipid' },
  'BC044':  { test_name: 'HbA1c',                  price_npr:  900, chart_key: 'hba1c' },
  'BC061':  { test_name: 'Thyroid Panel (TSH/T3/T4)', price_npr: 1800, chart_key: 'thyroid' },
  'BC134':  { test_name: 'Serum Magnesium',        price_npr: 1000, chart_key: 'magnesium' },
  'BC139':  { test_name: 'Serum Zinc',             price_npr: 1100, chart_key: 'zinc' },
  'BC076':  { test_name: 'hs-CRP',                 price_npr: 1100, chart_key: 'crp' },
};
function extractOrderPrompts(text) {
  if (!text) return { stripped: '', prompts: [] };
  const prompts = [];
  const stripped = text.replace(/^\s*ORDER_PROMPT:\s*(.+)$/gim, (_m, body) => {
    let parts = body.split('|').map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) return '';
    // New format leads with an action token (order|retest); tolerate the old
    // 3-field format by inferring the action from the reason text.
    let action = null;
    if (/^(order|re-?test)$/i.test(parts[0])) {
      action = /re-?test/i.test(parts[0]) ? 'retest' : 'order';
      parts = parts.slice(1);
    }
    const test_name = parts[0];
    const reason = parts[1] || '';
    const test_code = (parts[2] || '').toUpperCase();
    if (!action) {
      action = /re-?test|re-?check|confirm|after supplement|responded|monitor|data error/i.test(reason)
        ? 'retest' : 'order';
    }
    const cat = test_code && NPL_TEST_CATALOGUE_FE[test_code];
    const name = cat ? cat.test_name : test_name;
    prompts.push({
      key: `chat:${test_code || test_name}:${prompts.length}`,
      trigger_type: 'chat_suggestion',
      action,
      headline: `${action === 'retest' ? 'Re-test' : 'Consider ordering'}: ${name}`,
      subtext: reason || 'Suggested based on this conversation.',
      test_name: name,
      test_code: test_code,
      price_npr: cat ? cat.price_npr : 0,
      placement: 'chat',
      chart_key: cat ? cat.chart_key : null,
      dismissible: true,
    });
    return ''; // remove the raw line from the rendered text
  });
  return { stripped: stripped.replace(/\n{3,}/g, '\n\n').trim(), prompts };
}

function MarkdownContent({ content }) {
  // NPL pilot: AI may emit ORDER_PROMPT: lines that should be rendered as
  // OrderPromptCard inline, not as literal text. Extract them first so the
  // markdown render only sees the clean text. confirmingPrompt drives the
  // two-step modal exactly as on Health Record / Longevity.
  const [confirmingPrompt, setConfirmingPrompt] = useState(null);
  const extracted = extractOrderPrompts(content || '');
  const cleanContent = extracted.stripped;
  const chatPrompts = extracted.prompts;
  // Summary-first / collapsible-details split. The backend is instructed to
  // emit '### Details' on its own line between the short summary and the
  // detailed metric breakdown. We hide everything below that heading behind a
  // 'Show details' toggle so the user gets a 2–3 sentence answer up front.
  const detailSplit = useDetailSplit(cleanContent);
  const renderPromptCards = () => chatPrompts.map(p => (
    <OrderPromptCard
      key={p.key}
      prompt={p}
      onDismiss={() => {/* chat prompts are ephemeral; dismiss = just stop rendering */}}
      onOrder={() => setConfirmingPrompt(p)}
    />
  ));
  const renderConfirm = () => confirmingPrompt && (
    <OrderConfirmModal
      prompt={confirmingPrompt}
      onClose={() => setConfirmingPrompt(null)}
      onConfirmed={() => setConfirmingPrompt(null)}
    />
  );
  if (detailSplit) {
    if (!detailSplit.details.trim()) {
      return (
        <div className="markdown-content">
          <div dangerouslySetInnerHTML={{ __html: injectHtmlGlossary(renderMarkdown(detailSplit.summary)) }} />
          {renderPromptCards()}
          {renderConfirm()}
        </div>
      );
    }
    return (
      <div className="markdown-content">
        <div dangerouslySetInnerHTML={{ __html: injectHtmlGlossary(renderMarkdown(detailSplit.summary)) }} />
        {renderPromptCards()}
        <DetailsToggle html={injectHtmlGlossary(renderMarkdown(detailSplit.details))} />
        {renderConfirm()}
      </div>
    );
  }
  return (
    <div className="markdown-content">
      <div dangerouslySetInnerHTML={{ __html: injectHtmlGlossary(renderMarkdown(cleanContent)) }} />
      {renderPromptCards()}
      {renderConfirm()}
    </div>
  );
}

// Returns { summary, details } if the content contains a '### Details' heading
// on its own line, otherwise null. Tolerates leading/trailing whitespace and
// '#### Details' (one extra hash) which Gemini sometimes emits. Strips chart
// and image specs from BOTH halves so raw ===CHART_START=== JSON never
// reaches the rendered markdown — these are surfaced via msg.charts.
function useDetailSplit(content) {
  if (!content || typeof content !== 'string') return null;
  const m = content.match(/^[ \t]*#{3,4}[ \t]*Details[ \t]*$/m);
  if (!m) return null;
  const idx = content.indexOf(m[0]);
  if (idx < 0) return null;
  const rawSummary = content.slice(0, idx);
  const rawDetails = content.slice(idx + m[0].length).replace(/^\s*\n/, '');
  const summary = stripChartAndImageBlocks(rawSummary).trim();
  const details = stripChartAndImageBlocks(rawDetails).trim();
  if (!summary) return null;
  // details may be empty after stripping — caller renders summary alone.
  return { summary, details };
}

function DetailsToggle({ html }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="kc-details">
      <button
        type="button"
        className="kc-details-toggle"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {open ? 'Hide details' : 'Show details'}
        <span className={`kc-details-caret ${open ? 'open' : ''}`}>▾</span>
      </button>
      {open && <div className="kc-details-body" dangerouslySetInnerHTML={{ __html: html }} />}
    </div>
  );
}

// -- Chart colors palette ---------------------------------------------------
const CHART_COLORS = ['#4ecdc4','#e94560','#fbbf24','#a78bfa','#34d399','#60a5fa','#f472b6','#fb923c'];

// -- Inline Chart Component --------------------------------------------------
// Right-axis fallback for blood markers that aren't in the central registry
// (the registry covers steps/HR/HRV/SpO2/glucose/calories/weight; blood
// markers route here via name match).
const RIGHT_AXIS_PATTERNS = /mg\/dl|mmol|cholesterol|hdl|ldl|triglyceride|vldl|crp|hba1c|glucose|ferritin|vitamin|iron|albumin|creatinine|egfr|bilirubin|urea|uric|phosphate|calcium|sodium|potassium|tsh|t3|t4|psa|testosterone|estradiol|cortisol|insulin|homocysteine|fibrinogen|d-dimer|bnp/i;

function detectAxisId(s) {
  if (s.yAxisId) return s.yAxisId;
  const label = `${s.key || ''} ${s.name || ''}`;
  // Prefer the central registry — it is the source of truth for axis side.
  const m = resolveMetric(s.key) || resolveMetric(s.name);
  if (m) return m.axisGroup;
  if (RIGHT_AXIS_PATTERNS.test(label)) return 'right';
  return null; // undetermined
}

function InlineChart({ chart }) {
  if (!chart || !chart.data || chart.data.length === 0) return null;
  const series = chart.series || [{ key: 'value', name: 'Value', color: CHART_COLORS[0] }];

  // Auto-detect axis assignment from series names
  const resolved = series.map(s => ({ ...s, _axis: detectAxisId(s) }));
  const hasRightSeries = resolved.some(s => s._axis === 'right');
  const hasLeftSeries = resolved.some(s => s._axis === 'left' || s._axis === null);
  // If we detected right-axis series, assign all unresolved to left
  const finalSeries = resolved.map(s => ({
    ...s,
    _axis: s._axis || (hasRightSeries ? 'left' : 'left')
  }));
  const hasDualAxis = hasRightSeries && hasLeftSeries;

  // Detect if we need ComposedChart (mixed bar/line/area types)
  const hasMixedTypes = finalSeries.some(s => s.type === 'bar' || s.type === 'area') && finalSeries.some(s => !s.type || s.type === 'line');
  const useComposed = hasDualAxis || hasMixedTypes;

  const isMobile = window.innerWidth <= 768;
  const chartHeight = isMobile ? 220 : 280;
  const chartMargin = isMobile
    ? { top: 5, right: hasDualAxis ? 40 : 10, bottom: 15, left: 0 }
    : { top: 10, right: hasDualAxis ? 55 : 20, bottom: 20, left: 10 };
  const tickStyle = { fontSize: isMobile ? 9 : 10, fill: '#8B6914' };
  const axisWidth = isMobile ? 38 : 60;

  // Axis labels come from the central registry — never generic "Steps / HR"
  // or "mg/dL", always the specific metric label (e.g. "HR (bpm)", "HRV (ms)",
  // "Glucose (mg/dL)"). When multiple metrics share an axis we join their
  // labels with a separator so the user knows what each line is.
  const labelForSide = (side) => {
    const sideSeries = finalSeries.filter(s => s._axis === side);
    const labels = sideSeries
      .map(s => (resolveMetric(s.key) || resolveMetric(s.name) || {}).label)
      .filter(Boolean);
    if (labels.length === 0) return undefined;
    return [...new Set(labels)].join(' · ');
  };
  const leftLabel  = chart.leftLabel  || labelForSide('left');
  const rightLabel = chart.rightLabel || (hasDualAxis ? labelForSide('right') : undefined);

  // Tooltip: append the metric's unit to each value (e.g. "150 bpm", "94 %").
  const tooltipFormatterCb = (value, name, ctx) => {
    const seriesKey = ctx?.dataKey || name;
    const m = resolveMetric(seriesKey) || resolveMetric(name);
    if (m?.unit) return [`${value} ${m.unit}`, m.label || name];
    return [value, name];
  };

  const renderSeries = (s, i) => {
    const color = s.color || colorFor(s.key) || colorFor(s.name) || CHART_COLORS[i % CHART_COLORS.length];
    const yId = s._axis;
    // Default type: right-axis biomarker series render as lines, left-axis large-scale as bars/area
    const seriesType = s.type || (hasDualAxis && yId === 'left' ? 'bar' : (chart.type || 'line'));
    if (seriesType === 'bar') {
      return <Bar key={s.key} dataKey={s.key} name={s.name} fill={color}
        yAxisId={yId} opacity={0.6} barSize={isMobile ? 14 : 22} />;
    }
    if (seriesType === 'area') {
      return <Area key={s.key} type="monotone" dataKey={s.key} name={s.name}
        stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2}
        yAxisId={yId} dot={false} />;
    }
    return <Line key={s.key} type="monotone" dataKey={s.key} name={s.name}
      stroke={color} strokeWidth={2}
      dot={{ r: chart.data.length <= 20 ? 3 : 0 }}
      strokeDasharray={s.dashed ? '5 5' : undefined}
      yAxisId={yId} />;
  };

  const ChartWrapper = useComposed ? ComposedChart : (chart.type === 'bar' ? BarChart : LineChart);

  return (
    <div className="inline-chart">
      <div className="inline-chart-title">{chart.title || 'Chart'}</div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ChartWrapper data={chart.data} margin={chartMargin}>
          <XAxis dataKey={chart.xKey || 'date'} tick={tickStyle}
            tickFormatter={d => d ? d.substring(0, 7) : ''} axisLine={false} tickLine={false} />
          <YAxis yAxisId="left" tick={tickStyle} axisLine={false} tickLine={false}
            width={axisWidth} domain={['auto','auto']}
            label={leftLabel ? { value: leftLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#8B6914' } } : undefined} />
          {hasDualAxis && <YAxis yAxisId="right" orientation="right" tick={tickStyle} axisLine={false} tickLine={false}
            width={axisWidth} domain={['auto','auto']}
            label={rightLabel ? { value: rightLabel, angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#8B6914' } } : undefined} />}
          <Tooltip contentStyle={{ fontSize:'12px', background:'var(--bg-card, #112240)', color:'var(--text-primary, #e2e8f0)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'6px', padding:'8px 12px' }}
            formatter={tooltipFormatterCb} />
          <Legend wrapperStyle={{ fontSize: isMobile ? '11px' : '12px' }} />
          {finalSeries.map((s, i) => renderSeries(s, i))}
        </ChartWrapper>
      </ResponsiveContainer>
    </div>
  );
}

// -- Inline Image Component --------------------------------------------------
function InlineImage({ image }) {
  if (image.error) return <div className="inline-image-error">Image generation failed: {image.error}</div>;
  return (
    <div className="inline-image">
      <img src={`data:${image.mime || 'image/png'};base64,${image.data}`} alt={image.prompt} />
      <div className="inline-image-caption">{image.prompt}</div>
    </div>
  );
}

// -- Loading / Error States --------------------------------------------------
function LoadingSpinner({ message }) { return <div className="loading-state"><div className="spinner"></div><p>{message||'Loading...'}</p></div>; }
// KAL-322 — inline loading line, in the page, with the retry state spoken plainly. Replaces the
// full-page spinner on Longevity and Health Record.
function LoadingInline({ label, retrying }) {
  return (
    <div className="load-inline" role="status" aria-live="polite">
      <span className="load-inline-dot" aria-hidden="true" />
      <span>{retrying ? `Still loading, retrying (${retrying} of ${RETRY_DELAYS_MS.length})…` : `${label || 'Loading'}…`}</span>
    </div>
  );
}
// KAL-322 — Today's real card frames with inline loading text, for a first-ever load with no
// cached summary. Same classes and titles as the live cards, so nothing pops or reflows.
function TodayV2Frames({ retrying }) {
  const line = retrying ? `Still loading, retrying (${retrying} of ${RETRY_DELAYS_MS.length})…` : 'Loading…';
  const d = new Date();
  const stamp = `${d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()} ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()}`;
  return (
    <>
      <div className="tv2-appbar"><span className="tv2-who">Today</span><span className="tv2-stamp">{stamp}</span></div>
      <div className="tv2-zdiv tv2-zdiv-bright"><span className="tv2-zt">Kalpa Age</span><span className="tv2-zl" /></div>
      <div className="tv2-card tv2-frame"><div className="tv2-cardtitle">Kalpa Age</div><div className="tv2-loading-line">{line}</div></div>
      <div className="tv2-zdiv"><span className="tv2-zt">Today</span><span className="tv2-zl" /></div>
      {['Steps', 'Sleep', 'Active minutes', 'Resting heart rate'].map(t => (
        <div key={t} className="tv2-card tv2-frame"><div className="tv2-cardtitle">{t}</div><div className="tv2-loading-line">{line}</div></div>
      ))}
    </>
  );
}
function ErrorState({ message, onRetry }) { return <div className="error-state"><div className="error-icon">&#9888;</div><p>{message}</p>{onRetry && <button className="retry-btn" onClick={onRetry}>Retry</button>}</div>; }

// -- Date formatting helpers -------------------------------------------------
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function smartDateFormat(d) {
  // Single sitewide format (DD-MM-YYYY). Extra `data` arg (call sites pass the
  // series) is intentionally ignored — recharts thins ticks itself.
  return formatDate(d);
}

// -- Clinical direction hints ------------------------------------------------
const MARKER_DIRECTION = {
  'HDL':'higher is better','LDL':'lower is better','Total Cholesterol':'lower is better',
  'Triglycerides':'lower is better','Non-HDL Cholesterol':'lower is better','TC/HDL Ratio':'lower is better',
  'VLDL':'lower is better','ApoB':'lower is better','Lp(a)':'lower is better',
  'Glucose':'lower is better','HbA1c':'lower is better','ALT':'lower is better','AST':'lower is better',
  'ALP':'lower is better','Bilirubin':'lower is better','Creatinine':'lower is better',
  'Urea':'lower is better','BUN':'lower is better','Uric Acid':'lower is better',
  'CRP':'lower is better','ESR':'lower is better','TSH':'in range is optimal',
  'Hemoglobin':'higher is better','Vitamin D':'higher is better','B12':'higher is better',
  'Folate':'higher is better','Ferritin':'in range is optimal','Iron':'in range is optimal',
  'Calcium':'in range is optimal','Sodium':'in range is optimal','Potassium':'in range is optimal',
};

// Colours mirror backend marker_engine._status_color. A beneficial high/low
// (HDL 76, B12 996) is GREEN not red (5B); a suspected data error (iron 1.0) is
// grey and never rendered as a real flag (5D).
const STATUS_COLORS = { normal:'#22c55e', high:'#ef4444', low:'#f59e0b', high_borderline:'#f59e0b', low_borderline:'#f59e0b', high_good:'#22c55e', low_good:'#22c55e', data_error:'#9ca3af' };
const STATUS_BG = { normal:'#f0fdf4', high:'#fef2f2', low:'#fffbeb', high_borderline:'#fffbeb', low_borderline:'#fffbeb', high_good:'#f0fdf4', low_good:'#f0fdf4', data_error:'#f3f4f6' };

// -- MarkerSparkline ---------------------------------------------------------
const TIER_COLORS = { 1: '#8B6914', 2: '#C9973A', 3: '#112240' };
const TIER_LABELS_MAP = { 1: 'Unverified', 2: 'Lab-Verified', 3: 'Insurance-Grade' };

function MarkerSparkline({ marker, showOptimal, onClick }) {
  const isMobile = useIsMobile();
  const { history, refLow, refHigh, optimalLow, optimalHigh, optimalSource, optimalStatus,
          displayName, unit, description, status, latest, name, scoreTier } = marker;
  const [hover, setHover] = useState(false);
  if (!history || history.length === 0) return <div className="spark-card"><div className="spark-header"><span className="spark-name">{displayName}</span><span className="spark-nodata">No data</span></div></div>;
  const chartData = history.map(h=>({date:h.date,value:h.value,source_type:h.source_type||'user_upload'}));
  const vals = history.map(h=>h.value);
  const mn=Math.min(...vals), mx=Math.max(...vals), pad=(mx-mn)*0.2||(mx*0.1)||1;
  const hasOptimal = showOptimal && (optimalLow!=null || optimalHigh!=null);
  const yMin=Math.min(mn-pad,refLow!=null?refLow*0.9:mn-pad,hasOptimal&&optimalLow!=null?optimalLow*0.9:mn-pad);
  const yMax=Math.max(mx+pad,refHigh!=null?refHigh*1.1:mx+pad,hasOptimal&&optimalHigh!=null?optimalHigh*1.1:mx+pad);
  const optimalLabel = (optimalLow!=null||optimalHigh!=null) ? `Longevity target: ${optimalLow!=null?optimalLow:'<'}${optimalLow!=null&&optimalHigh!=null?' \u2013 ':''}${optimalHigh!=null?optimalHigh:'>'} ${unit}` : null;
  const sparkHeight = isMobile ? 50 : 80;
  return (
    <div className="spark-card" data-status={status} style={{borderLeft:`3px solid ${STATUS_COLORS[status]}`,backgroundColor:STATUS_BG[status],cursor:onClick?'pointer':undefined}}
      /* Touch-safe hover: on touch, mouseenter fires but mouseleave NEVER does, so the
         old onMouseEnter/onMouseLeave left `hover` stuck true → the .spark-tooltip
         stayed as a black box over the card after tapping through to the detail view
         and back. Gate hover to real mouse pointers, and always clear it on
         leave/cancel and when the card is tapped to open its detail. */
      onPointerEnter={(e)=>{ if(e.pointerType==='mouse') setHover(true); }}
      onPointerLeave={()=>setHover(false)} onPointerCancel={()=>setHover(false)}
      onClick={onClick?(e)=>{ setHover(false); onClick(e); }:undefined}>
      <div className="spark-header">
        <div className="spark-header-row1">
          <div className="spark-name-wrap"><span className="spark-name">{autoGlossary(displayName)}</span>{MARKER_DIRECTION[name]&&<span className="spark-direction">{autoGlossary(MARKER_DIRECTION[name])}</span>}</div>
          {marker.sourceType ? <ProvenanceBadge sourceType={marker.sourceType} sourceProvider={marker.sourceProvider} identityVerified={marker.identityVerified} /> : scoreTier && <span className="tier-dot" title={TIER_LABELS_MAP[scoreTier]||'Unverified'} style={{display:'inline-block',width:'6px',height:'6px',borderRadius:'50%',backgroundColor:TIER_COLORS[scoreTier]||'#8B6914',marginLeft:'4px',verticalAlign:'middle'}}/>}
        </div>
        <div className="spark-header-row2">
          <span className="spark-value" style={{color:STATUS_COLORS[status],fontWeight:700,fontSize:'1.2em'}}>{latest.value}</span><span className="spark-unit">{unit}</span>
          {marker.dataError
            ? <span className="spark-dataerror" title={marker.dataNote||'This value looks unusual — worth confirming with a re-test.'}>⚠ check this result</span>
            : status==='normal'&&optimalStatus&&optimalStatus!=='optimal'&&<span className="spark-optimal-flag" title={optimalLabel}>*</span>}
        </div>
      </div>
      <div className="spark-chart">
        <ResponsiveContainer width="100%" height={sparkHeight}>
          <LineChart data={chartData} margin={{top:5,right:8,bottom:12,left:2}}>
            {refLow!=null&&refHigh!=null&&<ReferenceArea y1={refLow} y2={refHigh} fill="#22c55e" fillOpacity={0.08} stroke="none"/>}
            {hasOptimal&&optimalLow!=null&&optimalHigh!=null&&<ReferenceArea y1={optimalLow} y2={optimalHigh} fill="#3b82f6" fillOpacity={0.10} stroke="none"/>}
            {hasOptimal&&optimalLow!=null&&optimalHigh==null&&<ReferenceLine y={optimalLow} stroke="#3b82f6" strokeDasharray="4 2" strokeWidth={1}/>}
            {hasOptimal&&optimalHigh!=null&&optimalLow==null&&<ReferenceLine y={optimalHigh} stroke="#3b82f6" strokeDasharray="4 2" strokeWidth={1}/>}
            {refLow!=null&&<ReferenceLine y={refLow} stroke="#8B6914" strokeDasharray="2 3" strokeWidth={0.5}/>}
            {refHigh!=null&&<ReferenceLine y={refHigh} stroke="#8B6914" strokeDasharray="2 3" strokeWidth={0.5}/>}
            <XAxis dataKey="date" tick={{fontSize:9,fill:'#8B6914'}} tickFormatter={d=>smartDateFormat(d,chartData)} interval="preserveStartEnd" axisLine={false} tickLine={false} padding={{left:6,right:6}}/>
            <YAxis domain={[yMin,yMax]} hide/>
            <Tooltip formatter={v=>[`${v} ${unit||''}`,displayName]} contentStyle={{fontSize:'11px',padding:'4px 8px',background:'var(--bg-card, #112240)',color:'var(--text-primary, #e2e8f0)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'6px'}}/>
            <Line type="monotone" dataKey="value" stroke={STATUS_COLORS[status]} strokeWidth={2} activeDot={{r:5}}
              dot={(props)=>{const{cx,cy,payload}=props;if(cx==null||cy==null)return null;const st=payload.source_type;const isLab=st==='lab_api_direct';const isBluecrest=st&&(st.includes('bluecrest')||st.includes('lab_upload'));const r=isLab?5:4;const fill=isLab?'#16a34a':isBluecrest?'#2563eb':'transparent';const stroke=isLab?'#22c55e':isBluecrest?'#3b82f6':'#8B6914';return<circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={1.5}/>;}}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {(()=>{const types=new Set(chartData.map(d=>d.source_type));const items=[];if(types.has('lab_api_direct'))items.push({color:'#22c55e',label:'NPL Direct'});if([...types].some(t=>t&&(t.includes('bluecrest')||t.includes('lab_upload'))))items.push({color:'#3b82f6',label:'Lab Upload'});if(types.has('user_upload')||types.has(undefined))items.push({color:'#8B6914',label:'Self-Reported'});return items.length>1?<div className="spark-source-legend">{items.map(it=><span key={it.label} className="spark-source-item"><span className="spark-source-dot" style={{backgroundColor:it.color}}/>{it.label}</span>)}</div>:null;})()}
      {hover&&(description||optimalSource)&&<div className="spark-tooltip">{autoGlossary(description)}{optimalSource&&<div className="spark-optimal-info">{autoGlossary(optimalLabel)} <span className="spark-optimal-source">({optimalSource})</span></div>}</div>}
      <div className="spark-footer">{history.length>1&&<span>{formatDate(history[0].date)} &rarr; {formatDate(history[history.length-1].date)}</span>}<span>{history.length} reading{history.length!==1?'s':''}</span></div>
    </div>
  );
}

// -- PanelCard ---------------------------------------------------------------
// forceExpanded:
//   true  — always show contents (Expand All / Longevity-flag mode for a flagged panel)
//   false — always hidden (Collapse All / Longevity-flag mode for an un-flagged panel)
//   null  — auto: open if there are flags, closed otherwise (default mode)
function PanelCard({ panel, forceExpanded, showOptimal, onMarkerClick }) {
  const autoOpen = panel.flagCount > 0;
  const initialOpen = forceExpanded == null ? autoOpen : !!forceExpanded;
  const [expanded, setExpanded] = useState(initialOpen);
  useEffect(() => {
    setExpanded(forceExpanded == null ? autoOpen : !!forceExpanded);
  }, [forceExpanded, autoOpen]);
  const flagged=panel.markers.filter(m=>m.status!=='normal'), normal=panel.markers.filter(m=>m.status==='normal');
  return (
    <div className="panel-card">
      <div className="panel-header" onClick={()=>setExpanded(!expanded)}>
        <div className="panel-header-left"><span className="panel-expand">{expanded?'\u25BC':'\u25B6'}</span><h3 className="panel-title">{panel.name}</h3><span className="panel-count">{panel.markerCount} markers</span></div>
        <div>{panel.flagCount>0?<span className="badge-flag">&#9888; {panel.flagCount} flag{panel.flagCount>1?'s':''}</span>:<span className="badge-ok">&#10004; All normal</span>}</div>
      </div>
      {expanded&&<div className="panel-grid">{flagged.map(m=><MarkerSparkline key={m.name} marker={m} showOptimal={showOptimal} onClick={()=>onMarkerClick&&onMarkerClick(m)}/>)}{normal.map(m=><MarkerSparkline key={m.name} marker={m} showOptimal={showOptimal} onClick={()=>onMarkerClick&&onMarkerClick(m)}/>)}</div>}
    </div>
  );
}

// -- ProvenanceBadge ---------------------------------------------------------
function ProvenanceBadge({ sourceType, sourceProvider, identityVerified }) {
  if (sourceType === 'lab_api_direct' || identityVerified) {
    const shortLabel = sourceProvider ? sourceProvider.replace(/ Kathmandu/i,'').replace(/National Path Lab/i,'NPL') : 'Lab';
    const tooltip = `Lab Verified \u00b7 ${sourceProvider||'Laboratory'} \u00b7 NPHL Category A`;
    return <span className="provenance-badge verified" title={tooltip}>{'\uD83D\uDD12'} {shortLabel}</span>;
  }
  return <span className="provenance-badge self-reported" title="Self-Reported \u00b7 User uploaded">{'\uD83D\uDD13'}</span>;
}

// -- ProvenanceCards (compact badge pills) ------------------------------------
function ProvenanceCards({ summary }) {
  if (!summary) return null;
  return (
    <div className="provenance-badges-row">
      <div className="provenance-badge-compact verified">
        <div className="badge-icon-wrap">
          <span className="lock-icon">{'\uD83D\uDD12'}</span>
          <span className="badge-count">{summary.labVerified}</span>
        </div>
        <span className="badge-label">Lab Verified</span>
        <span className="badge-sublabel">Direct laboratory API</span>
      </div>
      <div className="provenance-badge-compact self-reported">
        <div className="badge-icon-wrap">
          <span className="lock-icon">{'\uD83D\uDD13'}</span>
          <span className="badge-count">{summary.selfReported}</span>
        </div>
        <span className="badge-label">Self-Reported</span>
        <span className="badge-sublabel">User uploaded</span>
      </div>
    </div>
  );
}

// -- NPL Order Modal ---------------------------------------------------------
const NPL_PANELS = [
  { id: 'essentials', name: 'Longevity Essentials', markers: 'CBC, CRP, HbA1c, Lipid Panel, Glucose, Albumin', priceNPR: '4,500', priceGBP: '~\u00a327', why: 'Core PhenoAge inputs for biological age accuracy' },
  { id: 'inflammation', name: 'Longevity + Inflammation', markers: 'Above + LFT, KFT, Vitamin D, Thyroid', priceNPR: '8,500', priceGBP: '~\u00a351', why: 'Full biological age panel \u2014 removes all data gaps' },
  { id: 'annual', name: 'Annual Subscription', markers: 'All markers quarterly', priceNPR: '15,000/yr', priceGBP: '~\u00a390/yr', why: 'Track your trajectory across the year' },
];

function NPLOrderModal({ onClose, onOrder }) {
  const closeModal = useModalHashRoute('#npl-order', onClose);
  const features = useFeatures();
  const [selected, setSelected] = useState('inflammation');
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={closeModal}>
      <div className="npl-order-modal" onClick={e => e.stopPropagation()}>
        <h3>Complete Your Longevity Profile</h3>
        <p className="npl-order-sub">Order directly from National Path Lab &middot; Results appear automatically as Lab Verified</p>
        <div className="npl-panels">
          {NPL_PANELS.map(p => (
            <label key={p.id} className={`npl-panel-card ${selected === p.id ? 'selected' : ''}`} onClick={() => setSelected(p.id)}>
              <input type="radio" name="npl-panel" checked={selected === p.id} onChange={() => setSelected(p.id)} />
              <div className="npl-panel-body">
                <div className="npl-panel-name">{p.name}</div>
                <div className="npl-panel-markers">{p.markers}</div>
                <div className="npl-panel-price">NPR {p.priceNPR} &middot; {p.priceGBP}</div>
                <div className="npl-panel-why">{T(p.why)}</div>
              </div>
            </label>
          ))}
        </div>
        {/* W6: removed the fabricated Longevity-Points loyalty block (hardcoded "340
            points / NPR 340 / 68% bar / +10/+50/+25"). No points oracle exists, so the
            numbers were invented; gamification is off on NPL anyway. Re-add only behind a
            real points data source. */}
        <div className="npl-order-footer">
          <a href="https://apps.apple.com/np/app/national-path-lab/id6740814706" target="_blank" rel="noopener noreferrer" className="action-btn npl-order-btn" onClick={(e) => { onOrder(); closeModal(); }}>Order via NPL App</a>
          <button className="action-btn secondary" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── NPL Order Nudge System ──────────────────────────────────────────────
// Contextual nudges to order an NPL test, based on the user's actual data.
// Backend (/api/order-prompts) returns prompts only when triggers fire.
// Card layout: gradient bg, headline, subtext, primary order button with
// price baked in, "Not now" dismiss. Two-step: tap → confirmation modal.

// ── KAL-276 (4.4) — THE AVAILABILITY CHECK ────────────────────────────────────────────────────
// A suggested test is ORDERABLE iff:
//   1. the order_lab flag is on for this instance, AND
//   2. the suggestion carries a test_code, AND
//   3. that code exists in NPL_TEST_CATALOGUE_FE — the tests the lab actually offers.
// Anything else renders NO order/re-test action at all: the reasoning stays visible as a note,
// but a button that leads to an unorderable request is a dead end dressed as an action.
// KAL-303 — THE ONE ORDERING GATE for the client: the instance offers ordering AND this user is
// someone NPL can actually serve. Every order affordance reads this, never the flag alone.
function _orderingOn() {
  return !!(FEATURES_GLOBAL.order_lab && FEATURES_GLOBAL.order_eligible);
}

function _testOrderable(prompt) {
  return !!(_orderingOn()
            && prompt && prompt.test_code
            && (NPL_TEST_CATALOGUE_FE[prompt.test_code]
                || prompt.test_code === 'PANEL'));   // PANEL = the whole-panel booking flow
}

function OrderPromptCard({ prompt, onDismiss, onOrder }) {
  const orderable = _testOrderable(prompt);
  return (
    <div className="npl-gradient-card order-prompt-card" style={{
      padding: '12px 16px',
      margin: '12px 0 0',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="32" height="32"
        style={{position:'absolute', top:6, right:8, opacity:0.18}}
      >
        {/* Stylised tulip — purely decorative NPL motif */}
        <path d="M12 2 C 9 6, 9 10, 12 12 C 15 10, 15 6, 12 2 Z" fill="#EFD659" />
        <path d="M12 12 L 12 22" stroke="#7FDFDF" strokeWidth="1.5" fill="none" />
        <path d="M12 16 C 9 17, 8 18, 8 20" stroke="#7FDFDF" strokeWidth="1" fill="none" />
      </svg>
      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>
        {prompt.headline}
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.4 }}>
        {prompt.subtext}
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {orderable && (
        <button
          onClick={() => onOrder(prompt)}
          style={{
            flex: '0 0 auto',
            background: 'var(--accent, #7F91DF)',
            color: 'var(--on-accent, #1a1a1a)',   // KAL-256: white on the accent is 1.56:1
            border: 'none',
            borderRadius: 8,
            padding: '7px 14px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {prompt.action === 'retest' ? 'Re-test' : 'Order'} {prompt.test_name}
        </button>
        )}
        {prompt.dismissible !== false && (
          <button
            onClick={() => onDismiss(prompt)}
            style={{ flex:'0 0 auto', background:'transparent', border:'1px solid var(--border-color, rgba(255,255,255,0.18))', borderRadius:8, fontSize:12, color:'var(--text-dim)', cursor:'pointer', padding:'7px 14px', whiteSpace:'nowrap' }}
          >
            Not now
          </button>
        )}
      </div>
    </div>
  );
}

// THE single order component (FIX 4). A prefilled request form for the most
// appropriate NPL test for a flagged marker. No live order system / no pricing in
// the pilot — submit logs the request + notifies Bimal server-side (FIX 3).
function OrderConfirmModal({ prompt, onClose, onConfirmed }) {
  const closeModal = useModalHashRoute('#npl-order-confirm', onClose);
  useEffect(() => {   // KAL-276 (4.3) — Escape dismisses; an exit that no clipping can remove
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeModal]);
  const [collection, setCollection] = useState('visit_npl');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const markerName = prompt.marker || prompt.biomarker || '';
  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post('/npl/order', {
        test_code: prompt.test_code || '',
        test_name: prompt.test_name,
        marker: markerName,
        collection_type: collection,
        trigger_type: prompt.trigger_type || '',
      });
      setDone(r);
      onConfirmed && onConfirmed(r);
    } catch (e) {
      setError(e.message || 'Could not send the request');
    }
    setSubmitting(false);
  };
  // KAL-276 (4.3) — RENDERED THROUGH A PORTAL, and this is the whole of Tim's "could not be
  // scrolled and had no exit" bug. This modal used to render INLINE in whatever component
  // invoked it — including the CHAT, whose .ask-drawer carries a persistent CSS transform.
  // A transformed ancestor makes position:fixed lay out relative to ITSELF, so this overlay's
  // 100dvh bottom-sheet geometry was computed against an 80vh drawer: the panel's scroll region
  // and its ✕ landed outside the drawer's clip — unscrollable, unexitable. The portal pins it
  // to document.body where fixed means fixed. (BiologicalAgeMethodologyModal already did this;
  // this one was the outlier.) Escape also dismisses now — a second exit that cannot be clipped.
  return ReactDOM.createPortal(
    <div className="metric-detail-overlay order-confirm-overlay" onClick={closeModal}>
      <div className="metric-detail-panel npl-gradient-card" onClick={e => e.stopPropagation()} style={{maxWidth:480}}>
        <div className="metric-detail-head">
          <div>
            <div className="metric-detail-label">REQUEST A TEST</div>
            <div className="metric-detail-current" style={{ fontSize: '1.3rem' }}>{prompt.test_name}</div>
          </div>
          <button className="metric-detail-close" onClick={closeModal}>×</button>
        </div>
        {done ? (
          <div style={{padding:'10px 0 4px'}}>
            <div style={{fontSize:14, fontWeight:600, color:'#16a34a'}}>✓ Request sent to NPL</div>
            <div style={{fontSize:13, color:'var(--text-secondary)', marginTop:6, lineHeight:1.5}}>
              NPL has your request for <strong>{prompt.test_name}</strong> (ref {done.order_ref}). They’ll be in touch to arrange your sample.
            </div>
            <div style={{display:'flex', justifyContent:'flex-end', marginTop:14}}>
              <button onClick={closeModal} style={{background:'var(--accent, #7F91DF)', color:'var(--on-accent, #1a1a1a)', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer'}}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{padding:'6px 0 12px', fontSize:13, color:'var(--text-secondary)', lineHeight:1.5}}>
              {markerName ? <>Recommended for your <strong>{markerName}</strong>. </> : null}
              Free for pilot participants · results return as lab-verified · we’ll send your request to NPL to arrange.
            </div>
            <div style={{borderTop:'1px solid var(--border-color)', paddingTop:12}}>
              <div style={{fontSize:12, fontWeight:600, color:'var(--text-primary)', marginBottom:8}}>Sample collection</div>
              <label style={{display:'flex', alignItems:'center', gap:8, padding:'6px 0', cursor:'pointer'}}>
                <input type="radio" name="collection" value="visit_npl" checked={collection==='visit_npl'} onChange={()=>setCollection('visit_npl')} />
                <span style={{fontSize:13}}>Visit NPL (Maharajgunj)</span>
              </label>
              <label style={{display:'flex', alignItems:'center', gap:8, padding:'6px 0', cursor:'pointer'}}>
                <input type="radio" name="collection" value="home" checked={collection==='home'} onChange={()=>setCollection('home')} />
                <span style={{fontSize:13}}>Home collection</span>
              </label>
            </div>
            {error && <div style={{color:'var(--status-flagged, #dc2626)', fontSize:12, marginTop:8}}>{error}</div>}
            <div style={{display:'flex', gap:8, marginTop:14, justifyContent:'flex-end'}}>
              <button onClick={closeModal} style={{background:'none', border:'1px solid var(--border-color)', borderRadius:8, padding:'8px 14px', fontSize:13, cursor:'pointer', color:'var(--text-secondary)'}}>Cancel</button>
              <button onClick={confirm} disabled={submitting} style={{background:'var(--accent, #7F91DF)', color:'var(--on-accent, #1a1a1a)', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor: submitting ? 'wait' : 'pointer'}}>
                {submitting ? 'Sending…' : 'Send request to NPL'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Hook: fetch /api/order-prompts once when the placement view mounts.
// Returns { prompts, dismiss, refresh } — prompts is filtered by `placement`
// (so each tab only sees the prompts it owns).
function useOrderPrompts(placement) {
  const features = useFeatures();
  const [prompts, setPrompts] = useState([]);
  const [confirmingPrompt, setConfirmingPrompt] = useState(null);
  const refresh = useCallback(() => {
    if (!_orderingOn()) return;   // KAL-303
    api.get('/order-prompts').then(r => {
      const list = (r && r.prompts) || [];
      setPrompts(placement ? list.filter(p => p.placement === placement) : list);
    }).catch(() => {});
  }, [features.order_lab, placement]);
  useEffect(() => { refresh(); }, [refresh]);
  const dismiss = (p) => {
    setPrompts(prev => prev.filter(x => x.key !== p.key));
    api.post('/order-prompts/dismiss', { key: p.key }).catch(() => {});
  };
  const order = (p) => setConfirmingPrompt(p);
  return { prompts, dismiss, order, confirmingPrompt, setConfirmingPrompt, refresh };
}

// NPL Corporate Health tab — anonymised pilot metrics for admins.
// Aggregates only; never returns or displays per-user data.
// One-shot consent screen shown to NPL users who have a patient-id
// mapping but haven't yet agreed to the lab data import. Renders as a
// full-screen overlay above the rest of the app — once the user picks
// 'Import' or 'Not now' the screen unmounts and won't reappear (a
// deferred decision flips the UI to a small reminder banner instead).
function NplConsentScreen({ onAccepted, onDeferred }) {
  const [boxes, setBoxes] = useState({
    record_confirmed:   false,
    insights_accepted:  false,
    privacy_accepted:   false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const allChecked = boxes.record_confirmed && boxes.insights_accepted && boxes.privacy_accepted;

  const toggle = (k) => setBoxes(b => ({ ...b, [k]: !b[k] }));

  const handleAccept = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true); setError(null);
    try {
      await api.post('/npl/consent', {
        consent_version: '1.0',
        checkboxes: Object.entries(boxes).filter(([, v]) => v).map(([k]) => k),
      });
      onAccepted && onAccepted();
    } catch (e) {
      setError(e.message || 'Could not record consent — please try again.');
      setSubmitting(false);
    }
  };

  const handleDefer = async () => {
    if (submitting) return;
    setSubmitting(true);
    try { await api.post('/npl/consent/defer', {}); } catch {}
    onDeferred && onDeferred();
  };

  const cb = (k, label) => (
    <label style={{display:'flex', alignItems:'flex-start', gap:10, padding:'10px 0', cursor:'pointer'}}>
      <input
        type="checkbox"
        checked={boxes[k]}
        onChange={() => toggle(k)}
        style={{marginTop:4, width:16, height:16, accentColor:'var(--npl-blue, #7F91DF)', cursor:'pointer'}}
      />
      <span style={{fontSize:13, color:'var(--text-primary)', lineHeight:1.45}}>{label}</span>
    </label>
  );

  return (
    <div style={{
      position:'fixed', inset:0, zIndex: 9999,
      background:'rgba(20, 30, 50, 0.55)',
      display:'flex', alignItems:'flex-start', justifyContent:'center',
      padding:'24px 16px', overflowY:'auto',
    }}>
      <div style={{
        maxWidth:520, width:'100%', background:'#FFFFFF',
        borderRadius:14, padding:'28px 26px',
        boxShadow:'0 12px 40px rgba(0,0,0,0.18)',
        marginTop:'4vh',
      }}>
        <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:18}}>
          <img
            src={FEATURES_GLOBAL.branding.logo_url || '/static/npl_logo.png'}
            alt={FEATURES_GLOBAL.branding.app_name || 'NPL Health'}
            style={{height:40, width:'auto', objectFit:'contain'}}
          />
        </div>
        <h2 style={{
          fontFamily:"'Satoshi', sans-serif", fontWeight:500, fontSize:20,
          color:'var(--npl-deep-blue, #1B3A5C)', margin:'0 0 14px 0',
        }}>
          Import your NPL health records
        </h2>
        <div style={{fontSize:13.5, color:'var(--text-primary)', lineHeight:1.55, marginBottom:16}}>
          With your consent, NPL Health will use your blood test results from
          National Path Lab to power your health dashboard. Automatic import
          isn't switched on yet &mdash; for now, please upload your lab reports
          in the app and they'll appear in your dashboard. Once NPL's system is
          connected, we'll import new results automatically.
        </div>
        <div style={{fontSize:13, color:'var(--text-primary)', marginBottom:14}}>
          <strong style={{display:'block', marginBottom:6}}>What will be imported:</strong>
          <ul style={{margin:0, paddingLeft:20, lineHeight:1.55}}>
            <li>Your blood test results from NPL's laboratory</li>
            <li>Test dates and reference ranges</li>
            <li>Nothing else — no personal contact details, billing information, or other records</li>
          </ul>
        </div>
        <div style={{fontSize:13, color:'var(--text-primary)', marginBottom:18}}>
          <strong style={{display:'block', marginBottom:6}}>How your data is used:</strong>
          <ul style={{margin:0, paddingLeft:20, lineHeight:1.55}}>
            <li>Only you can see your results in this app</li>
            <li>Results are used to generate your personal health insights</li>
            <li>NPL Health staff cannot view your individual health data</li>
            <li>You can request deletion of your data at any time in Data &amp; Privacy settings</li>
          </ul>
        </div>
        <div style={{borderTop:'1px solid var(--border-color)', paddingTop:10, marginBottom:18}}>
          {cb('record_confirmed',  'I confirm this is my NPL health record and I consent to importing my results into NPL Health.')}
          {cb('insights_accepted', 'I understand my results will be used to generate personal health insights within this app.')}
          {cb('privacy_accepted',  <>I have read and accept the NPL Health Privacy Notice. <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:'var(--npl-blue, #7F91DF)'}}>View Privacy Notice</a></>)}
        </div>
        {error && <div style={{color:'#dc2626', fontSize:12.5, marginBottom:10}}>{error}</div>}
        <button
          type="button"
          onClick={handleAccept}
          disabled={!allChecked || submitting}
          style={{
            width:'100%', padding:'12px 16px', borderRadius:10,
            background: allChecked ? 'var(--npl-blue, #7F91DF)' : '#cbd5e1',
            color:'#FFFFFF', border:'none', fontWeight:600, fontSize:14,
            cursor: allChecked && !submitting ? 'pointer' : 'not-allowed',
            marginBottom:8,
          }}
        >
          {submitting ? 'Recording consent…' : 'Import my results'}
        </button>
        <button
          type="button"
          onClick={handleDefer}
          disabled={submitting}
          style={{
            display:'block', width:'100%', textAlign:'center',
            background:'none', border:'none', padding:'10px',
            color:'var(--text-secondary)', fontSize:12.5,
            cursor: submitting ? 'not-allowed' : 'pointer',
            textDecoration:'underline',
          }}
        >
          Not now — I&apos;ll do this later
        </button>
      </div>
    </div>
  );
}

// Small dismissible banner shown on the home view when the user
// previously deferred the NPL import consent. Clicking "Import now"
// re-opens the full consent screen.
function NplConsentDeferredBanner({ onOpen }) {
  return (
    <div style={{
      background:'#FFF7E6', border:'1px solid #F5B547', borderRadius:10,
      padding:'10px 14px', marginBottom:12, display:'flex',
      alignItems:'center', gap:10, fontSize:13, color:'#112240',
    }}>
      <span style={{fontSize:'1.05rem'}}>{'ⓘ'}</span>
      <span style={{flex:1}}>Your NPL results haven&apos;t been imported yet.</span>
      <button
        onClick={onOpen}
        style={{
          background:'var(--npl-blue, #7F91DF)', color:'#FFFFFF', border:'none',
          padding:'6px 14px', borderRadius:8, cursor:'pointer', fontWeight:600,
          fontSize:12.5, whiteSpace:'nowrap',
        }}
      >Import now</button>
    </div>
  );
}

// ── NPL first-run onboarding wizard (web) ────────────────────────────────
// Shown full-screen after OTP login on the NPL instance until the web steps
// of onboarding_state are complete. Resumes at the first incomplete step
// (never restarts). Sequence: welcome → all consents together → minimal
// profile (skippable) → install the NPL Health app (Download APK).
// Server is the source of truth (/api/onboarding/state); this drives it.
const _NPL_BLUE = 'var(--npl-blue, #7F91DF)';
const _NPL_DEEP = 'var(--npl-deep-blue, #1B3A5C)';

function _serverStepToFlow(s) {
  // Map the server's next_web_step to a local wizard screen.
  if (!s) return 'install';
  if (s === 'accepted_policies') return 'welcome';
  if (s === 'consent_health' || s === 'consent_ai' || s === 'consent_lab_link') return 'consents';
  if (s === 'profile_basic') return 'profile';
  if (s === 'lifestyle_basic') return 'lifestyle';
  if (s === 'app_installed_ack') return 'install';
  return 'welcome';
}

// ── RUN W — shared lifestyle inputs (chips, not dropdowns) ────────────────────
// One component drives BOTH the onboarding wizard step AND the Profile → Personal
// Information edit panel (two-way sync). Founding constraints: single-select
// CHIPS/segmented controls (never dropdowns); EVERY categorical question offers
// "Prefer not to say" (+ "Other" free-text for diet); diet is MULTI-select;
// smoking and vaping are SEPARATE questions (never merged — life-insurance
// fairness). Weight is NOT typed as a static value: we surface the measured
// wearable value + BMI + trend, with manual entry only as a fallback. All chips
// use the §C themed input tokens so contrast holds in light AND dark.
const _DIET_OPTS = [
  ['omnivore', 'Omnivore'], ['pescatarian', 'Pescatarian'], ['vegetarian', 'Vegetarian'],
  ['vegan', 'Vegan'], ['mostly_plant_based', 'Mostly plant-based'],
];
const _ALCOHOL_OPTS = [['none', 'None'], ['occasional', 'Occasional'], ['weekly', 'Weekly'], ['daily', 'Daily']];
const _TOBACCO_OPTS = [['never', 'Never'], ['former', 'Former'], ['current', 'Current']];
const _PNTS = 'prefer_not_to_say';
const _DIET_KNOWN = new Set(_DIET_OPTS.map(o => o[0]));

function _lifeChip(active) {
  return {
    padding: '8px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer', userSelect: 'none',
    border: active ? '1px solid var(--npl-indigo, #0D156A)' : '1px solid var(--border-color)',
    background: active ? 'var(--npl-indigo, #0D156A)' : 'var(--bg-input)',
    color: active ? '#FFFFFF' : 'var(--text-primary)',
    WebkitTextFillColor: active ? '#FFFFFF' : 'var(--text-primary)',
    fontWeight: active ? 600 : 500, transition: 'all 0.15s', display: 'inline-block',
  };
}

function _prettyLife(v) {
  if (!v) return null;
  if (v === _PNTS) return 'Prefer not to say';
  return String(v).replace(/^other:\s*/i, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// §P — weight surfaced from the wearable stream (manual fallback). Extracted from
// LifestyleFields so it can live in the Profile "Personal Info" section while the
// behaviour questions (diet/alcohol/smoking/vaping) sit in their own "Lifestyle"
// section — same component/styling in the onboarding gate and Profile.
function WeightField({ weight, onWeightManual }) {
  useLocale();   // re-render on a metric/imperial toggle
  const label = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: '2px 0 8px' };
  const hint = { fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 8 };
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={label}>Weight</div>
      {weight && weight.weight_kg != null ? (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>{fmtWeight(weight.weight_kg)}</span>
          {weight.bmi != null && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>BMI {weight.bmi}</span>}
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-card-raised)', color: 'var(--text-secondary)' }}>
            {weight.source === 'measured' ? 'from your device' : 'manually entered'}
          </span>
          {weight.source === 'measured' && Array.isArray(weight.trend) && weight.trend.length > 1 && (() => {
            const first = weight.trend[0].weight, last = weight.trend[weight.trend.length - 1].weight;
            const d = (last - first);
            return <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d > 0.2 ? '▲' : d < -0.2 ? '▼' : '▬'} {fmtWeightDelta(Math.abs(d))} over last {weight.trend.length}</span>;
          })()}
        </div>
      ) : (
        <div>
          <div style={hint}>Connect a smart scale to track automatically, or enter once — powers weight trends.</div>
          {/* LOCALE (2d): the box is labelled in the ACTIVE display unit and the typed number is
              converted back to kg before it leaves this component, so storage stays metric and
              every downstream calculation (BMI, bio-age) is untouched. */}
          <input type="number" placeholder={isImperial() ? 'e.g. 160' : 'e.g. 72'} defaultValue=""
            onBlur={e => { const kg = weightInputToKg(e.target.value); if (kg && !isNaN(kg) && onWeightManual) onWeightManual(kg); }}
            style={{ width: 140, padding: '8px 11px', borderRadius: 8, boxSizing: 'border-box' }} />
          <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{weightUnit()}</span>
        </div>
      )}
    </div>
  );
}

// §P — benefit-only microcopy per behaviour field (mirrors backend
// _PROFILE_VALUE_CAPTION). Shown in BOTH the onboarding gate and Profile because
// LifestyleFields is shared. Consents are neutral and never carry these.
const _LIFESTYLE_CAPTION = {
  diet:    'Helps explain how your eating relates to your blood markers — select all that apply.',
  alcohol: 'Adds context to liver and metabolic markers.',
  smoking: 'Key context for cardiovascular and respiratory markers.',
  vaping:  'Adds context to respiratory and cardiovascular insights.',
};

// §P — Personal-Info field captions (benefit-only) + the section intro. Consents
// are NEVER given these (they stay neutral). Kept in one place so the gate and the
// Profile menu render identical copy.
const _PROFILE_FIELD_CAPTION = {
  dob:            'Sets the baseline for your biological-age estimate.',
  biological_sex: 'Blood ranges differ by sex — this makes your results and bio-age more accurate. Optional.',
  height:         'Lets us track BMI and body trends over time.',
};
const _PROFILE_SECTION_INTRO = 'The more complete your profile, the sharper and more personal your insights. Every field is optional.';
const _profileIntroStyle = { fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 };
const _fieldCaptionStyle = { fontSize: 11.5, color: 'var(--text-secondary)', margin: '-6px 0 12px', lineHeight: 1.45 };

function LifestyleFields({ value, onChange, weight, onWeightManual, showWeight = true }) {
  const v = value || {};
  const diet = Array.isArray(v.diet) ? v.diet : [];
  // "Other" free-text = any stored diet item that isn't a known slug.
  const otherRaw = diet.find(d => !_DIET_KNOWN.has(d));
  const otherText = otherRaw ? otherRaw.replace(/^other:\s*/i, '') : '';
  const otherActive = !!otherRaw;

  const setDiet = (next) => onChange({ ...v, diet: next });
  const toggleDiet = (slug) => setDiet(diet.includes(slug) ? diet.filter(d => d !== slug) : [...diet, slug]);
  const setOther = (txt) => {
    const base = diet.filter(d => _DIET_KNOWN.has(d));
    setDiet(txt.trim() ? [...base, 'other: ' + txt.trim()] : base);
  };
  const setSingle = (key, slug) => onChange({ ...v, [key]: v[key] === slug ? '' : slug });

  const label = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: '2px 0 8px' };
  const hint = { fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 8 };
  const row = { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 };
  const block = { marginBottom: 20 };

  const singleGroup = (key, opts, title, sub) => (
    <div style={block}>
      <div style={label}>{title}</div>
      {sub && <div style={hint}>{sub}</div>}
      <div style={row}>
        {opts.map(([slug, lbl]) => (
          <span key={slug} role="button" tabIndex={0} style={_lifeChip(v[key] === slug)}
            onClick={() => setSingle(key, slug)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSingle(key, slug); } }}>{lbl}</span>
        ))}
        <span role="button" tabIndex={0} style={_lifeChip(v[key] === _PNTS)}
          onClick={() => setSingle(key, _PNTS)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSingle(key, _PNTS); } }}>Prefer not to say</span>
      </div>
    </div>
  );

  return (
    <div>
      {/* Diet — MULTI-select */}
      <div style={block}>
        <div style={label}>Diet</div>
        <div style={hint}>{_LIFESTYLE_CAPTION.diet}</div>
        <div style={row}>
          {_DIET_OPTS.map(([slug, lbl]) => (
            <span key={slug} role="button" tabIndex={0} style={_lifeChip(diet.includes(slug))}
              onClick={() => toggleDiet(slug)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDiet(slug); } }}>{lbl}</span>
          ))}
          <span role="button" tabIndex={0} style={_lifeChip(otherActive)}
            onClick={() => setOther(otherActive ? '' : ' ')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOther(otherActive ? '' : ' '); } }}>Other</span>
          <span role="button" tabIndex={0} style={_lifeChip(diet.length === 0)}
            onClick={() => setDiet([])}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDiet([]); } }}>Prefer not to say</span>
        </div>
        {otherActive && (
          <input type="text" value={otherText} placeholder="Describe your diet"
            onChange={e => setOther(e.target.value)}
            style={{ marginTop: 8, width: '100%', maxWidth: 320, padding: '8px 11px', borderRadius: 8, boxSizing: 'border-box' }} />
        )}
      </div>

      {singleGroup('alcohol', _ALCOHOL_OPTS, 'Alcohol', _LIFESTYLE_CAPTION.alcohol)}
      {singleGroup('smoking', _TOBACCO_OPTS, 'Smoking', _LIFESTYLE_CAPTION.smoking)}
      {/* Vaping is a SEPARATE question — never merged with smoking. */}
      {singleGroup('vaping', _TOBACCO_OPTS, 'Vaping', _LIFESTYLE_CAPTION.vaping)}

      {/* Weight lives here in the onboarding gate; in Profile it is rendered in the
          Personal Info section instead (showWeight=false). */}
      {showWeight && <WeightField weight={weight} onWeightManual={onWeightManual} />}
    </div>
  );
}

function NplOnboardingWizard({ state, profile, onState, onComplete }) {
  const appName = FEATURES_GLOBAL.branding.app_name || 'NPL Health';
  const policyVersion = state?.policy_version || '1.0';
  const isNative = typeof window !== 'undefined' && !!(window.ReactNativeWebView && window.ReactNativeWebView.postMessage);
  // NPL defaults to DARK, so the wizard MUST be legible there. The card adapts
  // to the theme (var(--card-bg)); isDark only drives the logo's white chip
  // (the multi-colour wordmark needs it on a dark surface, same as the header).
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-npl-theme') === 'dark';
  // §O — the step SEQUENCE is SERVER-DRIVEN by the card registry: the Welcome splash
  // (only if intro_seen === false) followed by the server's OUTSTANDING cards in order.
  // Already-satisfied cards never appear (auto-skip, e.g. profile_basic for a user who
  // already has name+DOB+height). We advance locally; each handler resolves its card
  // server-side (/complete | /skip | /intro-seen) BEFORE advancing so it never reappears.
  const _CARD_TO_STEP = { consents: 'consents', profile_basic: 'profile', lifestyle_basic: 'lifestyle', notifications_intent: 'notifications', connect_device: 'install' };
  const sequence = [
    ...(state?.intro_seen === false ? ['welcome'] : []),
    ...((state?.outstanding || []).map(c => _CARD_TO_STEP[c.id]).filter(Boolean)),
  ];
  const [stepIdx, setStepIdx] = useState(0);
  const step = sequence[stepIdx];
  const advance = () => {
    if (stepIdx + 1 >= sequence.length) { onComplete && onComplete(); }
    else { setStepIdx(i => i + 1); }
  };
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Consents screen state. health_data_processing is REQUIRED (explicit agree to
  // use the app). ai_processing + international_transfer_npl are OPTIONAL opt-ins,
  // DEFAULT OFF. Nothing is pre-ticked — every consent needs an active tap.
  const [boxes, setBoxes] = useState({ accepted_policies: false, consent_health: false, consent_ai: false });
  const [labLink, setLabLink] = useState(false); // optional; off until the user opts in
  // Only policies + health gate entry. AI + lab-link NEVER block completion.
  const allRequired = boxes.accepted_policies && boxes.consent_health;
  // "Select all" governs the OPTIONAL opt-ins only (AI + lab-link); it never
  // touches the required agrees and is user-initiated (not a pre-tick).
  const allOptionalSelected = boxes.consent_ai && labLink;
  const toggleSelectAll = () => {
    const next = !allOptionalSelected;
    setBoxes(b => ({ ...b, consent_ai: next }));
    setLabLink(next);
  };

  // Minimal profile (skippable)
  const [pf, setPf] = useState({
    display_name: profile?.display_name || profile?.displayName || '',
    dob: (profile?.dob || '').slice(0, 10),
    biological_sex: profile?.biological_sex || profile?.sex || '',
    height_cm: profile?.height_cm || profile?.height || '',
  });

  // Lifestyle (skippable). Prefilled from any facts already on file so the step
  // is resumable. Weight is surfaced read-only from the wearable stream.
  const [life, setLife] = useState({ diet: [], alcohol: '', smoking: '', vaping: '' });
  const [lifeWeight, setLifeWeight] = useState(null);
  const [lifeManualWeight, setLifeManualWeight] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get('/profile/lifestyle');
        if (cancelled || !d) return;
        setLife({ diet: d.diet || [], alcohol: d.alcohol || '', smoking: d.smoking || '', vaping: d.vaping || '' });
        setLifeWeight(d.weight || null);
      } catch (e) { /* non-fatal — start blank */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const post = async (body) => {
    // Consent SAVE via the existing endpoint (sets the consent timestamps → the
    // consents card becomes derived-complete). We advance the gate locally, so we do
    // NOT lift the legacy payload back to the parent (its shape lacks outstanding/intro_seen).
    return api.post('/onboarding/state', { policy_version: policyVersion, ...body });
  };

  const submitConsents = async () => {
    if (!allRequired || submitting) return;
    setSubmitting(true); setError(null);
    try {
      await post({ updates: {
        accepted_policies: true,
        consent_health: true,
        consent_ai: !!boxes.consent_ai,          // OPTIONAL opt-in — exactly what the user ticked
        consent_lab_link: !!labLink,             // OPTIONAL opt-in — default off
      }});
      advance();   // consents card resolves via the timestamps just written (derived-complete)
    } catch (e) {
      setError(e.message || 'Could not save your choices — please try again.');
    } finally { setSubmitting(false); }
  };

  const saveProfile = async (skip) => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    try {
      if (!skip) {
        const updates = {};
        if (pf.display_name) updates.display_name = pf.display_name;
        if (pf.dob) updates.dob = pf.dob;
        if (pf.biological_sex) updates.biological_sex = pf.biological_sex;
        if (pf.height_cm) updates.height_cm = Number(pf.height_cm) || undefined;
        if (Object.keys(updates).length) {
          try { await api.put('/users/current', updates); } catch (e) { /* non-fatal */ }
        }
      }
      await api.post('/onboarding/' + (skip ? 'skip' : 'complete'), { card: 'profile_basic' });
      advance();
    } catch (e) {
      setError(e.message || 'Could not save — please try again.');
    } finally { setSubmitting(false); }
  };

  const saveLifestyle = async (skip) => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    try {
      if (!skip) {
        const body = { diet: life.diet, alcohol: life.alcohol, smoking: life.smoking, vaping: life.vaping };
        if (lifeManualWeight) body.weight_kg = lifeManualWeight;
        try { await api.put('/profile/lifestyle', body); } catch (e) { /* non-fatal */ }
      }
      await api.post('/onboarding/' + (skip ? 'skip' : 'complete'), { card: 'lifestyle_basic' });
      advance();
    } catch (e) {
      setError(e.message || 'Could not save — please try again.');
    } finally { setSubmitting(false); }
  };

  // RUN B P11 — the ONBOARDING INTENT step. This records what the user WANTS and deliberately does
  // NOT call Notification.requestPermission(): the browser grants exactly one prompt per origin, and
  // spending it here — before the user has seen a single insight — is how apps get permanently blocked.
  // The real prompt is spent later, at a value moment, by the soft pre-prompt.
  const saveNotifIntent = async (want) => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    try {
      if (want === 'skip') {
        await api.post('/onboarding/skip', { card: 'notifications_intent' });
      } else {
        await api.post('/notifications/intent', { intent: want });   // no OS prompt fired
      }
      advance();
    } catch (e) {
      setError(e.message || 'Could not save — please try again.');
    } finally { setSubmitting(false); }
  };

  const finishInstall = async (dismiss) => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    try {
      // connect_device (web only): "installed/continue" completes it; "later" dismisses (skip).
      await api.post('/onboarding/' + (dismiss ? 'skip' : 'complete'), { card: 'connect_device' });
      advance();
    } catch (e) {
      setError(e.message || 'Could not continue — please try again.');
    } finally { setSubmitting(false); }
  };

  const shellStyle = {
    position:'fixed', inset:0, zIndex:9999, background:'rgba(20,30,50,0.55)',
    display:'flex', alignItems:'flex-start', justifyContent:'center',
    padding:'24px 16px', overflowY:'auto',
  };
  const cardStyle = {
    // Theme-aware: white in light, dark indigo card in dark — so the themed
    // text colours below (var(--text-primary) etc.) stay legible in BOTH modes.
    maxWidth:520, width:'100%', background:'var(--card-bg, #FFFFFF)', borderRadius:14,
    padding:'28px 26px', boxShadow:'0 12px 40px rgba(0,0,0,0.18)', marginTop:'4vh',
  };
  const h2 = { fontFamily:"'Satoshi', sans-serif", fontWeight:500, fontSize:20, color:_NPL_DEEP, margin:'0 0 14px 0' };
  const primaryBtn = (enabled) => ({
    // Use --npl-indigo (NOT --npl-blue): indigo stays #0D156A in both themes, so
    // white button text keeps contrast in dark (--npl-blue flips to a light
    // tint and would wash out white text). Light mode is unchanged.
    width:'100%', padding:'12px 16px', borderRadius:10,
    background: enabled ? 'var(--npl-indigo, #0D156A)' : '#cbd5e1', color:'#FFFFFF', border:'none',
    fontWeight:600, fontSize:14, cursor: enabled && !submitting ? 'pointer' : 'not-allowed', marginBottom:8,
  });
  const linkBtn = {
    display:'block', width:'100%', textAlign:'center', background:'none', border:'none',
    padding:'10px', color:'var(--text-secondary)', fontSize:12.5,
    cursor: submitting ? 'not-allowed' : 'pointer', textDecoration:'underline',
  };
  const cb = (k, label) => (
    <label style={{display:'flex', alignItems:'flex-start', gap:10, padding:'10px 0', cursor:'pointer'}}>
      <input type="checkbox" checked={boxes[k]} onChange={() => setBoxes(b => ({ ...b, [k]: !b[k] }))}
        style={{marginTop:4, width:16, height:16, accentColor:_NPL_BLUE, cursor:'pointer'}} />
      <span style={{fontSize:13, color:'var(--text-primary)', lineHeight:1.45}}>{label}</span>
    </label>
  );

  const Logo = () => (
    <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:18}}>
      <img src={FEATURES_GLOBAL.branding.logo_url || '/static/npl_logo.png'} alt={appName}
        style={{height:40, width:'auto', objectFit:'contain',
          // Dark card: sit the indigo+red wordmark on a white chip so it stays
          // legible (box-sizing:content-box so the chip frames, not shrinks it).
          ...(isDark ? { boxSizing:'content-box', background:'#FFFFFF', padding:'6px 10px', borderRadius:10 } : {}) }} />
    </div>
  );
  const StepDots = () => {
    // §O — dots reflect the ACTUAL server-driven sequence (auto-skipped cards absent),
    // filled up to the current index. Hidden for a single-card gate (one dot is noise).
    const total = sequence.length;
    if (total <= 1) return null;
    return (
      <div style={{display:'flex', gap:6, justifyContent:'center', marginTop:16}}>
        {Array.from({length: total}).map((_, i) => (
          <span key={i} style={{width:7, height:7, borderRadius:'50%',
            background: i <= stepIdx ? _NPL_BLUE : '#d8dee9'}} />
        ))}
      </div>
    );
  };

  // §O — sequence exhausted (parent is about to unmount the gate) → render nothing
  // rather than fall through to the install branch.
  if (!step) return null;
  let body = null, dotIdx = 0;
  if (step === 'welcome') {
    dotIdx = 0;
    body = (
      <>
        <h2 style={h2}>Welcome to {appName}</h2>
        <div style={{fontSize:14, color:'var(--text-primary)', lineHeight:1.6, marginBottom:14}}>
          Your wellness, in one place. {appName} turns your blood tests and everyday
          {T('wearable data into clear, personalised longevity insights — powered by Kalpa.')}
        </div>
        <ul style={{margin:'0 0 8px 0', paddingLeft:20, lineHeight:1.6, fontSize:13.5, color:'var(--text-primary)'}}>
          <li>See your biological age and what moves it</li>
          <li>Track sleep, heart rate and activity from your watch</li>
          <li>Ask the AI health assistant anything about your data</li>
        </ul>
        <div style={{fontSize:12.5, color:'var(--text-secondary)', margin:'14px 0 18px'}}>
          Setup takes about a minute. We&apos;ll ask for a few permissions and then help you connect your device.
        </div>
        <button type="button" style={primaryBtn(true)} disabled={submitting} onClick={async () => {
          setSubmitting(true);
          try { await api.post('/onboarding/intro-seen'); } catch (e) { /* non-fatal — advance anyway */ }
          setSubmitting(false); advance();
        }}>Get started</button>
        <StepDots idx={dotIdx} />
      </>
    );
  } else if (step === 'consents') {
    dotIdx = 1;
    body = (
      <>
        <h2 style={h2}>Your privacy &amp; consents</h2>
        <div style={{fontSize:13, color:'var(--text-primary)', lineHeight:1.55, marginBottom:8}}>
          A few permissions before we begin. You can change the optional ones any time
          in Data &amp; Privacy settings.
        </div>
        {/* Privacy/Terms — a SEPARATE required gate, never part of "Select all". */}
        <div style={{borderTop:'1px solid var(--border-color)', paddingTop:6, marginBottom:2}}>
          {cb('accepted_policies', <>I have read and accept the {' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:_NPL_BLUE}}>Privacy Policy &amp; Terms</a>.</>)}
        </div>
        {/* Optional opt-ins convenience toggle — ticks ONLY the two optional items
            (AI + lab-link). Default off; user-initiated, never a pre-tick. The
            required health consent is a separate tick below. */}
        <div style={{borderTop:'1px solid var(--border-color)', paddingTop:10, marginTop:6}}>
          <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer'}}>
            <input type="checkbox" checked={allOptionalSelected} onChange={toggleSelectAll}
              aria-label="Enable both optional data uses"
              style={{width:16, height:16, accentColor:_NPL_BLUE, cursor:'pointer'}} />
            <span style={{fontSize:13.5, fontWeight:600, color:'var(--text-primary)'}}>Enable both optional features</span>
          </label>
          <div style={{fontSize:12, color:'var(--text-secondary)', margin:'4px 0 2px 26px'}}>
            Optional — AI insights and NPL lab linking. Leave off to keep them disabled; you can change either any time in Data &amp; Privacy.
          </div>
        </div>
        {/* Data-use consents — each individually tickable. Health is REQUIRED; AI is OPTIONAL. */}
        <div style={{paddingTop:2, marginBottom:6}}>
          {cb('consent_health', 'I agree to Kalpa processing my health data (blood tests, wearable metrics) to run the app and show my results. Required to use the app.')}
          {cb('consent_ai', 'Optional — I consent to AI (Google Gemini) generating wellness insights and chat replies from my health data. You can leave this off and still use the app.')}
          <label style={{display:'flex', alignItems:'flex-start', gap:10, padding:'10px 0', cursor:'pointer'}}>
            <input type="checkbox" checked={labLink} onChange={() => setLabLink(v => !v)}
              style={{marginTop:4, width:16, height:16, accentColor:_NPL_BLUE, cursor:'pointer'}} />
            <span style={{fontSize:13, color:'var(--text-primary)', lineHeight:1.45}}>
              Link my NPL lab results so blood results import automatically — <em>optional</em>.
              <span style={{display:'block', color:'var(--text-secondary)', fontSize:12, marginTop:2}}>
                You can turn this on later instead.
              </span>
            </span>
          </label>
        </div>
        {error && <div style={{color:'#dc2626', fontSize:12.5, margin:'6px 0 10px'}}>{error}</div>}
        <button type="button" style={primaryBtn(allRequired)} disabled={!allRequired || submitting} onClick={submitConsents}>
          {submitting ? 'Saving…' : 'Agree & continue'}
        </button>
        <StepDots idx={dotIdx} />
      </>
    );
  } else if (step === 'profile') {
    dotIdx = 2;
    const field = (label, node, caption) => (
      <div style={{marginBottom:12}}>
        <label style={{display:'block', fontSize:12, color:'var(--text-secondary)', marginBottom:4}}>{label}</label>
        {node}
        {caption && <div style={{fontSize:11.5, color:'var(--text-secondary)', marginTop:5, lineHeight:1.45}}>{caption}</div>}
      </div>
    );
    const inputStyle = { width:'100%', padding:'9px 11px', borderRadius:8, border:'1px solid var(--border-color)', fontSize:14, boxSizing:'border-box' };
    body = (
      <>
        <h2 style={h2}>A little about you</h2>
        <div style={{fontSize:13, color:'var(--text-primary)', lineHeight:1.55, marginBottom:16}}>
          This helps tailor your insights. You can skip and fill it in later.
        </div>
        {field('Name', <input style={inputStyle} value={pf.display_name} onChange={e => setPf(p => ({...p, display_name: e.target.value}))} placeholder="Your name" />)}
        {field('Date of birth', <input type="date" style={inputStyle} value={pf.dob} onChange={e => setPf(p => ({...p, dob: e.target.value}))} />, _PROFILE_FIELD_CAPTION.dob)}
        {field('Biological sex', (
          <select style={inputStyle} value={pf.biological_sex} onChange={e => setPf(p => ({...p, biological_sex: e.target.value}))}>
            <option value="">Prefer not to say</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        ), _PROFILE_FIELD_CAPTION.biological_sex)}
        {field('Height (cm)', <input type="number" style={inputStyle} value={pf.height_cm} onChange={e => setPf(p => ({...p, height_cm: e.target.value}))} placeholder="e.g. 170" />, _PROFILE_FIELD_CAPTION.height)}
        {error && <div style={{color:'#dc2626', fontSize:12.5, margin:'6px 0 10px'}}>{error}</div>}
        <button type="button" style={primaryBtn(true)} onClick={() => saveProfile(false)} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save & continue'}
        </button>
        <button type="button" style={linkBtn} onClick={() => saveProfile(true)} disabled={submitting}>Skip for now</button>
        <StepDots idx={dotIdx} />
      </>
    );
  } else if (step === 'lifestyle') {
    dotIdx = 3;
    body = (
      <>
        <h2 style={h2}>A few lifestyle basics</h2>
        <div style={{fontSize:13, color:'var(--text-primary)', lineHeight:1.55, marginBottom:16}}>
          {T('These help personalise your insights (for example, how your diet relates to your blood markers). Every question is optional — pick “Prefer not to say”, or skip the whole step.')}
        </div>
        <LifestyleFields
          value={life}
          onChange={setLife}
          weight={lifeWeight}
          onWeightManual={(kg) => setLifeManualWeight(kg)}
        />
        {error && <div style={{color:'#dc2626', fontSize:12.5, margin:'6px 0 10px'}}>{error}</div>}
        <button type="button" style={primaryBtn(true)} onClick={() => saveLifestyle(false)} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save & continue'}
        </button>
        <button type="button" style={linkBtn} onClick={() => saveLifestyle(true)} disabled={submitting}>Skip for now</button>
        <StepDots idx={dotIdx} />
      </>
    );
  } else if (step === 'notifications') {
    dotIdx = 4;
    body = (
      <>
        <h2 style={h2}>Want a nudge when something matters?</h2>
        <div style={{fontSize:13.5, color:'var(--text-primary)', lineHeight:1.6, marginBottom:14}}>
          We can let you know when a marker moves meaningfully, a new lab result lands, or a medicine
          needs attention. Never marketing, never a daily nag — only when your own data changes.
        </div>
        <div style={{fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.55,
                     background:'var(--secondary-bg, rgba(0,0,0,0.04))', borderRadius:8,
                     padding:'10px 12px', marginBottom:16}}>
          Saying yes here <b>does not</b> turn anything on yet — your phone or browser will ask for
          permission later, once you have seen your first result and can judge whether it is worth it.
          You can change this any time in Profile → Notifications.
        </div>
        {error && <div style={{color:'#dc2626', fontSize:12.5, margin:'6px 0 10px'}}>{error}</div>}
        <button type="button" data-testid="notif-intent-yes" style={primaryBtn(true)}
                onClick={() => saveNotifIntent('yes')} disabled={submitting}>
          {submitting ? 'Saving…' : 'Yes, keep me posted'}
        </button>
        <button type="button" data-testid="notif-intent-no" style={linkBtn}
                onClick={() => saveNotifIntent('no')} disabled={submitting}>
          No thanks
        </button>
        <button type="button" data-testid="notif-intent-skip" style={linkBtn}
                onClick={() => saveNotifIntent('skip')} disabled={submitting}>
          Decide later
        </button>
        <StepDots idx={dotIdx} />
      </>
    );
  } else { // install
    dotIdx = 5;
    body = (
      <>
        <h2 style={h2}>{isNative ? "You're nearly there" : `Install the ${appName} app`}</h2>
        {isNative ? (
          <div style={{fontSize:14, color:'var(--text-primary)', lineHeight:1.6, marginBottom:18}}>
            You&apos;re already using the {appName} app. Next we&apos;ll connect your device so your
            wearable data syncs automatically.
          </div>
        ) : (
          <>
            <div style={{fontSize:14, color:'var(--text-primary)', lineHeight:1.6, marginBottom:14}}>
              To sync your watch or fitness band automatically, install the {appName} app.
              It reads your health data through your phone and keeps your dashboard up to date.
            </div>
            {/* RUN V — device-conditional: Android→APK, iOS→TestFlight, desktop→both. */}
            <AppInstallCTA />
            <div style={{fontSize:12, color:'var(--text-secondary)', lineHeight:1.5, margin:'4px 0 14px'}}>
              After installing, open the app and sign in with the same phone number. Your setup will continue there.
            </div>
          </>
        )}
        {error && <div style={{color:'#dc2626', fontSize:12.5, margin:'6px 0 10px'}}>{error}</div>}
        <button type="button" style={primaryBtn(true)} onClick={() => finishInstall(false)} disabled={submitting}>
          {submitting ? 'Finishing…' : (isNative ? 'Continue' : "I've installed it — continue")}
        </button>
        {!isNative && (
          <button type="button" style={linkBtn} onClick={() => finishInstall(true)} disabled={submitting}>
            I&apos;ll do this later
          </button>
        )}
        <StepDots idx={dotIdx} />
      </>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={cardStyle}>
        <Logo />
        {body}
      </div>
    </div>
  );
}

// NPL staff verified-upload (admin-only). One admin session: pick a pilot user,
// upload their lab PDF, REVIEW the parsed markers (edit any OCR mis-read), then
// confirm → committed as LAB-VERIFIED. Nothing writes to clinical_timeline until
// the staff member confirms (the safety gate). The user's own upload stays
// self-reported. Backend: /api/admin/lab-upload/parse + /commit.
// Stage 5 — human labels for the REAL streamed parse phases.
// The 7 REAL pipeline phases, streamed identically over SSE by BOTH the staff Lab
// Upload (/api/admin/lab-upload/parse-stream) and the Documents/Health-Record upload
// (/api/vault/upload-stream). Keys match the backend _emit() phase keys 1:1.
const LAB_PHASE_LABELS = {
  ingest:     'Upload & ingest…',
  classify:   'Identifying document type…',
  vision:     'Gemini Vision extraction…',
  grounding:  'Grounding (anti-hallucination)…',
  dictionary: T('Dictionary resolution + normalisation…'),
  verifier:   'Independent verifier…',
  dating:     'Date attribution + flags…',
  confirm:    'Final confirmation…',
  // legacy keys kept as harmless fallbacks during rollout
  storing: 'Upload & ingest…', extracting: 'Gemini Vision extraction…',
  reading: T('Gemini Vision extraction…'), parsing: T('Dictionary resolution + normalisation…'),
  checking: 'Independent verifier…', saving: 'Final confirmation…',
};
// Ordered list for the Documents-upload stepper (index → label), driven by real SSE events.
const LAB_PHASE_SEQUENCE = ['ingest', 'classify', 'vision', 'grounding', 'dictionary', 'verifier', 'dating', 'confirm'];

// A3 (doc-intel) — read-only interpretation view for a NON-blood review panel
// (imaging / clinical_note / other). Shown instead of the marker grid so the
// user reviews the document's summary, never an empty "couldn't read markers".
function _renderDocInterpretation(p) {
  const it = p.interpretation || {};
  const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const typeName = p.doc_type || (p.kind === 'imaging' ? 'Imaging' : p.kind === 'note' ? 'Document' : 'Other');
  return (
    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(129,145,223,0.18)', color: 'var(--accent, #7F91DF)' }}>{typeName}</span>
        {p.needs_review && <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>⚠ Needs review — auto-interpretation unavailable</span>}
        {p.needs_manual && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Needs manual classification</span>}
      </div>
      {p.kind === 'imaging' && (
        <div style={{ display: 'grid', gap: 6 }}>
          {(it.modality || it.body_region) && <div><span style={lbl}>Modality / region</span><div>{[it.modality, it.body_region].filter(Boolean).join(' · ') || '—'}</div></div>}
          {it.description && <div><span style={lbl}>Description</span><div>{it.description}</div></div>}
          {it.notable_findings && <div><span style={lbl}>Notable</span><div>{it.notable_findings}</div></div>}
          {it.impression && <div><span style={lbl}>Impression</span><div>{it.impression}</div></div>}
        </div>
      )}
      {p.kind === 'note' && (
        <div style={{ display: 'grid', gap: 6 }}>
          {it.source && <div><span style={lbl}>Source</span><div>{it.source}</div></div>}
          {it.summary && <div><span style={lbl}>Summary</span><div>{it.summary}</div></div>}
          {Array.isArray(it.key_points) && it.key_points.length > 0 && (
            <div><span style={lbl}>Key points</span><ul style={{ margin: '4px 0 0 18px' }}>{it.key_points.map((k, i) => <li key={i}>{k}</li>)}</ul></div>
          )}
        </div>
      )}
      {p.kind === 'body_composition' && (
        <div style={{ display: 'grid', gap: 6 }}>
          {it.device && <div><span style={lbl}>Device</span><div>{it.device}</div></div>}
          {it.summary && <div><span style={lbl}>Summary</span><div>{it.summary}</div></div>}
          {Array.isArray(it.measurements) && it.measurements.length > 0 && (
            <div>
              <span style={lbl}>Captured measurements ({it.measurements.length})</span>
              <div style={{ marginTop: 4 }}>
                {it.measurements.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', borderBottom: '1px solid var(--border-color)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{m.label || m.canonical_key}</span>
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{m.value}{m.unit ? ` ${m.unit}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {p.kind === 'other' && (
        <div style={{ color: 'var(--text-dim)' }}>Stored but not auto-classified — it will appear in your documents marked “Needs review”.</div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 10 }}>Saved to your record as {typeName.toLowerCase()} · no health-score impact.</div>
    </div>
  );
}
// RUN10 §2 — ONE shared parse-review/flags UI used by BOTH the staff Lab Upload and the
// self-upload #vault flows. Identical per-report editable marker rows, flag rendering,
// edit-value/unit/date, remove-row, "+ Add marker", and confirm. The ONLY difference between
// the two callers is the commit endpoint + the provenance stamped there (lab-verified vs
// self-reported) — passed in via props. data-testid="doc-review" lets UAT assert both paths
// render the SAME component.
function DocReviewPanels({ panels, updateRow, removeRow, addRow, setPanelDate, removePanel,
                           busy, progress, doCommit, onCancel, reviewName, totalMarkers, commitLabel }) {
  const card = { background: 'var(--secondary-bg, #fff)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16, marginBottom: 14 };
  const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const inp = { fontSize: 13, padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 6, background: '#ffffff', color: '#11182c', WebkitTextFillColor: '#11182c', fontFamily: 'inherit' };
  const btn = (primary) => ({ fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, border: 'none', cursor: busy ? 'wait' : 'pointer', background: primary ? 'var(--accent, #7F91DF)' : 'transparent', color: primary ? '#fff' : 'var(--text-secondary)' });
  if (!panels.length) return null;
  return (
    <div data-testid="doc-review">
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: '4px 2px 10px' }}>
        Review {panels.length} report{panels.length === 1 ? '' : 's'}{reviewName ? ` for ${reviewName}` : ''} — {totalMarkers} markers total
      </div>
      {panels.map((p, pi) => (
        <div key={p.id} style={card} data-testid="doc-review-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>📄 {p.filename}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ ...label, display: 'inline', margin: 0 }}>Test date</label>
              <input type="date" value={p.test_date} onChange={e => setPanelDate(pi, e.target.value)} style={inp} />
              <button onClick={() => removePanel(pi)} title="Drop this report" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
          </div>
          {p.kind && p.kind !== 'blood' ? _renderDocInterpretation(p) : (<>
          {p.read_failed && <div style={{ fontSize: 12.5, color: '#b45309', marginBottom: 10 }}>{p.warning}</div>}
          <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginBottom: 8 }}>
            {p.rows.length} markers (method: {p.method || '—'}). Edit any value/unit to match the report, or remove a bad row.
          </div>
          {(() => {
            const nflag = p.rows.filter(r => r.needs_review || r.plausible === false).length;
            return nflag > 0
              ? <div className="doc-review-flags" style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginBottom: 8 }}>⚠ {nflag} row{nflag === 1 ? '' : 's'} flagged for review — check the highlighted values before confirming.</div>
              : <div className="doc-review-flags" style={{ fontSize: 12, color: '#16a34a', marginBottom: 8 }}>✓ All rows verified — no flags.</div>;
          })()}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11.5 }}>
                  <th style={{ padding: '4px 6px' }}>Marker</th><th style={{ padding: '4px 6px' }}>Value</th><th style={{ padding: '4px 6px' }}>Unit</th><th style={{ padding: '4px 6px' }}>Date</th><th></th>
                </tr>
              </thead>
              <tbody>
                {p.rows.map((r, ri) => {
                  const flag = r.needs_review || r.plausible === false;
                  const reason = r.review_reason || (r.plausible === false ? (r.warn || 'implausible value/unit') : null);
                  return (
                    <tr key={ri} style={flag ? { background: 'rgba(245,158,11,0.13)' } : undefined}>
                      <td style={{ padding: '3px 6px', borderLeft: flag ? '3px solid #f59e0b' : '3px solid transparent' }}>
                        <input value={r.name} onChange={e => updateRow(pi, ri, 'name', e.target.value)} style={{ ...inp, width: '100%' }} />
                        {flag && reason && <div style={{ fontSize: 10.5, color: '#b45309', marginTop: 2, fontWeight: 600 }}>⚠ {reason}</div>}
                      </td>
                      <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>
                        <input value={r.value} onChange={e => updateRow(pi, ri, 'value', e.target.value)} style={{ ...inp, width: 76 }} />
                        {flag && <span title={reason || 'Please check'} style={{ color: '#b45309', marginLeft: 5, cursor: 'help' }}>⚠</span>}
                      </td>
                      <td style={{ padding: '3px 6px' }}><input value={r.unit} onChange={e => updateRow(pi, ri, 'unit', e.target.value)} style={{ ...inp, width: 82 }} /></td>
                      <td style={{ padding: '3px 6px' }}><input type="date" value={r.date || ''} onChange={e => updateRow(pi, ri, 'date', e.target.value)} style={{ ...inp, width: 140 }} /></td>
                      <td style={{ padding: '3px 6px' }}><button onClick={() => removeRow(pi, ri)} title="Remove" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16 }}>×</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={() => addRow(pi)} style={{ ...btn(false), padding: '6px 0', marginTop: 6 }}>+ Add marker</button>
          </>)}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, marginTop: 4, position: 'sticky', bottom: 0, background: 'var(--bg, #fff)', padding: '8px 0' }}>
        <button style={btn(true)} disabled={busy} onClick={doCommit}>{busy ? (progress || 'Saving…') : commitLabel}</button>
        <button style={btn(false)} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function StaffLabUpload() {
  const [users, setUsers] = useState([]);
  const [profile, setProfile] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [progressFiles, setProgressFiles] = useState([]);  // RUN10 §5 — per-file upload state
  // One panel per uploaded file: { id, filename, display_name, test_date, method,
  // read_failed, warning, rows:[{name,value,unit}], bp:[] }. Each is independently
  // editable; the single Confirm commits them all.
  const [panels, setPanels] = useState([]);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  // The selected pilot user's existing docs + DnD state.
  const [existingDocs, setExistingDocs] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);   // RUN6 §4 — Recently-Deleted panel
  const [dragOver, setDragOver] = useState(false);
  // Run 4 §A — EXACT-FILE dedup: SHA-256 of each selected file vs stored hashes of the
  // user's existing docs. The old fuzzy date/filename matcher is removed (false positives).
  const [fileHashes, setFileHashes] = useState({});  // file.name -> sha256 hex

  useEffect(() => {
    // Least-privilege picker: lab staff get genuine pilot users only (scoped server-side).
    api.get('/admin/lab-upload/targets').then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => setUsers([]));
  }, []);

  // Load the selected user's existing documents (staff-scoped server-side).
  const loadExisting = useCallback(() => {
    if (!profile) { setExistingDocs([]); return Promise.resolve(); }
    return api.get(`/admin/lab-upload/documents?target=${encodeURIComponent(profile)}`)
      .then(d => setExistingDocs(Array.isArray(d) ? d : []))
      .catch(() => setExistingDocs([]));
  }, [profile]);
  useEffect(() => { loadExisting(); }, [loadExisting]);

  // RUN6 §4 — per-row soft-delete + restore (identical to the Health Record). The doc
  // gate already authorises lab_staff/superadmin cross-profile, cascades to clinical_timeline,
  // is audited, and NEVER touches genetics.
  const _daysLeftLab = (deletedAt) => {
    if (!deletedAt) return 30;
    const ms = 30 * 864e5 - (Date.now() - new Date(deletedAt).getTime());
    return Math.max(0, Math.ceil(ms / 864e5));
  };
  const deleteLabDoc = async (id) => {
    try { await api.post(`/documents/${id}/soft-delete`, { reason: 'removed by lab staff' }); await loadExisting(); }
    catch (e) { setError(`Delete failed: ${e.message}`); }
  };
  const restoreLabDoc = async (id) => {
    try { await api.post(`/documents/${id}/restore`, {}); await loadExisting(); }
    catch (e) { setError(`Restore failed: ${e.message}`); }
  };

  // Hash the selected files (client-side SHA-256) to detect a byte-identical re-upload.
  useEffect(() => {
    let alive = true;
    (async () => {
      const map = {};
      for (const f of files) {
        try {
          const buf = await f.arrayBuffer();
          const dig = await crypto.subtle.digest('SHA-256', buf);
          map[f.name] = Array.from(new Uint8Array(dig)).map(b => b.toString(16).padStart(2, '0')).join('');
        } catch (e) { /* leave unhashed → never blocks */ }
      }
      if (alive) setFileHashes(map);
    })();
    return () => { alive = false; };
  }, [files]);

  // EXACT-match block: a file is blocked only when its SHA-256 equals a stored hash of an
  // existing (non-deleted) doc. NULL stored hash → no check (never blocks). Selection-level:
  // only the identical file(s) are blocked; other selected files still parse.
  // RUN6 §4 — split active vs soft-deleted; hash-block only against ACTIVE docs.
  const activeDocs = existingDocs.filter(d => !d.deleted);
  const deletedDocs = existingDocs.filter(d => d.deleted);
  const _existingByHash = new Map(activeDocs.filter(d => d.file_sha256).map(d => [d.file_sha256, d]));
  const blockedFiles = files.filter(f => fileHashes[f.name] && _existingByHash.has(fileHashes[f.name]));
  const parseableFiles = files.filter(f => !(fileHashes[f.name] && _existingByHash.has(fileHashes[f.name])));

  // Stage 5 — consume the streaming parse endpoint so progress reflects REAL pipeline
  // phases as they happen (not a timer). Per file we show "Report N of M — <phase>".
  const doParse = async () => {
    setError(null); setDone(null);
    if (!profile) { setError('Select a pilot user first.'); return; }
    // Exact-duplicate files are skipped (blocked at selection); parse only the rest.
    if (!parseableFiles.length) { setError(blockedFiles.length ? 'All selected files are already on record (byte-identical duplicates).' : 'Choose one or more lab PDFs/images.'); return; }
    setBusy(true);
    setProgressFiles(parseableFiles.map(f => ({ name: f.name, state: 'pending' })));  // RUN10 §5
    const mark = (i, state) => setProgressFiles(pf => pf.map((x, j) => j === i ? { ...x, state } : x));
    const out = []; const errs = [];
    for (let i = 0; i < parseableFiles.length; i++) {
      const f = parseableFiles[i];
      const rep = `Report ${i + 1} of ${parseableFiles.length}`;
      setProgress(`${rep} — Starting…`); mark(i, 'parsing');
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('profile', profile);
        const resp = await fetch('/api/admin/lab-upload/parse-stream', { method: 'POST', body: fd, credentials: 'include' });
        if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = '', final = null, streamErr = null;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
            const dl = chunk.split('\n').find(l => l.startsWith('data:'));
            if (!dl) continue;
            let evt; try { evt = JSON.parse(dl.slice(5).trim()); } catch { continue; }
            if (evt.phase === 'done') final = evt.result;
            else if (evt.phase === 'error') streamErr = evt.error;
            else setProgress(`${rep} — ${LAB_PHASE_LABELS[evt.phase] || evt.phase}`);
          }
        }
        if (streamErr) throw new Error(streamErr);
        if (!final) throw new Error('No result received from parser');
        const d = final;
        out.push({
          id: `${f.name}-${i}`, filename: d.filename || f.name, display_name: d.display_name,
          test_date: d.test_date || '', test_date_source: d.test_date_source, method: d.method, read_failed: d.read_failed, warning: d.warning,
          rows: (d.markers || []).map(m => ({ name: m.name, value: m.value, unit: m.unit, date: m.date || d.test_date, plausible: m.plausible !== false, warn: m.warn, needs_review: !!m.needs_review, review_reason: m.review_reason, confidence: m.confidence, reference_range: m.reference_range, source_snippet: m.source_snippet })), dates: d.dates || [], bp: d.bp || [],
        });
        mark(i, 'analysed');
      } catch (e) { errs.push(`${f.name}: ${e.message}`); mark(i, 'failed'); }
    }
    setPanels(out);
    setProgress(''); setProgressFiles([]);
    if (errs.length) setError('Some files could not be parsed: ' + errs.join('; '));
    setBusy(false);
  };

  const updateRow = (pi, ri, k, v) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, rows: p.rows.map((r, x) => x === ri ? { ...r, [k]: v } : r) } : p));
  const removeRow = (pi, ri) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, rows: p.rows.filter((_, x) => x !== ri) } : p));
  const addRow = (pi) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, rows: [...p.rows, { name: '', value: '', unit: '', date: p.test_date, plausible: true, warn: null, needs_review: false, review_reason: null }] } : p));
  const setPanelDate = (pi, v) => setPanels(ps => ps.map((p, j) => j === pi ? { ...p, test_date: v } : p));
  const removePanel = (pi) => setPanels(ps => ps.filter((_, j) => j !== pi));

  const totalMarkers = panels.reduce((n, p) => n + p.rows.filter(r => (r.name || '').trim() && String(r.value).trim() !== '').length, 0);

  const doCommitAll = async () => {
    setError(null); setBusy(true);
    let mk = 0, bp = 0, filesDone = 0; const errs = [];
    for (let pi = 0; pi < panels.length; pi++) {
      const p = panels[pi];
      setProgress(`Saving ${pi + 1}/${panels.length}: ${p.filename}…`);
      // Non-blood docs (body_composition / imaging / clinical_note / other) are already
      // persisted at parse time — no markers to commit — so confirm just counts them as
      // saved (mirrors the vault flow). Body-composition variables were captured on upload.
      if (p.kind && p.kind !== 'blood') { filesDone++; continue; }
      const markers = p.rows
        .filter(r => (r.name || '').trim() && String(r.value).trim() !== '')
        .map(r => ({ name: r.name.trim(), value: r.value, unit: (r.unit || '').trim(), date: r.date || p.test_date, reference_range: r.reference_range || '' }));  // RUN11.4 §1 — carry the lab's printed range to commit
      if (!markers.length && !(p.bp || []).length) continue;  // nothing to commit for this file
      try {
        const d = await api.post('/admin/lab-upload/commit', {
          profile, markers, bp: p.bp || [], test_date: p.test_date, filename: p.filename,
        });
        mk += d.markers_saved || 0; bp += d.bp_saved || 0; filesDone++;
      } catch (e) { errs.push(`${p.filename}: ${e.message}`); }
    }
    setProgress('');
    if (errs.length) setError('Some panels failed: ' + errs.join('; '));
    setDone({ files: filesDone, markers_saved: mk, bp_saved: bp, profile });
    if (!errs.length) { setPanels([]); setFiles([]); }
    // RUN10 §3 — RELOAD the list so the just-committed lab-verified doc(s) appear immediately.
    // This was the recurring "committed 3 reports / 33 markers but they don't show" bug: the
    // commit wrote correctly (parse-stub row → Verified) but the UI never re-fetched the list.
    try { await loadExisting(); } catch (e) { /* non-fatal */ }
    setBusy(false);
  };

  const card = { background: 'var(--secondary-bg, #fff)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16, marginBottom: 14 };
  const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' };
  // White field + explicit dark text. On the dark theme --text-primary is light and
  // the theme's -webkit-text-fill-color bleeds onto these white fields → faint,
  // near-illegible values (same class as the feedback-textarea fix). Pin both color
  // AND -webkit-text-fill-color to dark navy so the parsed values read clearly.
  // Scoped: `inp` is local to StaffLabUpload only.
  const inp = { fontSize: 13, padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 6, background: '#ffffff', color: '#11182c', WebkitTextFillColor: '#11182c', fontFamily: 'inherit' };
  // Contrast fix: native <select>/<option> rendered light-on-light (esp. the
  // highlighted row) on the dark theme. Force a solid white control + dark text so
  // the closed value AND the dropdown list are legible in both themes.
  const selectStyle = { ...inp, width: '100%', marginBottom: 14, background: '#ffffff', color: '#11182c' };
  const optionStyle = { background: '#ffffff', color: '#11182c' };
  const btn = (primary) => ({ fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, border: 'none', cursor: busy ? 'wait' : 'pointer', background: primary ? 'var(--accent, #7F91DF)' : 'transparent', color: primary ? '#fff' : 'var(--text-secondary)' });

  const reviewName = panels[0]?.display_name || profile;

  return (
    <div className="main-content-inner" style={{ maxWidth: 760, margin: '0 auto', padding: '18px 16px 60px' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '4px 0 4px' }}>Lab Upload</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>
        Review the parsed values for each
        report against the PDF, correct any mis-read, then confirm once — all reports commit as
        <strong> lab-verified</strong>. Nothing is saved until you confirm.
      </p>

      {error && <div style={{ ...card, borderColor: '#dc2626', color: '#dc2626', fontSize: 13 }}>{error}</div>}
      {done && (
        <div style={{ ...card, borderColor: '#16a34a' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>✓ Committed as lab-verified</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            {done.files} report{done.files === 1 ? '' : 's'} · {done.markers_saved} markers{done.bp_saved ? ` + ${done.bp_saved} BP` : ''} saved for {done.profile} (Insurance-Grade tier).
          </div>
        </div>
      )}

      {panels.length === 0 && (
        <div style={card}>
          <label style={label}>Pilot user</label>
          <select value={profile} onChange={e => setProfile(e.target.value)} style={selectStyle}>
            <option style={optionStyle} value="">— select —</option>
            {users.map(u => <option style={optionStyle} key={u.profile_name} value={u.profile_name}>{u.display_name || u.profile_name} ({u.profile_name})</option>)}
          </select>
          {/* Section B — existing docs for the selected user, via the SAME shared
              document-list renderer as the Health Record (Run 5 §B). Server-side
              scoped to LAB-VERIFIED only for this pilot user (strict provenance split);
              read-only here (staff add via the drop-zone below, they don't delete). */}
          {profile && (
            <div style={{ marginBottom: 14 }}>
              <label style={label}>Already uploaded for this user (lab-verified)</label>
              {/* RUN6 §4 — the SAME management component as the Health Record: sortable named
                  columns, right-aligned data, per-row soft-delete + Recently-Deleted/undo. */}
              <SharedDocList
                showSource={false}
                recentlyDeleted={deletedDocs}
                showDeleted={showDeleted}
                onToggleDeleted={() => setShowDeleted(s => !s)}
                onRestore={restoreLabDoc}
                daysLeft={_daysLeftLab}
                rows={activeDocs.map(d => ({
                  key: d.id,
                  badge: { label: (d.doc_type || 'lab report').replace(/_/g, ' '), color: '#A78BFA' },
                  // RUN11.1 §3 — derived meaningful title (NOT the raw drive filename); original in tooltip.
                  title: _docDisplayTitle({ provider: d.provider, docType: d.doc_type, date: d.draw_date, filename: d.filename, id: d.id }),
                  filename: d.filename,
                  date: d.draw_date, sortDate: d.uploaded_at || d.draw_date, provider: d.provider,
                  source: d.source,
                  statusText: d.status ? `${d.status}${d.marker_count ? ` · ${d.marker_count}` : ''}` : null,
                  statusKey: d.status,
                  onDelete: () => deleteLabDoc(d.id),
                }))}
                emptyText={`No lab reports on record for ${(users.find(u => u.profile_name === profile) || {}).display_name || profile} yet.`}
              />
            </div>
          )}
          <label style={label}>Lab reports (PDF / image — select multiple)</label>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const fs = Array.from(e.dataTransfer.files || []); if (fs.length) setFiles(fs); }}
            style={{ border: `1.5px dashed ${dragOver ? 'var(--accent, #7F91DF)' : 'var(--border-color)'}`, borderRadius: 10, padding: 16, textAlign: 'center', marginBottom: 8, background: dragOver ? 'rgba(127,145,223,0.08)' : 'transparent' }}
          >
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Drag &amp; drop lab reports here, or</div>
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setFiles(Array.from(e.target.files || []))} style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </div>
          {files.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{files.length} file{files.length === 1 ? '' : 's'} selected: {files.map(f => f.name).join(', ')}</div>}
          {/* Run 4 §A — EXACT-FILE block: only byte-identical files are blocked (selection-
              level); other selected files still parse. No fuzzy date/name guessing. */}
          {blockedFiles.length > 0 && (
            <div style={{ ...card, borderColor: '#b45309', background: 'rgba(255,180,80,0.12)', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginBottom: 4 }}>Already on record — blocked</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                {blockedFiles.map(f => {
                  const ex = _existingByHash.get(fileHashes[f.name]);
                  const who = (users.find(u => u.profile_name === profile) || {}).display_name || profile;
                  return (
                    <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ flex: '1 1 auto', minWidth: 0 }}>You've already uploaded this exact file{ex && ex.draw_date ? ` on ${formatDate(ex.draw_date)}` : ''}. It's already in {who}'s records: “{f.name}”.</span>
                      <button style={{ background: 'none', border: 'none', color: '#b45309', cursor: 'pointer', fontSize: 16, flex: '0 0 auto' }} title="Remove from selection" aria-label="Remove from selection" onClick={() => setFiles(fs => fs.filter(x => x !== f))}>×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <button style={btn(true)} disabled={busy || !parseableFiles.length} onClick={doParse}>{busy ? (progress || 'Reading…') : `Upload & parse${parseableFiles.length > 1 ? ` ${parseableFiles.length} files` : ''}`}</button>
          {busy && (
            <div style={{ marginTop: 10 }}>
              <div className="vault-upload-label" style={{ fontSize: 12.5 }}>{progress || 'Reading with Gemini…'}</div>
              <div className="vault-progress-track"><div className="vault-progress-fill indeterminate" /></div>
            </div>
          )}
          {/* RUN10 §5 — shared per-file multi-upload progress (same component as #vault). */}
          {progressFiles.length > 0 && <MultiFileProgress items={progressFiles} />}
        </div>
      )}

      {/* RUN10 §2 — the SHARED review/flags component (same as #vault). */}
      <DocReviewPanels
        panels={panels} updateRow={updateRow} removeRow={removeRow} addRow={addRow}
        setPanelDate={setPanelDate} removePanel={removePanel}
        busy={busy} progress={progress} doCommit={doCommitAll}
        onCancel={() => { setPanels([]); setFiles([]); }}
        reviewName={reviewName} totalMarkers={totalMarkers}
        commitLabel={`Confirm & save all ${panels.length} report${panels.length === 1 ? '' : 's'} as lab-verified`} />
    </div>
  );
}

function NPLCorporateDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/corporate/summary')
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);
  if (loading) return <LoadingSpinner message="Loading pilot metrics..." />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;
  const stat = (label, value, hint) => (
    <div className="npl-gradient-card" style={{padding:'16px 18px', minWidth:180, flex:'1 1 220px'}}>
      <div style={{fontSize:11, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em'}}>{label}</div>
      <div style={{fontSize:24, fontWeight:700, color:'var(--text-primary)', marginTop:4}}>{value}</div>
      {hint && <div style={{fontSize:11, color:'var(--text-dim)', marginTop:4}}>{hint}</div>}
    </div>
  );
  const p = data.participants || {};
  const flags = data.flagged_biomarkers || {};
  const w = data.wearable_means || {};
  return (
    <div className="corporate-page" style={{padding:'24px'}}>
      <h2 style={{marginBottom:8}}>Corporate Health · NPL Pilot</h2>
      <p style={{color:'var(--text-secondary)', fontSize:13, marginBottom:18}}>
        {T('Anonymised aggregate metrics. No individual data is exposed.')}
      </p>
      <div style={{display:'flex', gap:12, flexWrap:'wrap', marginBottom:24}}>
        {stat('Onboarded', `${p.onboarded ?? 0} / ${p.total ?? 0}`, `${p.rate_pct ?? 0}% participation`)}
        {stat('Avg KLS', data.avg_kls != null ? data.avg_kls : '—', 'across pilot participants')}
        {stat('Flagged biomarkers', flags.total ?? 0, flags.top_category ? `Top: ${flags.top_category}` : '')}
      </div>
      <h3 style={{marginBottom:10}}>Wearable means</h3>
      <div style={{display:'flex', gap:12, flexWrap:'wrap', marginBottom:24}}>
        {stat('Resting HR', w.resting_hr != null ? `${w.resting_hr} bpm` : '—')}
        {stat('HRV (RMSSD)', w.hrv_rmssd != null ? `${w.hrv_rmssd} ms` : '—')}
        {stat('Sleep duration', w.sleep_duration_h != null ? `${w.sleep_duration_h} h` : '—')}
      </div>
      {flags.by_category && Object.keys(flags.by_category).length > 0 && (
        <>
          <h3 style={{marginBottom:10}}>Flag distribution by category</h3>
          <div className="npl-gradient-card" style={{padding:'14px 18px'}}>
            {Object.entries(flags.by_category).map(([cat, n]) => (
              <div key={cat} style={{display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border-color)', fontSize:13}}>
                <span>{cat}</span><strong>{n}</strong>
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{marginTop:24, fontSize:11, color:'var(--text-dim)'}}>{data.privacy_note}</div>
      <NplPatientMappingsSection />
    </div>
  );
}

// Superadmin mapping table inside Corporate Health. Shows every user
// (mapped or not) so an admin can paste in an NPL patient reference and
// optionally trigger a re-sync. Never shows any health data — strictly
// mapping + sync-status metadata.
function NplPatientMappingsSection() {
  const [rows, setRows] = useState({ mapped: [], unmapped: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // { user_id, display_name, npl_patient_id }
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await api.get('/admin/npl-mappings');
      setRows(d || { mapped: [], unmapped: [] });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    if (!editing || !editing.npl_patient_id) return;
    setBusy(true);
    try {
      await api.post('/admin/npl-mappings', {
        user_id: editing.user_id,
        npl_patient_id: editing.npl_patient_id.trim(),
      });
      setEditing(null);
      await reload();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const remove = async (user_id) => {
    if (!window.confirm('Remove this NPL mapping? Already-synced labs are kept.')) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/admin/npl-mappings/${user_id}`, { method:'DELETE', credentials:'include' });
      await reload();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const syncNow = async (user_id) => {
    // Admin-side emergency re-sync. Does NOT bypass user consent — the
    // /api/npl/sync endpoint only fires when consent_given_at is set.
    setBusy(true);
    try {
      await fetch(`${API_BASE}/npl/sync`, { method:'GET', credentials:'include' });
      await reload();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const fmtDate = (s) => s ? formatDate(s) : '—';

  if (loading) return <div style={{marginTop:32}}><h3 style={{marginBottom:10}}>Patient Mappings</h3><div style={{fontSize:12, color:'var(--text-dim)'}}>Loading…</div></div>;

  return (
    <div style={{marginTop:32}}>
      <h3 style={{marginBottom:10}}>Patient Mappings</h3>
      <p style={{color:'var(--text-secondary)', fontSize:12.5, marginBottom:14}}>
        Pre-load each pilot participant&apos;s NPL patient reference. Sync only runs after the user explicitly consents in-app.
      </p>
      {err && <div style={{color:'#dc2626', fontSize:12.5, marginBottom:10}}>{err}</div>}
      <div className="npl-gradient-card" style={{padding:'8px 0', overflowX:'auto'}}>
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:12.5}}>
          <thead>
            <tr style={{textAlign:'left', color:'var(--text-dim)', fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em'}}>
              <th style={{padding:'8px 12px'}}>Name</th>
              <th style={{padding:'8px 12px'}}>Email</th>
              <th style={{padding:'8px 12px'}}>NPL Patient ID</th>
              <th style={{padding:'8px 12px'}}>Consent</th>
              <th style={{padding:'8px 12px'}}>Last Sync</th>
              <th style={{padding:'8px 12px', textAlign:'right'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.mapped.map(r => (
              <tr key={`m-${r.user_id}`} style={{borderTop:'1px solid var(--border-color)'}}>
                <td style={{padding:'8px 12px', fontWeight:500}}>{r.display_name || r.profile_name}</td>
                <td style={{padding:'8px 12px', color:'var(--text-secondary)'}}>{r.email}</td>
                <td style={{padding:'8px 12px', fontFamily:'monospace'}}>{r.npl_patient_id}</td>
                <td style={{padding:'8px 12px'}}>{r.consent_given_at ? <span style={{color:'#16a34a'}}>✓ {fmtDate(r.consent_given_at)}</span> : <span style={{color:'var(--text-dim)'}}>Pending</span>}</td>
                <td style={{padding:'8px 12px', color:'var(--text-secondary)'}}>{fmtDate(r.last_sync_at)}</td>
                <td style={{padding:'8px 12px', textAlign:'right', whiteSpace:'nowrap'}}>
                  <button disabled={busy || !r.consent_given_at} onClick={() => syncNow(r.user_id)} style={{background:'none', border:'1px solid var(--border-color)', borderRadius:6, padding:'3px 8px', fontSize:11.5, cursor: busy ? 'not-allowed' : 'pointer', marginRight:6}}>Sync now</button>
                  <button disabled={busy} onClick={() => setEditing({ user_id:r.user_id, display_name:r.display_name, npl_patient_id:r.npl_patient_id })} style={{background:'none', border:'1px solid var(--border-color)', borderRadius:6, padding:'3px 8px', fontSize:11.5, cursor:'pointer', marginRight:6}}>Edit</button>
                  <button disabled={busy} onClick={() => remove(r.user_id)} style={{background:'none', border:'1px solid #fca5a5', color:'#dc2626', borderRadius:6, padding:'3px 8px', fontSize:11.5, cursor:'pointer'}}>Remove</button>
                </td>
              </tr>
            ))}
            {rows.unmapped.map(r => (
              <tr key={`u-${r.user_id}`} style={{borderTop:'1px solid var(--border-color)'}}>
                <td style={{padding:'8px 12px', fontWeight:500}}>{r.display_name || r.profile_name}</td>
                <td style={{padding:'8px 12px', color:'var(--text-secondary)'}}>{r.email}</td>
                <td style={{padding:'8px 12px', color:'var(--text-dim)'}}>—</td>
                <td style={{padding:'8px 12px', color:'var(--text-dim)'}}>—</td>
                <td style={{padding:'8px 12px', color:'var(--text-dim)'}}>—</td>
                <td style={{padding:'8px 12px', textAlign:'right'}}>
                  <button disabled={busy} onClick={() => setEditing({ user_id:r.user_id, display_name:r.display_name, npl_patient_id:'' })} style={{background:'var(--npl-blue, #7F91DF)', color:'#FFFFFF', border:'none', borderRadius:6, padding:'4px 10px', fontSize:11.5, cursor:'pointer'}}>Add ID</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div style={{position:'fixed', inset:0, background:'rgba(20,30,50,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9998}} onClick={() => !busy && setEditing(null)}>
          <div onClick={e => e.stopPropagation()} style={{background:'var(--bg-card)', color:'var(--text-primary)', borderRadius:12, padding:'22px 24px', maxWidth:440, width:'90%'}}>
            <h3 style={{margin:'0 0 8px 0', fontSize:16}}>NPL Patient ID</h3>
            <div style={{fontSize:13, color:'var(--text-secondary)', marginBottom:14}}>
              For <strong>{editing.display_name}</strong>. Enter the reference exactly as it appears in the NPL LIS portal (e.g. <code style={{background:'var(--bg-card-raised)', color:'var(--text-primary)', padding:'1px 4px', borderRadius:3}}>LabBC00001</code>).
            </div>
            <input
              type="text"
              value={editing.npl_patient_id}
              onChange={e => setEditing({ ...editing, npl_patient_id: e.target.value })}
              placeholder="LabBC00001"
              style={{width:'100%', padding:'10px 12px', fontSize:14, border:'1px solid var(--border-color)', borderRadius:8, marginBottom:14, fontFamily:'monospace'}}
              autoFocus
            />
            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <button onClick={() => !busy && setEditing(null)} disabled={busy} style={{background:'none', border:'1px solid var(--border-color)', borderRadius:8, padding:'8px 14px', cursor: busy ? 'not-allowed' : 'pointer'}}>Cancel</button>
              <button onClick={save} disabled={busy || !editing.npl_patient_id.trim()} style={{background:'var(--npl-blue, #7F91DF)', color:'#FFFFFF', border:'none', borderRadius:8, padding:'8px 14px', cursor: (busy || !editing.npl_patient_id.trim()) ? 'not-allowed' : 'pointer', fontWeight:600}}>{busy ? 'Saving…' : 'Save mapping'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Nepali output toggle in the chat composer. Visible only when
// features.nepali_language is on. Stores the preference both in
// localStorage (instant UI feedback) and via /api/user/prefs/nepali so
// the next chat turn picks it up server-side.
function NepaliToggleButton() {
  const [enabled, setEnabled] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem('kalpa_nepali_output') === '1';
  });
  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try { localStorage.setItem('kalpa_nepali_output', next ? '1' : '0'); } catch {}
    try { await api.post('/user/prefs/nepali', { enabled: next }); } catch {}
  };
  return (
    <button
      type="button"
      onClick={toggle}
      title={enabled ? 'Replying in Nepali' : 'Reply in Nepali'}
      style={{
        position:'absolute', right:18, bottom:6,
        background:'none', border:'1px solid var(--border-color)',
        borderRadius:14, padding:'3px 10px', fontSize:11, cursor:'pointer',
        color: enabled ? 'var(--accent)' : 'var(--text-dim)',
        fontWeight: enabled ? 700 : 500,
      }}
    >
      {enabled ? '🇳🇵 ने' : 'EN'}
    </button>
  );
}

// NPL sync trigger button (visible only when features.order_lab is on AND
// the user has an npl_patient_id stored on their profile). Calls the
// /api/npl/sync probe and shows last-sync timestamp. The actual transaction
// fetch is via /api/npl/fetch-report with explicit IDs from the LIS portal.
function NPLSyncButton() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const sync = async () => {
    setLoading(true);
    try {
      const r = await api.get('/npl/sync');
      setStatus(r);
    } catch (e) {
      setStatus({ error: e.message });
    }
    setLoading(false);
  };
  return (
    <div className="hr-filter-npl-sync">
      <button className="hr-filter-btn" onClick={sync} disabled={loading}>
        {loading ? 'Syncing…' : '↻ Sync NPL Results'}
      </button>
      {status && status.last_sync && (
        <span className="hr-filter-npl-last" title={status.last_sync}>
          last sync: {formatDateTime(status.last_sync)}
        </span>
      )}
      {status && status.error && (
        <span className="hr-filter-npl-err">{status.error}</span>
      )}
      {status && status.reason && !status.success && (
        <span className="hr-filter-npl-err">{status.reason}</span>
      )}
    </div>
  );
}

// Map a panel name to the chart_key the order-nudge backend uses, so
// OrderPromptCard can be rendered right under the matching panel.
function _panelChartKey(panelName) {
  const n = (panelName || '').toLowerCase();
  if (n.includes('vitamin d')) return 'vitamin_d';
  if (n.includes('iron') || n.includes('ferritin')) return 'ferritin';
  if (n.includes('b12') || n.includes('cobalamin')) return 'b12';
  if (n.includes('hba1c') || n.includes('diabetes') || n.includes('glucose')) return 'hba1c';
  if (n.includes('lipid') || n.includes('cholesterol')) return 'lipid';
  if (n.includes('thyroid')) return 'thyroid';
  if (n.includes('magnesium')) return 'magnesium';
  if (n.includes('zinc')) return 'zinc';
  if (n.includes('crp') || n.includes('inflammation')) return 'crp';
  return null;
}

// -- ClinicalTimeline --------------------------------------------------------
// ── Longevity Flags View — action-first card list for flagged markers ───
// Each entry is keyed by the marker_key returned by
// /api/health-record/longevity-flags. `steps[]` is rendered in order as
// the numbered action ladder; lowest-friction first (lifestyle →
// supplement → GP → lab test). `orderTest` populates the conditional
// NPL order CTA at the bottom of the card — only shown when the user
// has taken at least one prior step (supplement aged ≥8 weeks or
// sustained exercise). `ageDelta` (years) is the impact figure shown
// in the impact line + used to sort the cards.
const LONGEVITY_ACTIONS = {
  vitamin_d: {
    steps: [
      { icon: '☀️', text: 'Get 15–20 minutes of direct sunlight daily.' },
      { icon: '💊', text: 'Take Vitamin D3 1000–2000 IU daily.', addMedicine: 'Vitamin D3 1000 IU' },
      { icon: '🩺', text: 'See your GP if levels stay deficient after 3 months.', showGP: true },
    ],
    orderTest: { name: 'Vitamin D (25-OH)', code: 'BC_VIT_D', priceNPR: 450 },
    supplement: 'Vitamin D3',
    ageDelta: 0.6,
  },
  hdl_cholesterol: {
    steps: [
      { icon: '🏃', text: 'Add 30 minutes of cardio exercise 3× per week.' },
      { icon: '🥑', text: 'Increase healthy fats — olive oil, nuts, avocado.' },
      { icon: '🚭', text: 'Avoid smoking and limit alcohol intake.' },
      { icon: '🩺', text: 'Discuss persistent low HDL with your GP.', showGP: true },
    ],
    orderTest: { name: 'Lipid Panel', code: 'BC_LIPID', priceNPR: 890 },
    supplement: null,
    exerciseTrigger: true,
    ageDelta: 0.8,
  },
  ldl_cholesterol: {
    steps: [
      { icon: '🥦', text: 'Reduce saturated fat and processed foods.' },
      { icon: '🏃', text: '30 minutes of cardio 3–4× per week reduces LDL by ~5–10%.' },
      { icon: '💊', text: 'Consider plant sterols or red yeast rice.', addMedicine: 'Plant Sterols 2g' },
      { icon: '🩺', text: 'See your GP about statins if LDL stays > 4.0 mmol/L.', showGP: true },
    ],
    orderTest: { name: 'Lipid Panel', code: 'BC_LIPID', priceNPR: 890 },
    supplement: 'Plant Sterols',
    exerciseTrigger: true,
    ageDelta: 0.7,
  },
  triglycerides: {
    steps: [
      { icon: '🍚', text: 'Reduce refined carbohydrates and sugar.' },
      { icon: '🐟', text: 'Add oily fish 2× per week, or take an Omega-3 supplement.', addMedicine: 'Omega-3 Fish Oil 1000 mg' },
      { icon: '🏃', text: '30 minutes of aerobic exercise most days.' },
    ],
    orderTest: { name: 'Lipid Panel', code: 'BC_LIPID', priceNPR: 890 },
    supplement: 'Omega-3',
    ageDelta: 0.5,
  },
  total_cholesterol: {
    steps: [
      { icon: '🥦', text: 'Mediterranean-style diet — vegetables, olive oil, fish.' },
      { icon: '🏃', text: 'Regular aerobic exercise 3–4× per week.' },
      { icon: '🩺', text: 'See your GP if persistently elevated despite lifestyle changes.', showGP: true },
    ],
    orderTest: { name: 'Lipid Panel', code: 'BC_LIPID', priceNPR: 890 },
    supplement: null,
    ageDelta: 0.4,
  },
  tc_hdl_ratio: {
    steps: [
      { icon: '🏃', text: 'Cardio exercise lifts HDL and lowers TC — both move the ratio.' },
      { icon: '🥑', text: 'Healthy fats over saturated fats — olive oil, nuts, avocado.' },
      { icon: '🩺', text: 'Discuss persistent ratio > 5 with your GP.', showGP: true },
    ],
    orderTest: { name: 'Lipid Panel', code: 'BC_LIPID', priceNPR: 890 },
    supplement: null,
    ageDelta: 0.6,
  },
  glucose_fasting: {
    steps: [
      { icon: '🍽️', text: 'Avoid high-glycaemic foods — white rice, bread, sugar.' },
      { icon: '🚶', text: 'A 10-minute walk after meals significantly lowers post-meal glucose.' },
      { icon: '💊', text: 'Consider berberine or magnesium with a clinician’s guidance.', addMedicine: 'Berberine 500 mg' },
      { icon: '🩺', text: 'See your GP if fasting glucose stays > 5.6 mmol/L.', showGP: true },
    ],
    orderTest: { name: 'Metabolic Panel', code: 'BC_META', priceNPR: 1200 },
    supplement: 'Berberine',
    ageDelta: 0.9,
  },
  hba1c: {
    steps: [
      { icon: '🥗', text: 'Low-glycaemic diet — vegetables, protein, whole grains first.' },
      { icon: '🏃', text: 'Combine aerobic + resistance training for the strongest effect.' },
      { icon: '⚖️', text: 'A 5% body-weight reduction significantly improves HbA1c.' },
      { icon: '🩺', text: 'See your GP if HbA1c reaches the 5.7% pre-diabetes threshold.', showGP: true },
    ],
    orderTest: { name: 'Metabolic Panel', code: 'BC_META', priceNPR: 1200 },
    supplement: null,
    exerciseTrigger: true,
    ageDelta: 1.1,
  },
  ferritin: {
    steps: [
      { icon: '🥩', text: 'Add red meat or dark leafy greens 3–4× per week.' },
      { icon: '🍊', text: 'Pair iron-rich foods with Vitamin C to improve absorption.' },
      { icon: '💊', text: 'Consider an iron supplement under GP guidance.', addMedicine: 'Ferrous Sulfate 200 mg' },
      { icon: '🩺', text: 'See your GP — low ferritin may indicate blood loss or absorption issues.', showGP: true },
    ],
    orderTest: { name: 'Iron Studies', code: 'BC_IRON', priceNPR: 650 },
    supplement: 'Iron',
    ageDelta: 0.4,
  },
  iron: {
    steps: [
      { icon: '🥩', text: 'Add iron-rich foods — red meat, lentils, dark leafy greens.' },
      { icon: '🍊', text: 'Take with Vitamin C to improve absorption.' },
      { icon: '🩺', text: 'See your GP — persistent low iron should be investigated.', showGP: true },
    ],
    orderTest: { name: 'Iron Studies', code: 'BC_IRON', priceNPR: 650 },
    supplement: 'Iron',
    ageDelta: 0.4,
  },
  b12: {
    steps: [
      { icon: '🥚', text: 'Increase eggs, dairy, meat and fish in the diet.' },
      { icon: '💊', text: 'Methylcobalamin B12 is the most absorbable form.', addMedicine: 'Vitamin B12 1000 mcg' },
      { icon: '🩺', text: 'See your GP if B12 stays low — may need injections.', showGP: true },
    ],
    orderTest: { name: 'Vitamin B12', code: 'BC_B12', priceNPR: 380 },
    supplement: 'Vitamin B12',
    ageDelta: 0.3,
  },
  crp: {
    steps: [
      { icon: '🐟', text: 'Omega-3 fatty acids reduce systemic inflammation.' },
      { icon: '🫐', text: 'Anti-inflammatory diet — berries, turmeric, olive oil, nuts.' },
      { icon: '😴', text: 'Prioritise 7–8 hours sleep; poor sleep raises CRP.' },
      { icon: '🩺', text: 'See your GP if CRP stays > 3 mg/L to rule out infection.', showGP: true },
    ],
    orderTest: { name: 'Inflammation Panel', code: 'BC_INFLAM', priceNPR: 550 },
    supplement: 'Omega-3',
    ageDelta: 0.6,
  },
  esr: {
    steps: [
      { icon: '🫐', text: 'Anti-inflammatory foods — berries, oily fish, turmeric.' },
      { icon: '🏃', text: 'Moderate regular exercise reduces baseline inflammation.' },
      { icon: '🩺', text: 'Discuss persistent elevation with your GP.', showGP: true },
    ],
    orderTest: { name: 'Inflammation Panel', code: 'BC_INFLAM', priceNPR: 550 },
    supplement: null,
    ageDelta: 0.3,
  },
  tsh: {
    steps: [
      { icon: '🍽️', text: 'Adequate iodine and selenium intake support thyroid function.' },
      { icon: '😴', text: 'Sleep and stress significantly affect thyroid hormone balance.' },
      { icon: '🩺', text: 'See your GP — thyroid imbalance usually needs investigation.', showGP: true },
    ],
    orderTest: { name: 'Thyroid Panel', code: 'BC_THY', priceNPR: 720 },
    supplement: null,
    ageDelta: 0.4,
  },
  hemoglobin: {
    steps: [
      { icon: '🥩', text: 'Iron-rich foods and Vitamin C pairing.' },
      { icon: '🩺', text: 'See your GP — low haemoglobin needs evaluation.', showGP: true },
    ],
    orderTest: { name: 'Complete Blood Count', code: 'BC_CBC', priceNPR: 580 },
    supplement: 'Iron',
    ageDelta: 0.5,
  },
};

// Marker key → human-readable status label for the impact line.
const _LONGEVITY_STATUS_LABEL = {
  deficient:        'Deficient',
  borderline_low:   'Borderline low',
  borderline_high:  'Borderline',
  elevated:         'Elevated',
};

// Status → colour for the value pill in the card header.
const _LONGEVITY_STATUS_COLOR = {
  deficient:        '#E16A8F',
  borderline_low:   '#F08E7F',
  borderline_high:  '#F08E7F',
  elevated:         '#E16A8F',
};

// Plain-language "what this marker means for wellbeing" — one wellness-framed
// sentence per marker (no diagnosis, no boilerplate). Absent markers simply omit
// the meaning line rather than inventing one.
const _LONGEVITY_MEANING = {
  vitamin_d:        'Vitamin D supports bone strength, immunity and mood, and below-optimal levels are common and usually correctable.',
  triglycerides:    'Triglycerides are a blood fat, and keeping them low supports heart and metabolic health.',
  ldl_cholesterol:  'LDL carries cholesterol that can build up in arteries, so lower levels support long-term heart health.',
  hdl_cholesterol:  'HDL helps clear cholesterol from your arteries, so higher levels are protective.',
  total_cholesterol:'Total cholesterol sums the fats in your blood, and keeping it in range supports heart health.',
  tc_hdl_ratio:     'This ratio weighs total cholesterol against protective HDL, and a lower ratio is better for your heart.',
  glucose_fasting:  'Fasting glucose reflects your blood-sugar control, and keeping it in range supports steady energy and metabolic health.',
  hba1c:            'HbA1c reflects your average blood sugar over about three months, and a lower in-range value supports long-term metabolic health.',
  ferritin:         'Ferritin reflects your iron stores, which support healthy red blood cells and energy.',
  iron:             'Iron supports healthy red blood cells and steady energy.',
  b12:              'Vitamin B12 supports nerve function, red blood cells and energy.',
  crp:              'CRP is a marker of inflammation, and lower, stable levels support long-term health.',
  esr:              'ESR is a marker of inflammation, and lower, stable levels support long-term health.',
  tsh:              'TSH reflects your thyroid balance, which affects metabolism, energy and mood.',
  hemoglobin:       'Haemoglobin carries oxygen around your body, and keeping it in range supports energy and stamina.',
};

// Trend-aware narrative for the "What you can do" section (Part 2). Deterministic,
// built from the SAME history the chart plots — cites the before value+date, the
// latest value+date and the direction (rising/falling/flat); for a single reading
// it states the value + which side of optimal it sits, with NO invented trend.
// Wellness framing, no diagnosis, no boilerplate disclaimer.
function _LongevityTrendNarrative({ flag }) {
  const u = flag.unit ? ` ${flag.unit}` : '';
  const name = flag.display_name || flag.marker_name || 'this marker';
  // Flag basis = the LAB REFERENCE range (matches the card/bar). Optimal is secondary.
  const lo = _rbNum(flag.ref_min), hi = _rbNum(flag.ref_max);
  const optimalText = (lo != null && hi != null) ? `${lo}–${hi}` : (hi != null ? `≤ ${hi}` : (lo != null ? `≥ ${lo}` : ''));
  const meaning = _LONGEVITY_MEANING[flag.marker_key] || '';
  // RUN11 §2 — the trend SHAPE is now computed by the shared backend engine (RCV, not
  // first-vs-last), plus §3 cross-marker + §4 meds context, so the drawer, this flags card,
  // and Ask-Kalpa chat all tell the SAME story. Render the governed sentences when present;
  // the deterministic client fallback below only runs if the backend insight is unavailable.
  const ins = flag.insight;
  if (ins && (ins.trend_sentence || ins.cross_marker || ins.meds_note || ins.diet_context)) {
    const cites = (ins.citations || []).join(' · ');
    return (
      <div style={{ fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 10 }}>
        {/* §B — glossary tooltips on Longevity prose via the shared autoGlossary (tappable GlossaryTerm).
            KAL-034 — these four are SERVER-COMPOSED sentences that embed dates as raw ISO
            ("has risen from 27.2 U/L (2024-11-17)"). Every other server-string surface already
            runs through deIsoDates (advice 1579, chips 1585, chat 7268, insight cards 7396) —
            this one was simply missed, so the demo surface showed YYYY-MM-DD against CLAUDE.md's
            DD MMM YYYY rule. deIsoDates is a thin delegate to the ONE deIsoDatesLocale rule
            (App.js:382); no second date formatter exists and none is added here. Order matches
            line 1579: de-ISO first, then glossary, so the glossary never sees a half-rewritten
            string. */}
        {ins.trend_sentence ? <div>{autoGlossary(deIsoDates(ins.trend_sentence + (meaning ? ` ${meaning}` : '')))}</div> : null}
        {ins.cross_marker ? <div style={{ marginTop: 6 }}>{autoGlossary(deIsoDates(ins.cross_marker))}</div> : null}
        {ins.meds_note ? <div style={{ marginTop: 6 }}>{autoGlossary(deIsoDates(ins.meds_note))}</div> : null}
        {/* RUN11.5 §B2 — governed diet lens (interpretation only; e.g. low Ferritin + plant-forward diet). */}
        {ins.diet_context ? <div style={{ marginTop: 6 }}>{autoGlossary(deIsoDates(ins.diet_context))}</div> : null}
        {cites ? <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text-secondary)' }}>Evidence: {cites}</div> : null}
      </div>
    );
  }
  const pts = (flag.history || [])
    .filter(p => p && p.value != null && p.date)
    .map(p => {
      const s = String(p.date);
      return { ts: new Date(s.length <= 10 ? s + 'T00:00:00' : s).getTime(), value: Number(p.value), date: p.date };
    })
    .filter(p => Number.isFinite(p.ts) && Number.isFinite(p.value))
    .sort((a, b) => a.ts - b.ts);

  let sentence;
  if (pts.length >= 2) {
    const a = pts[0], b = pts[pts.length - 1];
    const flat = Math.abs(b.value - a.value) < 1e-9;
    if (flat) {
      sentence = `Your ${name} has held steady around ${b.value}${u} (${formatDate(a.date)} → ${formatDate(b.date)}) — a flat trend.`;
    } else {
      const rose = b.value > a.value;
      sentence = `Your ${name} ${rose ? 'rose' : 'fell'} from ${a.value}${u} (${formatDate(a.date)}) to ${b.value}${u} (${formatDate(b.date)}) — ${rose ? 'an upward' : 'a downward'} trend.`;
    }
  } else if (pts.length === 1) {
    const p = pts[0];
    const side = (hi != null && p.value > hi) ? 'above' : (lo != null && p.value < lo) ? 'below' : 'outside';
    sentence = `This is your first ${name} reading — ${p.value}${u} on ${formatDate(p.date)}${optimalText ? `, ${side} the lab reference range (${optimalText}${u})` : ''}. Retest in a few weeks to establish a trend.`;
  } else {
    return null;
  }
  return (
    <div style={{ fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 10 }}>
      {T(sentence)}{meaning ? ` ${T(meaning)}` : ''}
    </div>
  );
}

// Single flagged-marker card. All the action-ladder + NPL-order logic
// for one marker lives in here.
// NOTE: OrderConfirmModal's real implementation lives above (NPL pilot order
// flow). The Kalpa-main stub that previously sat here was removed during the
// main → npl-instance merge so the real component is the single definition.

// W4 — deterministic range bar: optimal band + user value marker + min/max scale.
// Always renders (unlike the history sparkline, which is empty for a single reading);
// open-ended bounds render as "≤ max" / "≥ min" (never "None–75").
function _rbNum(n) {
  if (n == null || n === '' || n === 'None' || n === 'null') return null;
  const v = typeof n === 'number' ? n : parseFloat(n);
  return Number.isFinite(v) ? v : null;
}
// ── Reference-range PROVENANCE disclosure (10 Aug 2026) ─────────────────────────────────
// The backend resolver (_effective_ref) prefers the range THIS reading's own lab printed, and
// falls back to a general marker_panels range when the lab supplied none. Measured reach of
// that fallback: 31% of NPL readings and 54% of UK/US. Until now both rendered identically as
// "Reference", so a general population range was indistinguishable from the user's own
// laboratory's — and users comparing the app against their NPL report saw a conflict with no
// explanation (the committee raised exactly this).
//
// `ref_source` comes FROM the resolver, so this note can never disagree with the range above
// it. Renders nothing when the range is the lab's own. Display only: no range value changes,
// no flag/status/score changes — the number and the band are identical either way.
const REF_SRC_FALLBACK = 'general_guidance';

function _RefSourceNote({ refSource, style }) {
  if (refSource !== REF_SRC_FALLBACK) return null;
  return (
    <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 5, lineHeight: 1.4, ...(style || {}) }}>
      Reference range not supplied by the laboratory — general guidance shown.
    </div>
  );
}

function _LongevityRangeBar({ value, refMin, refMax, optMin, optMax, unit, valueColor, refSource }) {
  const v = _rbNum(value), lo = _rbNum(refMin), hi = _rbNum(refMax);
  const olo = _rbNum(optMin), ohi = _rbNum(optMax);
  if (v == null) return null;
  // FIXED CLINICAL SCALE — floor→just above the lab upper — NOT auto-scaled to the
  // reading (so the axis endpoints are clinical, never 65.9/145.2). The shaded band is
  // the LAB REFERENCE range; the optimal is only a thin secondary tick.
  const hiRef = hi != null ? hi : (lo != null ? lo * 1.5 : v * 1.3);
  const aMin = 0;
  const aMax = (Math.max(hiRef, v, ohi != null ? ohi : 0) * 1.1) || 1;
  const pct = x => Math.max(0, Math.min(100, ((x - aMin) / (aMax - aMin)) * 100));
  const bandL = lo != null ? pct(lo) : 0, bandR = hi != null ? pct(hi) : 100;
  const u = unit ? ` ${unit}` : '';
  const refText = (lo != null && hi != null) ? `${lo}–${hi}` : (hi != null ? `≤${hi}` : (lo != null ? `≥${lo}` : '—'));
  const optText = (olo != null && ohi != null) ? `${olo}–${ohi}` : (ohi != null ? `≤${ohi}` : (olo != null ? `≥${olo}` : ''));
  const round = x => Math.round(x * 10) / 10;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 6 }}>
        {refSource === REF_SRC_FALLBACK ? 'General reference' : 'Reference'} {refText}{u} · You <span style={{ color: valueColor || 'var(--text-primary)', fontWeight: 600 }}>{v}{u}</span>
      </div>
      <div style={{ position: 'relative', height: 10, borderRadius: 5, background: 'rgba(255,255,255,0.10)' }}>
        {/* The tooltip said "Lab reference" unconditionally, which is affirmatively WRONG on a
            fallback range — it asserted a provenance the reading does not have. */}
        <div title={`${refSource === REF_SRC_FALLBACK ? 'General reference (not supplied by the laboratory)' : 'Lab reference'} ${refText}${u}`} style={{ position: 'absolute', left: `${bandL}%`, width: `${Math.max(2, bandR - bandL)}%`, top: 0, bottom: 0, background: 'rgba(34,197,94,0.30)', borderRadius: 5 }} />
        {ohi != null && <div title={`Optimal ≤${ohi}${u}`} style={{ position: 'absolute', left: `calc(${pct(ohi)}% - 1px)`, top: -2, width: 2, height: 14, background: 'rgba(201,151,58,0.9)' }} />}
        {olo != null && <div title={`Optimal ≥${olo}${u}`} style={{ position: 'absolute', left: `calc(${pct(olo)}% - 1px)`, top: -2, width: 2, height: 14, background: 'rgba(201,151,58,0.9)' }} />}
        <div title={`You: ${v}${u}`} style={{ position: 'absolute', left: `calc(${pct(v)}% - 6px)`, top: -3, width: 12, height: 16, borderRadius: 3, background: valueColor || '#F4F6FF', border: '2px solid #0a0e1a', boxShadow: '0 0 0 1px rgba(255,255,255,0.6)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dim)', marginTop: 3 }}>
        <span>{round(aMin)}</span><span>{round(aMax)}{u}</span>
      </div>
      {optText && <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 5 }}>Optimal longevity target {optText}{u} (secondary — the flag above is vs the lab reference range).</div>}
      <_RefSourceNote refSource={refSource} />
    </div>
  );
}

// ── Longevity marker visualisation (REDESIGN) ───────────────────────────────
// Replaces the old two-stacked-unlabelled-graphics layout (an axis-less sparkline
// that went blank/dark for a flat series + a separate reference bar) with ONE
// self-explanatory graphic per state.
//
// Pattern (cited): a labelled biomarker time-series drawn against a SHADED
// OPTIMAL-RANGE BAND — the standard "reference-range plot" used in modern
// lab-result visualisation (e.g. InsideTracker / Function Health biomarker
// cards and clinical reference-interval plots): the value is always read
// RELATIVE to its optimal zone, on labelled axes, with the current reading
// called out. For a single reading there is no series to plot, so we show the
// value on a labelled reference-range bar + a "retest to see a trend" note —
// never an empty plot or an axis-less line.
function _LongevityMarkerViz({ history, current, refMin, refMax, optMin, optMax, unit, displayName, status, latestDate, refSource }) {
  const color = _LONGEVITY_STATUS_COLOR[status] || '#7F91DF';
  // PRIMARY shaded band = the LAB REFERENCE range (the flag basis). Optimal is secondary.
  // RUN11.6 §2 — NPL is locale-locked to the lab's OWN printed reference range and does NOT
  // render the Kalpa longevity "optimal" band (it conflicts with the lab range — e.g. Ferritin
  // optimal 50–150 vs lab-printed 30–400). Suppress the optimal line + legend chip on NPL only;
  // Kalpa-main keeps rendering it. The marker_panels optimal data + Kalpa path are untouched.
  const _isNPL = (typeof FEATURES_GLOBAL !== 'undefined') && FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.instance === 'npl';
  const lo = _rbNum(refMin), hi = _rbNum(refMax);
  const optLo = _isNPL ? null : _rbNum(optMin), optHi = _isNPL ? null : _rbNum(optMax);
  const u = unit ? ` ${unit}` : '';
  const optimalText = (lo != null && hi != null) ? `${lo}–${hi}` : (hi != null ? `≤ ${hi}` : (lo != null ? `≥ ${lo}` : '—'));
  const optText = (optLo != null && optHi != null) ? `${optLo}–${optHi}` : (optHi != null ? `≤ ${optHi}` : (optLo != null ? `≥ ${optLo}` : ''));
  const pts = (history || [])
    .filter(p => p && p.value != null && p.date)
    .map(p => {
      const s = String(p.date);
      const ts = new Date(s.length <= 10 ? s + 'T00:00:00' : s).getTime();
      return { ts, value: Number(p.value), date: p.date };
    })
    .filter(p => Number.isFinite(p.ts) && Number.isFinite(p.value))
    .sort((a, b) => a.ts - b.ts);

  // ── Single reading (or none): labelled reference bar + retest note ──
  // NO empty plot, NO axis-less line. The bar carries value + optimal + units +
  // min/max scale; the note states the one date and points to a retest.
  if (pts.length < 2) {
    return (
      <div style={{ marginTop: 10, marginBottom: 4 }}>
        <_LongevityRangeBar value={current} refMin={refMin} refMax={refMax} optMin={_isNPL ? null : optMin} optMax={_isNPL ? null : optMax} unit={unit} valueColor={color} refSource={refSource} />
        <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8, lineHeight: 1.4 }}>
          One reading so far{latestDate ? ` (${formatDate(latestDate)})` : ''} — retest to see a trend.
        </div>
      </div>
    );
  }

  // ── >=2 readings: labelled time-series with a shaded optimal band ──
  const vals = pts.map(p => p.value);
  const dMin = Math.min(...vals, lo != null ? lo : Infinity, optLo != null ? optLo : Infinity);
  const dMax = Math.max(...vals, hi != null ? hi : -Infinity, optHi != null ? optHi : -Infinity);
  const spanV = (dMax - dMin) || Math.abs(dMax) || 1;
  const yMin = Math.floor(dMin - spanV * 0.15);
  const yMax = Math.ceil(dMax + spanV * 0.15);
  const ticks = pts.map(p => p.ts);
  const latest = pts[pts.length - 1];
  const many = pts.length > 3;
  return (
    <div style={{ marginTop: 10, marginBottom: 4 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>
        {displayName} over time{unit ? ` (${unit})` : ''}
      </div>
      <ResponsiveContainer width="100%" height={196}>
        <LineChart data={pts} margin={{ top: 12, right: 34, bottom: many ? 20 : 6, left: 4 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          {/* PRIMARY shaded band = LAB REFERENCE range (the flag basis). */}
          {lo != null && hi != null && (
            <ReferenceArea y1={lo} y2={hi} fill="#22c55e" fillOpacity={0.15} stroke="#22c55e" strokeOpacity={0.4} strokeDasharray="3 3"
              label={{ value: `Reference ${optimalText}${u}`, position: 'insideTopLeft', fontSize: 10, fill: '#22c55e' }} />
          )}
          {lo != null && hi == null && (
            <ReferenceLine y={lo} stroke="#22c55e" strokeDasharray="4 3"
              label={{ value: `Reference ≥ ${lo}${u}`, position: 'insideBottomLeft', fontSize: 10, fill: '#22c55e' }} />
          )}
          {hi != null && lo == null && (
            <ReferenceLine y={hi} stroke="#22c55e" strokeDasharray="4 3"
              label={{ value: `Reference ≤ ${hi}${u}`, position: 'insideTopLeft', fontSize: 10, fill: '#22c55e' }} />
          )}
          {/* SECONDARY amber line = Kalpa longevity optimal target (informational). */}
          {optHi != null && (
            <ReferenceLine y={optHi} stroke="#C9973A" strokeDasharray="2 3" strokeOpacity={0.8}
              label={{ value: `Optimal ≤ ${optHi}${u}`, position: 'insideBottomRight', fontSize: 9.5, fill: '#C9973A' }} />
          )}
          {optLo != null && optHi == null && (
            <ReferenceLine y={optLo} stroke="#C9973A" strokeDasharray="2 3" strokeOpacity={0.8}
              label={{ value: `Optimal ≥ ${optLo}${u}`, position: 'insideTopRight', fontSize: 9.5, fill: '#C9973A' }} />
          )}
          <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']} ticks={ticks}
            tickFormatter={formatDate} tick={{ fontSize: 10, fill: 'var(--text-dim, #8a9bb0)' }}
            angle={many ? -20 : 0} textAnchor={many ? 'end' : 'middle'} height={many ? 46 : 22}
            axisLine={{ stroke: 'rgba(255,255,255,0.15)' }} tickLine={false} />
          <YAxis domain={[yMin, yMax]} width={40} tick={{ fontSize: 10, fill: 'var(--text-dim, #8a9bb0)' }}
            axisLine={false} tickLine={false} allowDecimals={false}
            label={{ value: unit || 'value', angle: -90, position: 'insideLeft', fontSize: 10, fill: 'var(--text-dim, #8a9bb0)', style: { textAnchor: 'middle' } }} />
          <Tooltip formatter={(v) => [`${v}${u}`, displayName]} labelFormatter={(ts) => formatDate(ts)}
            contentStyle={{ fontSize: 11, padding: '4px 8px', background: '#0d1a33', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6 }} />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2}
            dot={{ r: 3, fill: color, stroke: '#0a0e1a', strokeWidth: 1 }} activeDot={{ r: 5 }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      {/* Inline legend so the line, the band and the latest value are each self-explanatory. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 4, fontSize: 10.5, color: 'var(--text-dim)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 16, borderTop: `2px solid ${color}` }} /> Your readings
        </span>
        {(lo != null || hi != null) && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 10, background: 'rgba(34,197,94,0.30)', border: '1px solid rgba(34,197,94,0.55)', borderRadius: 2 }} /> {refSource === REF_SRC_FALLBACK ? 'General reference' : 'Reference'} {optimalText}{u}
          </span>
        )}
        {optText && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, borderTop: '2px dashed #C9973A' }} /> Optimal {optText}{u}
          </span>
        )}
        <span>Latest <strong style={{ color }}>{latest.value}{u}</strong> · {formatDate(latest.date)}</span>
      </div>
      <_RefSourceNote refSource={refSource} style={{ marginTop: 6 }} />
    </div>
  );
}

function LongevityActionCard({ flag, features, onNav }) {
  const cfg = LONGEVITY_ACTIONS[flag.marker_key] || { steps: [{ icon: '🩺', text: 'Discuss this result with your GP or a health professional.', showGP: true }], orderTest: null };
  // ageDelta drives relative PRIORITY (card sort) only — NOT a displayed year-claim.
  // The old "· costing ~X years" was a fabricated precise figure from a hardcoded
  // constant, not from the bio-age model. Show a qualitative priority instead.
  const ageDelta = cfg.ageDelta != null ? cfg.ageDelta : 0;
  // RUN11.4b §A2 — a GOVERNED status label (e.g. lymphocyte "worth re-checking") overrides the
  // generic ageDelta priority phrase so the pill matches the governed tier.
  const ageDeltaLabel = flag.status_label ? ('· ' + flag.status_label)
                        : ageDelta < 0.1 ? '· worth monitoring'
                        : ageDelta >= 0.8 ? '· higher priority'
                        : ageDelta >= 0.5 ? '· moderate priority'
                        : '· worth addressing';
  // Arrow glyph follows the NUMBER (up = value increased), not the semantic
  // improving/declining — the old code inverted it, showing ↓ when AST rose
  // 33→48 (5C). Colour reflects whether that numeric move is beneficial.
  const _arrow = flag.trend_arrow || (flag.trend === 'improving' ? 'down' : flag.trend === 'declining' ? 'up' : 'flat');
  const trendGlyph = _arrow === 'up' ? '↑' : (_arrow === 'down' ? '↓' : '→');
  const _good = (flag.trend_good != null) ? flag.trend_good : (flag.trend === 'improving');
  const trendColor = flag.trend === 'stable' || _arrow === 'flat' ? 'var(--text-dim)' : (_good ? '#22c55e' : '#E16A8F');
  const statusLabel = _LONGEVITY_STATUS_LABEL[flag.status] || 'Outside range';
  const valueColor = _LONGEVITY_STATUS_COLOR[flag.status] || 'var(--text-primary)';
  // Fix 3 — friendly date for the (non-clickable) retest nudge line.
  const _fmtRetestDate = (s) => formatDate(s);
  // Fix 2 — whole months since a supplement start date (for the retest nudge copy).
  const _monthsSince = (s) => { if (!s) return null; try { const d = (Date.now() - new Date(s + 'T00:00:00').getTime()) / 86400000; return Math.max(1, Math.round(d / 30.44)); } catch { return null; } };

  // NPL order CTA (FIX 2): show on EVERY order-eligible flagged marker (i.e. one
  // that has an orderTest mapping) when order_lab is on — not only after a prior
  // action. Previously this was gated on supplement≥8wk OR exercise≥10, so some
  // flagged markers (e.g. Vitamin D) showed no button while others did. In the
  // pilot the request is free + routes to Bimal, so a consistent CTA is right.
  const orderUnlocked = !!(_orderingOn() && cfg.orderTest);   // KAL-303

  // B3 — retest-timing gate: only nudge/offer a RETEST once a clinically sensible
  // interval has elapsed since the marker's LAST test date (not since a supplement
  // start). Intervals: Vitamin D ~12 wk after starting/adjusting supplementation
  // (Endocrine Society 2011 / NICE); lipids & triglycerides ~6–12 wk after a therapy
  // change, routine ~annually (NICE CG181); HbA1c ~3 months (ADA). Default 90 days.
  const _RETEST_INTERVAL_DAYS = { 'Vitamin D': 84, 'Triglycerides': 84, 'LDL': 84, 'HDL': 84, 'Total Cholesterol': 84, 'Cholesterol': 84, 'HbA1c': 90, 'Ferritin': 84, 'Vitamin B12': 90, 'B12': 90 };
  const _retestNm = `${flag.display_name || ''} ${flag.marker_name || ''}`;
  const _retestDays = (Object.entries(_RETEST_INTERVAL_DAYS).find(([k]) => _retestNm.includes(k)) || [null, 90])[1];
  const _daysSinceTest = flag.latest_date ? Math.round((Date.now() - new Date(flag.latest_date + 'T00:00:00').getTime()) / 86400000) : 9999;
  const _retestDue = _daysSinceTest >= _retestDays;   // premature to retest before this interval

  const [showOrderModal, setShowOrderModal] = useState(false);

  const handleAddMedicine = (name) => {
    // Store the prefill + return destination together so MedicinesPage
    // can a) open the add form pre-populated with this supplement name,
    // and b) bounce the user back to #longevity-flags after they save
    // or cancel — instead of leaving them on the Medicines tab where
    // browser-back would then exit the app.
    try {
      sessionStorage.setItem('add_medicine_prefill', JSON.stringify({
        name, returnTo: 'longevity-flags',
      }));
    } catch {}
    // Push a #medicines history entry FIRST so the App's currentView→hash sync
    // (which uses replaceState) skips its replace — leaving an intact back-stack
    // entry. Browser-back then returns to the Longevity markers (flags) view,
    // not the all-markers page that preceded it. Mirrors the ⚑/Documents pattern.
    try { window.history.pushState({}, '', '#medicines'); } catch {}
    if (onNav) onNav('medicines');
  };

  const handleOrder = () => {
    // Open the OrderConfirmModal inline as an overlay above the
    // Longevity Flags View. After confirm/dismiss the user stays on
    // #longevity-flags (do NOT navigate to timeline here — earlier
    // implementation did, which surfaced as "Order NPL button closes
    // the page" in user testing).
    setShowOrderModal(true);
  };

  // Build the prompt object OrderConfirmModal expects. It's lifted
  // straight from cfg.orderTest with a trigger_type for analytics
  // (so backend can tell longevity-flags-driven orders apart from
  // contextual chart prompts).
  const orderPrompt = cfg.orderTest ? {
    key:          `longevity_flag_${flag.marker_key}`,
    chart_key:    flag.marker_key,
    marker:       flag.display_name || flag.marker_name,
    test_code:    cfg.orderTest.code,
    test_name:    cfg.orderTest.name,
    price_npr:    cfg.orderTest.priceNPR,
    trigger_type: 'longevity_flag',
  } : null;

  return (
    <div className="longevity-action-card" style={{
      background: 'var(--secondary-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: 12,
      padding: '14px 14px 16px 14px',
      marginBottom: 14,
    }}>
      {flag.trend === 'improving' && (
        <div style={{
          background:'rgba(34, 197, 94, 0.12)', color:'#16a34a',
          fontSize:12, padding:'6px 10px', borderRadius:6,
          marginBottom:10, fontWeight:500,
        }}>
          ↑ Improving since your last test — keep going.
        </div>
      )}

      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:10}}>
        <div style={{fontFamily:"'Satoshi', sans-serif", fontWeight:500, fontSize:15, color:'var(--text-primary)', minWidth:0, flex:1}}>
          {flag.display_name}
        </div>
        <div style={{whiteSpace:'nowrap', fontSize:14, color: valueColor, fontWeight:600}}>
          <span style={{color: trendColor, marginRight:6}}>{trendGlyph}</span>
          {mkVal(flag.displayName || flag.name || flag.marker_key, flag.current_value, flag.unit)}
        </div>
      </div>

      <div style={{fontSize:12.5, color:'var(--text-secondary)', marginTop:4, marginBottom:10}}>
        {statusLabel} {ageDeltaLabel}
      </div>

      {/* RUN11.4b §A2 — single-card governance: the %/absolute pair for one analyte (e.g.
          Lymphocytes) render as ONE card; the second measure shows as a supporting line
          under the headline reading rather than as its own separate flag. */}
      {flag.supporting && (
        <div style={{fontSize:12, color:'var(--text-dim)', marginTop:-6, marginBottom:10}}>
          {flag.supporting.label} {flag.supporting.value}{flag.supporting.unit || ''}
          {(flag.supporting.ref_min != null || flag.supporting.ref_max != null)
            ? ` (ref ${flag.supporting.ref_min != null ? flag.supporting.ref_min : ''}${flag.supporting.ref_min != null && flag.supporting.ref_max != null ? '–' : ''}${flag.supporting.ref_max != null ? flag.supporting.ref_max : ''}${flag.supporting.unit || ''})`
            : ''}
        </div>
      )}

      <_LongevityMarkerViz
        history={flag.history}
        current={flag.current_value}
        refMin={flag.ref_min}
        refMax={flag.ref_max}
        optMin={flag.optimal_min}
        optMax={flag.optimal_max}
        unit={flag.unit}
        displayName={flag.display_name}
        status={flag.status}
        latestDate={flag.latest_date}
        refSource={flag.ref_source}
      />

      <div style={{height:1, background:'var(--border-color)', margin:'10px -14px 12px -14px'}} />

      <div style={{fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em'}}>What you can do</div>

      {/* Part 2 — deterministic, trend-aware lead-in built from the plotted history:
          before→after values + dates + direction (or single-reading state), plus a
          plain-language "what it means". Grounds the engine advice that follows in
          the actual numbers on the chart. */}
      <_LongevityTrendNarrative flag={flag} />

      {/* RUN11.6 §1 — BLOCK-2 RETIRED. The second "What you can do" block (flag.advice,
          from the ungoverned _intel_advice_bundle Gemini path — diet-blind, injected a
          Kalpa "optimal" band, recommended lean meats to a pescatarian) is removed. The
          governed flag.insight above (_LongevityTrendNarrative — cited, diet-aware,
          dose-free) is now the SOLE advice block under the single shared header. */}

      {/* Fix 2 — supplement-duration retest nudge: distinct, NON-clickable line.
          Fires on flagged marker + relevant active supplement + last test >90d.
          Shows months-taken from the real medicine start date.
          RUN11.8 §1 — gated on features.order_lab (false on NPL) so this "retest your…"
          line is removed alongside the other order/re-test CTAs. MAIN keeps it. */}
      {_orderingOn() && flag.retest_suggested && flag.supplement_matched_name && _retestDue && (() => {
        const months = _monthsSince(flag.supplement_started_date);
        const dur = months ? `~${months} month${months === 1 ? '' : 's'}` : 'a while';
        return (
          <div style={{
            marginTop:12, padding:'9px 11px', borderRadius:8,
            background:'rgba(201,151,58,0.10)', borderLeft:'3px solid #C9973A',
            fontSize:12.5, lineHeight:1.45, color:'var(--text-secondary)',
          }}>
            <span aria-hidden="true" style={{marginRight:6}}>🔁</span>
            You&apos;ve taken {flag.supplement_matched_name} for {dur} — retest your {flag.display_name} to see if it&apos;s responded{flag.latest_date ? ` (last tested ${_fmtRetestDate(flag.latest_date)})` : ''}.
          </div>
        );
      })()}

      {orderUnlocked && _retestDue && (
        <div style={{
          marginTop:14, padding:'10px 12px',
          background:'rgba(127, 145, 223, 0.10)',
          border:'1px dashed var(--npl-blue, #7F91DF)',
          borderRadius:8,
        }}>
          <div style={{fontSize:12.5, color:'var(--text-primary)', marginBottom:6}}>
            {flag.supplement_active && flag.supplement_matched_name
              ? <>You’ve been taking <strong>{flag.supplement_matched_name}</strong> for {flag.supplement_weeks} weeks — a lab retest at NPL will show if it’s working.</>
              : ((flag.exercise_sessions_recent || 0) >= 10
                  ? <>You’ve logged {flag.exercise_sessions_recent} exercise sessions recently — a lab retest at NPL will show the impact.</>
                  : <>Book a lab test for <strong>{flag.display_name}</strong> at NPL to track it.</>)
            }
          </div>
          <button
            onClick={handleOrder}
            style={{
              background:'var(--npl-blue, #7F91DF)', color:'#FFFFFF',
              border:'none', borderRadius:6, padding:'6px 12px',
              fontSize:12.5, fontWeight:600, cursor:'pointer',
            }}
          >
            Request {cfg.orderTest.name} retest at NPL
          </button>
        </div>
      )}
      {orderUnlocked && !_retestDue && (
        <div style={{ marginTop:14, fontSize:12, color:'var(--text-dim)' }}>
          Retested recently{flag.latest_date ? ` on ${_fmtRetestDate(flag.latest_date)}` : ''} — a lab retest isn’t due yet (recommended after ~{Math.round(_retestDays/7)} weeks).
        </div>
      )}
      {showOrderModal && orderPrompt && (
        <OrderConfirmModal
          prompt={orderPrompt}
          onClose={() => setShowOrderModal(false)}
          onConfirmed={() => setShowOrderModal(false)}
        />
      )}
    </div>
  );
}

// Top-of-view summary card. NOTE: no fabricated year figure — the old "Combined
// impact ~X years / improve by up to N years" was summed from hardcoded per-marker
// constants, not the bio-age model. Qualitative framing only.
// RUN11.6 §3 — ONE reconciled count line: total outside the lab range, split into
// worth-acting-on (genuine concerns) + reviewed-in-context (benign patterns). Replaces
// the old duplicate "⚑ N affecting" super-header + "N worth acting on" card headings.
function _LongevitySummaryCard({ total, concerns, contextual }) {
  return (
    <div className="npl-gradient-card" style={{
      padding:'18px 20px', marginBottom:18, borderRadius:12,
    }}>
      <div style={{fontSize:18, fontWeight:700, color:'var(--text-primary)', marginBottom:8}}>
        {total} marker{total === 1 ? '' : 's'} outside your lab range — {concerns} worth acting on, {contextual} reviewed in context
      </div>
      <div style={{fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.5}}>
        {concerns > 0
          ? <>The {concerns === 1 ? 'one' : concerns} worth acting on {concerns === 1 ? 'is' : 'are'} outside your lab reference range and not explained by a benign pattern — bringing {concerns === 1 ? 'it' : 'them'} back into range supports your long-term health.<br/>Sorted by priority — most significant first.</>
          : <>None are currently affecting your longevity score — the rest sit just outside the lab range but, read alongside your related markers, are not a concern.</>}
      </div>
    </div>
  );
}

// ── TODAY feed (kalpa.health flagship — "luminous clinical") ─────────────
// Default landing view. Dark canvas, glassmorphic cards. Phase 1 ships
// Cards 0–3 (greeting + AI headline, KLS hero ring, vitals 2x2,
// daily cross-stream insight). Cards 4–6 come in Phase 2.
//
// All rings + sparklines are featherweight raw SVG (no chart lib).

const _STATE_COLOR = {
  optimal: '#4ADE80',
  good:    '#7FDFDF',
  watch:   '#F5C451',
  alert:   '#F08E7F',
};

// Gradient ring used by the KLS hero. value 0..1 fills the arc; the
// stroke is painted with a gold→green gradient that lights up as the
// arc fills. Soft outer glow via SVG filter on the arc.
function _TodayRing({ value, size = 180, stroke = 14, children }) {
  const r = (size - stroke) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value || 0));
  // animation: render with full circumference at first, then set
  // dashoffset to target — CSS transition handles the fill animation.
  const [draw, setDraw] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDraw(v), 80);
    return () => clearTimeout(t);
  }, [v]);
  const gradId = `today-ring-grad-${size}`;
  const glowId = `today-ring-glow-${size}`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
         style={{display:'block'}}>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"  stopColor="#D4A94A" />
          <stop offset="60%" stopColor="#E9C56F" />
          <stop offset="100%" stopColor="#4ADE80" />
        </linearGradient>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle cx={cx} cy={cy} r={r}
              stroke="rgba(255,255,255,0.06)" strokeWidth={stroke}
              fill="none" />
      <circle cx={cx} cy={cy} r={r}
              stroke={`url(#${gradId})`} strokeWidth={stroke}
              strokeLinecap="round"
              fill="none"
              filter={`url(#${glowId})`}
              strokeDasharray={circ}
              strokeDashoffset={circ * (1 - draw)}
              transform={`rotate(-90 ${cx} ${cy})`}
              style={{transition:'stroke-dashoffset 800ms ease-out'}} />
      {children && (
        <foreignObject x="0" y="0" width={size} height={size}>
          <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center'}}>
            {children}
          </div>
        </foreignObject>
      )}
    </svg>
  );
}

// 7-day KLS sparkline. Featherweight SVG with the latest point
// emphasised. Auto-scales the y-domain to a small band around the values
// so movement is visible.
function _TodaySparkline({ values, height = 36, color = '#D4A94A' }) {
  if (!values || values.length < 2) {
    return <div style={{height, fontSize:11, color:'rgba(255,255,255,0.4)', display:'flex', alignItems:'center', justifyContent:'center'}}>Building 7-day trend…</div>;
  }
  const W = 240, padX = 4, padY = 4;
  const min = Math.min(...values), max = Math.max(...values);
  const span = Math.max(0.4, max - min); // never collapse the y-axis
  const sx = (i) => padX + (i / (values.length - 1)) * (W - padX * 2);
  const sy = (v) => padY + (1 - (v - min) / span) * (height - padY * 2);
  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${sx(i).toFixed(1)} ${sy(v).toFixed(1)}`).join(' ');
  const latestIdx = values.length - 1;
  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{display:'block'}}>
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.7" />
      <circle cx={sx(latestIdx)} cy={sy(values[latestIdx])} r="3" fill={color} />
      <circle cx={sx(latestIdx)} cy={sy(values[latestIdx])} r="6" fill={color} opacity="0.25" />
    </svg>
  );
}

// Short weekday label (Mon/Tue) from an ISO date, for the bio-age chart axis.
function _weekdayShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return iso;
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
}

// "12 May" — for multi-week ranges where weekday names repeat and confuse.
function _dayMonth(iso) {
  if (!iso) return '';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}`;
}

// Tooltip for the bio-age dual-line chart: "54y 1m biological / 55y 4m chronological".
function _BioAgeTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{background:'#0d1626', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 10px', fontSize:12}}>
      <div style={{color:'#C9973A', fontWeight:600}}>{fmtYearsMonths(row.biological_age)} biological</div>
      <div style={{color:'rgba(255,255,255,0.55)'}}>{fmtYearsMonths(row.chronological_age)} chronological</div>
    </div>
  );
}

// Larger gold glowing "Today" dot for the right-most point of the bio-age
// line. Anchored 'end' so the label never clips the right edge.
function _TodayDot({ cx, cy }) {
  if (cx == null || cy == null) return null;
  return (
    <g style={{pointerEvents:'none'}}>
      <circle cx={cx} cy={cy} r={9} fill="#C9973A" opacity={0.20} />
      <circle cx={cx} cy={cy} r={4.5} fill="#C9973A" stroke="#0d1626" strokeWidth={1.5} />
      <text x={cx} y={cy - 12} textAnchor="end" fill="#C9973A" fontSize={10} fontWeight={600}>Today</text>
    </g>
  );
}

// Blood-test event markers on the bio-age trend. Rendered as the custom `dot`
// of a dedicated overlay <Line> bound to the bio-age value at panel dates only
// (null elsewhere), so recharts hands us the exact cx/cy from the real axis
// scales — the pin lands precisely on the line at the test date and tracks
// whatever range the chart shows. This is the robust path: same function-dot
// mechanism as the "today" dot (no ReferenceDot ifOverflow/onClick fragility),
// and because the bio-age axes don't set allowDataOverflow the dots are never
// clipped, so the pin can float above the line. NPL panels get violet + an
// outer ring + "NPL" tag; self-reported/uploaded panels get cyan. Each pin has
// a pulsing halo (reads as interactive), a droplet icon, and a generous
// transparent hit target whose tap opens the blood-panel summary popup
// (stopPropagation so the tap never reaches the underlying card).
const _BLOOD_NPL_COLOR = '#A78BFA';
const _BLOOD_SELF_COLOR = '#5EC8D8';

// A single interactive blood-panel pin at the recharts-supplied (cx, cy) on the
// bio-age line, with a small badge floated just above the line on a stem.
const _BLOOD_PIN_GOLD = '#C9973A';
const _BLOOD_PIN_NAVY = '#112240';
function _BloodMarker({ ev, cx, cy, onTap }) {
  if (cx == null || cy == null) return null;
  const accent = ev.isNPL ? _BLOOD_NPL_COLOR : _BLOOD_SELF_COLOR; // source: cyan self / violet NPL
  const pinR = 9.5;
  const badgeCy = cy - 24; // float the pin above the line
  const tap = (e) => { e.stopPropagation(); if (onTap) onTap(ev); };
  return (
    <g className="bm-marker" onClick={tap} onMouseDown={(e) => e.stopPropagation()} style={{ cursor: 'pointer' }}>
      <title>{`Blood panel${ev.isNPL ? ' · NPL' : ''} — tap to view`}</title>
      {/* stem from the line up to the pin */}
      <line x1={cx} y1={cy} x2={cx} y2={badgeCy + pinR} stroke={accent} strokeWidth={1.6} opacity={0.6} pointerEvents="none" />
      {/* anchor dot sitting on the line (gold body, navy outline) */}
      <circle cx={cx} cy={cy} r={3.4} fill={_BLOOD_PIN_GOLD} stroke={_BLOOD_PIN_NAVY} strokeWidth={1.3} pointerEvents="none" />
      {/* pulsing source-coloured halo — signals "interactive" + self/NPL */}
      <circle className="bm-pulse" cx={cx} cy={badgeCy} r={pinR + 2.5} fill={accent} opacity={0.9} pointerEvents="none" />
      {/* NPL outer ring */}
      {ev.isNPL && <circle cx={cx} cy={badgeCy} r={pinR + 5} fill="none" stroke={accent} strokeWidth={1.5} opacity={0.6} pointerEvents="none" />}
      {/* gold pin body, navy outline — clearly visible against the gold line */}
      <circle cx={cx} cy={badgeCy} r={pinR} fill={_BLOOD_PIN_GOLD} stroke={_BLOOD_PIN_NAVY} strokeWidth={1.8} pointerEvents="none" />
      {/* vial icon (navy) centred in the pin */}
      <g transform={`translate(${cx}, ${badgeCy})`} pointerEvents="none">
        <rect x={-3} y={-5.4} width={6} height={2} rx={1} fill={_BLOOD_PIN_NAVY} />
        <rect x={-2.1} y={-4} width={4.2} height={7.6} rx={1.5} fill={_BLOOD_PIN_NAVY} />
      </g>
      {/* NPL tag */}
      {ev.isNPL && (
        <text x={cx} y={badgeCy + pinR + 9} textAnchor="middle" fontSize={7} fontWeight={700}
          fill={accent} letterSpacing="0.04em" pointerEvents="none">NPL</text>
      )}
      {/* generous transparent hit targets for comfortable tapping */}
      <circle cx={cx} cy={badgeCy} r={18} fill="transparent" style={{ pointerEvents: 'all', cursor: 'pointer' }} />
      <circle cx={cx} cy={cy} r={11} fill="transparent" style={{ pointerEvents: 'all', cursor: 'pointer' }} />
    </g>
  );
}

// Map panel events by test_date for O(1) lookup inside a line's dot callback.
// The marker is rendered from the MAIN bio-age line's dot (exact recharts cx/cy,
// same proven path as the Today dot) — no separate, unreliable overlay <Line>.
function _bloodEventsByDate(events) {
  const m = {};
  (Array.isArray(events) ? events : []).forEach(ev => { if (ev && ev.test_date) m[ev.test_date] = ev; });
  return m;
}

// 4-segment stacked bar for sleep stages. Each segment is its own
// thin block — visual cue, not exact data.
function _SleepStageBar({ stages }) {
  if (!stages) return null;
  const segs = [
    { key: 'deep',  pct: stages.deep_pct  || 0, color: '#4ADE80' },
    { key: 'rem',   pct: stages.rem_pct   || 0, color: '#7FDFDF' },
    { key: 'light', pct: stages.light_pct || 0, color: 'rgba(255,255,255,0.45)' },
    { key: 'awake', pct: stages.awake_pct || 0, color: 'rgba(255,255,255,0.18)' },
  ];
  return (
    <div style={{display:'flex', gap:2, height:6, borderRadius:3, overflow:'hidden', marginTop:8}}>
      {segs.map(s => (
        <div key={s.key} title={`${s.key} ${s.pct}%`} style={{flex: s.pct, background: s.color, minWidth: s.pct > 0 ? 4 : 0}} />
      ))}
    </div>
  );
}

// Thin progress arc used by the steps mini-metric.
function _TodayProgressArc({ pct, size = 38, stroke = 4, color = '#D4A94A' }) {
  const r = (size - stroke) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, (pct || 0) / 100));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block'}}>
      <circle cx={cx} cy={cy} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={stroke} fill="none" />
      <circle cx={cx} cy={cy} r={r}
              stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none"
              strokeDasharray={circ}
              strokeDashoffset={circ * (1 - v)}
              transform={`rotate(-90 ${cx} ${cy})`} />
    </svg>
  );
}

// ── Zone 3 event feed helpers ──────────────────────────────────────────────
const _EVENT_ICONS = { activity: '🏃', sleep: '🌙', blood: '🩸', insight: '✦', medicine: '💊' };

// Relative timestamp label: "08:42" today, "Last night" for sleep, "Yesterday",
// "N days ago", else "2 Jun".
function _eventRelLabel(ts, kind) {
  if (!ts) return '';
  const d = new Date(ts.length === 10 ? ts + 'T00:00:00' : ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startToday - dDay) / 86400000);
  if (kind === 'sleep' && dayDiff <= 1) return 'Last night';
  if (dayDiff === 0) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff > 1 && dayDiff < 7) return `${dayDiff} days ago`;
  return formatDate(d);
}

// _AskKalpaFAB (the floating ✦ diamond, bottom-right) was DELETED for committee #1380.
// It existed because Ask was hidden from the mobile bottom bar, so mobile needed some other
// way in. Ask is now a nav item on every viewport, so the FAB was a duplicate entry point that
// only appeared on small screens. Its `.today-fab` CSS went with it. The drawer it used to open
// (AskKalpaDrawer, below) is unchanged and is now opened from the nav item.

// Persistent "Ask Kalpa" slide-up coach drawer. Mounted once at the App
// shell so its conversation survives page navigation. Streams from
// /chat/stream and prepends a "[User is viewing: X]" hint so replies can be
// context-aware. Closes via the × button, backdrop tap, drag-down on the
// handle, or Escape.
const _ASK_VIEW_LABELS = {
  today: 'Today', longevity: 'Longevity', vitals: 'Fitness',
  genome: 'My Genome', timeline: 'Health Record', medicines: 'Medicines',
  vault: 'Documents', corporate: 'My Organisation',   // localised at the point of use via T()
  compliance: 'Privacy & Data', privacy: 'Privacy & Data', profile: 'Account Settings',
};
const _ASK_SUGGESTED = [
  'How did I sleep last night?',
  "What's my biological age telling me?",
  'Any trends I should watch this week?',
  'Explain my latest blood results',
];
const _ASK_FALLBACK = "Sorry, I couldn't generate a reply this time. Please send that again.";
const _ASK_CONN_LOST = 'Connection lost, tap to retry';
const _ASK_UPLOAD_FAIL = "The photo didn't upload. Check your connection and attach it again.";
// KAL-378 — downscale phone photos to ~1600 px JPEG before upload. A Pixel 8 photo is 2-5 MB; on
// 4G the 1 MB upload alone took 21 s (NPL Caddy, 15 Oct), long enough for the chat stream behind it
// to be dropped. Any failure returns the ORIGINAL file, so this can never block an attach.
async function _downscaleImage(file, maxPx = 1600, quality = 0.85) {
  try {
    if (!file || !/^image\/(jpeg|png|webp)$/i.test(file.type || '')) return file;
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 900 * 1024) { if (bmp.close) bmp.close(); return file; }
    const cv = document.createElement('canvas');
    cv.width = Math.max(1, Math.round(bmp.width * scale)); cv.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.drawImage(bmp, 0, 0, cv.width, cv.height);
    if (bmp.close) bmp.close();
    const blob = await new Promise(res => cv.toBlob(res, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch (e) { return file; }
}
// KAL-377 — when a stream drops, the server usually FINISHES and STORES the reply (NPL 15 Oct: the
// phone's stream died ~29 s in; the reply was stored 74 s after send). Poll the stored thread for the
// assistant message that follows our user message before telling the user anything failed.
async function _fetchStoredReply(convId, userQuery, { tries = 10, gapMs = 6000, onWait } = {}) {
  if (!convId) return null;
  for (let i = 0; i < tries; i++) {
    try {
      const r = await _abortableFetch(`${API_BASE}/conversations/${convId}/messages?limit=8`, { credentials: 'include' }, 15000, 'GET stored reply');
      if (r.ok) {
        const list = await r.json();
        if (Array.isArray(list)) {
          let u = -1;
          for (let k = list.length - 1; k >= 0; k--) { if (list[k].role === 'user' && list[k].content === userQuery) { u = k; break; } }
          if (u >= 0) {
            for (let k = u + 1; k < list.length; k++) {
              if (list[k].role === 'assistant' && (list[k].content || '').trim()) return list[k].content;
            }
          }
        }
      }
    } catch (e) { /* keep polling */ }
    if (onWait) { try { onWait(i + 1); } catch (_) {} }
    await new Promise(res => setTimeout(res, gapMs));
  }
  return null;
}
// KAL-366 — the non-streaming chat fallback. A full reply is a 20-30 s model call, so it gets
// its own 90 s bound; api.post's shared 20 s LOAD_TIMEOUT_MS guaranteed the fallback failed.
const CHAT_FALLBACK_TIMEOUT_MS = 90000;
async function _chatFallbackPost(body) {
  const r = await _abortableFetch(`${API_BASE}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json', ..._TZ_HEADER }, body: JSON.stringify(body), credentials: 'include' }, CHAT_FALLBACK_TIMEOUT_MS, 'POST /chat (fallback)');
  if (!r.ok) throw await _apiError(r);
  return r.json();
}

// Grow a chat textarea from 1 line up to `maxLines` (default 6), then scroll.
// Computed from the element's own line-height/padding so it stays correct
// across the two chat inputs (different font sizes) and after a reset to ''.
function autoGrowTextarea(el, maxLines = 6) {
  if (!el) return;
  el.style.height = 'auto';
  let lh = parseFloat(window.getComputedStyle(el).lineHeight);
  if (!lh || Number.isNaN(lh)) lh = parseFloat(window.getComputedStyle(el).fontSize) * 1.4 || 20;
  const cs = window.getComputedStyle(el);
  const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  const max = lh * maxLines + pad + border;
  el.style.height = Math.min(el.scrollHeight, max) + 'px';
  el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
}

// The user's IANA timezone (e.g. "Asia/Kathmandu" for Nepal pilot users),
// sent with every chat request so the backend can anchor replies to the
// user's real local "today" instead of guessing from the latest data point.
function clientTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
}

// S2.2 — compact relative time for the chat-history list ("just now", "3h", "2d", "5 Jul").
function _relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); } catch (e) { return ''; }
}
function AskKalpaDrawer({ open, onClose, currentView, prefillRef }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [convId, setConvId] = useState(null);
  // RUN6 §1 — Incognito: reads your health context but saves NOTHING new to your memory.
  const [incognito, setIncognito] = useState(false);
  const incognitoRef = useRef(false);
  useEffect(() => { incognitoRef.current = incognito; }, [incognito]);
  // Drag-to-resize was removed: the grip only nudged the panel a mm and couldn't be
  // made reliable in the WebView. The drawer uses its fixed CSS height (80vh mobile /
  // 60vh desktop) and no longer shows a grip that implies resizing. Close = X / backdrop / Escape.
  const taRef = useRef(null);
  const streamingRef = useRef(false);
  // §2 — per-message DOM refs + a guard set so the completion scroll (below) fires
  // exactly ONCE per assistant response and never re-fires on streamed-token
  // re-renders or while the user is scrolling.
  const msgRefs = useRef({});
  const scrolledIdsRef = useRef(new Set());
  const bodyRef = useRef(null);   // S2(this run) — scroll container, to reveal processing on send

  // S2.2 — chat history. Lists this user's PERSISTED conversations (incognito never
  // persists, so it never appears here — enforced server-side in S2.1). Click loads +
  // continues a thread; "New chat" starts fresh; per-chat delete.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const wasStreamingRef = useRef(false);
  // S2.3 — expandable (near-full) chat window; defaults collapsed on each fresh open.
  const [expanded, setExpanded] = useState(false);
  // S2.4 — chat attachments (+): CHAT-SCOPED, self-supplied, walled from the medical record.
  const [pendingAtt, setPendingAtt] = useState([]);
  const [attMenuOpen, setAttMenuOpen] = useState(false);
  const [attBusy, setAttBusy] = useState(false);
  const [attError, setAttError] = useState(null);   // A1 — loud per-file upload error
  const lastSendRef = useRef(null);                   // KAL-378 — last user message + attachments, for retry
  const attBtnRef = useRef(null), attMenuRef = useRef(null);
  useViewportMenu(attMenuOpen && (isMobileBrowser() || isInNativeApp()), attBtnRef, attMenuRef, { prefer: 'above' });
  const fileInputRef = useRef(null), photoInputRef = useRef(null), cameraInputRef = useRef(null);
  const uploadAttachment = useCallback(async (file) => {
    if (!file) return;
    setAttMenuOpen(false); setAttBusy(true);
    try {
      const up = await _downscaleImage(file);
      const fd = new FormData(); fd.append('file', up, up.name || file.name);
      const r = await fetch(`${API_BASE}/chat/attachment`, { method: 'POST', credentials: 'include', body: fd });
      if (r.ok) { const a = await r.json(); setPendingAtt(p => [...p, a]); }
      else {
        // Loud per-file error (magic-byte/size/type rejection) — surface the
        // server's specific message; the batch loop continues to the next file.
        let msg = `HTTP ${r.status}`;
        try { const j = await r.json(); if (j && j.error) msg = j.error; } catch (e2) {}
        setAttError(`${file.name}: ${msg}`);
        console.warn('[chat-attach] upload failed', r.status, msg);
      }
    } catch (e) {
      setAttError(/^image\//i.test(file.type || '') ? _ASK_UPLOAD_FAIL : _ASK_UPLOAD_FAIL.replace('photo', 'file'));
      reportClientError(`chat-attach upload failed: ${e && e.message} (type=${file.type}, bytes=${file.size})`, e && e.stack, 'chat-attach');
      console.warn('[chat-attach] upload error', e);
    }
    finally { setAttBusy(false); }
  }, []);
  // A1 — accept MULTIPLE files in one action (cap 10 per batch; the server also
  // gates each file by magic bytes + ≤10MB). One bad file must not fail the
  // batch: uploadAttachment surfaces a per-file failure and the loop continues.
  const uploadAttachments = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const room = Math.max(0, 10 - pendingAtt.length);
    for (const f of files.slice(0, room)) { await uploadAttachment(f); }
  }, [pendingAtt.length, uploadAttachment]);
  const removePending = useCallback((i) => setPendingAtt(p => p.filter((_, idx) => idx !== i)), []);
  const loadConversations = useCallback(() => {
    api.get('/conversations?limit=50').then(d => setConversations(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  const newChat = useCallback(() => {
    setMessages([]); setConvId(null); setHistoryOpen(false); setPendingAtt([]); setAttMenuOpen(false);
    scrolledIdsRef.current = new Set();
    setTimeout(() => taRef.current?.focus(), 60);
  }, []);
  const loadThread = useCallback((id) => {
    setHistoryOpen(false);
    api.get(`/conversations/${id}/messages?limit=100`).then(rows => {
      const msgs = (Array.isArray(rows) ? rows : []).filter(m => m.role !== 'system')
        .map(m => ({ id: `h-${m.id}`, role: m.role, content: m.content }));
      // Mark loaded assistant messages as already-scrolled so the §2 completion scroll
      // does not yank a freshly-loaded thread.
      msgs.forEach(m => { if (m.role === 'assistant') scrolledIdsRef.current.add(m.id); });
      setMessages(msgs); setConvId(id);
    }).catch(() => {});
  }, []);
  const deleteThread = useCallback((id, e) => {
    if (e) e.stopPropagation();
    fetch(`${API_BASE}/conversations/${id}`, { method: 'DELETE', credentials: 'include' })
      .then(() => { setConversations(cs => cs.filter(c => c.id !== id)); setConvId(cur => (cur === id ? null : cur)); if (convId === id) setMessages([]); })
      .catch(() => {});
  }, [convId]);

  // Back-button parity: the drawer stays mounted (animates via `open`), so the
  // hook is gated on `open` — it owns a single #ask-kalpa history entry only
  // while shown. ALL close paths (X, backdrop, swipe-down, Escape) route through
  // closeModal so hardware/browser back and in-app close take the same path.
  const closeModal = useModalHashRoute('#ask-kalpa', onClose, open);

  // §2 — scroll a newly-COMPLETED assistant response to the top of the view ONCE,
  // then hand scroll control back to the user. The previous effect was keyed on
  // `messages`, which gets a NEW array reference on every streamed token (see the
  // per-token setMessages in send()), so its smooth scroll re-fired on every token
  // and fought the user. This is keyed on the id of the latest completed assistant
  // message instead — a stable string that changes only when a response finishes —
  // and a per-id guard set makes it strictly one scroll per response. No scroll
  // during streaming; no re-scroll as the user reads.
  const lastCompleteAssistantId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && !m.streaming && m.content) return m.id;
    }
    return null;
  })();
  useEffect(() => {
    if (!open || !lastCompleteAssistantId) return;
    if (scrolledIdsRef.current.has(lastCompleteAssistantId)) return;
    scrolledIdsRef.current.add(lastCompleteAssistantId);
    // rAF so the just-rendered markdown has settled its height before we align it.
    requestAnimationFrame(() => {
      const el = msgRefs.current[lastCompleteAssistantId];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [lastCompleteAssistantId, open]);
  useEffect(() => { if (open) setTimeout(() => taRef.current?.focus(), 260); }, [open]);

  // Incognito must default OFF on every fresh open (normal, saved chat). The drawer is a
  // PERSISTENT single mount (rendered unconditionally; close only animates via `open`, never
  // unmounts — see comment above), so its useState(false) runs only once at app load. Without
  // this reset the `incognito` flag stays sticky across close/reopen — and across WebView
  // backgrounding/"reboot" where the SPA is never torn down — which read as "Incognito on by
  // default". Reset on the rising edge of `open` only; while the drawer stays open the user's
  // opt-IN toggle persists for that session. NOT persisted to any store.
  useEffect(() => { if (open) setIncognito(false); }, [open]);
  useEffect(() => { if (open) setExpanded(false); }, [open]);  // S2.3 — default collapsed on open
  useEffect(() => { if (open) { setPendingAtt([]); setAttMenuOpen(false); } }, [open]);  // S2.4 — no stale attachments
  // S2.2 — load the history list on open; refresh it when a response finishes (a new thread
  // may have been created/auto-titled). Never refreshes for an incognito exchange.
  useEffect(() => { if (open) loadConversations(); }, [open, loadConversations]);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && !incognitoRef.current) loadConversations();
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, loadConversations]);

  // Escape closes (only while open). W3: if the attach menu is open, Escape closes
  // the MENU first (not the whole drawer) — pressing Escape to dismiss the attach
  // menu should not eject the user from the chat.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (attMenuOpen) { setAttMenuOpen(false); return; }
      closeModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeModal, attMenuOpen]);

  const send = useCallback(async (textArg, optsArg) => {
    let text = (textArg != null ? textArg : input).trim();
    // S2.4 — an attachment can be sent with or without text.
    let atts = (optsArg && optsArg.atts) || pendingAtt;
    // KAL-378 — a bare "Try again" re-sends the LAST user message WITH its attachment. It used to
    // go as text only, so the model answered a photo question with no photo (15 Oct).
    if (textArg == null && !atts.length && lastSendRef.current && /^\s*(try again|retry|again|resend)[.!]?\s*$/i.test(text)) {
      text = lastSendRef.current.text; atts = lastSendRef.current.atts;
    }
    lastSendRef.current = { text, atts };
    const hasAtt = atts.length > 0;
    if ((!text && !hasAtt) || streamingRef.current) return;
    if (!text && hasAtt) text = 'Please look at what I attached.';
    setInput(''); setPendingAtt([]); setAttMenuOpen(false);
    // Collapse the auto-grown textarea back to a single line after sending.
    if (taRef.current) { taRef.current.style.height = 'auto'; taRef.current.style.overflowY = 'hidden'; }
    const streamId = `a-${Date.now()}`;
    const userImages = hasAtt ? atts.map(a => ({ filename: a.filename, thumb: a.thumb, provenance: a.provenance })) : null;
    setMessages(prev => [...prev,
      { id: `u-${Date.now()}`, role: 'user', content: text, images: userImages },
      { id: streamId, role: 'assistant', content: '', streaming: true },
    ]);
    // S2(this run) — on SEND, scroll the new message + the processing indicator into view
    // so the user sees Kalpa working (they used to stay stuck at the old scroll position
    // when sending inside a loaded thread). This does NOT touch scrolledIdsRef, so the §2
    // completion effect still fires its single scroll-to-TOP-of-response when the answer lands.
    requestAnimationFrame(() => {
      const b = bodyRef.current; if (b) b.scrollTop = b.scrollHeight;
      setTimeout(() => { const b2 = bodyRef.current; if (b2) b2.scrollTop = b2.scrollHeight; }, 80);
    });
    setIsStreaming(true);
    streamingRef.current = true;
    // Usage telemetry (no message text — privacy plane; only that a chat was sent).
    usageAnalytics.track('chat_message_sent', { source: hasAtt ? 'with_attachment' : 'text' });
    const viewLabel = _ASK_VIEW_LABELS[currentView] || 'the app';
    const contextualQuery = `[User is viewing: ${viewLabel}]\n${text}`;
    let streamedText = '', gotText = false, gotDone = false, gotFinal = false, liveConv = convId || null;
    const abortCtl = new AbortController();
    const timer = setTimeout(() => abortCtl.abort(), 60000);
    try {
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: contextualQuery, includeContext: true, conversation_id: convId || null, timezone: clientTimezone(), incognito: incognitoRef.current, attachments: hasAtt ? atts : undefined }),
        credentials: 'include',
        signal: abortCtl.signal,
      });
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt; try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'meta') {
            if (evt.conversation_id) { setConvId(evt.conversation_id); liveConv = evt.conversation_id; }
          } else if (evt.type === 'status') {
            setMessages(prev => prev.map(m => m.id === streamId
              ? { ...m, statusLog: [...(m.statusLog || []), evt.text] } : m));
          } else if (evt.type === 'text') {
            gotText = true;
            streamedText += evt.content;
            const snap = streamedText;
            setMessages(prev => prev.map(m => m.id === streamId ? { ...m, content: snap } : m));
          } else if (evt.type === 'done') {
            gotDone = true;
            const final = (evt.clean_text || streamedText || '').trim();
            if (final) {
              gotFinal = true;
              setMessages(prev => prev.map(m => m.id === streamId ? { ...m, content: final, streaming: false } : m));
            }
            if (evt.conversation_id) { setConvId(evt.conversation_id); liveConv = evt.conversation_id; }
          } else if (evt.type === 'error') {
            throw new Error(`stream error event: ${evt.message || 'unknown'}`);
          }
        }
      }
      if (!gotText && !gotFinal) throw new Error(gotDone ? 'Empty reply (done without text)' : 'Empty SSE stream');
    } catch (err) {
      if (gotText) {
        setMessages(prev => prev.map(m => m.id === streamId ? { ...m, streaming: false } : m));
      } else {
        // KAL-377/378 — the server probably finished: look for the stored reply first. Otherwise say
        // plainly that the connection was lost and offer a retry that keeps the attachment. The real
        // reason goes to /api/client-error. (The old /chat fallback is gone from here: it dropped the
        // attachment and regenerated a second reply on top of the one the server had already stored.)
        reportClientError(`ask-kalpa stream failed: ${err && err.name}: ${err && err.message} (conv=${liveConv}, attachments=${atts.length}, gotDone=${gotDone})`, err && err.stack, 'ask-kalpa');
        const stored = await _fetchStoredReply(liveConv, contextualQuery, {
          onWait: (n) => { if (n === 1) setMessages(prev => prev.map(m => m.id === streamId && !m.content
            ? { ...m, statusLog: [...(m.statusLog || []), 'Connection dropped, checking for your reply…'] } : m)); },
        });
        if (stored) {
          setMessages(prev => prev.map(m => m.id === streamId ? { ...m, content: stored, streaming: false } : m));
        } else {
          setMessages(prev => prev.map(m => m.id === streamId
            ? { ...m, content: _ASK_CONN_LOST, streaming: false, retry: { text, atts } } : m));
        }
      }
    } finally {
      clearTimeout(timer);
      setIsStreaming(false);
      streamingRef.current = false;
    }
  }, [input, convId, currentView, pendingAtt]);

  // Auto-send a prefilled question when an "Ask AI" button opened the drawer.
  useEffect(() => {
    if (open && prefillRef && prefillRef.current) {
      const q = prefillRef.current;
      prefillRef.current = null;
      send(q);
    }
  }, [open, prefillRef, send]);

  return (
    <>
      <div className={`ask-drawer-backdrop${open ? ' open' : ''}`} onClick={closeModal} aria-hidden="true" />
      <div
        className={`ask-drawer${open ? ' open' : ''}${expanded ? ' expanded' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Ask Kalpa"
      >
        <div className="ask-drawer-header">
          <div style={{display:'flex', alignItems:'center', gap:8, minWidth:0}}>
            <button type="button" className={`ask-hist-toggle${historyOpen ? ' on' : ''}`}
              aria-pressed={historyOpen} aria-label="Chat history"
              onClick={() => { setHistoryOpen(v => !v); loadConversations(); }} title="Your chats">☰</button>
            <div className="ask-drawer-title"><span className="ask-drawer-mark" aria-hidden="true">✦</span> Ask Kalpa</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <button type="button" className="ask-expand-btn" onClick={() => setExpanded(v => !v)}
              aria-pressed={expanded} aria-label={expanded ? 'Collapse chat' : 'Expand chat'}
              title={expanded ? 'Collapse' : 'Expand'}>{expanded ? '⤡' : '⤢'}</button>
            <button type="button" className="ask-newchat-btn" onClick={newChat} aria-label="New chat" title="New chat">＋</button>
            <button
              type="button"
              className={`ask-incognito-toggle${incognito ? ' on' : ''}`}
              aria-pressed={incognito}
              onClick={() => setIncognito(v => !v)}
              title={incognito ? 'Incognito is ON — nothing you say is saved to your health memory' : 'Turn on Incognito — Kalpa reads your context but saves nothing new'}
            >
              {incognito ? '🕶 Incognito on' : '🕶 Incognito'}
            </button>
            <button className="ask-drawer-close" onClick={closeModal} aria-label="Close">×</button>
          </div>
        </div>
        {/* S2.2 — collapsible chat-history panel (overlays the body). Incognito chats never appear. */}
        {historyOpen && (
          <div className="ask-history">
            <div className="ask-history-head">
              <span>Your chats</span>
              <button type="button" className="ask-history-new" onClick={newChat}>＋ New chat</button>
            </div>
            <div className="ask-history-list">
              {conversations.length === 0
                ? <div className="ask-history-empty">No saved chats yet.</div>
                : conversations.map(c => (
                  <div key={c.id} className={`ask-history-item${convId === c.id ? ' active' : ''}`}
                       onClick={() => loadThread(c.id)} role="button" tabIndex={0}>
                    <div className="ask-history-item-main">
                      <div className="ask-history-item-title">{c.title || c.first_message || 'Untitled chat'}</div>
                      <div className="ask-history-item-time">{_relTime(c.updated_at)}</div>
                    </div>
                    <button type="button" className="ask-history-del" onClick={(e) => deleteThread(c.id, e)}
                            aria-label="Delete chat" title="Delete">🗑</button>
                  </div>
                ))}
            </div>
          </div>
        )}
        {incognito && (
          <div className="ask-incognito-banner" role="status">
            🕶 Incognito — Kalpa uses what it already knows to answer, but won’t save anything you say to your health memory.
          </div>
        )}
        <div className="ask-drawer-body" ref={bodyRef}>
          {messages.length === 0 ? (
            <div className="ask-drawer-empty">
              <p className="ask-drawer-empty-lead">Ask anything about your health data — sleep, biomarkers, longevity, medicines.</p>
              <div className="ask-drawer-suggests">
                {_ASK_SUGGESTED.map((s, i) => (
                  <button key={i} type="button" className="ask-drawer-suggest" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map(m => (
              <div key={m.id} className={`ask-msg ask-msg-${m.role}`}
                   ref={el => { if (el) msgRefs.current[m.id] = el; else delete msgRefs.current[m.id]; }}>
                <div className="ask-msg-bubble">
                  {m.role === 'assistant'
                    ? (m.streaming && !m.content
                        ? <KalpaStatus log={m.statusLog} />
                        : <KalpaContent content={deIsoDates(m.content)} streaming={m.streaming} />)
                    : m.content}
                  {m.streaming && m.content && <span className="ask-cursor" aria-hidden="true">▋</span>}
                  {m.retry && !m.streaming && !isStreaming && (
                    <button type="button" className="ask-retry-btn"
                      onClick={() => { const r = m.retry; setMessages(prev => prev.map(x => x.id === m.id ? { ...x, retry: null } : x)); send(r.text, { atts: r.atts }); }}>
                      Tap to retry{m.retry.atts && m.retry.atts.length ? ' (photo kept)' : ''}
                    </button>
                  )}
                  {/* S2.4 — chat attachment thumbnails (self-supplied; never a medical record). */}
                  {Array.isArray(m.images) && m.images.length > 0 && (
                    <div className="ask-msg-atts">
                      {m.images.map((im, i) => (
                        <span key={i} className="ask-att-chip" title={im.filename || 'attachment'}>
                          {im.thumb ? <img src={im.thumb} alt="" className="ask-att-thumb" />
                            : <span className="ask-att-thumb ask-att-thumb-icon" aria-hidden="true">📎</span>}
                          <span className="ask-att-meta">
                            <span className="ask-att-name">{im.filename || 'attachment'}</span>
                            <span className="ask-att-kind">{im.mime && im.mime.startsWith('image') ? 'Image' : 'File'}</span>
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* §3 — persistent bio-age disclaimer under an answer that states the age. */}
                {m.role === 'assistant' && !m.streaming && m.content && /biological age|kalpa age|phenoage/i.test(m.content) && (
                  <div className="ask-msg-disclaimer" style={{marginTop:6, fontSize:'0.72em', color:'var(--text-secondary)', opacity:0.85, lineHeight:1.5}}>{BIOAGE_DISCLAIMER}</div>
                )}
                {m.role === 'assistant' && !m.streaming && m.content && (
                  <div style={{marginTop:6}}><AiThumbs surface="ask_kalpa" contextRef={m.id} /></div>
                )}
                {/* KAL-235 placement 5 — Ask Kalpa. CONTEXTUAL, NEVER AN UNPROMPTED INSERT:
                    three conditions must all hold, so this can only ever read as a follow-up
                    to something Kalpa just said.
                      1. it is the LATEST completed answer — so the offer does not litter the
                         scrollback on every past answer the user re-reads;
                      2. that answer actually TALKS about bloods — same content-keyed pattern
                         as the bio-age disclaimer immediately above, which is the established
                         convention on this screen rather than a new one;
                      3. the server still says the panel is missing or stale (the component
                         returns null otherwise, and honours the 30-day suppression).
                    A question about sleep or steps therefore never produces an order prompt. */}
                {m.role === 'assistant' && !m.streaming && m.content
                  && m.id === lastCompleteAssistantId
                  && /biological age|kalpa age|phenoage|blood|panel|marker|albumin|creatinine|glucose|crp|lymphocyte|mcv|rdw|alkaline phosphatase|wbc/i.test(m.content) && (
                  <OrderPanelPrompt placement="ask_kalpa" variant="link" />
                )}
              </div>
            ))
          )}
        </div>
        <form className="ask-drawer-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
          {attError && (
            <div className="ask-att-error" role="alert" style={{fontSize:12, color:'#ef6a6a', padding:'4px 8px', display:'flex', gap:8, alignItems:'center'}}>
              <span style={{flex:1, minWidth:0, overflowWrap:'anywhere'}}>⚠ {attError}</span>
              <button type="button" onClick={() => setAttError(null)} aria-label="Dismiss" style={{background:'none', border:'none', color:'inherit', cursor:'pointer'}}>×</button>
            </div>
          )}
          {/* S2.4 — pending attachment chips (above the input) */}
          {pendingAtt.length > 0 && (
            <div className="ask-pending-atts">
              {pendingAtt.map((a, i) => (
                <span key={i} className="ask-att-chip pending" title={a.filename}>
                  {a.thumb ? <img src={a.thumb} alt="" className="ask-att-thumb" />
                    : <span className="ask-att-thumb ask-att-thumb-icon" aria-hidden="true">📎</span>}
                  <span className="ask-att-meta">
                    <span className="ask-att-name">{a.filename}</span>
                    <span className="ask-att-kind">{a.kind === 'image' ? 'Image' : 'File'}</span>
                  </span>
                  <button type="button" className="ask-att-x" onClick={() => removePending(i)} aria-label="Remove">×</button>
                </span>
              ))}
            </div>
          )}
          {/* Platform-aware "+" (reuses the app-card mobile signal, e8b475f): DESKTOP opens the
              file picker DIRECTLY (camera/photo-library are meaningless on a PC); MOBILE opens a
              compact, clean menu (Take photo / Photo library / Upload file). */}
          <div className="ask-attach-wrap">
            <button type="button" className="ask-attach-btn" disabled={attBusy} ref={attBtnRef}
              aria-label="Add attachment" title="Attach"
              onClick={() => {
                if (attBusy) return;
                // Show the Take-photo/Photo-library/Upload menu on mobile web OR inside the
                // native WebView (bridge, not UA — a WebView with a non-standard UA was
                // silently falling through to the raw file picker and the menu never opened).
                if (isMobileBrowser() || isInNativeApp()) { setAttMenuOpen(v => !v); }
                else { setAttMenuOpen(false); if (fileInputRef.current) fileInputRef.current.click(); }
              }}>{attBusy ? '…' : '＋'}</button>
            {attMenuOpen && (isMobileBrowser() || isInNativeApp()) && (
              <>
                <div className="ask-attach-scrim" onClick={() => setAttMenuOpen(false)} aria-hidden="true" />
                <div className="ask-attach-menu" role="menu" ref={attMenuRef}>
                  {/* GESTURE-NATIVE: each item is a <label> with the real <input type=file>
                      overlaid on it, so the TAP itself activates the input. A native WebView's
                      onShowFileChooser fires only from a genuine user gesture on the input — the
                      previous synthetic inputRef.click() never reached it (no chooser, no perms
                      prompt → dead on native). Web/desktop taps activate the input identically. */}
                  <label className="ask-attach-item" role="menuitem">
                    <span className="ask-attach-ic" aria-hidden="true">📷</span><span className="ask-attach-lbl">Take photo</span>
                    <input type="file" accept="image/*" capture="environment" className="ask-attach-input"
                      onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; setAttMenuOpen(false); uploadAttachments(fs); }} />
                  </label>
                  <label className="ask-attach-item" role="menuitem">
                    <span className="ask-attach-ic" aria-hidden="true">🖼️</span><span className="ask-attach-lbl">Photo library</span>
                    <input type="file" accept="image/*" multiple className="ask-attach-input"
                      onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; setAttMenuOpen(false); uploadAttachments(fs); }} />
                  </label>
                  <label className="ask-attach-item" role="menuitem">
                    <span className="ask-attach-ic" aria-hidden="true">📄</span><span className="ask-attach-lbl">Upload file</span>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple className="ask-attach-input"
                      onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; setAttMenuOpen(false); uploadAttachments(fs); }} />
                  </label>
                </div>
              </>
            )}
          </div>
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            onChange={(e) => { setInput(e.target.value); autoGrowTextarea(e.target); }}
            onKeyDown={(e) => {
              // Enter SENDS (like the Claude chat app); Shift+Enter = newline.
              // IME composition (e.g. Nepali) must not submit mid-compose.
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
                e.preventDefault();
                if ((input.trim() || pendingAtt.length) && !isStreaming) send();
              }
            }}
            placeholder="Ask Kalpa…  (Enter to send, Shift+Enter for a new line)"
          />
          <button type="submit" disabled={(!input.trim() && !pendingAtt.length) || isStreaming} aria-label="Send">➤</button>
          {/* hidden inputs: file / photo (gallery) / camera (mobile capture). Chat-scoped only. */}
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple style={{display:'none'}}
            onChange={(e) => { const fs = e.target.files; e.target.value = ''; uploadAttachments(fs); }} />
          <input ref={photoInputRef} type="file" accept="image/*" multiple style={{display:'none'}}
            onChange={(e) => { const fs = e.target.files; e.target.value = ''; uploadAttachments(fs); }} />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{display:'none'}}
            onChange={(e) => { const fs = e.target.files; e.target.value = ''; uploadAttachments(fs); }} />
        </form>
      </div>
    </>
  );
}

// Reusable ring for the two-loop surfaces (Today hero + Longevity/Fitness anchors).
//  mode='progress' → an accent arc filling toward max (Active Zone Minutes).
//  mode='dial'     → green→amber→red young→old zones with a marker at the value
//                    (miniature Kalpa Age / Longevity dial). Number centred inside.
function _KalpaRing({ size = 68, stroke = 7, mode = 'progress', pct = 0, marker = null, accent = '#7F91DF', title, children }) {
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, pct || 0));
  const seg = circ / 3;
  let mx = null, my = null;
  if (marker != null) {
    const th = (-90 + Math.max(0, Math.min(1, marker)) * 360) * Math.PI / 180;
    mx = c + r * Math.cos(th); my = c + r * Math.sin(th);
  }
  const arc = { transform: 'rotate(-90deg)', transformOrigin: '50% 50%' };
  return (
    <div className="kring" style={{ width: size, height: size }} title={title || undefined}>
      <svg width={size} height={size} className="kring-svg" aria-hidden="true">
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(127,127,140,0.25)" strokeWidth={stroke} />
        {mode === 'dial' ? (
          <>
            <circle cx={c} cy={c} r={r} fill="none" stroke="#5EC86A" strokeWidth={stroke} strokeDasharray={`${seg} ${circ}`} strokeDashoffset={0} style={arc} />
            <circle cx={c} cy={c} r={r} fill="none" stroke="#FFC706" strokeWidth={stroke} strokeDasharray={`${seg} ${circ}`} strokeDashoffset={-seg} style={arc} />
            <circle cx={c} cy={c} r={r} fill="none" stroke="#F0785F" strokeWidth={stroke} strokeDasharray={`${seg} ${circ}`} strokeDashoffset={-2 * seg} style={arc} />
            {marker != null && <circle cx={mx} cy={my} r={stroke * 0.72} fill="#FFFFFF" stroke="#0D156A" strokeWidth={1.5} />}
          </>
        ) : (
          <circle cx={c} cy={c} r={r} fill="none" stroke={accent} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={`${p * circ} ${circ}`} style={arc} />
        )}
      </svg>
      <div className="kring-inner">{children}</div>
    </div>
  );
}

// ══ KAL-142 — TODAY v2 (B4) ═══════════════════════════════════════════════════════════════
// Built to design/"Kalpa Today — B4 · rolling AZM · 12 Aug 2026.html", which is authoritative
// for layout, tokens, bar anatomy and copy.
//
// THE ABSENCE CONTRACT IS THE POINT OF THIS FILE. Every value arrives as a number or null, and
// null is NEVER rendered as 0. Both live cases exist on master-test and must look different:
//   • source_present=true, earned=0   → a MEASURED zero. Draw the bar, show 0.
//   • source_present=false, earned=null → NO source. State the absence in words; draw no bar.
// A 0 is a claim that we measured and found none; rendering an absence as 0 is the same class
// of defect as a fabricated marker value.
const _tv2Hm = (mins) => {
  if (mins == null) return null;
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};
const _tv2Num = (n) => n == null ? null : Math.round(n).toLocaleString('en-GB');
// Clamp a percentage for POSITIONING only. The label always prints the server's own number, so
// a clamped bar can never disagree with the text beside it.
const _tv2Pos = (p) => Math.max(0, Math.min(100, Number(p) || 0));

// An honest absence. Never an empty bar labelled 0%.
function TV2Absent({ children }) {
  return <div className="tv2-absent">{children}</div>;
}

// ONE bar model for steps, sleep and AZM. The whole point is that a user who has learned to
// read the sleep bar can read all three without relearning anything — and that BEATING a
// target is visible. A bar capped at 100% cannot show a win, which is what the first build got
// wrong: it could only ever say how far short you were.
// ══ KAL-142 PHASE 3 — THE UNIVERSAL METRIC DRAWER ═════════════════════════════════════════
// ONE component, TWO entry points: the Today v2 tiles and the Body/Fitness metric rows. The
// whole point is that a metric has exactly one detail view — open steps from Today or from
// Body and you get the same drawer, same default range, same numbers. Two drawers for one
// metric is how a codebase ends up quoting itself two different answers.
//
// It also REPLACES the standalone cycle-report page while today_v2 is on: paging back to a
// closed month IS the report. The old route stays alive for flag-off users, but its nav entry
// is hidden so there are not two ways to reach the same content.
const TV2_METRICS = {
  steps: { label: 'Steps', unit: 'steps', hue: 'steps', defaultSeg: 'MTD',
           fmt: (n) => n == null ? '—' : Math.round(n).toLocaleString('en-GB') },
  sleep: { label: 'Sleep', unit: '', hue: 'sleep', defaultSeg: 'MTD',
           fmt: (n) => n == null ? '—' : _tv2Hm(n) },
  azm:   { label: 'Active Zone Minutes', unit: 'min', hue: 'azm', defaultSeg: '7d', rolling: true,
           fmt: (n) => n == null ? '—' : `${Math.round(n)} min` },
  rhr:   { label: 'Resting heart rate', unit: 'bpm', hue: 'rhr', defaultSeg: '30d', band: true,
           fmt: (n) => n == null ? '—' : `${Math.round(n)} bpm` },
};

function _tv2MonthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}
function _tv2ShiftMonth(key, delta) {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ── KAL-171 — THE RHR BAND CHART ─────────────────────────────────────────────────────────────
// A LINE over the user's own band, not columns. WHY THE FORM IS DIFFERENT FROM STEPS/SLEEP:
// resting heart rate lives in a narrow window (measured, Tim's own 30 days: 64-75 bpm), so
// columns from a zero baseline are thirty near-identical sticks and columns from a truncated
// baseline mis-encode magnitude — bar LENGTH has to mean the value, and a bar starting at 60
// says a 64 is a sixth of a 75. Position encodes a narrow range honestly; length cannot.
// The band itself is this person's OWN 30-day range — never a population range (KAL-101).
// Marks follow the house chart spec: 2px line, one ≥8px end-dot with a 2px surface ring,
// hairline solid rules, the fill a ~10% wash, and labels only on the endpoint and the average
// rather than on all thirty points.
function TV2BandChart({ series, band, avg7, latest, fmt, tapped, setTapped }) {
  const pts = (series || []).filter(r => r && r.value != null);
  const lo = band && band.lo != null ? band.lo : null;
  const hi = band && band.hi != null ? band.hi : null;
  if (pts.length < 2 || lo == null || hi == null) {
    // ABSENCE CONTRACT: state it, never draw a flat line through invented points.
    return (
      <div className="tv2-drawernote">
        {pts.length === 1
          ? `One reading in this window (${fmt(pts[0].value)}) — a trend needs at least two.`
          : 'No resting heart rate recorded in this window.'}
      </div>
    );
  }
  // Domain padded off the band so the extremes are not drawn ON the frame.
  const pad = Math.max(1, (hi - lo) * 0.18);
  const dmin = lo - pad, dmax = hi + pad;
  const y = (v) => 100 - ((v - dmin) / (dmax - dmin)) * 100;      // % from the top
  const x = (i) => pts.length > 1 ? (i / (pts.length - 1)) * 100 : 50;
  const path = pts.map((r, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(r.value).toFixed(2)}`)
    .join(' ');
  const lastIdx = pts.length - 1;
  const tappedIdx = tapped ? pts.findIndex(r => r.date === tapped) : -1;
  return (
    <div className="tv2-bandchart" role="img"
      aria-label={`Resting heart rate over ${pts.length} days, your own range ${Math.round(lo)} to ${Math.round(hi)} bpm, latest ${fmt(latest)}`}>
      {/* the user's own band — a wash, with hairline edges carrying the two figures */}
      <span className="tv2-bandzone" style={{ top: y(hi) + '%', bottom: (100 - y(lo)) + '%' }} />
      <span className="tv2-bandedge" style={{ top: y(hi) + '%' }}><em>{Math.round(hi)}</em></span>
      <span className="tv2-bandedge" style={{ top: y(lo) + '%' }}><em>{Math.round(lo)}</em></span>
      {avg7 != null && (
        <span className="tv2-bandavg" style={{ top: y(avg7) + '%' }}>
          <em>avg {fmt(avg7)}</em>
        </span>
      )}
      <svg className="tv2-bandsvg" viewBox="0 0 100 100" preserveAspectRatio="none"
        aria-hidden="true">
        <path d={path} fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      {/* the end-dot is the only marked point; the rest are carried by the line and the tap */}
      <span className="tv2-banddot" style={{ left: x(lastIdx) + '%', top: y(pts[lastIdx].value) + '%' }} />
      {tappedIdx >= 0 && (
        <span className="tv2-bandtap" style={{ left: x(tappedIdx) + '%' }} />
      )}
      {/* transparent per-day hit columns: a 2px line is not a hit target */}
      <div className="tv2-bandhits">
        {pts.map((r, i) => (
          <button key={r.date} type="button" className="tv2-bandhit"
            onClick={(e) => { e.stopPropagation(); setTapped(tapped === r.date ? null : r.date); }}
            aria-label={`${r.date}: ${fmt(r.value)}`} />
        ))}
      </div>
    </div>
  );
}

function TV2MetricDrawer({ spec, onClose }) {
  const metric = spec.metric || 'steps';
  const cfg = TV2_METRICS[metric] || TV2_METRICS.steps;
  const thisMonth = (spec.month || new Date().toISOString().slice(0, 7));
  const [month, setMonth] = useState(thisMonth);
  const [seg, setSeg] = useState(cfg.defaultSeg);
  const [data, setData] = useState(null);
  const [tapped, setTapped] = useState(null);
  // KAL-349 — a busy server is not a missing month. This fetch used to be a bare `api.get`: one
  // attempt, a 20 s abort, and then "That month isn't available right now." - a PERMANENT
  // sentence for a condition that clears itself in about two minutes. Today's own summary has
  // retried since KAL-322 (`apiGetWithRetry`, 2/5/10 s); the drawer and the report card never
  // adopted it, which is the whole of the reported defect. Same helper here - no second
  // mechanism - plus one automatic cycle after the helper gives up, because the measured window
  // (ingest + cold start on NPL) outlasts the helper's ~100 s.
  const [retrying, setRetrying] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const autoRetries = useRef(0);
  useEffect(() => { autoRetries.current = 0; }, [month]);

  useEffect(() => {
    let alive = true;
    setData(null); setRetrying(0);
    apiGetWithRetry(`/today/v2/metrics?month=${month}`, {
      label: 'tv2metric',
      onRetry: (n) => { if (alive) setRetrying(n); },
    })
      .then(d => { if (alive) { setData(d); setRetrying(0); } })
      .catch(e => { if (alive) setData({ error: true, transient: _isTransient(e) }); });
    return () => { alive = false; };
  }, [month, attempt]);

  // Self-resolving: a transient failure re-asks on its own, up to three times, and says so.
  useEffect(() => {
    if (!data || !data.error || !data.transient || autoRetries.current >= 3) return undefined;
    const t = setTimeout(() => { autoRetries.current += 1; setAttempt(a => a + 1); }, 15000);
    return () => clearTimeout(t);
  }, [data]);

  // KAL-285 — a tap belongs to the month and segment it was made in. It used to survive both,
  // so tapping 27 Aug in the August view and paging to September rendered "Thu 27 Aug · —":
  // a date from outside the window, against fmt(undefined).
  useEffect(() => { setTapped(null); }, [month, seg]);

  // KAL-283 — the SERVER decides which month is current (`is_current_month` is derived from the
  // instance-local today). `thisMonth` is the browser's UTC month, which is the previous month
  // for the first 5h45m of every NPT month — only the pre-load fallback still uses it.
  const isCurrent = (data && !data.error && data.is_current_month != null)
    ? !!data.is_current_month : (month === thisMonth);
  const earliest = (data && data.earliest_month) || null;
  // › is disabled at the current month; ‹ is disabled before the user's earliest DATA month.
  const canPrev = !earliest || _tv2ShiftMonth(month, -1) >= earliest;
  const canNext = !isCurrent;

  // KAL-155 — EVERY return path goes through KalpaDrawer. The loading/error states were bare
  // divs too, so a slow month render also drew unlayered.
  // What this drawer is waiting FOR: a month for the paged metrics, the rolling window itself
  // for AZM and RHR, which have no monthly quantity (KAL-171).
  const waitingFor = (cfg.rolling || cfg.band) ? cfg.label : _tv2MonthLabel(month);
  if (!data) return (
    <KalpaDrawer title={cfg.label} onClose={onClose}>
      <div className="kdrawer-empty">
        {retrying
          ? `Still loading ${waitingFor} — retrying (${retrying} of 3)…`
          : `Loading ${cfg.label.toLowerCase()}…`}
      </div>
    </KalpaDrawer>
  );
  if (data.error) return (
    <KalpaDrawer title={cfg.label} onClose={onClose}>
      <div className="kdrawer-empty">
        {data.transient
          ? <>{waitingFor} is taking longer than usual — this can happen for a minute or two
              right after a sync. {autoRetries.current >= 3 ? 'Tap to try again.' : 'Retrying…'}{' '}</>
          : <>{waitingFor} couldn’t be loaded.{' '}</>}
        <button type="button" className="tv2-retry"
          onClick={() => { autoRetries.current = 0; setAttempt(a => a + 1); }}>Retry now</button>
      </div>
    </KalpaDrawer>
  );

  const block = data[metric] || {};
  const series = block.series || [];
  const target = metric === 'sleep' ? block.target_min : block.target;
  const avg = metric === 'sleep' ? block.avg_complete_min : block.avg_complete;
  // THE CHART'S TARGET LINE IS NOT ALWAYS THE METRIC'S TARGET (KAL-171).
  // AZM's target is a WEEKLY 150 while the chart plots DAILY totals of ~20-50: drawn on this
  // chart it would sit three times above every bar and squash the whole series into the bottom
  // third, because `top` is max(target, values). There is no daily AZM guideline to substitute
  // and inventing one (150/7) would be a fabricated quantity. So the rolling metric's chart
  // carries an AVERAGE line only, and the weekly guideline stays where it belongs — in the
  // summary line above, against the rolling total it actually governs.
  const chartTarget = (cfg.rolling || cfg.band) ? null : target;

  // Range windows. A rolling metric has no month view BY DESIGN — AZM's whole meaning is the
  // trailing window, and slicing it by calendar month would invent a quantity we never compute.
  // KAL-265 — WAS `series.slice(-n)`, and that is the whole of the drawer-vs-card divergence.
  // This series is the CALENDAR MONTH and its last element is TODAY, so slice(-7) selected
  // 18-24 Aug and then `filter(r.complete)` threw the 24th away: SIX bars in a seven-slot
  // window, and an average over six days sitting beside a card averaging seven.
  // Measured 23 Aug: drawer 11,021 (18-23 Aug, n=6) vs card 10,412 (17-23 Aug, n=7).
  // `_windowByDate` drops incomplete days FIRST and then takes N calendar days, so the two
  // surfaces select the same seven dates from different series and cannot disagree again.
  const _drawerToday = data.as_of_date
    || (series.length ? series.filter(r => !r.complete).map(r => r.date)[0] : null)
    || null;
  // KAL-283 — the 7d/30d/90d segments window over the TRAIL, not the month. The month series is
  // bounded to one calendar month, so a window computed from it CLAMPS at the month boundary:
  // measured 2 Sept 2026 (Bimal, NPL), "30d" showed one day, labelled 01 Sep – 01 Sep. The trail
  // is served by the same resolvers and spans the largest segment; the month series remains the
  // source for MTD and closed months, which are month quantities by design.
  const segSource = (isCurrent && seg !== 'MTD' && Array.isArray(block.trail) && block.trail.length)
    ? block.trail : series;
  const windowed = (() => {
    if (!isCurrent || seg === 'MTD') return series.filter(r => r.complete);
    if (!segSource.length) return [];
    const n = seg === '7d' ? 7 : seg === '30d' ? 30 : 90;
    return _windowByDate(segSource, n, _drawerToday);
  })();

  const _segStats = _meanOver(windowed);
  const segAvg = _segStats.mean;
  const top = Math.max(chartTarget || 0, ...windowed.map(r => r.value || 0)) || 1;

  // ⚠ KAL-155 — THE ROOT CAUSE OF ALL FOUR DRAWER BUGS WAS ONE MISSING WRAPPER.
  // Every other drawer body returns <KalpaDrawer>…</KalpaDrawer> (App.js:19981), which supplies
  // the backdrop, the SIZED .kdrawer panel, the z-index layer, the header and the close button.
  // This component returned a BARE <div>, so it rendered unlayered into the host: it inherited
  // whatever width was going (narrow for steps/sleep, full-bleed for azm/rhr), and with no
  // stacking context it sat underneath the floating Feedback button. Four reported symptoms,
  // one omission — fixed once here rather than patched at four call sites.
  return (
    <KalpaDrawer title={cfg.label} onClose={onClose}>
    <div className={`tv2-drawer tv2-hue-${cfg.hue}`}>
      {/* MONTH PAGER — the same control whichever screen opened this, EXCEPT on the two metrics
          that have no monthly quantity at all (KAL-171). AZM is a rolling 7-day window and RHR
          is a rolling 30-day band; both are computed relative to TODAY server-side, so paging
          to July re-fetched the month and then redrew the same trailing window under a "July
          2026" heading, while the closed-month summary printed "—" for an average that does not
          exist for these metrics. A control that changes the label and not the data is worse
          than no control. */}
      {!cfg.rolling && !cfg.band ? (
        <div className="tv2-pager">
          <button type="button" className="tv2-pagerbtn" disabled={!canPrev}
            aria-label="Previous month"
            onClick={() => canPrev && setMonth(_tv2ShiftMonth(month, -1))}>‹</button>
          <span className="tv2-pagerlab">{_tv2MonthLabel(month)}</span>
          <button type="button" className="tv2-pagerbtn" disabled={!canNext}
            aria-label="Next month"
            onClick={() => canNext && setMonth(_tv2ShiftMonth(month, 1))}>›</button>
        </div>
      ) : (
        <div className="tv2-pager">
          <span className="tv2-pagerlab">
            {cfg.band ? `Last ${block.window_days || 30} days` : `Last ${block.window_days || 7} days`}
          </span>
        </div>
      )}

      {isCurrent && !cfg.rolling && !cfg.band && (
        <div className="tv2-segs" role="tablist">
          {['7d', 'MTD', '30d', '90d'].map(s => (
            <button key={s} type="button" role="tab" aria-selected={seg === s}
              className={'tv2-seg' + (seg === s ? ' is-on' : '')}
              onClick={() => setSeg(s)}>{s}</button>
          ))}
        </div>
      )}

      {/* CLOSED MONTH = THE REPORT. This is what replaces the standalone report page. */}
      {!isCurrent && (
        <div className="tv2-closed">
          <div className="tv2-closedlab">Closed month</div>
          <div className="tv2-closedrow">
            <span>Average</span><b>{cfg.fmt(avg)}</b>
          </div>
          {target != null && (
            <div className="tv2-closedrow">
              <span>Target</span><b>{cfg.fmt(target)}</b>
            </div>
          )}
          {target != null && avg != null && (
            <div className="tv2-closedrow">
              <span>Attainment</span>
              <b>{Math.round((avg / target) * 100)}%</b>
            </div>
          )}
          {spec.currentAvg != null && avg != null && (
            <div className="tv2-closedrow">
              <span>vs this month</span>
              <b className={avg > spec.currentAvg ? 'is-up' : 'is-down'}>
                {avg > spec.currentAvg ? '▲' : '▼'} {cfg.fmt(Math.abs(avg - spec.currentAvg))}
                {' '}{avg > spec.currentAvg ? 'higher than this month' : 'lower than this month'}
              </b>
            </div>
          )}
        </div>
      )}

      {/* CHART — daily bars, a SOLID average line labelled with the same number as the card,
          and a DASHED target line. Today is a dashed outline bar and is excluded from the
          average, exactly as the card excludes it.

          ⚠ KAL-171 — AZM AND RHR USED TO STOP HERE, AT A PARAGRAPH. That was a deliberate
          build decision and it was wrong: two of the four metrics in a "universal metric
          drawer" had no chart at all, so the drawer's whole promise failed on half its
          surface. It also explains the "wrong size" report — MEASURED, the panel box is
          IDENTICAL for all four (0,127.5,393.3,722.5 at 393px), so nothing was collapsing;
          a one-line paragraph in a 722px panel simply reads as the wrong size. The prior
          handoff's hypothesis (a note block with no chart height collapses the panel) is
          therefore REFUTED — the panel never resized.

          Both now carry a chart sized exactly like steps'/sleep's, with the numbers that used
          to be the whole content promoted to a summary line above it. The FORM differs by what
          the data is, which is the point:
            * AZM = magnitude per day over the rolling window -> daily columns, same component
              as steps/sleep.
            * RHR = a trend inside the user's own band -> a LINE over a shaded band. Daily bars
              from a zero baseline would be near-identical 64-75 sticks; bars with a truncated
              baseline are a misencoding (bar length must mean magnitude). Position encodes
              this correctly, a bar cannot. */}
      {cfg.band ? (
        <>
          <div className="tv2-drawerstat">
            <span>Your own 30-day range</span>
            <b>{block.band30 && block.band30.lo != null
              ? `${Math.round(block.band30.lo)}–${Math.round(block.band30.hi)} bpm` : '—'}</b>
          </div>
          <div className="tv2-drawerstat">
            <span>7-day average</span><b>{cfg.fmt(block.avg7)}</b>
          </div>
          <TV2BandChart series={series} band={block.band30} avg7={block.avg7}
            latest={block.latest} fmt={cfg.fmt} tapped={tapped} setTapped={setTapped} />
          <div className="tv2-readout">
            {tapped ? (() => {
              const row = series.find(r => r.date === tapped);
              const d = new Date(tapped + 'T00:00:00');
              return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · ${cfg.fmt(row && row.value)}`;
            })() : `Latest ${cfg.fmt(block.latest)} — tap a day for that day’s figure.`}
          </div>
        </>
      ) : cfg.rolling && !block.source_present ? (
        <div className="tv2-drawernote">
          No heart-zone data from your device, so no zone minutes can be derived.
        </div>
      ) : (
        <>
          {cfg.rolling && (
            <>
              <div className="tv2-drawerstat">
                <span>Rolling {block.window_days}-day total</span><b>{cfg.fmt(block.earned)}</b>
              </div>
              <div className="tv2-drawerstat">
                <span>Weekly guideline</span><b>{cfg.fmt(block.target)}</b>
              </div>
              <div className="tv2-drawernote is-tight">
                This window rolls daily — there is no monthly total by design, so the chart
                shows the last {block.window_days} days.
              </div>
            </>
          )}
          <div className="tv2-chart" role="img"
            aria-label={`${cfg.label} by day, average ${cfg.fmt(segAvg)}`}>
            {chartTarget != null && (
              <span className="tv2-chart-target" style={{ bottom: `${(chartTarget / top) * 100}%` }}>
                <i /><em>target {cfg.fmt(chartTarget)}</em>
              </span>
            )}
            {segAvg != null && (
              <span className="tv2-chart-avg" style={{ bottom: `${(segAvg / top) * 100}%` }}>
                <i /><em>avg {cfg.fmt(segAvg)}</em>
              </span>
            )}
            {/* KAL-283 — 90 bars at min-width 3px + 2px gap is 448px: wider than a phone. */}
            <div className={'tv2-chart-bars' + (windowed.length > 45 ? ' is-dense' : '')}>
              {windowed.map(r => (
                <button key={r.date} type="button"
                  className={'tv2-cbar' + (r.complete ? '' : ' is-today')
                    + (r.value == null ? ' is-absent' : '')
                    + (tapped === r.date ? ' is-tapped' : '')}
                  style={{ height: r.value == null ? '2px' : `${Math.max(2, (r.value / top) * 100)}%` }}
                  onClick={(e) => { e.stopPropagation(); setTapped(tapped === r.date ? null : r.date); }}
                  aria-label={r.value == null ? `${r.date}: no reading recorded` : `${r.date}: ${cfg.fmt(r.value)}`} />
              ))}
            </div>
          </div>
          <div className="tv2-readout">
            {tapped ? (() => {
              const row = windowed.find(r => r.date === tapped);
              // KAL-285 — a date the window does not contain must not print at all. Belt to the
              // reset-effect's braces: whatever leaves a stale tap behind, it renders as nothing.
              if (!row) return 'Tap a bar for that day’s figure.';
              const d = new Date(tapped + 'T00:00:00');
              const _day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
              // A day with no reading is an ABSENCE and is said in words — never "· —", which
              // reads as a value that failed to load. A real 0 still prints as a figure.
              if (row.value == null) return `${_day} · no reading recorded`;
              // ⚠ FOUND BY ITEM 1's ASSERTION, not by eye: this printed “53 min min” on AZM
              // and “— steps” on a day with no data. cfg.fmt ALREADY carries the unit for the
              // metrics whose numbers are meaningless without it (“53 min”, “73 bpm”), so the
              // unit is appended only when the formatter did not, and never onto an absence.
              const _v = cfg.fmt(row.value);
              const _u = (cfg.unit && !_v.endsWith(cfg.unit)) ? ' ' + cfg.unit : '';
              return `${_day} · ${_v}${_u}`;
            })() : 'Tap a bar for that day’s figure.'}
          </div>
        </>
      )}

      {/* KAL-266 — WAS `data.complete_days`, a MONTH-LEVEL figure (day_of_month - 1 = 23)
          printed under every segment. Under a 7d selector showing six bars it described
          neither the window nor the bars. It now describes THE SERIES ACTUALLY SHOWN.
          KAL-284 — and it said "N complete days" for a count of days WITH READINGS: on 2 Sept
          Bimal's card read "0 complete days" over a window holding one complete day with no
          sleep reading, and his closed August read "31 days, all complete" over 10 readings.
          Both quantities are now stated: complete days are the calendar, readings are the data. */}
      {/* RHR/AZM series carry only days WITH readings (sparse), so "N of M" would count itself;
          they state the reading count alone. Steps/sleep series are dense per calendar day. */}
      <div className="tv2-drawerfoot">
        {isCurrent
          ? (windowed.length
              ? ((cfg.band || cfg.rolling)
                  ? `Readings on ${_segStats.n} day${_segStats.n === 1 ? '' : 's'} (${formatDate(windowed[0].date)} – ${formatDate(windowed[windowed.length - 1].date)}) — today joins averages at midnight.`
                  : `Readings on ${_segStats.n} of ${windowed.length} complete day${windowed.length === 1 ? '' : 's'}${
                      seg !== 'MTD' ? ` (${formatDate(windowed[0].date)} – ${formatDate(windowed[windowed.length - 1].date)})` : ''
                    } — today joins averages at midnight.`)
              : 'No complete days in this window yet — today joins averages at midnight.')
          : `Readings on ${_segStats.n} of ${data.days_in_month} days.`}
      </div>
    </div>
    </KalpaDrawer>
  );
}

// KAL-334 — THE COVERAGE LABEL. An average is only as honest as its denominator. Bimal's 7 Sep
// report read "Sleep: average daily 10h 34m · 2h 34m over" from ONE recorded night (2 Sep) in a
// six-day-old month. Every bar now states "n of N days recorded" whenever the metric has fewer
// recorded days than the period it is averaged over; when coverage is full nothing extra prints.
// `coverage` = { n: days with a value, of: days in the averaged period }, both from the server
// (`days_with_data`, `complete_days` / `window_days`) — never inferred on the client.
function _tv2Coverage(coverage) {
  if (!coverage || coverage.n == null || coverage.of == null) return null;
  if (coverage.n >= coverage.of) return null;
  return `${coverage.n} of ${coverage.of} day${coverage.of === 1 ? '' : 's'} recorded`;
}
function TV2Bar({ metric, label, sub, value, target, live, liveLabel, fmt, onOpen, absent,
                  liveWhite, liveAsOf, liveIsCurrent = true, coverage = null }) {
  // KAL-260 — when the served value is not today's/last night's, the caption says which day it
  // IS. `liveLabel` ("today" / "last night") is only honest when liveIsCurrent; otherwise the
  // date replaces it. One expression, so the rail, the tooltip and the aria-label cannot
  // disagree about what day they are describing.
  const _liveLabelResolved = (liveIsCurrent || !liveAsOf) ? liveLabel : formatDate(liveAsOf);
  const covText = _tv2Coverage(coverage);
  if (value == null) {
    // KAL-283 — the absent state must still open the drawer. When Bimal's sleep ingestion
    // stalled, the whole month read absent, this branch rendered an inert div, and his entire
    // sleep HISTORY became unreachable from Today: a data gap turned into a dead surface. The
    // absence copy stays exactly as it was — it just becomes the tap target for the history.
    return (
      <>
        <div className="tv2-rowlab"><b>{label}</b><span>{sub}</span></div>
        {onOpen ? (
          <button type="button" className="tv2-absentbtn" onClick={onOpen}
            aria-label={`${label}: ${absent} Open history.`}>
            <TV2Absent>{absent}</TV2Absent>
          </button>
        ) : <TV2Absent>{absent}</TV2Absent>}
      </>
    );
  }
  // DESIGN SYSTEM v1 §0.5 — WHOLE BAR = TARGET. Track empty, fill = value/target.
  // The hatch is GONE: it was banked/expected marking carried over from the cycle report and is
  // meaningless once the bar itself is the target.
  // Over target the bar rescales so the excess is visible — a bar capped at 100% cannot show a
  // win — with a target reference line at the target's true proportional position.
  const over = target != null && value > target;
  const top = target != null ? Math.max(target, value) : value;
  const pct = (n) => top ? Math.max(0, Math.min(100, (n / top) * 100)) : 0;
  const fillPct = pct(value);
  const targetPct = target != null ? pct(target) : null;
  const delta = target != null ? Math.abs(value - target) : null;
  // ⚠ THE RAIL NEEDS A RENDERING FLOOR AT THE BAR'S ENDS — AND THIS IS ALMOST CERTAINLY WHAT
  //   TIM SAW AS “no white line, no dot” (14 Aug). `.tv2-bar` is `overflow:hidden`, and the dot
  //   is centred on its x with translateX(-50%), so at pct(live)=0 HALF THE GLYPH IS OUTSIDE
  //   THE BAR and the rest sits under the 12px rounded corner. MEASURED with real data the
  //   moment AZM's today_live came back as 0: line at x=31.7 against a bar starting at 32.7,
  //   dot at x=27.2 — rendered, asserted present, and invisible to a human. A caption with no
  //   visible rail is exactly the reported symptom.
  //   So the rail is clamped to keep the WHOLE glyph inside the bar. This is a rendering
  //   floor, NOT a change to the value: 2.5% of a ~320px bar is ~8px, the tooltip and the
  //   aria-label still carry the true figure, and the caption still states it in words. The
  //   alternative — letting a real measured zero render invisibly — is the worse trade.
  //   All three parts read this ONE value, so they cannot drift apart (the audit asserts
  //   their x's agree to within 1.5px).
  const RAIL_INSET = 2.5;                       // % of bar width: half the dot plus its ring
  const railPct = live == null ? null
    : Math.min(100 - RAIL_INSET, Math.max(RAIL_INSET, pct(live)));
  return (
    <>
      <div className="tv2-rowlab">
        <b>{label}</b>
        <span>
          {sub}{target != null ? ` · target ${fmt(target)}` : ''}
          {/* KAL-334 — the denominator, disclosed beside the figure it qualifies. */}
          {covText && <span className="tv2-cov" data-testid={`tv2-cov-${metric}`}> · {covText}</span>}
        </span>
      </div>
      <button type="button" className={`tv2-bar tv2-${metric}${over ? ' is-over' : ''}`}
        onClick={onOpen}
        aria-label={`${label}, ${fmt(value)}${target != null ? ` against a target of ${fmt(target)}` : ''}${covText ? `, ${covText}` : ''}${live != null ? `, ${_liveLabelResolved} ${fmt(live)}` : ''}${over ? `, ${fmt(delta)} above target` : delta != null ? `, ${fmt(delta)} short` : ''}`}>
        <span className="tv2-fill" style={{ width: fillPct + '%' }}>
          <span className="tv2-inbar">{fmt(value)}</span>
        </span>
        {/* ── THE DELTA LABEL: ONE TREATMENT, EVERY BAR, EVERY STATE (item 2, 14 Aug) ───
            RIGHT-JUSTIFIED TO THE BAR'S OWN RIGHT EDGE. Not to the fill's edge, not to the
            unfilled track's edge — the BAR's. Everything that made this conditional is gone:
            the `is-tight` branch, the inline left/width geometry, the segment-coloured halo.
            Each of those variants put the label's right edge somewhere different, so the label
            MOVED as the data moved — which is the defect this item names.
            HISTORY, so no variant gets reintroduced: (1) inside the FILL it was swallowed
            (KAL-144); (2) right-justified within its SEGMENT its right edge sat at
            fillPct−11px, not at the bar's edge; (3) FLIPPING it to the far end of the segment
            collided with the in-bar value (sleep printed “6h 17m” over “1h 43m short”); (4) a
            halo keyed to the segment's colour needed a width estimate to know WHICH colour, and
            the estimate was wrong by ~11px.
            WHAT MAKES IT LEGIBLE NOW IS AN OPAQUE CHIP (CSS), not a guess: the label sits over
            whatever is behind it — track, fill, or the rail crossing it — and “is it readable”
            stops depending on the data. */}
        {delta != null && !over && (
          <span className="tv2-deltain">
            <span className="tv2-deltatxt">▼ {fmt(delta)} short</span>
          </span>
        )}
        {over && targetPct != null && (
          <>
            <span className="tv2-excess" style={{ left: targetPct + '%', right: 0 }}>
              <span className="tv2-deltatxt is-over">▲ {fmt(delta)} over</span>
            </span>
            <span className="tv2-targetline" style={{ left: targetPct + '%' }} />
            {/* Edge-anchored for the same reason as the live caption: over target the line sits
                near the bar's right edge (measured 96.8% on AZM at 155/150) and a centred label
                was clipped to "TARGE" by the bar itself. */}
            <span className={'tv2-targetlab' + (targetPct >= 88 ? ' is-endanchor'
                              : targetPct <= 12 ? ' is-startanchor' : '')}
              style={{ left: targetPct + '%' }}>target</span>
          </>
        )}
        {/* §0.3 — THE LIVE POINT IS A RAIL, NOT A FLOATING DOT (KAL-170, 13 Aug).
            THREE PARTS, ONE OBJECT, ONE X-POSITION:
              1. a full-height vertical line across the bar at today's / last-night's value
              2. a hollow dot capping the TOP of that line
              3. the caption directly beneath, centred on the SAME x (rendered below, in
                 .tv2-liverow — it cannot be inside the <button> because it sits outside the
                 bar's box, but it shares the identical `left`)
            WHY IT CHANGED. The dot alone — even correctly positioned at pct(live) — read as an
            unrelated speck: measured 13 Aug, the dot's centre and the caption's centre were
            already identical to 0.1px on all three bars (steps/sleep/AZM), and it still looked
            like "a dot and a separate caption" because nothing joined them. The line is what
            makes the three parts one glyph. This is the third attempt at this construction;
            the first two moved the dot without ever drawing the connector. */}
        {live != null && (
          <>
            <span className="tv2-liveline" style={{ left: railPct + '%' }} aria-hidden="true" />
            <span className="tv2-livedot" style={{ left: railPct + '%' }}
              title={`${_liveLabelResolved} ${fmt(live)}`} />
          </>
        )}
      </button>
      {/* A single inline tag under the dot — no new row: it shares the row the bar already
          reserves for the target label. */}
      {/* ⚠ WHY THE STEPS LABEL CAN BE ABSENT — and why that is not a missing feature.
          `live` is today's real figure. When the phone has not synced today yet it is null
          (measured 13 Aug: the steps series ended 12 Aug while sleep had last night's value —
          which is exactly why the sleep label showed and steps' did not). Rendering nothing
          made a DATA state look like a BUILD state. The absence contract says never invent a
          0; it does not say render silence. So the row now states the absence in words. */}
      {live == null && value != null && (
        <div className="tv2-liverow">
          <span className="tv2-livetag is-absent">
            {metric === 'steps' ? 'no steps synced yet today'
              : metric === 'sleep' ? 'no sleep recorded last night'
              : 'nothing recorded today'}
          </span>
        </div>
      )}
      {/* KAL-260 — THE ABSENCE COPY ABOVE IS NOW THE NARROW CASE, and that is the whole fix.
          It used to fire whenever `live` was null, and `live` was null whenever the exact
          instance-local date had no row — which on NPL is every night from ~18:15Z. So the bar
          announced "no steps synced yet today" while the same database held 10,287 steps for
          the day that had just ended, and the Fitness page showed them.
          Now the resolver hands us a value plus the day it belongs to. When that day is not
          today we draw the marker AND NAME THE DAY. What we must never do is either of the two
          things this codebase has already done: show nothing, or quietly print yesterday's
          number under a caption that says "today". */}
      {live != null && (() => {
        // THE CAPTION HANGS OFF THE RAIL — and it must not leave the card doing it.
        // MEASURED on the deployed build, both widths: centred on a rail at 93.3%, "9,334 steps
        // today" ran to x=370.6 against a card edge of 369.3 at 393px, and 339.5 against 336.0
        // at 360px. Overflowing by 1-4px happens to be invisible on these two phones and would
        // clip on a narrower one — the kind of thing that passes a screenshot and fails on a
        // real device.
        // So near the ends the caption is anchored BY ITS EDGE to the rail instead of by its
        // centre, and the connecting stub moves to that same edge. It is still one object hung
        // off one x — the anchor point moves from the middle of the caption to its corner.
        // The 15/85 thresholds come from the widest caption actually rendered (78.5px, i.e.
        // ~27% of the 287px bar at 360px, so ~14% either side of centre) plus slack.
        const lp = railPct;                     // the SAME clamped x as the line and the dot
        const anchor = lp >= 85 ? ' is-endanchor' : lp <= 15 ? ' is-startanchor' : '';
        return (
          <div className="tv2-liverow">
            {/* PHASE 3 — the live label is WHITE (primary tier) on steps and sleep: it is a real
                measured figure for today/last night, not meta. AZM and RHR keep the dim tier —
                not requested this round. Steps reads "{N} steps today"; sleep keeps its existing
                "last night {X}" wording, promoted in colour only, position unchanged. */}
            <span className={'tv2-livetag' + (liveWhite ? ' is-bright' : '') + anchor}
              style={{ left: lp + '%' }}>
              {/* KAL-260 — the steps branch hard-coded the word "today". Caught by the
                  master-test render, NOT by review: the aria-label correctly read
                  "23 Aug 2026 10,287" while the visible caption beside it still said
                  "10,287 steps today" for a 23 Aug figure on 24 Aug. Exactly the class of
                  contradiction this item exists to remove, reintroduced one line away from
                  its own fix. The word "today" is now conditional on it BEING today. */}
              {metric === 'steps'
                ? (liveIsCurrent || !liveAsOf
                    ? `${fmt(live)} steps today`
                    : `${fmt(live)} steps on ${formatDate(liveAsOf)}`)
                : `${_liveLabelResolved} ${fmt(live)}`}
            </span>
          </div>
        );
      })()}
    </>
  );
}

// ══ KAL-287 — THE STALLED-STREAM CARD ════════════════════════════════════════════════════════
// Shown only when the server has classified a stream as STALLED (this metric silent ≥5 days
// while a sibling still arrives — the device is worn, one pipe is broken) AND, for the steps
// list, identified the writing app from Health Connect's own dataOrigin metadata. A user whose
// whole device went quiet gets NO card: we cannot distinguish "not worn" from "broken" there,
// and guessing at them is how generic "check your settings" nags get ignored.
/* KAL-297 — the staff-side of the Health Connect diagnostic.
   One cell per user: request a read, then show what came back. The verdict is the whole
   product — "health-connect-empty" means the phone's own store has no such night either, so
   the relay app never wrote it and nothing on our side could have helped; "ingest-gap" means
   the night IS on the device and missing here, which is ours. Before this, distinguishing
   those two required a screenshot from a pilot user in Nepal. */
function HcDiagnosticCell({ profile }) {
  const [state, setState] = useState('idle');   // idle | asking | pending | loaded | error
  const [run, setRun] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/admin/hc-diagnostic/${encodeURIComponent(profile)}`,
        { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const latest = (j.runs || [])[0] || null;
      setRun(latest);
      setState(latest ? (latest.state === 'requested' ? 'pending' : 'loaded') : 'idle');
    } catch (e) { setErr(String(e.message || e)); setState('error'); }
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const request = async () => {
    setState('asking'); setErr(null);
    try {
      const r = await fetch(`${API_BASE}/admin/hc-diagnostic/request`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState('pending');
      await load();
    } catch (e) { setErr(String(e.message || e)); setState('error'); }
  };

  const cmp = run && run.comparison;
  return (
    <td className="chealth-dim">
      <button type="button" className="tv2-ch-toggle" onClick={request}
              disabled={state === 'asking'}>
        {state === 'asking' ? 'Asking…' : state === 'pending' ? 'Re-ask' : 'Ask phone'}
      </button>
      {state === 'pending' && (
        <span className="chealth-days">waiting for next sync</span>
      )}
      {run && run.state === 'failed' && (
        <span className="chealth-badge">FAILED</span>
      )}
      {cmp && (
        <span className="chealth-days">
          {cmp.verdict === 'health-connect-empty'
            ? 'device has no sleep either'
            : cmp.verdict === 'ingest-gap'
              ? `${cmp.on_device_but_not_here.length} night(s) on device, not here`
              : `in sync (${cmp.device_nights} nights)`}
          {run.delivered_at ? ` · ${formatDate(String(run.delivered_at).slice(0, 10))}` : ''}
        </span>
      )}
      {err && <span className="um-err-inline">{err}</span>}
    </td>
  );
}

/* KAL-296 — "Refresh from device".
   The remediation we shipped for Bimal's stalled sleep was a sentence asking him to go and open
   the Zepp app himself. This does it for him. The LABEL IS DELIBERATELY GENERIC: the same button
   opens Zepp, Fitbit, Samsung Health or Health Connect depending on what the server has actually
   seen writing that user's data, and on iOS it re-reads HealthKit — naming a vendor in the
   button would be wrong for most users and unverifiable for the rest.
   Native-only: the launch needs PackageManager, which a browser tab does not have. On the web
   the button is not rendered at all rather than rendered and inert. */
function RefreshFromDeviceButton() {
  const [state, setState] = useState('idle');   // idle | working | done | error
  const [detail, setDetail] = useState(null);
  const isNative = typeof window !== 'undefined'
    && !!(window.ReactNativeWebView && window.ReactNativeWebView.postMessage);

  useEffect(() => {
    if (!isNative) return undefined;
    const prev = window.__kalpaNativeEvent;
    window.__kalpaNativeEvent = (msg) => {
      try { if (typeof prev === 'function') prev(msg); } catch (e) { /* chain, never swallow ours */ }
      if (!msg || msg.type !== 'refresh_from_device_result') return;
      if (msg.ok) {
        setState('done');
        setDetail(msg.awaiting_return
          ? `Opening ${msg.app_label || 'your device app'}. Come back when it has finished syncing — Kalpa will pick the new data up automatically.`
          : 'Re-reading your device now. New readings appear as soon as they arrive.');
      } else {
        // Say what actually failed. A generic "try again" is what made the original
        // stall invisible for six days.
        setState('error');
        setDetail(msg.error || 'Could not start the refresh.');
      }
    };
    return () => { window.__kalpaNativeEvent = prev; };
  }, [isNative]);

  if (!isNative) return null;

  const go = () => {
    setState('working');
    setDetail(null);
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'refresh_from_device' }));
    } catch (e) {
      setState('error');
      setDetail(String((e && e.message) || e));
    }
    // The native side always answers; this only covers a wrapper too old to know the message.
    setTimeout(() => setState(s => (s === 'working' ? 'error' : s)), 15000);
  };

  return (
    <div className="profile-section">
      <div className="profile-section-header"><h3>Device data</h3></div>
      <p style={_profileIntroStyle}>
        If a reading is missing, this asks your watch’s own app to hand its latest data to your
        phone, then reads it straight away.
      </p>
      <button className="profile-edit-btn" onClick={go} disabled={state === 'working'}>
        {state === 'working' ? 'Refreshing…' : 'Refresh from device'}
      </button>
      {detail && (
        <div className={state === 'error' ? 'um-err-inline' : 'chealth-dim'}
             style={{ marginTop: 10 }}>
          {state === 'error' ? `Couldn’t refresh: ${detail}` : detail}
        </div>
      )}
    </div>
  );
}

function ConnectionHealthCard({ data }) {
  const [open, setOpen] = useState(true);
  if (!data || !Array.isArray(data.stalled) || !data.stalled.length) return null;
  return (
    <div className="tv2-connhealth" role="status">
      <div className="tv2-ch-head">
        <b>Some of your data has stopped arriving</b>
        <button type="button" className="tv2-ch-toggle" aria-expanded={open}
          onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && data.stalled.map(s => (
        <div key={s.metric} className="tv2-ch-item">
          <div className="tv2-ch-line">
            <b>{s.label}</b> last reached Kalpa on {formatDate(s.last_data_day)} — your
            device’s other data is still arriving, so the connection itself works.
          </div>
          {s.signature === 'zepp_sleep_relay' && (
            <div className="tv2-ch-app">
              Zepp hasn’t passed your sleep to the phone since {formatDate(s.last_data_day)} —
              your heart rate is still arriving, so the watch is fine. Tap <b>Refresh from
              device</b> (Profile → Device data): it opens Zepp for you and Kalpa reads the night
              when you come back.
            </div>
          )}
          {s.investigating ? (
            <div className="tv2-ch-invest">
              We’re investigating why this one feed has stopped. Nothing is needed from
              you yet — your other data is unaffected.
            </div>
          ) : (
            <>
              <div className="tv2-ch-app">
                The {s.label.toLowerCase()} feed from <b>{s.app}</b> has stopped. To restart it:
              </div>
              <ol className="tv2-ch-steps">
                {s.steps.map((t, i) => <li key={i}>{t}</li>)}
              </ol>
              {s.source && <div className="tv2-ch-src">Steps per {s.source}</div>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function TodayV2({ onNav, onAskAI, onOpenDrawer }) {
  const [m, setM] = useState(null);
  const [insight, setInsight] = useState(undefined);   // undefined = loading, null = none
  const [err, setErr] = useState(false);
  // A7 — `attempt` is the retry lever: bumping it re-runs the effect. Without it the error
  // state was a dead end and the only way out was a full page reload.
  const [attempt, setAttempt] = useState(0);
  // KAL-333 — THE REPORT CARD PAGES THROUGH MONTHS. `reportMonth` null = the live current month
  // (rendered from `m`, exactly as before). A past month fetches the SAME endpoint with
  // `?month=` — the server's closed-month semantics (complete_days = days_in_month, averages over
  // the days that carry data, *_live removed) are what "read-only with the same cumulative-
  // average logic" means; nothing is recomputed on the client. The drawer already paged this
  // way (TV2MetricDrawer); the card now walks the same months with the same bounds.
  const [reportMonth, setReportMonth] = useState(null);
  const [rm, setRm] = useState(null);           // metrics for reportMonth; null while loading
  const [rmErr, setRmErr] = useState(false);
  // KAL-349 — the same class as the drawer's (TV2MetricDrawer): one attempt, a 20 s abort, and a
  // sentence that reads permanent. Two faults fixed together: this now retries through
  // `apiGetWithRetry` and distinguishes a TRANSIENT failure from a real one, and its Retry
  // button works at all — `setReportMonth(prev => prev)` wrote the state value it already held,
  // so React bailed out and the effect (dep: [reportMonth]) never re-ran. The button was dead.
  const [rmAttempt, setRmAttempt] = useState(0);
  const [rmRetrying, setRmRetrying] = useState(0);
  const rmAuto = useRef(0);
  useEffect(() => { rmAuto.current = 0; }, [reportMonth]);
  useEffect(() => {
    if (!reportMonth) { setRm(null); setRmErr(false); return undefined; }
    let alive = true;
    setRm(null); setRmErr(false); setRmRetrying(0);
    apiGetWithRetry(`/today/v2/metrics?month=${reportMonth}`, {
      label: 'today-report-month',
      onRetry: (n) => { if (alive) setRmRetrying(n); },
    })
      .then(d => { if (alive) { setRm(d); setRmRetrying(0); } })
      .catch(e => {
        if (!alive) return;
        console.error(`[today] report month ${reportMonth} failed:`, e);
        setRmErr(_isTransient(e) ? 'transient' : true);
      });
    return () => { alive = false; };
  }, [reportMonth, rmAttempt]);
  // Self-resolving, like the drawer: three automatic cycles, 15 s apart, then it waits for a tap.
  useEffect(() => {
    if (rmErr !== 'transient' || rmAuto.current >= 3) return undefined;
    const t = setTimeout(() => { rmAuto.current += 1; setRmAttempt(a => a + 1); }, 15000);
    return () => clearTimeout(t);
  }, [rmErr]);
  // KAL-287 — stalled-stream card. Separate, non-blocking fetch: the card is an add-on and
  // must never delay the main screen. TodayV2 mounts only after featuresReady (KAL-251), so
  // the flag read here is hydrated, never the pre-hydration default.
  // KAL-287 — stalled-stream card. KAL-322: DEFERRED off the first-content path. It used to
  // fire with the metrics request; /api/stream-health/self runs profile_health (1.1 s warm for a
  // 1.1M-row profile, 9–20 s under contention on 6 Sep 2026) and shared the worker's threads with
  // the calls that paint the screen. Now it waits until content is on screen, and a failure only
  // means the card does not render — the admin view is the loud surface.
  const [streamHealth, setStreamHealth] = useState(null);
  const [contentReadyAt, setContentReadyAt] = useState(null);
  useEffect(() => {
    if (!FEATURES_GLOBAL.connection_health || !contentReadyAt) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      api.get('/stream-health/self')
        .then(d => { if (alive) setStreamHealth(d); })
        .catch(() => {});
    }, 2500);
    return () => { alive = false; clearTimeout(t); };
  }, [contentReadyAt]);

  // KAL-322 — TODAY PAINTS FROM DATA. The last-known summary for THIS user is read from
  // IndexedDB and rendered at once with an honest "as of {time}" chip, then replaced in place
  // when the network answers. First-ever loads render the real card frames with inline
  // "Loading…" text, never a full-page placeholder. The fetch retries (2 s, 5 s, 10 s) and says
  // "Still loading, retrying" instead of a 20 s dead end; every timeout is reported server-side.
  const [asOf, setAsOf] = useState(null);          // ISO time of the cached summary being shown
  const [updating, setUpdating] = useState(false);
  const [retrying, setRetrying] = useState(0);
  const _cacheKey = `today_v2:${(typeof window !== 'undefined' && window.__kalpaProfileKey) || 'anon'}`;
  useEffect(() => {
    let alive = true;
    _kv.get(_cacheKey).then(hit => {
      if (!alive || !hit || !hit.m) return;
      setM(prev => prev || hit.m);
      setInsight(prev => (prev === undefined ? (hit.m.insight && hit.m.insight.id ? hit.m.insight : null) : prev));
      setAsOf(prev => prev || hit.at || null);
    }).catch(() => {});
    return () => { alive = false; };
  }, [_cacheKey]);

  useEffect(() => {
    let alive = true;
    const t0 = (typeof performance !== 'undefined') ? performance.now() : Date.now();
    setErr(false); setRetrying(0); setUpdating(true);
    _loadTrace.reset();
    apiGetWithRetry('/today/v2/metrics', { label: 'today', onRetry: (n) => { if (alive) setRetrying(n); } })
      .then(mm => {
        if (!alive) return;
        if (!mm) { setErr(true); setUpdating(false); return; }
        setM(mm); setAsOf(null); setUpdating(false); setRetrying(0);
        setInsight(mm.insight && mm.insight.id ? mm.insight : null);
        const now = (typeof performance !== 'undefined') ? performance.now() : Date.now();
        setContentReadyAt(now);
        _kv.set(_cacheKey, { m: mm, at: new Date().toISOString() }).catch(() => {});
        sendLoadBeacon({ screen: 'today', content_ready_ms: Math.round(now), from_cache: false,
                         retries: _loadTrace.retries, timeouts: _loadTrace.timeouts });
      })
      .catch(e => {
        if (!alive) return;
        setUpdating(false);
        // With a cached summary on screen the failure is a chip, not a wall.
        setM(prev => { if (!prev) setErr(e && e.isTimeout ? 'timeout' : true); return prev; });
        try { reportClientError('today/v2/metrics ' + ((e && e.message) || 'failed') + ` after ${_loadTrace.retries} retries`, null, 'today'); } catch (_) {}
      });
    return () => { alive = false; };
  }, [attempt]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (err && !m) return (
    <div className="tv2-wrap">
      <UpdateCard placement="today" />
      <div className="tv2-err">
        {err === 'timeout'
          ? 'Today could not be loaded after several attempts.'
          : 'Today isn’t available right now.'}
        <button type="button" className="tv2-retry" onClick={() => setAttempt(a => a + 1)}>
          Retry
        </button>
      </div>
    </div>
  );
  // KAL-322 — no skeleton. The real frames, with inline loading text per card. `.tv2-skel`
  // (the featureless box Tim photographed on 14 Aug, KAL-154's successor) is gone.
  if (!m) return (
    <div className="tv2-wrap">
      <UpdateCard placement="today" />
      <TodayV2Frames retrying={retrying} />
    </div>
  );

  const st = m.steps || {}, sl = m.sleep || {}, rh = m.rhr || {}, az = m.azm || {};
  const stamp = (() => {
    const d = new Date();
    return `${d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()} ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()}`;
  })();
  const monthName = (() => {
    const [y, mo] = (m.month || '').split('-').map(Number);
    return y ? new Date(y, mo - 1, 1).toLocaleDateString('en-GB', { month: 'long' }) : '';
  })();

  // KAL-142 Phase 3 (v1) — CLICKABILITY BY REUSE, NOT BY A PARALLEL DRAWER.
  // The app already has a drawer host with bodies for every one of these metrics
  // (DrawerHost, App.js:21457). Building a second drawer stack for Today v2 would give the
  // same metric two different detail views — the divergence this codebase keeps paying for.
  // Each tile therefore maps onto the EXISTING spec its metric already has.
  // KAL-333 — a bar on a paged month opens the drawer ON THAT MONTH (the drawer's own pager
  // then continues from there), so the two surfaces never show two months at once.
  const openMetric = (key, monthKey) => {
    if (!onOpenDrawer) return;
    if (['steps', 'sleep', 'azm', 'rhr'].indexOf(key) > -1) {
      return onOpenDrawer({
        kind: 'tv2metric', metric: key, month: monthKey || m.month, title: TV2_METRICS[key].label,
        currentAvg: key === 'sleep' ? sl.avg_complete_min
                  : key === 'steps' ? st.avg_complete : null,
      });
    }
    if (key === 'age')    return onNav && onNav('longevity');
    if (key === 'insight' && insight) {
      // Log the tap, then hand the seed to Ask Kalpa. Fire-and-forget: the drawer must open
      // whether or not the log write succeeds.
      api.post(`/insight/${insight.id}/event`, { event: 'tapped' }).catch(() => {});
      return onOpenDrawer({
        kind: 'insight', title: 'Today’s insight', text: insight.text,
        dataSource: (insight.reads && insight.reads[0]) || null,
        askSeed: insight.seed_prompt || insight.text,
      });
    }
  };

  return (
    <div className="tv2-wrap">
      <div className="tv2-appbar">
        <span className="tv2-who">Today</span>
        <span className="tv2-stamp">
          {stamp}
          {/* KAL-322 — honesty chip: this is the last-known summary until the network answers. */}
          {asOf && <span className="tv2-asof" title={asOf}>as of {_fmtAsOf(asOf)}{updating ? ' · updating…' : ''}</span>}
          {!asOf && updating && <span className="tv2-asof">updating…</span>}
        </span>
      </div>
      {/* KAL-320 — the version-aware update card, above the fold, only when state is not
          up_to_date; a blocked shell gets the modal instead. */}
      <UpdateCard placement="today" />

      {/* ── INSIGHT ─────────────────────────────────────────────────────────────────────── */}
      {insight === undefined ? null : insight ? (
        <button type="button" className="tv2-insight" onClick={() => openMetric('insight')}>
          {/* B4 — the tier chip moves up here, onto the row that was already a space-between
              flex with room to spare, which frees the row below to carry two items. */}
          <div className="tv2-ik">
            <span className="tv2-new">● New today 1 of 1 · rotates daily</span>
            {insight.tier && <span className="tv2-tier">{insight.tier}</span>}
          </div>
          <div className="tv2-ib">{insight.text}</div>
          {/* B4 — "reads · …" is right-justified on the SAME line as the Ask affordance, which
              buys back one full line above the fold at 393px. Reuses `.tv2-ifoot` — already
              `flex; justify-content:space-between` — rather than adding a class, so the markup
              cannot ship ahead of its stylesheet. */}
          <div className="tv2-ifoot">
            <span className="tv2-ia">Ask Kalpa about this ›</span>
            <span className="tv2-reads">
              reads · {(insight.reads && insight.reads.length ? insight.reads : ['your data']).join(' + ')}
            </span>
          </div>
        </button>
      ) : null}

      {/* KAL-235 placement 4 — Today, as a card BELOW the daily insight and ABOVE the Kalpa
          Age card, which is the thing it is about: a user whose panel is missing or stale is
          looking at an age card that cannot show them a number. It renders only when that is
          true, so it is never a standing CTA on a complete record. */}
      <OrderPanelPrompt placement="today" />

      {/* KAL-287 — a stalled stream is a problem the user can often fix in one menu, so it
          renders ABOVE the metrics it explains. Actionable-and-specific only: the server
          sends steps only for a writer it has identified from Health Connect's own origin
          metadata; otherwise it says we are investigating, never "check your settings". */}
      <ConnectionHealthCard data={streamHealth} />

      {/* ── MEASURED zone ───────────────────────────────────────────────────────────────── */}
      {/* 2.1 — page-level label above the measured card, primary brightness tier. */}
      <div className="tv2-zdiv tv2-zdiv-bright">
        <span className="tv2-zt">Kalpa Age</span><span className="tv2-zl" />
      </div>
      <TV2AgeCard age={m.age} onNav={onNav} onOpen={() => openMetric('age')} />

      {/* ── THE TRACKED ZONE ──────────────────────────────────────────────────── */}
      {/* ITEM 6 (Tim, 14 Aug) — the word “TRACKED” is GONE and the report line takes the
          divider's place and its type tier. It used to read “TRACKED” here and then repeat
          “AUGUST REPORT · DAY N OF 31” as a stamp inside the card immediately below — two
          header rows where one carries the information. The stamp is DELETED, not restyled,
          so the string cannot render twice. Asserted: exactly ONE node matching
          /REPORT · DAY n OF n/, no node reading “TRACKED”, that node wearing the divider tier
          the pre-change baseline recorded, and the header region SHORTER than baseline. */}
      {/* KAL-333 — the report header carries the month pager. `rep` is the month being shown:
          `m` for the live month, `rm` for a closed one. ‹ is bounded by the user's earliest data
          month (server-derived, KAL-142), › returns to the live month and no further. */}
      {(() => {
        const rep = reportMonth ? rm : m;
        const repKey = reportMonth || m.month;
        const earliest = (m && m.earliest_month) || null;
        const canPrev = !!repKey && (!earliest || _tv2ShiftMonth(repKey, -1) >= earliest);
        const canNext = !!reportMonth;
        const repName = (() => {
          const [y, mo] = (repKey || '').split('-').map(Number);
          return y ? new Date(y, mo - 1, 1).toLocaleDateString('en-GB', { month: 'long' }) : '';
        })();
        const goPrev = () => { if (canPrev) setReportMonth(_tv2ShiftMonth(repKey, -1)); };
        const goNext = () => {
          if (!canNext) return;
          const nxt = _tv2ShiftMonth(repKey, 1);
          setReportMonth(nxt === m.month ? null : nxt);
        };
        return (
          <div className="tv2-zdiv tv2-zdiv-bright tv2-repnav" data-testid="tv2-report-nav"
               data-month={repKey || ''}>
            <button type="button" className="tv2-pagerbtn tv2-repbtn" disabled={!canPrev}
              aria-label="Previous month's report" onClick={goPrev}>‹</button>
            <span className="tv2-zt">
              {repName ? `${repName.toUpperCase()}${reportMonth && repKey && repKey.slice(0, 4) !== m.month.slice(0, 4) ? ` ${repKey.slice(0, 4)}` : ''} REPORT` : 'REPORT'}
              {reportMonth
                ? (rep && rep.days_in_month ? ` · ${rep.days_in_month} DAYS` : '')
                : (m.day_of_month ? ` · DAY ${m.day_of_month} OF ${m.days_in_month}` : '')}
            </span>
            {/* KAL-348 - the two arrows now BRACKET the month label instead of sitting at
                the two far ends of the row with the divider rule between them. Tim, on the
                phone, 19 Sep: "hard to see and easy to miss". A stepper reads as ONE control;
                an arrow stranded at the right edge, past a hairline rule, reads as decoration.
                The rule keeps its job - it is still the zone divider - and moves right of the
                pair. */}
            <button type="button" className="tv2-pagerbtn tv2-repbtn" disabled={!canNext}
              aria-label={canNext ? 'Next month\'s report' : 'This is the current month'} onClick={goNext}>›</button>
            <span className="tv2-zl" />
          </div>
        );
      })()}

      {reportMonth && !rm && !rmErr && (
        <div className="tv2-card"><div className="tv2-absent">
          {rmRetrying
            ? `Still loading ${_tv2MonthLabel(reportMonth)} — retrying (${rmRetrying} of 3)…`
            : `Loading ${_tv2MonthLabel(reportMonth)}…`}
        </div></div>
      )}
      {reportMonth && rmErr && (
        <div className="tv2-card"><div className="tv2-absent">
          {rmErr === 'transient'
            ? <>{_tv2MonthLabel(reportMonth)} is taking longer than usual — this can happen for a
                minute or two right after a sync. {rmAuto.current >= 3 ? 'Tap to try again.' : 'Retrying…'}{' '}</>
            : <>{_tv2MonthLabel(reportMonth)} couldn’t be loaded.{' '}</>}
          <button type="button" className="tv2-retry"
            onClick={() => { rmAuto.current = 0; setRmAttempt(a => a + 1); }}>Retry now</button>
        </div></div>
      )}
      {(!reportMonth || rm) && (() => {
        // KAL-333 — ONE render path for live and closed months. For a closed month the server
        // strips the *_live fields, so the rails and "today" captions simply do not render, and
        // the averages are over that month's recorded days only. KAL-334 — every bar carries its
        // coverage: steps/sleep over the COMPLETE days of the month, AZM over its rolling window.
        const rep = reportMonth ? rm : m;
        const rst = rep.steps || {}, rsl = rep.sleep || {}, raz = rep.azm || {};
        const closed = !!reportMonth;
        const stCov = { n: rst.days_with_data, of: rep.complete_days };
        const slCov = { n: rsl.days_with_data, of: rep.complete_days };
        const azCov = { n: raz.days_with_data, of: raz.window_days };
        return (
      <div className="tv2-card" data-testid="tv2-report-card" data-closed={closed ? '1' : '0'}>
        {/* ── THE UNIFIED BAR — steps, sleep and AZM share ONE model ──────────────────────
            Tim: "sleep is clear, use as benchmark ... think beyond target and make consistent."
            All three now read identically:
              track  = 0 → max(target, actual)   ← the bar AUTO-SCALES when you beat the target
              fill   = your period figure
              target = the right edge while you are under it; a LINE INSIDE the bar once you
                       pass it, so beating a target is VISIBLE rather than invisible
              tick   = today / last night, on the SAME scale
            The delta label sits BELOW the bar, never inside it: inside, it was swallowed by the
            fill the moment you came within ~25% of target (KAL-144), and an over-target bar has
            no empty run to put it in at all. */}
        <TV2Bar metric="steps" label="Steps: average daily"
          sub={closed ? 'daily average that month' : 'daily average this month'}
          value={rst.avg_complete} target={rst.target}
          live={closed ? null : rst.today_live} liveLabel="today"
          liveAsOf={rst.today_live_as_of} liveIsCurrent={rst.today_live_is_today !== false}
          fmt={_tv2Num} liveWhite coverage={stCov}
          onOpen={() => openMetric('steps', rep.month)}
          absent={closed ? 'No step data recorded that month.' : 'No step data recorded this month yet.'} />

        <TV2Bar metric="sleep" label="Sleep: average daily"
          sub={closed ? 'average per night that month' : 'average per night this month'}
          value={rsl.avg_complete_min} target={rsl.target_min}
          live={closed ? null : rsl.last_night_min} liveLabel="last night"
          liveAsOf={rsl.last_night_as_of} liveIsCurrent={rsl.last_night_is_last_night !== false}
          fmt={_tv2Hm} liveWhite coverage={slCov}
          onOpen={() => openMetric('sleep', rep.month)}
          absent={closed ? 'No sleep recorded that month.' : 'No sleep recorded this month yet.'} />

        {/* KAL-333 — AZM is a ROLLING window "decoupled from the month by design" (today_v2.py):
            for a closed month there is no month figure to show, and printing this week's
            minutes under an August heading would be the label-changes-not-the-data defect the
            drawer already refused (KAL-285). So it renders for the live month only. */}
        {!closed && (
        <TV2Bar metric="azm" label="Active Zone Minutes"
          sub={`rolling ${raz.window_days || 7} days · rolls daily`}
          value={raz.source_present ? raz.earned : null} target={raz.target}
          live={raz.today_live} liveLabel="today"
          liveAsOf={raz.today_live_as_of} liveIsCurrent={raz.today_live_is_today !== false}
          fmt={(n) => n == null ? null : `${Math.round(n)} min`}
          coverage={raz.source_present ? azCov : null}
          onOpen={() => openMetric('azm')}
          absent={raz.source_present ? 'Zone minutes couldn’t be read just now.'
                                     : 'No heart-zone data from your device'} />
        )}
        {closed && (
          <div className="tv2-rowlab tv2-rowlab-note"><b>Active Zone Minutes</b>
            <span>rolling 7-day figure · shown on the current month only</span></div>
        )}

        {/* ── RHR — the user's OWN 30-day band. Never a population range (KAL-101). ────── */}
        {/* KAL-333 — like AZM, RHR is a rolling 30-day figure for NOW; a closed month shows a
            note, not this week's band under last month's heading. */}
        {closed ? (
          <div className="tv2-rowlab tv2-rowlab-note"><b>Resting heart rate</b>
            <span>rolling 30-day band · shown on the current month only</span></div>
        ) : (<>
        <div className="tv2-rowlab"><b>Resting heart rate</b><span>observed, no target</span></div>
        {rh.avg7 == null || rh.band30 == null || rh.band30.lo == null ? (
          <TV2Absent>No resting heart rate recorded in the last 30 days.</TV2Absent>
        ) : (() => {
          const lo = rh.band30.lo, hi = rh.band30.hi;
          const span = (hi - lo) || 1;
          const pos = (v) => v == null ? null : _tv2Pos(((v - lo) / span) * 100);
          return (
            <button type="button" className="tv2-band" onClick={() => openMetric('rhr')}
              aria-label={`Resting heart rate, 7-day average ${rh.avg7}, latest ${rh.latest}, your 30-day range ${lo} to ${hi}`}>
              <span className="tv2-base" />
              <span className="tv2-zone" style={{ left: '0%', right: '0%' }} />
              <span className="tv2-dot" style={{ left: pos(rh.avg7) + '%' }} />
              <span className="tv2-dv" style={{ left: pos(rh.avg7) + '%' }}>avg {Math.round(rh.avg7)}</span>
              {rh.latest != null && (
                <>
                  <span className="tv2-ringd" style={{ left: pos(rh.latest) + '%' }} />
                  {/* ⚠ ITEM 3 — “now” WAS FACTUALLY WRONG AND THE SERVER-SIDE CHECK IS WHAT
                      CAUGHT IT. today_v2.py:_rhr_block selects AVG(value) … GROUP BY date, so
                      this is the LATEST DAY'S MEAN, not a reading taken now. Measured on
                      master-test for this profile: the latest day carries 23 rows (up to 33
                      on other days, 19 of 31 days have >1) and they all share ONE timestamp
                      — there is no intraday time resolution to call “now” at all.
                      It is independent of avg7 (72.6046 vs 73.087 — different windows,
                      neither derived from the other), so the DATA is sound; only the word
                      was wrong. “latest” is what the aria-label and the drawer already say. */}
                  <span className="tv2-nv" style={{ left: pos(rh.latest) + '%' }}>latest {Math.round(rh.latest)}</span>
                </>
              )}
              <span className="tv2-tick">{Math.round(lo)}–{Math.round(hi)} bpm · your 30-day range</span>
            </button>
          );
        })()}
        </>)}

        {/* ── DAY STRIP + footer ──────────────────────────────────────────────────────── */}
        <div className="tv2-days" style={{ gridTemplateColumns: `repeat(${rep.days_in_month}, 1fr)` }}>
          {Array.from({ length: rep.days_in_month }, (_, i) => (
            <i key={i} className={i < rep.complete_days ? 'f' : (i === rep.complete_days && !closed ? 't' : '')} />
          ))}
        </div>
        <div className="tv2-dayscap">
          {closed ? (
            <>
              <span>{rep.days_in_month} days, closed — averages over the days that recorded data.</span>
              <span>read-only</span>
            </>
          ) : (
            <>
              <span>{rep.complete_days} complete days — today joins averages at midnight.</span>
              <span>{rep.days_remaining} days left</span>
            </>
          )}
        </div>
      </div>
        );
      })()}
    </div>
  );
}

// The measured-zone card. Reads the SAME authoritative bio-age surface the rest of the app
// uses; this component only presents it. NO number here may imply a day's behaviour moved it —
// the small print says so explicitly, verbatim from the mock.
function TV2AgeCard({ age, onNav, onOpen }) {
  // DESIGN SYSTEM v1 §0.4 — SEMANTICS, NOT DECORATION.
  // The ring is a POSITION ON A DISTRIBUTION, not a progress dial. Zones come from the server's
  // real NHANES 80% band; the client only positions what it is handed, because whether the user
  // may be told they are "younger" is a scientific decision, not a presentational one.
  //
  // ⚠ THE BAND IS ASYMMETRIC. bioage_band derives it from right-skewed residuals, so there is
  // no ±ci80 and chronological age does NOT sit at the ring's centre. `chrono_pos` is computed
  // server-side (Tim: 46.1%, not 50%) and the birthday-age label is placed from it — never
  // hardcoded to the middle.
  const d = age || {};
  const b = d.band || null;
  const kalpa = d.kalpa_age ?? null;
  const chrono = d.chrono ?? null;
  const R = 46, STROKE = 9, C = 2 * Math.PI * R;
  // Arc geometry: the ring is a full circle starting at 12 o'clock. A fraction of the domain
  // maps to a fraction of the circumference, so a zone is just a dash segment.
  const seg = (from, to) => ({
    strokeDasharray: `${Math.max(0, (to - from)) * C} ${C}`,
    strokeDashoffset: `${-from * C}`,
  });
  const ZONE_CLASS = { favourable: 'is-fav', band: 'is-band', unfavourable: 'is-unfav' };
  const zoneClass = b ? (ZONE_CLASS[b.zone] || 'is-band') : 'is-band';

  return (
    <button type="button" className="tv2-card tv2-measzone tv2-agecard" onClick={onOpen}>
      <span className="tv2-chev">›</span>
      {/* §0.1/0.2 — card title promoted to the PRIMARY brightness tier. The square keeps the
          accent hue; the text does not. It was reading as a caption, not a header. */}
      <div className="tv2-cardtitle">
        <span className="tv2-sq" />
        {d.draw_date ? `FROM BLOOD DRAW ${formatDate(d.draw_date)}` : 'FROM YOUR BLOOD DRAW'}
        {(d.present != null && d.total != null) ? ` · ${d.present} OF ${d.total}` : ''}
      </div>

      <div className="tv2-agerow">
        <div className="tv2-ringwrap">
          <svg width="112" height="112" viewBox="0 0 112 112" aria-hidden="true"
               className="tv2-ringsvg">
            <g transform="rotate(-90 56 56)">
              {/* ── KAL-281 — SUPPRESSED: nine segments, one per PhenoAge marker, filled when
                  that marker sits in its reference range (the lab's own printed range where
                  stored). A resolvable fact at ANY age — the age arc answers a question the
                  model cannot at low mortality risk; this answers one it can. A full ring
                  reads as a good result; Pawan's real ring is 8 of 9 (Lymphocytes 43% against
                  a 20-40 lab range), so the fill is a finding, never decoration. */}
              {d.suppressed && Array.isArray(d.markers) && d.markers.length === 9 ? (
                d.markers.map((m, i) => {
                  const GAP = 0.012;                       // fraction of C between segments
                  const segFrac = (1 / 9) - GAP;
                  const off = -(i * (1 / 9)) * C;
                  const cls = m.in_range === true ? 'tv2-z-fav'
                            : m.in_range === false ? 'tv2-z-unfav'
                            : 'tv2-ring-track';
                  return <circle key={m.key} cx="56" cy="56" r={R} className={cls}
                    strokeWidth={STROKE} fill="none"
                    style={{ strokeDasharray: `${segFrac * C} ${C - segFrac * C}`,
                             strokeDashoffset: off,
                             opacity: m.in_range === false ? 0.85 : m.in_range == null ? 0.4 : 1 }} />;
                })
              ) : b ? (
                <>
                  <circle cx="56" cy="56" r={R} className="tv2-z-fav" strokeWidth={STROKE}
                    fill="none" style={seg(0, b.band_lo_pos)} />
                  <circle cx="56" cy="56" r={R} className="tv2-z-band" strokeWidth={STROKE}
                    fill="none" style={seg(b.band_lo_pos, b.band_hi_pos)} />
                  <circle cx="56" cy="56" r={R} className="tv2-z-unfav" strokeWidth={STROKE}
                    fill="none" style={seg(b.band_hi_pos, 1)} />
                </>
              ) : (
                <circle cx="56" cy="56" r={R} className="tv2-ring-track" strokeWidth={STROKE}
                  fill="none" />
              )}
            </g>
            {/* birthday-age tick at the COMPUTED angle (age arc only — meaningless on the
                nine-segment marker ring) */}
            {!d.suppressed && b && (() => {
              const a = b.chrono_pos * 2 * Math.PI - Math.PI / 2;
              const x1 = 56 + Math.cos(a) * (R - STROKE / 2 - 2);
              const y1 = 56 + Math.sin(a) * (R - STROKE / 2 - 2);
              const x2 = 56 + Math.cos(a) * (R + STROKE / 2 + 2);
              const y2 = 56 + Math.sin(a) * (R + STROKE / 2 + 2);
              return <line x1={x1} y1={y1} x2={x2} y2={y2} className="tv2-ring-chrono" />;
            })()}
            {/* kalpa_age marker — the live point, same glyph language as §0.3.
                KAL-279 — positioned from b.kalpa_pos, NOT gated on the numeral: a suppressed
                headline keeps its band and marker (2.3); only the number itself is withheld. */}
            {!d.suppressed && b && b.kalpa_pos != null && (() => {
              const a = b.kalpa_pos * 2 * Math.PI - Math.PI / 2;
              return <circle cx={56 + Math.cos(a) * R} cy={56 + Math.sin(a) * R} r="6"
                className={'tv2-ring-marker ' + zoneClass} />;
            })()}
          </svg>
          {/* KAL-279 — a suppressed centre shows ✓ (the 23 Jul precedent), never '—', which
              reads as missing data to a user whose panel is complete. */}
          {(() => {
            let s;
            if (d.suppressed) {
              const ms = Array.isArray(d.markers) ? d.markers : [];
              const n = ms.filter(m => m.in_range === true).length;
              const known = ms.filter(m => m.in_range != null).length;
              // ✓ for a clean sweep; the honest count otherwise ("8/9" is a finding, not an error)
              s = (known === 9 && n === 9) ? '✓' : (known > 0 ? `${n}/9` : '✓');
            } else {
              s = kalpa != null ? Number(kalpa).toFixed(1) : '—';
            }
            return <span className={'tv2-ringnum' + (s.length >= 5 ? ' is-wide' : '')}>{s}</span>; })()}
        </div>

        <div className="tv2-agetext">
          <div className="tv2-agelead">Your Biological Age</div>
          {/* ── KAL-279 — SUPPRESSED: qualitative wording, never a number ────────────────────
              Committee decision 24 Aug (Pawan, Ruskin, Tim): below the model's resolution no
              numeric biological age renders — not the age, and not the "Xy younger" delta,
              which is the same claim in different clothes. The band graphic, the marker
              position, the bullets and everything below stay. This branch must sit BEFORE the
              kalpa==null one: a suppressed payload also nulls kalpa_age, and falling through
              would print "Add lab results" to a user whose panel is complete — reading as an
              error, which the committee wording explicitly forbids.
              COPY IS A DRAFT pending Tim's approval (his instruction: do not invent final
              wording) — it states the markers are healthy without implying a number exists. */}
          {d.suppressed ? (
            <>
              {/* KAL-281 (DRAFT COPY, for Tim's approval on return) — the headline states the
                  range the model ACTUALLY produces for THIS user: est_band = phenoage+q10..q90.
                  Its lower edge sits below the support floor (that is why the point is
                  suppressed), so only the UPPER edge prints — "reads under 27" — every number
                  shown is model-produced and supported; nothing clamped, nothing explained
                  away. The 27.91-41.19 the card used to print is the age-matched POPULATION
                  range — a different quantity; "your biological age reads between 28 and 41"
                  would have been false for a user whose estimate is 22. */}
              <div className={'tv2-agebig ' + zoneClass}>
                {d.est_band && d.est_band.hi != null
                  ? `Your biological age reads under ${Math.round(d.est_band.hi)}`
                  : 'Your markers point younger than your age'}
              </div>
              <div className="tv2-agesub">
                {(() => {
                  const ms = Array.isArray(d.markers) ? d.markers : [];
                  const n = ms.filter(m => m.in_range === true).length;
                  const known = ms.filter(m => m.in_range != null).length;
                  const chronoTxt = chrono != null ? ` · birthday age ${Number(chrono).toFixed(1)}` : '';
                  if (known === 9 && n === 9) return `All 9 markers in the healthy range${chronoTxt}.`;
                  if (known > 0) return `${n} of ${known < 9 ? known : 9} markers in the healthy range${chronoTxt}.`;
                  return `From your latest blood draw${chronoTxt}.`;
                })()}
              </div>
              <ul className="tv2-agebullets">
                <li>Wellness estimate · single blood panel · PhenoAge (Levine&nbsp;2018)</li>
                <li>Can shift with illness, hydration, lab variation</li>
                <li>Not a diagnosis — changes only at your next draw</li>
              </ul>
            </>
          ) : kalpa == null ? (
            <div className="tv2-agesub" data-testid="tv2-age-absent">
              {d.age_gate
                ? 'Biological age uses an adult model, so it isn’t shown under 18 — your markers, activity and sleep are here as normal.'
                : (() => {
                    // KAL-336 — NAME THE GAP. "8 of 9 · Add lab results" told nobody which
                    // result. `d.missing` is the server's single-draw list (the same one the
                    // Longevity headline prints as "missing CRP"); the names are the ones a lab
                    // request form uses, so the user or the lab knows exactly what to add.
                    const NAMES = {
                      Albumin: 'Albumin', Creatinine: 'Creatinine', Glucose: 'Glucose (fasting)',
                      CRP: 'hs-CRP (high-sensitivity C-reactive protein)',
                      Lymphocytes: 'Lymphocytes (% of white cells)',
                      MCV: 'MCV (mean cell volume)', RDW: 'RDW (red cell distribution width)',
                      ALP: 'Alkaline phosphatase (ALP)', WBC: 'White cell count (WBC)',
                    };
                    const miss = Array.isArray(d.missing) ? d.missing.map(k => NAMES[k] || k) : [];
                    if (!miss.length) return 'Add lab results to see your biological age.';
                    const list = miss.length === 1 ? miss[0]
                      : miss.slice(0, -1).join(', ') + ' and ' + miss[miss.length - 1];
                    return (
                      <>
                        {d.draw_date
                          ? `Your ${formatDate(d.draw_date)} panel is missing ${list}. `
                          : `Your panel is missing ${list}. `}
                        Add {miss.length === 1 ? 'that result' : 'those results'} to see your biological age.
                      </>
                    );
                  })()}
            </div>
          ) : (
            <>
              {/* THE COPY RULE. Inside the 80% band the honest claim is "in line with" — a raw
                  difference within normal scatter is not evidence of being younger or older. */}
              <div className={'tv2-agebig ' + zoneClass}>
                {b == null ? `${Number(kalpa).toFixed(1)}`
                  : b.claim === 'in_line' ? 'In line with your birthday age'
                  : `${_tv2YM(b.delta_years)} ${b.claim}`}
              </div>
              {/* ⚠ PHASE 2.4 — FLAGGED COMPROMISE, PENDING TIM'S CONFIRMATION.
                  Tim asked for a literal delta ("5y 7m younger"). That conflicts with the
                  scientific-honesty rule already implemented: his 50.0 sits INSIDE his 80%
                  band, where the supportable claim is "in line with". Resolution: the honest
                  claim stays PRIMARY (above), and the literal numbers he asked for go on a
                  second, dimmer line. Neither statement is false and the stronger claim is not
                  promoted above what one panel supports. Awaiting confirmation of wording. */}
              <div className="tv2-agesub">
                {b == null
                  ? `birthday age ${chrono != null ? Number(chrono).toFixed(1) : '—'}`
                  : b.claim === 'in_line'
                    ? `${Number(kalpa).toFixed(1)} vs birthday age ${Number(chrono).toFixed(1)} — ${b.delta_years.toFixed(1)}y difference, within your normal range.`
                    : `${Number(kalpa).toFixed(1)} vs birthday age ${Number(chrono).toFixed(1)} — outside your 80% range (${b.band_lo}–${b.band_hi}).`}
              </div>
              {b && b.out_of_domain && (
                <div className="tv2-ageflag">▲ beyond the chart — marker shown at the edge</div>
              )}
              {/* §0.4 — 2–3 short bullets. The full paragraph lives in the drawer. */}
              <ul className="tv2-agebullets">
                <li>Wellness estimate · single blood panel · PhenoAge (Levine&nbsp;2018)</li>
                <li>Can shift with illness, hydration, lab variation</li>
                <li>Not a diagnosis — changes only at your next draw</li>
              </ul>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

// Years-and-months, because "5y 7m younger" reads as a human span where "5.6" reads as a score.
function _tv2YM(years) {
  const y = Math.floor(years), mo = Math.round((years - y) * 12);
  if (y <= 0) return `${mo}m`;
  return mo ? `${y}y ${mo}m` : `${y}y`;
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// PANEL-ORDER PROMPT (KAL-235) — NPL only, gated on features.order_lab
//
// ONE component, mounted on five screens. It is deliberately the ONLY way this prompt reaches
// the UI: the server returns at most one prompt per call, and every placement renders through
// here, so "at most one order prompt visible per screen" is a property of the code rather than
// a rule five call sites have to remember.
//
// The tone matters more than the mechanics. These are ~10-16 real pilot users in Nepal who did
// not sign up to be sold to, so: no badge, no colour that shouts, no animation, a visible
// dismiss, and copy that says what the DATA is missing rather than what they should buy. NPL
// is named as the seller in the body text — Kalpa is not selling anything and must not look
// like it is.
// ══════════════════════════════════════════════════════════════════════════════════════════
// B1 — the persistent inline action. NOT a prompt: it ignores the trigger and the suppression
// window entirely, because it is an affordance on a line the user is already reading, not a
// suggestion pushed at them. `prompt` (the card) keeps every anti-spam rule; this does not need
// them, and if it obeyed them it would be invisible to exactly the people with a good panel —
// which is what happened to Tim.
function OrderPanelInlineAction({ where }) {
  const [panel, setPanel] = React.useState(null);
  React.useEffect(() => {
    if (!_orderingOn()) return;   // KAL-303
    let alive = true;
    fetch(`${API_BASE}/order-panel/prompt?placement=${encodeURIComponent(where)}`,
          { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d && d.panel) setPanel(d.panel); })
      .catch(() => {});
    return () => { alive = false; };
  }, [where]);
  if (!_orderingOn() || !panel) return null;
  const tap = () => {
    // Recorded as a tap-through so the CARD stays quiet for 30 days afterwards — someone who
    // has just opened the booking page must not then be prompted about it elsewhere.
    try {
      fetch(`${API_BASE}/order-panel/event`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ placement: where, action: 'tapped', panel_id: panel.id }),
      }).catch(() => {});
    } catch (_) {}
  };
  return (
    <>
      {/* KAL-280 — BACK TO THE PACKAGE PAGE, per the committee meeting (24 Aug): the expected
          path is Longevity -> Book blood test -> the NPL Longevity & Wellness package page.
          This walks back KAL-276's in-app modal FOR THE PANEL ACTION ONLY (marker re-tests
          keep the in-app request flow). Two facts stand from KAL-276 and are accepted, not
          hidden: the destination runs its OWN SMS-OTP login, and inside the NATIVE APP a
          target=_blank was silently swallowed (the WebView had no onOpenWindow — THAT is the
          "did not behave as expected"; fixed in npl-kalpa-mobile, needs the next build). */}
      <a className="opp-inline" href={panel.url} target="_blank" rel="noopener noreferrer"
         onClick={tap} title={`${panel.name}${panel.price_display ? ' — ' + panel.price_display : ''}`}>
        Book blood test
      </a>
    </>
  );
}


function OrderPanelPrompt({ placement, compact, variant }) {
  const [prompt, setPrompt] = React.useState(null);
  const [gone, setGone] = React.useState(false);

  React.useEffect(() => {
    // The flag is the first gate: on UK, US and master-test the route 404s, but not asking at
    // all keeps a pointless request off every page load on three of four instances.
    if (!_orderingOn()) return;   // KAL-303
    let alive = true;
    fetch(`${API_BASE}/order-panel/prompt?placement=${encodeURIComponent(placement)}`,
          { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d && d.prompt) setPrompt(d.prompt); })
      .catch(() => {});      // silent: a missing prompt is a normal state, not an error to show
    return () => { alive = false; };
  }, [placement]);

  const send = (action) => {
    try {
      fetch(`${API_BASE}/order-panel/event`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ placement, action, panel_id: prompt && prompt.panel_id }),
      }).catch(() => {});
    } catch (_) { /* the click must never be blocked by telemetry */ }
  };

  if (!_orderingOn() || !prompt || gone) return null;

  const onDismiss = () => { setGone(true); send('dismissed'); };
  // A tap-through counts as a dismissal server-side, so the card goes too — being asked again
  // on the next screen after opening the booking page is exactly the nagging this avoids.
  const onTap = () => { setGone(true); send('tapped'); };

  // B2 — Ask Kalpa gets a PLAIN TEXT LINK, not a card and not a button. A card inside a
  // conversation reads as an advert interrupting an answer; a sentence with a link reads as a
  // follow-up to what was just said. Same suppression rules either way.
  if (variant === 'link') {
    return (
      <div className="opp-linkline">
        {/* KAL-280 — package page, per the committee's expected path. */}
        <a href={prompt.url} target="_blank" rel="noopener noreferrer" onClick={onTap}>
          Book a blood test to update this.
        </a>
      </div>
    );
  }

  return (
    <div className="order-panel-prompt" data-placement={placement} data-compact={compact ? '1' : '0'}>
      <div className="opp-body">
        <div className="opp-headline">{prompt.headline}</div>
        <div className="opp-text">{prompt.body}</div>
      </div>
      <div className="opp-actions">
        {/* KAL-280 — package page primary, per the committee's expected path. */}
        <a className="opp-cta" href={prompt.url} target="_blank" rel="noopener noreferrer"
           onClick={onTap}>
          Book blood test
        </a>
        <button className="opp-dismiss" type="button" onClick={onDismiss}
                aria-label="Dismiss this suggestion">
          Not now
        </button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════════════════
// ORDER MANAGEMENT — the local-admin screen (Bimal)
//
// Add, edit, reorder, enable/disable panels, and set the retest interval. Nothing here needs a
// deploy: name, URL, price and the interval are all rows. That is the whole point — the older
// nudge engine's catalogue is a literal dict in app.py whose test codes are STILL placeholders
// a year on, because reconciling them means a code change.
// ══════════════════════════════════════════════════════════════════════════════════════════
function OrderManagementPage() {
  const [panels, setPanels] = React.useState([]);
  const [interval_, setInterval_] = React.useState(12);
  const [suppressionDays, setSuppressionDays] = React.useState(30);
  const [draft, setDraft] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');

  const load = React.useCallback(() => {
    fetch(`${API_BASE}/admin/order-panels`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`API ${r.status}`))))
      .then(d => {
        setPanels(d.panels || []);
        setInterval_(d.retest_interval_months || 12);
        setSuppressionDays(d.suppression_days || 30);
        setErr('');
      })
      .catch(e => setErr(String(e.message || e)));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Read the server's reason, never just the status code (KAL-222: the UI swallowed a precise
  // refusal and showed "API 403" while the server had explained itself exactly).
  const post = async (path, body, method = 'POST') => {
    setMsg(''); setErr('');
    try {
      const r = await fetch(`${API_BASE}/admin/order-panels${path}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      let payload = null;
      try { payload = await r.json(); } catch (_) { /* empty body */ }
      if (!r.ok) throw new Error((payload && (payload.error || payload.detail)) || `API ${r.status}`);
      setMsg('Saved.');
      setDraft(null);
      load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const blank = { id: null, name: '', url: '', price_display: '', active: true, sort_order: 0 };

  return (
    <div className="ordmgr">
      <h2 className="ordmgr-title">Order Management</h2>
      <p className="ordmgr-sub">
        Panels offered to users whose blood panel is missing, incomplete or out of date. The
        link opens National Path Lab&rsquo;s own booking page in a new tab &mdash; there is no
        payment in this app.
      </p>

      {err && <div className="ordmgr-err">{err}</div>}
      {msg && <div className="ordmgr-ok">{msg}</div>}

      <div className="ordmgr-settings">
        <label htmlFor="ordmgr-interval">Ask again when a panel is older than</label>
        <input
          id="ordmgr-interval"
          type="number"
          min="1"
          max="120"
          value={interval_}
          onChange={e => setInterval_(e.target.value)}
        />
        <span>months</span>
        <button type="button" onClick={() => post('/settings', { retest_interval_months: Number(interval_) })}>
          Save interval
        </button>
        <span className="ordmgr-note">
          After someone dismisses a suggestion or opens the booking page, they are not asked
          again for {suppressionDays} days &mdash; on any screen.
        </span>
      </div>

      <table className="ordmgr-table">
        <thead>
          <tr>
            <th>Order</th><th>Name</th><th>Link</th><th>Price</th><th>Shown</th><th></th>
          </tr>
        </thead>
        <tbody>
          {panels.map(p => (
            <tr key={p.id} className={p.active ? '' : 'is-off'}>
              <td>{p.sort_order}</td>
              <td>{p.name}</td>
              <td className="ordmgr-url" title={p.url}>{p.url}</td>
              <td>{p.price_display}</td>
              <td>{p.active ? 'Yes' : 'No'}</td>
              <td>
                <button type="button" onClick={() => setDraft({ ...p, active: !!p.active })}>Edit</button>
                <button
                  type="button"
                  onClick={() => post('', { ...p, active: !p.active })}
                >
                  {p.active ? 'Hide' : 'Show'}
                </button>
              </td>
            </tr>
          ))}
          {!panels.length && <tr><td colSpan="6">No panels configured.</td></tr>}
        </tbody>
      </table>

      <button type="button" className="ordmgr-add" onClick={() => setDraft({ ...blank })}>
        Add a panel
      </button>

      {draft && (
        <div className="ordmgr-form">
          <h3>{draft.id ? 'Edit panel' : 'Add panel'}</h3>
          <label>Name
            <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label>Booking link
            <input value={draft.url} placeholder="https://..."
                   onChange={e => setDraft({ ...draft, url: e.target.value })} />
          </label>
          <label>Price (shown exactly as typed)
            <input value={draft.price_display} placeholder="NPR 12,000"
                   onChange={e => setDraft({ ...draft, price_display: e.target.value })} />
          </label>
          <label>Order
            <input type="number" value={draft.sort_order}
                   onChange={e => setDraft({ ...draft, sort_order: e.target.value })} />
          </label>
          <label className="ordmgr-check">
            <input type="checkbox" checked={!!draft.active}
                   onChange={e => setDraft({ ...draft, active: e.target.checked })} />
            Show this panel to users
          </label>
          <div className="ordmgr-formactions">
            <button type="button" onClick={() => post('', {
              ...draft, sort_order: Number(draft.sort_order) || 0,
            })}>Save</button>
            <button type="button" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


function LongevityFlagsView({ onNav }) {
  const features = useFeatures();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/health-record/longevity-flags')
      .then(d => { if (!cancelled) { setData(convertFlagsPayload(d)); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner message="Loading longevity flags..." />;
  if (error)   return <ErrorState message={error} />;
  if (!data)   return null;

  // Sort by the ageDelta from LONGEVITY_ACTIONS (biggest impact first).
  // Markers not in the catalogue fall through with ageDelta=0.
  const sorted = [...(data.flags || [])].sort((a, b) => {
    const da = (LONGEVITY_ACTIONS[a.marker_key] || {}).ageDelta || 0;
    const db = (LONGEVITY_ACTIONS[b.marker_key] || {}).ageDelta || 0;
    return db - da;
  });
  // RUN11 §5 — split GENUINE concerns (count toward the longevity score) from markers shown
  // with reassuring context but NOT counted (benign cross-marker pattern). backend is_concern
  // is the source of truth; concernCount == data.flag_count.
  const concerns = sorted.filter(f => f.is_concern !== false);
  const contextual = sorted.filter(f => f.is_concern === false);
  const concernCount = (typeof data.flag_count === 'number') ? data.flag_count : concerns.length;
  const totalDelta = concerns.reduce((s, f) => s + ((LONGEVITY_ACTIONS[f.marker_key] || {}).ageDelta || 0), 0);

  return (
    <div className="longevity-flags-view" style={{padding:'18px 16px 60px 16px', maxWidth:720, margin:'0 auto'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14}}>
        <button
          onClick={() => {
            // replaceState (per spec) overwrites the longevity-flags
            // history entry with #timeline so a subsequent browser-
            // forward can't re-enter this view, and the previous
            // #timeline entry pushed when ⚑ was tapped stays put.
            try { window.history.replaceState({}, '', '#timeline'); } catch {}
            if (onNav) onNav('timeline');
          }}
          style={{
            background:'none', border:'none', color:'var(--text-secondary)',
            fontSize:14, cursor:'pointer', padding:0, display:'inline-flex',
            alignItems:'center', gap:4,
          }}
        >← All markers</button>
        {/* RUN11.6 §3 — redundant "⚑ N affecting your longevity score" super-header removed;
            the reconciled count now lives in the single summary card below. */}
      </div>

      {sorted.length === 0 ? (
        <div style={{padding:'40px 16px', textAlign:'center', color:'var(--text-secondary)', fontSize:14}}>
          No markers outside your lab reference range.
        </div>
      ) : (
        <>
          {/* RUN11.6 §3 — ONE reconciled summary line for ALL evaluated markers (shown whether or
              not any are genuine concerns). total = every marker outside the lab range. */}
          <_LongevitySummaryCard total={sorted.length} concerns={concernCount} contextual={contextual.length} />
          {concerns.length > 0 && concerns.map(f => (
            <LongevityActionCard key={f.marker_key} flag={f} features={features} onNav={onNav} />
          ))}

          {/* RUN11 §5 — markers reviewed WITH context and found benign: shown honestly, NOT counted. */}
          {contextual.length > 0 && (
            <>
              <div style={{margin:'22px 0 8px', fontSize:13, fontWeight:700, color:'var(--text-secondary)'}}>
                Reviewed in context ({contextual.length}) — not currently a concern
              </div>
              <div style={{fontSize:12.5, color:'var(--text-secondary)', marginBottom:10, lineHeight:1.5}}>
                These sit just outside the lab reference range, but read alongside your related markers
                they are a benign pattern — so they don’t count toward your longevity score.
              </div>
              {contextual.map(f => (
                <LongevityActionCard key={f.marker_key} flag={f} features={features} onNav={onNav} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

// P2 — Body-composition curated card (WELLNESS plane). Fetches the most-recent
// lab-provenance body-composition draw and shows the clinically-meaningful subset
// (capture-all / show-curated, like the fitness abstraction). Renders NOTHING when
// the user has no body-comp data, so a record without it is unchanged. Wellness
// framing, non-diagnostic — "typical" range, never "abnormal". Lab-verified data is
// badged distinctly (NPL violet) from self-reported (kept on its own provenance).
function BodyCompositionCard() {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let live = true;
    api.get('/body-composition/latest')
      .then(d => { if (live) setData(d); })
      .catch(() => { if (live) setData({ has_data: false }); });
    return () => { live = false; };
  }, []);
  if (!data || !data.has_data) return null;
  const curated = data.curated || [];
  const rows = showAll ? (data.all || []) : curated;
  if (rows.length === 0) return null;
  const isLab = data.source === 'lab_verified';
  const fmtRange = (m) => {
    if (m.ref_low == null && m.ref_high == null) return null;
    const lo = m.ref_low != null ? m.ref_low : '';
    const hi = m.ref_high != null ? m.ref_high : '';
    return `typical ${lo}${(m.ref_low != null && m.ref_high != null) ? '–' : ''}${hi}`.trim();
  };
  return (
    <div className="bodycomp-card" data-testid="bodycomp-card" style={{ background: 'var(--secondary-bg,#fff)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{'🧍'}</span>
          <h3 style={{ margin: 0, fontSize: 16 }}>Body Composition</h3>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: isLab ? 'rgba(167,139,250,0.18)' : 'rgba(94,200,216,0.18)', color: isLab ? '#A78BFA' : '#5EC8D8' }}>{isLab ? 'Lab-verified' : 'Self-reported'}</span>
        </div>
        {data.date && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{typeof formatDate === 'function' ? formatDate(data.date) : data.date}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
        {rows.map((m, i) => (
          <div key={i} style={{ padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{displayMarker(m.name || m.label, m.value, m.unit).value}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginLeft: 3 }}>{displayMarker(m.name || m.label, m.value, m.unit).unit}</span></div>
            {fmtRange(m) && <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{fmtRange(m)}</div>}
          </div>
        ))}
      </div>
      {(data.all || []).length > curated.length && (
        <button onClick={() => setShowAll(s => !s)} style={{ marginTop: 10, fontSize: 12, fontWeight: 600, background: 'transparent', border: 'none', color: 'var(--accent,#7F91DF)', cursor: 'pointer', padding: 0 }}>
          {showAll ? 'Show key measures' : `Show all ${(data.all || []).length} measurements`}
        </button>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 10 }}>Bioimpedance body-composition analysis · wellness monitoring, no health-score impact.</div>
    </div>
  );
}

function ClinicalTimeline({ onAskAI, onNav }) {
  const features = useFeatures();
  // Contextual NPL order nudges — only fetched when order_lab is on.
  const orderNudges = useOrderPrompts('health_record');
  const [panels,setPanels]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  // Filter mode for the panel grid:
  //   'default'  \u2014 each panel uses its own auto-expanded-if-flagged behaviour
  //   'all'      \u2014 every panel forced open
  //   'collapsed'\u2014 every panel forced shut
  //   'flagged'  \u2014 only flagged panels open; scrolls to the first flagged item
  // RUN6 §5 — expand ALL panels by default (was 'default', which opened only flagged panels).
  const [filterMode, setFilterMode] = useState('all');
  // ⚠ C2 (KAL-248) — THIS WAS THE ONE PLACE TWO RANGES RENDERED TOGETHER.
  // It read `const showOptimal = true;` with the note "always on now — the broken checkbox is
  // gone", so MarkerSparkline drew the lab reference range AND the longevity target line on the
  // same sparkline. Scherer AM, Zikmund-Fisher BJ, et al. JMIR 2018;20(10):e11027, n=6,776:
  // showing a clinical range and an optimal range simultaneously WORSENED comprehension. The
  // Longevity surface had already made the one-range choice (it passes showOptimal={false});
  // Health Record had not, and nothing connected the two decisions.
  // Under lab_presentation_v2 the optimal line is off here too. Left ON when the flag is off, so
  // this is a reviewable change on master-test rather than a silent fleet-wide edit.
  const showOptimal = !FEATURES_GLOBAL.lab_presentation_v2;
  const [markerDetail, setMarkerDetail] = useState(null);
  const [provenanceSummary, setProvenanceSummary] = useState(null);
  // KAL-322 — retry (2 s / 5 s / 10 s) + inline loading instead of a 20 s dead end.
  const [retrying, setRetrying] = useState(0);
  const loadData=useCallback(async()=>{setLoading(true);setError(null);setRetrying(0);try{const d=await apiGetWithRetry('/clinical-timeline/panels',{label:'health-record',onRetry:setRetrying});if(d.error)throw new Error(d.error);convertPanelsPayload(d);if(d.panels){setPanels(d.panels);setProvenanceSummary(d.provenanceSummary||null)}else{setPanels(d)}}catch(e){setError(e.message)}finally{setLoading(false);setRetrying(0)}},[]);
  useEffect(()=>{loadData()},[loadData]);
  useScreenSync(loadData);   // pull-to-refresh + status strip (Health Record)

  if(loading)return<LoadingInline label="Loading your health record" retrying={retrying}/>;if(error)return<ErrorState message={error} onRetry={loadData}/>;
  const totalFlags=panels.reduce((s,p)=>s+p.flagCount,0);
  const totalMarkers=panels.reduce((s,p)=>s+p.markerCount,0);
  const totalDP=panels.reduce((s,p)=>s+p.markers.reduce((ss,m)=>ss+m.dataPoints,0),0);
  // Map each panel to its forced expansion state. The 'flagged' mode
  // has been retired \u2014 its job (focusing the user on flagged markers)
  // is now handled by the Longevity Flags View, opened via the \u2691
  // button.
  const expansionFor = (p) => {
    if (filterMode === 'all') return true;
    if (filterMode === 'collapsed') return false;
    return null; // 'default' \u2014 panel decides
  };

  return (
    <div className="clinical-timeline">
      <div className="timeline-summary">
        <div>
          <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Health Record</h2>
          <span className="summary-text">{totalMarkers} markers &middot; {totalDP} data points &middot; {panels.length} panels</span>
        </div>
        <div className="summary-actions hr-filter-bar">
          <button
            className={`hr-filter-btn ${filterMode === 'all' ? 'active' : ''}`}
            onClick={() => setFilterMode('all')}
          >Expand All</button>
          <button
            className={`hr-filter-btn ${filterMode === 'collapsed' ? 'active' : ''}`}
            onClick={() => setFilterMode('collapsed')}
          >Collapse All</button>
          <button
            className="hr-filter-btn hr-filter-flag"
            onClick={() => {
              // pushState so browser-back lands on #timeline rather
              // than exiting the app. The App-level useEffect that
              // syncs currentView \u2192 hash uses replaceState and would
              // otherwise leave us with no history entry to pop back
              // to. We push first, then setCurrentView \u2014 the useEffect
              // sees the hash already matches and skips the replace.
              try { window.history.pushState({}, '', '#longevity-flags'); } catch {}
              if (onNav) onNav('longevity-flags');
            }}
            title="Open the Longevity Flags view \u2014 flagged markers with suggested actions"
          >
            <span className="hr-filter-flag-icon">{'\u2691'}</span> Longevity <span className="hr-filter-flag-count">{totalFlags}</span>
          </button>
          {/* Documents (formerly "Medical Vault") is nested here as a section of
              the Health Record. pushState first so browser-back lands on
              #timeline rather than exiting the app (same pattern as the flag
              button above). */}
          <button
            className="hr-filter-btn"
            onClick={() => {
              try { window.history.pushState({}, '', '#vault'); } catch {}
              if (onNav) onNav('vault');
            }}
            title="Documents \u2014 uploaded lab reports, scans and clinical files"
          >
            {'\ud83d\udcc4'} Documents
          </button>
          {/* "Order NPL" button removed \u2014 lab ordering is contextual
              only, surfaced inline under flagged charts via
              OrderPromptCard when trigger conditions are met
              (supplement aged \u22658 weeks, exercise volume, etc.). A
              generic top-of-page order button was never part of the
              agreed design. "Sync NPL Results" stays \u2014 it's a
              status/refresh action, not a sales prompt. */}
          {features.order_lab && <NPLSyncButton />}
        </div>
      </div>
      <ProvenanceCards summary={provenanceSummary} />
      <BodyCompositionCard />
      {panels.map((p) => {
        const chartKey = _panelChartKey(p.name);
        const promptsForPanel = chartKey
          ? orderNudges.prompts.filter(n => n.chart_key === chartKey)
          : [];
        return (
          <div key={p.name}>
            <PanelCard
              panel={p}
              forceExpanded={expansionFor(p)}
              showOptimal={showOptimal}
              onMarkerClick={m=>setMarkerDetail(m)}
            />
            {promptsForPanel.map(np => (
              <OrderPromptCard
                key={np.key}
                prompt={np}
                onDismiss={orderNudges.dismiss}
                onOrder={orderNudges.order}
              />
            ))}
          </div>
        );
      })}
      {markerDetail && <MarkerDetailView marker={markerDetail} onClose={()=>setMarkerDetail(null)} onAskAI={onAskAI} onDeleted={()=>{ setMarkerDetail(null); loadData(); }}/>}
      {/* KAL-235 placement 2 — Health Record, inline where the markers are.
          Guarded on the LEGACY nudge engine being empty for this screen: that engine renders
          per-chart prompts above (App.js:9669), and two order prompts on one screen is exactly
          the upsell feel the anti-spam rule exists to prevent. Cross-engine, so the "at most
          one per screen" property holds however either engine changes. */}
      {orderNudges.prompts.length === 0 && <OrderPanelPrompt placement="health_record" />}
      {orderNudges.confirmingPrompt && (
        <OrderConfirmModal
          prompt={orderNudges.confirmingPrompt}
          onClose={() => orderNudges.setConfirmingPrompt(null)}
          onConfirmed={() => { orderNudges.setConfirmingPrompt(null); orderNudges.refresh(); }}
        />
      )}
    </div>
  );
}

// -- MedicineInfoCard (Phase 2.4) --------------------------------------------
function MedicineInfoCard({ medicineId, medicineName }) {
  const [expanded, setExpanded] = useState(false);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const features = useFeatures(); // genetics flag — suppress pharmacogenomics on NPL
  const loadInfo = async () => {
    if (info) { setExpanded(!expanded); return; }
    setExpanded(true); setLoading(true); setError(null);
    try {
      const data = await api.get(`/medicines/${medicineId}/info`);
      setInfo(data);
    } catch (e) {
      // Never block the medicines page on enrichment failures. Show a
      // grey "Additional information unavailable" line and let the user
      // continue with the local-only view.
      setInfo({ unavailable: true });
    } finally {
      setLoading(false);
    }
  };
  const hasPgx = info && info.pgx && info.pgx.length > 0;
  const hasIx = info && info.interactions && info.interactions.length > 0;
  const hasProfile = info && info.drug_profile && info.drug_profile.found;
  const hasResearch = info && info.supplement_research;
  const hasEstablished = hasResearch && info.supplement_research.established && info.supplement_research.established.length > 0;
  const hasEmerging = hasResearch && info.supplement_research.emerging && info.supplement_research.emerging.length > 0;
  return (<>
    <button className="info-toggle" onClick={loadInfo}>{expanded ? '\u25BC Hide Info' : '\u2139\uFE0F Drug Info'}</button>
    {expanded && <div className="med-info-card">
      {loading && <div className="med-info-loading"><div className="spinner-small"></div> Loading drug information...</div>}
      {error && <div className="med-info-error">{error}</div>}
      {info && info.unavailable && !loading && (
        <div className="med-info-unavailable" style={{color:'#8B6914', fontSize:'0.85rem', padding:'12px 0'}}>
          Additional information unavailable.
        </div>
      )}
      {info && !info.unavailable && <>
        {hasProfile ? <div className="med-info-section">
          <div className="med-info-section-header"><h4>FDA Drug Profile</h4>{info.drug_profile.generic_name&&<span className="med-info-generic">{info.drug_profile.generic_name}</span>}</div>
          {info.drug_profile.searched_as&&<div className="med-info-alias">Matched as: {info.drug_profile.searched_as}</div>}
          {info.drug_profile.indications&&<div className="med-info-field"><span className="med-info-label">Indications</span><p>{info.drug_profile.indications}</p></div>}
          {info.drug_profile.warnings&&<div className="med-info-field med-info-warning"><span className="med-info-label">Warnings</span><p>{info.drug_profile.warnings}</p></div>}
          {info.drug_profile.top_adverse_events&&info.drug_profile.top_adverse_events.length>0&&<div className="med-info-field"><span className="med-info-label">Most Reported Adverse Events</span>
            <div className="med-info-events">{info.drug_profile.top_adverse_events.map((ev,i)=><span key={i} className="med-info-event-tag">{ev.reaction} ({(ev.count||0).toLocaleString('en-GB')})</span>)}</div></div>}
          <div className="med-info-source">Source: OpenFDA Drug Labeling</div>
        </div> : !hasResearch && info.drug_profile && info.category !== 'supplement' && <div className="med-info-section med-info-empty">No FDA drug label found for "{medicineName}".</div>}

        {hasEstablished && <div className="med-info-section med-info-research-established">
          <div className="med-info-section-header"><h4>Clinical Research (Human Trials)</h4><span className="research-badge established">Evidence-Based</span></div>
          {info.supplement_research.established.map((s,i)=><div key={i} className="research-study">
            <div className="research-effect">{autoGlossary(s.effect)}</div>
            <p className="research-evidence">{autoGlossary(s.evidence)}</p>
            <div className="med-info-source">Source: {s.source}</div>
          </div>)}
        </div>}

        {hasEmerging && <div className="med-info-section med-info-research-emerging">
          <div className="med-info-section-header"><h4>Emerging Research</h4><span className="research-badge emerging">Pre-Clinical / Animal Studies</span></div>
          {info.supplement_research.emerging.map((s,i)=><div key={i} className="research-study emerging">
            <div className="research-effect">{s.effect}</div>
            <p className="research-evidence">{s.evidence}</p>
            <div className="med-info-source">Source: {s.source}</div>
          </div>)}
          <div className="research-disclaimer">These findings are from pre-clinical research (animal models, in-vitro studies, or preliminary human trials). Potential benefits have not been confirmed in large-scale human clinical trials and should not be considered proven therapeutic effects. Consult a healthcare professional before making supplement decisions based on emerging research.</div>
        </div>}

        {!hasProfile && !hasResearch && info && (info.category==='supplement'||info.category==='vitamin'||info.category==='mineral') &&
          <div className="med-info-section med-info-empty">No curated research data is currently available for "{medicineName}". This supplement may not yet have sufficient published clinical trials or peer-reviewed studies in our database.</div>}

        {features.genetics && hasPgx && <div className="med-info-section med-info-pgx genetic-surface">
          <div className="med-info-section-header"><h4><GlossaryTerm term="pharmacogenomics">Pharmacogenomics</GlossaryTerm></h4><span className="med-info-pgx-badge">Your Genetics</span></div>
          {info.pgx.map((p,i)=><div key={i} className="pgx-finding">
            <div className="pgx-gene-row"><span className="pgx-gene"><GlossaryTerm term={p.gene.toLowerCase()}>{p.gene}</GlossaryTerm></span><span className="pgx-rsid">{p.rsid}</span><span className="pgx-genotype">{p.genotype}</span></div>
            <div className="pgx-metabolizer"><GlossaryTerm term="metaboliser">Metabolizer Status</GlossaryTerm>: <strong>{p.metabolizer_status}</strong></div>
            {p.drug_guidance&&<div className="pgx-guidance">Research suggests: {autoGlossary(p.drug_guidance)}</div>}
            <div className="med-info-source">Source: {p.source}</div>
          </div>)}
          <div className="med-info-disclaimer">Educational pharmacogenomics information based on published research. Not medical advice.</div>
        </div>}

        {hasIx ? <div className="med-info-section med-info-interactions">
          <div className="med-info-section-header"><h4>Interactions with Your Medicines</h4><span className="med-info-ix-count">{info.interactions.length} found</span></div>
          {info.interactions.map((ix,i)=><div key={i} className="ix-finding"><span className="ix-drug">{ix.drug}</span><p className="ix-context">{autoGlossary(ix.context)}</p><div className="med-info-source">Source: {ix.source}</div></div>)}
        </div> : info && !hasResearch && <div className="med-info-section med-info-empty">No known interactions found with your other active medicines.</div>}

        <div className="med-info-disclaimer-footer">{info.disclaimer}</div>
      </>}
    </div>}
  </>);
}

// -- MedicinesPage -----------------------------------------------------------
// ── KAL-104 — VIEWPORT-AWARE POPUP MENU ──────────────────────────────────────────────────────
// Places a popup menu relative to its trigger and GUARANTEES it lands inside the viewport:
// flips above/below depending on the space actually available, and clamps horizontally.
//
// Why this exists: .ask-attach-menu was `position:absolute; bottom:calc(100% + 8px)` — i.e. it
// always opened UPWARD. Correct for the Ask attach button, which sits at the bottom of the
// screen; wrong for the Medicines "+ Add" button, which sits in .page-header at the TOP, where
// upward means behind the header. Direction cannot be a constant when the same component is
// used at both ends of a screen, and CSS alone cannot measure the space available.
//
// position:fixed rather than absolute so the menu is immune to any ancestor with
// overflow:hidden or a transform (both would clip or re-anchor an absolute menu).
// KAL-378 — the visible area is the VISUAL viewport (the on-screen keyboard shrinks it) minus the
// bottom safe-area inset. `prefer: 'above'` anchors the menu over its trigger (the Ask composer's +
// sits at the bottom of the screen); it flips below only when there is clearly more room there.
function _safeInsetBottom() {
  try {
    const d = document.createElement('div');
    d.style.cssText = 'position:fixed;left:0;bottom:0;width:0;height:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none';
    document.body.appendChild(d);
    const h = d.getBoundingClientRect().height; d.remove();
    return h || 0;
  } catch (e) { return 0; }
}
function useViewportMenu(open, triggerRef, menuRef, { gap = 8, margin = 8, prefer = 'below' } = {}) {
  useLayoutEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const place = () => {
      const t = triggerRef.current, m = menuRef.current;
      if (!t || !m) return;
      const tr = t.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const vTop = vv ? vv.offsetTop : 0;
      const vBottom = vTop + (vv ? vv.height : window.innerHeight) - _safeInsetBottom();
      // Measure the menu at its natural size before deciding.
      m.style.maxHeight = '';
      const mr = m.getBoundingClientRect();
      const spaceAbove = tr.top - vTop - margin;
      const spaceBelow = vBottom - tr.bottom - margin;
      const fitsAbove = mr.height + gap <= spaceAbove, fitsBelow = mr.height + gap <= spaceBelow;
      const below = prefer === 'above' ? (!fitsAbove && (fitsBelow || spaceBelow > spaceAbove))
                                       : (fitsBelow || spaceBelow >= spaceAbove);
      const room = Math.max(120, (below ? spaceBelow : spaceAbove) - gap);
      const h = Math.min(mr.height, room);
      const top = below ? tr.bottom + gap : tr.top - gap - h;
      m.style.maxHeight = room + 'px';
      // Horizontal: align to the trigger, then clamp so neither edge leaves the viewport.
      let left = tr.right - mr.width;                 // right-aligned to the trigger by default
      if (left < margin) left = margin;               // ...but never off the left edge
      if (left + mr.width > vw - margin) left = Math.max(margin, vw - margin - mr.width);
      m.style.top = Math.max(vTop + margin, top) + 'px';
      m.style.left = left + 'px';
      m.style.width = Math.min(mr.width, vw - margin * 2) + 'px';
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    if (vv) { vv.addEventListener('resize', place); vv.addEventListener('scroll', place); }
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      if (vv) { vv.removeEventListener('resize', place); vv.removeEventListener('scroll', place); }
    };
  }, [open, triggerRef, menuRef, gap, margin, prefer]);
}

function MedicinesPage({ onNav }) {
  const [medicines,setMedicines]=useState([]);const [loading,setLoading]=useState(true);const [error,setError]=useState(null);
  const [editingId,setEditingId]=useState(null);const [editForm,setEditForm]=useState({});
  const [showAdd,setShowAdd]=useState(false);const [newMed,setNewMed]=useState({name:'',dosage:'',frequency:'Daily',purpose:'',category:'supplement'});
  // When non-null, the Add form was opened on behalf of another view
  // (currently only #longevity-flags). After save OR cancel the user
  // is bounced back to that view so browser-back doesn't exit the app.
  const [returnToView, setReturnToView] = useState(null);
  const [historyId,setHistoryId]=useState(null);const [history,setHistory]=useState([]);
  const [showInactive,setShowInactive]=useState(false);const [dragIdx,setDragIdx]=useState(null);
  const [suggestions,setSuggestions]=useState([]);const suggestTimer=useRef(null);
  // ── MEDICINE PHOTO CAPTURE ────────────────────────────────────────────────────────────────
  // `photoCards` holds CANDIDATES only. Nothing here is a record until the user confirms an
  // individual card — extraction is a suggestion, and health data must never appear in someone's
  // chart because a model read a blurry box.
  const [photoMenuOpen,setPhotoMenuOpen]=useState(false);
  // KAL-104 — the +Add menu is placed by measurement, not by a fixed CSS direction:
  // this button lives at the TOP of the page, so the old unconditional "open upward"
  // put three of its four options behind the header.
  const addBtnRef = useRef(null);
  const addMenuRef = useRef(null);
  useViewportMenu(photoMenuOpen, addBtnRef, addMenuRef);
  const [photoBusy,setPhotoBusy]=useState(false);
  const [photoCards,setPhotoCards]=useState(null);   // null = no extraction in progress
  const [photoImageRef,setPhotoImageRef]=useState(null);
  const [photoNotice,setPhotoNotice]=useState(null); // {kind:'error'|'info'|'consent', text}

  const extractFromPhoto = async (files) => {
    const f = files && files[0];
    if (!f) return;
    setPhotoBusy(true); setPhotoNotice(null); setPhotoCards(null);
    try {
      const fd = new FormData();
      // ANDROID CAMERA BLOB: a WebView capture often yields a File with a blank name. Supply a
      // fallback here AND server-side; the server decides the real type from magic bytes anyway.
      fd.append('file', f, (f.name && f.name.trim()) ? f.name : 'medicine-photo.jpg');
      const r = await fetch(`${API_BASE}/medicines/photo-extract`, {
        method:'POST', credentials:'include', body: fd });
      const j = await r.json().catch(()=>({}));
      if (r.status === 403 && j.code === 'ai_consent_required') {
        setPhotoNotice({kind:'consent', text:'Reading a photo sends it to our AI provider, which needs your “AI insights” consent. You can turn that on in Data & Privacy — or add this medicine manually below.'});
        return;
      }
      if (!r.ok) {
        // LOUD: never a silent empty result.
        setPhotoNotice({kind:'error', text: j.error || `Extraction failed (${r.status}).`});
        return;
      }
      if (!j.medicines || !j.medicines.length) {
        setPhotoNotice({kind:'info', text: j.message || 'No medicines could be read from that photo.'});
        return;
      }
      setPhotoImageRef(j.image_ref || null);
      setPhotoCards(j.medicines.map(m => ({
        ...m,
        // Pre-filled and EDITABLE. Nulls stay empty rather than being filled with a plausible guess.
        _name: m.name || '', _dosage: m.strength || '', _frequency: m.frequency || 'Daily',
        _form: m.form || '', _purpose: '', _category: 'medication',
        _saved: false, _mode: m.duplicate_of ? 'update' : 'add',
      })));
    } catch (e) {
      setPhotoNotice({kind:'error', text:`Could not read that photo: ${e.message}`});
    } finally { setPhotoBusy(false); }
  };

  const confirmPhotoCard = async (idx) => {
    const c = photoCards[idx];
    if (!c || !c._name.trim()) { setError('A name is required before saving.'); return; }
    try {
      if (c._mode === 'update' && c.duplicate_of) {
        const match = medicines.find(m => (m.name||'').trim().toLowerCase() === c.duplicate_of.trim().toLowerCase());
        if (match) {
          await api.put(`/medicines/${match.id}`, {
            name: c._name, dosage: c._dosage, frequency: c._frequency,
            purpose: c._purpose || match.purpose, category: match.category });
        }
      } else {
        await api.post('/medicines', {
          name: c._name, dosage: c._dosage, frequency: c._frequency,
          purpose: c._purpose, category: c._category,
          source: 'photo_extract', image_ref: photoImageRef });
      }
      setPhotoCards(cs => cs.map((x,i)=> i===idx ? {...x, _saved:true} : x));
      loadMedicines();
    } catch (e) { setError(e.message); }
  };
  const handleDragStart=i=>{setDragIdx(i)};
  const handleDragOver=(e,i)=>{e.preventDefault();if(dragIdx===null||dragIdx===i)return;const r=[...medicines];const[m]=r.splice(dragIdx,1);r.splice(i,0,m);setMedicines(r);setDragIdx(i)};
  const handleDragEnd=()=>{
    setDragIdx(null);
    // Persist new order to DB
    const orderedIds = medicines.map(m => m.id).filter(Boolean);
    if (orderedIds.length > 0) {
      api.post('/medicines/reorder', { ordered_ids: orderedIds }).catch(e => console.error('Reorder save failed:', e));
    }
  };
  const loadMedicines=useCallback(async()=>{setLoading(true);try{const d=await api.get(`/medicines?includeInactive=${showInactive}`);setMedicines(Array.isArray(d)?d:[])}catch(e){setError(e.message)}finally{setLoading(false)}},[showInactive]);
  useEffect(()=>{loadMedicines()},[loadMedicines]);
  useScreenSync(loadMedicines);   // pull-to-refresh + status strip (Medicines)
  // Pre-populated add flow — set by the Longevity Flags View "+ Add to
  // Supplements" buttons via sessionStorage. The payload is a JSON
  // blob { name, returnTo } so the page knows where to send the user
  // back to after save / cancel. Falls back to plain-string for
  // backwards compat with any old in-flight tab.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('add_medicine_prefill');
      if (!raw) return;
      sessionStorage.removeItem('add_medicine_prefill');
      let name = raw, returnTo = null;
      if (raw.startsWith('{')) {
        try {
          const obj = JSON.parse(raw);
          name = obj.name || '';
          returnTo = obj.returnTo || null;
        } catch {}
      }
      if (name) {
        setNewMed(m => ({ ...m, name }));
        setShowAdd(true);
        if (returnTo) setReturnToView(returnTo);
      }
    } catch {}
  }, []);

  // Bounce the user back to whoever sent them here, after the add form
  // closes (save success OR cancel). Clears returnToView so a later
  // independent visit to Medicines doesn't auto-redirect.
  const finishReturn = () => {
    const dest = returnToView;
    setReturnToView(null);
    if (dest && onNav) onNav(dest);
  };
  const handleSave=async id=>{try{await api.put(`/medicines/${id}`,editForm);setEditingId(null);loadMedicines()}catch(e){setError(e.message)}};
  const handleAdd=async()=>{try{await api.post('/medicines',newMed);setShowAdd(false);setNewMed({name:'',dosage:'',frequency:'Daily',purpose:'',category:'supplement'});setSuggestions([]);loadMedicines();finishReturn();}catch(e){setError(e.message)}};
  const handleNameChange=v=>{setNewMed({...newMed,name:v});setSuggestions([]);clearTimeout(suggestTimer.current);if(v.length>=2){suggestTimer.current=setTimeout(async()=>{try{const s=await api.get(`/medicines/suggest?q=${encodeURIComponent(v)}`);setSuggestions(s||[])}catch(e){}},250)}};
  const handleStop=async id=>{try{await api.put(`/medicines/${id}`,{is_active:0});loadMedicines()}catch(e){setError(e.message)}};
  const handleRestart=async id=>{try{await api.put(`/medicines/${id}`,{is_active:1});loadMedicines()}catch(e){setError(e.message)}};
  const viewHistory=async id=>{try{const d=await api.get(`/medicines/${id}/history`);setHistory(d);setHistoryId(id)}catch(e){setError(e.message)}};
  // "Re-parse PDF" REMOVED FROM THE UI (Tim, 10 Aug). It called /api/medicines/reparse with
  // force_reparse=True, which OVERWRITES the medicines list from a GCS PDF — clobbering anything
  // added by hand or by photo capture — and its source is non-deterministic: the server returns on
  // the FIRST blob whose name matches 'medicine'/'supplement' while TWO such PDFs exist for
  // Timothy_Gocher (a baseline and a "Copy of" duplicate), so which one wins depends on blob
  // iteration order. Destructive + non-deterministic = not a button.
  // THE BACKEND ENDPOINT IS DELIBERATELY KEPT (app.py:11259) — unreferenced but callable if a
  // baseline restore is ever needed.
  if(loading)return<LoadingSpinner message="Loading medicines..."/>;
  const hasMedicines = medicines.length > 0 || showInactive;
  return (
    <div className="medicines-list">
      <div className="page-header"><h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Medicines &amp; Supplements</h2>
        <div className="header-actions">
          {hasMedicines && <label className="toggle-label"><input type="checkbox" checked={showInactive} onChange={e=>setShowInactive(e.target.checked)}/>Show stopped</label>}
          {/* ONE "+ Add" entry point (Tim, 10 Aug). Previously "+ Add" opened the manual form
              and a SEPARATE "📷 Add from photo" button opened a photo menu, so the two ways to
              add a medicine were in two different places and the photo menu's "Upload file" was
              indistinguishable from "Photo library" (both accept="image/*"). Now every route in
              is one menu: Camera · Gallery · Upload file · Enter manually.

              ⚠ LOAD-BEARING, DO NOT "SIMPLIFY": each file route is a <label> with the real
              <input type=file> INSIDE it, so the TAP ITSELF activates the input. A native
              WebView's onShowFileChooser fires only from a genuine user gesture on the input;
              a synthetic inputRef.click() never reaches it and the picker dies silently. This
              was verified on-device against com.kalpa.health vCode3 on 10 Aug 2026 —
              onShowFileChooser fired at RNCWebChromeClient.java:336 and the CHOOSER intent
              displayed in 105ms. Keep the pattern. */}
          <div className="med-photo-wrap">
            <button className="action-btn" disabled={photoBusy} ref={addBtnRef}
              onClick={()=>setPhotoMenuOpen(v=>!v)}
              aria-haspopup="menu" aria-expanded={photoMenuOpen}>
              {photoBusy ? 'Reading…' : '+ Add'}
            </button>
            {photoMenuOpen && !photoBusy && (
              <>
                <div className="ask-attach-scrim" onClick={()=>setPhotoMenuOpen(false)} aria-hidden="true" />
                <div className="ask-attach-menu med-photo-menu" role="menu" ref={addMenuRef}>
                  <label className="ask-attach-item" role="menuitem">
                    <span className="ask-attach-ic" aria-hidden="true">📷</span>
                    <span className="ask-attach-lbl">Camera</span>
                    <input type="file" accept="image/*" capture="environment" className="ask-attach-input"
                      onChange={(e)=>{const fs=Array.from(e.target.files||[]); e.target.value=''; setPhotoMenuOpen(false); extractFromPhoto(fs);}} />
                  </label>
                  <label className="ask-attach-item" role="menuitem">
                    <span className="ask-attach-ic" aria-hidden="true">🖼️</span>
                    <span className="ask-attach-lbl">Gallery</span>
                    <input type="file" accept="image/*" className="ask-attach-input"
                      onChange={(e)=>{const fs=Array.from(e.target.files||[]); e.target.value=''; setPhotoMenuOpen(false); extractFromPhoto(fs);}} />
                  </label>
                  {/* Upload file now genuinely differs from Gallery: the server sniffs MAGIC
                      BYTES and accepts PDF as well as JPG/PNG/WEBP (_sniff_medical_file,
                      app.py:5548), so a prescription PDF is a first-class input here. */}
                  <label className="ask-attach-item" role="menuitem">
                    <span className="ask-attach-ic" aria-hidden="true">📄</span>
                    <span className="ask-attach-lbl">Upload file</span>
                    <input type="file" accept="image/*,application/pdf" className="ask-attach-input"
                      onChange={(e)=>{const fs=Array.from(e.target.files||[]); e.target.value=''; setPhotoMenuOpen(false); extractFromPhoto(fs);}} />
                  </label>
                  <button type="button" className="ask-attach-item" role="menuitem"
                    onClick={()=>{setPhotoMenuOpen(false); setShowAdd(true);}}>
                    <span className="ask-attach-ic" aria-hidden="true">✏️</span>
                    <span className="ask-attach-lbl">Enter manually</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Extraction notices — loud, never a silent empty result. */}
      {photoNotice && (
        <div className={`med-photo-notice med-photo-notice-${photoNotice.kind}`}>
          <span>{photoNotice.text}</span>
          <button onClick={()=>setPhotoNotice(null)} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {/* CONFIRM/EDIT CARDS — one per extracted medicine. NOTHING is saved until an explicit
          per-item Confirm. Cancel discards everything. */}
      {photoCards && photoCards.length > 0 && (
        <div className="med-photo-cards">
          <div className="med-photo-cards-head">
            <strong>{photoCards.length} medicine{photoCards.length===1?'':'s'} read from your photo</strong>
            <span>Check each one before saving — nothing is added until you confirm it.</span>
          </div>
          {photoCards.map((c,idx)=>(
            <div key={idx} className={`med-photo-card${c._saved?' saved':''}`}>
              {c.duplicate_of && !c._saved && (
                <div className="med-photo-dupe">
                  <strong>{c.duplicate_of}</strong> is already in your list.
                  <label><input type="radio" name={`m${idx}`} checked={c._mode==='update'}
                    onChange={()=>setPhotoCards(cs=>cs.map((x,i)=>i===idx?{...x,_mode:'update'}:x))}/> Update it</label>
                  <label><input type="radio" name={`m${idx}`} checked={c._mode==='add'}
                    onChange={()=>setPhotoCards(cs=>cs.map((x,i)=>i===idx?{...x,_mode:'add'}:x))}/> Add anyway</label>
                </div>
              )}
              <div className="med-photo-fields">
                <input placeholder="Name" value={c._name} disabled={c._saved}
                  onChange={e=>setPhotoCards(cs=>cs.map((x,i)=>i===idx?{...x,_name:e.target.value}:x))}/>
                <input placeholder="Strength / dosage" value={c._dosage} disabled={c._saved}
                  onChange={e=>setPhotoCards(cs=>cs.map((x,i)=>i===idx?{...x,_dosage:e.target.value}:x))}/>
                <input placeholder="Frequency" value={c._frequency} disabled={c._saved}
                  onChange={e=>setPhotoCards(cs=>cs.map((x,i)=>i===idx?{...x,_frequency:e.target.value}:x))}/>
                <input placeholder="Purpose (optional)" value={c._purpose} disabled={c._saved}
                  onChange={e=>setPhotoCards(cs=>cs.map((x,i)=>i===idx?{...x,_purpose:e.target.value}:x))}/>
              </div>
              {/* Unreadable fields arrive null and are shown as EMPTY, never filled with a
                  plausible-looking value. Say so, so a blank is understood as "not read". */}
              {(!c.strength || !c.frequency) && !c._saved && (
                <div className="med-photo-null">Some fields could not be read from the photo and are blank — fill them in if you know them.</div>
              )}
              <div className="med-photo-actions">
                {c._saved
                  ? <span className="med-photo-saved">✓ {c._mode==='update'?'Updated':'Added'}</span>
                  : <button className="action-btn" onClick={()=>confirmPhotoCard(idx)}>
                      {c._mode==='update'?'Confirm update':'Confirm & add'}
                    </button>}
              </div>
            </div>
          ))}
          <button className="action-btn secondary"
            onClick={()=>{setPhotoCards(null); setPhotoImageRef(null);}}>
            {photoCards.every(c=>c._saved) ? 'Done' : 'Cancel — save nothing'}
          </button>
        </div>
      )}
      {error&&<div className="error-banner">{error}<button onClick={()=>setError(null)}>&times;</button></div>}
      {!hasMedicines && !showAdd && (
        <div style={{textAlign:'center',padding:'48px 24px',color:'var(--text-secondary)'}}>
          <div style={{fontSize:'48px',marginBottom:'16px',opacity:0.5}}>&#128138;</div>
          <h3 style={{color:'var(--text-primary)',marginBottom:'8px'}}>No medicines or supplements added yet</h3>
          <p style={{fontSize:'14px',maxWidth:'400px',margin:'0 auto 16px'}}>Track your medicines and supplements to get interaction checks and pharmacogenomics insights.</p>
          <button className="action-btn" onClick={()=>setShowAdd(true)}>+ Add Medicine</button>
        </div>
      )}
      {showAdd&&(<div className="medicine-form"><h3>Add Medicine / Supplement</h3><div className="form-grid">
        <div className="med-autocomplete">
          <input placeholder="Name" value={newMed.name} onChange={e=>handleNameChange(e.target.value)} autoComplete="off"/>
          {suggestions.length>0&&<div className="med-suggestions">{suggestions.map((s,i)=><div key={i} className="med-suggestion" onClick={()=>{setNewMed({...newMed,name:s});setSuggestions([])}}>{s}</div>)}</div>}
        </div>
        <input placeholder="Dosage" value={newMed.dosage} onChange={e=>setNewMed({...newMed,dosage:e.target.value})}/>
        <select value={newMed.frequency} onChange={e=>setNewMed({...newMed,frequency:e.target.value})}><option>Daily</option><option>Twice Daily</option><option>Morning</option><option>Evening</option><option>Weekly</option><option>As Needed</option></select>
        <select value={newMed.category} onChange={e=>setNewMed({...newMed,category:e.target.value})}><option value="supplement">Supplement</option><option value="medication">Medication</option><option value="vitamin">Vitamin</option><option value="mineral">Mineral</option></select>
        <input placeholder="Purpose" className="span-2" value={newMed.purpose} onChange={e=>setNewMed({...newMed,purpose:e.target.value})}/></div>
        <div className="form-actions"><button className="action-btn" onClick={handleAdd}>Save</button><button className="action-btn secondary" onClick={()=>{setShowAdd(false);finishReturn();}}>Cancel</button></div></div>)}
      <div className="medicine-items">{medicines.length===0?<div className="empty-state"><p>No medicines found.</p></div>:
        medicines.map((med,idx)=>(
          <div key={med.id||med.name} className={`medicine-card ${med.is_active===0?'inactive':''} ${dragIdx===idx?'dragging':''}`} draggable onDragStart={()=>handleDragStart(idx)} onDragOver={e=>handleDragOver(e,idx)} onDragEnd={handleDragEnd}>
            {editingId===med.id?<div className="medicine-edit">
              <input value={editForm.name||''} onChange={e=>setEditForm({...editForm,name:e.target.value})}/>
              <input value={editForm.dosage||''} onChange={e=>setEditForm({...editForm,dosage:e.target.value})} placeholder="Dosage"/>
              <input value={editForm.frequency||''} onChange={e=>setEditForm({...editForm,frequency:e.target.value})} placeholder="Frequency"/>
              <input value={editForm.purpose||''} onChange={e=>setEditForm({...editForm,purpose:e.target.value})} placeholder="Purpose"/>
              <div className="form-actions"><button className="action-btn small" onClick={()=>handleSave(med.id)}>Save</button><button className="action-btn small secondary" onClick={()=>setEditingId(null)}>Cancel</button></div>
            </div>:<>
              <div className="medicine-card-header"><h3>{med.name}</h3>{med.is_active===0&&<span className="status-badge stopped">Stopped</span>}{med.category&&<span className="category-badge">{med.category}</span>}</div>
              <div className="medicine-details">{med.dosage&&<span className="dosage">{med.dosage}</span>}{med.frequency&&<span className="frequency">{med.frequency}</span>}</div>
              {med.purpose&&<p className="purpose">{med.purpose}</p>}
              <div className="medicine-actions">
                <button onClick={()=>{setEditingId(med.id);setEditForm({name:med.name,dosage:med.dosage,frequency:med.frequency,purpose:med.purpose})}}>Edit</button>
                {med.is_active!==0?<button className="stop" onClick={()=>handleStop(med.id)}>Stop</button>:<button className="restart" onClick={()=>handleRestart(med.id)}>Restart</button>}
                <button onClick={()=>viewHistory(med.id)}>History</button>
              </div>
              {med.id&&<MedicineInfoCard medicineId={med.id} medicineName={med.name}/>}
            </>}
          </div>))}</div>
      {historyId&&<div className="modal-overlay" onClick={()=>setHistoryId(null)}><div className="modal" onClick={e=>e.stopPropagation()}>
        <h3>Edit History</h3>{history.length===0?<p>No history</p>:<div className="history-list">{history.map((h,i)=><div key={i} className="history-entry"><div className="history-action">{h.action}</div><div className="history-date">{formatDateTime(h.changed_at)}</div><div className="history-by">{h.changed_by}</div></div>)}</div>}
        <button className="action-btn" onClick={()=>setHistoryId(null)}>Close</button></div></div>}
    </div>
  );
}

// -- PathwayDiagrams (Genome Pathways tab) — personal insight cards -----------
const IMPACT_COLORS = { favorable: '#22c55e', moderate: '#f59e0b', unfavorable: '#ef4444', unknown: '#8B6914' };
const IMPACT_LABELS = { favorable: 'Favourable', moderate: 'Moderate', unfavorable: 'Needs attention', unknown: 'Not tested' };
const IMPACT_ICONS = { favorable: '\u2713', moderate: '\u25CB', unfavorable: '\u25B3', unknown: '\u2014' };
const IMPACT_GLOW = { favorable: '0 0 8px rgba(34,197,94,0.4)', moderate: '0 0 8px rgba(245,158,11,0.4)', unfavorable: '0 0 8px rgba(239,68,68,0.4)', unknown: 'none' };

// Plain-English titles and summaries for each pathway
const PATHWAY_FRIENDLY = {
  methylation: { title: 'Folate & B-Vitamin Processing', icon: '\uD83E\uDDEC',
    why: 'This pathway converts dietary folate into its active form and recycles homocysteine, a molecule that damages arteries when elevated. Your MTHFR and MTR gene variants determine how efficiently this works.' },
  cardiovascular: { title: 'Heart & Cholesterol Health', icon: '\u2764\uFE0F',
    why: 'These genes control how your body handles cholesterol \u2014 from clearing LDL to protecting arteries. APOE is the single most important gene for lipid metabolism and Alzheimer\'s risk.' },
  drug_metabolism: { title: 'How You Process Medications', icon: '\uD83D\uDC8A',
    why: 'Your liver enzymes (CYP family) break down most medications. Variants in these genes can make you metabolise drugs faster or slower than average, affecting both effectiveness and side effects.' },
  inflammation: { title: 'Inflammation & Longevity Balance', icon: '\uD83D\uDEE1\uFE0F',
    why: 'Ageing is driven by chronic low-grade inflammation. FOXO3 \u2014 the most replicated longevity gene \u2014 activates repair pathways that counteract inflammatory damage. The balance between these forces affects how fast you age.' },
  vitamin_d: { title: 'Vitamin D Activation', icon: '\u2600\uFE0F',
    why: 'Vitamin D must be activated through two conversion steps before your cells can use it. Gene variants at each step determine whether you need more sun, supplements, or higher doses to maintain optimal levels.' },
};

function generatePathwaySummary(pw) {
  const variants = Object.values(pw.variants);
  const withData = variants.filter(v => v.genotype);
  const favorable = withData.filter(v => v.impact === 'favorable').length;
  const moderate = withData.filter(v => v.impact === 'moderate').length;
  const unfavorable = withData.filter(v => v.impact === 'unfavorable').length;
  const noData = variants.length - withData.length;

  // Build per-variant action items
  const actions = [];
  for (const v of variants) {
    if (v.impact === 'moderate' || v.impact === 'unfavorable') {
      // Extract actionable advice from the variant text
      const text = v.text || '';
      const consider = text.match(/consider\s+(.+?)(?:\.|$)/i);
      const mayNeed = text.match(/may need\s+(.+?)(?:\.|$)/i);
      const avoid = text.match(/avoid\s+(.+?)(?:\.|$)/i);
      if (consider) actions.push(consider[1].trim());
      else if (mayNeed) actions.push(mayNeed[1].trim());
      else if (avoid) actions.push('Avoid ' + avoid[1].trim());
    }
  }

  let summary;
  if (withData.length === 0) {
    summary = 'We don\'t have genetic data for this pathway yet. Upload VCF or MyHeritage data to see your results.';
  } else if (unfavorable > 0) {
    summary = `${unfavorable} of your ${withData.length} tested variants ${unfavorable === 1 ? 'needs' : 'need'} attention. ${moderate > 0 ? `${moderate} ${moderate===1?'is':'are'} moderate.` : ''} This may affect how this pathway functions for you.`;
  } else if (moderate > 0) {
    summary = `Your results are mostly good, with ${moderate} moderate ${moderate===1?'variant':'variants'}. No major concerns, but worth monitoring.`;
  } else {
    summary = `All ${favorable} tested ${favorable===1?'variant':'variants'} ${favorable===1?'is':'are'} favourable. This pathway appears to work well for you.`;
  }
  if (noData > 0 && withData.length > 0) {
    summary += ` (${noData} ${noData===1?'variant':'variants'} not in your data.)`;
  }

  return { summary, actions };
}

function PathwayDiagrams() {
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [showDiagram, setShowDiagram] = useState({});

  useEffect(() => {
    api.get('/genetics/pathways').then(d => { setPathways(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner message="Loading your genetic pathways..." />;
  if (!pathways || pathways.length === 0) return <div className="genome-empty">{T('No pathway data available. Upload genetic data to see personalised pathway insights.')}</div>;

  const toggleCard = id => setExpandedCards(prev => ({...prev, [id]: !prev[id]}));
  const toggleDiagram = id => setShowDiagram(prev => ({...prev, [id]: !prev[id]}));

  const SVG_WIDTH = 720;
  const SVG_HEIGHT = 260;

  return (
    <div className="pathway-diagrams">
      <div className="pathway-intro">
        <p>{T('Your DNA variants mapped to 5 key biological pathways. Each card shows how your genetics may affect that system, with personalised insights and suggestions.')}</p>
      </div>
      <div className="pathway-legend" style={{marginBottom:'16px'}}>
        {[['favorable','Favourable'],['moderate','Moderate'],['unfavorable','Needs attention'],['unknown','Not tested']].map(([k,l])=>(
          <span key={k} className="pathway-legend-item">
            <span className="pathway-legend-dot" style={{backgroundColor:IMPACT_COLORS[k]}}/>{l}
          </span>
        ))}
      </div>

      {pathways.map(pw => {
        const friendly = PATHWAY_FRIENDLY[pw.id] || { title: pw.name, icon: '\uD83E\uDDEC', why: pw.description };
        const isExpanded = expandedCards[pw.id];
        const isDiagramOpen = showDiagram[pw.id];
        const { summary, actions } = generatePathwaySummary(pw);
        const variants = Object.entries(pw.variants);
        const overallImpacts = variants.filter(([,v]) => v.genotype).map(([,v]) => v.impact);
        const overallColor = overallImpacts.includes('unfavorable') ? IMPACT_COLORS.unfavorable
          : overallImpacts.includes('moderate') ? IMPACT_COLORS.moderate
          : overallImpacts.some(i => i === 'favorable') ? IMPACT_COLORS.favorable
          : IMPACT_COLORS.unknown;

        return (
          <div key={pw.id} className={`pathway-card ${isExpanded ? 'expanded' : ''}`}>
            <div className="pathway-header" onClick={() => toggleCard(pw.id)}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',flex:1}}>
                <span style={{fontSize:'1.4em'}}>{friendly.icon}</span>
                <div>
                  <h3 className="pathway-title">{friendly.title}</h3>
                  {!isExpanded && <p className="pathway-desc" style={{margin:0,fontSize:'0.78em',color:'var(--text-secondary)',maxWidth:'500px'}}>{summary.slice(0, 80)}{summary.length > 80 ? '...' : ''}</p>}
                </div>
              </div>
              <div className="pathway-summary">
                {variants.map(([rsid, v]) => (
                  <span key={rsid} className="pathway-variant-dot" style={{backgroundColor: IMPACT_COLORS[v.impact]}} title={`${v.gene}: ${v.genotype || 'no data'}`}/>
                ))}
                <span className="pathway-expand">{isExpanded ? '\u25B2' : '\u25BC'}</span>
              </div>
            </div>

            {isExpanded && (
              <div className="pathway-body">
                {/* Why this matters */}
                <div className="pathway-why">
                  <strong>Why this matters:</strong> {autoGlossary(friendly.why)}
                </div>

                {/* Your variants — traffic light list */}
                <div className="pathway-variants-list">
                  <h4 style={{margin:'0 0 8px',fontSize:'0.85em',color:'var(--text-secondary)'}}>Your Variants</h4>
                  {variants.map(([rsid, v]) => (
                    <div key={rsid} className="pathway-variant-row" style={{borderLeftColor: IMPACT_COLORS[v.impact]}}>
                      <span className="pv-icon" style={{color: IMPACT_COLORS[v.impact]}}>{IMPACT_ICONS[v.impact]}</span>
                      <div className="pv-info">
                        <div className="pv-gene-line">
                          <GlossaryTerm term={v.gene.toLowerCase()}><strong>{v.gene}</strong></GlossaryTerm>
                          <span className="pv-variant-name">{v.name}</span>
                          <span className="pv-genotype-badge" style={{backgroundColor: IMPACT_COLORS[v.impact] + '22', color: IMPACT_COLORS[v.impact]}}>
                            {v.genotype || 'No data'}
                          </span>
                        </div>
                        <div className="pv-explanation">{autoGlossary(v.text)}</div>
                        {v.source && <span className="pv-source">Source: {v.source}</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Personal summary */}
                <div className="pathway-personal-summary" style={{borderLeftColor: overallColor}}>
                  <h4>What this means for you</h4>
                  <p>{summary}</p>
                  {actions.length > 0 && (
                    <div className="pathway-actions">
                      <strong>Consider:</strong>
                      <ul>{actions.map((a, i) => <li key={i}>{autoGlossary(a)}</li>)}</ul>
                    </div>
                  )}
                </div>

                {/* Expandable SVG diagram */}
                <button className="pathway-diagram-toggle" onClick={(e) => { e.stopPropagation(); toggleDiagram(pw.id); }}>
                  {isDiagramOpen ? '\u25B2 Hide' : '\u25BC Show'} biochemical pathway diagram
                </button>
                {isDiagramOpen && (
                  <div className="pathway-diagram-wrapper">
                    <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} className="pathway-svg">
                      <defs>
                        <marker id={`arrow-${pw.id}`} markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#555555"/></marker>
                        <marker id={`arrow-inh-${pw.id}`} markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#ef4444" opacity="0.5"/></marker>
                      </defs>
                      {pw.edges.map((e, i) => {
                        const from = pw.nodes.find(n => n.id === e.from);
                        const to = pw.nodes.find(n => n.id === e.to);
                        if (!from || !to) return null;
                        const isInhibit = e.type === 'inhibits';
                        const isCatalyze = e.type === 'catalyzes';
                        return (
                          <g key={i}>
                            <line x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                              stroke={isInhibit ? '#ef4444' : '#555555'} strokeWidth={isCatalyze ? 1 : 1.5}
                              strokeDasharray={isCatalyze ? '4,3' : isInhibit ? '2,4' : 'none'}
                              opacity={isInhibit ? 0.5 : 0.6}
                              markerEnd={`url(#${isInhibit ? `arrow-inh-${pw.id}` : `arrow-${pw.id}`})`}/>
                            {e.label && <text x={(from.x+to.x)/2} y={(from.y+to.y)/2-6} fill="#8B6914" fontSize="8" textAnchor="middle" fontStyle="italic">{e.label}</text>}
                          </g>
                        );
                      })}
                      {pw.nodes.map(node => {
                        const isGene = node.type === 'gene';
                        const nodeImpact = isGene ? (() => { const n = node; if (!n.rsids) return 'unknown'; const imps = n.rsids.map(rs => pw.variants[rs]?.impact||'unknown'); return imps.includes('unfavorable')?'unfavorable':imps.includes('moderate')?'moderate':imps.includes('favorable')?'favorable':'unknown'; })() : null;
                        const color = isGene ? IMPACT_COLORS[nodeImpact] : '#8B6914';
                        return (
                          <g key={node.id}>
                            {isGene && nodeImpact !== 'unknown' && <circle cx={node.x} cy={node.y} r={22} fill={color} opacity={0.12}/>}
                            {isGene ? (
                              <rect x={node.x-32} y={node.y-12} width={64} height={24} rx={4} fill="#F8F7F4" stroke={color} strokeWidth={1.5}
                                style={nodeImpact!=='unknown'?{filter:`drop-shadow(${IMPACT_GLOW[nodeImpact]})`}:undefined}/>
                            ) : (
                              <rect x={node.x-38} y={node.y-10} width={76} height={20} rx={10} fill="#F8F7F4" stroke="#555555" strokeWidth={0.5}/>
                            )}
                            <text x={node.x} y={node.y+4} fill={isGene?color:'#8B6914'} fontSize={isGene?10:8} textAnchor="middle" fontWeight={isGene?700:400}>{node.label}</text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                )}

                <div className="pathway-disclaimer">
                  These are educational wellness insights based on published research, not medical advice. Discuss medication changes with your doctor.
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// -- GenomePage (My Genome panel — 3 tabs) ------------------------------------

const WELLNESS_CATEGORIES = {
  metabolism_weight: { label: 'Metabolism & Weight', icon: '\u2696\uFE0F' },
  cardiovascular: { label: 'Heart & Circulation', icon: '\u2764\uFE0F' },
  inflammation_immune: { label: 'Inflammation & Immunity', icon: '\uD83D\uDEE1\uFE0F' },
  medication_pgx: { label: 'Medication Response', icon: '\uD83D\uDC8A' },
  sleep_circadian: { label: 'Sleep & Circadian', icon: '\uD83C\uDF19' },
  nutrient_processing: { label: 'Nutrients & Vitamins', icon: '\uD83E\uDD66' },
};

const BODY_REGIONS = {
  brain: { label: 'Brain & Neurology', categories: ['sleep_circadian'] },
  heart: { label: 'Heart & Circulation', categories: ['cardiovascular'] },
  liver: { label: 'Liver & Metabolism', categories: ['medication_pgx'] },
  gut: { label: 'Gut & Immunity', categories: ['inflammation_immune', 'metabolism_weight', 'nutrient_processing'] },
  muscle: { label: 'Muscle & Bone', categories: ['metabolism_weight'] },
};

// Professional body outline paths from react-native-body-highlighter (MIT license, HichamELBSI)
// Male front outline — viewBox 0 0 724 1448
const MALE_BODY_PATH = "M 309.48 168.91 Q 305.84 164.32 303.32 169.76 C 298.49 180.21 308.31 200.03 314.51 208.74 C 316.34 211.31 318.01 208.95 318.58 207.26 A 0.67 0.66 57.6 0 1 319.87 207.55 C 319.06 215.09 318.68 227.40 324.34 232.47 C 327.22 235.05 326.97 235.88 326.92 239.51 Q 326.68 255.16 323.97 266.82 Q 323.85 267.35 323.48 267.73 Q 308.61 282.73 290.26 293.23 C 278.34 300.05 267.53 299.26 253.00 298.03 Q 237.49 296.72 224.74 305.21 C 208.71 315.86 190.95 335.73 189.24 355.50 Q 186.95 381.81 190.53 412.66 C 190.79 414.92 190.69 417.49 191.02 419.92 Q 191.09 420.43 190.88 420.90 C 187.89 427.65 183.99 434.89 181.93 441.29 C 177.25 455.76 176.31 470.23 176.20 486.02 Q 176.20 486.51 175.90 486.90 C 159.84 507.69 147.56 529.29 141.49 554.95 Q 140.10 560.80 138.16 574.66 Q 131.28 623.74 118.11 671.52 C 115.99 679.21 112.98 690.29 104.08 693.63 Q 90.70 698.65 79.29 707.27 C 73.17 711.89 69.48 719.95 66.12 726.62 C 62.44 733.91 47.57 737.30 49.20 746.00 C 49.75 748.96 51.89 750.13 54.75 750.02 Q 67.27 749.50 74.18 740.00 C 76.03 737.45 77.93 736.62 80.54 735.24 Q 81.02 734.98 81.24 735.48 Q 84.59 743.00 80.47 750.73 Q 71.41 767.75 62.21 784.70 Q 60.53 787.81 59.49 791.20 C 57.52 797.69 65.78 800.84 69.45 795.20 C 76.80 783.92 82.72 773.30 92.55 762.52 Q 93.00 762.04 92.84 762.67 Q 87.89 783.24 79.07 802.44 C 77.36 806.17 75.64 812.30 79.19 815.18 C 89.50 823.53 107.08 773.44 109.24 767.88 A 0.37 0.36 -30.3 0 1 109.94 768.06 C 108.51 777.44 106.43 787.14 105.28 796.13 C 104.34 803.43 103.67 808.49 104.41 814.32 C 105.40 822.00 112.74 817.15 114.09 812.77 C 118.56 798.32 120.41 781.74 125.18 766.21 A 0.55 0.55 0.0 0 1 125.93 765.87 C 131.64 768.40 126.65 796.54 133.38 803.49 A 1.35 1.35 0.0 0 0 134.16 803.90 C 138.40 804.59 139.71 797.34 140.15 793.73 Q 141.74 780.80 142.58 767.76 Q 142.86 763.46 144.07 759.34 Q 150.39 737.64 154.77 715.46 Q 156.15 708.50 155.48 697.76 Q 154.48 681.63 161.99 665.46 Q 180.58 625.46 201.25 586.52 C 213.64 563.18 218.66 541.14 220.65 514.18 C 221.24 506.18 223.22 502.59 228.42 495.84 C 237.76 483.72 242.73 464.92 246.12 450.19 Q 246.24 449.64 246.75 449.42 L 250.30 447.82 A 0.49 0.49 0.0 0 1 250.99 448.23 Q 252.78 470.14 257.44 487.01 C 259.04 492.80 264.20 498.21 265.32 505.20 C 265.91 508.82 266.99 512.44 267.11 516.00 Q 267.57 529.33 266.95 540.50 C 265.58 565.32 263.85 592.20 259.98 619.13 C 258.39 630.19 253.14 640.55 250.52 651.43 Q 245.19 673.62 242.32 696.24 C 239.63 717.56 236.59 740.02 236.04 757.75 Q 234.98 791.48 237.98 842.55 Q 239.43 867.18 244.64 891.26 Q 247.76 905.70 255.88 917.90 Q 256.15 918.31 256.08 918.79 C 254.89 926.25 257.03 933.47 255.60 940.95 Q 252.28 958.32 251.77 975.98 C 251.55 983.43 252.85 991.28 253.67 998.93 Q 253.99 1001.95 253.29 1005.00 C 239.19 1067.03 246.93 1130.64 261.77 1190.07 C 266.01 1207.06 266.47 1222.37 264.71 1240.03 C 263.85 1248.62 262.10 1260.41 264.24 1268.75 C 266.05 1275.80 267.54 1287.46 261.78 1293.28 C 256.71 1298.39 242.40 1310.55 240.72 1316.98 C 239.19 1322.86 235.04 1332.26 242.29 1333.71 Q 242.69 1333.79 243.08 1333.66 L 244.23 1333.29 Q 245.05 1333.02 244.81 1333.85 C 242.95 1340.16 249.20 1340.52 253.77 1340.86 C 256.46 1341.06 257.37 1343.60 259.30 1344.71 Q 263.13 1346.91 267.14 1344.43 Q 267.59 1344.15 267.92 1344.56 Q 271.17 1348.61 276.21 1349.09 C 278.90 1349.35 281.27 1347.36 283.62 1346.09 Q 284.10 1345.82 284.44 1346.26 Q 288.33 1351.29 294.72 1351.38 C 295.77 1351.39 297.65 1351.62 298.54 1350.79 Q 301.20 1348.30 306.57 1341.58 C 312.04 1334.74 311.14 1328.85 310.29 1320.16 C 309.43 1311.33 311.17 1303.41 313.76 1295.20 C 315.84 1288.56 313.35 1280.06 314.07 1273.15 C 314.57 1268.39 315.80 1263.68 315.01 1259.02 C 314.06 1253.42 311.98 1247.60 311.31 1242.66 Q 309.57 1229.80 309.57 1219.75 Q 309.57 1192.29 313.54 1161.94 C 315.34 1148.21 319.24 1136.08 324.12 1123.46 Q 325.66 1119.48 326.10 1115.72 C 330.14 1081.34 326.20 1048.44 320.65 1013.26 C 319.84 1008.17 319.39 1002.54 321.72 997.72 C 328.03 984.68 329.28 969.38 329.07 954.15 C 329.01 949.50 327.95 944.55 327.58 939.63 C 327.13 933.64 329.28 925.78 330.82 919.80 C 334.72 904.69 337.76 888.96 341.43 874.30 Q 348.95 844.25 355.42 813.95 C 358.50 799.49 357.70 784.78 357.75 768.06 Q 357.78 756.80 356.36 748.81 Q 356.26 748.24 356.77 748.50 L 363.71 751.99 A 1.07 1.07 0.0 0 0 364.67 751.99 L 371.53 748.56 Q 372.07 748.29 371.98 748.89 C 369.47 765.94 370.28 783.04 371.30 800.17 Q 371.86 809.54 372.73 813.51 C 378.37 839.12 384.90 864.49 390.59 890.08 Q 394.83 909.20 399.51 928.22 C 400.58 932.58 401.13 937.66 400.58 941.57 C 398.11 958.92 398.53 982.22 407.11 998.54 C 408.41 1001.01 408.74 1005.35 408.31 1008.09 C 402.82 1043.75 398.07 1079.22 402.19 1115.33 Q 402.65 1119.34 404.21 1123.44 C 410.53 1140.06 413.55 1150.61 415.25 1164.75 C 418.31 1190.26 420.52 1218.43 416.79 1244.33 C 415.56 1252.86 411.78 1258.57 413.63 1267.80 Q 415.33 1276.21 414.16 1284.74 C 413.11 1292.39 415.65 1298.68 417.31 1305.89 C 419.02 1313.32 418.11 1320.99 417.47 1328.50 C 416.71 1337.55 423.74 1344.86 430.17 1350.90 A 1.48 1.46 -18.7 0 0 430.95 1351.28 Q 439.25 1352.41 444.03 1346.06 Q 444.40 1345.57 444.87 1345.96 Q 453.39 1352.89 460.49 1344.48 Q 460.81 1344.11 461.23 1344.37 C 469.09 1349.37 469.89 1340.80 474.98 1340.71 C 479.52 1340.64 485.21 1340.09 483.54 1333.77 Q 483.38 1333.17 483.97 1333.35 C 488.25 1334.67 490.66 1331.94 490.06 1327.75 C 489.09 1321.04 487.50 1314.41 483.44 1310.30 Q 474.77 1301.53 466.05 1292.83 C 461.19 1287.98 462.25 1276.40 463.74 1270.47 C 466.27 1260.35 464.49 1248.06 463.03 1236.25 C 461.04 1220.05 463.22 1204.28 467.41 1187.04 C 481.60 1128.60 488.89 1065.20 475.23 1006.07 C 473.92 1000.37 475.00 995.00 475.76 989.36 C 477.88 973.68 475.72 958.50 473.08 942.76 C 471.70 934.55 473.60 926.56 472.20 918.79 Q 472.11 918.30 472.39 917.89 C 483.07 902.63 486.53 880.99 488.49 863.25 C 492.12 830.38 492.47 797.34 492.26 764.31 C 492.11 741.56 488.80 719.07 486.12 696.53 C 484.30 681.19 480.76 664.32 477.47 649.99 C 474.89 638.73 469.69 628.87 468.04 617.25 C 465.37 598.45 464.19 580.92 462.40 556.31 Q 460.86 535.06 461.01 522.74 Q 461.13 512.05 463.22 504.00 C 464.54 498.90 468.30 493.91 469.91 489.46 C 474.50 476.74 476.10 461.71 477.56 448.28 Q 477.62 447.74 478.13 447.94 L 481.73 449.35 A 0.77 0.77 0.0 0 1 482.19 449.89 Q 486.03 466.84 492.52 482.96 C 494.16 487.04 496.63 491.75 500.12 495.79 C 505.75 502.32 507.17 507.95 508.00 517.24 C 509.72 536.47 512.15 552.06 518.89 569.24 Q 521.60 576.16 527.50 587.28 Q 543.57 617.60 558.56 648.47 C 566.04 663.89 571.90 675.54 572.85 690.59 Q 572.98 692.57 572.55 700.88 Q 572.12 709.31 573.99 718.25 Q 577.87 736.78 582.37 752.38 C 585.15 761.98 586.32 769.32 586.71 778.53 C 586.92 783.46 587.58 803.53 593.41 804.06 C 599.41 804.61 599.71 774.61 600.39 768.08 A 1.12 1.12 0.0 0 1 600.80 767.33 Q 601.30 766.93 601.62 766.30 A 1.39 1.00 59.0 0 1 603.70 767.19 C 607.27 782.50 609.43 797.55 614.25 812.25 C 615.52 816.12 618.33 820.08 622.81 817.38 A 1.18 1.17 -8.4 0 0 623.35 816.66 Q 624.98 810.32 624.13 803.72 Q 621.83 785.89 618.23 768.64 A 0.53 0.53 0.0 0 1 619.24 768.34 C 622.72 777.06 636.06 814.20 645.24 816.03 C 650.64 817.10 652.13 811.12 650.95 807.31 C 648.59 799.74 644.42 791.59 642.09 784.69 Q 638.29 773.46 635.22 761.98 A 0.15 0.14 -73.3 0 1 635.47 761.84 Q 640.35 767.61 644.90 773.66 C 649.45 779.70 653.60 787.18 658.03 793.93 Q 660.09 797.07 661.70 797.82 C 665.53 799.62 670.61 795.77 669.00 791.28 C 666.63 784.66 661.63 776.66 659.33 772.19 Q 654.22 762.29 648.82 752.53 C 645.43 746.40 644.71 741.93 646.89 735.59 Q 647.08 735.05 647.60 735.27 C 650.55 736.50 652.37 737.45 654.44 740.27 Q 661.27 749.61 673.53 749.92 C 681.25 750.12 680.47 740.89 676.20 738.28 C 671.33 735.31 664.61 731.14 661.97 725.94 C 657.98 718.11 654.62 711.26 649.21 707.28 Q 637.40 698.62 623.76 693.40 C 619.45 691.75 615.12 686.26 613.76 682.47 Q 608.42 667.65 602.70 641.81 Q 594.90 606.62 590.85 578.90 Q 588.46 562.58 587.74 559.15 C 582.02 531.75 569.74 509.81 552.98 487.61 C 551.81 486.06 551.91 485.12 551.97 483.26 Q 552.48 466.57 548.70 449.61 C 546.27 438.71 541.82 430.32 537.44 420.82 Q 537.22 420.36 537.28 419.85 C 539.40 398.94 540.83 377.68 539.05 356.70 C 537.31 336.13 521.34 317.28 504.86 306.23 C 494.75 299.45 485.77 296.97 473.93 298.16 Q 464.41 299.12 453.63 298.41 C 438.05 297.39 418.32 280.58 407.40 270.35 C 405.82 268.87 404.57 267.56 404.10 265.32 Q 401.24 251.68 401.26 237.76 Q 401.26 233.73 404.68 232.04 Q 405.14 231.82 405.39 231.38 C 409.76 223.86 408.77 215.16 408.75 206.85 A 0.38 0.38 0.0 0 1 409.48 206.69 C 410.36 208.62 412.01 211.62 414.22 208.45 C 421.05 198.67 427.45 183.93 425.97 172.00 C 425.49 168.15 422.83 165.91 418.91 167.68 Z";
// Female front outline — viewBox -50 -40 734 1538
const FEMALE_BODY_PATH = "m 373.53,205.88 c -0.45333,0.42667 -0.81,0.91667 -1.07,1.47 -3.29333,7.06 -7.28,13.70667 -11.96,19.94 -2.14,2.84667 -4.41,5.19333 -6.81,7.04 -0.36158,0.27621 -0.53584,0.73316 -0.45,1.18 0.43333,2.3 0.96,4.58667 1.58,6.86 7,25.46 37.78,29.15 59.68,31.39 6.10667,0.62 12.19667,1.61 18.27,2.97 7.96,1.77333 14.17,3.97667 18.63,6.61 14.63,8.62 23.15,23.99 26.24,40.5 3.70667,19.74 5.03,39.49 3.97,59.25 -0.0526,0.96072 0.11501,1.92175 0.49,2.81 1.62,3.77333 3.20667,7.65 4.76,11.63 6.18,15.8 10.13333,32.57333 11.86,50.32 1.18,12.12 1.68,23.92667 1.5,35.42 -0.02,1.29333 0.40333,2.43333 1.27,3.42 17.89,20.25 33.57,44.36 41.14,69.73 2.5,8.36667 5.17333,17.90333 8.02,28.61 4.34,16.3 12.18,33.04 22.68,47.08 3.98,5.31333 7.70667,10.38667 11.18,15.22 0.59333,0.83333 1.35333,1.58333 2.28,2.25 0.28,0.2 0.59,0.3 0.93,0.3 4.22667,-0.0133 8.26333,1.50667 12.11,4.56 6.02,4.78 12.17,10.11667 18.45,16.01 1.51,1.42 3.29,2.63 5.25,3.29 9.2,3.11 19.48,8.17 17.09,19.99 -0.32,1.61 -1.49,2.31 -3.05,2.48 -4.18667,0.46 -8.50667,-0.5 -12.96,-2.88 -4.75333,-2.53333 -8.58667,-4.99667 -11.5,-7.39 -0.55333,-0.45333 -1.12667,-0.48 -1.72,-0.08 l -0.24,0.16 c -0.30375,0.208 -0.35544,0.63562 -0.11,0.91 5.52667,6.25333 9.60333,12.61667 12.23,19.09 4.1,10.08667 7.07333,20.87333 8.92,32.36 0.56667,3.52 0.71667,7.10333 0.45,10.75 -0.34667,4.75333 -3.07,6.62333 -8.17,5.61 -0.77333,-0.16 -1.02333,0.13 -0.75,0.87 0.75333,2.03333 1.44333,4.07 2.07,6.11 1.56,5.04667 -0.2,8.07 -5.28,9.07 -3.49,0.69 -5.42,-2.17 -7.04,-4.75 -0.35421,-0.55015 -1.1808,-0.475 -1.43,0.13 -1.26667,3.16 -3.75333,4.66 -7.46,4.5 -2.69,-0.11 -4.64,-2.56 -5.81,-4.72 -1.54,-2.84667 -3.06,-5.96667 -4.56,-9.36 -0.72,-1.63333 -1.17,-1.56 -1.35,0.22 -0.04,0.44667 -0.04,0.88667 0,1.32 0.0506,0.45209 -0.0911,0.90266 -0.39,1.24 -6.1,6.88 -17.12,-14.55 -18.83,-18.14 -5.52,-11.58 -10.07333,-23.92333 -13.66,-37.03 -2.86,-10.44 -5.67667,-21.13333 -8.45,-32.08 -1.38,-5.43 -3.45,-10.62 -6.82,-15.28 -15.64,-21.66 -32.51,-41.59 -48.94,-62.08 -10.83333,-13.5 -19.95333,-26.60333 -27.36,-39.31 -4.35333,-7.44667 -7.92667,-14.98 -10.72,-22.6 -2.62,-7.14 -4.03667,-14.75667 -4.25,-22.85 -0.06,-2.17 -1.76,-6 -3.08,-8.08 -1.58667,-2.49333 -2.99,-5.05667 -4.21,-7.69 -7.74667,-16.80667 -13.65,-34.53333 -17.71,-53.18 -1.79333,-8.22 -2.94,-15.92 -3.44,-23.1 -0.02,-0.26 -0.0933,-0.50333 -0.22,-0.73 -0.27333,-0.50667 -0.47333,-0.48 -0.6,0.08 -1.26667,5.63333 -3.42667,10.71333 -6.48,15.24 -1.95,2.89 -4.68,5.3 -7.05,7.94 -1.00667,1.12667 -1.63667,2.41333 -1.89,3.86 -0.28667,1.63333 -0.44,3.18667 -0.46,4.66 -0.32667,32.01333 -1.04333,63.59667 -2.15,94.75 -0.1,2.86 0.11,5.76 0.63,8.7 0.94,5.33 4.48,9.32 7.59,13.42 28.13,37.13 39.68,83.8 42.02,130.13 1.55333,30.82667 0.26333,61.91 -3.87,93.25 -3.72667,28.26 -8.91333,56.45333 -15.56,84.58 -6.84667,28.98667 -14.71333,57.40667 -23.6,85.26 -1.72,5.39333 -3.00667,10.55333 -3.86,15.48 -2.69333,15.53333 -4.80333,30.7733 -6.33,45.72 -0.42667,4.1533 -0.60333,7.9733 -0.53,11.46 0.0667,3.3133 0.56667,6.8767 1.5,10.69 5.34,21.8067 9.62333,42.4733 12.85,62 1.64667,9.9467 2.82333,20.4633 3.53,31.55 1.09333,17.08 -0.91667,33.71 -6.03,49.89 -4.02,12.69 -10.84,31.49 -15.29,47.24 -5.78,20.44 -10.58667,41.91 -14.42,64.41 -0.91333,5.3467 -1.38,10.3533 -1.4,15.02 -0.0267,5.0533 -0.14333,10.22 -0.35,15.5 -0.12,3.09 0.5,6 1.74,8.88 2.88,6.73 5.31,15.28 2.81,22.38 -2.82,8 4.32,16.54 8.4,22.9 3.28,5.1067 5.71,10.0833 7.29,14.93 1.9,5.85 8.39,13.01 13.6,16.02 5.4,3.11 7.35,6.81 7,13.31 -0.24,4.33 -3.01,4.97 -6.62,5.27 -7.24,0.5933 -14.49333,1.0133 -21.76,1.26 h -26.32 c -6.06667,-0.1867 -12.12,-0.4867 -18.16,-0.9 -3.61333,-0.2533 -5.87,-0.5367 -6.77,-0.85 -6.82,-2.36 -6.74,-17.36 -6.44,-23.15 0.40667,-8.0133 1.49333,-15.8467 3.26,-23.5 1.32,-5.74 2.15,-11.64 2.97,-17.44 0.64,-4.61 0.34,-9.01 0.02,-13.63 -0.32667,-4.6333 -0.007,-9.1933 0.96,-13.68 0.27333,-1.26 0.40667,-2.6733 0.4,-4.24 -0.12,-20.3133 -0.66333,-40.95 -1.63,-61.91 -0.51333,-11.08 -1.24333,-21.3933 -2.19,-30.94 -0.24667,-2.4467 -0.48333,-4.12 -0.71,-5.02 -2.64,-10.4267 -4.75,-19.4267 -6.33,-27 -5.83,-28 -8.22,-56.65 -5.12,-86.07 0.81,-7.69 2.63,-17.18 3.77,-25.59 0.82,-6.0933 1.79667,-12.4767 2.93,-19.15 1.72667,-10.1533 2.95333,-19.9667 3.68,-29.44 0.24667,-3.2667 0.18667,-7.8433 -0.18,-13.73 -0.8,-12.77 -0.72,-25.5 -0.94,-38.64 -0.08,-4.77 0.66,-9.26 -0.1,-14.08 -1.01,-6.35 -1.99,-12.68 -2.63,-19.09 -1.76,-17.5 -3.09333,-31.96333 -4,-43.39 -2.92,-36.9 -4.94333,-71.84333 -6.07,-104.83 -0.62,-18.13333 -0.83,-32.25333 -0.63,-42.36 0.23,-11.76 0.52,-22.64 2.96,-33.39 0.3,-1.31 1.07,-2.44 1.58,-3.7 0.21633,-0.51724 -0.21543,-1.07235 -0.77,-0.99 -5.92,0.89333 -11.68667,0.89 -17.3,-0.01 -0.50066,-0.0855 -0.88038,0.44258 -0.64,0.89 1.22,2.32667 2.01,4.64 2.37,6.94 0.99333,6.25333 1.60667,12.63 1.84,19.13 0.87,24.2 0.11,49.22 -1.04,73.76 -1.77333,37.8 -4.43667,76.32667 -7.99,115.58 -0.87333,9.67333 -2.03333,19.13333 -3.48,28.38 -0.55333,3.52667 -0.79667,7.11333 -0.73,10.76 0.2,11.4933 0.0733,23.2633 -0.38,35.31 -0.37,9.95 -1.13,19.25 -0.15,28.95 0.70667,7.04 1.66,14.1833 2.86,21.43 1.86,11.23 3.05,20.33 4.64,30.47 1.44,9.1533 2.34667,15.75 2.72,19.79 1.86,19.88 1.4,39.8167 -1.38,59.81 -2,14.42 -4.83333,28.62 -8.5,42.6 -1.35333,5.1667 -2.25667,9.95 -2.71,14.35 -0.58,5.6133 -1.02,11.07 -1.32,16.37 -1.40667,25.2467 -2.17333,50.6367 -2.3,76.17 -0.007,1.0667 0.1,2.0967 0.32,3.09 0.94,4.1867 1.30667,7.6833 1.1,10.49 -0.4,5.5 -0.49667,10.37 -0.29,14.61 0.18,3.78 1.45333,11.2467 3.82,22.4 2.32,10.94 3.14,21.2167 2.46,30.83 -0.23,3.23 -0.99,6.23 -2.37,9.18 -1.12,2.4067 -3.07,3.7633 -5.85,4.07 -6.41333,0.72 -12.86333,1.1733 -19.35,1.36 h -32.5 c -6.38667,-0.3133 -12.76333,-0.75 -19.13,-1.31 -2.99,-0.26 -5.8,-0.23 -6.38,-3.95 -0.89,-5.79 0.46,-11.19 5.65,-14.03 3.87333,-2.1133 7.71,-5.6967 11.51,-10.75 1.39333,-1.8467 2.39,-3.6933 2.99,-5.54 2.15,-6.58 6.15,-14.18 10.8,-20.3 3.01333,-3.9733 4.75333,-8.46 5.22,-13.46 0.14667,-1.5867 -0.03,-3.33 -0.53,-5.23 -2.08,-7.87 0.88,-16.6 4.1,-23.94 0.75,-1.74 0.92,-4.92 0.84,-6.79 -0.22,-5.6333 -0.35667,-11.2933 -0.41,-16.98 -0.0333,-4.0267 -0.50667,-8.5367 -1.42,-13.53 -4.55,-24.93 -9.62,-49.15 -17.21,-73.21 -3.40667,-10.7933 -7.04667,-21.86 -10.92,-33.2 -6.66667,-19.4867 -9.09333,-39.4767 -7.28,-59.97 1.06667,-11.9667 2.67667,-23.9133 4.83,-35.84 3.06667,-17.02 6.78333,-34.3733 11.15,-52.06 1.02,-4.14 1.58,-7.7567 1.68,-10.85 0.11333,-3.6133 -0.0567,-7.4867 -0.51,-11.62 -1.76667,-16.2533 -4.03333,-32.40667 -6.8,-48.46 -0.41333,-2.42 -0.96667,-4.71 -1.66,-6.87 -9.93333,-30.95333 -19.14,-64.33 -27.62,-100.13 -1.60667,-6.76 -3.05667,-13.43 -4.35,-20.01 -3.02667,-15.4 -5.79667,-32.35667 -8.31,-50.87 -4.47333,-32.97333 -6.08,-64.13333 -4.82,-93.48 1.96,-45.94 12.04,-90.97 37.47,-128.29 2.24,-3.3 4.7,-6.4 7.12,-9.46 4.15,-5.26 5.95,-10.32 5.77,-16.87 -0.93333,-32.91333 -1.63667,-65.25667 -2.11,-97.03 -0.03,-1.94 -0.52,-3.58 -0.98,-5.42 -0.33333,-1.32 -0.91,-2.49333 -1.73,-3.52 -2.11,-2.64 -4.96,-4.81 -6.7,-7.55 -2.83333,-4.43333 -4.92333,-9.26 -6.27,-14.48 -0.44667,-1.74 -0.80333,-1.72333 -1.07,0.05 -0.21,1.4 -0.01,2.99 -0.19,4.42 -3.26,25.04 -10.27667,49.07667 -21.05,72.11 -2.51,5.36 -6.73,10.25 -7.04,16.61 -0.88,17.8 -8.25,33.44 -17.63,48.71 -6.92667,11.28 -14.61,22.25333 -23.05,32.92 -2.86667,3.61333 -5.78667,7.31667 -8.76,11.11 -0.48667,0.62 -1.03,1.17667 -1.63,1.67 -0.61333,0.50667 -1.13,1.1 -1.55,1.78 -0.54667,0.9 -1.16333,1.77 -1.85,2.61 -12.59,15.4 -25.29,30.72 -36.86,46.89 -5.45,7.62 -7.87,18.03 -9.84,26.83 -3.593333,16.11333 -8.14,31.19333 -13.64,45.24 -2.493333,6.38667 -5.346667,12.43 -8.56,18.13 -1.8,3.2 -10.72,18.37 -15.54,12.26 -0.68,-0.86 -0.25,-2.07 -0.49,-3.02 -0.326667,-1.25333 -0.753333,-1.28667 -1.28,-0.1 -1.83,4.14 -5.34,14.31 -10.67,14.48 -3.546667,0.10667 -5.953333,-1.43333 -7.22,-4.62 -0.209662,-0.51969 -0.897493,-0.62508 -1.24,-0.19 -0.76,0.94667 -1.49,1.90333 -2.19,2.87 -2.2,3.05 -6.34,2.41 -9.1,0.4 -3.57,-2.59 -0.63,-10.37 0.9,-13.45 0.236418,-0.45407 -0.157897,-0.97982 -0.66,-0.88 -2.81,0.51 -6.84,0.27 -7.75,-3 -1.3,-4.73 -0.81,-9.94 0.05,-14.85 1.773333,-10.17333 4.316667,-19.64333 7.63,-28.41 3.18,-8.41333 7.573333,-15.49667 13.18,-21.25 0.322884,-0.3286 0.340328,-0.84758 0.04,-1.19 -0.293333,-0.34 -0.703333,-0.50667 -1.23,-0.5 -0.278815,6e-5 -0.546543,0.0916 -0.76,0.26 -4.246667,3.34667 -9.05,6.25667 -14.41,8.73 -1.546667,0.70667 -3.236667,1.18333 -5.07,1.43 -3.24,0.44 -7.32,1.02 -8.34,-3.06 v -5.04 c 1.89,-8.36 10.71,-11.9 18.05,-14.48 1.214197,-0.42225 2.32994,-1.08557 3.28,-1.95 4.853333,-4.38 9.59,-8.60667 14.21,-12.68 4.72,-4.16 11.45,-9.69 17.55,-8.89 0.42,0.0533 0.796667,-0.0467 1.13,-0.3 0.92,-0.7 1.69,-1.49333 2.31,-2.38 2.833333,-4.10667 5.91,-8.36 9.23,-12.76 3.766667,-4.99333 6.703333,-9.25 8.81,-12.77 6.546667,-10.92667 11.606667,-22.59667 15.18,-35.01 2.026667,-7.06667 3.946667,-14.08667 5.76,-21.06 5.72,-22.05333 15.62667,-42.48333 29.72,-61.29 4.66,-6.22 9.65667,-12.45667 14.99,-18.71 0.39308,-0.46353 0.60294,-1.054 0.59,-1.66 -0.28,-12.94667 0.27,-25.82667 1.65,-38.64 2.22667,-20.61333 7.64667,-40.31667 16.26,-59.11 0.53333,-1.15333 0.76,-2.34333 0.68,-3.57 -1.06,-16.84667 -0.0433,-35.03667 3.05,-54.57 3.2,-20.24 13.06,-40.07 33.4,-47.77 5.15333,-1.95333 10.72667,-3.49333 16.72,-4.62 1.53333,-0.28667 7.27,-1.04667 17.21,-2.28 7.33333,-0.90667 14.61333,-2.13667 21.84,-3.69 9.63333,-1.85333 18.05333,-5.90667 25.26,-12.16 0.35333,-0.30667 0.64,-0.65667 0.86,-1.05 0.61333,-0.13333 1.14,-0.47333 1.58,-1.02 4.60667,-5.78667 7.37667,-12.60667 8.31,-20.46 0.0467,-0.42667 -0.10333,-0.76 -0.45,-1 -3.21333,-2.21333 -6.00333,-5.19 -8.37,-8.93 -0.12667,-0.20667 -0.29667,-0.25333 -0.51,-0.14 -0.12,0.0667 -0.20333,0.13667 -0.25,0.21 -0.11333,-0.71333 -0.36667,-1.38 -0.76,-2 -6.65333,-10.54 -11.87,-21.78 -15.65,-33.72 -0.14124,-0.44693 -0.52604,-0.77342 -0.99,-0.84 -4.45,-0.65 -6.81,-5.29 -8.11,-9.16 -2.44667,-7.28667 -3.74667,-15 -3.9,-23.14 -0.0333,-1.54 0.38667,-2.85667 1.26,-3.95 0.24667,-0.30667 0.56333,-0.44667 0.95,-0.42 0.94667,0.08 1.88667,0.10333 2.82,0.07 5.42,-0.17 7.25,-5.92 8.02,-10.27 1.31,-7.45 2.25,-14.74 4.33,-22.04 0.17452,-0.60386 0.64526,-1.07836 1.25,-1.26 10.82,-3.22 21.32,-7.26333 31.5,-12.13 8.3,-3.96 16.2,-9.09 23.95,-14.33 4.34667,-2.933333 9.10667,-4.106667 14.28,-3.52 10.03,1.15 20.04,4.55 29.47,7.81 0.67736,0.236152 1.23278,0.741083 1.54,1.4 1.14667,2.5 1.93667,5.07333 2.37,7.72 1.68,10.24 3.37667,19.77333 5.09,28.6 0.87,4.45 3.85,17.51 9.55,17.9 1.3,0.0933 2.66,0.0167 4.08,-0.23 0.42,-0.0733 0.81,0.007 1.17,0.24 1.26,0.8 1.89,1.95333 1.89,3.46 0.03,6.92 -2.08,32.36 -11.81,33.17 -0.47681,0.0359 -0.88461,0.36051 -1.03,0.82 -1.62,5.23333 -3.67333,10.6 -6.16,16.1 -0.06,0.13333 -0.0233,0.23 0.11,0.29 z";

// Body region positions per sex (in the respective SVG viewBox coordinate space)
const BODY_POSITIONS = {
  male: { brain: [364,80], heart: [395,385], liver: [310,520], gut: [364,710], muscle: [295,980] },
  female: { brain: [340,110], heart: [370,395], liver: [290,530], gut: [340,720], muscle: [280,1000] },
};

function BodyMapSVG({ sex, regionCounts, activeRegion, onRegionClick }) {
  const isFemale = sex === 'female';
  const vb = isFemale ? "-50 -40 734 1538" : "0 0 724 1448";
  const pos = BODY_POSITIONS[isFemale ? 'female' : 'male'];
  const headCx = isFemale ? 340 : 364;
  return (
    <svg viewBox={vb} className="body-map-svg">
      <defs>
        <linearGradient id="bodyFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(45,212,191,0.10)"/>
          <stop offset="100%" stopColor="rgba(45,212,191,0.03)"/>
        </linearGradient>
      </defs>
      <g className="body-silhouette">
        <ellipse cx={headCx} cy={isFemale ? 115 : 80} rx={isFemale ? 55 : 60} ry={isFemale ? 72 : 78}
          fill="url(#bodyFill)" stroke="rgba(45,212,191,0.3)" strokeWidth="2"/>
        <path d={isFemale ? FEMALE_BODY_PATH : MALE_BODY_PATH}
          fill="url(#bodyFill)" stroke="rgba(45,212,191,0.3)" strokeWidth="2"/>
      </g>
      {Object.entries(BODY_REGIONS).map(([id, region]) => {
        const xy = pos[id];
        if (!xy) return null;
        const count = regionCounts[id] || 0;
        const isActive = activeRegion === id;
        const glowOpacity = count > 0 ? Math.min(0.15 + count * 0.08, 0.5) : 0.05;
        return (
          <g key={id} className={`body-region ${isActive ? 'active' : ''}`} onClick={() => onRegionClick(id)} style={{cursor:'pointer'}}>
            <circle cx={xy[0]} cy={xy[1]} r={isActive ? 60 : 50}
              fill={`rgba(61,220,151,${isActive ? 0.3 : glowOpacity})`}
              stroke={count > 0 ? '#112240' : 'rgba(45,212,191,0.2)'}
              strokeWidth={isActive ? 4 : 2}
              className="region-circle"/>
            <text x={xy[0]} y={xy[1] + 10} textAnchor="middle" className="region-label" fontSize="32" fill="#112240">
              {count > 0 ? count : ''}
            </text>
            <title>{`${region.label}: ${count} variant${count !== 1 ? 's' : ''}`}</title>
          </g>
        );
      })}
    </svg>
  );
}

// -- DNA Source Badge ----------------------------------------------------------
const SOURCE_BADGE_MAP = {
  'NPL_exome': { label: 'NPL Exome', color: '#22c55e' },
  'npl_exome': { label: 'NPL Exome', color: '#22c55e' },
  'vcf': { label: 'NPL Exome', color: '#22c55e' },
  'myheritage': { label: 'MyHeritage', color: '#818CF8' },
};
function SourceBadge({ source }) {
  const info = SOURCE_BADGE_MAP[source];
  if (!info) return null;
  return <span className="source-badge" style={{ background: `${info.color}22`, color: info.color, borderColor: `${info.color}44` }}>{info.label}</span>;
}

// Enriched finding cache (persists across drawer open/close)
const _enrichedCache = {};

function FindingDrawer({ finding, onClose }) {
  const closeModal = useModalHashRoute('#finding-drawer', onClose);
  const [enriched, setEnriched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!finding?.rsid) return;
    if (_enrichedCache[finding.rsid]) { setEnriched(_enrichedCache[finding.rsid]); setLoading(false); return; }
    setLoading(true); setError(null); setEnriched(null); setShowDeepDive(false);
    api.get(`/genetics/enriched-finding/${finding.rsid}`)
      .then(data => { _enrichedCache[finding.rsid] = data; setEnriched(data); })
      .catch(err => { setError(err.message); })
      .finally(() => setLoading(false));
  }, [finding?.rsid]);

  if (!finding) return null;

  const evidenceLabels = {
    high: { label: 'Well-established research', detail: 'Supported by multiple large-scale studies across diverse populations.', color: '#112240' },
    moderate: { label: 'Growing body of research', detail: 'Supported by published studies, with ongoing research expanding our understanding.', color: '#F59E0B' },
    preliminary: { label: 'Early-stage research', detail: 'Initial findings that require further study to confirm.', color: '#8B6914' },
  };
  const ev = evidenceLabels[finding.confidence] || evidenceLabels.moderate;
  const ai = enriched?.ai_analysis;
  const riskColors = { mitigated: '#112240', partially_mitigated: '#F59E0B', monitor: '#F59E0B', action_needed: '#EF4444' };
  const riskLabels = { mitigated: 'Mitigated', partially_mitigated: 'Partially Mitigated', monitor: 'Monitor', action_needed: 'Action Needed' };
  const statusDots = { good: '#112240', watch: '#F59E0B', alert: '#EF4444' };

  return (
    <div className="finding-drawer-overlay" onClick={closeModal}>
      <div className="finding-drawer finding-drawer-enriched" onClick={e => e.stopPropagation()}>
        <button className="drawer-close" onClick={closeModal}>&times;</button>

        {/* Header */}
        <div className="finding-gene-row">
          <span className="finding-gene"><GlossaryTerm term={finding.gene.toLowerCase()}>{finding.gene}</GlossaryTerm> <GlossaryTerm term="gene">gene</GlossaryTerm></span>
          {finding.source && <SourceBadge source={finding.source} />}
          <span className="finding-genotype-plain">
            {finding.zygosity === 'heterozygous' ? 'one copy of this variant' : 'two copies of this variant'}
          </span>
          <div className="finding-evidence-level" style={{'--conf-color': ev.color}}>
            <span className="confidence-badge">{ev.label}</span>
          </div>
        </div>

        {/* AI-powered summary or fallback */}
        {loading && <div className="enriched-loading"><div className="enriched-spinner" /> {T('Analysing your health data for')} {finding.gene}...</div>}

        {ai ? (
          <>
            <div className="finding-plain-english">{autoGlossary(ai.summary)}</div>
            {ai.risk_status && (
              <span className="risk-badge" style={{ background: riskColors[ai.risk_status] || '#8B6914' }}>
                {ai.risk_status === 'mitigated' && '\u2713 '}{riskLabels[ai.risk_status] || ai.risk_status}
              </span>
            )}

            {/* Health Connections */}
            {ai.health_connections?.length > 0 && (
              <div className="enriched-section">
                <h4>Your Health Data</h4>
                {ai.health_connections.map((hc, i) => (
                  <div key={i} className="health-connection">
                    <span className="hc-dot" style={{ background: statusDots[hc.status] || '#8B6914' }} />
                    <span className="hc-marker">{autoGlossary(hc.marker)}</span>
                    <span className="hc-value">{hc.value}</span>
                    <span className="hc-explanation">{autoGlossary(hc.explanation)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            {ai.actions?.length > 0 && (
              <div className="enriched-section enriched-actions">
                <h4>What You Can Do</h4>
                <ol>{ai.actions.map((a, i) => <li key={i}>{autoGlossary(a)}</li>)}</ol>
              </div>
            )}

            {/* Deep Dive */}
            {ai.deep_dive && (
              <div className="enriched-section">
                <button className="deep-dive-toggle" onClick={() => setShowDeepDive(!showDeepDive)}>
                  {showDeepDive ? '\u25BC' : '\u25B6'} Deep Dive: The Biology
                </button>
                {showDeepDive && (
                  <div className="deep-dive-content">
                    {ai.deep_dive.split('\n').filter(Boolean).map((para, i) => (
                      <p key={i}>{autoGlossary(para)}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : !loading && (
          <>
            <div className="finding-plain-english">{autoGlossary(finding.plain_english_summary)}</div>
            {finding.what_this_means && <div className="finding-means">
              <h4>What this means</h4>
              <p>{autoGlossary(finding.what_this_means)}</p>
            </div>}
          </>
        )}

        {error && <div className="enriched-error">AI analysis unavailable: {error}</div>}

        {/* Technical detail (always available) */}
        <details className="finding-rsid-detail">
          <summary>Technical detail</summary>
          <div className="finding-technical">
            <span>{finding.rsid}</span>
            <span><GlossaryTerm term="genotype">Genotype</GlossaryTerm>: {finding.genotype}</span>
            <span><GlossaryTerm term="maf">MAF</GlossaryTerm>: {finding.maf ? (finding.maf * 100).toFixed(1) + '%' : 'N/A'}</span>
          </div>
        </details>

        <div className="finding-footer">
          Educational wellness information based on published research. Not a clinical finding or diagnosis.
        </div>
      </div>
    </div>
  );
}

function WellnessCategoryCard({ categoryId, info, findings, onFindingClick }) {
  const [expanded, setExpanded] = useState(false);
  const cat = WELLNESS_CATEGORIES[categoryId] || { label: categoryId, icon: '\uD83E\uDDEC' };
  const catFindings = findings.filter(f => f.wellness_category === categoryId);
  return (
    <div className={`wellness-card ${expanded ? 'expanded' : ''}`}>
      <div className="wellness-card-header" onClick={() => setExpanded(!expanded)}>
        <span className="wellness-icon">{cat.icon}</span>
        <div className="wellness-card-title">
          <h4><GlossaryTerm term="wellness category">{cat.label}</GlossaryTerm></h4>
          {info.top_finding && !expanded && <p className="wellness-preview">{autoGlossary(info.top_finding)}</p>}
        </div>
        <span className="wellness-count">{info.count}</span>
        <span className="wellness-expand">{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>
      {expanded && <div className="wellness-findings">
        {catFindings.map((f, i) => (
          <div key={i} className="wellness-finding-item" onClick={() => onFindingClick(f)}>
            <div className="wf-header"><span className="wf-gene"><GlossaryTerm term={f.gene.toLowerCase()}>{f.gene}</GlossaryTerm></span>
              {f.source && <SourceBadge source={f.source} />}
              {f.confidence === 'high' && <span className="wf-evidence-label">Well-established research</span>}
              {f.confidence === 'moderate' && <span className="wf-evidence-label wf-moderate-label">Growing research</span>}
              {f.confidence === 'preliminary' && <span className="wf-evidence-label wf-preliminary-label">Early research</span>}
            </div>
            <div className="wf-summary">{autoGlossary(f.plain_english_summary)}</div>
          </div>
        ))}
      </div>}
    </div>
  );
}

function CancerScreeningCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get('/genetics/cancer-screening').then(setData).catch(() => {});
  }, []);

  if (!data) return null;

  const keyGenes = data.key_genes || {};
  const allGenes = data.genes || {};
  const pancreaticGenes = ['BRCA1', 'BRCA2', 'PALB2', 'ATM', 'CDKN2A', 'STK11', 'PRSS1', 'KRAS', 'SMAD4'];
  const lynchGenes = ['MLH1', 'MSH2', 'MSH6', 'PMS2', 'EPCAM'];

  return (
    <div className={`cancer-screening-card ${expanded ? 'expanded' : ''}`}>
      <div className="cancer-screening-header" onClick={() => setExpanded(!expanded)}>
        <span className="cancer-screening-icon">{'\uD83D\uDEE1\uFE0F'}</span>
        <div className="cancer-screening-title">
          <h4>Hereditary Cancer Screening</h4>
          <span className="cancer-screening-subtitle">41-gene panel &mdash; {data.report_date}</span>
        </div>
        <span className="genome-lab-badge" title="Results transmitted directly from National Path Lab via certified NGS sequencing. Chain of custody verified.">{'\uD83D\uDD12'} Lab Verified &middot; NPL</span>
        <span className="cancer-all-clear">{'\u2713'} All Clear</span>
        <span className="wellness-toggle">{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>
      {expanded && (
        <div className="cancer-screening-body">
          <div className="cancer-result-summary">
            <p>{autoGlossary(data.result_summary)}</p>
            <div className="cancer-family-note">
              <strong>Family context:</strong> {data.family_context}
            </div>
          </div>

          <div className="cancer-gene-group">
            <h5>Pancreatic Cancer Genes <span className="gene-group-note">(most relevant to your family history)</span></h5>
            <div className="cancer-gene-grid">
              {pancreaticGenes.map(g => (
                <div key={g} className="cancer-gene-chip clear">
                  <span className="gene-check">{'\u2713'}</span> {g}
                  {keyGenes[g] && <span className="gene-cancers">{keyGenes[g].cancers.join(', ')}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="cancer-gene-group">
            <h5>Lynch Syndrome Genes <span className="gene-group-note">(<GlossaryTerm term="gene">colorectal</GlossaryTerm> cancer)</span></h5>
            <div className="cancer-gene-grid">
              {lynchGenes.map(g => (
                <div key={g} className="cancer-gene-chip clear">
                  <span className="gene-check">{'\u2713'}</span> {g}
                </div>
              ))}
            </div>
          </div>

          <div className="cancer-gene-group">
            <h5>BRCA1/BRCA2 &amp; DNA Repair</h5>
            <div className="cancer-gene-grid">
              {['BRCA1', 'BRCA2', 'PALB2', 'BRIP1', 'RAD50', 'RAD51B', 'RAD51C', 'RAD51D', 'ATM', 'CHEK2', 'BARD1', 'NBN'].map(g => (
                <div key={g} className="cancer-gene-chip clear">
                  <span className="gene-check">{'\u2713'}</span> {g}
                </div>
              ))}
            </div>
          </div>

          {showAll && (
            <div className="cancer-gene-group">
              <h5>All 41 Genes</h5>
              <div className="cancer-gene-grid">
                {Object.keys(allGenes).sort().map(g => (
                  <div key={g} className="cancer-gene-chip clear small">
                    <span className="gene-check">{'\u2713'}</span> {g}
                  </div>
                ))}
              </div>
            </div>
          )}
          <button className="show-all-genes" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Hide full list' : `Show all ${data.gene_count} genes`}
          </button>

          <div className="cancer-methodology">
            <strong>Method:</strong> {data.platform} | {data.lab}
          </div>

          <div className="finding-footer">
            {data.disclaimer}
          </div>
        </div>
      )}
    </div>
  );
}

function GenomePage() {
  const [tab, setTab] = useState('overview');
  const [findings, setFindings] = useState([]);
  const [categories, setCategories] = useState({});
  const [regionCounts, setRegionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeRegion, setActiveRegion] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [userProfile, setUserProfile] = useState({ biological_sex: 'male' });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [summary, profile] = await Promise.all([
          api.get('/genetics/summary'),
          api.get('/user/profile'),
        ]);
        setFindings(summary.findings || []);
        setCategories(summary.categories || {});
        setRegionCounts(summary.region_counts || {});
        if (profile) setUserProfile(profile);
      } catch (e) { console.error('Genome load error:', e); }
      finally { setLoading(false); }
    };
    loadData();
  }, []);

  const handleRegionClick = (regionId) => {
    setActiveRegion(activeRegion === regionId ? null : regionId);
  };

  // Filter categories by active body region
  const filteredCategories = activeRegion
    ? Object.fromEntries(Object.entries(categories).filter(([catId]) =>
        BODY_REGIONS[activeRegion]?.categories.includes(catId)))
    : categories;

  if (loading) return <LoadingSpinner message="Loading genome data..." />;

  return (
    <div className="genome-page" style={{position:'relative'}}>
      <div className="page-header genome-header">
        <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />My Genome</h2>
        <div className="genome-tabs">
          {['overview', 'pathways'].map(t => (
            <button key={t} className={`genome-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="genome-summary-badge">{findings.length} wellness-relevant variants</div>
      </div>

      {tab === 'overview' && (
        <div className="genome-overview">
          <div className="genome-col-left">
            <BodyMapSVG
              sex={userProfile.biological_sex || 'male'}
              regionCounts={regionCounts}
              activeRegion={activeRegion}
              onRegionClick={handleRegionClick}
            />
            {activeRegion && <div className="region-filter-label">
              Showing: {BODY_REGIONS[activeRegion]?.label}
              <button className="clear-filter" onClick={() => setActiveRegion(null)}>&times; Clear</button>
            </div>}
          </div>
          <div className="genome-col-right">
            {!activeRegion && <CancerScreeningCard />}
            {Object.entries(filteredCategories).length === 0 ? (
              <div className="genome-empty">No variants found for this region.</div>
            ) : (
              Object.entries(filteredCategories).map(([catId, info]) => (
                <WellnessCategoryCard key={catId} categoryId={catId} info={info}
                  findings={findings} onFindingClick={setSelectedFinding} />
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'pathways' && <PathwayDiagrams />}

      <FindingDrawer finding={selectedFinding} onClose={() => setSelectedFinding(null)} />
    </div>
  );
}

// -- LongevityPage (v6 — evidence-based biological age) ----------------------
// Layer attribution map — assigns each variable to the displayed scoring
// layer (per the spec). Drives the layer badge on driver cards and which
// expandable layer card a metric appears under. The underlying composite
// math is unchanged; this only affects presentation.
const LAYER_BY_METRIC = {
  // PhenoAge (Levine 2018 — clinical chemistry)
  'Albumin': 'phenoage', 'Creatinine': 'phenoage', 'CRP': 'phenoage',
  'ALP': 'phenoage', 'Glucose': 'phenoage', 'Lymphocytes': 'phenoage',
  'MCV': 'phenoage', 'RDW': 'phenoage', 'WBC': 'phenoage',
  // Cardiovascular (lipid risk + body composition)
  'HDL Cholesterol': 'cardiovascular', 'Triglycerides': 'cardiovascular',
  'TC/HDL Ratio': 'cardiovascular', 'ApoB': 'cardiovascular',
  'Lp(a)': 'cardiovascular', 'Blood Pressure': 'cardiovascular',
  'BMI': 'cardiovascular',
  // Fitness & Activity (wearable + renal function)
  'Resting HR': 'fitness', 'HRV (RMSSD)': 'fitness',
  'Daily Steps': 'fitness', 'Sleep Duration': 'fitness',
  'eGFR': 'fitness',
};

const LAYER_META = {
  phenoage:       { icon: '🧪', label: 'PhenoAge',           color: '#818CF8' },
  cardiovascular: { icon: '❤️',  label: 'Cardiovascular',     color: '#fb7185' },
  fitness:        { icon: '🏃', label: 'Fitness & Activity', color: '#34d399' },
};

// Phase A: the static ACTION_TIPS dict was removed. Per-marker guidance is now
// generated by the unified, contraindication-screened engine (/api/marker-advice
// + the batched advice attached to /api/health-record/longevity-flags) and shown
// on the Improve Longevity (flags) page and in each marker's detail view.

// Small coloured pill identifying which scoring layer a variable belongs to.
function LayerBadge({ metric }) {
  const layer = LAYER_BY_METRIC[metric] || 'phenoage';
  const meta = LAYER_META[layer];
  return (
    <span className="layer-badge" style={{
      background: meta.color + '22',
      color: meta.color,
      borderColor: meta.color + '55',
    }}>
      <span className="layer-badge-icon">{meta.icon}</span>
      <span className="layer-badge-label">{meta.label}</span>
    </span>
  );
}

// "How Your Biological Age Is Calculated" — opened from the ⓘ next to the
// Biological Age label. Three-layer methodology + citations in a compact
// dark-theme modal (max 420 px). Closes on backdrop click, ✕ button, or
// Escape key. Sized to fit one screen — no internal scroll.
function BiologicalAgeMethodologyModal({ onClose }) {
  const closeModal = useModalHashRoute('#bio-method', onClose);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeModal]);
  return ReactDOM.createPortal(
    <div className="bio-method-backdrop" onClick={closeModal}>
      <div className="bio-method-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="bio-method-title">
        <button className="bio-method-close" onClick={closeModal} aria-label="Close">{'✕'}</button>
        <h3 id="bio-method-title" className="bio-method-title">How Your Biological Age Is Calculated</h3>
        <p className="bio-method-intro">
          Your score combines three independent measures of how your body is
          ageing, each weighted by its scientific contribution to longevity.
        </p>
        {/* KAL-274 — the interpretation disclaimer lives HERE now, behind the ⓘ, relocated from
            the Longevity page surface (it was a 121px paragraph pushing the markers below the
            fold at 390x844). Verbatim BIOAGE_DISCLAIMER — one source, two readers (this modal
            and the chat footer), so the wording cannot fork. */}
        <p className="bio-method-intro" style={{opacity:0.85, fontSize:'0.92em'}}>{BIOAGE_DISCLAIMER}</p>
        <div className="bio-method-layer">
          <div className="bio-method-head">
            <span className="bio-method-icon">{'🧪'}</span>
            <span className="bio-method-name">Blood Biomarkers (PhenoAge)</span>
            <span className="bio-method-weight">50%</span>
          </div>
          <p className="bio-method-body">
            Nine markers from your blood test — including CRP, glucose, albumin,
            and white cell count — fed into the Levine PhenoAge algorithm.
            Validated against 14,000 people in the US National Health Survey.
            The strongest single predictor of biological age.
          </p>
          <p className="bio-method-cite">Levine et al., 2018</p>
        </div>
        <div className="bio-method-layer">
          <div className="bio-method-head">
            <span className="bio-method-icon">{'❤️'}</span>
            <span className="bio-method-name">Cardiovascular Health</span>
            <span className="bio-method-weight">30%</span>
          </div>
          <p className="bio-method-body">
            Your HDL, LDL/HDL ratio, ApoB, and Lp(a). Heart disease causes
            ~32% of all deaths globally — these markers predict risk
            independently of your blood biomarkers above.
          </p>
          <p className="bio-method-cite">GBD 2019; Framingham Heart Study</p>
        </div>
        <div className="bio-method-layer">
          <div className="bio-method-head">
            <span className="bio-method-icon">{'🏃'}</span>
            <span className="bio-method-name">Fitness & Activity</span>
            <span className="bio-method-weight">20%</span>
          </div>
          <p className="bio-method-body">
            Resting heart rate, HRV, daily steps, and sleep from your wearable.
            Each 1-MET improvement in fitness is linked to 13% lower
            all-cause mortality risk across 100,000+ people.
          </p>
          <p className="bio-method-cite">JAMA meta-analysis, 33 studies</p>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Section A — honest, modifiability-tiered marker education for the Longevity page.
// tier 'modifiable' → lifestyle guidance allowed (+ named supplements only where well
// established); tier 'indicator' → educational only, NO directives, NO supplement
// suggestions (nutrient checks go in the doctor-flag, never as a suggestion).
// RUN6 §2 — honest, CITED "what can influence it" for every PhenoAge marker. The old
// "mostly-fixed = nothing you can do" binary is RETIRED: Lymphocyte %, WBC and RDW genuinely
// respond to sleep, stress, exercise, infection and nutrition — so we say so and cite it.
// MCV and Creatinine really are largely stable — we say that honestly too (not a false lever).
// S1.2 — 6-section drawer copy authored strictly to KALPA_DRAWER_COPY_SPEC.md §3.
// Fields: what (empowering, one idea) / canInfluence (ONE gain-framed lever) /
// citation (named study) / horizon / trendCaveat (variability line for the trend
// section). MCV + creatinine are reframed as understand-the-cause, NOT "fix it".
// No marker is ranked as "biggest driver". Cross-marker/plausibility clauses live in
// the engine (evalMarkerClauses); lifestyle weave in _markerRealContext.
const _TREND_CAVEAT = 'Single readings vary, so we look at the pattern.';
const BIOAGE_MARKER_INFO = {
  'albumin': { tier: 'modifiable', label: 'Albumin',
    what: 'Albumin is a protein your liver makes that supports muscle, transport and resilience. Longevity cohorts tend to sit in the upper-normal band, so a healthy level here works in your favour.',
    canInfluence: 'Adequate protein and resolving any inflammation gently support it. It naturally eases about 0.08 g/dL per decade after 40, so small moves are normal.',
    citation: 'Swedish AMORIS centenarian cohort (GeroScience, 2023)',
    horizon: 'Weeks to months.', trendCaveat: 'Single readings vary with hydration, so we look at the pattern.' },
  'creatinine': { tier: 'indicator', label: 'Creatinine',
    what: 'Creatinine is a muscle by-product your kidneys clear. In a fit or creatine-supplementing person, a higher value usually reflects muscle and supplementation — not a problem to fix.',
    canInfluence: 'This is not really a short-term lifestyle lever. The useful move is to understand the cause — muscle and creatine versus kidney — rather than chase the number down.',
    citation: 'Creatine → creatinine/eGFR-artefact literature (renal-function reviews)',
    horizon: 'If you paused creatine, it washes out over about 2–4 weeks.', trendCaveat: _TREND_CAVEAT },
  'glucose': { tier: 'modifiable', label: 'Glucose (fasting)',
    what: 'Fasting glucose is your average fuel level after a night without food — a strong, responsive read on your metabolic health.',
    canInfluence: 'Weight loss, fewer refined carbs, regular exercise and good sleep all lower it, often within weeks.',
    // KAL-307 — the previous line cited a prescription drug's trial result to every user with a
    // glucose reading. Static copy names no medicine: the evidence here is the lifestyle lever the
    // drawer actually recommends (Knowler et al., DPP, NEJM 2002 — the 58% figure is the headline
    // result of the lifestyle arm). test_kal307_no_drug_in_static_copy.py enforces this.
    citation: 'Diabetes Prevention Program (Knowler et al., NEJM 2002): weight loss, diet change and ~150 min/week of activity lowered fasting glucose and cut progression to diabetes by 58%',
    horizon: 'Days to weeks. Day-to-day variation is ~4.5%, so one high reading may just be noise.', trendCaveat: _TREND_CAVEAT },
  'crp': { tier: 'modifiable', label: 'CRP',
    what: 'CRP is your body’s main inflammation signal — a quick read on how calm your system is right now.',
    canInfluence: 'Losing 5–10% of body weight, aerobic and strength training, a plant-forward diet and good sleep can lower it meaningfully, often within weeks.',
    citation: 'In the Look AHEAD study, a lifestyle programme cut hs-CRP by ~44% in a year',
    horizon: 'Days to weeks.', trendCaveat: 'CRP is naturally jumpy, so we read the pattern, not one number.' },
  'lymphocyte': { tier: 'modifiable', label: 'Lymphocyte %',
    what: 'This is the share of your white cells that are lymphocytes — a marker of your immune reserve.',
    canInfluence: 'Good nutrition, activity and managing chronic stress support it. A share on the low side matters more for ageing than a high one.',
    citation: 'Copenhagen General Population Study (Warny et al., CMAJ 2020)',
    horizon: 'Weeks; it shifts transiently after an infection.', trendCaveat: _TREND_CAVEAT },
  'mcv': { tier: 'indicator', label: 'MCV',
    what: 'MCV is the average size of your red blood cells — a clue to how your blood is being built.',
    canInfluence: 'This is not a short-term lifestyle lever. A high value usually reflects B12/folate, alcohol, thyroid or liver, and red cells turn over slowly — so treat it as a clue worth checking, not an alarm.',
    citation: 'Macrocytosis literature (Green, Blood 2017)',
    horizon: '2–3+ months, even after a cause is addressed.', trendCaveat: _TREND_CAVEAT, doctorCheck: 'B12, folate or thyroid' },
  'rdw': { tier: 'modifiable', label: 'RDW',
    what: 'RDW is how much your red-cell sizes vary — one of the steadier signals in the model of how your body is ageing.',
    canInfluence: 'Fixing iron, B12 or folate deficiencies and lowering inflammation bring it down over months.',
    citation: 'Patel et al., J Gerontol A 2010 — each 1% rise tracked ~14% higher mortality risk',
    horizon: 'Months, once the underlying cause is addressed.', trendCaveat: _TREND_CAVEAT },
  'alkaline phosphatase': { tier: 'modifiable', label: 'Alkaline Phosphatase',
    what: 'ALP is an enzyme from your liver, bile ducts and bones — a read on liver flow and bone activity.',
    canInfluence: 'When the cause is low vitamin D, repleting it can lower ALP over weeks to months. Otherwise it reflects liver/bile or bone turnover.',
    citation: 'Vitamin D / PTH / ALP studies',
    horizon: 'Weeks to months when vitamin D is the driver; variable otherwise.', trendCaveat: _TREND_CAVEAT },
  'wbc': { tier: 'modifiable', label: 'WBC',
    what: 'WBC is your total immune-cell count. A lower-normal count is generally better for longevity; a chronic creep upward can track inflammation.',
    canInfluence: 'Not smoking, losing visceral fat, exercise and resolving inflammation lower it.',
    citation: 'Nilsson et al. 2014 (higher WBC tracked higher mortality in 75-year-olds)',
    horizon: 'Weeks to months.', trendCaveat: _TREND_CAVEAT },
};
const BIOAGE_SUPP_CONSULT = "Supplements aren't a substitute for medical advice — check with your doctor or pharmacist first, especially if you take other medicines.";
// §2 — shown WITH the number (never suppresses it) when reliability === 'indicative_young'
// (backend tags chrono < 30 on a complete draw). Single source; reused by Longevity + chat.
const BIOAGE_YOUNG_CAVEAT = "PhenoAge is validated for adults 30+. Below that it reads optimistically and is less reliable — treat as indicative, not a precise age.";
// §3 — persistent, non-alarmist interpretation disclaimer. ONE shared source; shown on
// Longevity (both a gated/incomplete AND a scored user) and under a chat answer that states
// the age. Additive to the existing "Lab-verified results required for insurance use" note.
const BIOAGE_DISCLAIMER = "Your biological age is a wellness estimate from a single blood panel using the validated PhenoAge method (Levine 2018), intended for apparently-healthy, preventive use. It reflects that day's markers and can be skewed by acute illness — an infection, for example, raises CRP and can temporarily inflate the estimate — as well as by hydration or lab variation. It is not a diagnosis or a medical assessment, and it is not an insurance figure.";
function _bioageInfo(metric) {
  if (!metric) return null;
  const s = String(metric).toLowerCase();
  for (const k of Object.keys(BIOAGE_MARKER_INFO)) {
    if (s.indexOf(k) >= 0) return { key: k, ...BIOAGE_MARKER_INFO[k] };
  }
  if (/lympho/.test(s)) return { key: 'lymphocyte', ...BIOAGE_MARKER_INFO['lymphocyte'] };
  if (/white blood|leu[ck]ocyte/.test(s)) return { key: 'wbc', ...BIOAGE_MARKER_INFO['wbc'] };
  if (/alp|alkaline/.test(s)) return { key: 'alkaline phosphatase', ...BIOAGE_MARKER_INFO['alkaline phosphatase'] };
  if (/corpuscular volume/.test(s)) return { key: 'mcv', ...BIOAGE_MARKER_INFO['mcv'] };
  return null;
}

// ══ C1 (KAL-248) — HARM ANCHOR FOR THE LONGEVITY SURFACE ══════════════════════════════
// Zikmund-Fisher BJ, Scherer AM, Witteman HO, et al. "Effect of Harm Anchors in Visual Displays
// of Test Results on Patient Perceptions of Urgency About Near-Normal Values." J Med Internet
// Res 2018;20(3):e98. n=1,618. A SECOND labelled reference point — the level at which a
// clinician would actually act — reduced perceived urgency and intent to contact a doctor for
// NEAR-NORMAL values WITHOUT blunting response to genuinely extreme ones. That last clause is
// what makes it safe to show.
//
// ⚠ THIS IS A SECOND TABLE, AND THAT IS DELIBERATE, NOT DUPLICATION.
// HARM_ANCHORS (near _BPRangeBar) is keyed by CANONICAL MARKER NAME, because that surface reads
// `m.displayName` straight from the panels payload. This surface resolves markers through
// `_bioageInfo()` into nine short KEYS ('crp', 'wbc', 'alkaline phosphatase' …) via substring
// matching, and never sees a canonical name. Keying one table both ways would mean a lookup
// that silently returns nothing for half its callers — which is the failure mode a shared table
// would have hidden. Same THRESHOLDS and same sources in both; only the key differs.
//
// Keyed on the nine PhenoAge inputs only, because that is all this list renders.
const BIOAGE_HARM_ANCHOR = {
  // CRP >10 mg/L is the conventional cut for clinically significant inflammation; below it is
  // the low-grade range used for cardiovascular risk, not for acute concern.
  // Pearson TA, et al. AHA/CDC Scientific Statement. Circulation 2003;107:499-511.
  'crp':                  { at: 10,  unit: 'mg/L',    note: 'clinically significant inflammation' },
  // Fasting glucose ≥7.0 mmol/L (126 mg/dL) is the diabetes diagnostic threshold.
  // WHO/IDF 2006; ADA Standards of Care 2024.
  'glucose':              { at: 126, unit: 'mg/dL',   note: 'the diabetes diagnostic threshold' },
  // ALP >3× the upper reference limit (120 U/L) is where hepatocellular/cholestatic injury is
  // investigated rather than observed. Kwo PY, Cohen SM, Lim JK. ACG Clinical Guideline:
  // Evaluation of Abnormal Liver Chemistries. Am J Gastroenterol 2017;112:18-35.
  'alkaline phosphatase': { at: 360, unit: 'U/L',     note: '3× the upper reference limit' },
  // WBC >20 ×10³/µL is the conventional trigger for urgent haematology review.
  // NICE NG12, suspected cancer: recognition and referral (haematological).
  'wbc':                  { at: 20,  unit: '10³/µL',  note: 'urgent haematology review' },
};

// ⚠ THE FIVE MARKERS WITH NO ANCHOR, AND WHY — C1 says omit rather than invent, and an invented
// anchor is worse than none because it borrows the authority of a citation it does not have.
// Rendered in the UI (see the drawer) rather than only living here, because "we could not source
// one" is information the reader is entitled to.
//   creatinine  — the real action threshold is eGFR-based (NICE NG203 refers on eGFR <30), and
//                 eGFR needs age and sex, so NO single creatinine value expresses it. A lab
//                 "critical value" (~4.0 mg/dL, Kost 1990) exists but is the phone-the-clinician
//                 level, far above where a doctor first takes interest — using it as the anchor
//                 would make almost every raised creatinine look reassuring, which is the
//                 opposite failure to the one C1 corrects.
//   albumin     — interpreted against nutrition, inflammation and liver synthesis together.
//   lymphocyte  — a PERCENTAGE; meaningful only beside the absolute count and the rest of the
//                 differential.
//   mcv, rdw    — red-cell indices, read as a pattern with Hb/Hct/ferritin, not against a level
//                 at which a clinician acts on that number alone.
const BIOAGE_NO_ANCHOR = {
  'creatinine': 'the level that matters is eGFR-based and needs age and sex',
  'albumin':    'read alongside nutrition, inflammation and liver synthesis',
  'lymphocyte': 'a percentage — meaningful beside the absolute count',
  'mcv':        'a red-cell index, read as a pattern with the others',
  'rdw':        'a red-cell index, read as a pattern with the others',
};

function bioageAnchor(key) {
  if (!key || !FEATURES_GLOBAL.lab_presentation_v2) return null;
  return BIOAGE_HARM_ANCHOR[key] || null;
}

// Run 5 §A — one-line lay descriptors (≤6 words) for the ranked PhenoAge list.
const BIOAGE_DESC = {
  'albumin': 'Nutrition & liver protein',
  'creatinine': 'Kidney filtering marker',
  'glucose': 'Fasting blood sugar',
  'crp': 'Body-wide inflammation marker',
  'lymphocyte': 'Immune cell balance',
  'mcv': 'Red blood cell size',
  'rdw': 'Red-cell size variation',
  'alkaline phosphatase': 'Liver & bone enzyme',
  'wbc': 'White blood cell count',
};
// Standard adult reference ranges — fallback when NPL's marker_panels has no entry for a
// PhenoAge marker (panels currently cover only Albumin/Creatinine/MCV/CRP). [DECISION]
// sources: routine UK/US adult ranges — Albumin 3.5–5.0 g/dL, Glucose(fasting) 70–99 mg/dL,
// ALP 30–120 U/L, WBC 4.0–11.0 10³/µL, Lymphocyte 20–40 %, RDW 11.5–14.5 %, MCV 80–100 fL,
// Creatinine 0.7–1.3 mg/dL, CRP <5 mg/L. NPL's printed range is preferred where present.
const BIOAGE_RANGE = {
  'albumin': [3.5, 5.0], 'glucose': [70, 99], 'alkaline phosphatase': [30, 120],
  'wbc': [4.0, 11.0], 'lymphocyte': [20, 40], 'rdw': [11.5, 14.5],
  'mcv': [80, 100], 'creatinine': [0.7, 1.3], 'crp': [0, 5],
};
// C4 (KAL-248) — WCAG 1.4.1: a state must never be carried by hue alone. The pill already had
// TEXT ('High', 'Within range'), and the number line already encodes position; this adds the
// third channel, a glyph, so the state survives greyscale, colour-blindness and a low-quality
// screen. Same glyph vocabulary as _BP_STATUS on the other marker surface, deliberately.
const UBA_GLYPH = { ok: '✓', near: '~', far: '!', muted: '?' };

function _bioageComputeStatus(value, low, high) {
  if (value == null || low == null || high == null) return null;
  const near = (high - low) * 0.1;
  if (value < low - near) return 'low';
  if (value < low) return 'low_borderline';
  if (value > high + near) return 'high';
  if (value > high) return 'high_borderline';
  return 'normal';
}
// Resolve a marker's display value/unit/range/status from panel (preferred) then fallback.
function _bioageResolve(v) {
  const p = v.panel;
  const val = (p && p.latest && p.latest.value != null) ? p.latest.value : (v.rawValue != null ? Number(v.rawValue) : null);
  const unit = (p && (p.canonicalUnit || p.unit)) || v.unit || (p && p.latest && p.latest.unit) || '';
  const range = (p && p.refLow != null && p.refHigh != null) ? [p.refLow, p.refHigh] : (BIOAGE_RANGE[v.info.key] || null);
  const status = (p && p.status) ? p.status : (range ? _bioageComputeStatus(val, range[0], range[1]) : null);
  return { val, unit, low: range ? range[0] : null, high: range ? range[1] : null, status, label: _bioageStatusLabel(status) };
}
// marker_engine status → calm evaluative label (word + tone; never alarm-red).
function _bioageStatusLabel(status) {
  switch (status) {
    case 'normal': case 'in_range': case 'optimal': return { text: 'Within range', tone: 'ok' };
    case 'high_borderline': case 'borderline_high': return { text: 'Slightly high', tone: 'near' };
    case 'low_borderline': case 'borderline_low': return { text: 'Slightly low', tone: 'near' };
    case 'high': return { text: 'High', tone: 'far' };
    case 'low': return { text: 'Low', tone: 'far' };
    case 'data_error': return { text: 'Check reading', tone: 'muted' };
    default: return status ? { text: 'Within range', tone: 'ok' } : null;
  }
}
// RUN7/8 — coherent sign convention for the per-marker year effect (age-matched decomposition).
// Positive = ageing (calm warm tone); negative = protective (calm teal). Direction is ALWAYS
// worded (never colour alone) for accessibility. RUN8: the nine effects + a "Combined effect"
// line now reconcile EXACTLY to the headline offset (unit/log bugs fixed; see _phenoage_attribution).
function _bioageImpact(imp) {
  if (imp == null) return { str: '—', word: '', cls: 'imp-neutral', full: 'not scored' };
  const n = Number(imp);
  if (Math.abs(n) < 0.05) return { str: '≈0y', word: 'no effect', cls: 'imp-neutral', full: 'about neutral' };
  const ageing = n > 0;
  return {
    str: `${ageing ? '+' : '−'}${Math.abs(n).toFixed(1)}y`,
    word: ageing ? 'older' : 'younger',
    cls: ageing ? 'imp-ageing' : 'imp-protective',
    full: ageing ? T('adding to your biological age') : T('working in your favour'),
  };
}

// RUN7 §3 — DATA-DRIVEN CONTEXT ENGINE. Research-based relevance map (config, not per-marker
// hardcoded sentences): which modifiable signals + medication name-patterns are relevant to each
// PhenoAge marker, and the evidence citation. Keyed by _bioageInfo key.
const MARKER_RELEVANCE = {
  crp:                    { signals: ['activity', 'weight', 'sleep', 'smoking', 'vaping'], meds: ['omega', 'fish oil', 'curcumin', 'turmeric'], cite: 'Mendelian randomisation, medRxiv 2022 (CRP ↔ BMI/activity/smoking)' },
  glucose:                { signals: ['activity', 'weight', 'diet', 'sleep'], meds: ['mounjaro', 'tirzepatide', 'semaglutide', 'ozempic', 'metformin', 'berberine'], cite: 'CGM & lifestyle review 2025 (diet/sleep/activity modify fasting glucose)' },
  wbc:                    { signals: ['activity', 'smoking', 'vaping', 'recent_infection', 'sleep'], meds: [], cite: 'Yagi 2025 (inflammation ↔ activity/smoking/BMI)' },
  lymphocyte:             { signals: ['sleep', 'activity', 'recent_infection', 'stress', 'nutrition'], meds: [], cite: 'Feriel 2021 IJLH; Duggal 2019 Nat Rev Immunol' },
  albumin:                { signals: ['nutrition', 'alcohol', 'weight'], meds: [], cite: 'nutrition-status literature' },
  'alkaline phosphatase': { signals: ['alcohol', 'weight'], meds: ['vitamin d', 'd3', 'cholecalciferol'], cite: 'hepatobiliary & bone evidence' },
  rdw:                    { signals: ['nutrition', 'activity'], meds: ['b12', 'cobalamin', 'folate', 'folic', 'iron', 'ferrous', 'vitamin d', 'd3'], cite: 'BLSA (RDW ↔ inflammation/metabolic over time)' },
  mcv:                    { signals: ['alcohol'], meds: ['b12', 'cobalamin', 'folate', 'folic'], cite: 'standard haematology' },
  creatinine:             { signals: ['activity', 'hydration', 'protein'], meds: ['creatine'], cite: 'muscle-metabolism literature' },
};
// §B3 — de-duplicate repeated comma-separated tokens in a stored fact value.
// A lifestyle-write bug (see /api/profile/lifestyle PUT) could concatenate the same
// value twice ("high protein, high protein"); this renders it clean regardless. Order-
// preserving, case-insensitive dedup; collapses stray whitespace; leaves single values as-is.
function _dedupeCsvTokens(v) {
  if (v == null) return v;
  const seen = new Set();
  const out = String(v).split(',').map(s => s.trim().replace(/\s+/g, ' ')).filter(Boolean)
    .filter(t => { const k = t.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  return out.length ? out.join(', ') : String(v).trim();
}
// Compose the user's REAL context for one marker from only the signals relevant to it.
// Missing signals are omitted silently (no placeholder). ctx = {azm, sleepH, weightTrend, ledgerFacts, meds}.
function _markerRealContext(key, ctx) {
  const cfg = MARKER_RELEVANCE[key];
  if (!cfg) return { lines: [], meds: [], cite: null };
  const lines = [];
  const has = (s) => cfg.signals.indexOf(s) >= 0;
  // RUN11 §6 — fitness-sufficiency gate: only bring a fitness clause into a LONG-TERM marker
  // insight when the wearable history is long enough to support it (sufficient_for_trend from
  // /active-minutes). On a short window (e.g. NPL's ~30-day Health Connect) the clause is omitted
  // here — those AZM nudges live on the Today engagement surface, not in blood-marker context.
  if (has('activity') && ctx.azm && ctx.azm.connected && ctx.azm.week && ctx.azm.sufficient_for_trend) {
    const pts = ctx.azm.week.points, tgt = ctx.azm.week.target || 150;
    const lvl = pts >= tgt ? 'strong' : pts >= tgt * 0.5 ? 'moderate' : 'on the lighter side';
    lines.push(`Your activity is ${lvl} lately (${pts} of ${tgt} active-zone minutes this week)${pts < tgt * 0.5 ? ' — building it up would help here' : T(' — which works in your favour')}.`);
  }
  if (has('sleep') && ctx.sleepH) lines.push(`Your sleep is averaging ~${ctx.sleepH}h${ctx.sleepH < 7 ? ' — more consistent sleep could help this' : ' — which supports this'}.`);
  if ((has('weight') || has('nutrition')) && ctx.weightTrend) lines.push(`Your weight is trending ${ctx.weightTrend}.`);
  // §B2 — weave the user's OWN lifestyle facts into natural prose (only the signals
  // relevant to THIS marker), instead of the mechanical "You've told Kalpa: X — Y."
  // bolt-on. §B3 — de-dupe repeated comma tokens in a fact value on the way in
  // ("high protein, high protein" -> "high protein"), so a malformed stored value
  // never surfaces doubled here. One line per underlying fact (seen-by fact_key).
  const factBy = {}; (ctx.ledgerFacts || []).forEach(f => { if (f.fact_key) factBy[f.fact_key] = f.fact_value; });
  const weaveFor = {
    diet:             (v) => `You've described your diet as ${v}, which feeds into this.`,
    nutrition:        (v) => `Your nutrition — you've described your diet as ${v} — is relevant here.`,
    protein:          (v) => `You've described your diet as ${v}; a higher-protein diet can raise measured creatinine on its own, apart from kidney function.`,
    alcohol:          (v) => `You've described your alcohol intake as ${v} — one of the more modifiable influences on this marker.`,
    smoking:          (v) => `You've mentioned smoking (${v}), which tends to raise this.`,
    vaping:           (v) => `You've mentioned vaping (${v}), which can contribute here.`,
    stress:           (v) => `You've noted stress (${v}); sustained stress can shift this.`,
    recent_infection: (v) => `You've noted a recent infection (${v}) — that can transiently move this reading.`,
    hydration:        (v) => `You've described your hydration as ${v}, which affects this reading.`,
  };
  const seen = {};
  cfg.signals.forEach(sig => {
    const fk = ({ nutrition: 'diet', protein: 'diet' })[sig] || sig;
    const w = weaveFor[sig];
    if (w && factBy[fk] && !seen[fk]) { seen[fk] = 1; lines.push(w(_dedupeCsvTokens(factBy[fk]))); }
  });
  // Relevant medications ONLY (name-pattern match) — never dump the full list.
  const meds = (ctx.meds || []).filter(m => { const n = (m.name || '').toLowerCase(); return cfg.meds.some(p => n.indexOf(p) >= 0); });
  return { lines, meds, cite: cfg.cite };
}
// Number-line range bar: reference range as the shaded in-range zone on a padded axis,
// the user's value as a marker. Number-line reduces false urgency vs a bare number (JMIR).
function _RangeBar({ value, low, high, tone, large, anchor }) {
  if (value == null || low == null || high == null || !(high > low)) return null;
  const span = high - low;
  // C1 — the axis must STRETCH to include the anchor. Left at ±0.6·span, an anchor beyond that
  // would pin to 100% and imply the user's value is nearly at it, which is the exact false
  // urgency the anchor exists to remove.
  let axisLow = low - span * 0.6, axisHigh = high + span * 0.6;
  if (anchor) {
    if (anchor.below) axisLow = Math.min(axisLow, anchor.at - span * 0.2);
    else axisHigh = Math.max(axisHigh, anchor.at + span * 0.2);
  }
  const axisSpan = axisHigh - axisLow;
  const pct = (x) => Math.max(0, Math.min(100, ((x - axisLow) / axisSpan) * 100));
  return (
    <div className={`rbar${large ? ' rbar-lg' : ''}`}>
      <div className="rbar-track" />
      <div className="rbar-zone" style={{ left: pct(low) + '%', width: (pct(high) - pct(low)) + '%' }} />
      {/* C1 — a TICK, visually subordinate to the value marker. It is a reference point, not a
          second result, and must not compete for attention. */}
      {anchor && <div className="rbar-anchor" aria-hidden="true" style={{ left: pct(anchor.at) + '%' }} />}
      <div className="rbar-marker" data-tone={tone || 'ok'} style={{ left: pct(value) + '%' }} />
    </div>
  );
}

// C1 — the anchor's caption. Separate component so the ranked row and the drawer render the
// SAME sentence; two copies of this string would drift.
function _RangeAnchorNote({ anchor }) {
  if (!anchor) return null;
  return (
    <div className="rbar-anchor-note">
      <span className="rbar-anchor-key" aria-hidden="true" />
      {`Doctors typically aren’t concerned until ${anchor.below ? 'below ' : ''}${anchor.at}${anchor.unit ? ' ' + anchor.unit : ''}`}
      <span className="rbar-anchor-why">{` — ${anchor.note}.`}</span>
    </div>
  );
}

// ══ KAL-098 — PANEL RECOMMENDATION (research doc Part 2) ════════════════════
// Renders whatever panel_recommendation.py decided, including — most of the time — that the
// honest answer is "nothing". The "nothing" state is presented as REASSURANCE, not as an empty
// view or a gap to fill: Cochrane found general health checks in asymptomatic adults do not
// reduce morbidity or mortality while creating real harm, so declining to test IS the product
// working. The conflict-of-interest disclosure renders with every state, at body-copy size.
function PanelRecommendation() {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [email, setEmail] = useState('');
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/panel/recommendation')
      .then(d => alive && (d && d.state ? setRec(d) : null))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!rec) return null;

  const STATE_LABEL = {
    nothing: 'No test needed', targeted: 'Targeted retest',
    baseline: 'Baseline panel', unblock: 'One gap', refer: 'Discuss with your doctor',
  };
  const register = () => {
    setBusy(true);
    api.post('/panel/interest', { panel_id: rec.ideal ? rec.ideal.id : 'preventive_health_panel',
                                  email: email })
      // KAL-136 — surface the SERVER's reason. This swallowed every failure into one generic
      // line, so a refused panel id and a dead network were indistinguishable to the user and
      // to support. api.post rejects on non-2xx with the parsed body attached where there is
      // one, so both paths are checked.
      .then(r => setDone(r && r.ok ? (r.note || 'Registered.')
                                   : ((r && r.error) || 'Could not register just now.')))
      .catch(e => setDone((e && e.body && e.body.error) || 'Could not register just now.'))
      .finally(() => { setBusy(false); setShowModal(false); });
  };

  return (
    <div className="panelrec">
      <span className="panelrec-state">{STATE_LABEL[rec.state] || rec.state}</span>
      <div className="panelrec-head">{rec.headline}</div>
      <div className="panelrec-why">{rec.reason}</div>
      {rec.caveat && <div className="panelrec-alt">{rec.caveat}</div>}

      {rec.ideal && (
        <>
          <div className="panelrec-marker" style={{ marginTop: 14 }}>
            <span>{rec.ideal.name}</span>
            <span className="panelrec-marker-when">being finalised</span>
          </div>
          <div className="panelrec-alt">{rec.orderable_note}</div>
          <button className="panelrec-cta" onClick={() => setShowModal(true)}>
            Register interest
          </button>
        </>
      )}

      {rec.packages && rec.packages.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {rec.packages.map(p => (
            <div className="panelrec-marker" key={p.id}>
              {/* An individual test's name IS its marker, so listing covers repeats it
                  ("HbA1c · HbA1c"). Only show covers when it adds information. */}
              <span>{p.name}{p.covers && p.covers.length &&
                !(p.covers.length === 1 && p.covers[0].toLowerCase() === p.name.toLowerCase())
                ? ' · ' + p.covers.join(', ') : ''}</span>
              <span className="panelrec-marker-when">
                {p.price_npr ? 'Rs ' + p.price_npr.toLocaleString('en-GB') : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {done && <div className="panelrec-alt">{done}</div>}

      {/* Disclosed with EVERY state, same size as body copy, directly under the thing it
          qualifies — not tucked into a settings page. */}
      <div className="panelrec-coi">{rec.coi}</div>

      {showModal && (
        <KalpaDrawer title="Preventive Health Panel" onClose={() => setShowModal(false)}>
          <div className="kdrawer-sleep-summary">
            This panel is being finalised with National Path Lab. Register your interest and
            we’ll let you know when it’s available.
          </div>
          <div className="tgt-row">
            <input className="tgt-input" style={{ flex: 1 }} type="email"
              placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)} aria-label="Email for panel availability" />
          </div>
          <button className="tgt-save" disabled={busy} onClick={register}>
            {busy ? 'Registering…' : 'Register interest'}
          </button>
        </KalpaDrawer>
      )}
    </div>
  );
}


// ── KAL-156 — TARGETED RETEST PANEL (TEMPORARY) ──────────────────────────────────────────
// ⚠ TEMPORARY: remove, or wire the rows to real order buttons, once ordering exists.
// The "(not on record)" label is DATA-DRIVEN — it asks whether the marker is actually absent
// from THIS user's history. Hardcoding the words would have been correct for Tim today and
// wrong for the next user who has an HbA1c on file, which is the sort of thing that quietly
// turns a recommendation into a false statement.
// ⚠⚠ OFF BY TIM'S INSTRUCTION, 14 Aug 2026 — DO NOT RE-ENABLE WITHOUT HIS WORD.
// ⚠ AND THERE WERE **TWO** BLOCKS RENDERING THIS CONTENT, WHICH IS THE FINDING THAT
//   MATTERS. Turning off the one below left the copy still on the page — measured: the
//   body text still contained “A targeted retest is worth it now” and the NPL footer with
//   zero `.retest-panel` nodes in the DOM. The second source is `PanelRecommendation`
//   (KAL-098, `.panelrec`, App.js ~11087), whose copy comes from the BACKEND at
//   backend/panel_recommendation.py:349 — same headline, same HbA1c/Lipid rows, same NPL
//   footer, plus a “TARGETED RETEST” badge, sitting at the TOP of the Longevity page
//   above “Longevity Score”. That badge is the one in Tim's report, so the block he was
//   pointing at was the KAL-098 card, not the KAL-156 panel below the markers.
//   THIS CONSTANT NOW GATES BOTH. The backend endpoint is left untouched — nothing calls
//   it while the card is dark, and gutting a working recommendation engine to hide a
//   surface would be the wrong lever.
// The panel stays in the codebase, wired and working, behind this ONE constant. Tim wants
// it dark until real ORDER BUTTONS exist and the Preventive Health Panel ships — a
// recommendation to retest with no way to act on it is a dead end, and the NPL footer
// makes a commercial statement the product cannot yet honour.
// A FLAG, NOT A DELETION, and not a comment block either: a commented-out JSX island
// rots (it stops being type-checked, linted and refactored with the code around it),
// whereas this constant keeps RetestPanel a live reference that every rename and every
// lint pass still sees. Flip it to true and the panel returns exactly where it was —
// directly under the last marker row, above the section's wellness disclaimer (KAL-175).
// It is NOT a console/DB feature flag on purpose: this is a build-time hold on an
// unfinished surface, not per-instance behaviour, so it must not become a flag an
// operator could switch on by accident.
// KAL-237 — WAS `const RETEST_PANEL_ENABLED = false;`, a hardcoded boolean gating a LAB-ORDER
// surface. CLAUDE.md's rule is explicit: per-instance behaviour is console-owned, "never a
// static file boolean" — and this governs the same domain as order_lab. Now read from the
// flag store like everything else; still false on every instance, so nothing turns on here.
// A function, not a const, because FEATURES_GLOBAL is populated asynchronously.
const retestPanelEnabled = () => !!FEATURES_GLOBAL.retest_panel;   // covers BOTH sources

function RetestPanel() {
  const [have, setHave] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/clinical-timeline/panels')
      .then(d => {
        if (!alive) return;
        const names = [];
        (Array.isArray(d) ? d : (d && d.panels) || []).forEach(pn =>
          (pn.markers || pn.rows || []).forEach(m => {
            const n = (m.marker_name || m.name || '').toLowerCase();
            if (n) names.push(n);
          }));
        setHave(names);
      })
      .catch(() => alive && setHave([]));
    return () => { alive = false; };
  }, []);
  if (have === null) return null;              // never flash a half-built recommendation
  const has = (needles) => needles.some(n => have.some(h => h.includes(n)));
  const ROWS = [
    { label: 'HbA1c', match: ['hba1c', 'glycated', 'a1c'] },
    { label: 'Lipid profile · Lipids', match: ['ldl', 'hdl', 'triglyc', 'cholesterol', 'lipid'] },
  ];
  return (
    <div className="retest-panel">
      <div className="retest-head">A targeted retest is worth it now</div>
      <div className="retest-body">
        These are the markers where a retest could now show a real change:
      </div>
      {ROWS.map(r => {
        const onRecord = has(r.match);
        return (
          <div className="retest-row" key={r.label}>
            <span>{r.label}</span>
            <span className="retest-state">
              {onRecord ? 'on record — due a retest' : '(not on record)'}
            </span>
          </div>
        );
      })}
      <div className="retest-foot">
        National Path Lab provides both this recommendation and the test itself. This
        recommendation follows published retest guidelines (ADA, AHA/CDC, USPSTF) and your own
        marker kinetics — not a sales target. You are free to test anywhere, or not at all.
      </div>
    </div>
  );
}

function LongevityPage({ onNav }) {
  // genetics feature flag — gates the My Genome entry point below. On NPL
  // (kalpa_only / genetics:false from /api/features) this is false, so the
  // entry point never renders; the #genome route is likewise gated at the
  // App router. Genomics stays entirely off NPL.
  const features = useFeatures();
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which scoring-layer card is currently expanded (one at a time, click
  // again to collapse). Drives the per-variable breakdown inside each card.
  const [expandedLayer, setExpandedLayer] = useState(null);
  // Methodology modal — opened from the ⓘ next to "Biological Age".
  const [showMethodology, setShowMethodology] = useState(false);
  // Section A — right-side marker drawer (honest per-marker education).
  const [drawerMarker, setDrawerMarker] = useState(null);
  // Run 5 §A — merge in NPL's reference ranges + engine status + reading history from
  // /clinical-timeline/panels (same canonical source as Health Record), keyed by marker.
  const [panelByKey, setPanelByKey] = useState({});
  // S1.1 engine change — the drawer used to DISCARD non-PhenoAge markers (only the 9
  // were kept). Cross-marker clauses (§2) need the full set (HbA1c, GGT, ALT, B12,
  // folate, ferritin, iron, haematocrit, vitamin D…), so capture the WHOLE panel map
  // keyed by lowercased marker name. No new endpoint — same /clinical-timeline/panels.
  const [fullPanelByName, setFullPanelByName] = useState({});
  useEffect(() => {
    let alive = true;
    api.get('/clinical-timeline/panels').then(d => {
      if (!alive) return;
      convertPanelsPayload(d);
      const panels = (d && d.panels) ? d.panels : (Array.isArray(d) ? d : []);
      const map = {};
      const full = {};
      panels.forEach(p => (p.markers || []).forEach(m => {
        const info = _bioageInfo(m.name);
        if (info && !map[info.key]) map[info.key] = m;
        const nm = String(m.name || '').toLowerCase().trim();
        if (nm && !full[nm]) full[nm] = m;
      }));
      setPanelByKey(map);
      setFullPanelByName(full);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Contextual NPL order nudges that target the Longevity placement.
  const orderNudges = useOrderPrompts('longevity');

  // RUN6 §2 — real per-user context for the marker drawers: the wellness context ledger
  // (what the user told Kalpa), their active medicines, and their AZM fitness engine.
  const [ledgerFacts, setLedgerFacts] = useState([]);
  const [meds, setMeds] = useState([]);
  const [azm, setAzm] = useState(null);
  const [ctxSignals, setCtxSignals] = useState({});   // RUN7 §3 — real signals (sleep/weight)
  // S1.1 — biological sex + age for the CKD-EPI (2021 race-free) eGFR used by the
  // creatinine cross-marker clause. Compute-and-use only; never stored as a lab value.
  const [bioSex, setBioSex] = useState('male');
  useEffect(() => {
    let alive = true;
    api.get('/health-context').then(d => { if (alive) { setLedgerFacts((d && d.facts) || []); setCtxSignals((d && d.signals) || {}); } }).catch(() => {});
    api.get('/profile').then(d => { if (alive && d && (d.biological_sex || d.sex)) setBioSex(d.biological_sex || d.sex); }).catch(() => {});
    api.get('/medicines').then(d => { if (alive) setMeds(Array.isArray(d) ? d : (d && d.medicines) || []); }).catch(() => {});
    fetch(`${API_BASE}/active-minutes`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null).then(d => { if (alive && d) setAzm(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // KAL-322 — retry (2 s / 5 s / 10 s) instead of a 20 s dead end; inline loading, not a
  // full-page spinner; real error only after the retries fail.
  const [retrying, setRetrying] = useState(0);
  const loadScore = useCallback(() => {
    setRetrying(0);
    return apiGetWithRetry('/longevity-score', { label: 'longevity', onRetry: setRetrying })
      .then(d => { setScore(d); setLoading(false); setRetrying(0); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);
  useEffect(() => { loadScore(); }, [loadScore]);
  useScreenSync(loadScore);   // pull-to-refresh + status strip (Longevity)

  if (loading) return <LoadingInline label="Calculating your biological age" retrying={retrying} />;
  if (error) return <ErrorState message={error} />;
  if (!score) return null;

  // NOTE: mortality_risk_10yr is intentionally NOT destructured/rendered here — the 10yr
  // mortality % is hidden from the consumer Longevity view (display-honesty). It remains in
  // the /api/longevity-score response + underwriting/export path (backend unchanged).
  const { biological_age, chronological_age, offset, categories, drivers_younger, drivers_older, score_tier, score_tier_label, verified_inputs, total_inputs, provenance_caveat, data_provenance, phenoage_draw, display, completeness } = score;
  // HONEST display (23 Jul 2026): the headline shows the UNCLAMPED PhenoAge + a NHANES 80% band,
  // and SUPPRESSES the number only when the band lower bound < 20 (implausibly young). `display`
  // (from the backend) is authoritative; biological_age stays raw (used only for the ring fill).
  const dispBand = !!(display && display.interim && display.display_mode === 'suppressed');
  const dispAge = (display && display.display_age != null) ? display.display_age : biological_age;
  // RUN11.3 §4 — the headline uses a SINGLE COMPLETE DRAW. If the most-recent draw is incomplete,
  // show "Incomplete panel — N of 9" (from that draw's date) rather than borrowing older markers.
  const phenoIncomplete = !!(phenoage_draw && phenoage_draw.complete === false && (phenoage_draw.present || 0) < 9);
  const bioInsufficient = !!(data_provenance && data_provenance.sufficient === false);
  const bioLimited = !!(data_provenance && data_provenance.sufficient && data_provenance.label === 'limited');
  const ageColor = offset <= -3 ? '#22c55e' : offset <= -1 ? '#4ade80' : offset <= 1 ? '#60a5fa' : offset <= 3 ? '#fb923c' : '#ef4444';
  // When insufficient the backend nulls biological_age/offset — never fall
  // through to "Same as chronological age" (that reads as a real claim).
  const offsetLabel = (bioInsufficient || biological_age == null || offset == null)
    ? ''
    : offset < 0 ? `${fmtYearsMonths(offset)} younger than chronological`
    : offset > 0 ? `${fmtYearsMonths(offset)} older than chronological`
    : 'Same as chronological age';

  const gaugeMin = Math.round(chronological_age - 10);
  const gaugeMax = Math.round(chronological_age + 6);
  // Only draw the needle when we actually have a bio-age to point to.
  const showNeedle = !bioInsufficient && biological_age != null;
  const gaugeAngle = showNeedle
    ? Math.max(0, Math.min(180, ((biological_age - gaugeMin) / (gaugeMax - gaugeMin)) * 180))
    : 0;

  // RUN6 \u00a72 \u2014 the old Scoring-Layers / Making-You-Younger / Aging-You-Faster panels are
  // RETIRED (age-framed composite). Year-impacts now live ONLY in the ranked PhenoAge table
  // below; Fitness is a daily AZM score (no age impact) and CVD is a cited signals panel.
  const allDriverRows = [
    ...((categories.cardiovascular && categories.cardiovascular.drivers) || []),
    ...((categories.fitness        && categories.fitness.drivers)        || []),
  ];
  const variablesByLayer = {
    phenoage: (categories.phenoage && categories.phenoage.components) ? categories.phenoage.components.map(c => ({
      metric: c.marker,
      value: c.missing ? 'not in your labs' : `${c.value} ${c.unit}`,
      rawValue: c.missing ? null : c.value,
      unit: c.unit,
      impact: c.missing ? null : c.age_impact,
      missing: !!c.missing,
      detail: c.missing ? 'add this marker for a full score' : `pop avg ${c.population_avg}`,
    })) : [],
    cardiovascular: allDriverRows
      .filter(d => LAYER_BY_METRIC[d.metric] === 'cardiovascular')
      .map(d => ({ metric: d.metric, value: d.value, impact: d.impact, detail: d.detail, ref: d.ref })),
    fitness: allDriverRows
      .filter(d => LAYER_BY_METRIC[d.metric] === 'fitness')
      .map(d => ({ metric: d.metric, value: d.value, impact: d.impact, detail: d.detail, ref: d.ref })),
  };

  // Improve-today: every variable currently in the orange/red zone, ranked
  // by year impact (largest first). Includes a concrete action tip.
  const improveToday = [
    ...variablesByLayer.phenoage,
    ...variablesByLayer.cardiovascular,
    ...variablesByLayer.fitness,
  ].filter(v => v.impact > 0.3)
   .sort((a, b) => b.impact - a.impact)
   .slice(0, 5);

  return (
    <div className="longevity-page">
      {showMethodology && <BiologicalAgeMethodologyModal onClose={() => setShowMethodology(false)} />}
      {/* KAL-098 — the ONLY place a blood-panel prompt may appear. The behaviour tracker used to
          carry a duplicate "Book a blood panel", which made a behaviour surface argue for a lab
          order; it is gone. What renders here is decided by the kinetics model, which is equally
          able to conclude that nothing is worth testing. */}
      {retestPanelEnabled() && <PanelRecommendation />}
      {/* KAL-235 — the panel-order prompt (NPL only, features.order_lab). Deliberately guarded
          on !RETEST_PANEL_ENABLED so KAL-098's rule survives: exactly ONE blood-panel prompt
          may appear on this page. That constant is false on every instance today, so there is
          no live conflict — but writing the guard structurally means flipping it later cannot
          silently reintroduce the duplicate KAL-098 removed. */}
      {!retestPanelEnabled() && orderNudges.prompts.length === 0
        && <OrderPanelPrompt placement="longevity" />}
      {/* Section A — right-side marker drawer (slide-in; full-height sheet on mobile). */}
      {drawerMarker && drawerMarker.info && (() => {
        const v = drawerMarker; const info = v.info; const p = v.panel;
        const r = _bioageResolve(v);
        const val = r.val, unit = r.unit, lbl = r.label;
        const outOfRange = !!(lbl && lbl.tone === 'far');   // High/Low = out of range
        const hist = (p && p.history) || [];
        const hasTrend = hist.length > 1;   // graph shown ⇔ ≥2 readings
        const hasRange = (r.low != null && r.high != null);
        // Absolute lymphocyte count: a % marker also gets its absolute (WBC × %).
        const isPercent = /%/.test(unit || '');
        let absLymph = null;
        if (info.key === 'lymphocyte' && isPercent && val != null) {
          const wbcRow = (variablesByLayer.phenoage || []).find(x => (_bioageInfo(x.metric) || {}).key === 'wbc');
          const wbcVal = wbcRow ? (wbcRow.rawValue != null ? Number(wbcRow.rawValue) : null) : null;
          if (wbcVal != null) absLymph = Math.round(wbcVal * (val / 100) * 100) / 100; // ×10⁹/L
        }
        // S1.1 — deterministic Reference-Change-Value trend (spec §1): ≥3 readings, per-marker
        // threshold, unit-safe, else "broadly stable".
        const trend = computeTrend(hist, info.key, formatDate);   // S5 — dates via site-wide formatDate (DD MMM YYYY)
        const trendReady = (hist.length >= 3);
        // Lifestyle weave (diet/alcohol/sleep/activity) — real signals relevant to THIS marker.
        const rc = _markerRealContext(info.key, {
          azm, sleepH: ctxSignals.sleep_avg_h, weightTrend: ctxSignals.weight_trend,
          ledgerFacts, meds,
        });
        // S1.2 — §2 conditional cross-marker + plausibility clauses on the user's REAL data.
        const mkStatus = _bioageComputeStatus(val, r.low, r.high);
        const relatedMarker = (query) => {
          const q = String(query).toLowerCase();
          let m = fullPanelByName[q];
          if (!m) { const k = Object.keys(fullPanelByName).find(kk => kk.indexOf(q) >= 0 || q.indexOf(kk) >= 0); if (k) m = fullPanelByName[k]; }
          if (!m) return null;
          const mv = m.latest ? m.latest.value : (m.value != null ? m.value : null);
          // `unit` is carried so a clause can print the marker's DISPLAY unit rather than
          // assuming one (HbA1c is % in US convention, mmol/mol under NHS).
          return { value: mv, unit: m.unit, status: _bioageComputeStatus(mv, m.refLow, m.refHigh), low: m.refLow, high: m.refHigh };
        };
        const medsHasFn = (pat) => (meds || []).some(m => String(m.name || '').toLowerCase().indexOf(pat) >= 0);
        const factFn = (key) => { const f = (ledgerFacts || []).find(x => (x.fact_key || '') === key); return f ? { value: f.fact_value, date: String(f.fact_date || f.updated_at || '').slice(0, 10) } : null; };
        // ⚠ ckdEpiEgfr REQUIRES creatinine in mg/dL (Inker 2021 coefficients). The panels
        // payload is unit-converted for DISPLAY (UK -> umol/L), so feeding `hist` here would
        // silently compute eGFR from a number ~88x too large. Always use the preserved
        // stored-unit history when a conversion happened.
        const egfrHist = (p && p._stored && p._stored.history) ? p._stored.history : hist;
        const egfr = (info.key === 'creatinine') ? egfrStability(egfrHist, chronological_age, bioSex) : null;
        const clauseRes = evalMarkerClauses(info.key, {
          status: mkStatus, value: val, trend: { direction: trend.direction },
          related: relatedMarker, medsHas: medsHasFn, fact: factFn, egfr: egfr || {},
        });
        // §4 "Your context": fired clauses first, then relevant lifestyle — capped at 2 lines
        // so the drawer never scrolls on desktop.
        const contextLines = [...clauseRes.clauses, ...rc.lines].slice(0, 2);
        const hasContext = contextLines.length > 0;
        return (
          <div className="uba-drawer-backdrop" onClick={() => setDrawerMarker(null)}>
            <div className="uba-drawer" role="dialog" aria-label={info.label} onClick={e => e.stopPropagation()}>
              <div className="uba-drawer-head">
                <span className="uba-drawer-title">{info.label}</span>
                <button className="uba-drawer-close" onClick={() => setDrawerMarker(null)} aria-label="Close">×</button>
              </div>
              {/* 1 — What this is (empowering, one idea) */}
              <div className="uba-field"><div className="uba-field-h">What this is</div><p>{T(info.what)}</p></div>
              {/* 2 — Your result: value + status + range bar */}
              <div className="uba-field">
                <div className="uba-field-h">Your result</div>
                {val != null ? (
                  /* C3 — verdict first, then the number. */
                  FEATURES_GLOBAL.lab_presentation_v2 ? (
                    <div className="uba-result uba-result-v2">
                      {lbl && <span className={`uba-status uba-status-${lbl.tone}`}><span className="uba-status-g" aria-hidden="true">{UBA_GLYPH[lbl.tone] || ''}</span>{lbl.text}</span>}
                      <span className="uba-result-val">{val}{unit ? ` ${unit}` : ''}</span>
                    </div>
                  ) : (
                    <div className="uba-result">
                      <span className="uba-result-val">{val}{unit ? ` ${unit}` : ''}</span>
                      {lbl && <span className={`uba-status uba-status-${lbl.tone}`}>{lbl.text}</span>}
                    </div>
                  )
                ) : <p>Not in your recent labs.</p>}
                {absLymph != null && <div className="uba-abs">≈ {absLymph} ×10⁹/L absolute count (from WBC × %)</div>}
                {hasRange && <_RangeBar value={val} low={r.low} high={r.high} tone={lbl ? lbl.tone : 'ok'} large anchor={bioageAnchor(info.key)} />}
                {/* C2 — ONE RANGE PER VIEW. Scherer AM, Zikmund-Fisher BJ, et al. JMIR
                    2018;20(10):e11027, n=6,776: showing a clinical AND an optimal range together
                    WORSENED comprehension. This renders exactly one — the lab's own printed
                    range via _effective_ref — and the trend sparkline below passes
                    showOptimal={false} for the same reason. */}
                {/* KAL-278 (item 5) — the PhenoAge CRP slot is presented on the AHA/CDC
                    cardiovascular risk bands, never a "typical range": 0-5 is the STANDARD
                    assay's range, and after the assay gate (phenoage_resolve) only a
                    high-sensitivity reading can occupy this slot — so the hs reference is the
                    correct one BY CONSTRUCTION. The first draft of this line referenced a
                    variable from another component's scope, which would have thrown at render;
                    it now reads only `info.key`, which this block already uses two lines down
                    for the harm anchor. */}
                {info.key === 'crp'
                  ? <div className="uba-range-cap">AHA/CDC bands: &lt;1 low · 1–3 average · &gt;3 high{unit ? ` ${unit}` : ''}</div>
                  : hasRange && <div className="uba-range-cap">Typical range {r.low}–{r.high}{unit ? ` ${unit}` : ''}</div>}
                <_RangeAnchorNote anchor={bioageAnchor(info.key)} />
                {/* C1 — where no defensible anchor exists, SAY SO. Silence would read as "there
                    is no level worth knowing about" rather than "we could not source one". */}
                {FEATURES_GLOBAL.lab_presentation_v2 && !bioageAnchor(info.key) && BIOAGE_NO_ANCHOR[info.key] && (
                  <div className="rbar-anchor-note rbar-anchor-none">
                    {`No single-value concern threshold is published for this marker — ${BIOAGE_NO_ANCHOR[info.key]}.`}
                  </div>
                )}
              </div>
              {/* 3 — Your trend (RCV-gated phrase + variability caveat + sparkline) */}
              <div className="uba-field">
                <div className="uba-field-h">Your trend</div>
                {trendReady
                  ? <><p>{T(trend.phrase)} {T(info.trendCaveat)}</p>
                      {hasTrend && <div className="uba-trend"><MarkerSparkline marker={p} showOptimal={false} /></div>}</>
                  : <p>No trend yet — needs at least 3 readings.</p>}
              </div>
              {/* 4 — Your context (woven conditional clauses — fire only on your real data) */}
              {hasContext && (
                <div className="uba-field">
                  <div className="uba-field-h">Your context</div>
                  {contextLines.map((ln, i) => <p key={i}>{T(ln)}</p>)}
                </div>
              )}
              {/* 5 — What can influence it (ONE feasible, gain-framed lever) */}
              <div className="uba-field">
                <div className="uba-field-h">What can influence it</div>
                <p>{T(info.canInfluence)}</p>
              </div>
              {/* 6 — Evidence + typical time to change */}
              <div className="uba-field">
                <div className="uba-field-h">Evidence</div>
                <p className="uba-cite">{T(info.citation)}</p>
                <p className="uba-horizon">Typical time to change: {T(info.horizon)}</p>
              </div>
              {/* KAL-235 placement 3 — the marker drawer. This is where a user is looking
                  directly at one marker, so it is the most contextual place to offer the
                  panel; the prompt only appears at all when the PANEL is incomplete or stale,
                  which is exactly the condition under which this marker is absent or old.
                  Below the disclaimer, so the wellness framing is read first. */}
              <OrderPanelPrompt placement="marker_drawer" compact />
              <div className="uba-drawer-disc">Wellness guidance, not medical advice — Kalpa does not diagnose or treat.</div>
            </div>
          </div>
        );
      })()}
      <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} /><GlossaryTerm term="biological age">Longevity Score</GlossaryTerm></h2>

      <div className="longevity-gauge-section">
        {/* Section B — continuity with the Today hero: the big half-dial is replaced
            by the SAME Kalpa Age dial ring (smaller), TOP-LEFT beside the Biological
            Age text, so "What can I improve today?" clears the fold. ⓘ → methodology. */}
        <div className="longevity-ring-anchor">
          <_KalpaRing mode="dial" size={94} stroke={9}
            marker={(bioInsufficient || biological_age == null) ? null : Math.max(0, Math.min(1, (biological_age - 30) / 50))}>
            {/* Section-1 interim: band → no hard number (a ✓ for a healthy young panel); else the
                (delta-clamped) display age. Ring fill still uses the raw biological_age above. */}
            <span className="lring-num" style={dispBand ? {fontSize:'1.6em'} : undefined}>
              {bioInsufficient ? '—' : (dispBand ? '✓' : fmtYearsMonths(dispAge))}
            </span>
          </_KalpaRing>
          <div className="lring-text">
            <div className="bio-label">
              <GlossaryTerm term="biological age">Biological Age</GlossaryTerm>
              <button
                type="button"
                className="bio-info-btn"
                onClick={() => setShowMethodology(true)}
                aria-label="How is this calculated?"
                title="How PhenoAge builds this"
              >{'ⓘ'}</button>
            </div>
            {phenoIncomplete && completeness && (
              /* Built from THE completeness contract (backend _phenoage_completeness), the same
                 object the Today hero and the Kalpa-Age drawer render — so this line and the hero
                 cannot drift apart again. Previously open-coded from phenoage_draw here. */
              <div className="lring-note">Incomplete panel — {completeness.label} — retest a full panel for a bio-age</div>
            )}
            {/* Suppressed (NHANES 80% band lower bound < 20 → implausibly young): show the
                qualitative healthy-range sentence, NOT a fabricated young number. */}
            {!bioInsufficient && dispBand && display.message && (
              <div className="bio-offset" style={{color:'#5EC8D8'}}>{display.message}</div>
            )}
            {!bioInsufficient && !dispBand && offsetLabel && <div className="bio-offset" style={{color:'#C9973A'}}>{offsetLabel}</div>}
            {/* Point mode: the honest NHANES-III empirical 80% band ("8 in 10 people…"). */}
            {!bioInsufficient && !dispBand && display && display.interim && display.display_mode === 'point' && display.message && (
              <div className="lring-note" style={{marginTop:4}}>{display.message}</div>
            )}
            {/* §2 — young-age caveat: shown WITH the number (not suppressed) when the backend
                tags the draw indicative_young (chrono < 30 on a complete panel). */}
            {!bioInsufficient && !dispBand && biological_age != null && phenoage_draw && phenoage_draw.reliability === 'indicative_young' && (
              <div className="lring-note" style={{marginTop:4}}>{BIOAGE_YOUNG_CAVEAT}</div>
            )}
            {/* ONE completeness fact, from ONE source — and where a second quantity is genuinely
                useful it must NAME ITS BASIS. Two counters over the same 9-marker set used to render
                side by side here and contradict each other: the "Incomplete panel" line above counts
                markers in the SINGLE COMPLETE DRAW the headline is computed from (RUN11.3 §4,
                _resolve_phenoage_single_draw), while data_provenance counts the LATEST value per
                marker across ALL draws (_resolve_phenoage_inputs). Both were formatted "N of 9", so
                one card read "Incomplete panel — 8 of 9 inputs present … missing CRP" AND "9 of 9
                core markers" at the same time: two true statements about different quantities, with
                nothing to tell them apart. The draw-based line is authoritative because it is the
                basis of the number actually displayed, so this line never restates a competing count
                and always says "across all draws" when it shows one.
                The DOB requirement is now its own clause: appended as " + DOB" to a marker count it
                read like a tenth marker rather than a separate missing prerequisite. */}
            {/* KAL-304 — under 18 the number is withheld because the model is validated in
                adults, NOT because labs are missing; asking for "more labs" here would be a
                false instruction. Wellness copy only. */}
            {bioInsufficient && data_provenance && data_provenance.label === 'under_18' && (
              <div className="lring-note">
                Biological age uses an adult model (PhenoAge), so we don’t show a number under 18.
                Your markers, activity and sleep are all here as normal.
              </div>
            )}
            {bioInsufficient && data_provenance && data_provenance.label !== 'under_18' && (
              <div className="lring-note">
                {phenoIncomplete
                  ? (data_provenance.markers_present >= data_provenance.markers_total
                      ? `all ${data_provenance.markers_total} core markers are on file, but not from one draw`
                      : `${data_provenance.markers_present} of ${data_provenance.markers_total} core markers on file across all draws`)
                  : (data_provenance.markers_present >= data_provenance.markers_total
                      /* "9 of 9 … add more" is a contradiction: when every marker is on file the
                         only thing still missing is the DOB clause appended below. */
                      ? `all ${data_provenance.markers_total} core markers are on file`
                      : `${data_provenance.markers_present} of ${data_provenance.markers_total} core markers on file — add more for a reliable number`)}
                {data_provenance.has_dob ? '' : ' · date of birth missing'}
              </div>
            )}
            {bioLimited && data_provenance && (
              <div className="lring-note">Limited data · {data_provenance.markers_present}/{data_provenance.markers_total} core markers across all draws</div>
            )}
            <div className="lring-chrono">vs chronological {chronological_age != null ? chronological_age.toFixed(1) : '—'}</div>
          </div>
        </div>
        {score_tier_label && (
          <div style={{textAlign:'center',fontSize:'0.72em',color: 'var(--text-secondary)',marginTop:'12px'}}>
            <span className="tier-dot" style={{display:'inline-block',width:'6px',height:'6px',borderRadius:'50%',backgroundColor: (TIER_COLORS[score_tier]||'#8B6914'),marginRight:'4px',verticalAlign:'middle'}}/>
            {/* Wording: drop the 'Insurance-Grade' tier prefix — it overclaims for a
                wellness estimate (and the 9th input, CRP, may come from an earlier draw).
                Tier is still conveyed by the coloured dot; gating/count are unchanged. */}
            {/* B1 — WAS `${verified_inputs}/${total_inputs} PhenoAge inputs verified`.
                Three problems with that line: "PhenoAge inputs verified" is jargon for a fact
                the user cares about in plain terms; it gave no DATE, so "9/9" said nothing
                about whether the panel was current; and there was no way to act on it.
                Now: the completeness fact in plain words, the draw date beside it, and the
                order action inline.
                COUNT AND DATE BOTH COME FROM phenoage_draw — the SINGLE COMPLETE DRAW the
                headline is computed from — NOT from verified_inputs/data_provenance, which
                count the latest value per marker ACROSS draws. The block above this one exists
                entirely because those two quantities were once rendered side by side and
                contradicted each other ("8 of 9 … missing CRP" next to "9 of 9"). Putting a
                date on the wrong count would have revived exactly that bug. */}
            {(() => {
              const present = (phenoage_draw && phenoage_draw.present != null)
                ? phenoage_draw.present : (verified_inputs || 0);
              const total = (phenoage_draw && phenoage_draw.present != null)
                ? 9 : (total_inputs || 0);
              const drawn = phenoage_draw && phenoage_draw.draw_date;
              return (
                <>
                  {`${present}/${total} core markers`}
                  {drawn ? ` · last taken ${formatDate(drawn)}` : ''}
                  <OrderPanelInlineAction where="longevity_inline" />
                </>
              );
            })()}
            <span style={{display:'block',fontSize:'0.9em',marginTop:'2px'}}>{provenance_caveat || ''}</span>
          </div>
        )}
        {/* KAL-274 — the disclaimer paragraph (measured 121px tall at 390px) moved BEHIND the
            existing ⓘ control (BiologicalAgeMethodologyModal), per Tim: the markers must be
            visible without scrolling. NOT removed — relocated; this one-line cue keeps it one
            tap away and states the two facts that must never leave the surface. */}
        <button type="button" className="lring-note bioage-disclaimer-cue"
          onClick={() => setShowMethodology(true)}
          style={{display:'block', width:'100%', background:'none', border:'none', cursor:'pointer',
                  marginTop:8, opacity:0.85, fontSize:'0.72em', color:'var(--text-secondary)', textAlign:'center'}}>
          A wellness estimate, not a diagnosis — ⓘ how it works and its limits
        </button>
      </div>

      {/* Run 5 §A / RUN9 §5 — ONE ranked list of the 9 PhenoAge markers. Magnitude is shown by
          the range-bar (value vs reference range) + a calm worded status; per-marker YEAR figures
          are removed (RUN8 attribution code stays dormant). Ranked worst-first by how far the
          value sits OUTSIDE its reference range. Longevity ONLY. */}
      {(() => {
        const rows = variablesByLayer.phenoage
          .filter(v => !v.missing)
          .map(v => {
            const info = _bioageInfo(v.metric);
            if (!info) return null;
            const rr = _bioageResolve({ ...v, info, panel: panelByKey[info.key] });
            let dist = 0;
            if (rr.val != null && rr.low != null && rr.high != null && rr.high > rr.low) {
              const w = rr.high - rr.low;
              if (rr.val < rr.low) dist = (rr.low - rr.val) / w;
              else if (rr.val > rr.high) dist = (rr.val - rr.high) / w;
            }
            return { ...v, info, panel: panelByKey[info.key], _dist: dist };
          })
          .filter(Boolean)
          .sort((a, b) => (b._dist || 0) - (a._dist || 0));  // furthest-outside-range first
        if (!rows.length) return null;
        return (
          <div className="uba-section">
            <h3>Understanding your Biological Age</h3>
            {/* KAL-274 — was two wrapped lines (measured 48px). Same ordering fact, half the
                height; the full sentence lives in each marker's own drawer. */}
            <div className="uba-sub">{T('Ranked by distance outside range — the ones worth attention first.')}</div>
            <div className="uba-group">
              {rows.map(v => {
                const r = _bioageResolve(v);
                return (
                  <button key={v.metric} type="button" className="uba-row2" onClick={() => setDrawerMarker(v)}>
                    <div className="uba-row2-main">
                      <div className="uba-row2-head">
                        <span className="uba-metric">{v.info.label}</span>
                        {/* RUN7 §2 — "Mostly fixed" retired (contradicted the drawer copy + implied
                            zero agency). Slower-moving markers are now "Slower to shift", not fixed. */}
                        <span className={`uba-tag uba-tag-${v.info.tier === 'modifiable' ? 'life' : 'slow'}`}>{v.info.tier === 'modifiable' ? 'Lifestyle' : 'Slower to shift'}</span>
                      </div>
                      <div className="uba-desc">{BIOAGE_DESC[v.info.key] || ''}</div>
                      {r.low != null && r.high != null && r.val != null && <_RangeBar value={r.val} low={r.low} high={r.high} tone={r.label ? r.label.tone : 'ok'} anchor={bioageAnchor(v.info.key)} />}
                      {/* C1 — the anchor caption sits under the bar it explains, in the main
                          column, so it reads as part of the number line rather than as a second
                          status competing with the pill on the right. */}
                      <_RangeAnchorNote anchor={bioageAnchor(v.info.key)} />
                    </div>
                    {/* C3 — VERDICT ABOVE VALUE. The status pill was BELOW the number in DOM and
                        visual order, so the figure was read first and interpreted second. With
                        lab_presentation_v2 the plain-language verdict comes first. */}
                    <div className="uba-row2-right">
                      {FEATURES_GLOBAL.lab_presentation_v2 ? (
                        <>
                          {r.label && <div className={`uba-status uba-status-${r.label.tone}`}><span className="uba-status-g" aria-hidden="true">{UBA_GLYPH[r.label.tone] || ''}</span>{r.label.text}</div>}
                          {r.val != null && <div className="uba-val">{mkVal(r.name || r.marker || r.label, r.val, r.unit)}</div>}
                        </>
                      ) : (
                        <>
                          {r.val != null && <div className="uba-val">{mkVal(r.name || r.marker || r.label, r.val, r.unit)}</div>}
                          {r.label && <div className={`uba-status uba-status-${r.label.tone}`}>{r.label.text}</div>}
                        </>
                      )}
                    </div>
                    <span className="uba-chev">›</span>
                  </button>
                );
              })}
            </div>
            {/* ⚠ TEMPORARY (KAL-156) — REMOVE OR WIRE TO REAL ORDER BUTTONS once those exist.
                MOVED HERE 13 Aug (KAL-175, Tim's instruction). It used to render AFTER this
                whole section, which put the wellness disclaimer between the marker list and the
                panel — measured: last marker row bottom 1676.2, panel top 1772.4, and the only
                thing in the 96.2px between them was `.uba-disclaimer`. The panel is meant to
                read as a CONTINUATION of the marker list ("these are the markers where a retest
                could show a real change"), not as a separate section past the sign-off, so it
                now sits INSIDE the section, directly under the last row, and the disclaimer
                follows it — and therefore covers it too.
                ⚠ CONSEQUENCE, stated rather than discovered later: the panel now shares this
                section's render condition, so a user with NO resolvable markers at all
                (rows.length === 0 -> the section returns null) no longer sees it. That is the
                right behaviour for a panel about this user's marker history, but it IS a
                change. */}
            {retestPanelEnabled() && <RetestPanel />}
            <div className="uba-disclaimer">
              Kalpa is a wellness tool, not a medical device. It does not diagnose, treat, or prevent any condition. Discuss any health decisions with a qualified professional.
            </div>
          </div>
        );
      })()}

      {/* NPL order nudges with placement='longevity'. Rendered as a stack
          right above the scoring layers — earned by markers shifting, or
          a stale panel that needs a refresh to keep the score honest. */}
      {orderNudges.prompts.map(np => (
        <OrderPromptCard
          key={np.key}
          prompt={np}
          onDismiss={orderNudges.dismiss}
          onOrder={orderNudges.order}
        />
      ))}
      {orderNudges.confirmingPrompt && (
        <OrderConfirmModal
          prompt={orderNudges.confirmingPrompt}
          onClose={() => orderNudges.setConfirmingPrompt(null)}
          onConfirmed={() => { orderNudges.setConfirmingPrompt(null); orderNudges.refresh(); }}
        />
      )}

      {/* RUN6 §2 — FITNESS: a daily-moving engagement score anchored on Active Zone
          Minutes (AZM). It is the motivator/re-engagement lever and is NOT part of the
          biological-age number — so it carries NO year-impact. */}
      {/* The retest panel used to render HERE. It now renders INSIDE the marker section, directly
          under the last marker row and ABOVE that section's wellness disclaimer — see the note
          at its new call site (KAL-175). */}

      {(() => {
        const w = azm && azm.week;
        const connected = azm && azm.connected;
        const pts = w ? (w.points ?? null) : null;
        const target = (w && w.target) || 150;
        const pct = pts != null ? Math.min(100, Math.round((pts / target) * 100)) : null;
        const tier = azm && azm.streak && azm.streak.tier;
        const wy = azm && azm.streak && azm.streak.weeks_on_target_year;
        return (
          <div className="fitness-daily-card">
            <div className="fitness-daily-head">
              <h3>Fitness — your daily mover</h3>
              <span className="fitness-daily-badge">Live · no age impact</span>
            </div>
            <div className="fitness-daily-sub">A live engagement score from your Active Zone Minutes. It moves every day — it is <strong>not</strong> part of your biological-age number.</div>
            {connected && pts != null ? (<>
              <div className="fitness-daily-score">
                <span className="fitness-daily-num">{pts}</span>
                <span className="fitness-daily-of">/ {target} AZM this week</span>
              </div>
              <div className="fitness-daily-bar"><span style={{width: `${pct}%`}} /></div>
              <div className="fitness-daily-meta">
                {pct}% of the WHO weekly target{tier ? ` · ${tier} tier` : ''}{wy != null ? ` · ${wy} weeks on target this year` : ''}
              </div>
            </>) : (
              <div className="fitness-daily-meta">Connect a wearable to start your live Fitness score.</div>
            )}
            <div className="fitness-daily-cite">Evidence: cardiorespiratory fitness &amp; longevity — Nes/HUNT (VO₂max); Mandsager 2018, JAMA Netw Open; Kokkinos 2022, JACC; WHO 150 min/wk guideline.</div>
          </div>
        );
      })()}

      {/* RUN6 §2 — CVD / additional signals: cited context, value + status word, NO
          year-impacts and NO age framing. These inform but are not in the number. */}
      {categories.cardiovascular && Array.isArray(categories.cardiovascular.drivers) && categories.cardiovascular.drivers.length > 0 && (() => {
        const cvStatus = (imp) => {
          const n = Number(imp) || 0;
          if (n <= -2) return { t: 'Exceptional', tone: 'ok' };
          if (n < 0)   return { t: 'Excellent', tone: 'ok' };
          if (n <= 1)  return { t: 'Average', tone: 'near' };
          return { t: 'Needs attention', tone: 'far' };
        };
        return (
          <div className="cvd-signals-card">
            <h3>Additional longevity signals we track</h3>
            <div className="cvd-signals-sub">Cited cardiovascular signals that add context. They are not part of your biological-age number and carry no year figure.</div>
            {categories.cardiovascular.drivers.filter(d => !/9p21|APOE|FOXO3|IL-10|carrier/i.test(d.metric || '')).map((d, i) => {
              const s = cvStatus(d.impact);
              return (
                <div key={i} className="cvd-signal-row">
                  <div className="cvd-signal-main">
                    <span className="cvd-signal-name">{autoGlossary(d.metric)}</span>
                    <span className="cvd-signal-detail">{autoGlossary(d.detail || '')}</span>
                  </div>
                  <div className="cvd-signal-right">
                    <span className="cvd-signal-val">{d.value}</span>
                    <span className={`uba-status uba-status-${s.tone}`}>{s.t}</span>
                    {d.ref && <span className="cvd-signal-ref">{d.ref}</span>}
                  </div>
                </div>
              );
            })}
            {categories.cardiovascular.citation && <div className="cvd-signals-cite">{categories.cardiovascular.citation}</div>}
          </div>
        );
      })()}

      {/* My Genome entry point — nested under Longevity so it's reachable on
          every surface (incl. the 5-item mobile bottom bar) without a separate
          nav item. HARD-GATED on the genetics flag: absent on NPL. */}
      {features.genetics && (
        <button
          className="longevity-genome-entry"
          onClick={() => onNav && onNav('genome')}
          style={{
            width:'100%', display:'flex', alignItems:'center', gap:'12px',
            background:'var(--card-bg, #112240)',
            border:'1px solid var(--accent-violet, #818CF8)',
            borderRadius:'12px', padding:'14px 16px', marginTop:'12px',
            cursor:'pointer', color:'var(--text-primary, #ffffff)', textAlign:'left',
          }}
        >
          <span style={{fontSize:'1.5rem', lineHeight:1}}>{'🧬'}</span>
          <span style={{flex:1}}>
            <span style={{display:'block', fontWeight:600}}>My Genome</span>
            <span style={{display:'block', fontSize:'0.8rem', color:'var(--text-secondary, #94a3b8)'}}>
              Explore your genetic insights and wellness associations
            </span>
          </span>
          <span style={{color:'var(--accent-violet, #818CF8)', fontSize:'1.2rem'}}>{'→'}</span>
        </button>
      )}

      {/* Methodology */}
      {score.methodology && (
        <div className="longevity-sources">
          <div className="source-note">
            <strong>Methodology:</strong> {score.methodology.description}
          </div>
          <div style={{fontSize:'0.7em',color:'var(--text-secondary)',padding:'0 12px 12px'}}>
            {score.methodology.references.map((ref, i) => (
              <div key={i}>• {ref}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -- ECG Session Components ---------------------------------------------------
function ECGSessionCard({ session, onSelect }) {
  const cls = (session.result_classification || '').toLowerCase().replace(/\s+/g, '_');
  const clsLabel = {
    'sinus_rhythm': 'Sinus Rhythm', 'normal_sinus_rhythm': 'Sinus Rhythm',
    'atrial_fibrillation': 'AFib Detected', 'inconclusive': 'Inconclusive',
  };
  const label = clsLabel[cls] || session.result_classification || 'Unknown';
  const clsClass = cls.includes('fibrillation') ? 'atrial_fibrillation' : cls.includes('sinus') ? 'sinus_rhythm' : 'inconclusive';
  return (
    <div className="ecg-session-card" onClick={() => onSelect(session)}>
      <span className="session-date">{formatDate(session.start_time)}</span>
      <span className="session-hr">{session.average_hr} bpm</span>
      <span className={`ecg-classification ${clsClass}`}>{label}</span>
    </div>
  );
}

function ECGDetailModal({ session, onClose }) {
  const closeModal = useModalHashRoute('#ecg-detail', onClose);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/fitbit/ecg/${session.session_id}`).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
  }, [session.session_id]);

  const waveform = detail?.waveform_samples_json ? JSON.parse(detail.waveform_samples_json) : null;
  const cls = (session.result_classification || '').toLowerCase();
  const clsClass = cls.includes('fibrillation') ? 'atrial_fibrillation' : cls.includes('sinus') ? 'sinus_rhythm' : 'inconclusive';

  return ReactDOM.createPortal(
    <div className="ecg-detail-modal" onClick={closeModal}>
      <div className="ecg-detail-content" onClick={e => e.stopPropagation()}>
        <div className="ecg-detail-header">
          <h3>ECG Recording</h3>
          <button onClick={closeModal} style={{background:'none',border:'none',color:'var(--text-secondary)',fontSize:'1.5rem',cursor:'pointer'}}>&times;</button>
        </div>
        <div className="ecg-detail-meta">
          <span>{formatDateTime(session.start_time)}</span>
          <span>Avg HR: <strong>{session.average_hr} bpm</strong></span>
          <span className={`ecg-classification ${clsClass}`}>{session.result_classification || 'Unknown'}</span>
        </div>
        {loading && <div style={{textAlign:'center',padding:'24px',color:'var(--text-secondary)'}}>Loading waveform data...</div>}
        {waveform && waveform.length > 0 ? (() => {
          // Medical ECG grid: 128Hz, standard 25mm/s paper speed, 5mm major / 1mm minor grid
          // 5 minor squares = 1 major square = 200ms = 25 samples at 128Hz
          const SAMPLE_RATE = 128;
          const samples = waveform.slice(0, 512);
          const minV = Math.min(...samples), maxV = Math.max(...samples);
          const rangeV = maxV - minV || 1;

          const padL = 38, padR = 12, padT = 10, padB = 26;
          const svgW = 860, svgH = 220;
          const plotW = svgW - padL - padR;
          const plotH = svgH - padT - padB;

          // Grid: minor every 5 samples (~40ms), major every 25 samples (~200ms)
          const minorStep = 5, majorStep = 25;
          const xScale = plotW / samples.length;
          const hMinorCount = 8; // 8 minor rows vertically

          const gridV = [], gridH = [];
          for (let i = 0; i <= samples.length; i += minorStep) {
            const x = padL + i * xScale;
            const isMaj = i % majorStep === 0;
            gridV.push(<line key={`v${i}`} x1={x} y1={padT} x2={x} y2={padT + plotH} className={isMaj ? 'ecg-grid-major' : 'ecg-grid-minor'}/>);
          }
          for (let j = 0; j <= hMinorCount; j++) {
            const y = padT + j * (plotH / hMinorCount);
            const isMaj = j % 4 === 0;
            gridH.push(<line key={`h${j}`} x1={padL} y1={y} x2={padL + plotW} y2={y} className={isMaj ? 'ecg-grid-major' : 'ecg-grid-minor'}/>);
          }

          // X axis labels (every 200ms = every major grid step)
          const xLabels = [];
          for (let i = 0; i <= samples.length; i += majorStep) {
            const ms = Math.round((i / SAMPLE_RATE) * 1000);
            xLabels.push(<text key={`xl${i}`} x={padL + i * xScale} y={padT + plotH + 18} className="ecg-axis-label" textAnchor="middle">{ms}ms</text>);
          }

          // Y axis labels (evenly spaced, show raw ADC or µV estimate)
          const yLabels = [];
          for (let j = 0; j <= 4; j++) {
            const y = padT + j * (plotH / 4);
            const val = Math.round(maxV - (j / 4) * rangeV);
            yLabels.push(<text key={`yl${j}`} x={padL - 4} y={y + 4} className="ecg-axis-label" textAnchor="end">{val}</text>);
          }

          // Waveform path
          const pathD = samples.map((v, i) => {
            const x = (padL + i * xScale).toFixed(1);
            const y = (padT + (1 - (v - minV) / rangeV) * plotH).toFixed(1);
            return `${i === 0 ? 'M' : 'L'}${x},${y}`;
          }).join(' ');

          return (
            <svg className="ecg-waveform-svg" viewBox={`0 0 ${svgW} ${svgH}`}>
              {gridV}{gridH}
              {xLabels}{yLabels}
              <path d={pathD}/>
            </svg>
          );
        })() : !loading && <div style={{textAlign:'center',padding:'24px',color:'var(--text-dim)'}}>Waveform data not available for this session</div>}
        <div className="ecg-disclaimer">
          This ECG recording is provided for wellness and informational purposes only. It is not a medical diagnosis.
          If you have concerns about your heart rhythm, please consult a healthcare professional.
        </div>
      </div>
    </div>,
    document.body
  );
}

// -- EDA Session Components ---------------------------------------------------
function EDASessionCard({ session, onSelect }) {
  return (
    <div className="eda-session-card" onClick={() => onSelect(session)}>
      <span className="session-date">{formatDate(session.start_time)}</span>
      <span className="session-scl">{session.scl_avg ? `${session.scl_avg.toFixed(2)} \u00B5S` : '--'}</span>
      <span className="session-duration">{session.duration_seconds ? `${Math.round(session.duration_seconds / 60)}min` : ''}</span>
    </div>
  );
}

function EDADetailModal({ session, onClose }) {
  const closeModal = useModalHashRoute('#eda-detail', onClose);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/fitbit/eda/${session.session_id}`).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
  }, [session.session_id]);

  const sclData = detail?.scl_data_json ? JSON.parse(detail.scl_data_json) : null;
  const chartData = sclData ? sclData.map((v, i) => ({ t: i, value: typeof v === 'number' ? v : v?.scl || 0 })) : [];

  return ReactDOM.createPortal(
    <div className="eda-detail-modal" onClick={closeModal}>
      <div className="eda-detail-content" onClick={e => e.stopPropagation()}>
        <div className="eda-detail-header">
          <h3>EDA Session</h3>
          <button onClick={closeModal} style={{background:'none',border:'none',color:'var(--text-secondary)',fontSize:'1.5rem',cursor:'pointer'}}>&times;</button>
        </div>
        <div style={{display:'flex',gap:'16px',marginBottom:'16px',fontSize:'0.85rem',color:'var(--text-secondary)'}}>
          <span>{formatDateTime(session.start_time)}</span>
          <span>Duration: <strong>{session.duration_seconds ? `${Math.round(session.duration_seconds / 60)} min` : '--'}</strong></span>
          <span>Avg SCL: <strong>{session.scl_avg ? `${session.scl_avg.toFixed(2)} \u00B5S` : '--'}</strong></span>
        </div>
        {loading && <div style={{textAlign:'center',padding:'24px',color:'var(--text-secondary)'}}>Loading session data...</div>}
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{top:5,right:10,bottom:5,left:0}}>
              <XAxis dataKey="t" tick={{fill:'#8B6914',fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:'#8B6914',fontSize:10}} axisLine={false} tickLine={false} width={40}/>
              <Tooltip formatter={(v)=>[`${v.toFixed(3)} \u00B5S`,'SCL']} contentStyle={{background:'#112240',border:'1px solid rgba(255,255,255,0.12)',color:'#e2e8f0',borderRadius:8,fontSize:12}}/>
              <Line type="monotone" dataKey="value" stroke="#C9973A" dot={false} strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>
        ) : !loading && <div style={{textAlign:'center',padding:'24px',color:'var(--text-dim)'}}>SCL data not available for this session</div>}
        <div className="ecg-disclaimer">
          Electrodermal activity data is provided for wellness and informational purposes only.
        </div>
      </div>
    </div>,
    document.body
  );
}

// -- VitalProfile (v6 — BP chart, x-axis 1 tick/year, drill-down) ----------
// Tier 1 = always expanded sparkline; Tier 2 = collapsed card with expand
// All known card IDs in display priority order — cards with no data are hidden automatically
const DEFAULT_VITAL_CARD_ORDER = [
  'steps','sleep','resting_hr','calories','weight','hrv',
  'spo2','blood_pressure','breathing_rate','skin_temp','cardio_score','ecg','eda'
];
// Cards that need special rendering (not a standard sparkline)
const SPECIAL_CARD_IDS = new Set(['blood_pressure','spo2','ecg','eda']);

function VitalProfile({ profile, profileError, onRetry, onAskAI, syncing, triggerFitbitSync, syncStatusGlobal, fitbitSyncError, onNav }) {
  const [fitbitData,setFitbitData]=useState(null);const [loading,setLoading]=useState(true);
  const [fitnessData,setFitnessData]=useState(null);
  const [bpData,setBpData]=useState([]);
  const [drillDown, setDrillDown] = useState(null);
  const [spo2Resolution, setSpo2Resolution] = useState('day');
  const [ecgSessions, setEcgSessions] = useState([]);
  const [edaSessions, setEdaSessions] = useState([]);
  const [ecgDetail, setEcgDetail] = useState(null);
  const [edaDetail, setEdaDetail] = useState(null);
  const [expandedTier2, setExpandedTier2] = useState({});
  const [cardOrder, setCardOrder] = useState(DEFAULT_VITAL_CARD_ORDER);
  const [dragIdx, setDragIdx] = useState(null);
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState('today');
  const [todayData, setTodayData] = useState(null);
  const [sleepData, setSleepData] = useState(null);
  const [hrSparkline, setHrSparkline] = useState([]);
  const [hrvSparkline, setHrvSparkline] = useState([]);
  const [spo2Sparkline, setSpo2Sparkline] = useState([]);
  const [weightSparkline, setWeightSparkline] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [expandedRange, setExpandedRange] = useState('30d');
  const [expandedData, setExpandedData] = useState([]);
  useEffect(()=>{
    let done=0; const tick=()=>{ done++; if(done>=2) setLoading(false); };
    api.get('/fitbit/history?days=1825').then(d=>{setFitbitData(d);tick();}).catch(tick);
    api.get('/fitness/data?days=1825').then(d=>{setFitnessData(d);tick();}).catch(tick);
    api.get('/blood-pressure').then(setBpData).catch(()=>{});
    api.get('/fitbit/ecg').then(d => setEcgSessions(d.sessions || [])).catch(()=>{});
    api.get('/fitbit/eda').then(d => setEdaSessions(d.sessions || [])).catch(()=>{});
    api.get('/user/vital-card-order').then(d => { if (d.order && d.order.length > 0) setCardOrder(d.order); }).catch(()=>{});
  },[]);
  useEffect(() => {
    api.get('/fitness/today').then(d => setTodayData(d)).catch(() => {});
    api.get('/fitness/sleep/last-night').then(d => setSleepData(d)).catch(() => {});
    api.get('/fitness/metric/resting_hr?range=7d').then(d => setHrSparkline(d.data || [])).catch(() => {});
    api.get('/fitness/metric/hrv?range=7d').then(d => setHrvSparkline(d.data || [])).catch(() => {});
    api.get('/fitness/metric/spo2?range=7d').then(d => setSpo2Sparkline(d.data || [])).catch(() => {});
    api.get('/fitness/metric/weight?range=90d').then(d => setWeightSparkline(d.data || [])).catch(() => {});
  }, []);
  const handleDragStart = i => setDragIdx(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const r = [...cardOrder]; const [m] = r.splice(dragIdx, 1); r.splice(i, 0, m);
    setCardOrder(r); setDragIdx(i);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    api.put('/user/vital-card-order', { order: cardOrder }).catch(e => console.error('Card order save failed:', e));
  };

  const latestWeight = profile.weight || '--';
  const bmi = profile.bmi || (profile.height && typeof latestWeight === 'number' ? (latestWeight/((profile.height/100)**2)).toFixed(1) : '--');

  const makeChartData = (arr, vk, dk='date') => {
    if (!arr) return [];
    return arr.map(d => ({
      date: d[dk],
      ts: new Date(d[dk]).getTime(),
      // KAL-266 — was `parseFloat(d[vk]) || 0` then `.filter(d.value > 0)`: the `|| 0` turned
      // an unparseable value into a zero and the filter then removed it, so the pair worked by
      // cancelling out — and took every REAL zero with it. Unparseable is now null, and null is
      // what gets filtered.
      value: typeof d[vk]==='number' ? d[vk]
             : (Number.isFinite(parseFloat(d[vk])) ? parseFloat(d[vk]) : null)
    })).filter(d => d.value != null && !isNaN(d.ts));
  };

  // Aggregate chart data by week or month (for sparse data like SpO2)
  const aggregateChartData = (data, resolution) => {
    if (!data || data.length === 0 || resolution === 'day') return data;
    const buckets = {};
    data.forEach(d => {
      const dt = new Date(d.date);
      let key;
      if (resolution === 'week') {
        const mon = new Date(dt); mon.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
        key = mon.toISOString().slice(0, 10);
      } else {
        key = d.date.slice(0, 7) + '-15'; // mid-month for better chart spacing
      }
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(d.value);
    });
    return Object.entries(buckets).map(([k, vals]) => ({
      date: k, ts: new Date(k).getTime(),
      value: +(vals.reduce((a,b)=>a+b,0) / vals.length).toFixed(1)
    })).sort((a,b) => a.ts - b.ts);
  };

  // Generate 1 tick per year — returns array of timestamps for Jan 1 of each year in range
  const getYearTicks = (data) => {
    if (!data || data.length < 2) return [];
    const first = new Date(data[0].date);
    const last = new Date(data[data.length - 1].date);
    const startYear = first.getFullYear();
    const endYear = last.getFullYear();
    const ticks = [];
    for (let y = startYear; y <= endYear; y++) {
      ticks.push(new Date(y, 0, 1).getTime());
    }
    return ticks;
  };

  const yearTickFormatter = (ts) => {
    if (!ts) return '';
    return new Date(ts).getFullYear().toString();
  };

  const tooltipDateFormatter = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return formatDate(d);
  };

  // ── Unified fitness data helpers (Apple Health, Health Connect, etc.) ────
  const uRows = (fitnessData && fitnessData.rows) || [];

  // Aggregate unified rows for named metric(s) by calendar date.
  // agg: 'sum' | 'avg' | 'last'
  const uDaily = (names, agg='last') => {
    const ns = new Set(Array.isArray(names) ? names : [names]);
    const byDate = {};
    uRows.filter(r => ns.has(r.metric_name) && r.value > 0).forEach(r => {
      const date = (r.date || r.timestamp || '').slice(0, 10);
      if (!date) return;
      if (!byDate[date]) byDate[date] = [];
      byDate[date].push(r.value);
    });
    return Object.entries(byDate).map(([date, vals]) => {
      let v;
      if (agg==='sum') v = vals.reduce((s,x)=>s+x, 0);
      else if (agg==='avg') v = vals.reduce((s,x)=>s+x, 0) / vals.length;
      else v = vals[vals.length-1];
      return { date, value: Math.round(v*100)/100 };
    }).sort((a,b) => a.date < b.date ? -1 : 1);
  };

  // Sleep: sum Core+Deep+REM per day; fall back to Asleep if no staged data.
  const uSleepDaily = () => {
    const sleepNames = new Set(['Sleep Core','Sleep Deep','Sleep REM','Sleep Asleep']);
    const hasStaged = {};
    const byDate = {};
    uRows.filter(r => sleepNames.has(r.metric_name) && r.value > 0).forEach(r => {
      const date = (r.date || r.timestamp || '').slice(0, 10);
      if (!date) return;
      if (r.metric_name !== 'Sleep Asleep') hasStaged[date] = true;
      byDate[date] = byDate[date] || {};
      byDate[date][r.metric_name] = (byDate[date][r.metric_name] || 0) + r.value;
    });
    return Object.entries(byDate).map(([date, m]) => {
      const v = hasStaged[date]
        ? (m['Sleep Core']||0) + (m['Sleep Deep']||0) + (m['Sleep REM']||0)
        : (m['Sleep Asleep'] == null ? null : m['Sleep Asleep']);
      return { date, value: v == null ? null : Math.round(v*100)/100 };
      // KAL-266 — absence is null, not 0. A recorded sleep of exactly zero minutes is not a
      // thing that happens, so nothing is lost by filtering null instead of falsy — and the
      // fabricated 0 that used to stand in for "no record" is gone.
    }).filter(d => d.value != null).sort((a,b) => a.date < b.date ? -1 : 1);
  };

  // Merge Fitbit chart series with unified daily series.
  // Fitbit takes priority; unified fills any missing dates.
  const merge = (fitbitSeries, unifiedSeries) => {
    if (!unifiedSeries.length) return fitbitSeries;
    if (!fitbitSeries.length) return unifiedSeries.map(d => ({
      date: d.date, ts: new Date(d.date).getTime(), value: d.value
    })).filter(d => !isNaN(d.ts) && d.value > 0);
    const fitDates = new Set(fitbitSeries.map(d => d.date));
    const extra = unifiedSeries.filter(d => !fitDates.has(d.date))
      .map(d => ({ date: d.date, ts: new Date(d.date).getTime(), value: d.value }))
      .filter(d => !isNaN(d.ts) && d.value > 0);
    return [...fitbitSeries, ...extra].sort((a,b) => a.ts - b.ts);
  };

  // ── Chart series (Fitbit primary, unified fitness fallback) ───────────────
  const wD   = merge(fitbitData ? makeChartData(fitbitData.weight, 'weight') : [], uDaily('Weight','last'));
  const hrD  = merge(fitbitData ? makeChartData(fitbitData.heart_rate, 'resting_hr') : [], uDaily('Resting Heart Rate','avg'));
  const slD  = merge(
    fitbitData ? makeChartData(fitbitData.sleep, 'minutes_asleep').map(d=>({...d,value:+(d.value/60).toFixed(1)})) : [],
    uSleepDaily()
  );
  const stD  = merge(fitbitData ? makeChartData(fitbitData.activity, 'steps') : [], uDaily('Daily Steps','sum'));
  const hrvD = merge(fitbitData ? makeChartData(fitbitData.hrv || [], 'daily_rmssd') : [], uDaily('HRV (SDNN)','avg'));
  const spo2D = merge(fitbitData ? makeChartData(fitbitData.spo2 || [], 'avg_spo2') : [], uDaily('SpO2','avg'));
  const calD = merge(fitbitData ? makeChartData(fitbitData.activity || [], 'calories_out') : [], uDaily('Calories Burned','sum'));
  const breathingRateD = merge(fitbitData ? makeChartData(fitbitData.breathing_rate || [], 'breathing_rate') : [], uDaily('Respiratory Rate','avg'));
  const skinTempD = merge(fitbitData ? makeChartData(fitbitData.skin_temp || [], 'relative_temp') : [], uDaily('Wrist Temperature','avg'));
  const cardioScoreD = merge(
    fitbitData ? (fitbitData.cardio_score || []).map(d => ({
      date: d.date, ts: new Date(d.date).getTime(),
      // KAL-266 — same `|| 0` + `filter(> 0)` pair. A VO2 max of zero is not measurable, so
      // this changes no rendered value; it removes the fabricated zero that stood for absence.
      value: d.vo2_max_low != null && d.vo2_max_high != null
             ? +((d.vo2_max_low + d.vo2_max_high) / 2).toFixed(1)
             : (Number.isFinite(parseFloat(d.vo2_max_high != null ? d.vo2_max_high : d.vo2_max_low))
                ? parseFloat(d.vo2_max_high != null ? d.vo2_max_high : d.vo2_max_low) : null)
    })).filter(d => d.value != null && !isNaN(d.ts)) : [],
    uDaily('VO2 Max','last')
  );

  // Blood pressure chart data (dual series: systolic + diastolic)
  const bpChartData = (bpData || []).map(d => ({
    date: d.date,
    ts: new Date(d.date).getTime(),
    systolic: d.systolic,
    diastolic: d.diastolic,
    pulse: d.pulse
  })).filter(d => !isNaN(d.ts));

  const getStats = (data) => {
    if (!data || data.length === 0) return { avg: '--', latest: '--' };
    // KAL-266 — exclude null, include 0.
    const vals = data.map(d => (d.value == null ? null : Number(d.value)))
                     .filter(v => v != null && Number.isFinite(v));
    if (vals.length === 0) return { avg: '--', latest: '--' };
    const avg = (vals.reduce((s, v) => s + v, 0) / vals.length);
    const latest = vals[vals.length - 1];
    return {
      avg: avg >= 100 ? Math.round(avg).toLocaleString('en-GB') : avg.toFixed(1),
      latest: latest >= 100 ? Math.round(latest).toLocaleString('en-GB') : latest.toFixed(1)
    };
  };

  const VitalChart = ({ data, label, unit, color, height=120, onClick, extra }) => {
    if (!data || data.length < 1) return null;
    const stats = getStats(data);
    const yearTicks = getYearTicks(data);
    const chartHeight = isMobile ? 60 : height;
    return (
      <div className="vital-chart-card" onClick={onClick} style={onClick ? {cursor:'pointer'} : undefined}>
        <div className="vital-chart-header">
          <span className="vital-chart-label">{autoGlossary(label)}</span>
          <div style={{display:'flex',alignItems:'flex-start',gap:'6px'}}>
            {extra && <div onClick={e => e.stopPropagation()}>{extra}</div>}
            <div className="vital-chart-stats" style={{textAlign:'right'}}>
              <div style={{fontSize:'10px',color:'#8B6914',lineHeight:'1.2'}}>Average</div>
              <div style={{fontSize:'13px',color:'#8B6914',fontWeight:600}}>{stats.avg}<span style={{fontSize:'10px'}}> {unit}</span></div>
              <div style={{fontSize:'10px',color:'#8B6914',lineHeight:'1.2',marginTop:'4px'}}>Latest</div>
              <div style={{color,fontWeight:700,fontSize:'1.1em'}}>{stats.latest}<span className="spark-unit"> {unit}</span></div>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart data={data} margin={{top:5,right:5,bottom:12,left:-20}}>
            <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']}
              tick={{fontSize:9,fill:'#8B6914'}}
              tickFormatter={yearTickFormatter}
              ticks={yearTicks}
              axisLine={false} tickLine={false}/>
            <YAxis hide/>
            <Tooltip formatter={v=>[`${typeof v==='number' && v>=100 ? Math.round(v).toLocaleString('en-GB') : v} ${unit}`,label]} labelFormatter={tooltipDateFormatter} contentStyle={{fontSize:'11px',padding:'4px 8px',background:'#112240',color:'#e2e8f0',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'4px'}}/>
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={data.length<=30?{r:2,fill:color}:false} activeDot={{r:4}}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Blood Pressure chart (dual series)
  const [showBPForm, setShowBPForm] = useState(false);
  const [bpForm, setBpForm] = useState({ systolic: '', diastolic: '', pulse: '', date: new Date().toISOString().substring(0, 10) });
  const handleAddBP = async () => {
    if (!bpForm.systolic || !bpForm.diastolic) return;
    try {
      await api.post('/blood-pressure', bpForm);
      const updated = await api.get('/blood-pressure');
      setBpData(updated);
      setShowBPForm(false);
      setBpForm({ systolic: '', diastolic: '', pulse: '', date: new Date().toISOString().substring(0, 10) });
    } catch (e) { console.error('BP save failed:', e); }
  };

  const BPChart = () => {
    if (showBPForm) {
      return (
        <div className="vital-chart-card" style={{padding:'16px'}}>
          <div className="vital-chart-header">
            <span className="vital-chart-label">Add <GlossaryTerm term="blood pressure">Blood Pressure</GlossaryTerm></span>
            <button className="conv-toggle-btn" onClick={()=>setShowBPForm(false)} style={{fontSize:'14px'}}>&times;</button>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginTop:'12px'}}>
            <input type="number" placeholder="Systolic" value={bpForm.systolic} onChange={e=>setBpForm({...bpForm,systolic:e.target.value})} style={{padding:'8px',borderRadius:'6px',border:'1px solid var(--border-color)',background:'var(--primary-bg)',color:'var(--text-primary)',fontSize:'13px'}}/>
            <input type="number" placeholder="Diastolic" value={bpForm.diastolic} onChange={e=>setBpForm({...bpForm,diastolic:e.target.value})} style={{padding:'8px',borderRadius:'6px',border:'1px solid var(--border-color)',background:'var(--primary-bg)',color:'var(--text-primary)',fontSize:'13px'}}/>
            <input type="number" placeholder="Pulse (optional)" value={bpForm.pulse} onChange={e=>setBpForm({...bpForm,pulse:e.target.value})} style={{padding:'8px',borderRadius:'6px',border:'1px solid var(--border-color)',background:'var(--primary-bg)',color:'var(--text-primary)',fontSize:'13px'}}/>
            <input type="date" value={bpForm.date} onChange={e=>setBpForm({...bpForm,date:e.target.value})} style={{padding:'8px',borderRadius:'6px',border:'1px solid var(--border-color)',background:'var(--primary-bg)',color:'var(--text-primary)',fontSize:'13px'}}/>
          </div>
          <button className="action-btn" onClick={handleAddBP} style={{marginTop:'12px',width:'100%'}}>Save</button>
        </div>
      );
    }

    if (!bpChartData || bpChartData.length < 1) {
      return (
        <div className="vital-chart-card" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100px',cursor:'pointer'}} onClick={()=>setShowBPForm(true)}>
          <div style={{textAlign:'center',color:'var(--text-secondary)'}}>
            <div style={{fontSize:'24px',marginBottom:'8px'}}>&#128168;</div>
            <div style={{fontSize:'13px'}}>No blood pressure data yet</div>
            <div style={{fontSize:'11px',color:'var(--accent)',marginTop:'4px'}}>Click to add a reading</div>
          </div>
        </div>
      );
    }

    const latestBP = bpChartData[bpChartData.length - 1];
    const avgSys = Math.round(bpChartData.reduce((s, d) => s + d.systolic, 0) / bpChartData.length);
    const avgDia = Math.round(bpChartData.reduce((s, d) => s + d.diastolic, 0) / bpChartData.length);
    const yearTicks = getYearTicks(bpChartData);
    return (
      <div className="vital-chart-card" style={{position:'relative'}}>
        <div className="vital-chart-header">
          <span className="vital-chart-label"><GlossaryTerm term="blood pressure">Blood Pressure</GlossaryTerm></span>
          <div style={{display:'flex',alignItems:'flex-start',gap:'12px'}}>
            <button onClick={()=>setShowBPForm(true)} style={{background:'none',border:'1px solid var(--border-color)',color:'var(--accent)',borderRadius:'4px',padding:'2px 8px',fontSize:'11px',cursor:'pointer',whiteSpace:'nowrap'}}>+ BP</button>
            {!isMobile && <div style={{textAlign:'right'}}>
              <div style={{fontSize:'10px',color:'#8B6914',lineHeight:'1.2'}}>Average</div>
              <div style={{fontSize:'13px',color:'#8B6914',fontWeight:600}}>{avgSys}/{avgDia}<span style={{fontSize:'10px'}}> <GlossaryTerm term="mmhg">mmHg</GlossaryTerm></span></div>
              <div style={{fontSize:'10px',color:'#8B6914',lineHeight:'1.2',marginTop:'4px'}}>Latest</div>
              <div style={{color:'#ef4444',fontWeight:700,fontSize:'1.1em'}}>{latestBP.systolic}/{latestBP.diastolic}<span className="spark-unit"> mmHg</span></div>
            </div>}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={isMobile ? 60 : 120}>
          <LineChart data={bpChartData} margin={{top:5,right:5,bottom:12,left:-20}}>
            <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']}
              tick={{fontSize:9,fill:'#8B6914'}}
              tickFormatter={yearTickFormatter}
              ticks={yearTicks}
              axisLine={false} tickLine={false}/>
            <YAxis hide/>
            <ReferenceArea y1={90} y2={120} fill="#22c55e" fillOpacity={0.06} stroke="none"/>
            <ReferenceArea y1={60} y2={80} fill="#22c55e" fillOpacity={0.06} stroke="none"/>
            <Tooltip labelFormatter={tooltipDateFormatter} contentStyle={{fontSize:'11px',padding:'4px 8px',background:'#112240',color:'#e2e8f0',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'4px'}}/>
            <Line type="monotone" dataKey="systolic" name="Systolic" stroke="#ef4444" strokeWidth={2} dot={{r:3,fill:'#ef4444'}}/>
            <Line type="monotone" dataKey="diastolic" name="Diastolic" stroke="#60a5fa" strokeWidth={2} dot={{r:3,fill:'#60a5fa'}}/>
            <Legend wrapperStyle={{fontSize:'11px'}}/>
          </LineChart>
        </ResponsiveContainer>
        <div className="spark-footer"><span>{bpChartData.length} reading{bpChartData.length!==1?'s':''}</span></div>
      </div>
    );
  };

  // Refresh data when sync completes (syncing transitions from true→false)
  const prevSyncing = useRef(syncing);
  useEffect(() => {
    if (prevSyncing.current && !syncing) {
      api.get('/fitbit/history?days=1825').then(d => setFitbitData(d)).catch(()=>{});
      api.get('/fitness/data?days=1825').then(d => setFitnessData(d)).catch(()=>{});
      api.get('/fitbit/ecg').then(d => setEcgSessions(d.sessions || [])).catch(()=>{});
      api.get('/fitbit/eda').then(d => setEdaSessions(d.sessions || [])).catch(()=>{});
    }
    prevSyncing.current = syncing;
  }, [syncing]);

  // Build card data lookup by ID — all standard sparkline cards
  const chartById = {
    steps:          { data: stD,           label: 'Daily Steps',         unit: 'steps', color: '#fbbf24' },
    sleep:          { data: slD,           label: 'Sleep Duration',       unit: 'hrs',   color: '#a78bfa' },
    resting_hr:     { data: hrD,           label: 'Resting Heart Rate',   unit: 'bpm',   color: '#e94560' },
    calories:       { data: calD,          label: 'Calories Burned',      unit: 'cal',   color: '#f472b6' },
    weight:         { data: wD,            label: 'Weight',               unit: weightUnit(), color: '#fb923c' },
    hrv:            { data: hrvD,          label: 'HRV (RMSSD)',          unit: 'ms',    color: '#34d399' },
    breathing_rate: { data: breathingRateD,label: 'Breathing Rate',       unit: 'brpm',  color: '#67e8f9' },
    skin_temp:      { data: skinTempD,     label: 'Skin Temp (nightly Δ)',unit: '°C',    color: '#fda4af' },
    cardio_score:   { data: cardioScoreD,  label: 'Cardio Fitness (VO₂ Max est.)', unit: 'ml/kg/min', color: '#4ade80' },
  };

  // Merge user's saved order with any new metric IDs not yet persisted
  const effectiveCardOrder = React.useMemo(() => {
    const known = new Set(cardOrder);
    const extras = DEFAULT_VITAL_CARD_ORDER.filter(id => !known.has(id));
    return extras.length > 0 ? [...cardOrder, ...extras] : cardOrder;
  }, [cardOrder]);

  const renderCard = (id, idx) => {
    const isCollapsible = SPECIAL_CARD_IDS.has(id);
    const isExpanded = !isCollapsible || expandedTier2[id];
    const wrapProps = {
      draggable: true,
      onDragStart: () => handleDragStart(idx),
      onDragOver: e => handleDragOver(e, idx),
      onDragEnd: handleDragEnd,
      className: `vital-drag-wrapper${dragIdx === idx ? ' dragging' : ''}`,
    };

    if (id === 'blood_pressure') {
      if (!isExpanded) {
        const latestBP = bpChartData.length > 0 ? bpChartData[bpChartData.length - 1] : null;
        return (
          <div key={id} {...wrapProps}>
            <div className="vital-tier2-collapsed" onClick={() => setExpandedTier2(p => ({...p, [id]: true}))}>
              <span className="vital-tier2-label">{'\uD83E\uDE78'} Blood Pressure</span>
              {latestBP && <span className="vital-tier2-value">{latestBP.systolic}/{latestBP.diastolic} <span style={{fontSize:'10px',color:'#8B6914'}}>mmHg</span></span>}
              <span className="vital-tier2-expand">{'\u25BC'}</span>
            </div>
          </div>
        );
      }
      return <div key={id} {...wrapProps}><BPChart /></div>;
    }

    if (id === 'spo2') {
      if (spo2D.length === 0) return null;
      const aggData = aggregateChartData(spo2D, spo2Resolution);
      const latestSpo2 = spo2D.length > 0 ? spo2D[spo2D.length - 1]?.value : null;
      if (!isExpanded) {
        return (
          <div key={id} {...wrapProps}>
            <div className="vital-tier2-collapsed" onClick={() => setExpandedTier2(p => ({...p, [id]: true}))}>
              <span className="vital-tier2-label">{autoGlossary('SpO2')}</span>
              {latestSpo2 && <span className="vital-tier2-value">{latestSpo2}%</span>}
              <span className="vital-tier2-expand">{'\u25BC'}</span>
            </div>
          </div>
        );
      }
      const spo2Metric = {data:aggData,label:'SpO2',unit:'%',color:'#818cf8'};
      return (
        <div key={id} {...wrapProps}>
          <div className="vital-chart-card">
            <div className="chart-card-header">
              <span className="vital-chart-label">{autoGlossary('SpO2')}</span>
              <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                <div className="resolution-toggle">
                  {['day','week','month'].map(r => (
                    <button key={r} className={`res-btn${spo2Resolution===r?' active':''}`}
                      onClick={()=>setSpo2Resolution(r)}>{r.charAt(0).toUpperCase()+r.slice(1)}</button>
                  ))}
                </div>
                <button className="vital-collapse-btn" onClick={() => setExpandedTier2(p => ({...p, [id]: false}))}>▲</button>
              </div>
            </div>
            <div onClick={()=>setDrillDown(spo2Metric)} style={{cursor:'pointer'}}>
              <ResponsiveContainer width="100%" height={isMobile ? 60 : 120}>
                <LineChart data={aggData} margin={{top:5,right:5,bottom:12,left:-20}}>
                  <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']} ticks={getYearTicks(aggData)} tickFormatter={yearTickFormatter} tick={{fontSize:9,fill:'#8B6914'}} axisLine={false} tickLine={false}/>
                  <YAxis hide/>
                  <Tooltip labelFormatter={tooltipDateFormatter} formatter={(v)=>[`${v} %`,'SpO2']} contentStyle={{fontSize:'11px',padding:'4px 8px',background:'#112240',color:'#e2e8f0',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'4px'}}/>
                  <Line type="monotone" dataKey="value" stroke="#818cf8" dot={spo2Resolution==='day'?{r:2,fill:'#818cf8'}:false} strokeWidth={2} activeDot={{r:4}}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }

    if (id === 'ecg') {
      if (!isExpanded) {
        return (
          <div key={id} {...wrapProps}>
            <div className="vital-tier2-collapsed" onClick={() => setExpandedTier2(p => ({...p, [id]: true}))}>
              <span className="vital-tier2-label">{'\u2764\uFE0F'} ECG</span>
              <span className="vital-tier2-value">{ecgSessions.length} session{ecgSessions.length !== 1 ? 's' : ''}</span>
              <span className="vital-tier2-expand">{'\u25BC'}</span>
            </div>
          </div>
        );
      }
      return (
        <div key={id} {...wrapProps}>
          <div className="session-browser-card">
            <div className="session-browser-header">
              <h4>{'\u2764\uFE0F\u200D\uD83E\uDE79'} ECG Recordings</h4>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                <span className="session-browser-count">{ecgSessions.length} session{ecgSessions.length !== 1 ? 's' : ''}</span>
                <button className="vital-collapse-btn" onClick={() => setExpandedTier2(p => ({...p, [id]: false}))}>▲</button>
              </div>
            </div>
            {ecgSessions.length > 0 ? (
              <div className="ecg-session-list">{ecgSessions.slice(0,10).map(s => <ECGSessionCard key={s.session_id} session={s} onSelect={setEcgDetail}/>)}</div>
            ) : (
              <div style={{padding:'16px',textAlign:'center',color:'var(--text-dim)',fontSize:'0.8rem'}}>No ECG recordings. Use Fitbit to take an ECG scan.</div>
            )}
          </div>
        </div>
      );
    }

    if (id === 'eda') {
      if (!isExpanded) {
        return (
          <div key={id} {...wrapProps}>
            <div className="vital-tier2-collapsed" onClick={() => setExpandedTier2(p => ({...p, [id]: true}))}>
              <span className="vital-tier2-label">{'\u26A1'} EDA</span>
              <span className="vital-tier2-value">{edaSessions.length} session{edaSessions.length !== 1 ? 's' : ''}</span>
              <span className="vital-tier2-expand">{'\u25BC'}</span>
            </div>
          </div>
        );
      }
      return (
        <div key={id} {...wrapProps}>
          <div className="session-browser-card">
            <div className="session-browser-header">
              <h4>{'\u26A1'} EDA Sessions</h4>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                <span className="session-browser-count">{edaSessions.length} session{edaSessions.length !== 1 ? 's' : ''}</span>
                <button className="vital-collapse-btn" onClick={() => setExpandedTier2(p => ({...p, [id]: false}))}>▲</button>
              </div>
            </div>
            {edaSessions.length > 0 ? (
              <div className="eda-session-list">{edaSessions.slice(0,10).map(s => <EDASessionCard key={s.session_id} session={s} onSelect={setEdaDetail}/>)}</div>
            ) : (
              <div style={{padding:'16px',textAlign:'center',color:'var(--text-dim)',fontSize:'0.8rem'}}>No EDA sessions. Use Fitbit to run an EDA scan.</div>
            )}
          </div>
        </div>
      );
    }

    // Standard sparkline chart card — hide if no data
    const chart = chartById[id];
    if (!chart || chart.data.length === 0) return null;
    return (
      <div key={id} {...wrapProps}>
        <VitalChart {...chart} onClick={() => setDrillDown(chart)} />
      </div>
    );
  };

  const handleExpandCard = async (cardId, range) => {
    setExpandedCard(cardId);
    setExpandedRange(range || '30d');
    setExpandedData([]);
    const metricMap = { hr: 'resting_hr', hrv: 'hrv', spo2: 'spo2', weight: 'weight', sleep: null };
    const metric = metricMap[cardId];
    if (!metric) {
      if (cardId === 'sleep') {
        try {
          const d = await api.get(`/fitness/sleep/history?range=${range || '30d'}`);
          setExpandedData(d.data || []);
        } catch(e) {}
      }
      return;
    }
    try {
      const d = await api.get(`/fitness/metric/${metric}?range=${range || '30d'}`);
      setExpandedData(d.data || []);
    } catch(e) {}
  };

  const InlineSparkline = ({ data, color = '#112240', width = 120, height = 40 }) => {
    if (!data || data.length < 2) return <span style={{color:'#555555',fontSize:'0.7rem'}}>—</span>;
    const vals = data.map(d => d.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const pts = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.value - min) / range) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const lastPt = data[data.length - 1];
    const lastX = width;
    const lastY = height - ((lastPt.value - min) / range) * (height - 4) - 2;
    return (
      <svg width={width} height={height} style={{display:'block',overflow:'visible'}}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx={lastX} cy={lastY} r="3" fill={color}/>
      </svg>
    );
  };

  const TimeRangeSelector = ({ value, onChange }) => {
    const ranges = ['7d','30d','90d','1y','all'];
    return (
      <div style={{display:'flex',gap:'4px',marginBottom:'12px'}}>
        {ranges.map(r => (
          <button key={r} onClick={() => onChange(r)}
            style={{padding:'3px 10px',borderRadius:'12px',border:'none',cursor:'pointer',fontSize:'0.7rem',fontWeight:600,
              background: value===r ? '#112240' : 'rgba(255,255,255,0.08)',
              color: value===r ? '#FFFFFF' : '#8B6914',
              transition:'all 0.15s'}}>
            {r}
          </button>
        ))}
      </div>
    );
  };

  const getHrvPercentile = (hrv30dAvg, age) => {
    const medians = { 20: 42, 30: 35, 40: 30, 50: 25, 60: 22, 70: 20 };
    const decade = Math.floor(age / 10) * 10;
    const median = medians[Math.min(70, Math.max(20, decade))] || 25;
    if (!hrv30dAvg) return null;
    const ratio = hrv30dAvg / median;
    if (ratio >= 1.5) return '85th+ percentile';
    if (ratio >= 1.2) return '65th percentile';
    if (ratio >= 1.0) return '50th percentile';
    if (ratio >= 0.8) return '35th percentile';
    return '20th percentile';
  };

  return (
    <div className="vital-profile">
      {fitbitSyncError && (
        <div style={{background:'#F8F7F4',border:'1px solid #F59E0B',borderRadius:'8px',padding:'10px 14px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'10px',fontSize:'0.85rem'}}>
          <span style={{color:'#F59E0B',fontSize:'1.1rem'}}>⚠</span>
          <span style={{color:'#112240',flex:1}}>Fitbit connection expired — data may be weeks old.</span>
          <button onClick={()=>onNav&&onNav('profile')} style={{background:'none',border:'none',color:'#112240',fontWeight:600,cursor:'pointer',padding:0,fontSize:'0.85rem',whiteSpace:'nowrap'}}>Reconnect →</button>
        </div>
      )}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
        <h2 style={{margin:0}}>Fitness</h2>
        {syncing && <span style={{fontSize:'11px',color:'var(--accent)',display:'flex',alignItems:'center',gap:'4px'}}>{'\u21BB'} Syncing...</span>}
      </div>
      {profileError&&<div className="error-banner">Fitbit connection issue: {profileError}<button className="retry-btn" onClick={onRetry}>Retry</button></div>}
      <div className="vital-cards">{[
        {label:'Age', value: profile.age ? formatAge(profile.age) : '--'},
        {label:'Height', value: profile.height || '--', unit:'cm'},
        {label:'Weight', value: latestWeight, unit:'kg'},
        {label:<GlossaryTerm term="bmi">BMI</GlossaryTerm>, value: bmi}
      ].map((c,i)=>(
        <div key={i} className="vital-card"><div className="vital-label">{c.label}</div><div className="vital-value">{c.value}{c.unit?<span className="vital-unit"> {c.unit}</span>:null}</div></div>))}</div>

      {/* Mode toggle */}
      <div className="vp-mode-toggle">
        <button className={viewMode==='today'?'active':''} onClick={()=>setViewMode('today')}>Today</button>
        <button className={viewMode==='history'?'active':''} onClick={()=>setViewMode('history')}>History</button>
      </div>

      {viewMode === 'today' && (
        <div className="vp-today">
          {/* Row 1: Today's Activity */}
          {todayData && (() => {
          // KAL-259 — `|| 0` re-fabricated on the client the zero the server just stopped
          // inventing. A null steps total is an ABSENCE: no ring arc, no percentage, and the
          // figure below says so in words. Declared at CARD scope because the ring and the
          // figure both read it — the first version of this fix put them inside the ring's IIFE
          // and the build caught three no-undef references, which is what a build is for.
          const _stepsT = todayData.steps?.total;
          const _stepsMissing = (_stepsT == null);
          return (
            <div className="vp-card vp-activity-card">
              <div className="vp-card-label">Today's Activity {todayData.as_of_date && todayData.as_of_date !== _localISODate() && <span style={{fontSize:'0.65rem',color:'#F59E0B',marginLeft:'6px'}} title="Your day is counted in your instance's timezone, which has already rolled over relative to your device.">Counting {formatDate(todayData.as_of_date)}</span>}</div>
              <div className="vp-activity-main">
                {/* Steps ring */}
                <div className="vp-steps-ring-wrap">
                  {(() => {
                    const pct = _stepsMissing ? 0 : Math.min(100, Math.round(_stepsT / 10000 * 100));
                    const r = 32; const circ = 2 * Math.PI * r;
                    const dash = (pct / 100) * circ;
                    const col = pct >= 80 ? '#112240' : pct >= 50 ? '#F59E0B' : '#8B6914';
                    return (
                      <div style={{position:'relative',width:80,height:80}}>
                        <svg width="80" height="80" viewBox="0 0 80 80">
                          <circle cx="40" cy="40" r={r} fill="none" stroke="#F8F7F4" strokeWidth="6"/>
                          <circle cx="40" cy="40" r={r} fill="none" stroke={col} strokeWidth="6"
                            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                            transform="rotate(-90 40 40)" style={{transition:'stroke-dasharray 0.8s ease'}}/>
                        </svg>
                        <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
                          <span style={{fontSize:'0.72rem',fontWeight:700,color:col}}>{_stepsMissing ? '—' : pct + '%'}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{marginTop:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'1.6rem',fontWeight:800,color:'var(--text-primary)',lineHeight:1}}>{_stepsMissing ? '—' : _stepsT.toLocaleString('en-GB')}</div>
                    <div style={{fontSize:'0.68rem',color:'var(--muted)'}}>steps</div>
                    {/* KAL-259 — vs-7d is a COMPARISON: with no steps figure there is nothing to
                        compare, and the old code rendered `0 - avg` as a real shortfall. */}
                    {!_stepsMissing && todayData.steps?.vs_7d_avg != null && (
                      <div style={{fontSize:'0.68rem',color: todayData.steps.vs_7d_avg >= 0 ? '#112240' : '#ef4444', marginTop:'2px'}}>
                        vs 7d avg: {todayData.steps.vs_7d_avg >= 0 ? '+' : ''}{Math.round(todayData.steps.vs_7d_avg)} {todayData.steps.vs_7d_avg >= 0 ? '↑' : '↓'}
                      </div>
                    )}
                  </div>
                </div>
                {/* Stat pills */}
                <div className="vp-stat-pills">
                  {todayData.active_minutes > 0 && (
                    <div className="vp-stat-pill">
                      <span className="vp-stat-val">{todayData.active_minutes}</span>
                      <span className="vp-stat-unit">active min</span>
                    </div>
                  )}
                  {todayData.calories > 0 && (
                    <div className="vp-stat-pill">
                      <span className="vp-stat-val">{todayData.calories.toLocaleString('en-GB')}</span>
                      <span className="vp-stat-unit">kcal</span>
                    </div>
                  )}
                  {todayData.distance_km > 0 && (
                    <div className="vp-stat-pill">
                      <span className="vp-stat-val">{todayData.distance_km}</span>
                      <span className="vp-stat-unit">km</span>
                    </div>
                  )}
                </div>
                {/* (#1377) The "active min" pill above is /api/fitness/today's active_minutes —
                    fitbit_activity.active_minutes, the device's own moderate + vigorous sum. State
                    the rule under the pills; a bare "34 active min" says nothing about what counted. */}
                {todayData.active_minutes > 0 && (
                  <div style={{fontSize:'0.68rem',lineHeight:1.4,color:'var(--muted)',marginTop:'6px'}}>
                    {DEVICE_ACTIVE_MIN_DEF_SHORT}
                  </div>
                )}
              </div>
            </div>
          ); })()}

          {/* Row 2: Last Night's Sleep */}
          {sleepData?.available && (
            <div className="vp-card vp-sleep-card" onClick={() => expandedCard === 'sleep' ? setExpandedCard(null) : handleExpandCard('sleep', expandedRange)} style={{cursor:'pointer'}}>
              <div className="vp-card-label">Last Night's Sleep {/* KAL-261 class sweep - same UTC-vs-local comparison as the two above. */}
              {sleepData.date && sleepData.date !== _localISODate() && <span style={{fontSize:'0.65rem',color:'#F59E0B',marginLeft:'4px'}}>({formatDate(sleepData.date)})</span>}<span style={{float:'right',fontSize:'0.7rem',color:'#112240'}}>{expandedCard==='sleep'?'▲':'▼'}</span></div>
              <div className="vp-sleep-main">
                <div style={{display:'flex',alignItems:'baseline',gap:'8px'}}>
                  <span style={{fontSize:'2.2rem',fontWeight:800,color:'var(--text-primary)'}}>{Math.floor(sleepData.duration_minutes/60)}h {sleepData.duration_minutes%60}m</span>
                  {sleepData.sleep_score && <span style={{background:'#F8F7F4',border:'2px solid #112240',borderRadius:'50%',width:'36px',height:'36px',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:'0.78rem',fontWeight:700,color:'#112240'}}>{sleepData.sleep_score}</span>}
                </div>
                {/* Stage bar */}
                {sleepData.stages && (sleepData.stages.deep_min + sleepData.stages.rem_min + sleepData.stages.light_min) > 0 && (
                  <div style={{marginTop:'8px'}}>
                    <div style={{height:'12px',display:'flex',borderRadius:'6px',overflow:'hidden',gap:'1px'}}>
                      {(() => {
                        const total = sleepData.stages.deep_min + sleepData.stages.rem_min + sleepData.stages.light_min + sleepData.stages.awake_min;
                        const segs = [
                          {label:'Deep',val:sleepData.stages.deep_min,color:'#1a3a6b'},
                          {label:'REM',val:sleepData.stages.rem_min,color:'#7b4fa0'},
                          {label:'Light',val:sleepData.stages.light_min,color:'#4a90c4'},
                          {label:'Awake',val:sleepData.stages.awake_min,color:'#444'},
                        ];
                        return segs.filter(s=>s.val>0).map(s => (
                          <div key={s.label} style={{flex:s.val/total,background:s.color}}/>
                        ));
                      })()}
                    </div>
                    <div style={{display:'flex',gap:'8px',marginTop:'4px',flexWrap:'wrap'}}>
                      {[{l:'Deep',v:sleepData.stages.deep_min,c:'#4a90c4'},{l:'REM',v:sleepData.stages.rem_min,c:'#7b4fa0'},{l:'Light',v:sleepData.stages.light_min,c:'#60a5fa'}].filter(s=>s.v>0).map(s=>(
                        <span key={s.l} style={{fontSize:'0.65rem',color:s.c}}>{s.l} {Math.floor(s.v/60)}h{s.v%60}m</span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{fontSize:'0.68rem',color:'var(--muted)',marginTop:'6px'}}>
                  Efficiency {sleepData.efficiency}%{sleepData.awakenings ? ` · Awakenings ${sleepData.awakenings}` : ''}
                </div>
              </div>
              {expandedCard === 'sleep' && expandedData.length > 0 && (
                <div style={{marginTop:'12px'}} onClick={e=>e.stopPropagation()}>
                  <TimeRangeSelector value={expandedRange} onChange={r=>{setExpandedRange(r);handleExpandCard('sleep',r);}}/>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={expandedData.map(d=>({...d,hrs:+(d.duration_min/60).toFixed(1)}))} margin={{top:5,right:5,bottom:0,left:-20}}>
                      <XAxis dataKey="date" tick={{fontSize:9,fill:'#8B6914'}} tickFormatter={formatDate} axisLine={false} tickLine={false}/>
                      <YAxis hide domain={[0,'dataMax+1']}/>
                      <Tooltip formatter={(v)=>[`${v}h`,'Sleep']} contentStyle={{background:'#112240',border:'1px solid rgba(255,255,255,0.12)',color:'#e2e8f0',fontSize:'11px'}}/>
                      <Bar dataKey="hrs" fill="#4a90c4" radius={[3,3,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Row 3: Heart Health (HR + HRV) */}
          <div className={`vp-heart-row${isMobile?' vp-stacked':''}`}>
            {/* Resting HR */}
            {hrSparkline.length > 0 && (
              <div className="vp-card vp-metric-card" onClick={() => expandedCard === 'hr' ? setExpandedCard(null) : handleExpandCard('hr', expandedRange)}>
                <div className="vp-card-label">Resting Heart Rate <span style={{float:'right',fontSize:'0.7rem',color:'#112240'}}>{expandedCard==='hr'?'▲':'▼'}</span></div>
                <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between'}}>
                  <div>
                    <span style={{fontSize:'2rem',fontWeight:800,color:'#f87171'}}>{hrSparkline[hrSparkline.length-1]?.value}</span>
                    <span style={{fontSize:'0.75rem',color:'var(--muted)',marginLeft:'4px'}}>bpm</span>
                    {hrSparkline.length >= 2 && (() => {
                      const d = hrSparkline[hrSparkline.length-1]?.value - hrSparkline[hrSparkline.length-2]?.value;
                      return <span style={{fontSize:'0.9rem',marginLeft:'6px',color:d<0?'#112240':d>0?'#ef4444':'#60a5fa'}}>{d<0?'↓':d>0?'↑':'→'}</span>;
                    })()}
                    <div style={{fontSize:'0.68rem',color:'var(--muted)',marginTop:'2px'}}>
                      7d avg: {Math.round(hrSparkline.reduce((s,d)=>s+d.value,0)/hrSparkline.length)} bpm
                    </div>
                  </div>
                  <InlineSparkline data={hrSparkline} color="#f87171" width={100} height={44}/>
                </div>
                {expandedCard === 'hr' && (
                  <div style={{marginTop:'12px'}} onClick={e=>e.stopPropagation()}>
                    <TimeRangeSelector value={expandedRange} onChange={r=>{setExpandedRange(r);handleExpandCard('hr',r);}}/>
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart data={expandedData.map(d=>({...d,ts:new Date(d.date).getTime()}))} margin={{top:5,right:5,bottom:0,left:-20}}>
                        <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']} tick={{fontSize:9,fill:'#8B6914'}} tickFormatter={formatDate} axisLine={false} tickLine={false}/>
                        <YAxis hide/>
                        <Tooltip labelFormatter={formatDate} formatter={v=>[`${v} bpm`,'HR']} contentStyle={{background:'#112240',border:'1px solid rgba(255,255,255,0.12)',color:'#e2e8f0',fontSize:'11px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#f87171" strokeWidth={2} dot={expandedData.length<=30?{r:2,fill:'#f87171'}:false} activeDot={{r:4}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* HRV */}
            {hrvSparkline.length > 0 && (
              <div className="vp-card vp-metric-card" onClick={() => expandedCard === 'hrv' ? setExpandedCard(null) : handleExpandCard('hrv', expandedRange)}>
                <div className="vp-card-label">HRV <span style={{float:'right',fontSize:'0.7rem',color:'#112240'}}>{expandedCard==='hrv'?'▲':'▼'}</span></div>
                <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between'}}>
                  <div>
                    <span style={{fontSize:'2rem',fontWeight:800,color:'#818cf8'}}>{Math.round(hrvSparkline[hrvSparkline.length-1]?.value)}</span>
                    <span style={{fontSize:'0.75rem',color:'var(--muted)',marginLeft:'4px'}}>ms</span>
                    {hrvSparkline.length >= 2 && (() => {
                      const d = hrvSparkline[hrvSparkline.length-1]?.value - hrvSparkline[hrvSparkline.length-2]?.value;
                      return <span style={{fontSize:'0.9rem',marginLeft:'6px',color:d>0?'#112240':d<0?'#ef4444':'#60a5fa'}}>{d>0?'↑':d<0?'↓':'→'}</span>;
                    })()}
                    {(() => {
                      const avg = hrvSparkline.reduce((s,d)=>s+d.value,0)/hrvSparkline.length;
                      const pct = getHrvPercentile(avg, profile?.dob ? Math.floor((Date.now()-new Date(profile.dob).getTime())/3.15576e10) : 55);
                      return pct && <div style={{fontSize:'0.68rem',color:'var(--muted)',marginTop:'2px'}}>{pct} for age</div>;
                    })()}
                  </div>
                  <InlineSparkline data={hrvSparkline} color="#818cf8" width={100} height={44}/>
                </div>
                {expandedCard === 'hrv' && (
                  <div style={{marginTop:'12px'}} onClick={e=>e.stopPropagation()}>
                    <TimeRangeSelector value={expandedRange} onChange={r=>{setExpandedRange(r);handleExpandCard('hrv',r);}}/>
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart data={expandedData.map(d=>({...d,ts:new Date(d.date).getTime()}))} margin={{top:5,right:5,bottom:0,left:-20}}>
                        <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']} tick={{fontSize:9,fill:'#8B6914'}} tickFormatter={formatDate} axisLine={false} tickLine={false}/>
                        <YAxis hide/>
                        <Tooltip labelFormatter={formatDate} formatter={v=>[`${v} ms`,'HRV']} contentStyle={{background:'#112240',border:'1px solid rgba(255,255,255,0.12)',color:'#e2e8f0',fontSize:'11px'}}/>
                        <Line type="monotone" dataKey="value" stroke="#818cf8" strokeWidth={2} dot={expandedData.length<=30?{r:2,fill:'#818cf8'}:false} activeDot={{r:4}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Row 4: SpO2 */}
          {spo2Sparkline.length > 0 && (
            <div className="vp-card" onClick={() => expandedCard === 'spo2' ? setExpandedCard(null) : handleExpandCard('spo2', expandedRange)} style={{cursor:'pointer'}}>
              <div className="vp-card-label">Blood Oxygen (SpO₂) <span style={{float:'right',fontSize:'0.7rem',color:'#112240'}}>{expandedCard==='spo2'?'▲':'▼'}</span></div>
              <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between'}}>
                <div>
                  <span style={{fontSize:'2rem',fontWeight:800,color:'#60a5fa'}}>{spo2Sparkline[spo2Sparkline.length-1]?.value?.toFixed(1)}</span>
                  <span style={{fontSize:'0.75rem',color:'var(--muted)',marginLeft:'4px'}}>%</span>
                  <div style={{fontSize:'0.68rem',color:'var(--muted)',marginTop:'2px'}}>7d avg: {(spo2Sparkline.reduce((s,d)=>s+d.value,0)/spo2Sparkline.length).toFixed(1)}%</div>
                </div>
                <InlineSparkline data={spo2Sparkline} color="#60a5fa" width={120} height={44}/>
              </div>
              {expandedCard === 'spo2' && (
                <div style={{marginTop:'12px'}} onClick={e=>e.stopPropagation()}>
                  <TimeRangeSelector value={expandedRange} onChange={r=>{setExpandedRange(r);handleExpandCard('spo2',r);}}/>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={expandedData.map(d=>({...d,ts:new Date(d.date).getTime()}))} margin={{top:5,right:5,bottom:0,left:-20}}>
                      <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']} tick={{fontSize:9,fill:'#8B6914'}} tickFormatter={formatDate} axisLine={false} tickLine={false}/>
                      <YAxis hide domain={[90,100]}/>
                      <ReferenceLine y={95} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={1}/>
                      <Tooltip labelFormatter={formatDate} formatter={v=>[`${v}%`,'SpO2']} contentStyle={{background:'#112240',border:'1px solid rgba(255,255,255,0.12)',color:'#e2e8f0',fontSize:'11px'}}/>
                      <Line type="monotone" dataKey="value" stroke="#60a5fa" strokeWidth={2} dot={expandedData.length<=30?{r:2,fill:'#60a5fa'}:false} activeDot={{r:4}}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Row 5: Weight */}
          {weightSparkline.length > 0 && (
            <div className="vp-card" onClick={() => expandedCard === 'weight' ? setExpandedCard(null) : handleExpandCard('weight', '90d')} style={{cursor:'pointer'}}>
              <div className="vp-card-label">Weight <span style={{float:'right',fontSize:'0.7rem',color:'#112240'}}>{expandedCard==='weight'?'▲':'▼'}</span></div>
              <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between'}}>
                <div>
                  <span style={{fontSize:'2rem',fontWeight:800,color:'#34d399'}}>{weightSparkline[weightSparkline.length-1]?.value?.toFixed(1)}</span>
                  <span style={{fontSize:'0.75rem',color:'var(--muted)',marginLeft:'4px'}}>kg</span>
                  {profile?.height_cm && (() => {
                    const bmi = (weightSparkline[weightSparkline.length-1]?.value / ((profile.height_cm/100)**2)).toFixed(1);
                    const bmiColor = bmi < 18.5 ? '#F59E0B' : bmi < 25 ? '#112240' : bmi < 30 ? '#F59E0B' : '#ef4444';
                    return <span style={{fontSize:'0.75rem',color:bmiColor,marginLeft:'8px'}}>BMI {bmi}</span>;
                  })()}
                </div>
                <InlineSparkline data={weightSparkline} color="#34d399" width={120} height={44}/>
              </div>
              {expandedCard === 'weight' && (
                <div style={{marginTop:'12px'}} onClick={e=>e.stopPropagation()}>
                  <TimeRangeSelector value={expandedRange} onChange={r=>{setExpandedRange(r);handleExpandCard('weight',r);}}/>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={expandedData.map(d=>({...d,ts:new Date(d.date).getTime()}))} margin={{top:5,right:5,bottom:0,left:-20}}>
                      <XAxis dataKey="ts" type="number" scale="time" domain={['dataMin','dataMax']} tick={{fontSize:9,fill:'#8B6914'}} tickFormatter={formatDate} axisLine={false} tickLine={false}/>
                      <YAxis hide/>
                      <Tooltip labelFormatter={formatDate} formatter={v=>[`${v}kg`,'Weight']} contentStyle={{background:'#112240',border:'1px solid rgba(255,255,255,0.12)',color:'#e2e8f0',fontSize:'11px'}}/>
                      <Line type="monotone" dataKey="value" stroke="#34d399" strokeWidth={2} dot={false} activeDot={{r:4}}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {viewMode === 'history' && (
        <>
          {loading?<LoadingSpinner message="Loading fitness data..."/>:
            (wD.length===0 && hrD.length===0 && slD.length===0 && stD.length===0 && hrvD.length===0 && calD.length===0 && spo2D.length===0 && bpChartData.length===0 && breathingRateD.length===0 && skinTempD.length===0 && cardioScoreD.length===0 && ecgSessions.length===0 && edaSessions.length===0) ? (
              <div style={{textAlign:'center',padding:'48px 24px',color:'var(--text-secondary)'}}>
                <div style={{fontSize:'48px',marginBottom:'16px',opacity:0.5}}>&#9201;</div>
                <h3 style={{color:'var(--text-primary)',marginBottom:'8px'}}>No fitness data yet</h3>
                {/* B — the prompt must match how THIS user's data actually arrives. The old copy
                    named Fitbit unconditionally, so an Apple-Health user in the native app was
                    told to go and connect Fitbit — a provider they do not use and, inside the
                    app, cannot usefully connect. Three honest cases instead of one guess. */}
                {isInNativeApp()
                  ? <p style={{fontSize:'14px',maxWidth:'400px',margin:'0 auto'}}>Your device data syncs automatically in the background. If you have just granted permissions, pull down to refresh — the first sync can take a few minutes.</p>
                  : todayData?.fitbit_connected
                    ? <p style={{fontSize:'14px',maxWidth:'400px',margin:'0 auto'}}>Fitbit is connected but no data has synced yet. Tap <strong>Profile → Sync now</strong> to pull your data.</p>
                    : <p style={{fontSize:'14px',maxWidth:'400px',margin:'0 auto'}}>Open the <strong>Kalpa app</strong> on your phone to sync Apple Health or Health Connect automatically{FEATURES_GLOBAL.wearable_route === 'fitbit_direct' ? <>, or connect <strong>Fitbit</strong> on your Profile page</> : null}.</p>
                }
              </div>
            ) : (
            <div className="vital-charts-grid">
              <div style={{gridColumn:'1 / -1',fontSize:'11px',color:'var(--text-dim)',marginBottom:'4px'}}>Drag cards to reorder &mdash; cards with no data are hidden automatically</div>
              {effectiveCardOrder.map((id, idx) => renderCard(id, idx))}
            </div>)}
        </>
      )}

      {ecgDetail && <ECGDetailModal session={ecgDetail} onClose={() => setEcgDetail(null)}/>}
      {edaDetail && <EDADetailModal session={edaDetail} onClose={() => setEdaDetail(null)}/>}
      {drillDown && <MetricDetailView metric={drillDown} onClose={()=>setDrillDown(null)} onAskAI={onAskAI}/>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Home Feed — Proactive AI insight cards + inline chat
// ═══════════════════════════════════════════════════════════════════════

const CARD_TYPE_COLORS = {
  Fitness: '#112240',
  Biomarker: '#60a5fa',
  Medication: '#f59e0b',
  Genome: '#818CF8',
  Combined: '#C9973A',
};

function InsightCard({ card, onAskAI, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const color = CARD_TYPE_COLORS[card.card_type] || '#112240';
  // When onOpen is supplied (Today feed Phase 2), tapping the headline opens
  // the Insight drawer over Today instead of expanding inline.
  const handleHeadline = () => { if (onOpen) onOpen(card); else setExpanded(e => !e); };
  return (
    <div className="insight-card">
      <div className="insight-card-header">
        <span className="insight-type-badge" style={{ background: color + '22', color }}>{card.card_type}</span>
        {card.source_label && <span className="insight-source">{card.source_label}</span>}
      </div>
      <div className="insight-headline" onClick={handleHeadline}>
        {card.headline}
        <span className="insight-expand-icon">{onOpen ? ' →' : (expanded ? ' ▲' : ' ▼')}</span>
      </div>
      {!onOpen && expanded && <div className="insight-detail">{card.detail}</div>}
      {card.ai_question && (
        <button className="insight-ask-btn" onClick={() => onAskAI(card.ai_question)}>
          Ask AI &rarr;
        </button>
      )}
    </div>
  );
}

// -- TodayDashboard (v7) -------------------------------------------------------
function TodayDashboard({ onAskAI, fitbitSyncError, onNav }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coachEvents, setCoachEvents] = useState([]);
  const [coachExpanded, setCoachExpanded] = useState({});

  useEffect(() => {
    api.get('/today/summary')
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
    api.get('/coach/timeline?limit=5').then(d => setCoachEvents(d.events || [])).catch(() => {});
  }, []);

  // Same server-side timestamp the sync strip shows (/api/fitness/last-received).
  const { lastSynced } = useSyncStatus();
  // LOUD diagnostic: a sync can succeed and still bring nothing newer than what we
  // already hold (e.g. yesterday's run never left the phone). That reads as "broken"
  // on screen, so name the gap in the console instead of failing silently.
  const activityAsOf = data?.today_activity?.as_of_date || null;
  useEffect(() => {
    if (!activityAsOf || !lastSynced) return;
    const syncDay = String(lastSynced).slice(0, 10);   // server day (UTC) of last ingest
    if (activityAsOf < syncDay) {
      console.warn(`[today] activity gap: newest activity is ${activityAsOf} but data was last received ${syncDay} — the sync completed and returned nothing newer than ${activityAsOf}.`);
    }
  }, [activityAsOf, lastSynced]);

  const markCoachEventRead = async (id) => {
    try {
      await fetch(`${API_BASE}/coach/timeline/${id}/read`, { method: 'PATCH', credentials: 'include' });
    } catch(e) {}
  };

  if (loading) return <LoadingSpinner message="Loading today's summary..." />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const { greeting, readiness, last_night, today_activity, vitals_snapshot, bio_age, insights, blood_panel_status, coach_nudge } = data;

  // Readiness ring
  const rScore = readiness?.score ?? null;
  const rColor = rScore === null ? '#555555' : rScore >= 65 ? '#112240' : rScore >= 40 ? '#F59E0B' : '#ef4444';
  const rLabel = readiness?.label || 'Unavailable';
  const circum = 2 * Math.PI * 36;
  const rDash = rScore !== null ? (rScore / 100) * circum : 0;

  // Steps progress
  const steps = today_activity?.steps || 0;
  const stepsGoal = today_activity?.steps_goal || 10000;
  const stepsPct = Math.min(100, Math.round((steps / stepsGoal) * 100));
  const stepsColor = stepsPct >= 80 ? '#112240' : stepsPct >= 50 ? '#F59E0B' : '#8B6914';
  const hasActivity = steps > 0 || today_activity?.active_minutes > 0 || today_activity?.calories_burned > 0;

  // Bio age
  const bioAge = bio_age?.irs_a;
  const chrono = bio_age?.chrono;
  const delta = bio_age?.delta;
  const deltaColor = delta === undefined || delta === null ? '#8B6914' : delta < 0 ? '#112240' : delta > 0 ? '#ef4444' : '#60a5fa';

  // Sleep bar widths
  const sleepHrs = last_night?.duration_hrs;
  // 2h(ii) — render from EXACT minutes where the payload carries them. duration_hrs is
  // 1-dp, so 365 min round-trips to 6.1h and back to 366 min: the Today card said
  // "6h 6m" while the timeline, reading minutes directly, said "6h 5m". Same night,
  // one manufactured minute. duration_hrs remains the fallback and the colour input.
  const sleepMins = last_night?.asleep_minutes;
  const sleepColor = !sleepHrs ? '#555555' : sleepHrs >= 7 ? '#112240' : sleepHrs >= 6 ? '#F59E0B' : '#ef4444';

  // C — "Fitbit connection EXPIRED" was being shown to anyone whose Fitbit was simply NOT
  // CONNECTED, i.e. every user who has never used Fitbit at all. Daniel (Apple Watch, no Fitbit
  // account) was told his Fitbit connection had expired and invited to reconnect something he
  // never connected. A banner may only claim expiry when there IS a connection that expired:
  // the server sets fitbit_status.needs_reconnect only when a token EXISTS and needs_reauth is
  // set (app.py, _fitbit_sync_status), so trust that rather than inferring from `!connected`.
  const fs = data?.fitbit_status;
  const showFitbitBanner = !!(fitbitSyncError || (fs && fs.connected && fs.needs_reconnect));

  // RUN B P11 — the VALUE MOMENT. The soft pre-prompt is armed only once the user is actually LOOKING
  // at a score of their own (readiness or bio-age resolved), because that is the first point at which
  // "want to know when this changes?" means anything. The server still gates it on
  // intent=yes AND no token AND never-prompted, so this only decides WHEN to ask, not WHETHER.
  const firstScoreVisible = (rScore !== null && rScore !== undefined) ||
                            (bioAge !== null && bioAge !== undefined);

  return (
    <div className="today-dashboard">
      <ValueMomentPush ready={firstScoreVisible} />
      {showFitbitBanner && (
        <div style={{background:'#F8F7F4',border:'1px solid #F59E0B',borderRadius:'8px',padding:'10px 14px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'10px',fontSize:'0.85rem'}}>
          <span style={{color:'#F59E0B',fontSize:'1.1rem'}}>⚠</span>
          <span style={{color:'#112240',flex:1}}>Fitbit connection expired — data may be weeks old.</span>
          <button onClick={()=>onNav&&onNav('profile')} style={{background:'none',border:'none',color:'#112240',fontWeight:600,cursor:'pointer',padding:0,fontSize:'0.85rem',whiteSpace:'nowrap'}}>Reconnect →</button>
        </div>
      )}
      <div className="today-greeting">{localGreeting(greeting)}</div>

      {/* Top row: Readiness + Sleep */}
      <div className="today-top-row">
        {/* Readiness ring */}
        <div className="today-card readiness-card">
          <div className="today-card-label">Readiness</div>
          <div className="readiness-ring-wrap">
            <svg width="88" height="88" viewBox="0 0 88 88">
              <circle cx="44" cy="44" r="36" fill="none" stroke="#F8F7F4" strokeWidth="8"/>
              {rScore !== null && (
                <circle cx="44" cy="44" r="36" fill="none" stroke={rColor} strokeWidth="8"
                  strokeDasharray={`${rDash} ${circum}`}
                  strokeLinecap="round"
                  transform="rotate(-90 44 44)"
                  style={{transition:'stroke-dasharray 0.8s ease'}}
                />
              )}
              <text x="44" y="40" textAnchor="middle" fill={rColor} fontSize="16" fontWeight="700">{rScore ?? '—'}</text>
              <text x="44" y="54" textAnchor="middle" fill="#8B6914" fontSize="9">{rLabel}</text>
            </svg>
          </div>
          {readiness?.breakdown && (
            <div className="readiness-breakdown">
              {Object.entries(readiness.breakdown).map(([k, v]) => (
                <div key={k} className="readiness-row">
                  <span className="readiness-key">{k.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</span>
                  <span className="readiness-val" style={{color: v>=65?'#112240':v>=40?'#F59E0B':'#ef4444'}}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sleep card */}
        <div className="today-card sleep-card">
          <div className="today-card-label">Last Night</div>
          {last_night ? (
            <>
              <div className="sleep-big" style={{color: sleepColor}}>{sleepMins != null ? fmtDurationMin(sleepMins) : fmtDurationHours(sleepHrs)}</div>
              <div className="sleep-meta-row">
                <span className="sleep-chip">Eff {last_night.efficiency_pct}%</span>
                <span className="sleep-chip">Deep {last_night.deep_pct}%</span>
                <span className="sleep-chip">REM {last_night.rem_pct}%</span>
              </div>
              <div className="sleep-score-bar-wrap">
                <div className="sleep-score-bar" style={{width:`${last_night.sleep_score}%`, backgroundColor: sleepColor}}/>
              </div>
              <div className="sleep-score-label">Sleep score {last_night.sleep_score}/100</div>
            </>
          ) : (
            <div className="today-no-data">No sleep data</div>
          )}
        </div>
      </div>

      {/* Activity strip */}
      <div className="today-card activity-strip">
        <div className="today-card-label">Activity</div>
        {!hasActivity ? (
          // Honest, not blank: say when data last arrived so an empty day is
          // distinguishable from a sync that never landed.
          <div className="today-no-data">
            No activity recorded yet{lastSynced ? ` — data last received ${_relSync(lastSynced)}` : ' — no wearable data received yet'}.
          </div>
        ) : (
        <div className="activity-row">
          <div className="activity-metric">
            <div className="activity-val" style={{color: stepsColor}}>{steps.toLocaleString('en-GB')}</div>
            <div className="activity-unit">steps</div>
            <div className="steps-bar-track">
              <div className="steps-bar-fill" style={{width:`${stepsPct}%`, backgroundColor: stepsColor}}/>
            </div>
            <div className="activity-unit">{stepsPct}% of {stepsGoal.toLocaleString('en-GB')}</div>
          </div>
          {today_activity?.active_minutes > 0 && (
            <div className="activity-metric">
              <div className="activity-val">{today_activity.active_minutes}</div>
              <div className="activity-unit">active min</div>
              {/* (#1377) /api/today/summary serves fitbit_activity.active_minutes — the device's own
                  moderate + vigorous sum. Say so here: without it "active min" is unfalsifiable,
                  and it is NOT the heart-rate-zone figure used for Active Zone Minutes.
                  Styled inline: .activity-unit carries no rules of its own in App.css. */}
              <div style={{ maxWidth: 200, marginTop: 3, fontSize: '0.68rem', lineHeight: 1.35, color: 'var(--muted)' }}>
                {DEVICE_ACTIVE_MIN_DEF_SHORT}
              </div>
            </div>
          )}
          {today_activity?.calories_burned > 0 && (
            <div className="activity-metric">
              <div className="activity-val">{today_activity.calories_burned.toLocaleString('en-GB')}</div>
              <div className="activity-unit">kcal</div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Vitals snapshot */}
      {vitals_snapshot && (vitals_snapshot.resting_hr || vitals_snapshot.hrv || vitals_snapshot.spo2) && (
        <div className="today-vitals-row">
          {vitals_snapshot.resting_hr && (
            <div className="vitals-chip">
              <span className="vitals-icon">❤️</span>
              <span className="vitals-val">{Math.round(vitals_snapshot.resting_hr)}</span>
              <span className="vitals-unit">bpm</span>
            </div>
          )}
          {vitals_snapshot.hrv && (
            <div className="vitals-chip">
              <span className="vitals-icon">〰️</span>
              <span className="vitals-val">{Math.round(vitals_snapshot.hrv)}</span>
              <span className="vitals-unit">ms HRV</span>
            </div>
          )}
          {vitals_snapshot.spo2 && (
            <div className="vitals-chip">
              <span className="vitals-icon">🩸</span>
              <span className="vitals-val">{vitals_snapshot.spo2.toFixed(1)}</span>
              <span className="vitals-unit">% SpO₂</span>
            </div>
          )}
        </div>
      )}

      {/* Bio age snap */}
      {bioAge && (
        <div className="today-card bio-age-snap">
          <div className="today-card-label">Wearable Biological Age</div>
          <div className="bio-snap-row">
            <div className="bio-snap-age" style={{color: deltaColor}}>{bioAge.toFixed(1)}</div>
            <div className="bio-snap-detail">
              <div>Chronological: {chrono}</div>
              {delta !== null && delta !== undefined && (
                <div style={{color: deltaColor, fontWeight: 600}}>
                  {delta < 0 ? `${Math.abs(delta).toFixed(1)}y younger` : delta > 0 ? `${delta.toFixed(1)}y older` : 'Same as chrono'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Insights */}
      {insights && insights.length > 0 && (
        <div className="today-insights">
          <div className="today-section-label">Insights</div>
          {insights.map((ins, i) => {
            const pColor = ins.priority === 'warning' ? '#F59E0B' : ins.priority === 'success' ? '#112240' : '#60a5fa';
            return (
              <div key={i} className="today-insight-item" style={{borderLeftColor: pColor}}>
                <div className="insight-item-title">{ins.title}</div>
                <div className="insight-item-body">{deIsoDates(ins.body)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Coach Timeline */}
      {coachEvents.length > 0 && (
        <div className="coach-timeline">
          <div className="today-section-label">Coach Timeline</div>
          <div className="coach-timeline-rail">
            {coachEvents.map((ev) => {
              const isExpanded = coachExpanded[ev.id];
              const isUnread = !ev.read_at;
              const dotColor = ev.type === 'achievement' ? '#112240'
                : ev.type === 'anomaly' ? '#ef4444'
                : ev.type === 'morning_readiness' ? '#F59E0B'
                : '#60a5fa';
              const relTime = (() => {
                const diff = Date.now() - new Date(ev.created_at + 'Z').getTime();
                const h = Math.floor(diff / 3600000);
                if (h < 1) return 'Just now';
                if (h < 24) return `${h}h ago`;
                const d = Math.floor(h / 24);
                if (d === 1) return 'Yesterday';
                return `${d} days ago`;
              })();
              return (
                <div key={ev.id} className={`coach-event${isUnread?' coach-event-unread':''}`}
                  onClick={() => {
                    setCoachExpanded(p => ({...p, [ev.id]: !p[ev.id]}));
                    if (isUnread) markCoachEventRead(ev.id);
                  }}>
                  <div className="coach-event-dot" style={{background:dotColor}}/>
                  <div className="coach-event-body">
                    <div className="coach-event-header">
                      <span className="coach-event-headline">{ev.headline}</span>
                      <span className="coach-event-time">{relTime}</span>
                    </div>
                    {isExpanded && (
                      <div className="coach-event-detail">
                        <p>{ev.detail}</p>
                        {ev.action?.type === 'chat' && (
                          <button className="coach-action-btn" onClick={e => {
                            e.stopPropagation();
                            onAskAI && onAskAI(ev.action.prompt);
                          }}>{ev.action.label} &rarr;</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Blood panel status */}
      {blood_panel_status?.last_date && (
        <div className={`today-blood-status ${blood_panel_status.stale ? 'stale' : ''}`}>
          <span>🩺</span>
          <span>Last blood panel: {blood_panel_status.last_date} ({blood_panel_status.days_ago}d ago)</span>
          {blood_panel_status.stale && <span className="stale-badge">Due for retest</span>}
        </div>
      )}

      {/* Coach prompt */}
      {coach_nudge && (
        <button className="today-coach-btn" onClick={() => onAskAI && onAskAI(coach_nudge.prompt)}>
          🧠 {coach_nudge.text}
        </button>
      )}
    </div>
  );
}

function HomeFeed({ onAskAI }) {
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);

  const loadCards = useCallback(() => {
    return api.get('/insights').then(d => {
      setCards(d.cards || []);
      if (d.generating && (!d.cards || d.cards.length === 0)) {
        setGenerating(true);
        if (!pollRef.current) {
          pollRef.current = setInterval(() => {
            api.get('/insights').then(d2 => {
              if (d2.cards && d2.cards.length > 0) {
                setCards(d2.cards);
                setGenerating(false);
                clearInterval(pollRef.current);
                pollRef.current = null;
              }
            }).catch(() => {});
          }, 8000);
        }
      } else {
        setGenerating(false);
      }
    }).catch(() => setCards([]))
      .finally(() => setCardsLoading(false));
  }, []);

  useEffect(() => {
    setCardsLoading(true);
    loadCards();
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [loadCards]);

  const handleRegenerateInsights = async () => {
    setGenerating(true);
    setCards([]);
    try {
      await api.post('/insights/generate', {});
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      pollRef.current = setInterval(() => {
        api.get('/insights').then(d => {
          if (d.cards && d.cards.length > 0) {
            setCards(d.cards);
            setGenerating(false);
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }).catch(() => {});
      }, 8000);
    } catch (e) { setGenerating(false); }
  };

  return (
    <div className="home-feed">
      <div className="home-feed-header">
        <h2 className="home-feed-title">Health Insights</h2>
        <button className="insight-regen-btn" onClick={handleRegenerateInsights} disabled={generating} title="Regenerate insights">
          {generating ? '\u23F3' : '\uD83D\uDD04'}
        </button>
      </div>
      {cardsLoading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div className="spinner" style={{ width: 24, height: 24, margin: '0 auto 8px' }} />
          Generating your personalised insights...
        </div>
      ) : cards.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>&#129302;</div>
          <p>No insights yet. <button onClick={handleRegenerateInsights} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}>Generate now</button></p>
        </div>
      ) : (
        <div className="insight-cards-grid">
          {cards.map((card, i) => (
            <InsightCard key={card.id || i} card={card} onAskAI={onAskAI} />
          ))}
        </div>
      )}
    </div>
  );
}


// -- Main App ----------------------------------------------------------------
// -- NotificationPanel (Phase 3 — evidence-scored health insights) ----------
function NotificationBell({ onClick, count, pulse }) {
  return (
    <button className={`notification-bell${pulse ? ' bell-pulse' : ''}`} onClick={onClick} title="Health Insights">
      <span className="icon">{'\uD83D\uDD14'}</span>
      {count > 0 && <span className="notification-badge">{count}</span>}
      {pulse && <span className="bell-pulse-dot"/>}
    </button>
  );
}

// Initials extracted from a display name. "Timothy Gocher" -> "TG",
// "Priya Sharma" -> "PS", single names -> first two letters. Returns
// upper-case glyphs ready for direct render inside an avatar circle.
function _initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const p = parts[0];
    return (p.length >= 2 ? p.slice(0, 2) : p[0]).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Coloured initials circle. The fill colour follows the active brand
// (Kalpa gold on main, NPL blue on the NPL instance) so the avatar
// blends with the rest of the chrome. `size` controls the diameter in
// px; default 32 is the chrome-bar size, 40 is the dropdown header.
function Avatar({ name, size = 32, onClick, title }) {
  const isNpl = FEATURES_GLOBAL.branding.instance === 'npl';
  const fill  = isNpl ? '#7F91DF' : '#C9973A';
  const Tag   = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      title={title || name || ''}
      className="kalpa-avatar"
      aria-label={onClick ? `Open account menu for ${name || 'user'}` : undefined}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: fill, color: '#FFFFFF',
        fontFamily: "'Satoshi', sans-serif", fontWeight: 500,
        fontSize: Math.round(size * 0.44), lineHeight: 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', padding: 0, cursor: onClick ? 'pointer' : 'default',
        flexShrink: 0, letterSpacing: 0.2,
      }}
    >{_initials(name)}</Tag>
  );
}

// Avatar + dropdown/sheet that hosts the account menu. Replaces the
// "profile-mini" footer chip and the Profile/Privacy nav buttons.
// On desktop the menu pops as a dropdown anchored to the avatar; on
// mobile (\u2264768px) it slides up as a bottom sheet.
// ── NPL light/dark theme (NPL instance ONLY) ────────────────────────────────
// Two modes from the NPL logo palette; default light; persists per device in
// localStorage. Applied via data-npl-theme on <html>+<body>; the index.html
// bootstrap sets it pre-paint so there's no flash. Never rendered on Kalpa/main.
function _nplGetTheme() {
  // NPL defaults to DARK; only an explicit saved 'light' choice opts out.
  try { return localStorage.getItem('npl_theme') === 'light' ? 'light' : 'dark'; }
  catch { return 'dark'; }
}
function _nplApplyTheme(mode) {
  const m = mode === 'dark' ? 'dark' : 'light';
  try { localStorage.setItem('npl_theme', m); } catch {}
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-npl-theme', m);
    if (document.body) document.body.setAttribute('data-npl-theme', m);
    const tc = document.querySelector('meta[name="theme-color"]');
    if (tc) tc.setAttribute('content', m === 'dark' ? '#0A0E2A' : '#FFFFFF');
  }
}

function NplThemeToggle() {
  // Dark-toggle availability is driven by the THEME flag (independent of branding).
  const [mode, setMode] = useState(_nplGetTheme);
  // Dark-toggle is a CORE element (present on all instances); both kalpa + npl themes have
  // light+dark palettes. Renders for any theme value.
  if (!FEATURES_GLOBAL.theme) return null;
  const dark = mode === 'dark';
  const toggle = () => {
    const next = dark ? 'light' : 'dark';
    _nplApplyTheme(next);
    setMode(next);
  };
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Dark mode — tap for light' : 'Light mode — tap for dark'}
      className="npl-theme-toggle"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 34, height: 34, borderRadius: 999, padding: 0, flexShrink: 0,
        background: 'transparent', border: '1px solid var(--border-color)',
        color: 'var(--accent)', cursor: 'pointer', fontSize: 16, lineHeight: 1,
      }}
    >
      {/* RUN9 §6 — reflect the CURRENT mode (moon = dark / sun = light), so the badge is a
          distinct light/dark toggle and no longer duplicates the Today sun logo in dark mode. */}
      {dark ? '🌙' : '☀️'}
    </button>
  );
}

// ═══════════════════ OPS CONSOLE (superadmin-only, NPL) ═══════════════════
// Three-leg continuous-improvement console: Inbox (signal ingestion +
// synthesis), Bundles (issue-cluster pipeline), Horizon (strategic triage),
// KB (knowledge base + changelog). Every endpoint is superadmin-gated
// server-side; the client additionally hides the route and menu entry.
// All fetch errors surface loud (server error string in a banner) — no
// silent catches anywhere below.

const opsApi = {
  call: async (path, opts = {}) => {
    // KAL-262 — bounded here rather than at 63 call sites.
    const r = await _abortableFetch(`${API_BASE}/admin/ops${path}`, { credentials: 'include', ...opts }, LOAD_TIMEOUT_MS, `ops ${path}`);
    let j = null;
    try { j = await r.json(); } catch (e) { /* non-JSON body — message built below */ }
    if (!r.ok) {
      const msg = j ? [j.error, j.detail].filter(Boolean).join(': ') : '';
      const err = new Error(msg || `HTTP ${r.status}`);
      err.status = r.status;
      throw err;
    }
    return j;
  },
  get: (p) => opsApi.call(p),
  post: (p, data) => opsApi.call(p, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data || {}) }),
  del: (p) => opsApi.call(p, { method: 'DELETE' }),
};

function useOpsMobile() {
  const [m, setM] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const fn = (e) => setM(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', fn); else mq.addListener(fn);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', fn); else mq.removeListener(fn); };
  }, []);
  return m;
}

function OpsErrorBanner({ error, onRetry, onDismiss }) {
  if (!error) return null;
  return (
    <div className="ops-error-banner" role="alert">
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>⚠ {String(error)}</span>
      {onRetry && <button className="ops-btn ops-btn-small" onClick={onRetry}>Retry</button>}
      {onDismiss && <button className="ops-btn ops-btn-small" onClick={onDismiss}>Dismiss</button>}
    </div>
  );
}

function OpsSkeleton({ rows = 3 }) {
  return (
    <div>
      {Array.from({ length: rows }, (_, i) => <div key={i} className="ops-skel" />)}
    </div>
  );
}

const OPS_SEV_COLOR = { crash: '#dc2626', major: '#d97706', minor: '#2563eb', cosmetic: '#6b7280' };
const OPS_MOAT_COLOR = { reinforce: '#16a34a', threat: '#dc2626', neutral: '#6b7280' };
const OPS_PIPELINE = ['new', 'triaged', 'specd', 'fixing', 'shipped', 'verified', 'notified'];
// Mirror of the server's LEGAL_TRANSITIONS (verified only via smoke pass).
const OPS_NEXT_LEGAL = {
  new: ['triaged', 'logged'], triaged: ['specd', 'fixing', 'logged'],
  specd: ['fixing', 'logged'], fixing: ['shipped'], shipped: [],
  verified: ['notified'], notified: [], logged: [],
};

function OpsChip({ children, color, title, onClick, active }) {
  return (
    <span
      className={`ops-chip${onClick ? ' ops-chip-btn' : ''}${active ? ' ops-chip-active' : ''}`}
      style={color ? { background: `${color}22`, color, borderColor: `${color}55` } : undefined}
      title={title}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >{children}</span>
  );
}

// Launch line for the Claude Code daemon. In-flight Build 1 removed the manual
// "Copy launch + prompt" button (Run-in-CC is disabled until Build 2); this
// constant is RETAINED for Build 2, which wires the actual daemon launch.
// eslint-disable-next-line no-unused-vars
const OPS_LAUNCH_LINE = 'cd C:\\Users\\tim\\ClaudeHealth\\health-app ; claude --dangerously-skip-permissions';

function OpsCopyButton({ text, label = 'Copy Agent Prompt', doneLabel = '✓ Copied', title }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch (e) {
      // Clipboard API can be unavailable in WebViews — textarea fallback.
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { ok = document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
    if (ok) { setDone(true); setTimeout(() => setDone(false), 2200); }
  };
  return <button className="ops-btn ops-btn-primary" onClick={copy} title={title}>{done ? doneLabel : label}</button>;
}

function OpsSignalRow({ s, onImage }) {
  const srcColor = { error_event: '#dc2626', feedback: '#2563eb', ai_feedback: '#7c3aed',
                     paste: '#0891b2', screenshot: '#d97706', email: '#16a34a' }[s.source] || '#6b7280';
  return (
    <div className="ops-signal-row">
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <OpsChip color={srcColor}>{s.source}</OpsChip>
        {s.platform && <OpsChip>{s.platform}</OpsChip>}
        {s.user_id && <OpsChip title="user">{s.user_id}</OpsChip>}
        {s.reporter_handle && <OpsChip title="reporter">{s.reporter_handle}</OpsChip>}
        {!!s.muted && <OpsChip color="#6b7280">muted</OpsChip>}
        <span style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 'auto' }}>{formatDate(s.created_at)}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 96, overflow: 'hidden' }}>
        {s.raw_text || '(no text)'}
      </div>
      {/* B2 — ALL attachments for this signal (multi), each a thumbnail → full view.
          Falls back to the legacy single image_path for pre-B1 screenshot signals. */}
      {(() => {
        const atts = (s.attachments && s.attachments.length)
          ? s.attachments
          : (s.image_path ? [{ image_path: s.image_path, extraction_pending: false }] : []);
        if (!atts.length) return null;
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'flex-start' }}>
            {atts.map((a, i) => (
              <div key={a.id || i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <img className="ops-thumb" alt="attachment"
                     src={`${API_BASE}/admin/ops/uploads/${a.image_path}`}
                     onClick={() => onImage && onImage(a.image_path)} />
                {a.extraction_pending && <span style={{ fontSize: 9, color: '#b45309' }}>extract pending</span>}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// Full-view screenshot overlay (portal — never inline in a transformed ancestor).
function OpsImageOverlay({ path, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="ops-overlay-scrim" onClick={onClose}>
      <img src={`${API_BASE}/admin/ops/uploads/${path}`} alt="screenshot full view"
           style={{ maxWidth: '92vw', maxHeight: '86vh', borderRadius: 8 }}
           onClick={(e) => e.stopPropagation()} />
    </div>,
    document.body
  );
}

function OpsInboxTab({ onCounts }) {
  const [signals, setSignals] = useState(null);
  const [mutedSignals, setMutedSignals] = useState(null);
  const [showMuted, setShowMuted] = useState(false);
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('sync'); // 'sync' | 'synth' | null
  const [synthProgress, setSynthProgress] = useState(null);
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const [channel, setChannel] = useState('viber');
  const [reporter, setReporter] = useState('');
  const [staged, setStaged] = useState([]);      // B1 — files staged for ONE combined signal
  const [submitting, setSubmitting] = useState(false);
  const [ingestNote, setIngestNote] = useState(null);
  const [imageView, setImageView] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async (query) => {
    setBusy('sync'); setError(null);
    try {
      const j = await opsApi.get(`/signals?muted=0&limit=200${query ? `&q=${encodeURIComponent(query)}` : ''}`);
      setSignals(j.signals); setCounts(j.counts); onCounts && onCounts(j.counts);
      if (showMuted) {
        const m = await opsApi.get('/signals?muted=1&limit=100');
        setMutedSignals(m.signals);
      }
    } catch (e) { setError(e.message); }
    setBusy(null);
  }, [onCounts, showMuted]);

  useEffect(() => { load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const synthesize = async () => {
    setBusy('synth'); setError(null); setSynthProgress({ done: 0, remaining: '?' });
    try {
      let remaining = 1, done = 0, guard = 0;
      while (remaining > 0 && guard < 40) {
        guard += 1;
        const j = await opsApi.post('/synthesize');
        done += j.processed; remaining = j.remaining;
        setSynthProgress({ done, remaining });
        if (j.processed === 0) break;
      }
      await load(q);
    } catch (e) { setError(e.message); }
    setBusy(null); setSynthProgress(null);
  };

  // B1 — stage files locally (cap 10); they submit together with the explainer
  // as ONE signal (not one-per-file). Clipboard paste of images also stages.
  const stageFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setStaged(prev => {
      const add = files.slice(0, Math.max(0, 10 - prev.length)).map(f => ({
        file: f, name: f.name || 'image',
        previewUrl: (() => { try { return URL.createObjectURL(f); } catch (e) { return null; } })(),
      }));
      return [...prev, ...add];
    });
  };
  const unstage = (i) => setStaged(prev => {
    const r = prev[i]; if (r && r.previewUrl) { try { URL.revokeObjectURL(r.previewUrl); } catch (e) {} }
    return prev.filter((_, idx) => idx !== i);
  });
  const onIngestPaste = (e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    const imgs = [];
    for (const it of items) { if (it.type && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) imgs.push(f); } }
    if (imgs.length) stageFiles(imgs);   // text paste falls through to the textarea normally
  };
  const ingest = async () => {
    if (!text.trim() && !staged.length) { setError('Add an explainer and/or at least one file.'); return; }
    setError(null); setIngestNote(null); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('text', text); fd.append('channel', channel);
      if (reporter) fd.append('reporter_handle', reporter);
      staged.forEach(s => fd.append('files', s.file));
      const r = await fetch(`${API_BASE}/admin/ops/ingest`, { method: 'POST', credentials: 'include', body: fd });
      let j = null; try { j = await r.json(); } catch (e) {}
      if (!r.ok) { setError((j && (j.detail || j.error)) || `Ingest failed: HTTP ${r.status}`); setSubmitting(false); return; }
      const bits = [`signal #${j.signal && j.signal.id}`];
      if (j.attachments) bits.push(`${j.attachments} attachment${j.attachments === 1 ? '' : 's'}`);
      if (j.extraction_pending) bits.push(`${j.extraction_pending} extraction pending (Gemini)`);
      if (j.rejected && j.rejected.length) bits.push(`${j.rejected.length} rejected: ${j.rejected.join('; ')}`);
      setIngestNote(bits.join(' · '));
      staged.forEach(s => { if (s.previewUrl) { try { URL.revokeObjectURL(s.previewUrl); } catch (e) {} } });
      setText(''); setStaged([]);
      await load(q);
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  const unclustered = counts ? counts.unclustered : null;
  return (
    <div>
      <OpsErrorBanner error={error} onRetry={() => { setError(null); load(q); }} onDismiss={() => setError(null)} />
      <div className="ops-card">
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Ingest a report</div>
        {/* B1 — ONE signal = explainer text + N attached files, submitted together.
            Paste images straight from WhatsApp/email into the box; drop/tap to add more. */}
        <textarea
          className="ops-input" rows={3}
          placeholder="Explain the report, and/or paste a user message or image (Viber / WhatsApp / Messenger / email)…"
          value={text} onChange={(e) => setText(e.target.value)} onPaste={onIngestPaste}
        />
        <div
          className="ops-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); stageFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          <span>📎 Drop or tap to attach files (jpeg/png/webp, ≤10MB, up to 10) — they send with the text as one signal</span>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: 'none' }}
                 onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }} />
        </div>
        {staged.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {staged.map((s, i) => (
              <div key={i} style={{ position: 'relative' }}>
                {s.previewUrl ? <img src={s.previewUrl} alt={s.name} className="ops-thumb" /> : <span className="ops-chip">{s.name}</span>}
                <button type="button" className="ops-btn ops-btn-small" style={{ position: 'absolute', top: -6, right: -6, padding: '0 6px', lineHeight: 1.6 }}
                        onClick={() => unstage(i)} aria-label="Remove">×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="ops-input" style={{ width: 'auto' }} value={channel} onChange={(e) => setChannel(e.target.value)}>
            {['viber', 'whatsapp', 'messenger', 'email', 'other'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="ops-input" style={{ width: 140 }} placeholder="reporter (optional)"
                 value={reporter} onChange={(e) => setReporter(e.target.value)} />
          <button className="ops-btn ops-btn-primary" disabled={submitting} onClick={ingest}>
            {submitting ? 'Adding…' : `Add to Inbox${staged.length ? ` (${staged.length} file${staged.length === 1 ? '' : 's'})` : ''}`}
          </button>
        </div>
        {ingestNote && (
          <div className="ops-extract-preview">
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>✓ Ingested — {ingestNote}</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '14px 0 10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="ops-btn" disabled={busy === 'sync'} onClick={() => load(q)}>
          {busy === 'sync' ? 'Syncing…' : '⟳ Sync'}
        </button>
        <button className="ops-btn ops-btn-primary" disabled={!!busy || !unclustered} onClick={synthesize}>
          {busy === 'synth'
            ? `Synthesizing… ${synthProgress ? `${synthProgress.done} done, ${synthProgress.remaining} left` : ''}`
            : `Synthesize ${unclustered != null ? unclustered : '…'} unclustered`}
        </button>
        <input className="ops-input" style={{ flex: 1, minWidth: 140 }} placeholder="Search signals…"
               value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') load(q); }} />
      </div>

      {signals === null ? <OpsSkeleton rows={4} /> : (
        signals.length === 0
          ? <div className="ops-empty">No signals yet — Sync pulls from error logs + feedback, or paste a report above.</div>
          : signals.map(s => <OpsSignalRow key={s.id} s={s} onImage={setImageView} />)
      )}

      {counts && counts.muted > 0 && (
        <div style={{ marginTop: 10 }}>
          <button className="ops-btn ops-btn-small" onClick={async () => {
            const next = !showMuted; setShowMuted(next);
            if (next && !mutedSignals) {
              try { const m = await opsApi.get('/signals?muted=1&limit=100'); setMutedSignals(m.signals); }
              catch (e) { setError(e.message); }
            }
          }}>
            {showMuted ? '▾' : '▸'} {counts.muted.toLocaleString()} muted
          </button>
          {showMuted && (mutedSignals === null ? <OpsSkeleton rows={2} /> :
            mutedSignals.map(s => <OpsSignalRow key={s.id} s={s} onImage={setImageView} />))}
        </div>
      )}
      {imageView && <OpsImageOverlay path={imageView} onClose={() => setImageView(null)} />}
    </div>
  );
}

function OpsBundleCard({ c, onOpen, selectable, selected, onToggleSelect, draggable, onDragStartCard, onDragEndCard, dragging, onArchive }) {
  const sev = c.severity;
  const klass = opsCardClass(c);
  return (
    <div
      className={`ops-card ops-bundle-card${selected ? ' ops-selected' : ''}${dragging ? ' ops-dragging' : ''}${klass.autoShip ? ' ops-card-eligible' : ' ops-card-manual'}`}
      style={{ borderLeft: `4px solid ${klass.color}` }}
      onClick={(e) => { if (e.target.closest('.ops-sel-circle')) return; onOpen(c.id); }}
      role="button"
      draggable={!!draggable}
      onDragStart={draggable ? (e) => onDragStartCard(e, c) : undefined}
      onDragEnd={draggable ? onDragEndCard : undefined}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {selectable && (
          <button
            type="button"
            className={`ops-sel-circle${selected ? ' on' : ''}`}
            aria-pressed={selected}
            title={selected ? 'Selected — tap to deselect (shift-tap for range)' : 'Select (shift-tap for range)'}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(c.id, e.shiftKey); }}
          >{selected ? '✓' : ''}</button>
        )}
        <OpsChip color={klass.color} title={(c.daemon_eligible && c.daemon_eligible.reason) || klass.label}>{klass.label}{klass.autoShip ? '' : ' 👤'}</OpsChip>
        {sev && <OpsChip color={OPS_SEV_COLOR[sev]}>{sev}</OpsChip>}
        {!!c.is_regression && <OpsChip color="#dc2626">regression</OpsChip>}
        {c.category === 'horizon_feature' && <OpsChip color="#7c3aed">[Horizon]</OpsChip>}
        <span style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 'auto' }}>{c.status}</span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', margin: '6px 0 4px' }}>{c.friendly_title || c.title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', maxHeight: 54, overflow: 'hidden' }}>{c.friendly_summary || c.summary}</div>
      {(c.friendly_title || c.friendly_summary) && (c.title || c.summary) && (
        <details className="ops-card-tech" onClick={(e) => e.stopPropagation()}>
          <summary>technical detail</summary>
          <div className="ops-card-tech-body">{c.title}{c.summary ? ` — ${c.summary}` : ''}</div>
        </details>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 11, color: 'var(--text-dim)', flexWrap: 'wrap' }}>
        <span>{c.live_signal_count ?? c.signal_count} signals · {c.affected_user_count} users</span>
        {c.confidence != null && <span>conf {Math.round(c.confidence * 100)}%</span>}
        {c.recommended_decision && !c.decision && (
          <OpsChip color="#0891b2" title="AI recommendation">→ {c.recommended_decision}</OpsChip>
        )}
        {c.decision && <OpsChip>{c.decision}</OpsChip>}
      </div>
      {selectable && onArchive && (
        <button type="button" className="ops-card-bin" title="Archive (restorable via Archived toggle)"
                aria-label="Archive bundle" draggable={false}
                onClick={(e) => { e.stopPropagation(); onArchive(c.id); }}
                onMouseDown={(e) => e.stopPropagation()}>🗑</button>
      )}
    </div>
  );
}

// Portaled sticky bulk-action bar (Phase 2). Shows when ≥1 card selected.
function OpsBulkBar({ selectedIds, statusOf, onAction, onClear, busy }) {
  const ids = [...selectedIds];
  const legalCount = (action) => ids.filter(id => opsBulkLegal(action, statusOf(id))).length;
  const actions = [
    { key: 'scheduled', label: 'Schedule', kind: 'triage' },
    { key: 'immediate', label: 'Immediate', kind: 'triage' },
    { key: 'log_only', label: 'Log', kind: 'triage' },
    { key: 'fixing', label: 'Mark In-flight', kind: 'status' },
    { key: 'archive', label: '🗑 Archive', kind: 'archive' },
  ];
  return ReactDOM.createPortal(
    <div className="ops-bulk-bar">
      <span className="ops-bulk-count">{ids.length} selected</span>
      {busy && <span className="ops-bulk-progress">{busy.label}… {busy.done}/{busy.total}</span>}
      <div className="ops-bulk-actions">
        {actions.map(a => {
          const n = legalCount(a.key);
          return (
            <button
              key={a.key}
              className="ops-btn ops-btn-small"
              disabled={!!busy || n === 0}
              title={n === 0
                ? `No selected bundle can move to “${a.label}” (illegal from their current status)`
                : (n < ids.length ? `${n} of ${ids.length} eligible — the rest are skipped` : `Apply to ${n}`)}
              onClick={() => onAction(a)}
            >{a.label}{n > 0 && n < ids.length ? ` (${n})` : ''}</button>
          );
        })}
        <button className="ops-btn ops-btn-small" disabled={!!busy} onClick={onClear}>Clear</button>
      </div>
    </div>,
    document.body
  );
}

function OpsStatusPipeline({ cluster, onAdvance }) {
  const cur = cluster.status;
  if (cur === 'logged') return <div className="ops-empty">Logged (no fix pipeline).</div>;
  const curIdx = OPS_PIPELINE.indexOf(cur);
  const legal = OPS_NEXT_LEGAL[cur] || [];
  return (
    <div className="ops-pipeline">
      {OPS_PIPELINE.map((st, i) => {
        const isCur = st === cur;
        const done = i < curIdx;
        const clickable = legal.includes(st);
        return (
          <div
            key={st}
            className={`ops-pipe-step${isCur ? ' cur' : ''}${done ? ' done' : ''}${clickable ? ' next' : ''}`}
            title={st === 'verified' && !clickable ? 'Reached via Run smoke (pass) only' : (clickable ? `Advance to ${st}` : st)}
            onClick={clickable ? () => onAdvance(st) : undefined}
          >
            {st}
          </div>
        );
      })}
    </div>
  );
}

// ── In-flight prepare (Build 1) ─────────────────────────────────────────────
// PREPARE + DISPLAY ONLY. "Run in CC" is DISABLED here — the daemon that actually
// runs it is Build 2. This surface merges/checks the selection read-only and shows
// the operator what would run. Reused by the Prepare modal (multi-card) and the
// single-card drawer.
const OPS_RUN_DISABLED_TIP = 'Enabled in Build 2 (daemon)';
const OPS_PLATFORM_COLOR = { web: '#2563eb', backend: '#0891b2', native: '#dc2626' };

// P2 — colour-code bundles by daemon class. The daemon Fix-RUNS every class now;
// the split is the SHIP path: web/backend = 🤖 auto-ship (armed auto-deploy),
// native/auth/schema = 👤 human-ship (a person deploys the committed change).
const OPS_CLASS_STYLE = {
  web:     { color: '#2563eb', label: 'web',    autoShip: true },
  backend: { color: '#0891b2', label: 'backend', autoShip: true },
  native:  { color: '#dc2626', label: 'native',  autoShip: false },
  auth:    { color: '#b45309', label: 'auth',    autoShip: false },
  schema:  { color: '#7c3aed', label: 'schema',  autoShip: false },
};
function opsCardClass(c) {
  const k = (c && c.daemon_eligible && c.daemon_eligible.klass) || (c && c.platform_class) || 'web';
  const base = OPS_CLASS_STYLE[k] || OPS_CLASS_STYLE.web;
  return { ...base, shipMode: base.autoShip ? 'auto-ship' : 'human-ship' };
}
function OpsClassLegend() {
  return (
    <div className="ops-class-legend">
      {Object.entries(OPS_CLASS_STYLE).map(([k, s]) => (
        <span key={k} className="ops-legend-item">
          <span className="ops-legend-swatch" style={{ background: s.color }} />{s.label}{s.autoShip ? '' : ' 👤'}
        </span>
      ))}
      <span className="ops-legend-note">daemon Fixes every class · web + backend = 🤖 auto-ship · native/auth/schema = 👤 human-ship</span>
    </div>
  );
}

// ── Build 2 · PHASE 3 — armed deploy behind a green-smoke gate + auto-rollback ─
// The browser NEVER talks to the local daemon (P1's loopback was blocked by Edge
// Local Network Access). It POSTs a SAME-ORIGIN run-request; the LOCAL daemon
// polls the VM outbound, runs claude -p, and posts steps back. Superadmin +
// single-card drawer only. Daemon runs WEB/BACKEND only (native/schema/auth
// refused server-side + here).
//   no-op — read-only.   real — edit + commit + LOCAL build + smoke, NO deploy.
//   ship  — ARMED deploy: needs armed=true (Tim, by hand) AND a smoke_ok job AND
//           the Ship click. Post-deploy guardrails green → shipped; any red →
//           AUTO-ROLLBACK. Disarmed → the Ship button is disabled.
const OPS_JOB_TERMINAL = ['smoke_ok', 'smoke_fail', 'shipped', 'rolled_back', 'error'];

function OpsRunSpike({ clusterId, canRun, blocked, eligible }) {
  const [mode, setMode] = useState('real');
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);       // {id, status, steps, result, mode, ...}
  const [armed, setArmed] = useState(false);  // daemon-reported armed+fresh
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(false);    // daemon-probably-not-running hint
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Poll the daemon's armed heartbeat (for the Ship button) while the drawer is open.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const r = await opsApi.get('/daemon-status'); if (alive) setArmed(!!r.armed); }
      catch (e) { if (alive) setArmed(false); }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const isEligible = !eligible || eligible.eligible;   // absent → treat as eligible (noop-safe)
  const runnable = canRun && !blocked && (mode === 'noop' || isEligible);

  const launch = async (runMode) => {
    setError(null); setHint(false); setJob(null);
    setRunning(true);
    let created;
    try {
      const r = await opsApi.post('/run-request', { bundle_id: clusterId, mode: runMode });
      created = r.job;
      setJob(created);
    } catch (e) {
      setRunning(false); setError(e.message);
      return;
    }
    let ticks = 0, sawProgress = false;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const r = await opsApi.get(`/run-jobs/${created.id}`);
        const j = r.job;
        setJob(j);
        if (j.status && j.status !== 'pending') sawProgress = true;
        if (OPS_JOB_TERMINAL.includes(j.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          setRunning(false);
        }
      } catch (e) {
        clearInterval(pollRef.current); pollRef.current = null;
        setRunning(false); setError(e.message);
      }
      if (!sawProgress && ticks === 14) setHint(true);   // stuck pending ~20s
      if (ticks > 400) {   // ~10 min ceiling (ship builds + deploys + guardrails)
        clearInterval(pollRef.current); pollRef.current = null;
        setRunning(false);
        setError('Timed out waiting for the daemon to process this job.');
      }
    }, 1500);
  };

  const steps = (job && job.steps) || [];
  const terminal = !!(job && OPS_JOB_TERMINAL.includes(job.status));
  const ok = !!(job && (job.status === 'smoke_ok' || job.status === 'shipped'));
  const smokeOk = !!(job && job.status === 'smoke_ok' && job.mode === 'real');
  const commit = job && job.result && job.result.commit;

  return (
    <div className="ops-prepare-run ops-spike-wrap">
      <div className="ops-spike-row">
        <select className="ops-input ops-spike-mode" value={mode}
                disabled={running || !canRun}
                onChange={(e) => setMode(e.target.value)} aria-label="Run mode">
          <option value="real">real (edit + commit + local build)</option>
          <option value="noop">no-op (read-only)</option>
        </select>
        <button
          className="ops-btn ops-btn-primary"
          disabled={!runnable || running}
          title={!canRun ? 'Superadmin only'
                 : blocked ? 'Resolve checker blocks first'
                 : (!isEligible && mode === 'real') ? `Not daemon-eligible: ${eligible && eligible.reason}`
                 : (mode === 'real' ? 'CC edits, commits, and locally builds — NO deploy'
                                    : 'Read-only no-op via the local daemon')}
          onClick={() => launch(mode)}
        >{running ? '▶ Running…' : (mode === 'real' ? '▶ Run in CC' : '▶ Run in CC (no-op)')}</button>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
          {!canRun ? 'Superadmin only'
            : (!isEligible ? `⛔ ${eligible.klass} — stays manual` : 'same-origin job queue')}
          {blocked ? ' · blocked by checker' : ''}
          {job ? ` · job #${job.id} ${job.status}` : ''}
          {` · daemon ${armed ? '🟢 armed' : '⚪ disarmed'}`}
        </span>
      </div>
      {canRun && !isEligible && (
        <div className="ops-spike-hint">⛔ {eligible.reason} — run/ship refused; keep this one manual.</div>
      )}
      <OpsErrorBanner error={error} onDismiss={() => { setError(null); setHint(false); }} />
      {hint && (
        <div className="ops-spike-hint">
          Job still pending — is the local daemon running? Launch it:{' '}
          <code>python C:\Users\tim\ClaudeHealth\ops-daemon\daemon.py</code>
        </div>
      )}
      {(steps.length > 0 || terminal) && (
        <div className="ops-spike-steps">
          {steps.map((s, i) => (
            <div key={i} className="ops-spike-step">
              <span className={`ops-spike-status s-${(s.status || '').replace(/[^a-z]/gi, '')}`}>{s.status}</span>
              <span className="ops-spike-name">{s.step}</span>
              {s.detail ? <span className="ops-spike-detail">{s.detail}</span> : null}
            </div>
          ))}
          {terminal && (
            <div className={`ops-spike-final ${ok ? 'ok' : 'err'}`}>
              {ok
                ? <>✓ {job.status}{commit ? ` · commit ${String(commit).slice(0, 10)}` : ''} · <span className="ops-spike-result">{job.result && (job.result.result_text || job.result.smoke)}</span></>
                : <>✕ {job.status} · {(job.result && (job.result.error || job.result.result_text)) || 'see daemon.log'}</>}
            </div>
          )}
        </div>
      )}
      {smokeOk && (
        <div className="ops-spike-row" style={{ marginTop: 6 }}>
          <button className={`ops-btn ${armed ? 'ops-btn-primary' : ''}`}
                  disabled={!armed || running}
                  title={armed ? 'ARMED — deploys to NPL behind a green-smoke gate with auto-rollback'
                               : 'Deploy disarmed — set armed=true in the daemon (daemon.config.json) to enable'}
                  onClick={() => launch('ship')}>
            {armed ? '⇧ Ship to NPL (armed)' : '⇧ Ship (deploy disarmed)'}
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {armed ? 'Local smoke green · armed → deploy + guardrails + auto-rollback'
                   : 'Local smoke green · deploy disarmed (arm the daemon to enable)'}
          </span>
        </div>
      )}
    </div>
  );
}

function OpsCheckerBanner({ checker, onQuickAdd }) {
  if (!checker) return null;
  const { blocks = [], warnings = [], suggestions = [] } = checker;
  if (!blocks.length && !warnings.length && !suggestions.length) return null;
  return (
    <div className="ops-checker">
      {blocks.map((b, i) => <div key={`b${i}`} className="ops-check ops-check-block">⛔ {b.message}</div>)}
      {warnings.map((w, i) => <div key={`w${i}`} className="ops-check ops-check-warn">⚠ {w.message}</div>)}
      {suggestions.map((s, i) => (
        <div key={`s${i}`} className="ops-check ops-check-suggest">
          <span style={{ flex: 1, minWidth: 0 }}>💡 {s.message}</span>
          {onQuickAdd && s.cluster_id != null && (
            <button className="ops-btn ops-btn-small" onClick={() => onQuickAdd(s.cluster_id)}>+ Add</button>
          )}
        </div>
      ))}
    </div>
  );
}

// clusterIds is passed by the parent; we key all refetch on a stable sorted id
// string so a fresh array literal each render doesn't loop. reloadToken lets a
// parent (e.g. the drawer after a respec) force a re-merge.
function OpsPreparePanel({ clusterIds, onQuickAdd, reloadToken, runSpike }) {
  const idsKey = clusterIds.slice().sort((a, b) => a - b).join(',');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('load'); // 'load' | 'regen' | null
  const [edited, setEdited] = useState('');
  const isMobile = useOpsMobile();

  const prepare = useCallback(async (intent) => {
    setBusy(intent != null ? 'regen' : 'load'); setError(null);
    try {
      const ids = idsKey.split(',').filter(Boolean).map(Number);
      const j = await opsApi.post('/prepare', { cluster_ids: ids, ...(intent != null ? { intent } : {}) });
      setData(j);
      setEdited((j.change_list || []).map(c => `${c.n}. ${c.text}`).join('\n'));
    } catch (e) { setError(e.message); }
    setBusy(null);
  }, [idsKey]);

  useEffect(() => { prepare(); }, [prepare, reloadToken]); // fresh merge when the id set (or token) changes

  const blocked = !!(data && data.checker && data.checker.blocks && data.checker.blocks.length);

  return (
    <div className="ops-prepare">
      <OpsErrorBanner error={error} onRetry={() => prepare()} onDismiss={() => setError(null)} />
      {data && <OpsCheckerBanner checker={data.checker} onQuickAdd={onQuickAdd} />}
      {(busy === 'load' || !data) ? <OpsSkeleton rows={5} /> : (
        <>
          <div className={`ops-drawer-cols${isMobile ? ' stacked' : ''}`}>
            <div className="ops-drawer-col">
              <div className="ops-section-title">What changes</div>
              <textarea
                className="ops-input ops-prepare-changes"
                rows={Math.min(16, Math.max(4, (data.change_list || []).length + 1))}
                value={edited} onChange={(e) => setEdited(e.target.value)}
                aria-label="Editable change list" />
              <button className="ops-btn" disabled={busy === 'regen'} onClick={() => prepare(edited)}>
                {busy === 'regen' ? 'Regenerating…' : '↻ Regenerate'}
              </button>
              <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 6 }}>
                Edit the intent above, then Regenerate to re-merge the prompt to match it.
              </div>
            </div>
            <div className="ops-drawer-col">
              <div className="ops-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Combined prompt
                {data.unmerged && <OpsChip color="#d97706" title="Gemini was unavailable — specs concatenated, review manually">unmerged</OpsChip>}
              </div>
              <pre className="ops-spec-pre ops-prepare-prompt">{data.combined_prompt}</pre>
            </div>
          </div>
          {runSpike ? (
            <OpsRunSpike clusterId={runSpike.clusterId} canRun={runSpike.canRun} blocked={blocked} eligible={runSpike.eligible} />
          ) : (
            <div className="ops-prepare-run">
              <button className="ops-btn ops-btn-primary" disabled title={OPS_RUN_DISABLED_TIP}>▶ Run in CC</button>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {OPS_RUN_DISABLED_TIP}{blocked ? ' · blocked by checker' : ''}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OpsCollapsible({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ops-collapsible">
      <button type="button" className="ops-collapsible-head" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span className="ops-caret">{open ? '▾' : '▸'}</span>{title}
      </button>
      {open && <div className="ops-collapsible-body">{children}</div>}
    </div>
  );
}

// Multi-card Prepare modal (Triaged column "Prepare in-flight"). Owns the id set
// so suggestion quick-add can grow it and the panel re-merges.
function OpsPrepareModal({ initialIds, onClose }) {
  const [ids, setIds] = useState(() => [...new Set(initialIds)]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const quickAdd = (cid) => setIds(prev => prev.includes(cid) ? prev : [...prev, cid]);
  return ReactDOM.createPortal(
    <div className="ops-overlay-scrim" onClick={onClose}>
      <div className="ops-drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ops-drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Prepare in-flight</div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{ids.length} bundle{ids.length === 1 ? '' : 's'} · merged read-only (nothing runs in Build 1)</div>
          </div>
          <button className="ops-btn ops-btn-small" onClick={onClose}>✕ Close</button>
        </div>
        <OpsPreparePanel clusterIds={ids} onQuickAdd={quickAdd} />
      </div>
    </div>,
    document.body
  );
}

// ── Combined-run box — multi-drop onto In-flight opens ONE of these ─────────
// ORDERED, reorderable bundle list + ONE merged prompt + ONE "Run in CC" for the
// whole set (a single CC invocation — never N separate boxes). Reordering
// re-merges the prompt in the operator's order. The Immediate/Scheduled toggle
// lives INSIDE the box (batch tag only — everything here is already in-flight).
function OpsCombinedRunModal({ initialIds, clusters, isSuperadmin, onClose, onChanged }) {
  const [order, setOrder] = useState(() => [...initialIds]);
  const [decision, setDecision] = useState('immediate'); // drop path triaged as immediate
  const byId = useCallback((id) => (clusters || []).find(c => c.id === id) || { id }, [clusters]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const move = (i, d) => setOrder(o => {
    const j = i + d;
    if (j < 0 || j >= o.length) return o;
    const n = [...o]; [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const setDec = async (d) => {
    setDecision(d);
    // Batch-tag every bundle in the set; decision_only NEVER touches status.
    for (const id of order) {
      try { await opsApi.post(`/clusters/${id}/triage`, { decision: d, decision_only: true }); }
      catch (e) { console.log('[ops-combined] decision retag failed #%s: %s', id, e.message); }
    }
    onChanged && onChanged();
  };
  return ReactDOM.createPortal(
    <div className="ops-overlay-scrim" onClick={onClose}>
      <div className="ops-drawer-panel ops-drawer-runviewer" onClick={(e) => e.stopPropagation()}>
        <div className="ops-drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Combined run — {order.length} bundles
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              one merged prompt · one CC invocation · reorder with ↑↓ (the prompt follows your order)
            </div>
          </div>
          <button className="ops-btn ops-btn-small" onClick={onClose}>✕ Close</button>
        </div>
        <div className="ops-combined-list">
          {order.map((id, i) => {
            const c = byId(id);
            const klass = (c.daemon_eligible && c.daemon_eligible.klass) || c.platform_class || 'web';
            return (
              <div key={id} className="ops-combined-row">
                <span className="ops-combined-n">{i + 1}.</span>
                <span className="ops-combined-title" title={c.title}>#{id} {c.friendly_title || c.title || ''}</span>
                <OpsChip color={OPS_PLATFORM_COLOR[klass] || OPS_PLATFORM_COLOR.web}>{klass}</OpsChip>
                {c.severity && <OpsChip color={OPS_SEV_COLOR[c.severity]}>{c.severity}</OpsChip>}
                <span className="ops-combined-updown">
                  <button className="ops-btn ops-btn-small" disabled={i === 0} title="Run earlier" onClick={() => move(i, -1)}>↑</button>
                  <button className="ops-btn ops-btn-small" disabled={i === order.length - 1} title="Run later" onClick={() => move(i, 1)}>↓</button>
                </span>
              </div>
            );
          })}
        </div>
        <OpsRunViewer clusterIds={order} canRun={!!isSuperadmin} eligible={null} allowShip={false}
                      decision={decision} onDecisionChange={setDec}
                      onTerminal={() => { onChanged && onChanged(); }}
                      onLaunched={() => { onChanged && onChanged(); onClose(); }}
                      onScheduled={() => { onChanged && onChanged(); onClose(); }} />
      </div>
    </div>,
    document.body
  );
}

// ── FINAL-WF P5 — background job chips + reconnectable live-job modal ─────────
// Runs are fire-and-forget: the gate box collapses on launch and these chips are
// the ONLY client handle — they re-derive from GET /run-jobs/active (server state),
// so they survive navigation, refresh, and tab-close. Click a chip to re-open the
// live stream; execution stays strictly serial on the daemon regardless of chips.
function OpsJobChips({ onOpen, reloadToken }) {
  const [jobs, setJobs] = useState({ live: [], recent: [] });
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const r = await opsApi.get('/run-jobs/active'); if (alive) setJobs({ live: r.live || [], recent: r.recent || [] }); }
      catch (e) { /* transient — keep last state */ }
    };
    tick(); const iv = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [reloadToken]);
  const all = [...jobs.live, ...jobs.recent.slice(0, 2)];
  if (!all.length) return null;
  const meta = (j) => {
    try { return JSON.parse(j.result_json || '{}'); } catch (e) { return j.result || {}; }
  };
  return ReactDOM.createPortal(
    <div className="ops-chipbar" role="status">
      {all.map(j => {
        const m = (typeof j.result === 'object' && j.result) || meta(j) || {};
        const label = m.batch_name ? `${m.batch_name}` : `#${(m.bundle_ids || [j.bundle_id]).join(',#')}`;
        const liveish = ['pending', 'claimed', 'running', 'input_required'].includes(j.status);
        return (
          <button key={j.id} className={`ops-jobchip s-${j.status}${liveish ? '' : ' done'}`}
                  title={`job #${j.id} · ${j.mode} · ${j.status}${j.queue_position ? ` · queued behind ${j.queue_position - 1 || 'the running job'}` : ''} — click for the live stream`}
                  onClick={() => onOpen(j.id)}>
            {j.status === 'running' || j.status === 'claimed' ? '▶' : j.status === 'pending' ? '⏳' : j.status === 'input_required' ? '⚠' : (j.status === 'smoke_ok' || j.status === 'shipped') ? '✓' : '✕'}
            {' '}job #{j.id} · {j.mode === 'noop' ? 'diagnose' : j.mode} · {label}
            {j.status === 'pending' && j.queue_position > 1 ? ` · queued #${j.queue_position}` : ''}
            {liveish && (j.status === 'running' || j.status === 'claimed') ? ' · streaming' : ''}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

function OpsJobModal({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [ccView, setCcView] = useState('raw');
  const [error, setError] = useState(null);
  const [inputText, setInputText] = useState('');
  const ccRef = useRef(null);
  const terminal = !!(job && OPS_JOB_TERMINAL.includes(job.status));
  useEffect(() => {                       // reconnect to live-or-finished job
    let alive = true;
    const tick = async () => {
      try { const r = await opsApi.get(`/run-jobs/${jobId}`); if (alive) setJob(r.job); }
      catch (e) { if (alive) setError(e.message); }
    };
    tick();
    const iv = setInterval(() => { if (!terminal) tick(); }, 1500);
    return () => { alive = false; clearInterval(iv); };   // poller STOPS on unmount
  }, [jobId, terminal]);
  useEffect(() => { if (ccRef.current) ccRef.current.scrollTop = ccRef.current.scrollHeight; }, [job]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const resume = async (withInput) => {
    setError(null);
    try {
      await opsApi.post(`/run-jobs/${jobId}/resume`, withInput ? { input: inputText } : {});
      setInputText(''); onClose();      // re-invoked as a NEW job → its chip appears
    } catch (e) { setError(e.message); }
  };
  const raw = (job && job.raw) || [];
  const transcript = (job && job.transcript) || [];
  const ok = !!(job && (job.status === 'smoke_ok' || job.status === 'shipped'));
  return ReactDOM.createPortal(
    <div className="ops-overlay-scrim" onClick={onClose}>
      <div className="ops-drawer-panel ops-drawer-runviewer" onClick={(e) => e.stopPropagation()}>
        <div className="ops-drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Job #{jobId}{job ? ` · ${job.mode === 'noop' ? 'diagnose' : job.mode} · ${job.status}` : ''}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>live stream — close anytime; the run continues server-side</div>
          </div>
          <button className="ops-btn ops-btn-small" onClick={onClose}>✕ Close</button>
        </div>
        <OpsErrorBanner error={error} onDismiss={() => setError(null)} />
        {job && job.status === 'input_required' && (
          <div className="ops-input-required" role="alert">
            <div className="ops-input-required-head">⚠ INPUT REQUIRED — {(job.result && job.result.detail) || 'the run is blocked awaiting input'}</div>
            <div className="ops-input-required-row">
              <input className="ops-input" placeholder="Optional follow-up input for CC…"
                     value={inputText} onChange={(e) => setInputText(e.target.value)} />
              <button className="ops-btn ops-btn-primary" disabled={!inputText.trim()} onClick={() => resume(true)}>Send</button>
              <button className="ops-btn" onClick={() => resume(false)}>Resume</button>
            </div>
          </div>
        )}
        <div className="ops-cc-viewtoggle">
          <button className={`ops-cc-vopt${ccView === 'raw' ? ' on' : ''}`} onClick={() => setCcView('raw')}>Raw</button>
          <button className={`ops-cc-vopt${ccView === 'tidied' ? ' on' : ''}`} onClick={() => setCcView('tidied')}>Tidied</button>
        </div>
        <div className="ops-cc-log ops-jobmodal-log" ref={ccRef}>
          {ccView === 'raw' ? (
            raw.length ? <pre className="ops-cc-raw">{raw.join('\n')}</pre>
              : <div className="ops-empty">{terminal ? 'No raw output recorded.' : 'Waiting for Claude Code output…'}</div>
          ) : (
            transcript.length === 0
              ? <div className="ops-empty">{terminal ? 'No transcript recorded.' : 'Waiting for Claude Code output…'}</div>
              : transcript.map((t, i) => (
                  <div key={i} className={`ops-cc-entry cc-${t.kind}`}>
                    <span className="ops-cc-kind">{t.kind === 'tool_use' ? `⛭ ${t.name}` : t.kind === 'tool_result' ? (t.is_error ? '✕ result' : '↳ result') : t.kind}</span>
                    <span className="ops-cc-text">{t.text}</span>
                  </div>
                ))
          )}
        </div>
        {terminal && (
          <div className={`ops-spike-final ${ok ? 'ok' : 'err'}`}>
            {ok ? <>✓ {job.status}</> : <>✕ {job.status} · {(job.result && (job.result.error || job.result.result_text)) || 'see daemon.log'}</>}
          </div>
        )}
        {/* P1 STANDING AUTO-PUBLISH — native runs now publish DIRECT: the download
            button serves the new APK immediately, and the iOS build is submitted
            to TestFlight by the daemon. Apple's in-TF release steps (export
            compliance / tester groups) remain manual — stated, never claimed. */}
        {terminal && ok && job.result && job.result.apk_url && (
          <div className="ops-humanship hs-native">
            <div className="ops-humanship-head">📱 Native build AUTO-PUBLISHED</div>
            <div className="ops-humanship-body">
              <div>Android: <a href={job.result.apk_url} target="_blank" rel="noreferrer">download button is LIVE with this build</a>
                {job.result.apk_versioned_url ? <> · <a href={job.result.apk_versioned_url} target="_blank" rel="noreferrer">versioned copy</a></> : null}
                {job.result.commit ? <> · commit <code>{String(job.result.commit).slice(0, 10)}</code></> : null}
              </div>
              {job.result.tf_status && (
                <div style={{ marginTop: 4 }}>iOS: build submitted to TestFlight ({job.result.tf_status}) —
                  finish export-compliance / tester-group steps inside TestFlight.</div>
              )}
              {/* Back-compat: older jobs carry staged commands instead */}
              {!job.result.tf_status && job.result.staged_tf_submit && (
                <div className="ops-humanship-hint" style={{ marginTop: 6 }}>
                  TestFlight (staged on this older job):
                  <pre className="ops-staged-cmd">{job.result.staged_tf_submit}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Build 3 — run viewer (tabbed live CC transcript + pinned stepper + run bar) ─
// The star surface of the single-card drawer. Owns the prepare merge AND the run
// state (mode/job/armed/poll), and renders: What-changes (auto-collapse on run),
// a large tabbed viewer (Combined prompt | live Claude Code transcript), a
// panel-width pinned pipeline stepper, and a sticky run bar. On input_required it
// shows a flashing banner + input/Send/Resume (P4).
function opsStepperNodes(job) {
  if (!job) return [];
  const steps = job.steps || [];
  const stat = (name) => {
    const s = [...steps].reverse().find(x => (x.step || '').toLowerCase() === name.toLowerCase());
    return s ? s.status : null;
  };
  const terminal = OPS_JOB_TERMINAL.includes(job.status);
  const ccRun = (job.status === 'error') ? 'error'
    : (job.status === 'input_required') ? 'input_required'
    : terminal ? 'ok' : 'running';
  let names;
  if (job.mode === 'ship') names = ['ship guards', 'pre-ship advisory', 'local build', 'snapshot', 'deploy', 'restart', 'guardrails'];
  else if (job.mode === 'real') names = ['CC run', 'local smoke'];
  else names = ['CC run'];
  const nodes = names.map(n => {
    if (n === 'CC run') return { name: n, status: ccRun };
    return { name: n, status: stat(n) || 'pending' };
  });
  if (stat('ROLLBACK')) nodes.push({ name: 'ROLLBACK', status: stat('ROLLBACK') });
  if (job.status === 'input_required') nodes.push({ name: 'input required', status: 'input_required' });
  return nodes;
}

function OpsRunViewer({ clusterId, clusterIds, canRun, eligible, reloadToken, onRunningChange,
                        allowShip = true, decision, onDecisionChange, onTerminal,
                        onLaunched, onScheduled }) {
  // Single-bundle (clusterId, the drawer) OR combined-run (clusterIds, ORDERED —
  // the multi-drop box). One merged prompt + ONE CC invocation either way.
  const ids = (clusterIds && clusterIds.length ? clusterIds : [clusterId]).map(Number);
  const idsKey = ids.join(',');
  const isCombined = ids.length > 1;
  const [data, setData] = useState(null);          // {combined_prompt, change_list, checker}
  const [prepBusy, setPrepBusy] = useState('load');
  const [prepErr, setPrepErr] = useState(null);
  const [edited, setEdited] = useState('');
  const [origEdited, setOrigEdited] = useState('');   // P4 — for contextual Regenerate
  const [mode, setMode] = useState('noop');           // P4 — default Diagnose only (read-only, safer); do NOT auto-pick from the prompt
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);
  const [armed, setArmed] = useState(false);
  const [tab, setTab] = useState('cc');            // 'prompt' | 'cc'
  const [ccView, setCcView] = useState('raw');     // P6 — 'raw' (default) | 'tidied'
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(false);
  const [inputText, setInputText] = useState('');
  const [whatOpen, setWhatOpen] = useState(true);
  const pollRef = useRef(null);
  const ccRef = useRef(null);

  const prepare = useCallback(async (intent) => {
    setPrepBusy(intent != null ? 'regen' : 'load'); setPrepErr(null);
    try {
      // Combined mode: pass the ORDERED id list + ordered:true so the merge keeps
      // the operator's sequence (reordering the list re-runs this → new prompt).
      const j = await opsApi.post('/prepare', {
        cluster_ids: idsKey.split(',').map(Number),
        ...(idsKey.includes(',') ? { ordered: true } : {}),
        ...(intent != null ? { intent } : {}),
      });
      setData(j);
      const cltext = (j.change_list || []).map(c => `${c.n}. ${c.text}`).join('\n');
      setEdited(cltext); setOrigEdited(cltext);
    } catch (e) { setPrepErr(e.message); }
    setPrepBusy(null);
  }, [idsKey]);
  useEffect(() => { prepare(); }, [prepare, reloadToken]);

  const [daemonFresh, setDaemonFresh] = useState(true); // heartbeat seen ≤20s ago
  useEffect(() => {   // armed + liveness heartbeat (Ship button + offline banner)
    let alive = true;
    const tick = async () => {
      try {
        const r = await opsApi.get('/daemon-status');
        if (alive) { setArmed(!!r.armed); setDaemonFresh(!!r.fresh); }
      }
      catch (e) { if (alive) { setArmed(false); setDaemonFresh(false); } }
    };
    tick(); const iv = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { onRunningChange && onRunningChange(running); }, [running, onRunningChange]);
  useEffect(() => { if (running) setWhatOpen(false); }, [running]);
  // auto-scroll the CC transcript to newest
  useEffect(() => {
    if (tab === 'cc' && ccRef.current) ccRef.current.scrollTop = ccRef.current.scrollHeight;
  }, [job, tab]);

  const blocked = !!(data && data.checker && data.checker.blocks && data.checker.blocks.length);
  // P2: Fix runs on ALL classes; only armed AUTO-ship is web/backend. native/auth/
  // schema = human-ship (a person deploys the committed change after review).
  const shipEligible = !eligible || !!eligible.ship_eligible;
  const humanShip = !!eligible && eligible.ship_eligible === false;
  const shipMode = (eligible && eligible.ship_mode) || 'auto-ship';
  const runnable = canRun && !blocked;   // any class is Fix-runnable

  const poll = (jobId) => {
    let ticks = 0, sawProgress = false;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const r = await opsApi.get(`/run-jobs/${jobId}`);
        const j = r.job; setJob(j);
        if (j.status && j.status !== 'pending') sawProgress = true;
        if (OPS_JOB_TERMINAL.includes(j.status)) {
          clearInterval(pollRef.current); pollRef.current = null; setRunning(false);
          // Loop-close hook: the owner (drawer/board) refreshes so a terminal
          // status (smoke_ok/shipped) is reflected on the card immediately.
          onTerminal && onTerminal(j);
        }
      } catch (e) {
        clearInterval(pollRef.current); pollRef.current = null; setRunning(false); setError(e.message);
      }
      if (!sawProgress && ticks === 14) setHint(true);
      if (ticks > 500) { clearInterval(pollRef.current); pollRef.current = null; setRunning(false); setError('Timed out waiting for the daemon.'); }
    }, 1500);
  };

  const launch = async (runMode) => {
    setError(null); setHint(false); setJob(null); setRunning(true); setTab('cc');
    try {
      // Ordered bundle_ids — a combined box runs all N as ONE CC invocation.
      const r = await opsApi.post('/run-request', { bundle_ids: idsKey.split(',').map(Number), mode: runMode });
      // FINAL-WF P5 — fire-and-forget: with an onLaunched owner the box collapses to
      // a background chip; the chip modal owns the live stream (reconnect-safe).
      if (onLaunched) { setRunning(false); onLaunched(r.job); return; }
      setJob(r.job); poll(r.job.id);
      // LOUD, immediate: no daemon heartbeat → the job will sit pending. Say so
      // now instead of a silent "Waiting for Claude Code output…" hang.
      if (!daemonFresh) setHint(true);
    } catch (e) { setRunning(false); setError(e.message); }
  };

  // FINAL-WF P2 — Scheduled path: the gate's primary action becomes Add-to-batch.
  const [schedBatches, setSchedBatches] = useState([]);
  const [batchSel, setBatchSel] = useState('new');       // 'new' | batch id
  const [batchName, setBatchName] = useState('');
  const [batchTime, setBatchTime] = useState('');        // NPT datetime-local value
  const [schedBusy, setSchedBusy] = useState(false);
  useEffect(() => {
    if (decision !== 'scheduled') return;
    let alive = true;
    opsApi.get('/batches').then(r => {
      if (alive) setSchedBatches((r.batches || []).filter(b => b.status === 'scheduled'));
    }).catch(() => {});
    return () => { alive = false; };
  }, [decision]);
  const addToBatch = async () => {
    setError(null); setSchedBusy(true);
    const ids = idsKey.split(',').map(Number);
    try {
      let batch;
      if (batchSel === 'new') {
        const utc = nptLocalToUtc(batchTime);
        if (!batchName.trim() || !utc) { setError('Name + a future NPT time are required.'); setSchedBusy(false); return; }
        const r = await opsApi.post('/batches', {
          name: batchName.trim(), run_at_utc: utc,
          mode: mode === 'real' ? 'fix' : 'diagnose', cluster_ids: ids,
        });
        batch = r.batch;
      } else {
        for (const id of ids) {
          const r = await opsApi.post(`/batches/${batchSel}/members`, { cluster_id: id });
          batch = r.batch;
        }
      }
      onScheduled && onScheduled(batch);
    } catch (e) {
      // Conflict 409s carry the human reason — show it verbatim, loudly.
      setError(e.message);
    }
    setSchedBusy(false);
  };

  const resume = async (withInput) => {   // P4 Send (withInput) / Resume
    if (!job) return;
    setError(null); setRunning(true); setTab('cc');
    try {
      const r = await opsApi.post(`/run-jobs/${job.id}/resume`, withInput ? { input: inputText } : {});
      setInputText(''); setJob(r.job); poll(r.job.id);
    } catch (e) { setRunning(false); setError(e.message); }
  };

  const steps = (job && job.steps) || [];
  const transcript = (job && job.transcript) || [];
  const raw = (job && job.raw) || [];             // P6 — full raw stream-json lines
  const terminal = !!(job && OPS_JOB_TERMINAL.includes(job.status));
  const ok = !!(job && (job.status === 'smoke_ok' || job.status === 'shipped'));
  const smokeOk = !!(job && job.status === 'smoke_ok' && job.mode === 'real');
  const inputRequired = !!(job && job.status === 'input_required');
  const commit = job && job.result && job.result.commit;
  const stepperNodes = opsStepperNodes(job);

  return (
    <div className="ops-runviewer">
      {/* What changes (editable intent) + Regenerate — auto-collapses on run */}
      <div className="ops-collapsible">
        <button type="button" className="ops-collapsible-head" aria-expanded={whatOpen}
                onClick={() => setWhatOpen(o => !o)}>
          <span className="ops-caret">{whatOpen ? '▾' : '▸'}</span>What changes
          {running && <span style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 6 }}>(collapsed during run)</span>}
        </button>
        {whatOpen && (
          <div className="ops-collapsible-body">
            <OpsErrorBanner error={prepErr} onRetry={() => prepare()} onDismiss={() => setPrepErr(null)} />
            {(prepBusy === 'load' || !data) ? <OpsSkeleton rows={3} /> : (
              <>
                {/* P6 — "💡 Consider adding" lines are PROPOSED bundles: one-click
                    + Add promotes the bundle into Triage (triaged, via=affinity).
                    Never auto-injected — the click is the decision. */}
                {data.checker && <OpsCheckerBanner checker={data.checker}
                  onQuickAdd={async (cid) => {
                    try {
                      await opsApi.post(`/clusters/${cid}/triage`, { decision: 'scheduled', via: 'affinity' });
                      setPrepErr(null); prepare();
                    } catch (e) { setPrepErr(`Add #${cid} failed: ${e.message}`); }
                  }} />}
                <textarea className="ops-input ops-prepare-changes"
                          rows={Math.min(10, Math.max(3, (data.change_list || []).length + 1))}
                          value={edited} onChange={(e) => setEdited(e.target.value)}
                          aria-label="Editable change list" />
                {/* P4 — Regenerate is contextual: only when the intent box is dirty. */}
                {edited !== origEdited && (
                  <button className="ops-btn ops-btn-small" disabled={prepBusy === 'regen'} onClick={() => prepare(edited)}>
                    {prepBusy === 'regen' ? 'Regenerating…' : '↻ Regenerate to match edits'}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* P4 — flashing INPUT REQUIRED banner */}
      {inputRequired && (
        <div className="ops-input-required" role="alert">
          <div className="ops-input-required-head">⚠ INPUT REQUIRED — {(job.result && job.result.detail) || 'the run is blocked awaiting input'}</div>
          <div className="ops-input-required-body">
            Resolve the blocker (e.g. run <code>gcloud auth login</code>) if it needs external
            action, then Resume. Or type guidance and Send — the run re-invokes with your input.
            <div className="ops-input-required-row">
              <input className="ops-input" placeholder="Optional follow-up input for CC…"
                     value={inputText} onChange={(e) => setInputText(e.target.value)} />
              <button className="ops-btn ops-btn-primary" disabled={!inputText.trim() || running} onClick={() => resume(true)}>Send</button>
              <button className="ops-btn" disabled={running} onClick={() => resume(false)}>Resume</button>
            </div>
          </div>
        </div>
      )}

      {/* PRIMARY tabbed viewer */}
      <div className="ops-viewer-tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'prompt'} className={`ops-viewer-tab${tab === 'prompt' ? ' active' : ''}`} onClick={() => setTab('prompt')}>Combined prompt</button>
        <button role="tab" aria-selected={tab === 'cc'} className={`ops-viewer-tab${tab === 'cc' ? ' active' : ''}`} onClick={() => setTab('cc')}>
          Claude Code{transcript.length ? ` (${transcript.length})` : ''}{running ? ' · live' : ''}
        </button>
      </div>
      <div className="ops-viewer-body">
        {tab === 'prompt' && (
          <>
            {/* Loud re-merge indicator: without it a reorder silently shows the STALE
                prompt until the new merge lands. */}
            {prepBusy && data && <div className="ops-col-loading" role="status">↻ re-merging the combined prompt…</div>}
            <pre className="ops-viewer-prompt" data-merging={prepBusy ? '1' : '0'}>{data ? (data.unmerged ? '⚠ UNMERGED — review manually\n\n' : '') + (data.combined_prompt || '') : 'Preparing…'}</pre>
          </>
        )}
        {tab === 'cc' && (
          <>
            <div className="ops-cc-viewtoggle">
              <button className={`ops-cc-vopt${ccView === 'raw' ? ' on' : ''}`} onClick={() => setCcView('raw')}
                      title="Full unfiltered stream-json from CC">Raw</button>
              <button className={`ops-cc-vopt${ccView === 'tidied' ? ' on' : ''}`} onClick={() => setCcView('tidied')}
                      title="Parsed, readable transcript">Tidied</button>
            </div>
            <div className="ops-cc-log" ref={ccRef}>
              {ccView === 'raw' ? (
                (raw && raw.length) ?
                  <pre className="ops-cc-raw">{raw.join('\n')}</pre>
                  : <div className="ops-empty">{running ? (daemonFresh ? 'Waiting for Claude Code output…' : '⚠ Local daemon OFFLINE — job queued; start the daemon to run it.') : 'No raw stream yet — press Run in CC.'}</div>
              ) : (
                <>
                  {transcript.length === 0 && <div className="ops-empty">{running ? (daemonFresh ? 'Waiting for Claude Code output…' : '⚠ Local daemon OFFLINE — job queued; start the daemon to run it.') : 'No run yet — press Run in CC.'}</div>}
                  {transcript.map((t, i) => (
                    <div key={i} className={`ops-cc-entry cc-${t.kind}`}>
                      <span className="ops-cc-kind">{t.kind === 'tool_use' ? `⛭ ${t.name}` : t.kind === 'tool_result' ? (t.is_error ? '✕ result' : '↳ result') : t.kind}</span>
                      <span className="ops-cc-text">{t.text}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* PINNED pipeline stepper (panel width) */}
      {stepperNodes.length > 0 && (
        <div className="ops-stepper">
          {stepperNodes.map((n, i) => (
            <div key={i} className={`ops-stepper-node s-${(n.status || 'pending').replace(/[^a-z]/gi, '')}`} title={`${n.name}: ${n.status || 'pending'}`}>
              <span className="ops-stepper-dot" />{n.name}
            </div>
          ))}
        </div>
      )}

      <OpsErrorBanner error={error} onDismiss={() => { setError(null); setHint(false); }} />
      {hint && (
        <div className="ops-spike-hint" role="alert">
          ⚠ Local CC daemon {daemonFresh ? 'has not claimed the job yet' : 'is OFFLINE (no heartbeat)'} —
          the job is queued and runs the moment the daemon is up. Start it on the local machine:
          {' '}<code>python C:\Users\tim\ClaudeHealth\ops-daemon\daemon.py</code>
        </div>
      )}
      {terminal && (
        <div className={`ops-spike-final ${ok ? 'ok' : 'err'}`}>
          {ok ? <>✓ {job.status}{commit ? ` · commit ${String(commit).slice(0, 10)}` : ''}</>
              : <>✕ {job.status} · {(job.result && (job.result.error || job.result.result_text)) || 'see daemon.log'}</>}
        </div>
      )}
      {terminal && ok && isCombined && job.mode === 'real' && (
        <div className="ops-humanship-hint" style={{ marginTop: 6 }}>
          Combined fix committed + local smoke green for all {ids.length} bundles. Deploy by hand
          (<code>local build → scp → restart npl-kalpa</code>) — the deploy-marker flips every
          bundle in this set to shipped when it sees the commit live.
        </div>
      )}

      {/* FINAL-WF P2 — Scheduled: batch target picker (NPT input, live UK hint). */}
      {decision === 'scheduled' && (
        <div className="ops-schedule-panel">
          <select className="ops-input ops-sched-select" value={batchSel}
                  onChange={(e) => setBatchSel(e.target.value)} aria-label="Target batch">
            <option value="new">＋ New batch…</option>
            {schedBatches.map(b => (
              <option key={b.id} value={b.id}>
                {b.name} · {opsZoneTime(b.run_at_utc, 'Asia/Kathmandu')} NPT / {opsZoneTime(b.run_at_utc, 'Europe/London')} UK
              </option>
            ))}
          </select>
          {batchSel === 'new' && (
            <>
              <input className="ops-input" placeholder="Batch name" value={batchName}
                     onChange={(e) => setBatchName(e.target.value)} aria-label="Batch name" />
              <input type="datetime-local" className="ops-input" value={batchTime}
                     onChange={(e) => setBatchTime(e.target.value)} aria-label="Fire time (NPT)" />
              <span className="ops-batch-tzhint">
                {batchTime ? `NPT = ${opsZoneTime(nptLocalToUtc(batchTime), 'Europe/London')} UK` : 'time in NPT'}
              </span>
            </>
          )}
        </div>
      )}

      {/* STICKY run bar */}
      <div className="ops-runbar">
        <div className="ops-runbar-left">
          {/* P4 — two-state toggle replaces the run-mode dropdown. Default Diagnose
              only (read-only, safer); never auto-picked from the prompt. */}
          <div className="ops-mode-toggle" role="group" aria-label="Run mode">
            <button type="button" className={`ops-mode-opt${mode === 'noop' ? ' on' : ''}`}
                    disabled={running || !canRun} onClick={() => setMode('noop')}
                    title="Diagnose only — CC reads/diagnoses, edits nothing (read-only)">Diagnose only</button>
            <button type="button" className={`ops-mode-opt${mode === 'real' ? ' on' : ''}`}
                    disabled={running || !canRun} onClick={() => setMode('real')}
                    title="Fix — CC edits, commits, and locally builds — NO deploy">Fix</button>
          </div>
          {/* Immediate/Scheduled lives IN the box now (no pre-drop popup): it only
              tags batch-grouping — the bundle is already in-flight. */}
          {decision != null && onDecisionChange && (
            <div className="ops-mode-toggle" role="group" aria-label="Batch decision"
                 title="Batch tag only — the bundle is already in-flight either way">
              <button type="button" className={`ops-mode-opt${decision === 'immediate' ? ' on' : ''}`}
                      disabled={running} onClick={() => onDecisionChange('immediate')}>Immediate</button>
              <button type="button" className={`ops-mode-opt${decision === 'scheduled' ? ' on' : ''}`}
                      disabled={running} onClick={() => onDecisionChange('scheduled')}>Scheduled</button>
            </div>
          )}
          <span className="ops-runbar-meta">
            {!canRun ? 'Superadmin only'
              : (eligible ? `${eligible.klass} · ${humanShip ? '👤 human-ship' : '🤖 auto-ship'}` : 'same-origin job queue')}
            {job ? ` · job #${job.id} ${job.status}` : ''} · daemon {armed ? '🟢 armed' : daemonFresh ? '⚪ disarmed' : '🔴 offline'}
          </span>
        </div>
        <div className="ops-runbar-right">
          {decision === 'scheduled' && onScheduled ? (
            <button className="ops-btn ops-btn-primary" disabled={!runnable || schedBusy}
                    title="Add to the selected batch — it runs at the batch's fire time (that was your authorisation; no further prompt)"
                    onClick={addToBatch}>
              {schedBusy ? '＋ Adding…' : '＋ Add to batch'}
            </button>
          ) : (
          <button className="ops-btn ops-btn-primary" disabled={!runnable || running}
                  title={!canRun ? 'Superadmin only' : blocked ? 'Resolve checker blocks first'
                         : (mode === 'real' ? 'Fix — CC edits, commits, and locally builds — NO deploy (any class)' : 'Diagnose only — read-only')}
                  onClick={() => launch(mode)}>
            {running ? '▶ Running…' : (mode === 'real' ? '▶ Run in CC (Fix)' : '▶ Run in CC (Diagnose)')}
          </button>
          )}
          {allowShip && !isCombined && smokeOk && shipEligible && (
            <button className={`ops-btn ${armed ? 'ops-btn-primary' : ''}`} disabled={!armed || running}
                    title={armed ? 'ARMED — deploys to NPL behind a green-smoke gate w/ auto-rollback'
                                 : 'Deploy disarmed — set armed=true in the daemon (daemon.config.json)'}
                    onClick={() => launch('ship')}>
              {armed ? '⇧ Ship to NPL (armed)' : '⇧ Ship (disarmed)'}
            </button>
          )}
        </div>
      </div>

      {/* P4 — HUMAN-SHIP panel (native/auth/schema): the daemon left a committed +
          locally-smoked change; a person deploys it after review. */}
      {allowShip && !isCombined && smokeOk && humanShip && (
        <div className={`ops-humanship ${eligible.klass === 'auth' ? 'hs-auth' : eligible.klass === 'schema' ? 'hs-schema' : ''}`}>
          <div className="ops-humanship-head">👤 Ready to deploy (human-ship) — {eligible.klass}</div>
          <div className="ops-humanship-body">
            {commit ? <>Fix committed <code>{String(commit).slice(0, 10)}</code> and passed local smoke.
              This class is <b>not</b> auto-deployed — deploy it by hand after review.</> :
              <>Local smoke green; deploy by hand after review.</>}
            {eligible.klass === 'auth' && <div className="ops-humanship-warn">⚠ AUTH-CLASS — re-check login/role gates and the lockout risk before you deploy.</div>}
            {eligible.klass === 'schema' && <div className="ops-humanship-warn">⚠ SCHEMA/MIGRATION — take a DB snapshot before deploying; migrations are forward-only.</div>}
            <div className="ops-humanship-hint">
              Deploy: <code>local build → scp → restart npl-kalpa → import-smoke</code>, then the deploy-marker
              flips this bundle to shipped automatically. It stays <b>fixing</b> until the marker sees the commit live.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OpsBundleDrawer({ clusterId, onClose, onChanged, canRunSpike }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notifyDraft, setNotifyDraft] = useState('');
  const [kbPreview, setKbPreview] = useState(null);
  const [notifyOptIn, setNotifyOptIn] = useState(false); // P6 — default on iff feedback-sourced
  const [imageView, setImageView] = useState(null);
  const [prepReload, setPrepReload] = useState(0); // bump to re-merge the prepare panel
  const [maximized, setMaximized] = useState(false);
  const [runActive, setRunActive] = useState(false);
  const [triageOpen, setTriageOpen] = useState(true);
  const isMobile = useOpsMobile();

  useEffect(() => { if (runActive) setTriageOpen(false); }, [runActive]); // auto-collapse on run

  const load = useCallback(async () => {
    setError(null);
    try {
      const j = await opsApi.get(`/clusters/${clusterId}`);
      setDetail(j.cluster);
      setNotifyDraft(j.cluster.notify_draft || '');
      setNotifyOptIn(!!j.cluster.has_feedback_signal); // P6 — default on for feedback-sourced bundles
      if (j.cluster.kb_articles && j.cluster.kb_articles.length) setKbPreview(j.cluster.kb_articles[0]);
    } catch (e) { setError(e.message); }
  }, [clusterId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const act = async (label, fn) => {
    setBusy(label); setError(null);
    try { await fn(); await load(); onChanged && onChanged(); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };

  const c = detail;
  const body = (
    <div className={`ops-drawer-panel ops-drawer-runviewer${maximized ? ' ops-drawer-max' : ''}`} onClick={(e) => e.stopPropagation()}>
      {/* Compact header: title · bundle#N status · tags on one line; signals·users far right. */}
      <div className="ops-drawer-head ops-drawer-head-compact">
        <div className="ops-head-line">
          <span className="ops-head-title">{c ? c.title : 'Loading…'}</span>
          {c && <span className="ops-head-meta">bundle #{c.id} · {c.status}{c.decision ? ` · ${c.decision}` : ''}</span>}
          {c && (
            <span className="ops-head-tags">
              <OpsChip color={OPS_PLATFORM_COLOR[c.platform_class] || OPS_PLATFORM_COLOR.web}
                       title={(c.platform_reasons || []).join(' · ')}>{c.platform_class || 'web'}</OpsChip>
              {c.severity && <OpsChip color={OPS_SEV_COLOR[c.severity]}>{c.severity}</OpsChip>}
              {!!c.is_regression && <OpsChip color="#dc2626">regression</OpsChip>}
              {c.category && <OpsChip>{c.category}</OpsChip>}
            </span>
          )}
          {c && <span className="ops-head-counts">{(c.live_signal_count ?? c.signal_count)} signals · {c.affected_user_count} users</span>}
          {c && <button className="ops-btn ops-btn-small" title="Soft-archive this bundle (restorable via Archived toggle)"
                        disabled={busy === 'archive'}
                        onClick={async () => { try { setBusy('archive'); await opsApi.post(`/clusters/${c.id}/archive`); onChanged && onChanged(); onClose(); } catch (e) { setError(e.message); setBusy(null); } }}>🗑 Archive</button>}
          <button className="ops-btn ops-btn-small" title={maximized ? 'Restore' : 'Maximize'} onClick={() => setMaximized(m => !m)}>{maximized ? '❐ Restore' : '⛶ Maximize'}</button>
          <button className="ops-btn ops-btn-small" onClick={onClose}>✕ Close</button>
        </div>
      </div>
      <OpsErrorBanner error={error} onDismiss={() => setError(null)} />
      {!c ? <OpsSkeleton rows={5} /> : (
        <div className="ops-drawer-scroll">
          {c.summary && <div className="ops-head-summary">{c.summary}</div>}

          {/* Triage — only while untriaged; auto-collapses when a run is in progress. */}
          {!c.decision && (
            <div className="ops-collapsible">
              <button type="button" className="ops-collapsible-head" aria-expanded={triageOpen} onClick={() => setTriageOpen(o => !o)}>
                <span className="ops-caret">{triageOpen ? '▾' : '▸'}</span>Triage
                {c.recommended_decision && <span style={{ fontWeight: 400, color: 'var(--text-dim)', marginLeft: 6 }}>(AI recommends {c.recommended_decision})</span>}
              </button>
              {triageOpen && (
                <div className="ops-collapsible-body" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['immediate', 'scheduled', 'log_only'].map(d => (
                    <button key={d} className={`ops-btn${c.recommended_decision === d ? ' ops-btn-recommended' : ''}`}
                            disabled={busy === 'triage'}
                            onClick={() => act('triage', () => opsApi.post(`/clusters/${c.id}/triage`, { decision: d }))}>{d}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PRIMARY run viewer — tabbed CC transcript + stepper + run bar (the star).
              Immediate/Scheduled is an in-box toggle (batch tag only, decision_only —
              flipping never knocks a fixing bundle back to triaged). */}
          <OpsRunViewer clusterId={c.id} canRun={!!canRunSpike} eligible={c.daemon_eligible}
                        reloadToken={prepReload} onRunningChange={setRunActive}
                        decision={(c.decision === 'immediate' || c.decision === 'scheduled') ? c.decision : 'immediate'}
                        onDecisionChange={(d) => act('decision', () => opsApi.post(`/clusters/${c.id}/triage`, { decision: d, decision_only: true }))}
                        onTerminal={() => { load(); onChanged && onChanged(); }}
                        onLaunched={() => { onChanged && onChanged(); onClose(); }}
                        onScheduled={() => { onChanged && onChanged(); onClose(); }} />

          {/* Kept below the fold (per operator decision): raw signals, manual
              pipeline advance + spec regen, and loop-closure. Smoke/verify are no
              longer buttons — they become automatic in Build 2. */}
          <OpsCollapsible title={`Raw signals (${c.signals.length})`}>
            {c.signals.length === 0 && <div className="ops-empty">No member signals.</div>}
            {c.signals.map(s => <OpsSignalRow key={s.id} s={s} onImage={setImageView} />)}
          </OpsCollapsible>

          <OpsCollapsible title="Pipeline (advance status)">
            <OpsStatusPipeline
              cluster={c}
              onAdvance={(st) => act('status', () => opsApi.post(`/clusters/${c.id}/status`, { status: st }))}
            />
            <div style={{ marginTop: 10 }}>
              <button className="ops-btn ops-btn-small" disabled={busy === 'respec'}
                      onClick={() => act('respec', async () => { await opsApi.post(`/clusters/${c.id}/respec`); setPrepReload(x => x + 1); })}>
                {busy === 'respec' ? (c.fix_spec ? 'Regenerating…' : 'Generating…') : (c.fix_spec ? '↻ Regenerate spec (saves)' : 'Generate spec')}
              </button>
              <span style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 8 }}>Persists fix_spec, then re-merges the prompt above.</span>
            </div>
          </OpsCollapsible>

          <OpsCollapsible title="Close the loop (KB + notify)"
                          defaultOpen={c.status === 'verified' || c.status === 'notified' || !!c.notify_draft || !!kbPreview}>
            {/* P5 — KB is AUTO-DRAFTED on a successful Fix ship (no manual button). */}
            {kbPreview ? (
              <div className="ops-kb-preview">
                <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginBottom: 4 }}>KB draft — auto-generated on ship</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{kbPreview.title}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{kbPreview.changelog_line}</div>
                <button className="ops-btn ops-btn-small" style={{ marginTop: 6 }} disabled={busy === 'kb'}
                        onClick={() => act('kb', () => opsApi.post(`/kb/${kbPreview.id}/publish`))}>
                  {kbPreview.published ? 'Unpublish' : 'Publish to KB'}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>A KB draft is written automatically when a Fix ships.</div>
            )}

            {/* P6 — notify applies ONLY to feedback-sourced bundles; optional, default on. */}
            <div className="ops-section-title" style={{ marginTop: 12 }}>Notify the reporter</div>
            {!c.has_feedback_signal ? (
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>No user feedback in this bundle — notify not applicable (telemetry/errors don’t get a notify).</div>
            ) : (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={notifyOptIn} onChange={(e) => setNotifyOptIn(e.target.checked)} />
                  Generate notify draft (short thank-you to the reporter — saved as a draft, never auto-sent)
                </label>
                {notifyOptIn && !notifyDraft && (
                  <button className="ops-btn" style={{ marginTop: 8 }} disabled={busy === 'notify'}
                          onClick={() => act('notify', async () => {
                            const r = await opsApi.post(`/clusters/${c.id}/notify-draft`);
                            setNotifyDraft(r.notify_draft || '');
                          })}>
                    {busy === 'notify' ? 'Drafting…' : 'Generate draft'}
                  </button>
                )}
                {notifyOptIn && notifyDraft && (
                  <>
                    <textarea className="ops-input" style={{ marginTop: 8 }} rows={3} value={notifyDraft}
                              onChange={(e) => setNotifyDraft(e.target.value)} />
                    <div style={{ fontSize: 10.5, color: 'var(--text-dim)', margin: '4px 0 8px' }}>
                      Saved as a draft — send manually (no in-app delivery wired yet). The console never auto-sends.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <OpsCopyButton text={notifyDraft} label="Copy message" />
                      {c.status === 'verified' && (
                        <button className="ops-btn ops-btn-primary" disabled={busy === 'status'}
                                onClick={() => act('status', () => opsApi.post(`/clusters/${c.id}/status`, { status: 'notified' }))}>
                          ✓ Mark sent
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </OpsCollapsible>
        </div>
      )}
      {imageView && <OpsImageOverlay path={imageView} onClose={() => setImageView(null)} />}
    </div>
  );

  return ReactDOM.createPortal(
    <div className="ops-overlay-scrim" onClick={onClose}>{body}</div>,
    document.body
  );
}

// P6 — New + Triaged merged into ONE wide "Triage" lane (needs-triage sorted to top).
const OPS_BUNDLE_GROUPS = [
  { key: 'triage', label: 'Triage', statuses: ['new', 'triaged'], wide: true },
  { key: 'inflight', label: 'In-flight', statuses: ['specd', 'fixing', 'shipped'] },
  // FINAL-WORKFLOW — 4th lane: batches (first-class objects), not clusters. Cards
  // that belong to an active batch render INSIDE their batch group only.
  { key: 'scheduled', label: 'Scheduled', statuses: [], batchLane: true },
  { key: 'done', label: 'Done', statuses: ['verified', 'notified', 'logged'] },
];

// ── Batch time display: stored UTC; shown in BOTH NPT (Asia/Kathmandu, fixed
// UTC+5:45, no DST) and UK (Europe/London, auto GMT/BST). ──────────────────────
function opsUtcDate(utcStr) {                      // 'YYYY-MM-DDTHH:MM:SS' (UTC) → Date
  return new Date((utcStr || '').replace(' ', 'T') + 'Z');
}
function opsZoneTime(utcStr, tz) {
  try {
    return opsUtcDate(utcStr).toLocaleString('en-GB', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false });
  } catch (e) { return '?'; }
}
function opsZoneFull(utcStr, tz) {
  try {
    return opsUtcDate(utcStr).toLocaleString('en-GB', {
      timeZone: tz, weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false });
  } catch (e) { return '?'; }
}
function opsCountdown(utcStr) {
  const ms = opsUtcDate(utcStr).getTime() - Date.now();
  if (isNaN(ms)) return '?';
  if (ms <= 0) return 'due';
  const m = Math.round(ms / 60000);
  if (m < 60) return `runs in ${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `runs in ${h}h ${m % 60}m` : `runs in ${Math.round(h / 24)}d`;
}
// NPT datetime-local input value → UTC 'YYYY-MM-DDTHH:MM:SS' (NPT = UTC+5:45 fixed).
function nptLocalToUtc(v) {
  if (!v) return null;
  const ms = Date.parse(v + ':00Z');               // treat the wall time as if UTC…
  if (isNaN(ms)) return null;
  const utc = new Date(ms - (5 * 60 + 45) * 60000); // …then subtract the fixed offset
  return utc.toISOString().slice(0, 19);
}
const OPS_BATCH_STATUS_COLOR = { scheduled: '#2563eb', running: '#d97706', missed: '#dc2626', done: '#16a34a', failed: '#dc2626' };

// One collapsible batch group card in the Scheduled lane.
function OpsBatchCard({ b, onChanged, flash }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tval, setTval] = useState('');
  const [suggest, setSuggest] = useState(null);   // P3 affinity: null=unloaded
  const [, tick] = useState(0);
  useEffect(() => { const iv = setInterval(() => tick(x => x + 1), 30000); return () => clearInterval(iv); }, []);
  // P3 — affinity suggestions load when the card expands (editable states only).
  useEffect(() => {
    if (!open || !['scheduled', 'missed'].includes(b.status)) return;
    let alive = true;
    opsApi.get(`/batches/${b.id}/suggest`)
      .then(r => { if (alive) setSuggest(r.suggestions || []); })
      .catch(() => { if (alive) setSuggest([]); });
    return () => { alive = false; };
  }, [open, b.id, b.status, (b.members || []).length]);
  const band = `hsl(${(b.id * 67) % 360} 60% 45%)`;
  const act = async (label, fn) => {
    try { await fn(); onChanged && onChanged(); }
    catch (e) { flash && flash(`${label} failed: ${e.message}`); }
  };
  return (
    <div className="ops-batch-card" style={{ borderLeft: `4px solid ${OPS_BATCH_STATUS_COLOR[b.status] || band}` }}>
      <div className="ops-batch-head" onClick={() => setOpen(o => !o)}>
        <span className="ops-caret">{open ? '▾' : '▸'}</span>
        <span className="ops-batch-name" title={`#${b.id} · ${b.mode}`}>{b.name}</span>
        <OpsChip color={OPS_BATCH_STATUS_COLOR[b.status]}>{b.status}</OpsChip>
        <OpsChip>{b.mode}</OpsChip>
        <span className="ops-batch-count">{(b.members || []).length}</span>
      </div>
      <div className="ops-batch-when"
           title={`${opsZoneFull(b.run_at_utc, 'Asia/Kathmandu')} NPT · ${opsZoneFull(b.run_at_utc, 'Europe/London')} UK`}>
        {b.status === 'scheduled' ? opsCountdown(b.run_at_utc)
          : b.status === 'missed' ? '⚠ missed' : b.status}
        {' · '}{opsZoneTime(b.run_at_utc, 'Asia/Kathmandu')} NPT / {opsZoneTime(b.run_at_utc, 'Europe/London')} UK
      </div>
      {b.status === 'missed' && (
        <div className="ops-batch-missed">
          Fire time passed while the runner was off — never auto-run late.
          <button className="ops-btn ops-btn-small ops-btn-primary"
                  onClick={() => act('Run now', () => opsApi.post(`/batches/${b.id}/run-now`))}>▶ Run now?</button>
        </div>
      )}
      {open && (
        <div className="ops-batch-body">
          {(b.members || []).map(m => (
            <div key={m.cluster_id} className="ops-batch-member">
              <span className="ops-batch-mtitle" title={m.title}>#{m.cluster_id} {m.friendly_title || m.title}</span>
              <OpsChip color={OPS_PLATFORM_COLOR[m.platform_class] || OPS_PLATFORM_COLOR.web}>{m.platform_class}</OpsChip>
              {(b.status === 'scheduled' || b.status === 'missed') && (
                <button className="ops-btn ops-btn-small" title="Remove from batch"
                        onClick={() => act('Remove', () => opsApi.post(`/batches/${b.id}/remove-member`, { cluster_id: m.cluster_id }))}>✕</button>
              )}
            </div>
          ))}
          {/* P3 — affinity suggest: same module/class, conflict-free. Suggestion
              only; one-click add (server re-checks conflicts on add). */}
          {suggest && suggest.length > 0 && (b.status === 'scheduled' || b.status === 'missed') && (
            <div className="ops-batch-suggest">
              <div className="ops-batch-suggest-head">{suggest.length} more fit this batch — add?</div>
              {suggest.map(s => (
                <div key={s.cluster_id} className="ops-batch-member">
                  <span className="ops-batch-mtitle" title={s.why}>#{s.cluster_id} {s.title} <em>({s.why})</em></span>
                  <button className="ops-btn ops-btn-small"
                          onClick={() => act('Add suggested', () => opsApi.post(`/batches/${b.id}/members`, { cluster_id: s.cluster_id }))}>＋</button>
                </div>
              ))}
            </div>
          )}
          {(b.status === 'scheduled' || b.status === 'missed') && (
            <div className="ops-batch-actions">
              {editing ? (
                <>
                  <input type="datetime-local" className="ops-input" value={tval}
                         onChange={(e) => setTval(e.target.value)} aria-label="New fire time (NPT)" />
                  <span className="ops-batch-tzhint">{tval ? `= ${opsZoneTime(nptLocalToUtc(tval), 'Europe/London')} UK` : 'NPT time'}</span>
                  <button className="ops-btn ops-btn-small ops-btn-primary" disabled={!tval}
                          onClick={() => act('Reschedule', async () => {
                            await opsApi.post(`/batches/${b.id}`, { run_at_utc: nptLocalToUtc(tval) });
                            setEditing(false);
                          })}>Save</button>
                  <button className="ops-btn ops-btn-small" onClick={() => setEditing(false)}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="ops-btn ops-btn-small" onClick={() => setEditing(true)}>🕒 Edit time</button>
                  <button className="ops-btn ops-btn-small" title="Soft-delete — members reappear on the board"
                          onClick={() => act('Delete', () => opsApi.post(`/batches/${b.id}/delete`))}>🗑</button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
const OPS_COL_LABEL = Object.fromEntries(OPS_BUNDLE_GROUPS.map(g => [g.key, g.label]));
// Resolved (terminal) statuses — re-triaging or re-working these is blocked
// client-side even where the server's triage endpoint would technically allow
// it, per spec ("disable Schedule on a Done card").
const OPS_TERMINAL_STATUSES = new Set(['verified', 'notified', 'logged']);

// Bulk-action legality (client-side mirror of what the server will accept, kept
// deliberately strict). action ∈ {scheduled, immediate, log_only} → triage;
// 'fixing' → status. Returns true iff firing this on `status` would succeed.
function opsBulkLegal(action, status) {
  if (action === 'scheduled' || action === 'immediate' || action === 'log_only') {
    return !OPS_TERMINAL_STATUSES.has(status);
  }
  if (action === 'fixing') return status === 'triaged' || status === 'specd';
  if (action === 'archive') return true;   // any board (non-archived) card can be soft-archived
  return false;
}

// ── Ops drag/drop transition model — ONE authority, geometry-independent ──────
// Columns (lanes): 'triage' | 'inflight' | 'done'. The two-column Triage lane and
// the wide grid are irrelevant here — legality is a pure function of lanes, never
// of pixels. This replaced the pre-two-column logic that keyed a 'triaged' colKey
// (the group key is 'triage'), which mis-routed EVERY Triage drop through the
// /verify (Done) path → cards leaked into Done + reverse "Move → Triage" 0/2.
//
// LEGAL LANE MOVES (spec): Triage→In-flight, In-flight→Done, In-flight→Triage,
// Done→In-flight, Done→Triage, Triage→Done (deliberate). same→same = no-op.
const OPS_LEGAL_MOVES = {
  triage:   new Set(['inflight', 'done']),
  inflight: new Set(['done', 'triage']),
  done:     new Set(['inflight', 'triage']),
};
// SOLE legality authority. No geometry, no scattered per-status conditionals.
function isLegalTransition(fromCol, toCol) {
  if (!fromCol || !toCol || fromCol === toCol) return false; // same→same handled as no-op by callers
  const s = OPS_LEGAL_MOVES[fromCol];
  return !!(s && s.has(toCol));
}
function opsLaneForStatus(status) {
  const g = OPS_BUNDLE_GROUPS.find(x => x.statuses.includes(status));
  return g ? g.key : null;
}
// Per-CARD feasibility once the lane move is legal — mirrors the real endpoints so
// the DROP OK indicator is honest (never OK-then-fails):
//  triage  → /triage (transition-free): any card NOT already in the triage lane.
//  inflight→ /status{fixing}: forward triaged/specd, or reverse-reopen from verified.
//  done    → /verify smoke: shipped only (verified-via-smoke invariant preserved).
function opsCardCanEnter(toCol, status) {
  if (toCol === 'triage')   return opsLaneForStatus(status) !== 'triage';
  if (toCol === 'inflight') return status === 'triaged' || status === 'specd' || status === 'verified';
  if (toCol === 'done')     return status === 'shipped';
  return false;
}
// A drop is allowed iff the lane move is legal AND the card can actually complete it.
function opsDropLegal(colKey, status) {
  return isLegalTransition(opsLaneForStatus(status), colKey) && opsCardCanEnter(colKey, status);
}
// A drop is ACCEPTED on a column if it is directly legal (opsDropLegal), OR it is a 'new'
// (un-triaged) bundle dropped on In-flight — accepted via an inline triage decision: the
// chooser assigns Immediate/Scheduled, then the bundle is triaged AND moved to In-flight
// in one gesture (see applyInflightWithTriage). Drives the drop indicator + drop routing;
// the executor still triages-then-moves so every backend transition stays legal.
function opsDroppable(colKey, status) {
  return opsDropLegal(colKey, status) || (colKey === 'inflight' && status === 'new');
}
const OPS_DROP_HINT = {
  triage: 'Already triaged / a resolved card can’t be re-triaged.',
  scheduled: 'Batches live here, not cards — drag to In-flight, pick Scheduled in the run box, add to a batch.',
  done: 'Done = verified, which needs a passing smoke run. Only a shipped bundle can be dropped here (it runs Run smoke); advance others via In-flight → shipped in the drawer.',
};

function OpsBundlesTab({ onCounts, isSuperadmin }) {
  const [clusters, setClusters] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(null); // {label, done, total}
  const [toast, setToast] = useState(null);
  const [dragSet, setDragSet] = useState(null);   // ids being dragged (Set)
  const [dragOverCol, setDragOverCol] = useState(null);
  const [triageDrop, setTriageDrop] = useState(null); // {ids:[...], x, y}
  const [combinedRun, setCombinedRun] = useState(null); // ORDERED ids — combined-run box (multi-drop + ⚙ Prepare)
  const [jobModal, setJobModal] = useState(null);       // P5 — open chip → live-job modal
  const [archived, setArchived] = useState([]);       // soft-archived bundles
  const [showArchived, setShowArchived] = useState(false);
  const [undo, setUndo] = useState(null);             // {ids} — Undo toast after archive
  const isMobile = useOpsMobile();
  const lastSelRef = useRef(null); // {colKey, index} for shift-range
  const dragSetRef = useRef(null); // P3: mirror of dragSet, set SYNCHRONOUSLY on
                                   // dragstart so the very first dragover can
                                   // preventDefault (no not-allowed flicker while
                                   // React state settles).
  const toastTimer = useRef(null);

  const flash = useCallback((msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4500);
  }, []);

  const [batches, setBatches] = useState([]);
  const load = useCallback(async () => {
    setError(null);
    try {
      const j = await opsApi.get('/clusters');
      setClusters(j.clusters); onCounts && onCounts(j.counts);
      try { const bt = await opsApi.get('/batches'); setBatches(bt.batches || []); } catch (e) { console.log('[ops-batch] load failed:', e.message); }
      try { const a = await opsApi.get('/clusters?archived=1'); setArchived(a.clusters || []); } catch (e) { /* toggle count non-fatal */ }
    } catch (e) { setError(e.message); }
  }, [onCounts]);

  const restoreBundle = useCallback((id) => {
    opsApi.post(`/clusters/${id}/restore`).then(() => { flash(`Restored #${id}`); load(); })
      .catch((e) => flash(`Restore #${id} failed: ${e.message}`));
  }, [flash, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Soft-delete: single (hover bin), bulk (bar), and Delete-key all route here.
  // Optimistically drops the cards, archives each (reversible), then shows an
  // Undo toast; Undo restores exactly those bundles. No hard delete anywhere.
  const archiveIds = useCallback(async (rawIds) => {
    const ids = [...new Set(rawIds)];
    if (!ids.length) return;
    setClusters(cs => (cs || []).filter(c => !ids.includes(c.id))); // optimistic
    setSelected(new Set()); lastSelRef.current = null;
    const ok = [];
    for (const id of ids) { try { await opsApi.post(`/clusters/${id}/archive`); ok.push(id); } catch (e) { /* skip; reload reconciles */ } }
    if (ok.length) setUndo({ ids: ok });
    await load();
  }, [load]);
  const doUndo = useCallback(async () => {
    const ids = (undo && undo.ids) || []; setUndo(null);
    for (const id of ids) { try { await opsApi.post(`/clusters/${id}/restore`); } catch (e) { /* skip */ } }
    flash(`Restored ${ids.length}`);
    await load();
  }, [undo, load, flash]);
  useEffect(() => { if (!undo) return; const t = setTimeout(() => setUndo(null), 6000); return () => clearTimeout(t); }, [undo]);
  // Delete/Backspace on a selection → archive (power-user path). Ignored while typing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const t = e.target || {};
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
      if (!selected || selected.size === 0) return;
      e.preventDefault();
      archiveIds([...selected]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, archiveIds]);
  useEffect(() => { load(); }, [load]);

  const statusOf = useCallback(
    (id) => { const c = (clusters || []).find(x => x.id === id); return c ? c.status : null; },
    [clusters]);
  const clusterOf = useCallback((id) => (clusters || []).find(x => x.id === id), [clusters]);
  // P2 — the daemon Fix-runs EVERY class now, so any card may enter the in-flight
  // (daemon Fix) lane. The auto-ship split (web/backend) is enforced at the Ship
  // button + server, not here. Absent metadata → eligible.
  const isDaemonEligible = useCallback((id) => {
    const c = clusterOf(id);
    return !c || !c.daemon_eligible || c.daemon_eligible.run_eligible !== false;
  }, [clusterOf]);
  const classOf = useCallback((id) => {
    const c = clusterOf(id);
    return (c && c.daemon_eligible && c.daemon_eligible.klass) || (c && c.platform_class) || 'manual';
  }, [clusterOf]);

  const clearSel = useCallback(() => { setSelected(new Set()); lastSelRef.current = null; }, []);

  if (error) return <OpsErrorBanner error={error} onRetry={load} />;
  // P1 — progressive load: render the interface SHELL (toolbar + legend + column
  // headers + skeleton cards) IMMEDIATELY; swap in real cards when /clusters
  // returns. No blocking spinner over the whole panel.
  const loading = clusters === null;
  const cl = clusters || [];
  if (!loading && cl.length === 0) return <div className="ops-empty">No bundles yet — synthesize signals in the Inbox tab.</div>;

  // Clusters inside an ACTIVE batch render ONLY within their batch group card —
  // never duplicated into the cluster lanes.
  const activeBatches = batches.filter(b => ['scheduled', 'missed', 'running'].includes(b.status));
  const batchMemberIds = new Set(activeBatches.flatMap(b => (b.members || []).map(m => m.cluster_id)));
  const groups = OPS_BUNDLE_GROUPS.map(g => {
    if (g.batchLane) return { ...g, items: [], batches: activeBatches };
    let items = cl.filter(c => g.statuses.includes(c.status) && !batchMemberIds.has(c.id));
    // P6 — in the merged Triage lane, needs-triage (status 'new') sorts to the top.
    if (g.key === 'triage') items = [...items].sort((a, b) =>
      (a.status === 'new' ? 0 : 1) - (b.status === 'new' ? 0 : 1));
    return { ...g, items };
  });
  const nextBatch = cl.filter(c => c.decision === 'scheduled' && c.status === 'triaged');

  // ── selection ──────────────────────────────────────────────────────────
  const colItems = (colKey) => (groups.find(g => g.key === colKey) || {}).items || [];
  const colKeyForStatus = (st) => (OPS_BUNDLE_GROUPS.find(g => g.statuses.includes(st)) || {}).key;

  // Prepare in-flight: merge the IN-FLIGHT column's bundles (the run set — matches
  // the drag model: Triaged stages, In-flight runs) into one CC prompt. Uses the
  // In-flight selection if any of that column is selected, else all of In-flight.
  // (Fix: previously read the Triaged column — wrong set, leaked Triaged
  // native/schema/auth BLOCK rows into the merge.)
  const inflightSelCount = colItems('inflight').filter(x => selected.has(x.id)).length;
  // ⚙ Prepare = the SAME combined-run box the multi-drop opens (review: one run
  // surface everywhere; the legacy Build-1 read-only modal with its permanently
  // disabled Run button is retired). Single bundle → the drawer (full context).
  const startPrepare = () => {
    const inflight = colItems('inflight');
    const sel = inflight.filter(x => selected.has(x.id)).map(x => x.id);
    const ids = sel.length ? sel : inflight.map(x => x.id);
    if (ids.length === 1) setOpenId(ids[0]);
    else if (ids.length) setCombinedRun(orderForCombined(ids));
  };

  const toggleSelect = (id, shift) => {
    const colKey = colKeyForStatus(statusOf(id));
    const items = colItems(colKey);
    const idx = items.findIndex(x => x.id === id);
    setSelected(prev => {
      const next = new Set(prev);
      if (shift && lastSelRef.current && lastSelRef.current.colKey === colKey) {
        const [a, b] = [lastSelRef.current.index, idx].sort((m, n) => m - n);
        for (let i = a; i <= b; i++) next.add(items[i].id);
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastSelRef.current = { colKey, index: idx };
  };

  const colSelState = (colKey) => {
    const items = colItems(colKey);
    if (!items.length) return 'none';
    const sel = items.filter(x => selected.has(x.id)).length;
    return sel === 0 ? 'none' : sel === items.length ? 'all' : 'some';
  };
  const toggleSelectAll = (colKey) => {
    const items = colItems(colKey);
    const state = colSelState(colKey);
    setSelected(prev => {
      const next = new Set(prev);
      if (state === 'all') items.forEach(x => next.delete(x.id));
      else items.forEach(x => next.add(x.id));
      return next;
    });
    lastSelRef.current = null;
  };

  // ── bulk + drop execution ──────────────────────────────────────────────
  // Runs `fn(id)` over ids sequentially with progress; loud partial-failure
  // report; snapshot revert on any failure so no server-rejected state shows.
  const runOver = async (label, ids, fn, { revertOnFail = false } = {}) => {
    if (!ids.length) return 0;
    const snapshot = clusters;
    setBulkBusy({ label, done: 0, total: ids.length });
    const failed = [];
    let done = 0;
    for (const id of ids) {
      try { await fn(id); }
      catch (e) { failed.push(`#${id}: ${e.message}`); }
      done += 1; setBulkBusy({ label, done, total: ids.length });
    }
    setBulkBusy(null);
    if (failed.length) {
      if (revertOnFail) setClusters(snapshot);
      flash(`${label}: ${ids.length - failed.length}/${ids.length} ok — FAILED ${failed.join('; ')}`);
    } else {
      flash(`${label}: ${ids.length} updated`);
    }
    clearSel();
    await load();
    return failed.length;   // callers gate follow-on UI (e.g. auto-open the box) on 0
  };

  const bulkAction = (a) => {
    const ids = [...selected].filter(id => opsBulkLegal(a.key, statusOf(id)));
    if (!ids.length) return;
    if (a.kind === 'archive') { archiveIds(ids); return; }   // → optimistic + Undo toast
    const fn = a.kind === 'triage'
      ? (id) => opsApi.post(`/clusters/${id}/triage`, { decision: a.key })
      : a.kind === 'archive'
      ? (id) => opsApi.post(`/clusters/${id}/archive`)
      : (id) => opsApi.post(`/clusters/${id}/status`, { status: a.key });
    runOver(a.label, ids, fn);
  };

  // Apply a column drop to a set of ids. `decision` only for the triaged path.
  const applyDrop = async (colKey, ids, decision) => {
    let legal = ids.filter(id => opsDropLegal(colKey, statusOf(id)));
    let skipped = ids.length - legal.length;
    // P3c — In-flight is the daemon-batch lane: only web/backend may enter via
    // drag. native/auth/schema are FLAGGED (not silently mixed) and kept manual —
    // they still advance via the drawer's Pipeline.
    if (colKey === 'inflight') {
      const blocked = legal.filter(id => !isDaemonEligible(id));
      if (blocked.length) {
        const kinds = [...new Set(blocked.map(classOf))].join('/');
        console.log('[ops-dnd] in-flight drop — %d manual card(s) BLOCKED (%s), moving %d eligible',
                    blocked.length, kinds, legal.length - blocked.length);
        flash(`${blocked.length} kept manual (${kinds}) — advance native/auth/schema via the drawer, not the daemon batch`);
      }
      legal = legal.filter(isDaemonEligible);
    }
    if (!legal.length) { flash(`Can’t move ${OPS_COL_LABEL[colKey]} — ${OPS_DROP_HINT[colKey] || 'illegal or manual-only for the selected card(s)'}`); return; }
    // Optimistic patch so the move is instant; load() reconciles, revert on fail.
    // Lane key is 'triage' (the merged group key) — NOT 'triaged'; keying the wrong
    // string here was the bug that routed Triage drops through /verify → Done leak.
    const optimisticStatus = colKey === 'triage' ? 'triaged' : colKey === 'inflight' ? 'fixing' : 'verified';
    setClusters(cs => cs.map(c => legal.includes(c.id)
      ? { ...c, status: optimisticStatus, ...(colKey === 'triage' && decision ? { decision } : {}) }
      : c));
    const label = `Move → ${OPS_COL_LABEL[colKey]}`;
    const fn = (id) => {
      // Transition-free /triage persists a reverse (In-flight/Done → Triage) too.
      if (colKey === 'triage') return opsApi.post(`/clusters/${id}/triage`, { decision });
      if (colKey === 'inflight') return opsApi.post(`/clusters/${id}/status`, { status: 'fixing' });
      // done → existing /verify smoke; must pass AND transition (shipped only).
      return opsApi.post(`/clusters/${id}/verify`).then(v => {
        if (!v.pass) throw new Error('smoke failed');
        if (!v.transitioned_to_verified) throw new Error('smoke passed but card was not shipped');
      });
    };
    await runOver(label + (skipped ? ` (${skipped} skipped)` : ''), legal, fn, { revertOnFail: true });
  };

  // Default order for the combined-run box: shared-file-together (bundles of one
  // platform class touch the same artifact — web≈App.js, backend≈app.py), then
  // severity major→minor within the class. The operator can reorder in the box.
  const orderForCombined = (ids) => {
    const sevRank = { crash: 0, major: 1, minor: 2, cosmetic: 3 };
    return [...ids].sort((a, b) => {
      const ca = classOf(a), cb = classOf(b);
      if (ca !== cb) return ca < cb ? -1 : 1;
      const sa = sevRank[(clusterOf(a) || {}).severity] ?? 9;
      const sb = sevRank[(clusterOf(b) || {}).severity] ?? 9;
      return sa - sb;
    });
  };

  // Drop onto In-flight = intent to RUN. No pre-drop popup: new bundles are
  // auto-triaged (decision=immediate — retag via the in-box toggle), everything
  // eligible moves to fixing, then the box AUTO-OPENS: the single-bundle drawer
  // (its run viewer) for ONE, or ONE combined-run box for TWO+. Daemon-batch gate
  // preserved (native/auth/schema stay manual). Backend hops stay legal:
  // new→triaged via /triage, then →fixing via /status.
  const applyInflightAndOpen = async (ids) => {
    let moveIds = ids.filter(id => statusOf(id) === 'new' || opsDropLegal('inflight', statusOf(id)));
    const skipped = ids.length - moveIds.length;
    const blocked = moveIds.filter(id => !isDaemonEligible(id));
    if (blocked.length) {
      const kinds = [...new Set(blocked.map(classOf))].join('/');
      flash(`${blocked.length} kept manual (${kinds}) — advance native/auth/schema via the drawer, not the daemon batch`);
    }
    moveIds = moveIds.filter(isDaemonEligible);
    const newSet = new Set(moveIds.filter(id => statusOf(id) === 'new'));
    if (!moveIds.length) { flash(`Can’t move to In-flight — not a legal move for the selected card(s)`); return; }
    // Optimistic: newly-triaged bundles record the default decision.
    setClusters(cs => cs.map(c => moveIds.includes(c.id)
      ? { ...c, status: 'fixing', ...(newSet.has(c.id) ? { decision: 'immediate' } : {}) } : c));
    const fn = async (id) => {
      if (newSet.has(id)) await opsApi.post(`/clusters/${id}/triage`, { decision: 'immediate' });
      await opsApi.post(`/clusters/${id}/status`, { status: 'fixing' });
    };
    const failed = await runOver(`Move → In-flight` + (skipped ? ` (${skipped} skipped)` : ''),
                                 moveIds, fn, { revertOnFail: true });
    if (failed) return;   // partial failure was flashed + reverted — never open a half-set box
    if (moveIds.length === 1) setOpenId(moveIds[0]);
    else setCombinedRun(orderForCombined(moveIds));
  };

  // ── drag handlers (desktop only) ────────────────────────────────────────
  const onDragStartCard = (e, c) => {
    const set = selected.has(c.id) ? new Set(selected) : new Set([c.id]);
    dragSetRef.current = set;   // P3: synchronous — dragover reads this immediately
    setDragSet(set);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(c.id));
      if (set.size > 1) {
        const ghost = document.createElement('div');
        ghost.className = 'ops-drag-ghost';
        ghost.textContent = `${set.size} bundles`;
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 10, 10);
        setTimeout(() => document.body.removeChild(ghost), 0);
      }
    } catch (err) { /* dataTransfer unavailable — drag still tracked via state */ }
  };
  const onDragEndCard = () => { dragSetRef.current = null; setDragSet(null); setDragOverCol(null); };
  const dropLegalCount = (colKey, set) =>
    set ? [...set].filter(id => opsDroppable(colKey, statusOf(id))).length : 0;
  const colDropLegalCount = (colKey) => dropLegalCount(colKey, dragSet); // render indicator (state)
  const onColDragOver = (colKey, e) => {
    // The HTML5 DnD contract: the browser fires `drop` on a target ONLY if
    // preventDefault() was called during its dragenter/dragover. So we ALWAYS
    // preventDefault while a drag is active (event delegation on the whole .ops-col,
    // robust to the 1- or 2-column inner grid; min-height makes the whole box a target).
    // Legality is decided ON DROP — NOT here. The prior code preventDefaulted only when
    // dropLegalCount>0, so any card with no legal target in this column (e.g. a 'new'
    // card, legal nowhere) got the native no-drop cursor and drop never fired → nothing
    // dropped anywhere. (Confirmed with a REAL native drag; synthetic dispatch had hidden
    // it because dispatched drop events ignore the preventDefault contract.)
    const set = dragSetRef.current || dragSet;
    if (!set) return;
    e.preventDefault();
    // dropEffect MUST NOT be 'none' — per the HTML5 spec, 'none' at drop time SUPPRESSES
    // the drop event (the browser rejects it before onDrop runs), which brings back the
    // native no-drop cursor and stops onDrop from adjudicating. So we always advertise
    // 'move'; illegality is conveyed by the column indicator (red highlight + NO badge)
    // and enforced ON DROP (onColDrop rejects with a flash, no persist).
    try { e.dataTransfer.dropEffect = 'move'; } catch (err) { /* ignore */ }
    // Anchor the indicator to the lane UNDER THE POINTER regardless of legality, so an
    // illegal target shows NO on itself (never a floating badge drifting to In-flight).
    if (dragOverCol !== colKey) setDragOverCol(colKey);
  };
  const onColDrop = (colKey, e) => {
    e.preventDefault();
    const set = dragSetRef.current || dragSet;
    if (!set) return;
    const ids = [...set];
    console.log('[ops-dnd] drop → %s · ids=%o · statuses=%o · classes=%o',
                colKey, ids, ids.map(statusOf), ids.map(classOf));
    // Legality decided ON DROP (single authority). All-illegal → reject with NO persist,
    // no chooser — the drop fired (preventDefault above) but nothing moves.
    const anyLegal = ids.some(id => opsDroppable(colKey, statusOf(id)));
    if (!anyLegal) {
      dragSetRef.current = null; setDragOverCol(null); setDragSet(null);
      flash(`Can’t move to ${OPS_COL_LABEL[colKey]} — ${OPS_DROP_HINT[colKey] || 'not a legal move for the selected card(s)'}`);
      return;
    }
    dragSetRef.current = null;
    setDragOverCol(null); setDragSet(null);
    if (colKey === 'triage') {
      // Inline chooser at the drop point → Immediate | Scheduled (drop onto the
      // merged Triage lane, incl. a reverse send-back from In-flight/Done).
      setTriageDrop({ ids, x: e.clientX, y: e.clientY, target: 'triage' });
    } else if (colKey === 'inflight') {
      // Drop = intent to run: auto-triage new (immediate), move to fixing, then
      // AUTO-OPEN the box (single drawer / combined-run). No pre-drop popup.
      applyInflightAndOpen(ids);
    } else {
      applyDrop(colKey, ids);
    }
  };

  const renderCard = (c) => (
    <OpsBundleCard
      key={c.id} c={c} onOpen={setOpenId}
      selectable
      selected={selected.has(c.id)}
      onToggleSelect={toggleSelect}
      draggable={!isMobile}
      onDragStartCard={onDragStartCard}
      onDragEndCard={onDragEndCard}
      dragging={dragSet && dragSet.has(c.id)}
      onArchive={(id) => archiveIds([id])}
    />
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <OpsChip onClick={() => setFilter('all')} active={filter === 'all'}>All ({loading ? '…' : cl.length})</OpsChip>
        <OpsChip onClick={() => setFilter('next-batch')} active={filter === 'next-batch'} color="#16a34a">
          Next batch ({nextBatch.length})
        </OpsChip>
        <button className="ops-btn ops-btn-small" onClick={() => setShowArchived(s => !s)}
                title="View / restore soft-archived bundles (nothing is ever hard-deleted)">
          {showArchived ? '▾' : '▸'} Archived ({archived.length})
        </button>
        <button className="ops-btn ops-btn-small" title="Rewrite bundle titles/summaries in plain English (Gemini; falls back to raw)"
                onClick={async () => { try { await opsApi.post('/clusters/humanize', {}); await load(); } catch (e) { setError(e.message); } }}>
          ✨ Humanize
        </button>
        {isMobile && groups.map(g => (
          <OpsChip key={g.key} onClick={() => setFilter(g.key)} active={filter === g.key}>
            {g.label} ({g.items.length})
          </OpsChip>
        ))}
        {!isMobile && <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 4 }}>
          Tap ○ to select (shift-tap for a range) · drag cards between columns
        </span>}
        {isMobile && <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 4 }}>
          Tap ○ to select; use the action bar (drag is desktop-only)
        </span>}
        {isMobile && (
          <button className="ops-btn ops-btn-small" style={{ marginLeft: 'auto' }}
                  disabled={!colItems('inflight').length}
                  title="Merge the In-flight bundles (selected, or all) into one CC prompt"
                  onClick={startPrepare}>
            ⚙ Prepare in-flight{inflightSelCount ? ` (${inflightSelCount})` : ''}
          </button>
        )}
      </div>
      {showArchived && (
        <div className="ops-archived-panel">
          <div className="ops-archived-head">Archived ({archived.length}) — soft-archived, fully restorable (never hard-deleted)</div>
          {archived.length === 0 ? <div className="ops-empty">Nothing archived.</div>
            : archived.map(x => (
              <div key={x.id} className="ops-archived-row">
                <span className="ops-arch-title">#{x.id} {x.title}</span>
                <span className="ops-arch-meta">{x.status}{x.archived_at ? ` · archived ${formatDate(x.archived_at)}` : ''}</span>
                <button className="ops-btn ops-btn-small" onClick={() => restoreBundle(x.id)}>↩ Restore</button>
              </div>
            ))}
        </div>
      )}
      {!isMobile && <OpsClassLegend />}
      {filter === 'next-batch' ? (
        nextBatch.length === 0
          ? <div className="ops-empty">Next batch is empty — triage a bundle to 'scheduled' or Build Now a horizon item.</div>
          : nextBatch.map(renderCard)
      ) : isMobile ? (
        (filter === 'all' ? cl : colItems(filter)).map(renderCard)
      ) : (
        <div className="ops-columns">
          {groups.map(g => {
            const selState = colSelState(g.key);
            const legalN = dragSet ? colDropLegalCount(g.key) : null;
            const isOver = dragSet && dragOverCol === g.key;
            const overLegal = isOver && legalN > 0;      // pointer over a valid target
            const overBlocked = isOver && legalN === 0;  // pointer over an illegal target
            const droppable = dragSet && !isOver && legalN > 0; // candidate-target hint
            return (
              <div
                key={g.key}
                className={`ops-col${g.wide ? ' ops-col-wide' : ''}${overLegal ? ' ops-col-over' : ''}${overBlocked ? ' ops-col-over-blocked' : ''}${droppable ? ' ops-col-droppable' : ''}`}
                onDragEnter={(e) => onColDragOver(g.key, e)}
                onDragOver={(e) => onColDragOver(g.key, e)}
                onDragLeave={() => setDragOverCol(o => o === g.key ? null : o)}
                onDrop={(e) => onColDrop(g.key, e)}
                title={overBlocked ? OPS_DROP_HINT[g.key] : undefined}
              >
                <div className="ops-col-head">
                  {!g.batchLane && (
                    <button
                      type="button"
                      className={`ops-sel-circle ops-sel-all${selState === 'all' ? ' on' : ''}${selState === 'some' ? ' ind' : ''}`}
                      title="Select all / none in this column"
                      disabled={!g.items.length}
                      onClick={() => toggleSelectAll(g.key)}
                    >{selState === 'all' ? '✓' : selState === 'some' ? '–' : ''}</button>
                  )}
                  {g.label} <span style={{ color: 'var(--text-dim)' }}>({loading ? '…' : (g.batchLane ? (g.batches || []).length : g.items.length)})</span>
                  {loading && <span className="ops-col-loading">loading…</span>}
                  {!loading && g.key === 'inflight' && g.items.length > 0 && !dragSet && (
                    <button className="ops-btn ops-btn-small" style={{ marginLeft: 'auto' }}
                            title="Merge the In-flight bundles (selected, or all) into one CC prompt"
                            onClick={startPrepare}>
                      ⚙ Prepare{inflightSelCount ? ` (${inflightSelCount})` : ''}
                    </button>
                  )}
                  {isOver && (legalN > 0
                    ? <span className="ops-drop-ok">drop ok</span>
                    : <span className="ops-drop-no" title={OPS_DROP_HINT[g.key]}>no</span>)}
                </div>
                {loading
                  ? [0, 1, 2].map(i => <div key={i} className="ops-skel ops-skel-card" />)
                  : g.batchLane
                  ? (<div className="ops-col-cards">
                      {(g.batches || []).length === 0 &&
                        <div className="ops-empty">No batches — drag to In-flight, pick Scheduled, add to a batch.</div>}
                      {(g.batches || []).map(b => <OpsBatchCard key={b.id} b={b} onChanged={load} flash={flash} />)}
                    </div>)
                  : (<div className="ops-col-cards">
                      {g.items.length === 0 && <div className="ops-empty">—</div>}
                      {g.items.map(renderCard)}
                    </div>)}
              </div>
            );
          })}
        </div>
      )}
      {openId && <OpsBundleDrawer clusterId={openId} onClose={() => setOpenId(null)} onChanged={load} canRunSpike={isSuperadmin} />}
      {combinedRun && <OpsCombinedRunModal initialIds={combinedRun} clusters={cl}
                                           isSuperadmin={isSuperadmin}
                                           onClose={() => setCombinedRun(null)} onChanged={load} />}
      <OpsJobChips onOpen={setJobModal} />
      {jobModal && <OpsJobModal jobId={jobModal} onClose={() => setJobModal(null)} />}
      {selected.size > 0 && (
        <OpsBulkBar selectedIds={selected} statusOf={statusOf} onAction={bulkAction} onClear={clearSel} busy={bulkBusy} />
      )}
      {triageDrop && ReactDOM.createPortal(
        <div className="ops-overlay-scrim" onClick={() => setTriageDrop(null)}>
          <div className="ops-triage-chooser"
               style={{ position: 'fixed', left: Math.min(triageDrop.x, window.innerWidth - 220), top: Math.min(triageDrop.y, window.innerHeight - 120) }}
               onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              Triage {triageDrop.ids.length > 1 ? `${triageDrop.ids.length} bundles` : 'bundle'} as:
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ops-btn ops-btn-primary ops-btn-small"
                      onClick={() => { const ids = triageDrop.ids; setTriageDrop(null); applyDrop('triage', ids, 'immediate'); }}>Immediate</button>
              <button className="ops-btn ops-btn-primary ops-btn-small"
                      onClick={() => { const ids = triageDrop.ids; setTriageDrop(null); applyDrop('triage', ids, 'scheduled'); }}>Scheduled</button>
              <button className="ops-btn ops-btn-small" onClick={() => setTriageDrop(null)}>Cancel</button>
            </div>
          </div>
        </div>, document.body)}
      {toast && <div className="ops-toast" role="status">{toast}</div>}
      {undo && undo.ids && undo.ids.length > 0 && ReactDOM.createPortal(
        <div className="ops-toast ops-undo-toast" role="status">
          <span>Archived {undo.ids.length}</span>
          <button className="ops-btn ops-btn-small" onClick={doUndo}>↩ Undo</button>
        </div>, document.body)}
    </div>
  );
}

// Horizon v1 — a scanned finding is moat-judged in free text; tint the moat line
// by the sentiment word it carries (threat/reinforce/neutral) for a fast read.
function _horizonMoatTone(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('threat')) return { color: OPS_MOAT_COLOR.threat, label: 'Threat' };
  if (t.includes('reinforce')) return { color: OPS_MOAT_COLOR.reinforce, label: 'Reinforce' };
  if (t.includes('neutral')) return { color: OPS_MOAT_COLOR.neutral, label: 'Neutral' };
  return { color: 'var(--text-dim)', label: 'Moat' };
}

function OpsHorizonFindingCard({ finding, onPromote, onDismiss, onRestore, busy }) {
  const [showEv, setShowEv] = useState(false);
  const ev = Array.isArray(finding.evidence) ? finding.evidence : [];
  const tone = _horizonMoatTone(finding.moat_relevance);
  const promoted = finding.status === 'promoted';
  const dismissed = finding.status === 'dismissed';
  const bid = `promote-${finding.id}`, did = `dismiss-${finding.id}`;
  return (
    <div className="ops-card" style={dismissed ? { opacity: 0.6 } : undefined}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        <OpsChip color={tone.color}>{tone.label}</OpsChip>
        {promoted && <OpsChip color="#0891b2">In Triage · bundle #{finding.promoted_cluster_id}</OpsChip>}
        {dismissed && <OpsChip color="#6b7280">Dismissed</OpsChip>}
        <span style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 'auto' }}>
          {ev.length} source{ev.length === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{finding.title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-primary)', margin: '6px 0', lineHeight: 1.45 }}>{finding.summary}</div>
      {finding.moat_relevance && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0', borderLeft: `3px solid ${tone.color}`, paddingLeft: 8 }}>
          <b style={{ color: tone.color }}>Moat:</b> {finding.moat_relevance}
        </div>
      )}
      {finding.suggested_response && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0' }}>
          <b>Suggested response:</b> {finding.suggested_response}
        </div>
      )}
      {ev.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <button className="ops-btn ops-btn-small" onClick={() => setShowEv(v => !v)}>
            {showEv ? '▾ Hide evidence' : `▸ Evidence (${ev.length})`}
          </button>
          {showEv && (
            <div style={{ marginTop: 6, fontSize: 11.5, overflowWrap: 'anywhere' }}>
              {ev.map((e, i) => (
                <div key={i} style={{ marginBottom: 6, paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
                  <a href={e.source_url} target="_blank" rel="noreferrer">{e.source_title || e.source_url}</a>
                  {e.date ? <span style={{ color: 'var(--text-dim)' }}> · {e.date}</span> : null}
                  {e.snippet ? <div style={{ color: 'var(--text-dim)', marginTop: 2 }}>{e.snippet}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {!promoted && !dismissed && (
          <>
            <button className="ops-btn ops-btn-primary" disabled={busy === bid}
                    onClick={() => onPromote(finding.id)}>
              {busy === bid ? 'Promoting…' : '→ Triage'}
            </button>
            <button className="ops-btn" disabled={busy === did} onClick={() => onDismiss(finding.id)}>
              {busy === did ? 'Dismissing…' : 'Dismiss'}
            </button>
          </>
        )}
        {promoted && <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>Promoted to a Triage bundle — see the Bundles board.</span>}
        {dismissed && (
          <button className="ops-btn ops-btn-small" disabled={busy === `restore-${finding.id}`}
                  onClick={() => onRestore(finding.id)}>Restore</button>
        )}
      </div>
    </div>
  );
}

// P4 — the editable moat/strategy spine the scan reads as grounding. Collapsible.
function OpsHorizonSpine({ onSaved }) {
  const [spine, setSpine] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    opsApi.get('/horizon/strategy')
      .then(j => setSpine((j.strategy && j.strategy.spine_md) || ''))
      .catch(e => setErr(e.message));
  }, []);
  const save = async () => {
    setSaving(true); setErr(null);
    try { await opsApi.call('/horizon/strategy', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spine_md: spine }) }); onSaved && onSaved(); }
    catch (e) { setErr(e.message); }
    setSaving(false);
  };
  if (spine === null && !err) return <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 8 }}>Loading spine…</div>;
  return (
    <div style={{ marginTop: 8 }}>
      {err && <div style={{ fontSize: 11.5, color: OPS_MOAT_COLOR.threat }}>⚠ {err}</div>}
      <textarea className="ops-input" rows={10} value={spine || ''} onChange={e => setSpine(e.target.value)}
                style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, lineHeight: 1.45 }} />
      <div style={{ fontSize: 11, color: 'var(--text-dim)', margin: '4px 0' }}>
        The scan judges every finding against this. Keep it current so suggestions defend the moat, not chase noise.
      </div>
      <button className="ops-btn ops-btn-primary ops-btn-small" disabled={saving} onClick={save}>
        {saving ? 'Saving…' : 'Save spine'}
      </button>
    </div>
  );
}

function OpsHorizonTab() {
  const [data, setData] = useState(null);   // { groups, new_count, last_scan }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [scanTarget, setScanTarget] = useState('');
  const [scanning, setScanning] = useState(false);
  const [spineOpen, setSpineOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const status = showDismissed ? 'dismissed' : '';
      const j = await opsApi.get('/horizon/findings' + (status ? `?status=${status}` : ''));
      setData(j);
    } catch (e) { setError(e.message); }
  }, [showDismissed]);
  useEffect(() => { load(); }, [load]);

  const toast = (m, ms = 3500) => { setToastMsg(m); setTimeout(() => setToastMsg(null), ms); };

  const scanNow = async () => {
    setScanning(true); setError(null);
    try {
      const j = await opsApi.post('/horizon/scan', { target_text: scanTarget });
      const per = (j.per_strand || []).map(s => s.error ? `${s.strand} ✗` : `${s.strand}:${s.new}`).join(' · ');
      toast(`Scan ${j.status}: ${j.findings_new} new finding${j.findings_new === 1 ? '' : 's'}${per ? ' — ' + per : ''}`, 6000);
      setScanTarget('');
      await load();
    } catch (e) { setError(e.message); }
    setScanning(false);
  };

  const promote = async (id) => {
    setBusy(`promote-${id}`); setError(null);
    try {
      const j = await opsApi.post(`/horizon/findings/${id}/promote`, {});
      toast(j.already ? `Already in Triage (bundle #${j.linked_cluster_id})`
                      : `→ Triage as bundle #${j.linked_cluster_id} (see Bundles board)`, 4500);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };
  const dismiss = async (id) => {
    setBusy(`dismiss-${id}`); setError(null);
    try { await opsApi.post(`/horizon/findings/${id}/dismiss`, {}); await load(); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };
  const restore = async (id) => {
    setBusy(`restore-${id}`); setError(null);
    try { await opsApi.post(`/horizon/findings/${id}/restore`, {}); await load(); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };

  const groups = (data && data.groups) || [];
  const shown = groups.filter(g => (g.findings || []).length > 0);
  const totalFindings = groups.reduce((n, g) => n + (g.findings || []).length, 0);

  return (
    <div>
      <OpsErrorBanner error={error} onRetry={load} onDismiss={() => setError(null)} />
      {toastMsg && <div className="ops-toast">{toastMsg}</div>}

      <div className="ops-card">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="ops-input" style={{ flex: 1, minWidth: 200 }} disabled={scanning}
                 placeholder="Optional target — a competitor, theme, or question to focus this scan…"
                 value={scanTarget} onChange={e => setScanTarget(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter' && !scanning) scanNow(); }} />
          <button className="ops-btn ops-btn-primary" disabled={scanning} onClick={scanNow}>
            {scanning ? 'Scanning the web…' : '⟳ Scan now'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
          Scans all active strands against the strategy spine (grounded web search). A weekly auto-scan also runs.
        </div>
        <button className="ops-btn ops-btn-small" style={{ marginTop: 8 }} onClick={() => setSpineOpen(v => !v)}>
          {spineOpen ? '▾ Strategy spine' : '▸ Strategy spine'}
        </button>
        {spineOpen && <OpsHorizonSpine onSaved={() => toast('Strategy spine saved — next scan uses it.')} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 2px 12px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {data ? `${data.new_count} new · ${totalFindings} in view` : 'Loading…'}
        </div>
        {data && data.last_scan && data.last_scan.finished_at && (
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            last scan {formatDate(data.last_scan.finished_at)} · {data.last_scan.status}
          </div>
        )}
        <label style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-dim)', display: 'flex', gap: 5, alignItems: 'center' }}>
          <input type="checkbox" checked={showDismissed} onChange={(e) => setShowDismissed(e.target.checked)} />
          Dismissed
        </label>
      </div>

      {data === null ? <OpsSkeleton rows={4} /> : (
        totalFindings === 0
          ? <div className="ops-empty">{showDismissed ? 'No dismissed findings.' : 'No findings yet — run a scan to populate the Horizon knowledge base.'}</div>
          : shown.map(g => (
            <div key={g.strand_key} style={{ marginBottom: 16 }}>
              <div className="ops-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{g.label || g.strand_key}</span>
                <span style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-dim)' }}>
                  {(g.findings || []).length} finding{(g.findings || []).length === 1 ? '' : 's'}
                  {g.last_scanned_at ? ` · scanned ${formatDate(g.last_scanned_at)}` : ' · never scanned'}
                </span>
              </div>
              {g.findings.map(f => (
                <OpsHorizonFindingCard key={f.id} finding={f} onPromote={promote}
                                       onDismiss={dismiss} onRestore={restore} busy={busy} />
              ))}
            </div>
          ))
      )}
    </div>
  );
}

function OpsKBTab() {
  const [articles, setArticles] = useState(null);
  const [changelogRows, setChangelogRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [a, cl] = await Promise.all([opsApi.get('/kb'), opsApi.get('/changelog')]);
      setArticles(a.articles); setChangelogRows(cl.changelog);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <OpsErrorBanner error={error} onRetry={load} />;
  if (articles === null) return <OpsSkeleton rows={4} />;
  return (
    <div>
      <div className="ops-section-title">Knowledge base</div>
      {articles.length === 0 && <div className="ops-empty">No KB articles yet — close a verified bundle to create one.</div>}
      {articles.map(a => (
        <div key={a.id} className="ops-card">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 600, flex: 1, color: 'var(--text-primary)' }}>{a.title}</div>
            <OpsChip color={a.published ? '#16a34a' : '#6b7280'}>{a.published ? 'published' : 'draft'}</OpsChip>
          </div>
          {a.problem && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}><strong>Problem:</strong> {a.problem}</div>}
          {a.root_cause && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}><strong>Root cause:</strong> {a.root_cause}</div>}
          {a.resolution && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}><strong>Resolution:</strong> {a.resolution}</div>}
          <button className="ops-btn ops-btn-small" style={{ marginTop: 8 }}
                  onClick={async () => { try { await opsApi.post(`/kb/${a.id}/publish`); load(); } catch (e) { setError(e.message); } }}>
            {a.published ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      ))}
      <div className="ops-section-title" style={{ marginTop: 16 }}>Changelog (published)</div>
      {(changelogRows || []).length === 0
        ? <div className="ops-empty">Nothing published yet.</div>
        : changelogRows.map(r => (
          <div key={r.id} style={{ fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--text-dim)', marginRight: 8 }}>{formatDate(r.created_at)}</span>{r.changelog_line}
          </div>
        ))}
    </div>
  );
}

// ── Usage Analytics tab (5th Ops Console tab, superadmin only) ────────────────
// Reads the superadmin-gated /api/usage/* aggregates (operator already excluded
// server-side). Small-N honesty is surfaced, not hidden: raw counts lead, ratios
// wear a "directional · N small" badge. No chart library (Recharts NaN-domain has
// white-screened this app before) — plain CSS bars/grid, all scroll contained.
const usageApi = {
  get: async (path) => {
    // KAL-262
    const r = await _abortableFetch(`${API_BASE}/usage${path}`, { credentials: 'include' }, LOAD_TIMEOUT_MS, `usage ${path}`);
    let j = null;
    try { j = await r.json(); } catch (e) { /* non-JSON */ }
    if (!r.ok) {
      const err = new Error((j && (j.error || j.detail)) || `HTTP ${r.status}`);
      err.status = r.status;
      throw err;
    }
    return j;
  },
};

function UsageNSmallBadge({ show }) {
  if (!show) return null;
  return <OpsChip color="#d97706" title="Counts are reliable; ratios are not statistically meaningful at pilot scale.">directional · N small</OpsChip>;
}

function UsageSummaryCards({ summary }) {
  const cards = [
    { k: 'dau', label: 'DAU', sub: 'active · 24h' },
    { k: 'wau', label: 'WAU', sub: 'active · 7d' },
    { k: 'mau', label: 'MAU', sub: 'active · 30d' },
    { k: 'total_users', label: 'Users', sub: 'all time' },
  ];
  return (
    <div className="usage-cards">
      {cards.map(c => (
        <div key={c.k} className="usage-card">
          <div className="usage-card-num">{summary[c.k] != null ? summary[c.k] : '—'}</div>
          <div className="usage-card-label">{c.label}</div>
          <div className="usage-card-sub">{c.sub}</div>
        </div>
      ))}
      <div className="usage-card">
        <div className="usage-card-num">{summary.stickiness != null ? `${Math.round(summary.stickiness * 100)}%` : '—'}</div>
        <div className="usage-card-label">Stickiness</div>
        <div className="usage-card-sub">DAU/MAU</div>
      </div>
    </div>
  );
}

function UsageActiveChart({ summary }) {
  const rows = [
    { label: 'DAU', v: summary.dau || 0 },
    { label: 'WAU', v: summary.wau || 0 },
    { label: 'MAU', v: summary.mau || 0 },
  ];
  const max = Math.max(1, ...rows.map(r => r.v));
  return (
    <div className="usage-barset">
      {rows.map(r => (
        <div key={r.label} className="usage-bar-row">
          <div className="usage-bar-key">{r.label}</div>
          <div className="usage-bar-track">
            <div className="usage-bar-fill" style={{ width: `${(r.v / max) * 100}%` }} />
          </div>
          <div className="usage-bar-val">{r.v}</div>
        </div>
      ))}
    </div>
  );
}

const USAGE_USER_COLS = [
  { k: 'identity', label: 'User', numeric: false },
  { k: 'active_days', label: 'Days', numeric: true },
  { k: 'event_count', label: 'Events', numeric: true },
  { k: 'days_since_seen', label: 'Last seen (d)', numeric: true },
  // KAL-041(b) — the column is relabelled rather than the METRIC redefined. ACTIVATION_EVENT is
  // 'document_uploaded' (usage_analytics.py:343). Changing the event would silently redefine a
  // number Tim has been reading for weeks and break comparability with the monthly reports, so
  // the label now says what it actually measures.
  { k: 'activation_status', label: 'Uploaded a doc', numeric: false },
];

function UsagePerUserTable({ users }) {
  const [sortK, setSortK] = useState('days_since_seen');
  const [asc, setAsc] = useState(true);
  const sorted = [...users].sort((a, b) => {
    let x = a[sortK], y = b[sortK];
    if (x == null) x = asc ? Infinity : -Infinity;
    if (y == null) y = asc ? Infinity : -Infinity;
    if (typeof x === 'string') return asc ? String(x).localeCompare(y) : String(y).localeCompare(x);
    return asc ? x - y : y - x;
  });
  const setSort = (k) => { if (k === sortK) setAsc(a => !a); else { setSortK(k); setAsc(k === 'identity'); } };
  if (!users.length) return <div className="ops-empty">No users tracked yet.</div>;
  return (
    <div className="usage-tablewrap">
      <table className="usage-table">
        <thead>
          <tr>{USAGE_USER_COLS.map(c => (
            <th key={c.k} onClick={() => setSort(c.k)} className={c.numeric ? 'usage-num' : ''}>
              {c.label}{sortK === c.k ? (asc ? ' ▲' : ' ▼') : ''}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {sorted.map((u, i) => (
            <tr key={u.identity + i} className={u.needs_nudge ? 'usage-row-nudge' : ''}>
              <td title={u.identity}>
                {u.is_anonymous ? <span className="usage-anon">anon</span> : null} {u.display_name || u.identity}
              </td>
              <td className="usage-num">{u.active_days}</td>
              <td className="usage-num">{u.event_count}</td>
              <td className="usage-num">{u.days_since_seen != null ? u.days_since_seen : '—'}</td>
              <td>
                <span className={u.activation_status === 'activated' ? 'usage-pill-on' : 'usage-pill-off'}>
                  {u.activation_status === 'activated' ? 'uploaded' : 'none yet'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageRetentionGrid({ retention }) {
  const cohorts = retention.cohorts || [];
  if (!cohorts.length) return <div className="ops-empty">No cohorts yet.</div>;
  const heat = (rate) => {
    if (rate == null) return 'transparent';
    const a = 0.12 + rate * 0.55;
    return `rgba(90, 200, 216, ${a.toFixed(2)})`;
  };
  return (
    <div className="usage-tablewrap">
      <table className="usage-table usage-cohort">
        <thead>
          <tr>
            <th>Cohort (wk)</th><th className="usage-num">Size</th>
            {retention.offsets.map(o => <th key={o} className="usage-num">W{o}</th>)}
          </tr>
        </thead>
        <tbody>
          {cohorts.map(c => (
            <tr key={c.cohort_week}>
              <td>{c.cohort_week}</td>
              <td className="usage-num">{c.size}</td>
              {c.retained.map((n, i) => (
                <td key={i} className="usage-num" style={{ background: heat(c.rates[i]) }}
                    title={c.rates[i] != null ? `${Math.round(c.rates[i] * 100)}%` : ''}>
                  {n}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageFunnel({ funnel }) {
  const stages = funnel.stages || [];
  const top = Math.max(1, ...stages.map(s => s.n));
  return (
    <div className="usage-funnel">
      {stages.map(s => (
        <div key={s.key} className="usage-funnel-row">
          <div className="usage-funnel-label">{s.label}</div>
          <div className="usage-bar-track">
            <div className="usage-bar-fill usage-funnel-fill" style={{ width: `${(s.n / top) * 100}%` }} />
          </div>
          <div className="usage-bar-val">{s.n}{s.pct_of_opened != null ? ` · ${Math.round(s.pct_of_opened * 100)}%` : ''}</div>
        </div>
      ))}
    </div>
  );
}

function UsageFeatureBars({ features }) {
  const rows = features.features || [];
  if (!rows.length) return <div className="ops-empty">No feature events yet.</div>;
  const max = Math.max(1, ...rows.map(r => r.events));
  return (
    <div className="usage-barset">
      {rows.map(r => (
        <div key={r.event_name} className="usage-bar-row">
          <div className="usage-bar-key usage-bar-key-wide" title={r.event_name}>{r.event_name}</div>
          <div className="usage-bar-track">
            <div className="usage-bar-fill" style={{ width: `${(r.events / max) * 100}%` }} />
          </div>
          <div className="usage-bar-val">{r.events} · {r.users}u</div>
        </div>
      ))}
    </div>
  );
}

function UsageNudgeList({ users, nudged, onNudge }) {
  const needs = (users || []).filter(u => u.needs_nudge);
  if (!needs.length) return <div className="ops-empty">No one needs a nudge — everyone active in the last 3 days.</div>;
  return (
    <div>
      {needs.map((u, i) => (
        <div key={u.identity + i} className="ops-card usage-nudge-card">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{u.display_name || u.identity}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
              last seen {u.days_since_seen}d ago · {u.activation_status === 'activated' ? 'activated' : 'not activated'}
            </div>
          </div>
          {nudged[u.identity]
            ? <span className="usage-pill-on">nudged ✓</span>
            : <button className="ops-btn ops-btn-small" onClick={() => onNudge(u.identity)}>Mark nudged</button>}
        </div>
      ))}
    </div>
  );
}

function OpsUsageTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nudged, setNudged] = useState({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const [summary, users, retention, funnel, features] = await Promise.all([
        usageApi.get('/summary'), usageApi.get('/users'), usageApi.get('/retention'),
        usageApi.get('/funnel'), usageApi.get('/features'),
      ]);
      setData({ summary, users: users.users || [], retention, funnel, features });
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markNudged = (ident) => {
    // Logs a nudge_sent event via the same telemetry path. Attributed to the
    // operator's session (so it's excluded from engagement metrics — correct;
    // it's an operator action). Per-target association is a Stage-2 concern.
    usageAnalytics.track('nudge_sent', { source: 'ops_console' });
    usageAnalytics.flush(true);
    setNudged(n => ({ ...n, [ident]: true }));
  };

  if (error) return <OpsErrorBanner error={error} onRetry={load} onDismiss={() => setError(null)} />;
  if (data === null) return <OpsSkeleton rows={5} />;
  const { summary, users, retention, funnel, features } = data;

  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginBottom: 10 }}>
        Operator activity excluded. {summary.n_small ? summary.n_small_note : ''}
      </div>

      <div className="ops-section-title" style={{ marginTop: 0 }}>
        Overview <UsageNSmallBadge show={summary.n_small} />
      </div>
      <UsageSummaryCards summary={summary} />

      <div className="ops-section-title">Active users</div>
      <div className="ops-card"><UsageActiveChart summary={summary} /></div>

      <div className="ops-section-title">Per-user engagement</div>
      <UsagePerUserTable users={users} />

      <div className="ops-section-title">
        Activation funnel <UsageNSmallBadge show={funnel.n_small} />
      </div>
      <div className="ops-card"><UsageFunnel funnel={funnel} /></div>

      <div className="ops-section-title">
        Weekly retention <UsageNSmallBadge show={retention.n_small} />
      </div>
      <div className="usage-note">{retention.note}</div>
      <UsageRetentionGrid retention={retention} />

      <div className="ops-section-title">Feature usage</div>
      <div className="ops-card">
        <UsageFeatureBars features={features} />
        {/* KAL-041(c) — this endpoint uses _excl() (anon rows pass) while DAU/WAU/MAU, the user
            table and retention use _excl_auth() (authenticated only). _IDENTITY COALESCEs each
            anonymous_id into its own identity, so the user counts here include pre-login visitors
            and CANNOT be compared with the authenticated total above. Stated on the surface
            because the discrepancy (50 vs 10) otherwise reads as a bug. */}
        <div style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-dim, #8896a8)' }}>
          Users here counts <strong>distinct identities including anonymous pre-login visitors</strong>
          {' '}(each new browser or cleared cookie is one identity). Every other panel on this tab counts
          authenticated users only — so this column is deliberately not comparable with the all-time
          user total.
        </div>
      </div>

      <div className="ops-section-title">Return-nudge list (≥3 days unseen)</div>
      <UsageNudgeList users={users} nudged={nudged} onNudge={markNudged} />
    </div>
  );
}

function OpsConsolePage({ isSuperadmin }) {
  const readTab = () => {
    const m = (window.location.hash || '').match(/[?&]tab=([\w-]+)/);
    return m && ['inbox', 'bundles', 'horizon', 'kb', 'usage'].includes(m[1]) ? m[1] : 'inbox';
  };
  const [tab, setTabState] = useState(readTab);
  const [counts, setCounts] = useState(null);
  const setTab = (t) => {
    setTabState(t);
    try { window.history.replaceState(null, '', `#ops-console${t === 'inbox' ? '' : `?tab=${t}`}`); } catch (e) { /* hash cosmetic only */ }
  };
  const onCounts = useCallback((c) => setCounts(c), []);
  const tabs = [
    { key: 'inbox', label: 'Inbox', badge: counts ? counts.unclustered : null },
    { key: 'bundles', label: 'Bundles', badge: counts ? counts.open_clusters : null },
    { key: 'horizon', label: 'Horizon', badge: counts ? counts.horizon_new : null },
    { key: 'kb', label: 'KB', badge: null },
    { key: 'usage', label: 'Usage', badge: null },
  ];
  return (
    <div className="ops-page">
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Ops Console</h2>
        <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>
          {counts
            ? `${counts.unclustered} new signals · ${counts.open_clusters} open bundles · ${counts.horizon_new} horizon new`
            : 'Signals import on Inbox sync'}
        </div>
      </div>
      <div className="ops-tabs" role="tablist">
        {tabs.map(t => (
          <button key={t.key} role="tab" aria-selected={tab === t.key}
                  className={`ops-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}{t.badge != null && t.badge > 0 ? ` (${t.badge})` : ''}
          </button>
        ))}
      </div>
      {tab === 'inbox' && <OpsInboxTab onCounts={onCounts} />}
      {tab === 'bundles' && <OpsBundlesTab onCounts={onCounts} isSuperadmin={isSuperadmin} />}
      {tab === 'horizon' && <OpsHorizonTab />}
      {tab === 'kb' && <OpsKBTab />}
      {tab === 'usage' && <OpsUsageTab />}
    </div>
  );
}

// Route guard: only a superadmin on the NPL instance may see the console.
// Anyone else hitting #ops-console lands back on Today (once the profile
// has resolved — never redirect on a still-loading profile).
function OpsConsoleGuard({ profile, navTo }) {
  const allowed = FEATURES_GLOBAL.branding.instance === 'npl' && profile?.admin_role === 'superadmin';
  useEffect(() => {
    if (profile && !allowed) navTo('today');
  }, [profile, allowed, navTo]);
  if (!profile) return <LoadingSpinner message="Loading..." />;
  return allowed ? <OpsConsolePage isSuperadmin={allowed} /> : null;
}

function AccountMenu({ user, profile, currentView, onNav, onLogout, variant = 'compact' }) {
  // variant='compact': just an avatar button (mobile header chrome).
  // variant='labelled': avatar + display-name + chevron — meant for
  // the desktop sidebar where the user has the space and shouldn't
  // have to hunt for it as an unlabeled 32px circle.
  //
  // ⚠ KAL-245 — THIS HOOK IS WHY THE ORDER-MANAGEMENT ITEM WAS INVISIBLE.
  // The menu's items are gated on FEATURES_GLOBAL, which is a PLAIN MUTABLE OBJECT, not React
  // state. Writing to it when /api/features resolves triggers NO re-render. So a menu that
  // first rendered before that fetch returned kept `order_lab === false` forever, and the
  // entry never appeared — for a superadmin, on an instance where the flag was measurably 1.
  // useFeatures() subscribes to FEATURES_LISTENERS and forces the re-render. Same failure
  // class the comment on showUserMgmt below already describes, in the same component.
  useFeatures();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on click outside or Escape.
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = profile?.displayName || profile?.name || user?.name || user?.email || 'User';
  const email = profile?.email || user?.email || '';
  const showSwitcher = !!profile?.has_family_accounts;
  // NPL Corporate Health used to live in the bottom nav, but on small
  // Android screens 7 items overflowed. Surfaced here instead for any
  // admin (admin_role !== null OR legacy is_admin=1) on the NPL
  // instance. Non-admins never saw it; nothing changes for them.
  const showNplCorporate = FEATURES_GLOBAL.branding.instance === 'npl'
    && (profile?.is_admin || profile?.admin_role === 'admin' || profile?.admin_role === 'superadmin');
  // Lab Upload is also visible to the lab_staff role (lab-upload only) — not just
  // general admins/superadmin. (Corporate Health stays admin/superadmin above.)
  // 2g — ROLE ONLY, no instance gate. The server already grants this to lab_staff/admin/
  // superadmin on every instance (require_lab_staff), and CLAUDE.md records lab_upload as
  // CORE and de-flagged ("role-gated"). Gating the menu on branding.instance === 'npl' was
  // an INSTANCE-NAME gate — the exact thing the flag normalisation forbids — and its effect
  // was that Tim had working lab-upload access on US with no way to reach it. The server
  // remains the boundary; this only decides whether the entry point is drawn.
  const showLabUpload = (profile?.is_admin
    || ['admin', 'superadmin', 'lab_staff'].includes(profile?.admin_role));
  // Ops Console — STRICTLY superadmin (server enforces the same gate on every
  // /api/admin/ops route). Hidden entirely for admin/lab_staff/regular users.
  const showOpsConsole = FEATURES_GLOBAL.branding.instance === 'npl'
    && profile?.admin_role === 'superadmin';
  // Underwriting (Section 4) — flag AND superadmin. This only decides whether the MENU
  // ITEM is drawn; the route and every fetch behind it are gated server-side, so hiding
  // the entry is convenience, never the security boundary.
  // Two disjoint ways in, and they gate on DIFFERENT flags on purpose:
  //   * the REAL plane  — insurance_export + superadmin (unchanged);
  //   * the SANDBOX     — underwriter_role + the underwriter role (KAL-200).
  // master-test carries insurance_export=0 so it can stay NPL-shaped for the compliance
  // guard, which is precisely why the sandbox could not reuse that flag.
  // Menu visibility is convenience only — every route behind it is gated server-side, and
  // an underwriter session is additionally confined by the before_request wall.
  const showUnderwriting = (!!FEATURES_GLOBAL.insurance_export
      && profile?.admin_role === 'superadmin')
    || (!!FEATURES_GLOBAL.underwriter_role && profile?.admin_role === 'underwriter');
  // KAL-212 — an underwriter SESSION, as distinct from "the underwriting surface is
  // available". A superadmin who can reach Underwriting is still a consumer of their own
  // account and keeps their settings; an underwriter is not, and has none.
  const isUnderwriterSession = profile?.admin_role === 'underwriter';
  // User Management — admin OR superadmin (server enforces the same gate + the
  // per-capability role split). Separate from the Ops Console (which is
  // superadmin-only and unrelated).
  // FIX: role-gated on EVERY instance. Was keyed to instance === 'npl' — the accidental
  // instance-axis bug (same class as the console gate) that hid User Management on UK/US even
  // for superadmins. Backend already enforces the role gate (@require_admin + _um_is_super).
  const showUserMgmt = (profile?.is_admin || profile?.admin_role === 'admin' || profile?.admin_role === 'superadmin');
  // KAL-235 — Order Management. Deliberately NOT showUserMgmt: `lab_admin` is the narrow role
  // Bimal holds, which reaches the order catalogue and nothing else. Reusing the user-mgmt gate
  // would have meant giving him account administration to let him edit a price.
  const showOrderMgmt = FEATURES_GLOBAL.order_lab
    && (profile?.is_admin || ['lab_admin', 'admin', 'superadmin'].includes(profile?.admin_role));

  const go = (view) => { setOpen(false); onNav(view); };

  const itemStyle = {
    display: 'block', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', padding: '10px 14px',
    fontSize: 13.5, color: 'var(--text-primary)', cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const divider = <div style={{height: 1, background: 'var(--border-color)', margin: '4px 0'}} />;

  const trigger = variant === 'labelled' ? (
    <button
      onClick={() => setOpen(o => !o)}
      aria-haspopup="menu"
      aria-expanded={open}
      style={{
        display:'flex', alignItems:'center', gap:10,
        background:'none', border:'1px solid var(--border-color)',
        borderRadius:24, padding:'4px 12px 4px 4px',
        cursor:'pointer', width:'100%', minHeight:40,
        color:'var(--text-primary)', fontFamily:'inherit',
      }}
      title={displayName}
    >
      <Avatar name={displayName} size={32} />
      <span style={{flex:1, minWidth:0, textAlign:'left', fontSize:13, fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{displayName}</span>
      <span style={{fontSize:10, color:'var(--text-dim)'}}>▾</span>
    </button>
  ) : (
    <Avatar
      name={displayName}
      size={32}
      onClick={() => setOpen(o => !o)}
      title={displayName}
    />
  );

  return (
    <div ref={rootRef} className="account-menu-root" style={{position: 'relative', display: variant === 'labelled' ? 'block' : 'inline-block'}}>
      {trigger}
      {open && (
        <div
          className="account-menu-panel"
          role="menu"
          style={{
            position: 'absolute',
            background: 'var(--secondary-bg, #FFFFFF)',
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            zIndex: 9999,
            minWidth: 220,
          }}
        >
          <div style={{padding: '14px 14px 10px 14px', display: 'flex', gap: 10, alignItems: 'center'}}>
            <Avatar name={displayName} size={40} />
            <div style={{minWidth: 0}}>
              <div style={{fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{displayName}</div>
              {email && <div style={{fontSize: 11.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{email}</div>}
            </div>
          </div>
          {divider}
          {/* KAL-212 — an UNDERWRITER has no consumer settings to manage. Clicking the profile
              chip as uw_demo landed on Data & Privacy, offering consent controls for an
              identity that holds no health data and grants no consents — a page that could
              only confuse, on the surface an insurer is being shown. The underwriter's menu
              carries the cohort and sign-out, nothing else. The route stays server-gated; this
              only stops the app OFFERING it. */}
          {!isUnderwriterSession && (
            <>
              {/* KAL-364 — My Genome is mobileHidden in the bottom bar, and on a phone its only
                  other entry was a card at the very END of Longevity, so it read as absent
                  (Tim, 28 Sep 2026). Same gate as the nav item: the genetics flag, never a demo. */}
              {FEATURES_GLOBAL.genetics && !profile?.is_demo && (
                <button role="menuitem" style={itemStyle} onClick={() => go('genome')} data-testid="acct-genome">{'🧬'} My Genome</button>
              )}
              <button role="menuitem" style={itemStyle} onClick={() => go('profile')}>Profile Settings</button>
              <button role="menuitem" style={itemStyle} onClick={() => go('privacy')}>Data &amp; Privacy</button>
            </>
          )}
          {/* NPL corporate surface (NPLCorporateDashboard via #npl-corporate),
              gated on showNplCorporate. NPL keeps its own corporate dashboard
              rather than main's My Organisation. */}
          {showNplCorporate && (
            <button role="menuitem" style={itemStyle} onClick={() => go('npl-corporate')}>Corporate Health</button>
          )}
          {showLabUpload && (
            <button role="menuitem" style={itemStyle} onClick={() => go('staff-lab-upload')}>Lab Upload</button>
          )}
          {showUserMgmt && (
            <button role="menuitem" style={itemStyle} onClick={() => go('user-management')}>User Management</button>
          )}
          {showOrderMgmt && (
            <button role="menuitem" style={itemStyle} onClick={() => go('order-management')}>Order Management</button>
          )}
          {showOpsConsole && (
            <button role="menuitem" style={itemStyle} onClick={() => go('ops-console')}>Ops Console</button>
          )}
          {showUnderwriting && (
            <button role="menuitem" style={itemStyle} onClick={() => go('underwriting')}>Underwriting</button>
          )}
          {showSwitcher && (
            <>
              {divider}
              <button role="menuitem" style={itemStyle} onClick={() => go('profile')}>Switch account</button>
            </>
          )}
          {divider}
          <button role="menuitem" style={{...itemStyle, color: '#dc2626'}} onClick={() => { setOpen(false); onLogout(); }}>Sign out</button>
        </div>
      )}
    </div>
  );
}

// Filter Fitbit-flavoured rows out of the notification stream on the
// NPL instance, where Fitbit isn't part of the product. Any row whose
// type / category / source / action_type / title / body contains
// 'fitbit', 'reauth', or 'sync_error' is suppressed both from the bell
// count and from the dropdown. Returns the input unchanged on every
// other instance.
function _filterFitbitNotificationsForNpl(rows) {
  if (FEATURES_GLOBAL.branding.instance !== 'npl' || !Array.isArray(rows)) {
    return rows;
  }
  const re = /(fitbit|reauth|sync_error)/i;
  return rows.filter(n => {
    const haystack = [
      n.type, n.category, n.source, n.action_type, n.action_data,
      n.title, n.body, n.message,
    ].filter(Boolean).join(' ');
    return !re.test(haystack);
  });
}

function NotificationPanel({ onClose }) {
  const closeModal = useModalHashRoute('#notifications', onClose);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      const data = await api.get('/notifications');
      // NPL: drop Fitbit rows the user can't see, then recompute unread on the
      // filtered set (server count includes hidden Fitbit rows). No-op on Kalpa.
      const filtered = _filterFitbitNotificationsForNpl(data.notifications || []);
      setNotifications(filtered);
      const unread = filtered.filter(n => !n.read).length;
      setUnreadCount(unread);
      // Mark-read-on-view: opening the panel clears the unread state so the bell
      // badge can't accumulate unbounded (the 101-unread bug). Items stay listed
      // (read-styled); the "mark all unread" button can re-flag.
      if (unread > 0) {
        try {
          await api.post('/notifications/read', {});
          setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
          setUnreadCount(0);
        } catch (e) {}
      }
    } catch (e) {}
    setLoading(false);
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read', {});
      setNotifications(prev => prev.map(n => ({...n, read: 1})));
      setUnreadCount(0);
    } catch (e) {}
  };

  const markAllUnread = async () => {
    try {
      await api.post('/notifications/unread', {});
      setNotifications(prev => prev.map(n => ({...n, read: 0})));
      setUnreadCount(notifications.length);
    } catch (e) {}
  };

  const dismiss = async (id) => {
    try {
      await api.post('/notifications/dismiss', { id });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) {}
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      await api.post('/notifications/analyze', {});
      await loadNotifications();
    } catch (e) { setLoading(false); }
  };

  const catEmoji = { biomarker: '🧪', medicine: '💊', fitness: '🏃', genetics: '🧬', research: '📚' };

  const panel = (
    <>
      <div className="notification-backdrop" onClick={closeModal} />
      <div className="notification-panel">
        <div className="notification-header">
          <h3>Health Insights</h3>
          <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
            {unreadCount > 0
              ? <button className="notif-btn" onClick={markAllRead}>Mark read</button>
              : notifications.length > 0 && <button className="notif-btn" onClick={markAllUnread}>Mark unread</button>
            }
            <button className="notif-btn" onClick={runAnalysis}>🔄</button>
            <button className="notif-btn notif-close-btn" onClick={closeModal}>✕</button>
          </div>
        </div>
        {loading ? <div className="notif-empty">Analyzing your data...</div> : (
          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">No insights right now. Your data looks good! 🎯</div>
            ) : notifications.map(n => {
              let optimise = '';
              try { const ad = typeof n.action_data === 'string' ? JSON.parse(n.action_data) : (n.action_data || {}); optimise = ad.optimise || ''; } catch(e){ console.log('parse err', e); }
              return (
                <div key={n.id} className={`notification-item ${n.read ? 'read' : 'unread'}`}>
                  <div className="notif-top">
                    <span className="notif-category">{catEmoji[n.category] || '📋'}</span>
                    <span className="notif-title">{n.title}</span>
                    <button className="notif-dismiss" onClick={() => dismiss(n.id)}>✕</button>
                  </div>
                  <div className="notif-body">{autoGlossary(n.body)}</div>
                  {optimise && <div className="notif-optimise">💡 <strong>{T('Optimise')}:</strong> {autoGlossary(optimise)}</div>}
                  {n.reference && <div className="notif-ref">{n.reference}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(panel, document.body);
}

// ── ProviderCards — unified fitness provider connect/disconnect UI ─────────────

// ── KAL-312 — CONNECTIONS MANAGER (Connectivity spec §2) ─────────────────────────────────────
// Replaces the Profile "Health Platforms" block. One list of the connections the user ACTUALLY
// has, from /api/connections, which derives each chip from the STREAMS (stream health), not from
// a token row — so it can never say "Not connected yet" to someone whose data is arriving (Benji,
// 5 Sep 2026). Labels are "{device} via {writing app}" when the device is known ("Amazfit GTR 4
// via Zepp"), "via Zepp" when only the app is, never a brand list. "Syncing" comes from the same
// sync lifecycle the GlobalSyncBar owns. Add-connection appears only for a slot that is free.
// Gated by what is connected and by the wearable_route FLAG — never by an instance name.
function _cxPlatformHint() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (isInNativeApp()) return /iPhone|iPad|iPod/i.test(ua) ? 'ios' : 'android';
  return detectPlatform();   // 'android' | 'ios' | 'desktop'
}
function ConnectionsManager({ providers, onFitbitConnect, onFitbitSync, onFitbitFullSync, onFitbitDisconnect,
                              fitbitConnecting, onRefresh, setMessage, handleSync, handleDisconnect }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState({});
  const { state: syncState } = useSyncStatus();
  const isNative = isInNativeApp();
  const hint = _cxPlatformHint();
  const load = useCallback(async () => {
    try {
      const j = await api.get(`/connections?platform=${encodeURIComponent(hint)}`);
      setData(j); setErr(null);
    } catch (e) {
      setErr(e && e.message ? e.message : 'connections unavailable');
      console.error('[connections] load failed:', e);
    }
  }, [hint]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Re-read when the phone finishes a sync or the app returns to the foreground — a chip must
    // move when the data does, not on the next login.
    const onSynced = () => load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('kalpa:native-synced', onSynced);
    window.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('kalpa:native-synced', onSynced); window.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  const connectHealth = () => {
    console.log('[NPL-bridge][web] connect tapped; native=' + isNative + ' platform=' + hint);
    if (isNative) {
      try { window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'connect_health' })); }
      catch (e) { console.error('[NPL-bridge][web] postMessage threw: ' + (e && e.message)); setMessage({ type: 'error', text: 'Could not reach the app — please reopen it and try again.' }); }
      return;
    }
    setMessage({ type: 'info', text: hint === 'ios'
      ? 'Apple Health connects inside the Kalpa app on your iPhone — open the app and tap Connect there.'
      : 'Your watch connects inside the Kalpa app on your phone — open the app and tap Connect there.' });
  };
  const relTime = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    if (!isFinite(ms)) return null;
    const m = Math.round(ms / 60000);
    if (m < 2) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 36) return `${h} h ago`;
    return `${Math.round(h / 24)} days ago`;
  };
  // The chip is the STATUS. A sync in flight only decorates a healthy connection — measured on
  // kalpa.health, 6 Sep 2026: a connection 14 days quiet rendered "Syncing…" because the global
  // sync lifecycle was busy at page load. "Needs attention" is never hidden behind a spinner.
  const syncBusy = syncState === 'syncing' || syncState === 'loading';
  const chipFor = (c) => {
    if (c.status === 'needs_attention') return { cls: 'is-needs_attention', text: 'Needs attention' };
    if (c.status === 'connected') return syncBusy && c.kind === 'store'
      ? { cls: 'is-syncing', text: 'Connected · syncing…' } : { cls: 'is-connected', text: 'Connected' };
    if (syncBusy && c.kind === 'store') return { cls: 'is-syncing', text: 'Syncing…' };
    return { cls: 'is-never', text: isNative ? 'Waiting for first sync' : 'No data yet' };
  };
  const iconFor = (c) => c.provider === 'fitbit' ? '📳' : c.provider === 'google_fit' ? '🏃' : c.provider === 'apple_health' ? '🍎' : '⌚';

  if (err && !data) {
    // Loud, and honest about what is known: the legacy token-row grid, never a blank.
    return (
      <div>
        <div className="cx-note" style={{color:'#b45309'}}>Connection details are temporarily unavailable ({err}). Showing basic status.</div>
        <ProviderCardsLegacy providers={providers} onFitbitConnect={onFitbitConnect} onFitbitSync={onFitbitSync}
          onFitbitFullSync={onFitbitFullSync} onFitbitDisconnect={onFitbitDisconnect}
          fitbitConnecting={fitbitConnecting} onRefresh={onRefresh} setMessage={setMessage} />
      </div>
    );
  }
  if (!data) return <div className="cx-note">Checking your connections…</div>;

  const oauthProvider = (id) => (providers || []).find(p => p.id === id) || { id, display_name: id === 'fitbit' ? 'Fitbit' : 'Google Fit' };
  return (
    <div className="cx-list" data-testid="connections-manager">
      {data.connections.map(c => {
        const chip = chipFor(c);
        // "Last synced" is the store's own provider row; "Data to" is what actually arrived. When
        // the data is FRESHER than the row (measured on NPL, 6 Sep 2026: "Last synced 52 days ago ·
        // Data to 06 Sep 2026" for a store whose rows came through another path) the row is stale
        // bookkeeping, not a fact about the connection — say only what the data says.
        const dataFresher = c.last_data_day && c.last_synced_at
          && c.last_data_day >= String(c.last_synced_at).slice(0, 10);
        const last = dataFresher ? null : relTime(c.last_synced_at);
        const stalled = (c.streams || []).filter(s => s.state === 'stalled');
        return (
          <div key={c.id} className={`cx-row is-${c.status}`}>
            <div className="cx-top">
              <span className="cx-icon" aria-hidden="true">{iconFor(c)}</span>
              <div className="cx-meta">
                <div className="cx-label">{c.label}</div>
                <div className="cx-sub">
                  {c.kind === 'store' ? c.store_label : 'Account connection'}
                  {last ? ` · Last synced ${last}` : ''}
                  {c.last_data_day ? ` · Data to ${formatDate(c.last_data_day)}` : ''}
                </div>
              </div>
              <span className={`cx-chip ${chip.cls}`}>{chip.text}</span>
            </div>
            {c.kind === 'store' && c.streams && c.streams.length > 0 && (
              <div className="cx-streams">
                {c.streams.filter(s => s.state !== 'never').map(s => (
                  <span key={s.metric} className={`cx-stream is-${s.state}`} title={s.last_data_day ? `last ${formatDate(s.last_data_day)}` : ''}>
                    {s.label}{s.state === 'stalled' ? ` · ${s.days_behind} days behind` : ''}
                  </span>
                ))}
              </div>
            )}
            {stalled.length > 0 && c.remedy && (
              <div className="cx-sub">
                {c.streams.find(s => s.signature === 'zepp_sleep_relay')
                  ? `${c.app_label || 'Your watch app'} has not passed sleep on since ${formatDate(stalled[0].last_data_day)}. Refresh from device makes it sync now.`
                  : `${c.remedy.app || c.app_label || 'Your device app'} has not synced ${stalled.map(s => s.label.toLowerCase()).join(', ')} since ${formatDate(stalled[0].last_data_day)}.`}
                {Array.isArray(c.remedy.steps) && c.remedy.steps.length > 0 && (
                  <ol className="cx-steps">{c.remedy.steps.slice(0, 3).map((st, i) => <li key={i}>{st}</li>)}</ol>
                )}
              </div>
            )}
            {c.note && <div className="cx-note">{c.note}</div>}
            <div className="cx-actions">
              {c.kind === 'store' && isNative && <RefreshFromDeviceButton />}
              {c.kind === 'store' && c.actions.includes('reconnect') && (
                <button className="profile-connect-btn" onClick={connectHealth}>Reconnect</button>
              )}
              {c.kind === 'oauth' && c.actions.includes('sync') && (
                <button className="profile-sync-btn" disabled={!!busy[c.id]}
                  onClick={async () => { setBusy(b => ({...b, [c.id]: true})); try { await handleSync(oauthProvider(c.provider)); await load(); } finally { setBusy(b => ({...b, [c.id]: false})); } }}>
                  {busy[c.id] ? 'Syncing…' : 'Sync'}
                </button>
              )}
              {c.provider === 'fitbit' && onFitbitFullSync && (
                <button className="profile-sync-btn" disabled={!!busy.fitbit_full}
                  onClick={async () => { setBusy(b => ({...b, fitbit_full: true})); try { await onFitbitFullSync(); } finally { setBusy(b => ({...b, fitbit_full: false})); } }}>
                  {busy.fitbit_full ? 'Full sync…' : 'Full Re-sync'}
                </button>
              )}
              {c.kind === 'oauth' && c.actions.includes('disconnect') && (
                <button className="profile-disconnect-btn" onClick={async () => { await handleDisconnect(oauthProvider(c.provider)); await load(); }}>Disconnect</button>
              )}
            </div>
          </div>
        );
      })}
      {/* KAL-320 — the ONE update card, Profile home: always visible ("You have N · Latest M"
          or "Up to date"). The earlier per-payload row is gone so there is one component. */}
      <div className="cx-add" data-testid="cx-update"><UpdateCard placement="profile" /></div>
      {(data.add_options || []).map(o => (
        <div key={o.id} className="cx-add" data-testid={`cx-add-${o.id}`}>
          <div>
            <div className="cx-label">{o.label}</div>
            <div className="cx-sub">via {o.via}</div>
          </div>
          <button className="profile-connect-btn"
            disabled={o.id === 'fitbit' ? fitbitConnecting : false}
            onClick={o.action === 'connect_fitbit' ? onFitbitConnect : connectHealth}>
            {o.id === 'fitbit' && fitbitConnecting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      ))}
      {data.connections.length === 0 && (data.add_options || []).length === 0 && (
        <div className="cx-note">
          {hint === 'desktop'
            ? 'Wearables connect from the Kalpa app on your phone. Open it, sign in with the same account, and tap Connect.'
            : 'Nothing connected yet. Install the Kalpa app on this phone to connect your watch or health app.'}
        </div>
      )}
      <div className="cx-note">{data.scope_note}</div>
    </div>
  );
}

function ProviderCards(props) {
  // KAL-312 — every instance renders the Connections manager; the legacy token-row grid survives
  // only as its loud fallback when /api/connections is unreachable (ProviderCardsLegacy).
  return <ProviderCardsBody {...props} />;
}
function ProviderCardsLegacy(props) {
  return <ProviderCardsBody {...props} legacy />;
}
function ProviderCardsBody({ providers, onFitbitConnect, onFitbitSync, onFitbitFullSync, onFitbitDisconnect,
                          fitbitConnecting, onRefresh, setMessage, legacy = false }) {
  const [syncing, setSyncing] = useState({});
  // A — the native app IS the Apple Health integration. HealthKit is read natively and pushed to
  // /api/fitness/ingest-native, so inside the WebView there is nothing to "set up".
  const nativeApp = isInNativeApp();

  const handleConnect = async (p) => {
    if (p.id === 'fitbit') { onFitbitConnect(); return; }
    if (p.id === 'apple_health_import') {
      // The Health Auto Export setup modal is GONE — see the note where it used to render.
      // Native: HealthKit is already connected through this app; ask it to sync.
      if (nativeApp) {
        try {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'force_sync' }));
          setMessage({ type: 'success', text: 'Syncing Apple Health from this device…' });
        } catch (e) {
          setMessage({ type: 'error', text: 'Could not reach the app’s health sync.' });
        }
        return;
      }
      // Browser: point at the app rather than a third-party paid workaround.
      setMessage({ type: 'info',
        text: 'Apple Health syncs through the Kalpa app — download it below and sign in with the same account.' });
      return;
    }
    if (p.id === 'google_fit') {
      window.location.href = `${API_BASE}/fitness/google_fit/connect`;
      return;
    }
  };

  const handleSync = async (p) => {
    setSyncing(s => ({ ...s, [p.id]: true }));
    try {
      if (p.id === 'fitbit') { await onFitbitSync(); return; }
      const r = await api.post(`/fitness/${p.id}/sync`, {});
      setMessage({ type: 'success', text: `${p.display_name} synced: ${r.records ?? 0} records` });
      onRefresh();
    } catch (e) { setMessage({ type: 'error', text: `Sync failed: ${e.message}` }); }
    finally { setSyncing(s => ({ ...s, [p.id]: false })); }
  };

  const handleDisconnect = async (p) => {
    if (p.id === 'fitbit') { onFitbitDisconnect(); return; }
    try {
      await api.post(`/fitness/${p.id}/disconnect`, {});
      setMessage({ type: 'success', text: `${p.display_name} disconnected` });
      onRefresh();
    } catch (e) { setMessage({ type: 'error', text: `Disconnect failed: ${e.message}` }); }
  };

  // KAL-312 — the instance-gated NPL placeholder that lived here (a "Wearable device sync" card
  // keyed on `branding.instance === 'npl'`, chip from a token row, brand wall in the copy) is
  // gone. Every instance renders the Connections manager; this token-row grid is reached only
  // as its loud fallback (`legacy`) when /api/connections cannot be read.
  if (!legacy) {
    return (
      <ConnectionsManager providers={providers} onFitbitConnect={onFitbitConnect}
        onFitbitSync={onFitbitSync} onFitbitFullSync={onFitbitFullSync}
        onFitbitDisconnect={onFitbitDisconnect} fitbitConnecting={fitbitConnecting}
        onRefresh={onRefresh} setMessage={setMessage}
        handleSync={handleSync} handleDisconnect={handleDisconnect} />
    );
  }

  return (
    <>
      <div className="fitness-providers-grid">
        {providers.map(p => {
          // A — Apple Health reads differently depending on where this page is being viewed. In
          // the native app HealthKit IS the connection, so the tile must say so rather than offer
          // a setup flow. In a browser the honest answer is "this happens in the app".
          const _appleNative = (p.id === 'apple_health_import' && nativeApp);
          return (
          <div key={p.id} className={`provider-card ${(p.connected || _appleNative) ? 'connected' : ''} ${p.coming_soon ? 'coming-soon' : ''}`}>
            <div className="provider-card-top">
              <span className="provider-icon">{p.icon}</span>
              <div className="provider-meta">
                <div className="provider-name">
                  {p.display_name}{_appleNative ? ' — connected via this app' : ''}
                </div>
                <div className="provider-desc">
                  {p.id === 'apple_health_import'
                    ? (nativeApp
                        ? 'Read from HealthKit on this device and synced in the background. No setup needed.'
                        : 'Syncs through the Kalpa app — download it below and sign in with the same account.')
                    : p.description}
                </div>
              </div>
              <div className="provider-status-dot"
                   title={(p.connected || _appleNative) ? 'Connected' : p.coming_soon ? 'Coming soon' : 'Not connected'}>
                {(p.connected || _appleNative) ? '🟢' : p.coming_soon ? '⏳' : '⚪'}
              </div>
            </div>
            {p.last_sync_at && (
              <div className="provider-last-sync">Last sync: {formatDateTime(p.last_sync_at)}</div>
            )}
            {p.coming_soon_label && (
              <div className="provider-coming-soon-label">{T(p.coming_soon_label)}</div>
            )}
            <div className="provider-card-actions">
              {/* A — in the native app, Apple Health gets the Sync action even when the server has
                  no `connected` row for it: the connection is HealthKit itself, and the sync is
                  the app's own. Never a Disconnect — there is no web-side token to revoke. */}
              {_appleNative && !p.connected && (
                <button className="profile-sync-btn" onClick={() => handleConnect(p)}>Sync now</button>
              )}
              {p.connected && !p.coming_soon && (
                <>
                  <button className="profile-sync-btn" onClick={() => handleSync(p)} disabled={syncing[p.id]}>
                    {syncing[p.id] ? 'Syncing…' : 'Sync'}
                  </button>
                  {p.id === 'fitbit' && onFitbitFullSync && (
                    <button className="profile-sync-btn" style={{background:'rgba(129,140,248,0.15)',color:'#818CF8',borderColor:'rgba(129,140,248,0.3)'}}
                      onClick={async () => { setSyncing(s => ({...s, fitbit_full: true})); try { await onFitbitFullSync(); } finally { setSyncing(s => ({...s, fitbit_full: false})); } }}
                      disabled={syncing['fitbit_full']}>
                      {syncing['fitbit_full'] ? 'Full sync…' : 'Full Re-sync'}
                    </button>
                  )}
                  <button className="profile-disconnect-btn" onClick={() => handleDisconnect(p)}>Disconnect</button>
                </>
              )}
              {!p.connected && !p.coming_soon && p.available && !_appleNative && (
                <button className="profile-connect-btn"
                  onClick={() => handleConnect(p)}
                  disabled={p.id === 'fitbit' ? fitbitConnecting : false}>
                  {p.id === 'fitbit' && fitbitConnecting
                    ? 'Connecting…'
                    : p.id === 'apple_health_import'
                      ? 'How Apple Health syncs'
                      : `Connect ${p.display_name}`}
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* The "Apple Health Setup" modal was REMOVED here (9 Aug 2026).
          It instructed the user to buy a third-party app (Health Auto Export, "one-time £19.99")
          and RENDERED THEIR PERSONAL INGEST URL ON SCREEN — a per-user HMAC key. Two problems:
            1. Inside the native app it was simply false. HealthKit is already connected through
               the app and pushes to /api/fitness/ingest-native; Daniel was shown a paid setup
               flow for something already working.
            2. The key was displayed (and copyable) on a page a user might screenshot or screen-
               share. Nothing else in the product prints a credential.
          The webhook route itself still EXISTS server-side and keeps working for anyone already
          using it — this removes the surface that hands the key out, not the capability. */}
    </>
  );
}


// One-tap "Report a problem" (Profile). A single text box + Send; the server
// auto-bundles diagnostics (version, instance, platform, connection/sync state,
// recent errors). The client also attaches app_version / platform / native flag.
// Reusable feedback form body (free text → /api/report-problem with auto-bundled
// diagnostics). Used by the all-pages FeedbackWidget and the Profile section.
function FeedbackForm({ onSent }) {
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [queued, setQueued] = useState(false);
  const [err, setErr] = useState(null);
  const send = async () => {
    const text = msg.trim();
    if (!text) { setErr('Please tell us what’s up.'); return; }
    setSending(true); setErr(null); setQueued(false);
    const client = {
      app_version: '8.0',
      platform: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
      native: isInNativeApp(),
      url: (typeof window !== 'undefined' && window.location && window.location.href) || '',
    };
    // sendFeedback retries (1s/2s/4s), and on final failure queues in-memory + logs to
    // /api/client-error. It NEVER throws and never reports success unless it actually sent.
    const res = await sendFeedback('/report-problem', { message: text, client });
    setSending(false);
    if (res.ok) { usageAnalytics.track('feedback_submitted'); setSent(true); setMsg(''); if (onSent) setTimeout(onSent, 1300); }
    else { setQueued(true); setMsg(''); }   // honest: saved + will retry — NOT "sent"
  };
  if (sent) return (
    <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
      Thanks — your feedback was sent. We attached basic diagnostics (app version, device, sync status) to help us look into it.
    </p>
  );
  if (queued) return (
    <p style={{ fontSize: 13.5, color: 'var(--warning, #B8890A)', margin: 0 }}>
      Saved — we couldn’t reach the server just now, so your feedback will send automatically when the connection’s back. You can close this.
    </p>
  );
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>
        Anything not working, not looking right, or an idea? Tell us in a line — we’ll attach your app version, device and sync status automatically.
      </p>
      <textarea
        className="kalpa-feedback-textarea"
        value={msg}
        onChange={e => setMsg(e.target.value)}
        rows={3}
        placeholder="e.g. My watch data isn’t showing today’s steps."
        /* S6 — colour, caret, background and border are set in App.css keyed on data-npl-theme,
           NOT here. Two reasons: an inline style beats any stylesheet rule, so a theme could
           never override it; and the var() approach I tried first silently failed because the
           light-theme token block is scoped to [data-instance="kalpa"], so on NPL
           --text-primary keeps its DARK value (#F4F6FF) and the field stayed white-on-white. */
        style={{ width: '100%', fontSize: 13.5, padding: '8px 10px', borderRadius: 8, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
      />
      {err && <div style={{ color: '#dc2626', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
      <button
        onClick={send}
        disabled={sending}
        style={{ marginTop: 10, fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, border: 'none', cursor: sending ? 'wait' : 'pointer', background: 'var(--accent, #7F91DF)', color: 'var(--on-accent, #1a1a1a)' }}
      >
        {sending ? 'Sending…' : 'Send feedback'}
      </button>
    </>
  );
}

// Persistent, unobtrusive "Feedback" entry point on EVERY page — a small floating
// pill that opens the feedback form in a sheet. Rendered once at the app root.
function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => { setOpen(true); flushFeedbackQueue(); }}
        aria-label="Send feedback"
        // D5 — the launcher is inline-styled with no class, so the print stylesheet had nothing
        // to target and "💬 Feedback" was printing onto the Applicant Risk Report. Naming it is
        // the fix; the class exists purely so print can hide it.
        className="feedback-launcher"
        style={{
          // TOP-RIGHT, just below the sticky header bar (header is 44px + safe-area,
          // z-index 50) — clears the header's avatar + theme toggle above it, and the
          // bottom-right Ask-Kalpa FAB below it. Consistent across desktop/mobile web
          // + app WebView. Fixed so it never overlaps scrolling content.
          // S5 — the offset was bare env(safe-area-inset-top), and the NATIVE WebView reports
          // env()=0 while the wrapper injects the real inset as --safe-area-inset-top (this is
          // why every safe-area rule in App.css is max(env, var)). With a 44px inset the header
          // grows to 88px and this pill stayed at 52px, i.e. BEHIND the header — invisible and
          // untappable in the Android app. Measured: injecting the var moved the header 44 -> 88
          // and left the pill at 52. Now it tracks the same inset the header does.
          position: 'fixed', right: 12,
          top: 'calc(max(env(safe-area-inset-top, 0px), var(--safe-area-inset-top, 0px)) + 52px)',
          zIndex: 1150, fontSize: 12, fontWeight: 600, padding: '6px 11px', borderRadius: 999,
          border: '1px solid var(--border-color)', background: 'var(--secondary-bg, #fff)',
          color: 'var(--text-secondary)', boxShadow: '0 2px 10px rgba(0,0,0,0.18)', cursor: 'pointer', opacity: 0.92,
        }}
      >💬 Feedback</button>
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'var(--secondary-bg, #fff)', width: '100%', maxWidth: 480, borderRadius: '14px 14px 0 0', padding: 18, boxShadow: '0 -4px 24px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>Feedback</h3>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-dim)', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <FeedbackForm onSent={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}

// One-tap thumbs up/down on an AI insight/advice → /api/ai-feedback, logged with
// the insight context (surface + a ref to the specific insight).
function AiThumbs({ surface, contextRef }) {
  const [vote, setVote] = useState(0);
  const send = (v) => {
    setVote(v);
    // Usage telemetry — up/down only. NOT surface/contextRef (contextRef can be a
    // marker name → health content; the privacy plane must never carry it).
    usageAnalytics.track('thumb_rated', { result: v > 0 ? 'up' : 'down' });
    // retry + in-memory queue + client-error trace on failure; never throws (no unhandled rejection).
    sendFeedback('/ai-feedback', { surface, context_ref: contextRef || '', vote: v });
  };
  if (vote !== 0) return <span className="ai-thumbs-label" style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>Thanks for the feedback</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="ai-thumbs-label" style={{ fontSize: 11, color: 'var(--text-dim)' }}>Was this helpful?</span>
      <button onClick={() => send(1)} aria-label="Helpful" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2, lineHeight: 1 }}>👍</button>
      <button onClick={() => send(-1)} aria-label="Not helpful" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2, lineHeight: 1 }}>👎</button>
    </span>
  );
}

// USER-MGMT build P5 — admin/superadmin roster + add/edit/deactivate. The
// server enforces the role split (admin: pilot users only, no roles/allow-list;
// superadmin: all + roles + Google allow-list); the UI mirrors it. Distinct from
// the Ops Console. Roster moved here off #profile.
// App-shell banner shown on EVERY view whenever a superadmin is viewing as someone
// else. Before this, the only "Viewing X" indicator lived on the #profile page, so an
// active impersonation was invisible on Today / Longevity / Fitness / Health Record —
// a superadmin could read a patient's record with nothing on screen saying so. It is
// rendered at the shell (not per page) precisely so there is no view where it can be
// missing, and it carries the single Exit control back to admin.
function ViewingAsBanner({ profile, onExit }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const viewingAs = !!(profile && profile.viewing_as);
  const exit = async () => {
    setBusy(true); setErr(null);
    try {
      // Dedicated exit endpoint: never depends on round-tripping our own
      // profile_name, so this button cannot strand us inside another account.
      const r = await fetch(`${API_BASE}/users/exit-view`, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        let msg = `Could not exit (HTTP ${r.status})`;
        try { const b = await r.json(); if (b && b.error) msg = b.error; } catch { /* keep status */ }
        throw new Error(msg);
      }
      await onExit();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  // HARD FALLBACK: Escape always exits view-as, independent of anything on screen being
  // clickable — no overlay in this app calls preventDefault/stopPropagation on a bare
  // Escape keydown, so this still reaches us from underneath a full-screen modal that
  // visually covers the banner. Backed by a second, UI-independent fallback: GET
  // /api/users/exit-view (typed straight into the address bar) also clears view-as now.
  useEffect(() => {
    if (!viewingAs) return;
    const onKey = (e) => { if (e.key === 'Escape') exit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingAs]);
  if (!viewingAs) return null;
  // /api/profile (the shell's source) spells it `displayName`; /api/users/current spells it
  // `display_name`. Accept both, then fall back to the raw profile_name, so the banner can
  // never render a blank name whichever payload it is handed.
  const who = profile.displayName || profile.display_name || profile.name || profile.viewing_as;
  return (
    // Inline zIndex (not the CSS class's 1200) DELIBERATELY: it must outrank every
    // modal/overlay in the app, including ones added after this file's 1200 was chosen
    // (the NPL onboarding wizard's 9999 backdrop, ops overlays up to 10003, etc). Inline
    // style beats a plain class rule regardless of what future overlay z-indices land at,
    // so Exit can never again end up behind something while impersonating.
    <div className="viewing-as-banner" role="status" aria-live="polite" style={{ zIndex: 2147483647 }}>
      <span className="vab-eye" aria-hidden="true">👁</span>
      <span className="vab-text">
        Viewing as <strong>{who}</strong>
        <span className="vab-ro"> · read-only</span>
      </span>
      <button type="button" className="vab-exit" onClick={exit} disabled={busy}>
        {busy ? 'Exiting…' : 'Exit'}
      </button>
      {err && <span className="vab-err">{err}</span>}
    </div>
  );
}

function UserManagementPage({ profile, onChanged, onViewAs }) {
  const [data, setData] = useState(null);       // {users, viewer_is_superadmin}
  const [allow, setAllow] = useState(null);      // superadmin: allow-list rows
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [add, setAdd] = useState({ display_name: '', phone: '', dob: '', biological_sex: '', height_cm: '', npl_patient_id: '' });
  const [newAllow, setNewAllow] = useState({ email: '', profile_name: '' });
  const isSuper = !!data?.viewer_is_superadmin;
  // KAL-287 — connection health: the one screen that answers "who is broken right now".
  // Role-gated server-side (lab_staff/admin/superadmin); rendered for whoever can load it.
  const [streamHealth, setStreamHealth] = useState(null);
  const [shErr, setShErr] = useState(null);
  // KAL-321 — load telemetry p50/p95 per platform/build with a deploy-events overlay.
  const [loadTel, setLoadTel] = useState(null);
  // KAL-335 — the server now answers from the last sweep's SNAPSHOT (instant) and refreshes in
  // the background; `refreshing` means a sweep is running, so poll every 10 s until it lands
  // (a cold sweep is 87-112 s on NPL, KAL-331). `shRefreshing` drives the header state; the
  // Refresh button asks for a forced sweep. Before this the route RAN the sweep and this fetch
  // timed out at 20 s on every cold view (Bimal, 7 Sep 08:25Z, twice).
  const [shRefreshing, setShRefreshing] = useState(false);
  const shPollRef = useRef(null);
  const loadStreamHealth = useCallback((opts = {}) => {
    const q = opts.refresh ? '?refresh=1' : '';
    return _abortableFetch(`${API_BASE}/admin/stream-health${q}`, { credentials: 'include' },
                    LOAD_TIMEOUT_MS, 'stream-health')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(j => {
        setShErr(null);
        setStreamHealth(j);
        setShRefreshing(!!j.refreshing);
        if (j.refreshing) {
          if (shPollRef.current) clearTimeout(shPollRef.current);
          shPollRef.current = setTimeout(() => loadStreamHealth(), 10000);
        }
      })
      .catch(e => { setShErr(e.message || 'failed'); setShRefreshing(false); });
  }, []);
  useEffect(() => {
    let alive = true;
    loadStreamHealth();
    _abortableFetch(`${API_BASE}/admin/load-telemetry?days=7`, { credentials: 'include' },
                    LOAD_TIMEOUT_MS, 'load-telemetry')
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (alive && j) setLoadTel(j); })
      .catch(() => {});
    return () => { alive = false; if (shPollRef.current) clearTimeout(shPollRef.current); };
  }, [loadStreamHealth]);
  // KAL-335 — the admin view names people. Every row in this page that used to print a
  // profile id (allow-list picker and table) now prints the display name with the id dim
  // beside it, the way the users table above already does.
  const nameOf = (pn) => {
    const u = (data?.users || []).find(x => x.profile_name === pn);
    return (u && u.display_name && u.display_name.trim()) ? u.display_name.trim() : pn;
  };

  const call = async (path, opts) => {
    // KAL-262
    const r = await _abortableFetch(`${API_BASE}/admin/user-management${path}`, { credentials: 'include', ...opts }, LOAD_TIMEOUT_MS, `user-mgmt ${path}`);
    let j = null; try { j = await r.json(); } catch (e) { /* non-JSON */ }
    if (!r.ok) throw new Error((j && (j.error || j.detail)) || `HTTP ${r.status}`);
    return j;
  };
  const load = useCallback(async () => {
    setError(null);
    try {
      const j = await call('/users');
      setData(j);
      if (j.viewer_is_superadmin) {
        try { const a = await call('/allowlist'); setAllow(a.allowlist); } catch (e) { /* non-fatal */ }
      }
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const jsonOpts = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  const doAdd = async () => {
    if (!add.display_name.trim()) { setError('Name is required'); return; }
    setBusy(true); setError(null);
    try {
      await call('/users', jsonOpts('POST', { ...add, height_cm: add.height_cm ? Number(add.height_cm) : null }));
      setShowAdd(false); setAdd({ display_name: '', phone: '', dob: '', biological_sex: '', height_cm: '', npl_patient_id: '' });
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const patch = async (pn, updates) => {
    setBusy(true); setError(null);
    try { await call(`/users/${encodeURIComponent(pn)}`, jsonOpts('PATCH', updates)); await load(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  // View as <user>. The POST always worked and the session DID switch — but this then
  // only called onChanged() (a data reload) and left the superadmin sitting on this
  // very table, which looks identical afterwards. So the click appeared to do nothing
  // while impersonation was silently active. It must now LEAVE this page for the
  // target's Today, and the app-shell banner makes the state visible everywhere.
  const viewAs = async (pn) => {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/users/switch`, { credentials: 'include', ...jsonOpts('POST', { profile_name: pn }) });
      if (!r.ok) {
        // Surface the SERVER's reason (e.g. the superadmin gate's 403) rather than a
        // generic string — a silent-looking failure was the whole complaint here.
        let msg = `Could not switch profile (HTTP ${r.status})`;
        try { const b = await r.json(); if (b && b.error) msg = b.error; } catch { /* keep status text */ }
        throw new Error(msg);
      }
      if (onViewAs) await onViewAs(pn);
      else onChanged && onChanged();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const addAllow = async () => {
    if (!newAllow.email.trim() || !newAllow.profile_name.trim()) { setError('Email and profile required'); return; }
    setBusy(true); setError(null);
    try { await call('/allowlist', jsonOpts('POST', newAllow)); setNewAllow({ email: '', profile_name: '' }); await load(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  const delAllow = async (email) => {
    setBusy(true); setError(null);
    try { await call(`/allowlist/${encodeURIComponent(email)}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };

  const roleLabel = (u) => u.admin_role || 'user';
  const users = data?.users || [];

  return (
    <div className="profile-page um-page">
      <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{ height: 24, width: 24, marginRight: 8, verticalAlign: 'middle', objectFit: 'contain' }} />User Management</h2>
      <p className="um-sub">{isSuper ? 'Superadmin — all users incl. staff; manage roles + the Google allow-list.' : 'Admin — pilot users only; add, edit and deactivate. Roles and staff are superadmin-only.'}</p>
      {error && <div className="profile-message profile-message-error" role="alert">⚠ {error}</div>}

      <div className="profile-section">
        <div className="profile-section-header">
          <h3>Users ({users.length})</h3>
          <button className="profile-edit-btn" disabled={busy} onClick={() => setShowAdd(s => !s)}>{showAdd ? 'Cancel' : '+ Add user'}</button>
        </div>

        {showAdd && (
          <div className="um-add">
            <input className="um-input" placeholder="Full name *" value={add.display_name} onChange={e => setAdd({ ...add, display_name: e.target.value })} />
            <input className="um-input" placeholder="Phone (+977…)" value={add.phone} onChange={e => setAdd({ ...add, phone: e.target.value })} />
            <input className="um-input" placeholder="NPL patient ID" value={add.npl_patient_id} onChange={e => setAdd({ ...add, npl_patient_id: e.target.value })} />
            <input className="um-input" type="date" placeholder="DOB" value={add.dob} onChange={e => setAdd({ ...add, dob: e.target.value })} />
            <select className="um-input" value={add.biological_sex} onChange={e => setAdd({ ...add, biological_sex: e.target.value })}>
              <option value="">Sex…</option><option value="female">Female</option><option value="male">Male</option>
            </select>
            <input className="um-input" type="number" placeholder="Height cm" value={add.height_cm} onChange={e => setAdd({ ...add, height_cm: e.target.value })} />
            <button className="um-btn um-btn-primary" disabled={busy} onClick={doAdd}>{busy ? 'Adding…' : 'Create pilot user'}</button>
            <div className="um-note">Admins create plain pilot users (is_pilot, non-staff). Staff/roles are set by a superadmin below.</div>
          </div>
        )}

        {data === null ? <LoadingSpinner message="Loading users…" /> : (
          <div className="um-tablewrap">
            <table className="um-table">
              <thead><tr><th>User</th><th>Contact</th><th>Type</th>{isSuper && <th>Role</th>}<th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map(u => {
                  const isSelf = u.profile_name === profile?.logged_in_profile || u.profile_name === profile?.profile_name;
                  return (
                    <tr key={u.profile_name} className={u.active ? '' : 'um-inactive'}>
                      <td>
                        <div className="um-user">
                          <Avatar name={u.display_name} size={32} />
                          <div style={{ minWidth: 0 }}>
                            <div className="um-name">{u.display_name}</div>
                            <div className="um-dim">{u.profile_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="um-dim">{u.email}{u.phone ? <div>{u.phone}</div> : null}</td>
                      <td>
                        {u.is_staff ? <span className="um-badge um-badge-staff">staff</span> : <span className="um-badge um-badge-pilot">{u.is_pilot ? 'pilot' : 'user'}</span>}
                      </td>
                      {isSuper && (
                        <td>
                          <select className="um-input um-role" value={u.admin_role || ''} disabled={busy || isSelf}
                                  title={isSelf ? "You can't change your own role" : 'Set role'}
                                  onChange={e => patch(u.profile_name, { admin_role: e.target.value || null })}>
                            <option value="">user</option>
                            <option value="admin">admin</option>
                            <option value="lab_staff">lab_staff</option>
                            <option value="superadmin">superadmin</option>
                          </select>
                          <label className="um-staff-toggle" title="WF family — excluded from cohort + usage">
                            <input type="checkbox" checked={!!u.is_staff} disabled={busy || isSelf}
                                   onChange={e => patch(u.profile_name, { is_staff: e.target.checked })} /> staff
                          </label>
                        </td>
                      )}
                      <td>{u.active ? <span className="um-badge um-badge-active">active</span> : <span className="um-badge um-badge-off">inactive</span>}</td>
                      <td>
                        <div className="um-actions">
                          <button className="um-btn" disabled={busy} onClick={() => patch(u.profile_name, { active: u.active ? 0 : 1 })}>{u.active ? 'Deactivate' : 'Activate'}</button>
                          {isSuper && !isSelf && <button className="um-btn" disabled={busy} onClick={() => viewAs(u.profile_name)}>View as</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* KAL-287 — CONNECTION HEALTH. Per user, per stream: last received, arrival pattern,
          and a STALLED badge when one stream stopped while its siblings still arrive (the
          watch is worn, that pipe is broken) — distinct from a device gone silent. The
          server logs every stall to stream_health_log whether or not anyone opens this. */}
      <div className="profile-section">
        <div className="profile-section-header">
          <h3>Connection health</h3>
          {/* KAL-335 — the snapshot's age is part of the data; a viewer can ask for a fresh
              sweep and watch it land instead of waiting on a request that used to time out. */}
          <div className="chealth-asof" data-testid="chealth-asof">
            {streamHealth && streamHealth.as_of
              ? <span>as of {new Date(streamHealth.as_of).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{streamHealth.stale && !shRefreshing ? ' · stale' : ''}</span>
              : streamHealth ? <span>no sweep yet</span> : null}
            {shRefreshing && <span className="chealth-refreshing"> · refreshing… (a full sweep takes up to 2 min)</span>}
            <button type="button" className="um-btn" disabled={shRefreshing}
              onClick={() => { setShRefreshing(true); loadStreamHealth({ refresh: true }); }}>
              {shRefreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
        </div>
        {shErr && (
          <div className="umgmt-err">
            Connection health failed to load: {shErr}{' '}
            <button type="button" className="um-btn" onClick={() => { setShErr(null); loadStreamHealth(); }}>Retry</button>
          </div>
        )}
        {streamHealth && streamHealth.note && (!streamHealth.users || !streamHealth.users.length) && (
          <div className="chealth-dim">{streamHealth.note}</div>
        )}
        {/* KAL-321 — how long Today actually took to become ready, per platform and build, over
            the last 7 days; "post-deploy" = loads within 10 min after a deploy event. */}
        {loadTel && (
          <div className="lt-wrap" data-testid="load-telemetry">
            <div className="chealth-dim" style={{margin:'4px 0 6px'}}>
              Today load, last {loadTel.days} days · content ready p50 / p95 ·
              steady {loadTel.steady.n ? `${loadTel.steady.p50_ms} / ${loadTel.steady.p95_ms} ms (n=${loadTel.steady.n})` : 'no loads'} ·
              within 10 min of a deploy {loadTel.post_deploy.n ? `${loadTel.post_deploy.p50_ms} / ${loadTel.post_deploy.p95_ms} ms (n=${loadTel.post_deploy.n})` : 'no loads'} ·
              deploys {loadTel.deploys.length}
            </div>
            {loadTel.groups.length > 0 && (
              <div className="chealth-tablewrap"><table className="chealth-table lt-table">
                <thead><tr><th>platform</th><th>build</th><th>loads</th><th>p50</th><th>p95</th><th>cold loads</th><th>cold p95</th><th>timeouts</th><th>retries</th><th>from cache</th></tr></thead>
                <tbody>{loadTel.groups.map(g => (
                  <tr key={g.platform + g.build}><td>{g.platform}</td><td>{g.build}</td><td>{g.n}</td>
                    <td>{g.p50_ms} ms</td><td className={g.p95_ms > 3000 ? 'is-stalled' : ''}>{g.p95_ms} ms</td>
                    <td>{g.cold_n}</td><td>{g.cold_p95_ms != null ? `${g.cold_p95_ms} ms` : '—'}</td>
                    <td>{g.timeouts}</td><td>{g.retries}</td><td>{g.from_cache_n}</td></tr>
                ))}</tbody></table></div>
            )}
          </div>
        )}
        {!streamHealth && !shErr && <div className="chealth-dim">Checking every stream…</div>}
        {streamHealth && (
          <div className="chealth-tablewrap">
            <table className="chealth-table">
              <thead>
                <tr><th>User</th><th>Status</th>
                  <th>Sleep</th><th>Heart rate</th><th>Steps</th><th>Writes via</th>
                  <th>App build</th>{FEATURES_GLOBAL.hc_diagnostic && <th>Device check</th>}</tr>
              </thead>
              <tbody>
                {(streamHealth.users || []).filter(u => !u.is_staff).concat(
                  (streamHealth.users || []).filter(u => u.is_staff)).map(u => {
                  const cell = (m) => {
                    const s = (u.streams || []).find(x => x.metric === m);
                    if (!s || s.state === 'never') return <td className="chealth-dim">never</td>;
                    const days = s.days_behind;
                    return (
                      <td className={s.state === 'stalled' ? 'is-stalled'
                                     : s.state === 'quiet' ? 'is-quiet' : ''}>
                        {formatDate(s.last_data_day)}
                        <span className="chealth-days">
                          {days === 0 ? 'today' : `${days}d ago`}
                          {` · ${s.data_days_60d}/60d`}
                        </span>
                        {s.state === 'stalled' && <span className="chealth-badge">STALLED</span>}
                        {s.signature === 'zepp_sleep_relay' && <span className="chealth-badge" title="HR flowing, sleep silent, writer Zepp — remedy: Refresh from device">ZEPP RELAY</span>}
                      </td>
                    );
                  };
                  const origins = [...new Set((u.streams || []).map(s => s.origin).filter(Boolean))];
                  return (
                    <tr key={u.profile} className={u.is_staff ? 'chealth-staffrow' : ''}>
                      <td>{u.display_name}{u.is_staff ? ' (staff)' : ''}</td>
                      <td className={`chealth-state is-${u.state}`}>
                        {u.state === 'device_silent' ? 'device silent' : u.state}
                      </td>
                      {cell('sleep')}{cell('heart_rate')}{cell('steps')}
                      <td className="chealth-dim">
                        {origins.length ? origins.map(o => {
                          const short = { 'com.huami.watch.hmwatchmanager': 'Zepp',
                                          'com.fitbit.FitbitMobile': 'Fitbit',
                                          'com.sec.android.app.shealth': 'Samsung Health',
                                          'com.google.android.apps.fitness': 'Google Fit',
                                          'com.noisefit': 'NoiseFit' }[o];
                          return short || o;
                        }).join(', ')
                          : ((u.providers || []).some(p => p.provider === 'apple_health')
                             ? 'Apple Health' : 'unknown (older app)')}
                      </td>
                      {/* KAL-294 — app build. `reported` is the client's own claim and is
                          exact; `inferred` is read off the wire shape of stored rows and can
                          only ever be a FLOOR, which is why it renders as "≥ v28" and not a
                          number. Hover gives the evidence. */}
                      <td className={u.build && u.build.stale ? 'is-stalled' : 'chealth-dim'}
                          title={(u.build && u.build.why || []).join(' · ')}>
                        {!u.build || u.build.source === 'unknown' ? 'unknown' : u.build.label}
                        <span className="chealth-days">
                          {u.build && u.build.source === 'inferred' ? 'inferred'
                            : u.build && u.build.source === 'reported' ? 'reported' : '—'}
                          {u.build && u.build.platform ? ` · ${u.build.platform}` : ''}
                        </span>
                        {u.build && u.build.stale && <span className="chealth-badge">OLD</span>}
                        {/* KAL-320 — the same resolver's verdict per user: behind / blocked. */}
                        {u.update && u.update.state === 'blocked' && <span className="chealth-badge is-blocked">BLOCKED</span>}
                        {u.update && u.update.state === 'behind' && <span className="chealth-badge is-behind">BEHIND</span>}
                      </td>
                      {/* KAL-297 — ask the PHONE what its own health store holds for sleep.
                          This is the screenshot request, replaced by data: the answer says
                          whether a missing night is absent from Health Connect too (the relay
                          never wrote it) or present there and missing here (our fault). */}
                      {FEATURES_GLOBAL.hc_diagnostic && (
                        <HcDiagnosticCell profile={u.profile} />
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="chealth-legend">
              STALLED = this stream stopped while others still arrive (device worn, pipe
              broken). Device silent = nothing arriving — not worn, phone off, or lapsed.
              “n/60d” = days with data in the last 60. App build: “reported” is the client’s
              own claim (exact); “inferred” is read from the shape of data already stored and
              is only ever a floor — v28 and v33 are identical on the wire. OLD = below v28,
              whose read window drops any record Health Connect receives more than 3 days late.
            </div>
          </div>
        )}
      </div>

      {isSuper && (
        <div className="profile-section">
          <h3>Google allow-list</h3>
          <p className="um-note">Each verified Google email maps to an EXISTING profile. Non-listed emails are rejected (no account is created). This is the sole boundary for /wf Google sign-in.</p>
          <div className="um-add">
            <input className="um-input" type="email" autoComplete="off" spellCheck={false}
                   placeholder="google email" value={newAllow.email}
                   onChange={e => setNewAllow({ ...newAllow, email: e.target.value })} />
            {/* The card says this must map to an EXISTING profile, and the server enforces exactly
                that — it 400s with "no such profile: X". A free-text box invited the one mistake
                the rule forbids: typing the DISPLAY name ("Daniel Gocher") instead of the profile
                identifier ("Daniel_Gocher"). A select built from the roster already loaded on this
                page makes that error unrepresentable, which is better than reporting it. */}
            <select className="um-input" value={newAllow.profile_name}
                    onChange={e => setNewAllow({ ...newAllow, profile_name: e.target.value })}>
              <option value="">— map to profile —</option>
              {users.map(u => (
                <option key={u.profile_name} value={u.profile_name}>
                  {nameOf(u.profile_name)}{nameOf(u.profile_name) !== u.profile_name ? ` (${u.profile_name})` : ''}
                </option>
              ))}
            </select>
            <button className="um-btn um-btn-primary"
                    disabled={busy || !newAllow.email.trim() || !newAllow.profile_name}
                    onClick={addAllow}>{busy ? 'Working…' : 'Add / update'}</button>
          </div>
          {/* Errors were rendered once at the top of the page, far from this card — a 400 from
              here could scroll out of sight and read as "the button does nothing". */}
          {error && <div className="um-err-inline">{error}</div>}
          <div className="um-tablewrap">
            <table className="um-table">
              <thead><tr><th>Email</th><th>→ Profile</th><th>Note</th><th></th></tr></thead>
              <tbody>
                {(allow || []).map(a => (
                  <tr key={a.email}>
                    <td className="um-dim">{a.email}</td>
                    <td>{nameOf(a.profile_name)}{nameOf(a.profile_name) !== a.profile_name && <div className="um-dim">{a.profile_name}</div>}</td>
                    <td className="um-dim">{a.note}</td>
                    <td><button className="um-btn" disabled={busy} onClick={() => delAllow(a.email)}>Remove</button></td>
                  </tr>
                ))}
                {allow && allow.length === 0 && <tr><td colSpan={4} className="um-dim">No entries.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── RUN B P11 — the notification triad, surfaces 2 and 3 ─────────────────────────────────────────
// ONE shared helper for actually turning push on. It is the ONLY place in the app that calls
// Notification.requestPermission(), and it is only ever reached from an explicit user tap — never on
// load, never from onboarding. The browser grants one prompt per origin; spending it unprompted is how
// an app gets permanently blocked.
async function enablePushNow() {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
    return { ok: false, reason: 'This browser does not support push notifications.' };
  }
  // Two sources, because a MISSING route is not the same thing as a missing key. The dedicated
  // endpoint did not exist on the core at first, so this returned index.html, JSON.parse choked, and the
  // user was told "push is not configured" while a valid key sat in secrets.toml. Fall back to the
  // /api/features payload, which has always carried vapid_public_key.
  let key = null;
  try {
    const r = await api.get('/push/vapid-public-key');
    key = r && (r.vapid_public_key || r.key);
  } catch (e) { /* route absent or non-JSON — try features below */ }
  if (!key) {
    try {
      const f = await api.get('/features');
      key = f && f.vapid_public_key;
    } catch (e) { /* reported below */ }
  }
  if (!key) return { ok: false, reason: 'Push is not configured on this instance yet.' };

  const perm = await Notification.requestPermission();          // the ONE real OS prompt
  if (perm !== 'granted') return { ok: false, reason: 'Permission was not granted.', permission: perm };
  try {
    // REGISTER the push worker explicitly. The original code awaited
    // navigator.serviceWorker.ready, which NEVER RESOLVES on this app: nothing registers a worker, and
    // the root service-worker.js is a deliberate kill-switch that unregisters itself and handles no
    // push. So the Enable button span forever with no error. Register our own push-only worker at its
    // own scope, and bound every wait so this can never hang again.
    const withTimeout = (p, ms, what) => Promise.race([
      p,
      new Promise((_, rej) => setTimeout(() => rej(new Error(what + ' timed out after ' + ms + 'ms')), ms)),
    ]);
    const reg = await withTimeout(
      navigator.serviceWorker.register('/push-sw.js', { scope: '/push-sw-scope/' }),
      15000, 'service worker registration');
    // A freshly-registered worker may still be installing; subscribe needs it active.
    if (!reg.active) {
      await withTimeout(new Promise((res) => {
        const w = reg.installing || reg.waiting;
        if (!w) return res();
        w.addEventListener('statechange', () => { if (w.state === 'activated') res(); });
      }), 15000, 'service worker activation');
    }
    const sub = await withTimeout(reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: Uint8Array.from(
        atob(key.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
    }), 20000, 'push subscribe');
    await api.post('/push/register', { platform: 'web', token: sub.toJSON() });
    return { ok: true, permission: perm };
  } catch (e) {
    // LOUD: the reason reaches the operator instead of an eternal spinner.
    console.error('[push] enable failed:', e);
    return { ok: false, reason: e.message || 'Could not register for push.', permission: perm };
  }
}

// SURFACE 2 — Profile -> Notifications. Granular PER-CATEGORY toggles. The server's
// notification_preferences.categories is the source of truth (the same column the email digest reads),
// so this panel and the digest can never disagree.
function NotificationSettings() {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    try { setT(await api.get('/notifications/triad')); }
    catch (e) { setMsg('Could not load notification settings.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, enabled) => {
    setBusy(true); setMsg(null);
    try {
      await api.post('/notifications/categories', { category: key, enabled });
      await load();
    } catch (e) { setMsg(e.message || 'Could not save that change.'); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true); setMsg(null);
    const r = await enablePushNow();
    setMsg(r.ok ? 'Notifications are on for this device.' : r.reason);
    await load();
    setBusy(false);
  };

  if (!t) return <div className="profile-section" data-testid="notif-settings"><h3>Notifications</h3>
    <div style={{fontSize:13, color:'var(--text-secondary)'}}>Loading…</div></div>;

  return (
    <div className="profile-section" data-testid="notif-settings">
      <h3>Notifications</h3>
      <div style={{fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.55, marginBottom:12}}>
        Choose what is worth interrupting you for. These apply to both push and the email digest — one
        setting, not two.
      </div>

      <div data-testid="notif-device-row"
           style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
                   padding:'10px 12px', borderRadius:8, marginBottom:14,
                   background:'var(--card-bg, rgba(0,0,0,0.04))'}}>
        <div style={{fontSize:13}}>
          <b>This device</b>
          <div style={{fontSize:12, color:'var(--text-secondary)'}}>
            {t.has_token ? 'Registered for push.'
              : (t.push_available ? 'Not registered on this device yet.'
                                  : 'Push is not configured on this instance.')}
          </div>
        </div>
        {!t.has_token && t.push_available && (
          <button type="button" data-testid="notif-enable-device" onClick={enable} disabled={busy}
                  style={{padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer',
                          background:'var(--accent)', color:'var(--on-accent)', fontSize:13, fontWeight:600}}>
            {busy ? 'Enabling…' : 'Enable on this device'}
          </button>
        )}
      </div>

      {(t.categories || []).map(c => (
        <label key={c.key} data-testid={`notif-cat-${c.key}`}
               style={{display:'flex', alignItems:'flex-start', gap:10, padding:'9px 2px',
                       borderTop:'1px solid var(--border-color)', cursor:'pointer'}}>
          <input type="checkbox" checked={!!c.enabled} disabled={busy}
                 data-testid={`notif-toggle-${c.key}`}
                 onChange={(e) => toggle(c.key, e.target.checked)}
                 style={{marginTop:3, width:17, height:17, flex:'0 0 auto'}} />
          <span style={{fontSize:13.5}}>
            {c.label}
            <div style={{fontSize:12, color:'var(--text-secondary)'}}>{c.blurb}</div>
          </span>
        </label>
      ))}
      {msg && <div style={{fontSize:12.5, marginTop:10, color:'var(--text-secondary)'}}>{msg}</div>}
    </div>
  );
}

// SURFACE 3 — the VALUE-MOMENT soft pre-prompt. Shown ONCE, only after the user has seen their first
// score, and only when the server says should_soft_prompt (intent=yes AND no token AND never prompted).
// It explains the value in OUR words FIRST; the OS prompt is only reached if they tap Enable. Dismissing
// still records that it was shown, so it never nags.
function ValueMomentPush({ ready }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    if (!ready || done) return;
    let off = false;
    api.get('/notifications/triad')
      .then(d => { if (!off) setT(d); })
      .catch(() => { /* silent: never block the page on a prompt */ });
    return () => { off = true; };
  }, [ready, done]);

  // S8.1 — this prompt is suppressed while impersonating by the SERVER: /notifications/triad
  // returns should_soft_prompt=false for a read-only View-as session, so the condition below is
  // already false and nothing renders. Deliberately NOT also gated here: one authority per rule.
  if (!ready || done || !t || !t.should_soft_prompt || !t.push_available) return null;

  const record = async (granted) => {
    // S8.3 — the UI closes FIRST and unconditionally. A dismiss path whose completion depends on a
    // successful write is a trap by construction; the write is best-effort telemetry, not a gate.
    setDone(true);
    try { await api.post('/notifications/soft-prompted', { granted: !!granted }); } catch (e) { /* noop */ }
  };

  return (
    <div data-testid="value-moment-prompt"
         style={{position:'fixed', left:0, right:0, bottom:0, zIndex:9998, display:'flex',
                 justifyContent:'center', padding:'0 12px 16px'}}>
      <div style={{maxWidth:440, width:'100%', background:'var(--card-bg, #fff)',
                   border:'1px solid var(--border-color)', borderRadius:12,
                   boxShadow:'0 8px 28px rgba(0,0,0,0.18)', padding:'14px 16px'}}>
        <div style={{fontSize:14.5, fontWeight:600, marginBottom:6}}>
          Want to know when this changes?
        </div>
        <div style={{fontSize:13, color:'var(--text-secondary)', lineHeight:1.55, marginBottom:12}}>
          You said you wanted a nudge when something matters. We will tell you when a marker moves
          meaningfully or a new result lands — nothing else. Your phone will ask for permission next.
        </div>
        {msg && <div style={{fontSize:12.5, color:'var(--text-secondary)', marginBottom:10}}>{msg}</div>}
        <div style={{display:'flex', gap:8}}>
          <button type="button" data-testid="value-moment-enable" disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    const r = await enablePushNow();      // the OS prompt, on an explicit tap only
                    setMsg(r.ok ? null : r.reason);
                    await record(r.ok);
                    setBusy(false);
                  }}
                  style={{flex:1, padding:'10px 12px', borderRadius:8, border:'none', cursor:'pointer',
                          background:'var(--accent)', color:'var(--on-accent)', fontSize:13.5, fontWeight:600}}>
            {busy ? 'Enabling…' : 'Enable notifications'}
          </button>
          <button type="button" data-testid="value-moment-later" disabled={busy}
                  onClick={() => record(false)}
                  style={{padding:'10px 12px', borderRadius:8, cursor:'pointer', fontSize:13.5,
                          background:'transparent', color:'var(--text-secondary)',
                          border:'1px solid var(--border-color)'}}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

// ── KAL-081 (spec 5.4/5.5/5.6) — TARGET EDIT SHEET ────────────────────────────────────────
// Reached by tapping "Edit target" on a ring (PRIMARY surface). Renders the published guideline
// beside the user's own value, states the bounds WITH their reason, and says plainly that the
// change starts next cycle — the anti-gaming rule (5.5) is the most important line in the spec
// and the user must not discover it after the fact.
// ── KAL-083 (spec sections 3 + 4) — the monthly report, as an IN-APP VIEW ────────────────────
// Track A (behaviour) is always present and is the ONLY input to the trajectory percentage.
// Track B (blood) appears ONLY when a new draw landed inside the cycle; when none did, the
// section becomes the "book a test" state, which the spec identifies as the strongest honest
// nudge in the product. A Kalpa Age is NEVER shown as having moved from the same draw.
//
// No PDF, no archive, no download. Those need the renderer decision in KAL-015, which is
// unmade — and a report the user cannot re-read is still better than a promise of one.
const _CYCLE_VERDICT = {
  improving: { label: 'Improving', color: '#22c55e' },
  holding:   { label: 'Holding',   color: '#5EC8D8' },
  slipping:  { label: 'Slipping',  color: '#EAB308' },
  too_early: { label: 'Too early to call', color: '#8a9bb0' },
  no_comparison: { label: 'No comparison yet', color: '#8a9bb0' },
};

// KAL-115 — the hash AS THE PAGE LOADED, captured at module scope before any effect runs.
// CycleReportView mounts only once currentView flips to 'cycle-report', which is AFTER App's
// hash-tidying effect. Reading window.location.hash at that point can miss a `?cycle=` that was
// present in the original URL. Same module-capture pattern as GSILENT_AT_LOAD, for the same
// reason. The live hash still wins when it carries a cycle, so in-app navigation keeps working.
const CYCLE_HASH_AT_LOAD = (typeof window !== 'undefined' && window.location.hash) || '';

function CycleReportView({ onNav }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    const CYC = /[?&]cycle=(\d{4}-\d{2})/;
    const m = (window.location.hash || '').match(CYC) || CYCLE_HASH_AT_LOAD.match(CYC);
    api.get('/cycle/report' + (m ? '?cycle=' + m[1] : ''))
      .then(d => alive && (d && d.cycle ? setRep(d) : setErr(true)))
      .catch(() => alive && setErr(true));
  }, []);
  if (err) return <div className="kdrawer-empty">Your report isn’t available right now.</div>;
  if (!rep) return <LoadingSpinner message="Building your report…" />;

  const open = rep.state === 'in_progress';
  const act = rep.activity || {};
  const pace = rep.pace;
  const num = (n) => n == null ? '—' : Math.round(n).toLocaleString('en-GB');
  const hm = (n) => n == null ? '—'
    : Math.floor(n / 60) + 'h ' + String(Math.round(n % 60)).padStart(2, '0') + 'm';
  const fmt = (k, n) => k === 'sleep_duration' ? hm(n) : num(n);
  // Unit label on EVERY number (research §d). Sleep and steps are per DAY; AZM is per WEEK and
  // must never be summed into a cycle total.
  const unitOf = (k) => act[k] && act[k].period === 'week' ? '/week' : '/day';

  return (
    <div className="cycrep">
      <button className="cycrep-back" onClick={() => onNav && onNav('today')}>← Today</button>
      <div className="cycrep-eyebrow">{open ? 'In progress' : 'Report card'}</div>
      <h2 className="cycrep-title">{rep.cycle.label}</h2>
      <div className="cycrep-sub">
        {open
          ? rep.cycle.days_left + (rep.cycle.days_left === 1 ? ' day' : ' days') + ' remaining'
          : 'Closed'}
      </div>

      {/* ══ THE ONE HERO. In progress it is PACE on the primary BEHAVIOUR — never a
          physiological outcome, never a verdict while the cycle is open. ══ */}
      {open ? (
        pace ? (
          <div className="cycrep-hero">
            <div className="cycrep-hero-label">{pace.label} · pace</div>
            <div className="cycrep-hero-num">
              {num(pace.avg_so_far)}<span className="cycrep-act-unit">/day so far</span>
            </div>
            <div className="cycrep-hero-unit">
              target {num(pace.target_per_day)}/day · projected {num(pace.projected_per_day)}/day
            </div>
            <PaceBar pace={pace} />
            <div className="cycrep-hero-sub">
              {pace.on_pace
                ? 'At this rate you finish the month on target.'
                : pace.catchup_realistic
                  ? num(pace.rate_needed) + '/day for the remaining ' + pace.days_left +
                    (pace.days_left === 1 ? ' day' : ' days') + ' would bring the month to target.'
                  : 'The month’s average is out of reach from here, so the useful goal is the ' +
                    'daily one: ' + num(pace.target_per_day) + '/day from now on.'}
            </div>
            <div className="cycrep-fine">
              Based on {pace.days_with_data} {pace.days_with_data === 1 ? 'day' : 'days'} of data.
            </div>
          </div>
        ) : (
          <div className="cycrep-hero">
            <div className="cycrep-hero-label">Pace</div>
            <div className="cycrep-hero-num">—</div>
            <div className="cycrep-hero-sub">Not enough data yet this cycle to show a pace.</div>
          </div>
        )
      ) : (
        <div className="cycrep-hero">
          <div className="cycrep-hero-label">Trajectory vs {rep.trajectory.previous_cycle}</div>
          <div className="cycrep-hero-num">
            {rep.trajectory.pct != null
              ? (rep.trajectory.pct > 0 ? '+' : '') + rep.trajectory.pct + '%'
              : '—'}
          </div>
          <div className="cycrep-hero-sub">{deIsoDates(rep.trajectory.message)}</div>
        </div>
      )}

      {/* Sufficiency gate — never a percentage the data cannot carry. */}
      {rep.trajectory.verdict === 'insufficient' && (
        <div className="cycrep-gather">
          Still gathering — {rep.trajectory.days_with_data}
          {rep.trajectory.days_with_data === 1 ? ' day' : ' days'} so far.
          <div className="cycrep-fine">{deIsoDates(rep.trajectory.message)}</div>
        </div>
      )}

      {/* What moved — closed only. Direction by glyph AND signed number, never hue alone. */}
      {!open && rep.moved && rep.moved.length > 0 && (
        <section className="cycrep-sec">
          <span className="cycrep-sec-label">What moved</span>
          {rep.moved.map(m => (
            <div className="movedrow" key={m.metric}>
              {/* KAL-118 — THE ARROW FOLLOWS THE VALUES, NOT THE VERDICT. `pct` is sign-flipped
                  server-side for lower-is-better metrics (monthly_cycle.py:506) so it encodes
                  GOODNESS, not change. Rendered beside the raw before→after pair that produced
                  it, that read as a contradiction: resting heart rate 64 → 67 displayed as
                  "↓ −5%" — the number plainly rose while the arrow pointed down. Direction now
                  comes from before/after, and better/worse is carried by a WORD, so the good/bad
                  signal survives without relying on the sign (or on hue alone). */}
              <div className="movedrow-top">
                <span className="movedrow-label">{m.label}</span>
                <span className={'movedrow-pct' + (m.better ? ' is-better' : ' is-worse')}>
                  {m.after > m.before ? '↑' : '↓'} {Math.abs(m.pct)}%
                  <span className="movedrow-verdict">{m.better ? 'better' : 'worse'}</span>
                </span>
              </div>
              <div className="movedrow-bar">
                <div className="movedrow-fill" style={{ width: Math.min(100, Math.abs(m.pct)) + '%' }} />
              </div>
              <div className="cycrep-fine">
                {fmt(m.metric, m.before)} → {fmt(m.metric, m.after)}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Activity — unit on every number; AZM stated per WEEK. */}
      <section className="cycrep-sec">
        <span className="cycrep-sec-label">{open ? 'So far this cycle' : 'Activity'}</span>
        {['steps', 'active_zone_minutes', 'sleep_duration', 'resting_hr'].map(k => {
          const b = act[k]; if (!b) return null;
          const met = b.met_target;
          return (
            <div className={'cycrep-act' + (met ? ' is-met' : '')} key={k}>
              <div className="cycrep-act-top">
                <span className="cycrep-act-label">
                  {b.label}{met && <span className="cycrep-met">· met</span>}
                </span>
                <span className="cycrep-act-num">
                  {fmt(k, b.achieved)}
                  <span className="cycrep-act-unit">
                    {k === 'resting_hr' ? ' bpm' : unitOf(k)}
                  </span>
                </span>
              </div>
              {b.target != null && (() => {
                // KAL-137 — ZERO DATA DRAWS NO FILL. The floor was Math.max(2, …), a 2% sliver
                // that rendered identically for "no active zone minutes recorded at all" and
                // "you have made a start". On AZM, whose days_with_data is routinely 0, that
                // sliver was the only thing on the bar and it implied progress that does not
                // exist. The floor existed so a tiny non-zero value stayed visible — that case
                // is kept (anything above 0 still gets at least 2%); only true zero now draws
                // nothing, and the absence is stated in words beneath.
                const raw = Math.min(100, b.pct_of_target || 0);
                const w = raw <= 0 ? 0 : Math.max(2, raw);
                return (
                  <div className="cycrep-act-bar">
                    {w > 0 && <div className="cycrep-act-fill" style={{ width: w + '%' }} />}
                  </div>
                );
              })()}
              <div className="cycrep-fine">
                {b.days_with_data} {b.days_with_data === 1 ? 'day' : 'days'} of data
                {b.target ? ' · target ' + fmt(k, b.target) + unitOf(k) : ''}
                {b.guideline ? ' · guideline ' + fmt(k, b.guideline) + unitOf(k) : ''}
              </div>
            </div>
          );
        })}
      </section>

      {/* Where to put the effort — exactly two, behavioural, actionable this cycle.
          NO blood-panel prompt: that lives in the biomarker section only. */}
      {rep.focus && rep.focus.length > 0 && (
        <section className="cycrep-sec">
          <span className="cycrep-sec-label">Where to put the effort</span>
          {rep.focus.map((f, i) => (
            <div className="cycrep-focus" key={i}>
              <span className="cycrep-focus-n">{i + 1}</span>
              <div>
                <div className="cycrep-focus-title">{f.title}</div>
                <div className="cycrep-verdict-msg">{deIsoDates(f.detail)}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* The ONLY permitted biological-age surface in the tracker: a text reference carrying no
          number that could imply this month moved it (spec 2e). */}
      {rep.bio_age_reference && (
        <div className="cycrep-bioref">
          {rep.bio_age_reference}{' '}
          <button onClick={() => onNav && onNav('longevity')}>See your biomarkers</button>
        </div>
      )}
    </div>
  );
}

// Burn-up pace bar. The fill width and the label BOTH come from `pace.pct`, so the marker
// position is the labelled percentage by construction — the v1 bar drew 25% beside a "89%"
// label. The pro-rata "expected by today" mark is a separate thin rule and is LABELLED, so the
// on/behind state never rides on colour alone.
function PaceBar({ pace }) {
  const pct = Math.max(0, Math.min(100, pace.pct));
  const exp = Math.max(0, Math.min(100, pace.expected_pct));
  return (
    <div className="pacebar">
      <div className="pacebar-track">
        <div className="pacebar-fill" style={{ width: pct + '%' }} />
        <div className="pacebar-expected" style={{ left: exp + '%' }}
             title={'Expected by today: ' + exp + '%'} />
      </div>
      <div className="pacebar-scale">
        <span>{pct}% of the month’s target</span>
        <span>expected {exp}%</span>
      </div>
      <div className="pacebar-legend">
        <span className="pacebar-state">{pace.state_label}</span>
        {pace.on_pace
          ? 'Ahead of the pro-rata line for day ' + pace.days_elapsed + '.'
          : 'Behind the pro-rata line for day ' + pace.days_elapsed + '.'}
      </div>
    </div>
  );
}

// ── KAL-082 (spec section 6) — the Today cycle card ──────────────────────────────────────────
// "7 days left in your August report", progress against two or three LIVE targets, and at most
// two suggested actions. Concrete and current — the point of the whole mechanic is a loop the
// user can close inside 30 days, against numbers they can still move.
//
// Renders NOTHING on failure or when there is no wearable data to show progress against: an
// empty countdown with no numbers under it is worse than no card, because it advertises a
// deadline the user has no way to act on.
// ── KAL-132 — the v2 Today cycle card (Direction B: two marks on one track) ──────────────────
// The organising idea: pace is the frame. ONE track carries where you are and where pro-rata
// says you should be by today, and the distance between them is the message — it reads before
// any word does. Beneath it the month is an OBJECT (a 31-cell day strip), not the sentence
// "19 days left" it used to be.
//
// Three rules this component exists to hold, each with research behind it:
//   • Hue means IDENTITY, not judgement. Steps is teal whether it is going well or badly, so a
//     colour-deficient reader (~1 in 12 men) and a Nepali reader (for whom red is auspicious
//     and green means nature) both lose nothing — the state is carried by a WORD and a glyph.
//   • Behind is "Below range", never red and never shame. Strava frames a quiet week as
//     legitimate recovery, and the overjustification literature is why there is no counter,
//     no badge and no streak anywhere on this card.
//   • A fill is a TINT, never a saturated block. Direction C proved the trap: a solid fill
//     behind the hero number erases it. The number never sits on the filled zone.
function _sinceKey(p) { return 'kalpa.cycle.lastseen' + (p ? '.' + p : ''); }

// "Since you last looked" — the returning-user reward is INFORMATION, not points (Deci 1999:
// positive feedback ENHANCES intrinsic motivation, contingent rewards undermine it). It is
// device-local by design: it answers "what changed since I last looked at this", which is a
// property of this device's last view, not of the account. NEVER fabricated — if nothing moved,
// or there is no prior snapshot, it renders nothing at all.
function _sinceLastLooked(card, prev) {
  if (!prev || !card || !card.cycle) return null;
  const bits = [];
  const cur = {};
  (card.targets || []).forEach(t => { cur[t.metric] = t.achieved; });
  const stepsNow = cur.steps, stepsWas = prev.metrics && prev.metrics.steps;
  if (stepsNow != null && stepsWas != null && Math.round(stepsNow) > Math.round(stepsWas)) {
    bits.push(<><b>+{Math.round(stepsNow - stepsWas).toLocaleString('en-GB')} steps</b> a day on average</>);
  }
  const slNow = cur.sleep_duration, slWas = prev.metrics && prev.metrics.sleep_duration;
  if (slNow != null && slWas != null && Math.round(slNow) !== Math.round(slWas)) {
    bits.push(<>your sleep average moved to <b>{_fmtHm(slNow / 60)}</b></>);
  }
  const dayNow = card.cycle.days_elapsed, dayWas = prev.days_elapsed;
  if (dayNow != null && dayWas != null && dayNow > dayWas) {
    const d = dayNow - dayWas;
    bits.push(<><b>{d} day{d === 1 ? '' : 's'}</b> of {card.cycle.month_name} passed</>);
  }
  if (!bits.length) return null;
  return bits;
}

function CycleCardV2({ card, onNav }) {
  const [since, setSince] = useState(null);
  // Snapshot AFTER computing the delta, so the first render of a session still has something
  // to compare against. Wrapped because Safari private mode throws on localStorage writes.
  useEffect(() => {
    if (!card || !card.cycle) return;
    const key = _sinceKey(card.cycle.key);
    let prev = null;
    try { prev = JSON.parse(window.localStorage.getItem(key) || 'null'); } catch { prev = null; }
    setSince(_sinceLastLooked(card, prev));
    try {
      const metrics = {};
      (card.targets || []).forEach(t => { metrics[t.metric] = t.achieved; });
      window.localStorage.setItem(key, JSON.stringify({
        metrics, days_elapsed: card.cycle.days_elapsed, at: Date.now(),
      }));
    } catch { /* storage unavailable — the card simply shows no "since" line */ }
  }, [card]);

  if (!card || !card.cycle) return null;
  const pace = card.pace;
  const cyc = card.cycle;
  const n = (x) => x == null ? '—' : Math.round(x).toLocaleString('en-GB');
  const go = () => {
    try { window.history.pushState({}, '', '#cycle-report'); } catch {}
    if (onNav) onNav('cycle-report');
  };
  const byMetric = {};
  (card.targets || []).forEach(t => { byMetric[t.metric] = t; });
  const sleep = byMetric.sleep_duration, azm = byMetric.active_zone_minutes;

  // The tile array. `fillPct` null => NO fill drawn at all (KAL-137): zero data and "a little
  // progress" must not look the same.
  const tiles = [
    sleep && {
      key: 'sleep_duration', label: 'Sleep', hue: 'sleep',
      value: sleep.achieved != null ? _fmtHm(sleep.achieved / 60) : '—',
      unit: sleep.achieved != null ? '/night' : '',
      fillPct: (sleep.achieved != null && sleep.target) ? Math.min(100, sleep.pct_of_target || 0) : null,
      sub: sleep.target
        ? (sleep.achieved != null
            ? `${sleep.pct_of_target}% of your ${_fmtHm(sleep.target / 60)} target`
            : `no sleep recorded · target ${_fmtHm(sleep.target / 60)} a night`)
        : 'no target set',
    },
    azm && {
      key: 'active_zone_minutes', label: 'Active zone min', hue: 'azm',
      // AZM is a WEEKLY guideline and is never summed into a cycle total (research §d).
      value: azm.achieved != null ? n(azm.achieved) : '—',
      unit: azm.achieved != null ? '/week' : '',
      fillPct: (azm.achieved != null && azm.target) ? Math.min(100, azm.pct_of_target || 0) : null,
      sub: azm.achieved != null
        ? `${n(azm.achieved)} of ${n(azm.target)} this week`
        : `nothing recorded this week · guideline ${n(azm.target)}/week`,
    },
  ].filter(Boolean);

  return (
    <div className="today-card today-stagger cyc2" style={{ animationDelay: '80ms' }}>
      {since && (
        <div className="cyc2-since">
          <span className="cyc2-since-dot" aria-hidden="true" />
          <span className="cyc2-since-t">
            Since you last looked — {since.map((b, i) => (
              <React.Fragment key={i}>{i > 0 ? (i === since.length - 1 ? ', and ' : ', ') : ''}{b}</React.Fragment>
            ))}.
          </span>
        </div>
      )}

      <button type="button" className="cyc2-main" onClick={go}
        aria-label={`Open your ${cyc.month_name} report`}>
        <div className="cyc2-head">
          <span className="cyc2-eyebrow">
            {cyc.month_name} report · day {cyc.days_elapsed} of {cyc.days_total}
          </span>
          <span className="cyc2-arrow" aria-hidden="true">→</span>
        </div>

        {pace ? (
          <>
            <div className="cyc2-hero">
              <span className="cyc2-hero-num">{n(pace.avg_so_far)}</span>
              <span className="cyc2-hero-of">
                steps/day so far · target {n(pace.target_per_day)}/day
              </span>
            </div>
            <div className="cyc2-chiprow">
              {/* The state is a WORD and a GLYPH first; the hue is the metric's identity and
                  carries no verdict. "Below range" is Strava's framing, deliberately. */}
              <span className={'cyc2-chip' + (pace.on_pace ? ' is-on' : '')}>
                <span aria-hidden="true">{pace.on_pace ? '▲' : '▼'}</span>
                {pace.on_pace ? 'On pace' : 'Below range'}
              </span>
            </div>

            {/* THE TRACK — one object, two marks. Fill width and its label are both pace.pct,
                so the marker position IS the labelled percentage by construction. */}
            <div className="cyc2-flagrow">
              <span className="cyc2-flag cyc2-flag-exp" style={{ left: pace.expected_pct + '%' }}>
                expected {pace.expected_pct}%
              </span>
            </div>
            <div className="cyc2-track" role="img"
                 aria-label={`You are at ${pace.pct}% of ${cyc.month_name}'s step target; ${pace.expected_pct}% expected by today`}>
              <div className="cyc2-track-fill" style={{ width: pace.pct + '%' }} />
              {pace.expected_pct > pace.pct && (
                <div className="cyc2-track-gap"
                     style={{ left: pace.pct + '%', width: (pace.expected_pct - pace.pct) + '%' }} />
              )}
              <div className="cyc2-track-exp" style={{ left: pace.expected_pct + '%' }} />
            </div>
            <div className="cyc2-flagrow">
              <span className="cyc2-flag cyc2-flag-you" style={{ left: pace.pct + '%' }}>
                you {pace.pct}%
              </span>
            </div>
            <div className="cyc2-scale">
              <span>start of {cyc.month_name}</span><span>the month's target</span>
            </div>
            <div className="cyc2-fine">
              Based on {pace.days_with_data} {pace.days_with_data === 1 ? 'day' : 'days'} of data.
            </div>
          </>
        ) : (
          <div className="cyc2-nopace">
            <div className="cyc2-hero-of">Not enough data yet this cycle to show a pace.</div>
          </div>
        )}

        {/* THE MONTH AS A SHAPE — you see where you are before you read a word. */}
        <div className="cyc2-daystrip" role="img"
             aria-label={`${cyc.month_name}: ${cyc.days_elapsed} of ${cyc.days_total} days elapsed`}>
          {Array.from({ length: cyc.days_total }, (_, i) => (
            <span key={i} className={'cyc2-day'
              + (i < cyc.days_elapsed - 1 ? ' is-past' : '')
              + (i === cyc.days_elapsed - 1 ? ' is-today' : '')} />
          ))}
        </div>
        <div className="cyc2-countdown">{card.countdown}</div>
      </button>

      {tiles.length > 0 && (
        <div className="cyc2-tiles">
          {tiles.map(t => (
            <div className={'cyc2-tile cyc2-hue-' + t.hue} key={t.key}>
              {t.fillPct != null && t.fillPct > 0 && (
                <div className="cyc2-tile-fill" style={{ width: t.fillPct + '%' }} />
              )}
              <div className="cyc2-tile-in">
                <div className="cyc2-tile-lab">{t.label}</div>
                <div className="cyc2-tile-num">
                  {t.value}{t.unit && <span className="cyc2-tile-unit">{t.unit}</span>}
                </div>
                <div className="cyc2-tile-sub">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CycleCard({ onNav }) {
  const [card, setCard] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/cycle/card')
      .then(d => alive && setCard(d && d.cycle ? d : null))
      .catch(() => alive && setCard(null));
    return () => { alive = false; };
  }, []);
  // KAL-132 — the v2 surface is CONSTRUCTED only when the flag is on. Not hidden by CSS, not
  // rendered-then-discarded: flag off means this component never exists in the tree, so an
  // instance with the flag off cannot be affected by a v2 bug.
  if (FEATURES_GLOBAL.cycle_ui_v2) return <CycleCardV2 card={card} onNav={onNav} />;
  if (!card || !Array.isArray(card.targets) || card.targets.length === 0) return null;
  // ONE number on the card, and it is the primary behaviour's daily average — same hero as
  // the report, same unit. The v1 card stacked three equal-weight bars, which is four heroes.
  const primary = card.targets.find(t => t.metric === 'steps') || card.targets[0];
  const n = (x) => x == null ? '—' : Math.round(x).toLocaleString('en-GB');
  const per = primary.period === 'week' ? '/week' : '/day';
  return (
    <button type="button" className="today-card today-stagger cyclecard"
      style={{ animationDelay: '80ms' }}
      onClick={() => { try { window.history.pushState({}, '', '#cycle-report'); } catch {} if (onNav) onNav('cycle-report'); }}>
      <div className="cyclecard-head">
        <span className="cyclecard-count">{card.countdown}</span>
        <span className="cyclecard-arrow">→</span>
      </div>
      <div className="cyclecard-hero">
        <div className="cyclecard-hero-num">
          {n(primary.achieved)}<span className="cycrep-act-unit">{per}</span>
        </div>
        <div className="cyclecard-hero-unit">
          {primary.label.toLowerCase()} so far · target {n(primary.target)}{per}
        </div>
      </div>
      {card.actions && card.actions.length > 0 && (
        <ul className="cyclecard-actions">
          {card.actions.slice(0, 2).map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      )}
    </button>
  );
}

function TargetEditSheet({ metric, onClose, onSaved }) {
  const [spec, setSpec] = useState(null);
  const [val, setVal] = useState('');
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/cycle/targets').then(d => {
      if (!alive) return;
      const t = (d.targets || []).find(x => x.metric === metric);
      setSpec(t || null);
      if (t) setVal(String(t.pending ? t.pending.value : t.value));
    }).catch(() => alive && setSpec(null));
    return () => { alive = false; };
  }, [metric]);
  if (!spec) return null;
  const isSleep = metric === 'sleep_duration';
  const show = (n) => isSleep ? `${Math.floor(n / 60)}h ${String(Math.round(n % 60)).padStart(2, '0')}m`
                              : `${Number(n).toLocaleString('en-GB')} ${spec.unit}`;
  const save = () => {
    const n = Number(val);
    // Validate client-side FIRST. api.put throws a bare "API 400" and discards the response
    // body, so the server's bound_reason would never reach the user. The server still enforces
    // the same bounds — this is the message, not the guard.
    if (!Number.isFinite(n)) { setErr('Enter a number.'); return; }
    if (spec.floor != null && n < spec.floor) { setErr(`Minimum is ${show(spec.floor)}. ${spec.bound_reason || ''}`); return; }
    if (spec.ceiling != null && n > spec.ceiling) { setErr(`Maximum is ${show(spec.ceiling)}. ${spec.bound_reason || ''}`); return; }
    setSaving(true); setErr(null);
    api.put(`/cycle/targets/${metric}`, { value: n })
      .then(r => { if (r && r.ok) { onSaved && onSaved(r); onClose(); } else setErr((r && r.error) || 'Could not save'); })
      .catch(() => setErr('Could not save that target. Please try again.'))
      .finally(() => setSaving(false));
  };
  return (
    <KalpaDrawer title={`Your ${spec.label.toLowerCase()} target`} onClose={onClose}>
      <div className="kdrawer-sleep-summary">
        Currently <strong>{show(spec.value)}</strong> per {spec.period}
        {spec.is_custom ? ' (your own target)' : ' (the default)'}.
      </div>
      <div className="tgt-row">
        <input className="tgt-input" type="number" inputMode="numeric"
          value={val} min={spec.floor} max={spec.ceiling} step={spec.step || 1}
          onChange={e => setVal(e.target.value)} aria-label={`${spec.label} target`} />
        <span className="tgt-unit">{isSleep ? 'minutes' : spec.unit}</span>
      </div>
      {isSleep && val !== '' && Number.isFinite(Number(val)) && (
        <div className="kdrawer-sleep-note" style={{ borderLeft: 'none', paddingLeft: 0 }}>
          That is {show(Number(val))} a night.
        </div>
      )}
      {spec.guideline != null && (
        <div className="tgt-guideline">
          Published guideline: <strong>{show(spec.guideline)}</strong>
          {spec.guideline_note ? ` — ${spec.guideline_note}` : ''}
        </div>
      )}
      {spec.bound_reason && <div className="kdrawer-sleep-note">{spec.bound_reason}</div>}
      {/* spec 5.5 — stated BEFORE saving, not discovered afterwards. */}
      {/* KAL-092 — THE ASYMMETRIC RULE, stated BEFORE saving and specific to what the user is
          about to do. The flat "always next cycle" rule was wrong: correct for a lower, perverse
          for a raise, because it deferred exactly the behaviour we want to encourage. The
          direction is computed from what they have typed, so the sentence updates as they type
          rather than describing a generic policy they must then apply to themselves. */}
      {(() => {
        const n = Number(val);
        const raising = !Number.isFinite(n) || spec.value == null || n >= Number(spec.value);
        return (
          <div className={'tgt-nextcycle' + (raising ? ' is-now' : '')}>
            {raising
              ? <><strong>This applies straight away.</strong> Raising a target makes the cycle
                  harder, so there is no reason to make you wait for it.</>
              : <><strong>This starts next cycle.</strong> Lowering a target takes effect at the
                  start of your next report, so a cycle already under way can never be made
                  easier after the fact.</>}
          </div>
        );
      })()}
      {spec.pending && (
        <div className="kdrawer-sleep-note">
          You already have {show(spec.pending.value)} queued from {spec.pending.effective_from}.
          Saving replaces it.
        </div>
      )}
      {err && <div className="tgt-err">{err}</div>}
      <button className="tgt-save" disabled={saving} onClick={save}>
        {saving ? 'Saving…' : 'Save target'}
      </button>
    </KalpaDrawer>
  );
}

// ── KAL-081 (spec 5.4) — "Your targets", the SECONDARY surface ───────────────────────────────
// One screen listing every target with the published guideline beside it, for the user who wants
// to review everything at once. Deliberately lists the NOT-settable metrics too, with the reason:
// a metric that is simply absent reads as an oversight, whereas a stated reason is a design
// decision the user can disagree with. Spec 5.4 point 3: target-setting is NOT on Today.
function YourTargetsSection() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get('/cycle/targets')
      .then(d => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [tick]);
  if (!data || !Array.isArray(data.targets)) return null;
  const fmt = (t, n) => n == null ? '—'
    : t.metric === 'sleep_duration'
      ? `${Math.floor(n / 60)}h ${String(Math.round(n % 60)).padStart(2, '0')}m`
      : `${Number(n).toLocaleString('en-GB')}${t.unit ? ' ' + t.unit : ''}`;
  return (
    <div className="profile-section">
      <div className="profile-section-header"><h3>Your targets</h3></div>
      <p style={{ fontSize: '0.8rem', lineHeight: 1.55, color: 'var(--text-muted)', margin: '0 0 6px' }}>
        {data.anti_gaming_note}
      </p>
      {/* KAL-091 — SETTABLE TARGETS GET THE VISUAL WEIGHT. Resting HR, HRV and calories used to
          occupy half this screen at equal weight to real targets, which buried the three things
          the user can actually change. They are collapsed behind one row — but the explanations
          are KEPT inside it, because each pre-empts a support question ("why can't I set my
          resting heart rate?"). Collapsed, not deleted. */}
      {data.targets.filter(t => t.settable).map(t => (
        <div className="tgt-list-row" key={t.metric}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{t.label}</div>
            <div className="tgt-list-meta">
              Guideline {fmt(t, t.guideline)} per {t.period}
              {t.guideline_note ? ` · ${t.guideline_note}` : ''}
              {t.is_custom ? ' · you have set your own' : ' · using the default'}
            </div>
            {t.pending && (
              <div className="tgt-queued">
                {fmt(t, t.pending.value)} starts {t.pending.effective_from} — lowering a target
                takes effect next cycle.
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="tgt-list-val tgt-list-val-lg">{fmt(t, t.value)}</div>
            <button className="fitness-target-edit" style={{ marginLeft: 0, marginTop: 4 }}
                    onClick={() => setEdit(t.metric)}>Change</button>
          </div>
        </div>
      ))}
      <details className="tgt-why">
        <summary>Why some measures aren’t targets</summary>
        {data.targets.filter(t => !t.settable).map(t => (
          <div className="tgt-why-row" key={t.metric}>
            <div className="tgt-why-label">{t.label}</div>
            <div className="tgt-list-meta">{t.not_settable_reason}</div>
          </div>
        ))}
      </details>
      {edit && <TargetEditSheet metric={edit} onClose={() => setEdit(null)}
                                          onSaved={() => setTick(x => x + 1)} />}
    </div>
  );
}

function ProfilePage({ onProfileSwitch }) {
  useLocale();   // LOCALE (2d) — re-render the info grid when the units toggle flips
  const _privPack = privacyPack(FEATURES_GLOBAL.country);   // 2e — state field is pack-driven
  const [userData, setUserData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [fitbitConnecting, setFitbitConnecting] = useState(false);
  const [message, setMessage] = useState(null);
  const [dnaUploading, setDnaUploading] = useState(false);
  const [dnaResult, setDnaResult] = useState(null);
  const [fitnessProviders, setFitnessProviders] = useState([]);
  // RUN W — structured lifestyle (diet/alcohol/smoking/vaping) + surfaced weight,
  // two-way synced with the onboarding wizard via /api/profile/lifestyle.
  const [lifestyle, setLifestyle] = useState({ diet: [], alcohol: '', smoking: '', vaping: '' });
  const [lifeWeight, setLifeWeight] = useState(null);
  const [manualWeight, setManualWeight] = useState(null);
  const features = useFeatures(); // genetics flag — hides the Genetic Data section on NPL

  const loadData = useCallback(async () => {
    try {
      const user = await api.get('/users/current');
      setUserData(user);
      setEditForm({
        display_name: user.display_name || '',
        email: user.email || '',
        dob: user.dob || '',
        biological_sex: user.biological_sex || '',
        diet: user.diet || '',
        medical_notes: user.medical_notes || '',
        height_cm: user.height_cm || '',
        npl_patient_id: user.npl_patient_id || '',
        // 2e — default NY only where the jurisdiction pack surfaces the field, so what the
        // select SHOWS is what a save would store (an unset '' behind a displayed "New York"
        // would have been a silent mismatch). Elsewhere it stays '' and is never written.
        state: user.state || (privacyPack(FEATURES_GLOBAL.country).stateField ? 'NY' : ''),
      });
      if (user.is_admin) {
        const users = await api.get('/users');
        setAllUsers(users);
      }
    } catch (e) { console.error('Profile load error:', e); }
    try {
      const life = await api.get('/profile/lifestyle');
      if (life) {
        setLifestyle({ diet: life.diet || [], alcohol: life.alcohol || '', smoking: life.smoking || '', vaping: life.vaping || '' });
        setLifeWeight(life.weight || null);
      }
    } catch (e) { /* non-fatal */ }
    try {
      const fp = await api.get('/fitness/providers');
      setFitnessProviders(fp.providers || []);
    } catch (e) {
      // fall back to legacy endpoint
      try { const fp2 = await api.get('/user/fitness-providers'); setFitnessProviders(fp2.providers || []); } catch {}
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // diet is now owned by the lifestyle store (multi-select); keep it out of
      // the users payload so the two don't fight over the single users.diet col.
      const { diet: _ignore, ...userPayload } = editForm;
      if (userData?.is_admin) {
        await api.put(`/users/${userData.profile_name}`, userPayload);
      } else {
        await api.put('/users/current', userPayload);
      }
      try {
        const body = { diet: lifestyle.diet, alcohol: lifestyle.alcohol, smoking: lifestyle.smoking, vaping: lifestyle.vaping };
        if (manualWeight) body.weight_kg = manualWeight;
        await api.put('/profile/lifestyle', body);
      } catch (e) { /* non-fatal — profile fields already saved */ }
      setMessage({ type: 'success', text: 'Profile updated' });
      setEditing(false);
      loadData();
      if (onProfileSwitch) onProfileSwitch();
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to save: ' + e.message });
    }
    setSaving(false);
  };

  const handleFitbitConnect = async () => {
    setFitbitConnecting(true);
    try {
      const data = await api.get('/user/fitbit/connect');
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        setMessage({ type: 'error', text: 'Failed to get Fitbit auth URL' });
        setFitbitConnecting(false);
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Fitbit connect failed: ' + e.message });
      setFitbitConnecting(false);
    }
  };

  const handleFitbitDisconnect = async () => {
    try {
      await api.post('/user/fitbit/disconnect');
      setMessage({ type: 'success', text: 'Fitbit disconnected' });
      loadData();
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to disconnect: ' + e.message });
    }
  };

  const handleFitbitSync = async () => {
    try {
      setMessage({ type: 'info', text: 'Sync started — running in background…' });
      await api.post('/fitbit/sync', { full: false });
      setMessage({ type: 'success', text: 'Fitbit sync running in background. Data will appear within a minute.' });
      setTimeout(() => setMessage(null), 8000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Sync failed: ' + e.message });
    }
  };

  const handleFitbitFullSync = async () => {
    try {
      setMessage({ type: 'info', text: 'Full historical sync started — this may take several minutes…' });
      await api.post('/fitbit/sync', { full: true });
      setMessage({ type: 'success', text: 'Full sync running in background. All historical data will be loaded shortly.' });
      setTimeout(() => setMessage(null), 10000);
    } catch (e) {
      setMessage({ type: 'error', text: 'Full sync failed: ' + e.message });
    }
  };

  const handleDnaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setMessage({ type: 'error', text: 'Please select a CSV file' });
      return;
    }
    setDnaUploading(true);
    setDnaResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${API_BASE}/genetics/upload-csv`, { method: 'POST', body: fd, credentials: 'include' });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || 'Upload failed');
      setDnaResult(result);
      setMessage({ type: 'success', text: `Imported ${result.variants_imported?.toLocaleString('en-GB')} genetic variants` });
      setTimeout(() => setMessage(null), 5000);
    } catch (e) {
      setMessage({ type: 'error', text: 'DNA upload failed: ' + e.message });
    }
    setDnaUploading(false);
    e.target.value = '';
  };

  const handleSwitchProfile = async (profileName) => {
    try {
      await api.post('/users/switch', { profile_name: profileName });
      if (onProfileSwitch) onProfileSwitch();
      loadData();
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to switch: ' + e.message });
    }
  };

  if (loading) return <LoadingSpinner message="Loading profile..." />;

  const isViewingOther = !!userData?.viewing_as;

  return (
    <div className="profile-page">
      <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Profile Settings</h2>
      {message && <div className={`profile-message profile-message-${message.type}`}>{message.text}</div>}

      {/* USER-MGMT build P5: the Profiles roster moved OFF #profile to the
          dedicated admin-gated User Management page. #profile is now purely the
          signed-in user's OWN profile (personal info, connections, privacy) for
          ALL roles. The "Viewing X" banner stays so an active superadmin
          view-as (set from User Management) is still clearly indicated here. */}

      {isViewingOther && (
        <div className="profile-viewing-banner">
          Viewing {userData.display_name}'s profile
          <button onClick={() => handleSwitchProfile(userData.logged_in_profile)}>Back to my profile</button>
        </div>
      )}

      <div className="profile-section">
        <div className="profile-section-header">
          <h3>Personal Information</h3>
          {!editing && <button className="profile-edit-btn" onClick={() => setEditing(true)}>Edit</button>}
        </div>
        {/* Always-visible read-only email: this is the login identity,
            not an editable field. Surfaced at the top of the section
            so a user can confirm which account they are signed in
            with at a glance (matters once family-account switching is
            in play). Admins can still change a user's email via the
            edit form below. */}
        <div
          className="profile-email-display"
          style={{
            padding: '10px 14px',
            background: 'var(--card-bg, rgba(0,0,0,0.04))',
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span style={{fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600}}>
            Signed in as
          </span>
          <span style={{fontSize: 13.5, color: 'var(--text-primary)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {userData?.email || '—'}
          </span>
        </div>
        {/* §P — value-explaining section intro (benefit-only, honest: all optional). */}
        <p style={_profileIntroStyle}>{_PROFILE_SECTION_INTRO}</p>
        {editing ? (
          <div className="profile-form">
            <label>Display Name<input type="text" value={editForm.display_name} onChange={e => setEditForm({...editForm, display_name: e.target.value})} /></label>
            {!!userData?.is_admin && (
              <label>Email<input type="email" value={editForm.email || ''} onChange={e => setEditForm({...editForm, email: e.target.value})} /></label>
            )}
            {!!userData?.is_admin && FEATURES_GLOBAL.order_lab && (
              <label>NPL Patient ID
                <input type="text" value={editForm.npl_patient_id || ''}
                  onChange={e => setEditForm({...editForm, npl_patient_id: e.target.value})}
                  placeholder="e.g. NPL12345" />
              </label>
            )}
            <label>Date of Birth<input type="date" value={editForm.dob || ''} onChange={e => setEditForm({...editForm, dob: e.target.value})} /></label>
            <div style={_fieldCaptionStyle}>{_PROFILE_FIELD_CAPTION.dob}</div>
            <label>Biological Sex
              <select value={editForm.biological_sex} onChange={e => setEditForm({...editForm, biological_sex: e.target.value})}>
                <option value="">Prefer not to say</option><option value="male">Male</option><option value="female">Female</option>
              </select>
            </label>
            <div style={_fieldCaptionStyle}>{_PROFILE_FIELD_CAPTION.biological_sex}</div>
            {/* 2e — state of residence. Rendered only where the jurisdiction pack asks for
                it, so UK/NPL profiles are unchanged. Default NY on first edit; never
                written to an existing row behind the user's back. */}
            {_privPack.stateField && (
              <>
                <label>State of residence
                  <select value={editForm.state || 'NY'}
                    onChange={e => setEditForm({...editForm, state: e.target.value})}>
                    {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                  </select>
                </label>
                <div style={_fieldCaptionStyle}>{_privPack.stateNote}</div>
              </>
            )}
            <label>Height (cm)<input type="number" value={editForm.height_cm || ''} onChange={e => setEditForm({...editForm, height_cm: e.target.value ? parseFloat(e.target.value) : ''})} /></label>
            <div style={_fieldCaptionStyle}>{_PROFILE_FIELD_CAPTION.height}</div>
            <div style={{marginTop:6, paddingTop:12, borderTop:'1px solid var(--border-color)'}}>
              <WeightField weight={lifeWeight} onWeightManual={(kg) => setManualWeight(kg)} />
            </div>
            <label>Medical Notes<textarea value={editForm.medical_notes || ''} onChange={e => setEditForm({...editForm, medical_notes: e.target.value})} rows={3} /></label>
          </div>
        ) : (
          <div className="profile-info-grid">
            <div className="profile-info-item"><span className="profile-info-label">Name</span><span>{userData?.display_name}</span></div>
            {/* Email moved to the always-visible "Signed in as" row above. */}
            <div className="profile-info-item"><span className="profile-info-label">Date of Birth</span><span>{userData?.dob ? fmtDateNumeric(userData.dob) : 'Not set'}</span></div>
            <div className="profile-info-item"><span className="profile-info-label">Sex</span><span>{userData?.biological_sex ? _prettyLife(userData.biological_sex) : 'Not set'}</span></div>
            {_privPack.stateField && (
              <div className="profile-info-item"><span className="profile-info-label">State</span><span>{userData?.state || 'Not set'}</span></div>
            )}
            <div className="profile-info-item"><span className="profile-info-label">Height</span><span>{userData?.height_cm ? fmtHeight(userData.height_cm) : 'Not set'}</span></div>
            <div className="profile-info-item"><span className="profile-info-label">Weight</span><span>{lifeWeight && lifeWeight.weight_kg != null ? `${fmtWeight(lifeWeight.weight_kg)}${lifeWeight.bmi != null ? ` · BMI ${lifeWeight.bmi}` : ''}${lifeWeight.source === 'measured' ? '' : ' (manual)'}` : 'Not set'}</span></div>
            {userData?.medical_notes && <div className="profile-info-item profile-info-full"><span className="profile-info-label">Medical Notes</span><span>{userData.medical_notes}</span></div>}
            {/* LOCALE (2d) — units toggle. Offered only where the country default makes it
                meaningful (US), so UK/NPL render exactly what they rendered before. Display
                only: the stored value stays kg/cm and no score is recomputed. */}
            {unitsToggleAvailable() && (
              <div className="profile-info-item">
                <span className="profile-info-label">Units</span>
                <span>
                  <button type="button" className="units-toggle-btn"
                    aria-pressed={!isImperial()}
                    onClick={() => setUnitSystem(isImperial() ? 'metric' : 'imperial')}>
                    {isImperial() ? 'Imperial (lb, ft/in)' : 'Metric (kg, cm)'}
                  </button>
                  <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--text-dim)' }}>tap to switch</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* §P — Lifestyle is its OWN section (never buried in Personal Info). Same
          shared LifestyleFields component/styling as the onboarding gate; edits
          save via the same handleSave → PUT /profile/lifestyle (two-way synced
          with the gate). Governed by the single `editing` flag. */}
      <YourTargetsSection />

      {/* KAL-296 — native-only; renders nothing in a browser. */}
      <RefreshFromDeviceButton />

      <div className="profile-section">
        <div className="profile-section-header">
          <h3>Lifestyle</h3>
          {!editing && <button className="profile-edit-btn" onClick={() => setEditing(true)}>Edit</button>}
        </div>
        <p style={_profileIntroStyle}>{_PROFILE_SECTION_INTRO}</p>
        {editing ? (
          <div className="profile-form">
            <LifestyleFields value={lifestyle} onChange={setLifestyle} showWeight={false} />
          </div>
        ) : (
          <div className="profile-info-grid">
            <div className="profile-info-item"><span className="profile-info-label">Diet</span><span>{(lifestyle.diet && lifestyle.diet.length) ? lifestyle.diet.map(_prettyLife).join(', ') : (userData?.diet ? _prettyLife(userData.diet) : 'Not set')}</span></div>
            <div className="profile-info-item"><span className="profile-info-label">Alcohol</span><span>{_prettyLife(lifestyle.alcohol) || 'Not set'}</span></div>
            <div className="profile-info-item"><span className="profile-info-label">Smoking</span><span>{_prettyLife(lifestyle.smoking) || 'Not set'}</span></div>
            <div className="profile-info-item"><span className="profile-info-label">Vaping</span><span>{_prettyLife(lifestyle.vaping) || 'Not set'}</span></div>
          </div>
        )}
        {/* Shared Save/Cancel — persists BOTH Personal Info + Lifestyle in one save. */}
        {editing && (
          <div className="profile-form-actions" style={{marginTop:16}}>
            <button className="profile-save-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button className="profile-cancel-btn" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        )}
      </div>

      <div className="profile-section">
        <h3>Health Platforms</h3>
        <p style={{fontSize:'13px',color:'var(--text-secondary)',marginBottom:'16px'}}>{T('Connect your wearables and health apps. Metrics sync automatically in the background; the Sync buttons force an immediate refresh if you do not want to wait.')}</p>
        <ProviderCards
          providers={fitnessProviders}
          onFitbitConnect={handleFitbitConnect}
          onFitbitSync={handleFitbitSync}
          onFitbitFullSync={handleFitbitFullSync}
          onFitbitDisconnect={handleFitbitDisconnect}
          fitbitConnecting={fitbitConnecting}
          onRefresh={loadData}
          setMessage={setMessage}
        />
      </div>

      {/* Genetic Data — HARD-GATED on the genetics flag. Absent on NPL
          (genetics:false): no DNA upload, no pharmacogenomics/genome-informed
          chat affordance. Compliance: NPL exposes zero genetic surface. */}
      {features.genetics && (
      <div className="profile-section genetic-surface">
        <h3>Genetic Data</h3>
        <p className="profile-dna-description">
          Upload your MyHeritage DNA CSV file to enable genetic analysis, pharmacogenomics, and genome-informed chat.
        </p>
        <div className="profile-dna-upload">
          <label className="profile-dna-upload-btn">
            {dnaUploading ? 'Uploading...' : 'Upload DNA CSV'}
            <input type="file" accept=".csv" onChange={handleDnaUpload} disabled={dnaUploading} style={{display: 'none'}} />
          </label>
          <span className="profile-dna-hint">MyHeritage raw DNA CSV format</span>
        </div>
        {dnaResult && (
          <div className="profile-dna-result">
            <span className="profile-status-dot connected"></span>
            {dnaResult.variants_imported?.toLocaleString('en-GB')} variants imported from {dnaResult.source}
          </div>
        )}
      </div>
      )}

      <NotificationSettings />
      <div className="profile-section"><h3>Feedback</h3><FeedbackForm /></div>
    </div>
  );
}

// ── Privacy Page (GDPR Data Subject Rights) ────────────────────────────────
function PrivacyPage() {
  // v7c consents API returns an ordered list of consent objects already
  // filtered to the current instance (NPL hides genetics + insurance, adds
  // npl_data_sharing). We render directly from that list rather than
  // maintaining a parallel CONSENT_LABELS map.
  // 2e — jurisdiction copy pack, derived from the console-owned `country` flag.
  const pack = privacyPack(FEATURES_GLOBAL.country);
  const [consents, setConsents] = useState([]);          // list from /api/consents
  const [consentsMeta, setConsentsMeta] = useState({});  // { instance, deletion_pending, last_updated_iso }
  const [consentTexts, setConsentTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedType, setExpandedType] = useState(null);
  // 4e — a consent is accepted only after its FULL TEXT has been put in front of the
  // user and they act a second time. One-click grant from a summary line is not
  // informed consent.
  const [pendingAccept, setPendingAccept] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [objection, setObjection] = useState(false);
  const [profile, setProfile] = useState('');
  const [justGranted, setJustGranted] = useState(null);
  const [deletionRef, setDeletionRef] = useState(null);

  // Compat shim — the rest of the component references CONSENT_LABELS for
  // toast strings. Derive from the live consents list so labels stay in
  // sync with whatever the backend says.
  const CONSENT_LABELS = Object.fromEntries(
    consents.map(c => [c.type, { title: c.label, required: c.required }])
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const p = await api.get('/profile');
      const profileName = p.profile || p.name || 'Timothy_Gocher';
      setProfile(profileName);
      const [consentRes, textsRes] = await Promise.all([
        api.get('/consents'),
        api.get('/consent/texts').catch(() => ({ texts: [] })),
      ]);
      setConsents(consentRes.consents || []);
      setConsentsMeta({
        instance:           consentRes.instance,
        deletion_pending:   consentRes.deletion_pending,
        last_updated_iso:   consentRes.last_updated_iso,
        policy_acceptance:  consentRes.policy_acceptance,
      });
      setConsentTexts(textsRes.texts || []);
      try {
        const userRes = await api.get('/profile');
        setObjection(!!userRes.automated_processing_objection);
      } catch {}
    } catch (e) {
      console.error('Privacy page load error:', e);
    }
    setLoading(false);
  };

  const handleGrant = async (consentType) => {
    try {
      await api.post('/consents', { consent_type: consentType, granted: true });
      setActionMsg(`Consent granted: ${CONSENT_LABELS[consentType]?.title || consentType}`);
      setJustGranted(consentType);
      setPendingAccept(null);
      setExpandedType(null);
      setTimeout(() => { setActionMsg(''); setJustGranted(null); }, 3000);
      loadData();
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  const handleWithdraw = async (consentType) => {
    try {
      await api.post('/consents', { consent_type: consentType, granted: false });
      setActionMsg(`Consent withdrawn: ${CONSENT_LABELS[consentType]?.title || consentType}`);
      setTimeout(() => setActionMsg(''), 3000);
      loadData();
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  const handleWithdrawAll = async () => {
    try {
      const r = await api.post('/consents/withdraw-all', {});
      setActionMsg(`Withdrew ${r.withdrawn || 0} optional consent${(r.withdrawn || 0) === 1 ? '' : 's'}.`);
      setTimeout(() => setActionMsg(''), 4000);
      loadData();
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  // "Select all" — grants every not-yet-granted consent (incl. the optional
  // lab-link). Each is a separate POST /api/consents, so it writes its own
  // consent_records row exactly as a single grant would. Unticking withdraws
  // all OPTIONAL consents (required ones stay on). Never auto-applied — an
  // active tap only.
  const allConsentsGranted = consents.length > 0 && consents.every(c => c.granted);
  const handleSelectAll = async () => {
    try {
      if (allConsentsGranted) { await handleWithdrawAll(); return; }
      const toGrant = consents.filter(c => !c.granted);
      for (const c of toGrant) {
        await api.post('/consents', { consent_type: c.type, granted: true });
      }
      setActionMsg(`Granted ${toGrant.length} consent${toGrant.length === 1 ? '' : 's'}.`);
      setTimeout(() => setActionMsg(''), 3000);
      loadData();
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  // RUN6 §1 — "Delete everything" is now an immediate, irreversible, AUDITED erasure
  // (behind the confirm/cancel modal below), and it wipes the Kalpa health-memory ledger
  // + chat-derived memory too. No silent action: the modal is the explicit safeguard.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirmDeleteEverything = async () => {
    setDeleting(true);
    try {
      const r = await api.post(`/gdpr/delete/${profile}`, { confirm: true });
      setShowDeleteModal(false);
      setActionMsg('Everything has been permanently deleted, including your Kalpa health memory. Signing you out…');
      setTimeout(() => { window.location.href = '/'; }, 2600);
      return r;
    } catch (e) {
      setActionMsg(`Deletion error: ${e.message}`);
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setActionMsg('Preparing data export...');
      const data = await api.get(`/gdpr/export/${profile}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `kalpa-data-export-${profile}-${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      setActionMsg('Data export downloaded.');
      setTimeout(() => setActionMsg(''), 3000);
    } catch (e) {
      setActionMsg(`Export error: ${e.message}`);
    }
  };


  const handleObjectionToggle = async () => {
    try {
      const newVal = !objection;
      await api.post('/gdpr/object-automated', { profile, object: newVal });
      setObjection(newVal);
      setActionMsg(newVal ? 'Objection to automated processing recorded.' : 'Objection removed.');
      setTimeout(() => setActionMsg(''), 3000);
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  // 2e — prefer the version this jurisdiction currently requires. The endpoint returns
  // every version ordered by date, so "first match for this type" is merely the newest
  // one GLOBALLY; on US that must resolve to v2.0_insurance explicitly.
  const getConsentText = (consentType) => {
    const row = consents.find(c => c.type === consentType);
    const want = row && row.current_version;
    const t = (want && consentTexts.find(tx => tx.version_id === want))
      || consentTexts.find(tx => tx.consent_type === consentType);
    return T(t?.consent_text || '');
  };

  if (loading) return <LoadingSpinner message="Loading privacy settings..." />;

  return (
    <div className="privacy-page">
      <div className="page-header"><h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Data &amp; Privacy</h2><p className="page-subtitle">{pack.subtitle}</p></div>

      {pack.commitments && (
        <div className="privacy-commitments">
          <h3>Our commitments to you</h3>
          <ul>
            {pack.commitments.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      {actionMsg && <div className="privacy-action-msg">{actionMsg}</div>}

      {consentsMeta.deletion_pending && (
        <div className="privacy-action-msg" style={{background:'#FFF7E6', borderLeft:'3px solid var(--warning)', color:'var(--text-primary)'}}>
          Your data deletion request is pending. We will process it within 30 days and confirm by email.
        </div>
      )}
      {deletionRef && (
        <div className="privacy-action-msg" style={{background:'#EEF0FB', borderLeft:'3px solid var(--accent)'}}>
          Deletion request reference: <strong>{deletionRef}</strong>
        </div>
      )}

      <div className="privacy-section">
        <h3>Your consents</h3>
        <p className="privacy-desc">
          {consentsMeta.last_updated_iso ? `Privacy notice last updated: ${formatDate(consentsMeta.last_updated_iso)}. ` : ''}
          You control what data we process. Required consents keep the app working; optional consents can be withdrawn any time.
        </p>
        {/* Select all — grants every consent (incl. the optional lab-link) in one
            tap. Individual cards below stay visible and independently toggleable;
            nothing is hidden. Privacy/Terms acceptance is handled at onboarding,
            not here, so it is never part of this control. */}
        <div style={{borderTop:'1px solid var(--border-color)', borderBottom:'1px solid var(--border-color)', padding:'10px 0', margin:'4px 0 12px'}}>
          <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer'}}>
            <input type="checkbox" checked={allConsentsGranted} onChange={handleSelectAll}
              aria-label="Select all consents"
              style={{width:16, height:16, accentColor:'var(--accent)', cursor:'pointer'}} />
            <span style={{fontWeight:600, color:'var(--text-primary)'}}>Select all</span>
          </label>
          <div style={{fontSize:'0.8rem', color:'var(--text-secondary)', margin:'4px 0 0 26px'}}>
            You can turn any of these off — optional items are marked.
          </div>
        </div>
        <div className="consent-cards">
          {/* §P — Privacy & Terms: viewable acceptance for parity with onboarding.
              READ-ONLY (no toggle) — this is a policy acceptance recorded when the
              account was set up, not a revocable consent. Neutral copy, no nudge. */}
          <div className="consent-card granted">
            <div className="consent-card-header">
              <div className="consent-card-title">
                <span className="consent-title-text">Privacy &amp; Terms</span>
                <span className="consent-required-badge">Required</span>
              </div>
              <span className="consent-status-badge granted">
                {consentsMeta.policy_acceptance ? 'Accepted' : 'Accepted at sign-up'}
              </span>
            </div>
            <div style={{fontSize:'0.85rem', color:'var(--text-secondary)', marginTop:6}}>
              You accepted the Privacy Notice and Terms when you set up your account. This is required to use the service.
            </div>
            {consentsMeta.policy_acceptance && consentsMeta.policy_acceptance.accepted_at && (
              <div className="consent-meta">
                {consentsMeta.policy_acceptance.version ? `Version ${consentsMeta.policy_acceptance.version} · ` : ''}
                Accepted {formatDate(consentsMeta.policy_acceptance.accepted_at)}
              </div>
            )}
          </div>
          {consents.map(c => {
            const type = c.type;
            const granted = c.granted;
            const status = { withdrawn_at: c.withdrawn_at, given_at: c.granted_at, version: c.version };
            const meta = { title: c.label, required: c.required };
            return (
              <div key={type} className={`consent-card ${granted ? 'granted' : 'not-granted'} ${justGranted === type ? 'just-granted' : ''}`}>
                <div className="consent-card-header">
                  <div className="consent-card-title">
                    <span className="consent-title-text">{T(meta.title)}</span>
                    {meta.required && <span className="consent-required-badge">Required</span>}
                    {!meta.required && <span className="consent-optional-badge">Optional</span>}
                  </div>
                  <span className={`consent-status-badge ${granted ? 'granted' : status.withdrawn_at ? 'withdrawn' : 'pending'} ${justGranted === type ? 'pulse-granted' : ''}`}>
                    {justGranted === type ? '\u2713 ' : ''}{granted ? 'Granted' : status.withdrawn_at ? 'Withdrawn' : 'Not granted'}
                  </span>
                </div>
                {c.description && (
                  <div style={{fontSize:'0.85rem', color:'var(--text-secondary)', marginTop:6}}>{T(c.description)}</div>
                )}
                {/* 2e — jurisdiction addendum on the genetic card (US states plainly that
                    genetics never reaches underwriting; UK/NPL packs carry no line). */}
                {type === 'genetic_data_processing' && pack.geneticFooter && (
                  <div style={{fontSize:'0.85rem', color:'var(--text-secondary)', marginTop:6, fontWeight:600}}>
                    {pack.geneticFooter}
                  </div>
                )}
                {/* 2e — the held consent stays valid AT ITS OWN SCOPE; we ask for an
                    explicit re-grant rather than migrating it. Nothing is pre-ticked. */}
                {/* J — copy comes from the CONSENT TYPE (server-supplied), not a hard-coded
                    sentence. "Aggregate only" is an insurance scope statement and was being
                    printed on required health, AI and data-transfer cards on NPL. The raw
                    version strings are gone from the user-facing line too: they are internal
                    ids ('2026-06-24' vs 'v1.0_health') that mean nothing to a pilot user and
                    read as alarming when the two schemes simply differ. */}
                {c.needs_reconsent && (
                  <div className="consent-reconsent-note">
                    {c.reconsent_note || 'A newer version of this consent is available.'}
                  </div>
                )}
                {granted && status.given_at && (
                  <div className="consent-meta">Version {status.version} &middot; Since {formatDateFromInstant(status.given_at)}</div>
                )}
                {getConsentText(type) && (
                  <>
                    <button className="consent-expand-btn" onClick={() => setExpandedType(expandedType === type ? null : type)}>
                      {expandedType === type ? 'Hide legal text' : 'Read full consent text'}
                    </button>
                    {expandedType === type && (
                      <div className="consent-text-expanded">{getConsentText(type)}</div>
                    )}
                  </>
                )}
                {/* 4e — EXPLICIT accept. Opening the full legal text IS the first step;
                    the accept button only exists once that text is on screen. Nothing is
                    pre-ticked, and a summary line can never be accepted on its own — which
                    matters more now that v2.0 covers per-marker laboratory data rather than
                    aggregate scores. */}
                {pendingAccept === type && (
                  <div className="consent-accept-box">
                    <div className="consent-accept-text">{getConsentText(type)}</div>
                    <div className="consent-accept-actions">
                      <button className="consent-grant-btn" onClick={() => handleGrant(type)}>
                        I accept version {c.current_version}
                      </button>
                      <button className="consent-cancel-btn"
                        onClick={() => { setPendingAccept(null); setExpandedType(null); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div className="consent-actions">
                  {meta.required ? (
                    <button className="consent-grant-btn" disabled
                      style={{opacity:0.5, cursor:'not-allowed'}}
                      title="Required for the app to function - cannot be withdrawn">
                      Always on
                    </button>
                  ) : !granted ? (
                    pendingAccept === type ? null : (
                      <button className="consent-grant-btn"
                        onClick={() => { setPendingAccept(type); setExpandedType(type); }}>
                        Review &amp; accept{c.current_version ? ` version ${c.current_version}` : ''}
                      </button>
                    )
                  ) : (
                    <>
                      {/* Held AND current: there is nothing to accept, so the only action is
                          withdraw. A live "Review & accept" here invited the user to re-grant
                          a consent they already hold. */}
                      {c.needs_reconsent && pendingAccept !== type && (
                        <button className="consent-grant-btn"
                          onClick={() => { setPendingAccept(type); setExpandedType(type); }}>
                          Review &amp; accept version {c.current_version}
                        </button>
                      )}
                      {!c.needs_reconsent && (
                        <span className="consent-held-note">Granted &middot; version {c.version}</span>
                      )}
                      <button className="consent-withdraw-btn" onClick={() => handleWithdraw(type)}>Withdraw</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{marginTop:16, display:'flex', gap:10, flexWrap:'wrap'}}>
          <button className="rights-btn secondary" onClick={handleWithdrawAll}>
            Withdraw all optional consents
          </button>
        </div>
      </div>

      <div className="privacy-section">
        <h3>{pack.rightsHeading}</h3>
        <p className="privacy-desc">{pack.rightsIntro}</p>
        <div className="rights-grid">
          <div className="rights-card">
            <h4>{pack.downloadHeading}</h4>
            <p>{pack.downloadBody}</p>
            <button className="rights-btn" onClick={handleExport}>Download Export</button>
          </div>
          <div className="rights-card">
            <h4>{pack.deleteHeading}</h4>
            <p>{pack.deleteBodyPre}<strong>{pack.deleteBodyStrong}</strong>{pack.deleteBodyPost}</p>
            <button
              className="rights-btn danger"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete everything
            </button>
          </div>
          <div className="rights-card">
            <h4>{pack.automatedHeading}</h4>
            <p>{pack.automatedBody}</p>
            <label className="objection-toggle">
              <input type="checkbox" checked={objection} onChange={handleObjectionToggle} />
              <span>Object to automated processing</span>
            </label>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="delete-everything-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-everything-title">
          <div className="delete-everything-modal">
            <h3 id="delete-everything-title">Delete everything?</h3>
            <p className="delete-everything-warn">
              This will <strong>permanently and immediately</strong> erase all of your data —
              blood results, medicines, wearable data, documents, chat history, and your
              <strong> Kalpa health memory</strong> (everything you’ve told Kalpa).
            </p>
            <p className="delete-everything-warn">This cannot be undone.</p>
            <div className="delete-everything-actions">
              <button className="rights-btn" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="rights-btn danger" onClick={confirmDeleteEverything} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, delete everything'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="privacy-footer">
        {pack.footerLines.map((ln, i) => <p key={i}>{ln}</p>)}
        {pack.medicalDisclaimer && (
          <p className="privacy-medical-disclaimer">{pack.medicalDisclaimer}</p>
        )}
      </div>
    </div>
  );
}

// ── UNDERWRITING PLANE (Section 4) ──────────────────────────────────────────
// Consumer UI is untouched by this: nothing here is imported by any consumer screen, and
// the route is unreachable unless the insurance_export flag is on AND the caller is
// superadmin. The server re-checks the flag, the role AND the subject's consent on every
// fetch — this component never decides access, it only reflects what the server allows.

function _uwFmtPct(x, dp = 2) {
  if (x == null || isNaN(x)) return '—';
  return `${(x * 100).toFixed(dp)}%`;
}

// ── KAL-200 — UNDERWRITER SANDBOX ────────────────────────────────────────────────────
// The sandbox is a SEPARATE surface reached through the same route. UnderwritingPage
// probes /underwriting/cohort first: it answers 200 only for a synthetic session and 404
// everywhere else, so a superadmin on a real instance falls through to the ORIGINAL
// applicants list, byte-for-byte the view that shipped. The real plane is not touched by
// any of this — the fallback is the old component, not a re-implementation of it.

// Persistent, on every underwriter screen. Not a toast and not dismissible: an
// underwriter who forgets which cohort they are looking at is the failure this prevents.
function SyntheticBanner({ jurisdiction, onJurisdiction }) {
  return (
    <div className="uw-synbar" role="status">
      <span className="uw-synbar-dot" aria-hidden="true">◆</span>
      <span className="uw-synbar-text">
        <strong>Synthetic demonstration cohort.</strong> Generated from published
        population parameters. No real person is represented.
      </span>
      {/* KAL-210 — the SWITCH exists only where the console flag allows it (master-test,
          for parity testing). On a live instance the jurisdiction is resolved server-side
          from the `country` flag and is shown here READ-ONLY: an underwriter must be able
          to see which regulator applies before generating anything, but must not be able
          to choose it. Jurisdiction is a property of the applicant and the carrier. */}
      {onJurisdiction && FEATURES_GLOBAL.uw_jurisdiction_selector ? (
        <span className="uw-synbar-jur">
          <span className="uw-synbar-jurlabel">Regulatory pack</span>
          {['UK', 'US'].map(j => (
            <button
              key={j}
              type="button"
              className={'uw-jurbtn' + (jurisdiction === j ? ' is-on' : '')}
              aria-pressed={jurisdiction === j}
              onClick={() => onJurisdiction(j)}
            >{j}</button>
          ))}
        </span>
      ) : (
        <span className="uw-synbar-jur">
          <span className="uw-synbar-jurlabel">Regulatory pack</span>
          <span className="uw-jurfixed" data-testid="uw-jurisdiction-fixed">
            {jurisdiction || '—'}
          </span>
        </span>
      )}
    </div>
  );
}

function UnderwritingPage({ profile }) {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);
  const [openProfile, setOpenProfile] = useState(null);
  const [cohort, setCohort] = useState(null);      // null = not probed, false = not synthetic
  // KAL-210 — starts UNKNOWN, not 'UK'. The old initial value was a guess that happened to be
  // wrong on the US instance, and it rendered before the server had answered.
  const [jur, setJur] = useState(null);
  const [detailProfile, setDetailProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/underwriting/cohort')
      .then(d => {
        if (cancelled) return;
        setCohort(d.cohort || []);
        if (d.jurisdiction) setJur(d.jurisdiction);
      })
      .catch(() => {
        // 404 => not a synthetic session. Fall through to the real applicants list.
        if (!cancelled) setCohort(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (cohort !== false) return;                  // only the real plane needs this
    let cancelled = false;
    api.get('/underwriting/applicants')
      .then(d => { if (!cancelled) { setRows(d.applicants || []); setMeta(d); } })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [cohort]);

  const switchJur = (j) => {
    setJur(j);
    api.post('/underwriting/jurisdiction', { jurisdiction: j }).catch(() => {});
  };

  if (cohort === null) return <LoadingSpinner message="Loading underwriting workspace..." />;

  // ── SYNTHETIC SANDBOX ──
  if (cohort !== false) {
    if (openProfile) {
      return (
        <div className="uw-page">
          <SyntheticBanner jurisdiction={jur} />
          <UnderwritingReport profileName={openProfile}
                              onBack={() => setOpenProfile(null)} />
        </div>
      );
    }
    if (detailProfile) {
      return (
        <div className="uw-page">
          <SyntheticBanner jurisdiction={jur} onJurisdiction={switchJur} />
          <UwProfileDetail
            profileName={detailProfile}
            jurisdiction={jur}
            onBack={() => setDetailProfile(null)}
            onReport={() => { setOpenProfile(detailProfile); setDetailProfile(null); }}
          />
        </div>
      );
    }
    return (
      <div className="uw-page">
        <SyntheticBanner jurisdiction={jur} onJurisdiction={switchJur} />
        <UwCohortTable rows={cohort} onOpen={setDetailProfile}
                       onReport={setOpenProfile} />
      </div>
    );
  }

  // The flag is the server's decision. If it is off the route 404s, and we say that plainly
  // rather than rendering an empty shell that would read as "no applicants".
  if (error) {
    return (
      <div className="uw-page">
        <div className="page-header"><h2>Underwriting</h2></div>
        <div className="uw-empty">This view is not available on this instance ({error}).</div>
      </div>
    );
  }
  if (rows === null) return <LoadingSpinner message="Loading consented applicants..." />;

  if (openProfile) {
    return <UnderwritingReport profileName={openProfile} onBack={() => setOpenProfile(null)} />;
  }

  return (
    <div className="uw-page">
      <div className="page-header">
        <h2>Underwriting</h2>
        <p className="page-subtitle">
          Applicants who have granted insurance-related consent. A report opens only for an
          active {meta.required_version} grant; earlier grants stay valid at their own
          aggregate-only scope.
        </p>
      </div>
      {rows.length === 0 && <div className="uw-empty">No insurance consents on record.</div>}
      {rows.length > 0 && (
        <div className="uw-table-wrap">
          <table className="uw-table">
            <thead>
              <tr>
                <th>Applicant</th><th>Consent</th><th>Granted</th><th>State</th>
                <th>Single-draw completeness</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.profile} className={r.report_available ? '' : 'uw-row-limited'}>
                  <td>
                    <div className="uw-name">{r.name}</div>
                    <div className="uw-sub">{r.dob ? fmtDateNumeric(r.dob) : 'DOB not set'}</div>
                  </td>
                  <td>
                    <div>{r.consent_version || '—'}</div>
                    {!r.report_available && (
                      <div className="uw-scope-badge">{r.scope_label}</div>
                    )}
                  </td>
                  <td>{r.consent_given_at ? formatDateFromInstant(r.consent_given_at) : '—'}</td>
                  <td>{r.state || '—'}</td>
                  <td>
                    <div>{r.completeness.label}</div>
                    {r.completeness.draw_date && (
                      <div className="uw-sub">draw {fmtDate(r.completeness.draw_date)}</div>
                    )}
                  </td>
                  <td>
                    {r.report_available ? (
                      <button className="uw-open-btn" onClick={() => setOpenProfile(r.profile)}>
                        Open report
                      </button>
                    ) : (
                      <span className="uw-sub">No report — re-consent required</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// D1 — full-span trend, drawn as an inline SVG polyline.
//
// MONO-PRINT SAFE BY CONSTRUCTION: stroke only, in currentColor, with no fill, no gradient, no
// axis and no colour encoding. It carries the same information on a black-and-white printer, in
// dark mode and in light mode. It is INLINE SVG rather than a chart library on purpose — the
// print path must not depend on a canvas that renders lazily or after the print snapshot is taken.
//
// It is deliberately unlabelled and unscaled: it shows SHAPE, and the number beside it is the
// figure. Putting an axis on a 180x28 strip would imply a precision it does not have.
function UwSparkline({ points, months }) {
  if (!points) {
    return <span className="uw-sub">not enough span</span>;
  }
  return (
    <svg className="uw-spark" viewBox="0 0 180 28" width="180" height="28"
         role="img" aria-label={`Trend across ${months || 0} months`} focusable="false">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.25"
                strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ── COHORT TABLE ─────────────────────────────────────────────────────────────────────
const UW_CLASSES = ['Preferred Plus', 'Preferred', 'Standard Plus', 'Standard', 'Table-rated'];
const UW_AGE_BANDS = [['25-34', 25, 35], ['35-44', 35, 45], ['45-54', 45, 55],
                      ['55-64', 55, 65], ['65+', 65, 200]];

function UwCohortTable({ rows, onOpen, onReport }) {
  // A2 — default sort is the PRICED FIGURE. It already keyed on multiplier; the column
  // is now labelled and positioned as the primary one.
  const [sort, setSort] = useState({ key: 'multiplier', dir: 'asc' });
  const [fCls, setFCls] = useState('');
  const [fSex, setFSex] = useState('');
  const [fBand, setFBand] = useState('');
  const [fMin, setFMin] = useState('');
  const [fMax, setFMax] = useState('');

  const filtered = rows.filter(r => {
    if (fCls && r.assigned_class !== fCls) return false;
    if (fSex && r.sex !== fSex) return false;
    if (fBand) {
      const b = UW_AGE_BANDS.find(x => x[0] === fBand);
      if (b && !(r.chronological_age >= b[1] && r.chronological_age < b[2])) return false;
    }
    if (fMin !== '' && !(r.multiplier >= parseFloat(fMin))) return false;
    if (fMax !== '' && !(r.multiplier <= parseFloat(fMax))) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sort.key], bv = b[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;            // absent sorts last in BOTH directions —
    if (bv == null) return -1;           // a missing value is not a small value
    if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sort.dir === 'asc' ? av - bv : bv - av;
  });

  const th = (key, label, help) => (
    <th className={'uw-th-sort' + (sort.key === key ? ' is-sorted' : '')}
        onClick={() => setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
        title={help || ''} scope="col">
      {label}
      <span className="uw-sortmark" aria-hidden="true">
        {sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ·'}
      </span>
    </th>
  );

  return (
    <>
      <div className="page-header">
        <h2>Cohort</h2>
        <p className="page-subtitle">
          {sorted.length} of {rows.length} applicants. Every figure is computed at read
          time by the same engine that scores a real applicant.
        </p>
      </div>

      <div className="uw-filters">
        <label>Class
          <select value={fCls} onChange={e => setFCls(e.target.value)}>
            <option value="">All</option>
            {UW_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>Age band
          <select value={fBand} onChange={e => setFBand(e.target.value)}>
            <option value="">All</option>
            {UW_AGE_BANDS.map(b => <option key={b[0]} value={b[0]}>{b[0]}</option>)}
          </select>
        </label>
        <label>Sex
          <select value={fSex} onChange={e => setFSex(e.target.value)}>
            <option value="">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </label>
        <label>Multiplier
          <input type="number" step="0.05" placeholder="min" value={fMin}
                 onChange={e => setFMin(e.target.value)} className="uw-numin" />
          <span className="uw-dash">to</span>
          <input type="number" step="0.05" placeholder="max" value={fMax}
                 onChange={e => setFMax(e.target.value)} className="uw-numin" />
        </label>
        <button type="button" className="uw-clearbtn"
                onClick={() => { setFCls(''); setFSex(''); setFBand(''); setFMin(''); setFMax(''); }}>
          Clear filters
        </button>
      </div>

      <div className="uw-table-wrap">
        <table className="uw-table uw-cohort">
          <thead><tr>
            {th('pseudonym', 'Applicant')}
            {th('chronological_age', 'Age')}
            {th('sex', 'Sex')}
            {th('assigned_class', 'Class')}
            {/* A2 — an underwriter does not price youth, they price mortality. "% of
                standard" leads and is the default sort; the multiplier and the delta stay
                as secondary columns because they are what the engine computes. */}
            {th('multiplier', '% of standard', 'Mortality as a percentage of standard: 100% = standard, industry-typical standard band roughly 75-125%')}
            {th('kalpa_age', 'PhenoAge', 'PhenoAge (Levine 2018) from the most recent complete draw')}
            {th('delta_years', 'Delta', 'PhenoAge minus chronological age')}
            {th('last_panel_date', 'Last panel')}
            {/* P0.3 (KAL-223) — SAY WHAT THE NUMBER COUNTS. It was headed "Completeness"
                and rendered "9/9", which an underwriter reads as "this record is complete".
                It is not: it counts the NINE PRICED PhenoAge inputs, and the record also
                holds blood pressure, lipids, liver and renal markers that are not among
                them. A completeness claim contradicted by an empty chart two clicks away is
                worse than no claim. */}
            {th('markers_present', 'Priced inputs', 'How many of the NINE priced PhenoAge inputs are present in the single most recent complete draw. It is not a measure of the whole record - blood pressure and the wider panel are held separately and are not priced inputs.')}
            <th scope="col">Report</th>
          </tr></thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.profile}>
                <td>
                  <button type="button" className="uw-linkbtn"
                          onClick={() => onOpen(r.profile)}>{r.pseudonym}</button>
                  <div className="uw-sub">{r.profile}</div>
                </td>
                <td>{r.chronological_age != null ? r.chronological_age.toFixed(1) : '—'}</td>
                <td>{r.sex === 'male' ? 'M' : r.sex === 'female' ? 'F' : '—'}</td>
                <td>{r.assigned_class || '—'}</td>
                {/* A2 — the priced figure first, in the reader's unit. The decimal stays
                    beneath it: it is what the engine computes and what this table sorts on. */}
                <td className="uw-num uw-mult">
                  {r.multiplier == null ? '—' : (
                    <>{Math.round(r.multiplier * 100)}%
                      <span className="uw-sub"> {r.multiplier.toFixed(3)}×</span>
                    </>
                  )}
                </td>
                <td>{r.kalpa_age != null ? r.kalpa_age.toFixed(1) : '—'}</td>
                {/* Sign is WORDED as well as signed: colour is never the only carrier. */}
                <td className="uw-num">
                  {r.delta_years == null ? '—' : (
                    <>{r.delta_years > 0 ? '+' : ''}{r.delta_years.toFixed(1)}
                      <span className="uw-sub"> {r.delta_years < 0 ? 'younger' : r.delta_years > 0 ? 'older' : 'level'}</span>
                    </>
                  )}
                </td>
                <td>{r.last_panel_date ? fmtDate(r.last_panel_date) : '—'}</td>
                <td>
                  {r.markers_present}/{r.markers_total}
                  {!r.complete && <span className="uw-sub"> incomplete</span>}
                </td>
                <td>
                  <button type="button" className="uw-open-btn"
                          disabled={!r.complete}
                          onClick={() => onReport(r.profile)}>
                    {r.complete ? 'Generate' : 'Not scoreable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length === 0 && <div className="uw-empty">No applicants match these filters.</div>}
    </>
  );
}

// ── PROFILE DETAIL — the two planes, visually separate ───────────────────────────────
const UW_ZOOMS = [['1M', 30], ['3M', 92], ['1Y', 365], ['5Y', 100000]];

function UwSeriesChart({ title, points, unit, colour, days, refBand }) {
  // Recharts renders a WHITE SCREEN on a NaN axis domain, so every numeric is coerced
  // and non-finite points are dropped before they can reach an axis.
  const cutoff = Date.now() - days * 86400000;
  const data = (points || [])
    .map(([d, v]) => ({ d, t: Date.parse(d), v: Number(v) }))
    .filter(p => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t >= cutoff);
  if (!data.length) {
    return (
      <div className="uw-chart">
        <div className="uw-chart-title">{title}</div>
        <div className="uw-chart-empty">No readings in this window.</div>
      </div>
    );
  }
  const vals = data.map(p => p.v);
  let lo = Math.min(...vals), hi = Math.max(...vals);
  if (!(Number.isFinite(lo) && Number.isFinite(hi)) || lo === hi) { lo = lo - 1; hi = hi + 1; }
  const pad = (hi - lo) * 0.12;
  return (
    <div className="uw-chart">
      <div className="uw-chart-title">
        {title} <span className="uw-chart-unit">{unit}</span>
        <span className="uw-chart-n">{data.length} readings</span>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={data} margin={{ top: 6, right: 8, bottom: 2, left: -12 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" vertical={false} />
          {refBand && Number.isFinite(refBand[0]) && Number.isFinite(refBand[1]) && (
            <ReferenceArea y1={refBand[0]} y2={refBand[1]} fill="var(--meas)"
                           fillOpacity={0.9} stroke="var(--measline)" />
          )}
          <XAxis dataKey="t" type="number" scale="time"
                 domain={['dataMin', 'dataMax']}
                 tickFormatter={t => fmtDateShort(new Date(t).toISOString().slice(0, 10))}
                 tick={{ fontSize: 10, fill: 'var(--tx3)' }} stroke="var(--line)" />
          <YAxis domain={[lo - pad, hi + pad]} tick={{ fontSize: 10, fill: 'var(--tx3)' }}
                 stroke="var(--line)" width={44} />
          <Tooltip
            labelFormatter={t => fmtDate(new Date(t).toISOString().slice(0, 10))}
            formatter={v => [v, title]}
            contentStyle={{ background: 'var(--panel)', border: '1px solid var(--line)',
                            borderRadius: 8, fontSize: 12, color: 'var(--tx)' }} />
          <Line type="monotone" dataKey="v" stroke={colour} strokeWidth={1.6} dot={false}
                isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function UwProfileDetail({ profileName, jurisdiction, onBack, onReport }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(365);

  useEffect(() => {
    let cancelled = false;
    api.get(`/underwriting/detail/${encodeURIComponent(profileName)}`)
      .then(x => { if (!cancelled) setD(x); })
      .catch(e => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [profileName]);

  if (err) return <div className="uw-empty">Applicant unavailable: {err}</div>;
  if (!d) return <LoadingSpinner message="Loading applicant..." />;

  const uwm = d.underwriting.markers || {};
  const nine = ['Albumin', 'Creatinine', 'Fasting Glucose', 'CRP', 'Lymphocytes %',
                'MCV', 'RDW-CV', 'Alkaline Phosphatase', 'WBC Count'];
  const REF = {
    'Albumin': [3.5, 5.0], 'Creatinine': [0.7, 1.3], 'Fasting Glucose': [70, 100],
    'CRP': [0, 3], 'Lymphocytes %': [20, 40], 'MCV': [80, 100], 'RDW-CV': [11.5, 14.5],
    'Alkaline Phosphatase': [40, 130], 'WBC Count': [4, 11],
  };
  const excluded = d.excluded_from_underwriting || [];
  const exclTotal = excluded.reduce((s, e) => s + (e.records || 0), 0);

  return (
    <>
      <div className="uw-noprint uw-detail-bar">
        <button className="uw-back" onClick={onBack}>← Back to cohort</button>
        {/* KAL-210 — say WHICH regulator this will be produced under, ON the control that
            produces it. Tim generated a UK-regulated report from the US instance and could
            only tell afterwards, from the report id. A jurisdiction shown only in a toggle
            somewhere else is not a safeguard. */}
        <span className="uw-genjur" data-testid="uw-generate-jurisdiction">
          {jurisdiction ? `${jurisdiction} regulatory pack` : 'Resolving jurisdiction…'}
        </span>
        <button className="uw-print" onClick={onReport} disabled={!jurisdiction}>
          Generate report
        </button>
      </div>

      <div className="page-header">
        <h2>{d.pseudonym}</h2>
        <p className="page-subtitle">
          {d.profile} · {d.sex} · born {fmtDateNumeric(d.dob)}
          {d.assigned_class ? ` · assigned class ${d.assigned_class}` : ''}
          {d.jurisdiction ? ` · ${d.jurisdiction}` : ''}
        </p>
      </div>

      <div className="uw-zoom" role="group" aria-label="Time range">
        {UW_ZOOMS.map(([lbl, n]) => (
          <button key={lbl} type="button"
                  className={'uw-zoombtn' + (days === n ? ' is-on' : '')}
                  aria-pressed={days === n}
                  onClick={() => setDays(n)}>{lbl}</button>
        ))}
      </div>

      {/* ── PLANE 1 ── */}
      <section className="uw-plane uw-plane-wellness">
        <header className="uw-plane-head">
          <h3>Wellness plane <span className="uw-plane-tag">not used for pricing</span></h3>
          <p className="uw-plane-note">{d.wellness.note}</p>
        </header>
        <div className="uw-chartgrid">
          <UwSeriesChart title="Daily steps" points={d.wellness.steps.points} unit="steps"
                         colour="#5FD3DC" days={days} />
          <UwSeriesChart title="Sleep" points={d.wellness.sleep.points} unit="minutes asleep"
                         colour="#9E95F5" days={days} />
          <UwSeriesChart title="Resting heart rate" points={d.wellness.rhr.points} unit="bpm"
                         colour="#8FA3C8" days={days} />
          <UwSeriesChart title="Heart-rate variability" points={d.wellness.hrv.points}
                         unit="RMSSD ms" colour="#F0C97A" days={days} />
          <UwSeriesChart title="Cardio fitness" points={d.wellness.vo2.points}
                         unit="VO₂max mL/kg/min" colour="#6FD3A2" days={days} />
        </div>
      </section>

      {/* The wall, stated between the two planes rather than asserted on a slide. */}
      <div className="uw-wall" role="separator">
        <span className="uw-wall-label">Two-plane separation</span>
        <p>
          {exclTotal.toLocaleString()} rows above originate in a platform health store
          ({excluded.map(e => e.source).join(', ') || 'none'}) and are excluded by design
          from every figure below and from the generated report.
        </p>
      </div>

      {/* ── PLANE 2 ── */}
      <section className="uw-plane uw-plane-underwriting">
        <header className="uw-plane-head">
          <h3>Underwriting plane <span className="uw-plane-tag">laboratory evidence</span></h3>
          <p className="uw-plane-note">
            Laboratory markers with their reference band shaded. Blood pressure is
            clinic-measured. Medication start dates are marked so a change in a marker can
            be read alongside them — the timing is shown, no causal claim is made.
          </p>
        </header>
        <div className="uw-chartgrid">
          {nine.filter(m => uwm[m]).map(m => (
            <UwSeriesChart key={m} title={m} points={uwm[m].points} unit={uwm[m].unit}
                           colour="#8FA0FF" days={days} refBand={REF[m]} />
          ))}
        </div>

        <div className="uw-subhead">Blood pressure</div>
        <UwSeriesChart title="Systolic" unit="mmHg" colour="#8FA0FF" days={days}
                       points={(d.underwriting.bp || []).map(b => [b[0], b[1]])} />
        <UwSeriesChart title="Diastolic" unit="mmHg" colour="#A9B3D2" days={days}
                       points={(d.underwriting.bp || []).map(b => [b[0], b[2]])} />

        <div className="uw-subhead">Medication timeline</div>
        {(d.underwriting.medications || []).length === 0 ? (
          <div className="uw-chart-empty">No medications on record.</div>
        ) : (
          <ul className="uw-medlist">
            {d.underwriting.medications.map((m, i) => (
              <li key={i}>
                <span className="uw-med-date">{fmtDate(m.started)}</span>
                <span className="uw-med-name">{m.name} {m.dosage}</span>
                <span className="uw-sub">started</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

// ── P0.4 — WHICH MARKERS AN INSURANCE LABORATORY PANEL ALREADY REPORTS ───────────────
// Eight of the nine priced inputs are markers reported on a standard insurance laboratory
// panel; only C-reactive protein is additional. NYDFS Circular Letter No. 1 warns
// specifically about models predicting health status "based on a single or limited number
// of unconventional criteria", so showing the overlap answers that objection where the
// underwriter actually reads.
//
// ⚠ NO CARRIER IS NAMED HERE, DELIBERATELY. Nothing in this product — copy, code comment
// or cohort — may imply that criteria are aligned to, derived from or validated against any
// named carrier. The grouping is sourced from the insurance-medicine literature and from
// what a standard panel reports, both public; it is not aligned to anyone's guidelines.
//
// GROUPING IS A RENDER CONCERN ONLY. The underlying rows are not reordered and the PhenoAge
// input order is untouched — the same array is partitioned into two lists for display.
const _UW_PANEL_STANDARD = [
  // the eight of nine, by the names the report renders
  'albumin', 'alkaline phosphatase', 'creatinine', 'glucose', 'fasting glucose',
  'wbc', 'wbc count', 'white blood cell', 'lymphocytes', 'lymphocytes %', 'lymphocyte',
  'mcv', 'rdw', 'rdw-cv',
  // and the wider panel a carrier's lab already reports
  'total cholesterol', 'hdl cholesterol', 'hdl', 'ldl cholesterol', 'ldl', 'triglycerides',
  'tc/hdl ratio', 'hba1c', 'alt', 'ast', 'ggt', 'bilirubin', 'total bilirubin',
  'total protein', 'globulin', 'a/g ratio', 'urea', 'bun', 'egfr',
  'urine protein/creatinine ratio', 'microalbumin',
];
function _uwIsStandardPanel(name) {
  const n = String(name || '').trim().toLowerCase();
  return _UW_PANEL_STANDARD.includes(n);
}

function UnderwritingReport({ profileName, onBack }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/underwriting/report/${encodeURIComponent(profileName)}`)
      .then(d => { if (!cancelled) setRep(d); })
      .catch(e => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [profileName]);

  // ── P2 / KAL-217 — PDF FILENAME AND PAPER SIZE ──────────────────────────────────────
  // Chrome takes the suggested "Save as PDF" filename from document.title, which is why every
  // report saved as "Kalpa.pdf" — an underwriting artifact that files under the app's name
  // tells the recipient nothing and collides with the last one they saved.
  //
  // ⚠ I could not find the naming convention from the earlier session: nothing in this repo
  // records one. Rather than invent a decorative scheme, this uses the artifact's OWN
  // canonical identifier — the report id already printed on page 1 and already carrying
  // jurisdiction, state and date (KAL-SYN-US-NY-20260816-799721) — followed by the subject.
  // That is what an underwriter files and searches by. One line to change if the recorded
  // convention differs.
  //
  // Paper size is injected here rather than written in App.css because `@page` accepts no
  // class or media selector and named pages are unevenly supported in Chrome.
  useEffect(() => {
    if (!rep) return;
    const h = rep.header || {};
    const prevTitle = document.title;
    const safe = (s) => String(s || '').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
    const name = [safe(h.report_id), safe(h.pseudonym || profileName)].filter(Boolean).join('_');
    if (name) document.title = name;
    const el = document.createElement('style');
    el.setAttribute('data-uw-paper', '1');
    el.textContent = '@page { size: ' + (h.country === 'US' ? 'Letter' : 'A4') + '; margin: 12.7mm; }';
    document.head.appendChild(el);
    return () => {
      document.title = prevTitle;
      try { el.remove(); } catch (e) { /* already gone */ }
    };
  }, [rep, profileName]);

  if (err) return (
    <div className="uw-page">
      <button className="uw-back" onClick={onBack}>← Back</button>
      <div className="uw-empty">Report unavailable: {err}</div>
    </div>
  );
  if (!rep) return <LoadingSpinner message="Building report..." />;

  const h = rep.header, p = rep.priced, m = rep.methodology;

  return (
    <div className="uw-page uw-report">
      <div className="uw-noprint">
        <button className="uw-back" onClick={onBack}>← Back to applicants</button>
        <button className="uw-print" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>

      {/* Print shell: a table whose tfoot repeats at the bottom of EVERY printed page AND
          reserves its own space, so flow text can never overlap the running footer.
          (position:fixed cannot reserve space, and Chromium slices a fixed element that
          extends into the page margin onto the NEXT page's top — both proven.) On screen
          the shell displays as plain blocks. */}
      <div className="uw-doc">
        <table className="uw-printshell"><tbody><tr><td>
        <header className="uw-doc-head">
          <h1>Applicant Risk Report</h1>
          <div className="uw-doc-meta">
            <div><span>Applicant</span><strong>{h.name}</strong></div>
            <div><span>Date of birth</span><strong>{h.dob ? fmtDateNumeric(h.dob) : '—'}</strong></div>
            {/* State renders ONLY when applicable (US) AND actually stored — never a dash
                on UK, never an unconfirmed default printed as fact on US. */}
            {h.state_applicable && h.state ? (
              <div><span>State</span><strong>{h.state}</strong></div>
            ) : null}
            <div><span>Report ID</span><strong>{h.report_id}</strong></div>
            <div><span>Generated</span><strong>{formatDateTimeZoned(h.generated_at)}</strong></div>
          </div>
        </header>

        {/* ── The priced figure ── */}
        <section className="uw-sec">
          <h2>Priced figure</h2>
          {p.scoreable ? (
            <>
              <div className="uw-priced">
                {/* 4f — one decimal on BOTH ages. 50 vs 55.6 read as different precisions
                    and invited "is the 50 exact?"; 50.0 vs 55.6 does not. */}
                <div className="uw-priced-cell">
                  <span>PhenoAge</span>
                  <strong>{p.phenoage != null ? p.phenoage.toFixed(1) : '—'}</strong><em>years</em>
                </div>
                <div className="uw-priced-vs">vs</div>
                <div className="uw-priced-cell">
                  <span>Chronological</span>
                  <strong>{p.chronological_age != null ? p.chronological_age.toFixed(1) : '—'}</strong><em>years</em>
                </div>
                {/* P0.1 — the SAME number, stated the way an underwriter states it. Mortality
                    is read as a percentage of standard (100% = standard, standard band roughly
                    75-125%); "0.686" is our unit, not theirs. The percentage leads because it
                    is the readable figure; the decimal stays because it is what the engine
                    computes and what every other surface sorts and filters on. */}
                <div className="uw-priced-cell uw-priced-mult">
                  <span>Mortality vs standard</span>
                  <strong>{p.pct_of_standard != null ? p.pct_of_standard + '%' : '—'}</strong>
                  <em>{p.multiplier != null ? p.multiplier.toFixed(3) + '× standard' : ''}</em>
                </div>
              </div>
              {/* P0.2 — the indicative table rating, surfaced. INDICATIVE is the fact, not a
                  hedge: Kalpa has filed no table structure and has no sight of the partner's
                  classes, so this can only be a translation aid. Same wording as the
                  limitations section. */}
              {/* A3 — the STANDARD BAND, rendered, so an underwriter sees at a glance whether
                  the applicant sits inside it rather than doing the arithmetic. Industry
                  convention places standard at roughly 75-125% of standard mortality (some
                  carriers extend to 130-140 under age 30). ILLUSTRATIVE: this is the
                  industry-typical band, not any carrier's filed guidelines. */}
              {p.pct_of_standard != null && (
                <div className="uw-band">
                  <div className="uw-band-track">
                    <div className="uw-band-std" />
                    <div className="uw-band-mark"
                         style={{ left: Math.max(0, Math.min(100, (p.pct_of_standard / 250) * 100)) + '%' }} />
                  </div>
                  <div className="uw-band-legend">
                    <span>0%</span><span>75-125% standard band</span><span>250%+</span>
                  </div>
                  <div className="uw-band-verdict">
                    {p.pct_of_standard <= 125 && p.pct_of_standard >= 75
                      ? 'Inside the industry-typical standard band (illustrative).'
                      : p.pct_of_standard < 75
                        ? 'Below the industry-typical standard band (illustrative) — favourable.'
                        : 'Above the industry-typical standard band (illustrative).'}
                  </div>
                </div>
              )}
              {p.table_rating && p.table_rating !== 'n/a' && (
                <div className="uw-table-rating">
                  <span>Indicative rating</span>
                  <strong>{p.table_rating}</strong>
                  <em>25 percentage points per table; standard band to 125%. Indicative only —
                    not a filed table structure, and to be mapped to the partner's actual risk
                    classes.</em>
                </div>
              )}
              {p.class_label && <div className="uw-class-label">{p.class_label}</div>}
              <table className="uw-mini">
                <tbody>
                  <tr><td>10-year mortality, this panel</td><td>{_uwFmtPct(p.mortality_10yr, 3)}</td></tr>
                  <tr><td>10-year mortality, age-matched reference</td><td>{_uwFmtPct(p.mortality_10yr_reference, 3)}</td></tr>
                  <tr><td>Biological-age delta</td><td>{p.delta_years > 0 ? '+' : ''}{p.delta_years} years</td></tr>
                </tbody>
              </table>
              {/* E1 — the methodology STATEMENT sits directly under the delta, as one line, so a
                  reader meets the basis of the number where the number is. The full Methodology
                  section (limitations, device-evidence basis, gradients) moved below the data —
                  it is 210.3mm and was sitting between the priced figure and the Inputs table,
                  which is what pushed every data block off page 1. */}
              {m.note && <p className="uw-method-oneline">Methodology: {m.note}</p>}
            </>
          ) : (
            <div className="uw-notscore">Not scoreable — {p.not_scoreable_reason}.</div>
          )}
        </section>

        {/* ── Inputs ── */}
        <section className="uw-sec uw-sec-inputs">
          <h2>Inputs</h2>
          <div className="uw-basis">
            Single-draw basis — all inputs from the blood draw of{' '}
            <strong>{rep.inputs.draw_date ? fmtDate(rep.inputs.draw_date) : '—'}</strong>{' '}
            ({rep.inputs.present} of {rep.inputs.total} present). No cross-date substitution.
          </div>
          {rep.inputs.any_self_reported && (
            <div className="uw-banner-selfreported">
              One or more inputs below are SELF-REPORTED, not laboratory-verified. This report
              should not be relied on for pricing until those values are confirmed against a
              laboratory record.
            </div>
          )}
          <table className="uw-table uw-inputs">
            <thead>
              <tr><th>Marker</th><th>Value</th><th>Unit</th><th>Draw date</th><th>Provenance</th><th>Laboratory</th></tr>
            </thead>
            <tbody>
              {(() => {
              const _rows = rep.inputs.rows || [];
              const _std = _rows.filter(r => _uwIsStandardPanel(r.marker));
              const _extra = _rows.filter(r => !_uwIsStandardPanel(r.marker));
              const _ctx = (rep.inputs.panel_context || [])
                .filter(c => _uwIsStandardPanel(c.marker));
              const _row = r => {
                // Phase 4 — the report renders in the instance's own clinical convention
                // (UK: NHS/SI). DISPLAY ONLY: the priced figure, the multiplier and every
                // scoring input are computed server-side from the STORED values and are
                // byte-identical either side of this call.
                const d = displayMarker(r.resolved_from || r.marker, r.value, r.unit);
                return (
                <tr key={r.marker}>
                  <td>
                    {r.marker}
                    {r.resolved_from && r.resolved_from !== r.marker && (
                      <span className="uw-sub"> (from {r.resolved_from})</span>
                    )}
                  </td>
                  <td>{r.present ? d.value : <em>missing</em>}</td>
                  <td>{d.unit || '—'}</td>
                  <td>{r.draw_date ? fmtDate(r.draw_date) : '—'}</td>
                  <td>
                    {r.present ? (
                      <span className={`uw-prov uw-prov-${r.provenance}`}>
                        {r.provenance === 'lab_verified' ? 'Lab-verified' : 'Self-reported'}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {r.lab || '—'}
                    {r.identity_verified && <span className="uw-sub"> · ID verified</span>}
                    {/* 4f — duplicate stored rows collapsing to one marker. Agreeing values
                        are a hygiene note; DISAGREEING values change the priced figure
                        depending on which row was selected, so that is a warning. */}
                    {r.duplicates && (
                      <div className={r.duplicates.values_agree ? 'uw-sub' : 'uw-dup-conflict'}>
                        {r.duplicates.count} stored rows for this marker
                        {r.duplicates.values_agree
                          ? ' (values agree; highest-provenance row selected)'
                          : ' — VALUES DISAGREE, priced figure depends on row selection'}
                      </div>
                    )}
                    {r.value_provenance_mismatch && (
                      <div className="uw-dup-conflict">
                        provenance row value differs from the priced value
                      </div>
                    )}
                  </td>
                </tr>
                );
              };
              return (<>
                <tr className="uw-group-head"><td colSpan={6}>
                  Reported on a standard insurance laboratory panel
                </td></tr>
                {_std.map(_row)}
                {_ctx.map(c => (
                  <tr key={'ctx-' + c.marker} className="uw-ctx-row">
                    <td>{c.marker}<span className="uw-sub"> (not priced)</span></td>
                    <td>{c.value != null ? c.value : '—'}</td>
                    <td>{c.unit || '—'}</td>
                    <td>{rep.inputs.draw_date ? fmtDate(rep.inputs.draw_date) : '—'}</td>
                    <td>
                      <span className={`uw-prov uw-prov-${c.provenance}`}>
                        {c.provenance === 'lab_verified' ? 'Lab-verified' : 'Self-reported'}
                      </span>
                    </td>
                    <td>{c.lab || '—'}</td>
                  </tr>
                ))}
                <tr className="uw-group-note"><td colSpan={6}>
                  Albumin and alkaline phosphatase are established mortality predictors in the
                  insurance-medicine literature (Journal of Insurance Medicine; albumin
                  n=1,704,566 / 53,211 deaths; liver function tests n=15,272,955 / 268,593
                  deaths).
                </td></tr>
                {_extra.length > 0 && (<>
                  <tr className="uw-group-head"><td colSpan={6}>
                    Additional marker
                  </td></tr>
                  {_extra.map(_row)}
                </>)}
              </>);
              })()}
            </tbody>
          </table>
        </section>

        {/* ── D1 — Longitudinal wearable evidence ── */}
        <section className="uw-sec uw-sec-evidence">
          <h2>{rep.wearables.section_title || 'Longitudinal wearable evidence'}</h2>
          {rep.wearables.framing && (
            <p className="uw-framing">{rep.wearables.framing}</p>
          )}
          {rep.wearables.eligible.length > 0 ? (
            <table className="uw-table uw-evidence">
              <thead>
                <tr>
                  <th>Stream</th><th>12-month mean</th><th>Trend (full span)</th>
                  <th>Coverage</th><th>Span</th>
                </tr>
              </thead>
              <tbody>
                {rep.wearables.eligible.map(w => (
                  <tr key={w.table}>
                    {/* E2 step 1 — the eligibility string was repeated in full inside EVERY
                        Stream cell. Measured under print emulation: that cell's inner width is
                        17.1mm, so a ~60-character string had no word that fits and wrapped
                        character-by-character into a 133px-tall block — which both blew the
                        table to 228mm (cap 184.6mm) and inflated row height until rows split
                        across the page boundary. It is the same sentence for every row, so it
                        belongs in one shared footnote, not six cells. */}
                    <td>
                      {w.label}<sup className="uw-fn">†</sup>
                      {w.caveat && <div className="uw-caveat">{w.caveat}</div>}
                    </td>
                    <td className="uw-ev-val">
                      {w.mean_12mo != null
                        ? <><strong>{w.mean_12mo.toLocaleString()}</strong> <em>{w.unit}</em></>
                        : <span className="uw-sub">—</span>}
                    </td>
                    <td><UwSparkline points={w.sparkline} months={w.sparkline_months} /></td>
                    <td className="uw-ev-cov">
                      {w.coverage_pct_12mo != null ? (
                        <>
                          {w.coverage_pct_12mo}%{' '}
                          <span className="uw-sub">({w.days_with_data_12mo} days)</span>
                          {w.sufficient === false && (
                            <div className="uw-insufficient">limited — below {rep.wearables.sufficiency_rule}</div>
                          )}
                        </>
                      ) : <span className="uw-sub">—</span>}
                    </td>
                    <td className="uw-sub">
                      {w.first_date ? `${fmtDate(w.first_date)} – ${fmtDate(w.last_date)}` : '—'}
                      <div>{w.records != null ? `${w.records.toLocaleString()} records` : ''}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="uw-sub">No cloud-API device data on record.</div>}

          {/* E2 — the one shared footnote the † in each Stream cell refers to. Taken from the
              payload so the wording stays server-owned; every eligible stream carries the same
              eligibility statement by construction. */}
          {rep.wearables.eligible.length > 0 && rep.wearables.eligible[0].eligibility && (
            <p className="uw-fn-note">
              <span className="uw-fn">†</span> {rep.wearables.eligible[0].eligibility}
            </p>
          )}
          {rep.wearables.no_percentiles_note && (
            <p className="uw-nopct">{rep.wearables.no_percentiles_note}</p>
          )}

          {/* D2 — one clause. The full reasoning lives in Attestations, not here. */}
          {rep.wearables.excluded.length > 0 && (
            <p className="uw-excl-line">
              <strong>Excluded by design:</strong>{' '}
              {rep.wearables.excluded.map(w =>
                `${w.source} (${w.records.toLocaleString()})`).join(', ')}
              {' '}— platform health-store data, never used. See Attestations.
            </p>
          )}

          {rep.irs_a && (
            <div className="uw-irs">
              IRS-A {rep.irs_a.value != null ? Number(rep.irs_a.value).toFixed(1) : '—'} — <strong>{rep.irs_a.label}</strong>
            </div>
          )}
        </section>

        {/* ── Attestations ── */}
        <section className="uw-sec">
          <h2>Attestations</h2>
          <ul className="uw-limits">
            {rep.attestations.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </section>

        {/* ── Consent ── */}
        <section className="uw-sec">
          <h2>Consent</h2>
          <table className="uw-mini">
            <tbody>
              <tr><td>Consent type</td><td>{rep.consent.type}</td></tr>
              <tr><td>Version</td><td>{rep.consent.version}</td></tr>
              <tr><td>Granted</td><td>{rep.consent.granted_at ? formatDateTimeZoned(rep.consent.granted_at) : '—'}</td></tr>
              <tr><td>Withdrawal</td><td>{rep.consent.withdrawal_right}</td></tr>
            </tbody>
          </table>
        </section>

        {/* ── Methodology + the limitations that travel with the number ── */}
        <section className="uw-sec uw-sec-method">
          <h2>Methodology &amp; limitations</h2>
          <p className="uw-method-note">{m.note}</p>
          <h3>Limitations</h3>
          <ul className="uw-limits">
            {m.limitations.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
          {/* D4 — published mortality gradients and wearable caveats live HERE, footnoted,
              never beside a value on the evidence table. */}
          {m.evidence_notes && m.evidence_notes.length > 0 && (
            <>
              <h3>Device evidence — basis and caveats</h3>
              <ul className="uw-limits uw-evidence-notes">
                {m.evidence_notes.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </>
          )}
        </section>

        {/* D4 — jurisdiction-packed notices. Their own section, above the footer, because a
            permissible-use statement that a reader has to find in small print is not a notice. */}
        {rep.footer.notices && rep.footer.notices.length > 0 && (
          <section className="uw-sec uw-sec-notices">
            <h2>Use and disclosure</h2>
            <ul className="uw-notices">
              {rep.footer.notices.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
            {/* Phase 5 — a regulation-FORCED element, present in the shared codebase for
                both jurisdictions and switched by the copy pack, never conditionally
                compiled and never an instance-only file. The pack supplies null where the
                jurisdiction does not require it, so there is no jurisdiction test here —
                only a presence test, which is the same test for every jurisdiction. */}
            {rep.footer.jurisdiction_notice && (
              <div className="uw-jurnotice">
                <h3>{rep.footer.jurisdiction_notice.title}</h3>
                <p>{rep.footer.jurisdiction_notice.body}</p>
              </div>
            )}
          </section>
        )}

        <footer className="uw-doc-foot">
          <p>{rep.footer.entity}</p>
          <p>{rep.footer.medical_disclaimer}</p>
          <p>{rep.footer.regulatory}</p>
        </footer>
        </td></tr></tbody>
        <tfoot className="uw-printfoot"><tr><td>
          <p className="uw-runfoot">
            {h.report_id} · Generated {formatDateTimeZoned(h.generated_at)}
          </p>
          {/* KAL-200 — synthetic notice. It lives INSIDE the repeating tfoot so it appears
              on EVERY printed page: a page that gets separated from page 1 must still say
              what it is. Deliberately the same small running-footer type as the report ID,
              NOT a watermark across the face — a diagonal overlay would obscure the very
              figures the page exists to show, and reads as a draft rather than a statement
              of provenance. */}
          {rep.synthetic && (
            <p className="uw-runfoot uw-runfoot-syn">{rep.synthetic_notice}</p>
          )}
        </td></tr></tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Corporate Wellness (B2B Admin) ─────────────────────────────────────────
function CorporatePage() {
  const ageDist = [
    { range: '30-35', count: 12 }, { range: '35-40', count: 28 }, { range: '40-45', count: 45 },
    { range: '45-50', count: 38 }, { range: '50-55', count: 22 }, { range: '55-60', count: 15 },
    { range: '60-65', count: 8 }, { range: '65+', count: 4 },
  ];
  const kpis = [
    { label: 'Avg Biological Age', value: '44.2', sub: '2.1 yrs younger than workforce avg', icon: '\u23F3' },
    { label: 'Test Completion', value: '87%', sub: '149 of 172 enrolled', icon: '\uD83D\uDCCA' },
    { label: 'Active Users (30d)', value: '134', sub: '78% engagement rate', icon: '\uD83D\uDC65' },
  ];
  const credits = [
    { company: 'Dolma Fund', plan: 'Enterprise', credits: 500, used: 127, expires: '2027-01-15' },
    { company: 'NPL Kathmandu', plan: 'Lab Partner', credits: 1000, used: 312, expires: '2026-12-31' },
    { company: 'Demo Corp', plan: 'Pilot', credits: 50, used: 8, expires: '2026-06-30' },
  ];
  return (
    <div className="corporate-page">
      <div className="page-header"><h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Corporate Wellness</h2><p className="page-subtitle">Workforce health overview and B2B account management</p></div>
      <div className="corporate-kpis">
        {kpis.map(k => (
          <div key={k.label} className="corporate-kpi-card">
            <span className="kpi-icon">{k.icon}</span>
            <div className="kpi-value">{k.value}</div>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>
      <div className="corporate-section">
        <h3>Workforce Biological Age Distribution</h3>
        <div className="corporate-chart-wrap">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ageDist} margin={{top:10,right:20,left:0,bottom:0}}>
              <XAxis dataKey="range" tick={{fill:'#8B6914',fontSize:12}} />
              <YAxis tick={{fill:'#8B6914',fontSize:11}} />
              <Tooltip contentStyle={{background:'var(--bg-card, #112240)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'8px',color:'var(--text-primary, #e2e8f0)'}} cursor={{fill:'rgba(255,255,255,0.04)'}} />
              <Bar dataKey="count" fill="#C9973A" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="corporate-section">
        <h3>Test Credits</h3>
        <div className="corporate-table-wrap">
          <table className="corporate-table">
            <thead><tr><th>Company</th><th>Plan</th><th>Credits</th><th>Used</th><th>Remaining</th><th>Expires</th></tr></thead>
            <tbody>
              {credits.map(c => (
                <tr key={c.company}>
                  <td>{c.company}</td><td>{c.plan}</td><td>{c.credits}</td><td>{c.used}</td>
                  <td style={{color: c.credits - c.used < 50 ? '#F59E0B' : '#112240'}}>{c.credits - c.used}</td>
                  <td>{c.expires}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="corporate-section npl-revenue-section">
        <h3>Partnership Revenue &mdash; National Path Lab</h3>
        <div className="corporate-revenue">
          <div className="revenue-card npl-hero-card"><div className="revenue-value npl-hero-value">NPR 139,500</div><div className="revenue-label">Monthly Revenue</div></div>
          <div className="revenue-card"><div className="revenue-value">NPR 42,000</div><div className="revenue-label">This Week</div></div>
          <div className="revenue-card"><div className="revenue-value">127</div><div className="revenue-label">Tests This Month</div></div>
          <div className="revenue-card"><div className="revenue-value">NPR 1,098</div><div className="revenue-label">Avg Revenue / Test</div></div>
        </div>
      </div>
    </div>
  );
}

// ── Compliance Dashboard (Admin Only) ───────────────────────────────────────
function ComplianceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ropaData, setRopaData] = useState(null);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const summary = await api.get('/admin/compliance/summary');
      setData(summary);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const loadRopa = async () => {
    try {
      const r = await api.get('/admin/ropa');
      setRopaData(r);
    } catch (e) {
      setError('Failed to load ROPA: ' + e.message);
    }
  };

  if (loading) return <LoadingSpinner message="Loading compliance data..." />;
  if (error) return <div className="compliance-error">Error: {error}</div>;
  if (!data) return null;

  return (
    <div className="compliance-dashboard">
      <div className="page-header"><h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />GDPR Compliance</h2><p className="page-subtitle">UK GDPR compliance dashboard for Kalpa Health Analytics.</p></div>

      {/* Consent Matrix */}
      <div className="compliance-section">
        <h3>Consent Status</h3>
        <div className="compliance-table-wrap">
          <table className="compliance-table">
            <thead>
              <tr>
                <th>User</th>
                {data.consent_types.map(ct => <th key={ct}>{ct.replace(/_/g, ' ')}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.consent_matrix.map(row => (
                <tr key={row.profile}>
                  <td>{row.display_name}</td>
                  {data.consent_types.map(ct => {
                    const s = row.consents[ct]?.status || 'not_granted';
                    return (
                      <td key={ct}>
                        <span className={`compliance-badge ${s}`}>
                          {s === 'granted' ? 'Yes' : s === 'withdrawn' ? 'Withdrawn' : 'No'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transfer Agreements */}
      <div className="compliance-section">
        <h3>International Transfer Agreements</h3>
        <div className="transfer-cards">
          {data.transfers.map(t => (
            <div key={t.id} className={`transfer-card ${t.is_active ? 'active' : 'inactive'}`}>
              <div className="transfer-header">
                <span className="transfer-name">{t.partner_name}</span>
                <span className={`transfer-badge ${t.is_active ? 'active' : 'inactive'}`}>
                  {t.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="transfer-details">
                <span>Country: {t.partner_country}</span>
                <span>Mechanism: {t.transfer_mechanism}</span>
                <span>IDTA Ref: {t.idta_reference}</span>
                {!t.has_adequacy && <span className="transfer-warning">No adequacy decision</span>}
              </div>
              {!t.is_active && <div className="transfer-alert">Data transfers blocked until agreement is activated.</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Open Breaches */}
      {data.open_breaches.length > 0 && (
        <div className="compliance-section breach-section">
          <h3>Open Breaches</h3>
          {data.open_breaches.map(b => (
            <div key={b.id} className="breach-card">
              <div className="breach-header">
                <span className="breach-type">{b.breach_type}</span>
                {b.ico_notification_required ? (
                  <span className="breach-ico-required">ICO notification required — 72hr deadline</span>
                ) : null}
              </div>
              <p>{b.description}</p>
              <span className="breach-meta">Reported: {formatDateTimeZoned(b.reported_at)} &middot; Affected: ~{b.estimated_users_affected} users</span>
            </div>
          ))}
        </div>
      )}

      {/* Pending Requests */}
      {(data.pending_deletions.length > 0 || data.pending_objections.length > 0) && (
        <div className="compliance-section">
          <h3>Pending Data Requests</h3>
          {data.pending_deletions.map(u => (
            <div key={u.profile_name} className="pending-request">Deletion requested: {u.display_name} ({u.profile_name})</div>
          ))}
          {data.pending_objections.map(u => (
            <div key={u.profile_name} className="pending-request">Automated processing objection: {u.display_name} ({u.profile_name})</div>
          ))}
        </div>
      )}

      {/* Recent Events */}
      <div className="compliance-section">
        <h3>Recent Compliance Events</h3>
        <div className="compliance-events-list">
          {data.recent_events.map(e => (
            <div key={e.id} className={`compliance-event severity-${e.severity}`}>
              <span className="event-type">{e.event_type}</span>
              <span className="event-actor">{e.actor || '—'}</span>
              <span className="event-details">{typeof e.details === 'string' ? e.details : JSON.stringify(e.details)}</span>
              <span className="event-time">{formatDateTimeZoned(e.created_at)}</span>
            </div>
          ))}
          {data.recent_events.length === 0 && <p className="compliance-empty">No compliance events recorded yet.</p>}
        </div>
      </div>

      {/* DPIA & ROPA */}
      <div className="compliance-section">
        <h3>Documentation</h3>
        <div className="compliance-docs">
          <div className="doc-card">
            <h4>DPIA Status</h4>
            <span className="doc-status stub">Stub — pending legal review</span>
            <p>Next review: {data.dpia_next_review}</p>
          </div>
          <div className="doc-card">
            <h4>Record of Processing Activities</h4>
            {!ropaData ? (
              <button className="rights-btn" onClick={loadRopa}>Load ROPA</button>
            ) : (
              <div className="ropa-summary">
                <p>{ropaData.processing_activities?.length || 0} processing activities documented</p>
                <p>Last updated: {ropaData.last_updated}</p>
              </div>
            )}
          </div>
          <div className="doc-card">
            <h4>Automated Decisions</h4>
            <p>{data.automated_decisions_count} decisions logged</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LongevityPointsPill({ points = null }) {
  const features = useFeatures();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  // Gamification disabled for this instance — hide the pill entirely.
  if (!features.gamification) return null;
  // W6: never show a FABRICATED points total. Render only when a real numeric
  // points value is supplied by data (no /api/points oracle exists yet); otherwise
  // render nothing rather than a hardcoded "340" + invented +10/+50/+25 earn rules.
  if (typeof points !== 'number' || !Number.isFinite(points)) return null;
  return (
    <div className="points-pill-wrap" ref={ref}>
      <button className="points-pill" onClick={() => setOpen(!open)}>{'\uD83D\uDD25'} {points}</button>
      {open && (
        <div className="points-dropdown">
          <div className="points-dropdown-title">{'\uD83D\uDD25'} {points} Longevity Points</div>
          <div className="points-redeem">Redeem at checkout against NPL test orders</div>
        </div>
      )}
    </div>
  );
}

// ── Tiny inline sparkline SVG ─────────────────────────────────────────────────
function MiniSparkline({ data = [], color = '#112240', width = 60, height = 24 }) {
  if (!data || data.length < 2) return <span style={{color:'#555555',fontSize:'0.7rem'}}>—</span>;
  const vals = data.map(d => typeof d === 'number' ? d : (d.value || 0));
  const min = Math.min(...vals); const max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{verticalAlign:'middle'}}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Fitness page (Section 2 redesign) ────────────────────────────────────────
// Period averages helper — pulls last N points by date and averages.
// ══ KAL-265/266 — ONE WINDOW, ONE MEAN, FOR EVERY SURFACE ═══════════════════════════════════
// This block replaces SIX private window definitions and THREE separate zero-filters. Measured on
// NPL, 23 Aug 2026, one session, one metric (steps), one nominal "7 days":
//
//   Averages card   slice(-7) of a series ending YESTERDAY      17-23 Aug  n=7  10,412
//   Today drawer    slice(-7) of a month series ending TODAY     18-23 Aug  n=6  11,021
//   MDB drawer      server range=7d  (since = today-7, date>=)   16-23 Aug  n=8   9,911
//   AZM drawer      server range=7d  SPECIAL-CASED to Mon week   24 Aug     n=1       -
//   Today bar       month-to-date complete days                  1-23 Aug   n=23  8,039
//   AZM ring        Monday-reset calendar week                   24 Aug     n=1       0
//
// Every one of those is defensible in isolation and the set is indefensible. `slice(-n)` is the
// common error: it takes n ELEMENTS, so its meaning depends on whether the series happens to be
// contiguous AND on whether its last element is today. Both vary by metric and by endpoint.
//
// THE RULE, and it is the only one now:
//   * A window is N CALENDAR DAYS, selected BY DATE, ending at the latest COMPLETE day.
//   * "Complete" means strictly before the INSTANCE-LOCAL today. A day still being lived is a
//     partial reading (monthly_cycle.py:373 measured 246 steps against the device's 4,297).
//   * A mean EXCLUDES null and INCLUDES 0. `filter(v > 0)` deleted a real rest day and
//     reported AZM 69.7 where the true 7-day mean was 59.7 — a 17% overstatement.
//   * The denominator is DISCLOSED, never implied by the label.
//
// `todayISO` must be the SERVER's as_of_date (instance-local), not the browser's clock: on NPL
// those differ by 5h45m and the browser's would reintroduce KAL-261.
function _windowByDate(seriesArr, days, todayISO) {
  if (!Array.isArray(seriesArr) || seriesArr.length === 0) return [];
  // Guard the whole contract on having a real boundary. With no as_of_date we cannot know which
  // day is incomplete, so we fall back to "everything up to the last element" rather than
  // silently trusting the browser's date.
  const complete = todayISO ? seriesArr.filter(d => d && d.date && d.date < todayISO)
                            : seriesArr.slice();
  if (complete.length === 0) return [];
  // ⚠ THE ANCHOR IS THE LATEST COMPLETE CALENDAR DAY, NOT THE LAST ELEMENT PRESENT.
  // The first version used `complete[complete.length - 1].date`, and that reintroduced the very
  // divergence this function exists to remove — because it means something different on a
  // SPARSE series than on a DENSE one, and the two producers for one metric differ in exactly
  // that way. `/api/fitness/metric/<m>` builds from a date-keyed dict, so days with no data are
  // ABSENT; `today_v2` emits one entry per calendar day with `value: null`.
  // MEASURED on US, 23 Aug 2026, steps, both series carrying IDENTICAL values:
  //   Fitness series ends 20 Aug (sparse)  -> anchor 20 Aug -> window 14-20 Aug -> 7,003
  //   today_v2 series ends 22 Aug (dense)  -> anchor 22 Aug -> window 16-22 Aug -> 6,568
  // Same metric, same rule, two answers — from the shape of the array rather than the data.
  // Anchoring on the calendar makes the window identical for both, and the DISCLOSED
  // denominator then does its job: 5 of 7 days with data, on both surfaces.
  const end = todayISO ? _isoAddDays(todayISO, -1) : complete[complete.length - 1].date;
  const start = _isoAddDays(end, -(days - 1));
  return complete.filter(d => d.date >= start && d.date <= end);
}
// NOTE: `_isoAddDays` is NOT redeclared here. An identical implementation already existed at
// App.js:21186 for the AZM week bucketing, and the first version of this block added a second
// copy — caught by the build, which is the only reason it is not now two functions drifting
// apart. Function declarations hoist, so the one below is in scope here.
// Mean over a window. EXCLUDES null/non-finite; INCLUDES 0. Returns the denominator with the
// value so no caller can print a mean without being able to state what it averaged.
function _meanOver(windowArr) {
  if (!Array.isArray(windowArr) || windowArr.length === 0) return { mean: null, n: 0 };
  const vals = windowArr.map(d => (d && d.value == null) ? null : Number(d.value))
                        .filter(v => v != null && Number.isFinite(v));
  if (vals.length === 0) return { mean: null, n: 0 };
  return { mean: vals.reduce((a, b) => a + b, 0) / vals.length, n: vals.length };
}
// The two public helpers every surface now calls. `days` is a COUNT OF CALENDAR DAYS.
function _periodStats(seriesArr, days, todayISO) {
  const win = _windowByDate(seriesArr, days, todayISO);
  const { mean, n } = _meanOver(win);
  return { mean, daysWithData: n, daysInWindow: days,
           from: win.length ? win[0].date : null, to: win.length ? win[win.length - 1].date : null };
}
function _periodAvg(seriesArr, days, todayISO) {
  return _periodStats(seriesArr, days, todayISO).mean;
}
// Previous-period average — the SAME window rule shifted back one full period, for trend arrows.
function _prevPeriodAvg(seriesArr, days, todayISO) {
  const win = _windowByDate(seriesArr, days, todayISO);
  if (!win.length) return null;
  const end = _isoAddDays(win[0].date, -1);
  const start = _isoAddDays(end, -(days - 1));
  const prev = (Array.isArray(seriesArr) ? seriesArr : [])
    .filter(d => d && d.date && d.date >= start && d.date <= end);
  return _meanOver(prev).mean;
}

// ── S2.2 — provenance + freshness for the Fitness surface ─────────────────────
// Pilot users compared Kalpa against their watch's own app (Zepp, Google Health) and
// had no way to tell WHAT Kalpa was reading or WHEN it last received anything — so a
// definitional difference read as "Kalpa is wrong". This states both, and only from
// what the server actually reported: when the server names no source, no source line
// is invented. `received` is the SAME server timestamp the sync bar shows, so the two
// can never disagree.
// Section C — the wording below is COMPLIANCE COPY and is reproduced verbatim in the popover.
// Only its presentation changed: it used to sit open above the fold as a 2-3 line paragraph that
// pushed Today's Activity down the page and wrapped mid-phrase on a phone. Do not reword it here;
// edit it in one place and it stays one place.
const FIT_PROV_FULL_NOTE =
  'Kalpa shows what your phone or watch recorded — it doesn’t measure separately, so these ' +
  'figures are only as complete as what that app exports.';

function FitnessProvenance({ prov, received, asOfDate, tzLabel }) {
  const [, tick] = useState(0);
  const [showNote, setShowNote] = useState(false);
  useEffect(() => { const id = setInterval(() => tick(x => x + 1), 60000); return () => clearInterval(id); }, []);
  // Escape closes it, matching every other dismissible surface in the app.
  useEffect(() => {
    if (!showNote) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowNote(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showNote]);
  if (!prov) return null;   // backend without provenance → render nothing, never a guess
  const rel = _relSync(received);
  const label = prov.source_label;
  const dates = Object.values(prov.fields || {})
    .map(x => x && x.last_recorded_date).filter(Boolean).sort();
  const lastAny = dates.length ? dates[dates.length - 1] : null;
  return (
    <div className="fit-provenance" data-testid="fitness-provenance">
      <div className="fit-prov-line">
        {/* KAL-268 — approved copy: ONE fact, naming the counting day, its timezone and the
            last arrival. Was "No data today — last 23 Aug 2026" sitting under a separate
            "Counting 24 Aug 2026" banner. */}
        {label
          ? <><span className="fit-prov-key">Read from</span> <strong>{label}</strong></>
          : <><span className="fit-prov-key">No data yet for</span>{' '}
              <strong>{asOfDate ? formatDate(asOfDate) : 'today'}</strong>
              {tzLabel && <> ({tzLabel})</>}
              {lastAny && <> · last received <strong>{formatDate(lastAny)}</strong></>}</>}
        {rel && <><span className="fit-prov-sep">·</span><span>received {rel}</span></>}
        <button type="button" className="fit-prov-info" data-testid="fitness-provenance-info"
          aria-label="What these figures are based on" aria-expanded={showNote}
          onClick={() => setShowNote(v => !v)}>i</button>
      </div>
      {showNote && ReactDOM.createPortal(
        // A portal at z-index 1300 — ABOVE the fixed Feedback pill (1150) and the sticky
        // GlobalSyncBar (40). Rendering it inside the page flow would have put it under the pill,
        // which is exactly the stacking defect S5 fixed for the sync bar; it is measured in
        // verification, not assumed.
        <div className="fit-prov-pop-backdrop" onClick={() => setShowNote(false)}>
          <div className="fit-prov-pop" role="dialog" aria-label="About these figures"
            data-testid="fitness-provenance-popover" onClick={e => e.stopPropagation()}>
            <div className="fit-prov-pop-head">
              <span>About these figures</span>
              <button type="button" className="fit-prov-pop-x" aria-label="Close"
                onClick={() => setShowNote(false)}>×</button>
            </div>
            <p className="fit-prov-pop-body">{FIT_PROV_FULL_NOTE}</p>
          </div>
        </div>,
        document.body)}
    </div>
  );
}

function FitnessPage({ profile, fitbitSyncError, onNav, sectionRef, syncing, triggerFitbitSync, openDrawer }) {
  // 7d / 30d / 90d. Drives period-averages section and trend charts.
  const [period, setPeriod] = useState('7d');
  // Deep-section scroll: a Today vital tap (or #vitals?section=X deep link)
  // requests we scroll to a specific trend once it has rendered. Seed once
  // on mount from the nav ref (preferred) or the URL hash, then clear the ref.
  const [pendingSection, setPendingSection] = useState(() => {
    const fromRef = sectionRef && sectionRef.current;
    if (fromRef) { sectionRef.current = null; return fromRef; }
    const m = (window.location.hash || '').match(/[?&]section=([\w-]+)/);
    return m ? m[1] : null;
  });
  const [todayData, setTodayData] = useState(null);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing2, setSyncing2] = useState(false);
  // A7 — failure is tracked, not swallowed. `failedMetrics` names WHICH ones, so the
  // message can be specific instead of a generic apology.
  const [failedMetrics, setFailedMetrics] = useState([]);
  const [todayFailed, setTodayFailed] = useState(false);
  const [seriesAttempt, setSeriesAttempt] = useState(0);
  // Section B — two-loop Active Zone Minutes (same /api/active-minutes engine as the
  // Today hero) so the Fitness AZM square is continuous with the hero ring. two_loop only.
  const [azmWeek, setAzmWeek] = useState(null);   // drives the AZM ring number; tap opens the drawer
  // (fitAzmTip inline-popup state + its outside-tap effect removed 2026-07-20 — the AZM ring now
  //  opens the shared MetricDrawerBody drawer, same as the Today card and the other vitals.)
  useEffect(() => {
    if (!FEATURES_GLOBAL.two_loop) return;
    let alive = true;
    fetch(`${API_BASE}/active-minutes`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (alive && d && !d.error) setAzmWeek(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Metric taps open the shared App-level drawer (overlays the page; never a
  // route). showMetric resolves the spec from METRIC_CONFIG.
  // KAL-142 Phase 3 — BODY ROUTES TO THE SAME DRAWER. While today_v2 is on, the four metrics
  // Today v2 owns open the universal drawer from here too, with the SAME default range, so the
  // two entry points cannot show one metric two ways. Everything else on Body is untouched —
  // this is a wiring change, not a Body redesign.
  const TV2_BODY_KEYS = { steps: 'steps', sleep_duration: 'sleep',
                          active_zone_minutes: 'azm', resting_hr: 'rhr' };
  const showMetric = (key) => {
    if (!openDrawer) return;
    const tv2key = FEATURES_GLOBAL.today_v2 ? TV2_BODY_KEYS[key] : null;
    if (tv2key) {
      return openDrawer({ kind: 'tv2metric', metric: tv2key,
                          month: new Date().toISOString().slice(0, 7),
                          title: TV2_METRICS[tv2key].label });
    }
    // KAL-265 — hand the drawer the SERVER's day boundary. Without it the drawer would fall back
    // to "the last element of the series", which is right for a series ending yesterday and wrong
    // for active_minutes, whose series includes today.
    openDrawer(metricSpecFromConfig(key, { asOfDate: todayData?.as_of_date || null }));
  };
  // Selected activity log_id (opens ActivityDetailPanel)
  const [activityDetail, setActivityDetail] = useState(null);
  const [sleepDetail, setSleepDetail] = useState(false);   // S3 — sleep-stage detail overlay
  const [targetEdit, setTargetEdit] = useState(null);      // KAL-081 — metric key being edited
  const [targetSavedAt, setTargetSavedAt] = useState(0);   // bump to re-read after a save
  const [cycleTargets, setCycleTargets] = useState(null);  // KAL-090 — for the queued badge
  // KAL-107 — NEVER ADVERTISE AN AFFORDANCE THE BACKEND CANNOT HONOUR. NPL's app.py does not
  // register the /api/cycle/* blueprint (KAL-036 keeps the fleet backends deliberately
  // divergent), so /api/cycle/targets 404s there. Without this gate the same bundle renders
  // "tap ring to edit" and a pencil on NPL, and tapping opens a sheet that fetches 404, gets
  // null and renders NOTHING — a tap target that silently does nothing, which is worse than the
  // feature simply being absent. Gating on the endpoint having actually answered lets ONE
  // bundle be correct on all four instances.
  const targetsAvailable = Array.isArray(cycleTargets) && cycleTargets.length > 0;
  useEffect(() => {
    let alive = true;
    api.get('/cycle/targets')
      .then(d => alive && setCycleTargets(d && Array.isArray(d.targets) ? d.targets : null))
      .catch(() => alive && setCycleTargets(null));
    return () => { alive = false; };
  }, [targetSavedAt]);
  // Deep link: refresh on /#activity-{id} should reopen that activity.
  // ActivityDetailPanel writes this hash whenever it mounts, so the URL
  // shape is the contract — parse it here on mount and seed the state.
  useEffect(() => {
    const m = (window.location.hash || '').match(/^#activity-(.+)$/);
    if (m && m[1]) {
      setActivityDetail(decodeURIComponent(m[1]));
    }
  }, []);
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  // S2.2 — the same server-side "last data received" the sync bar reads. ONE authority,
  // so the Fitness header and the sync bar can never quote different freshness.
  const { lastSynced: lastReceived } = useSyncStatus();

  const PERIODS = { '7d': 7, '30d': 30, '90d': 90 };
  const periodDays = PERIODS[period];
  // Fetch double the period so we can compute the previous-period mean
  // for trend arrows without an extra round-trip per metric.
  const fetchRange = periodDays <= 7 ? '30d' : periodDays <= 30 ? '90d' : '1y';

  const METRIC_KEYS = ['steps', 'resting_hr', 'hrv', 'sleep_duration', 'spo2',
                        'calories', 'active_minutes'];

  // ── A7 / KAL-241 — STOP RENDERING FAILURE AS EMPTINESS ────────────────────────────────
  // Both loaders below used to swallow: loadToday did `.catch(() => null)` and loadSeries did
  // `.catch(() => ({ data: [] }))`. A metric that ERRORED therefore drew an empty chart,
  // pixel-identical to a metric with genuinely no data — and with N=7 concurrent requests on a
  // saturated single worker, some failed and some did not. That is exactly Tim's report that
  // Fitness "arrives incomplete, with metrics appearing minutes later": the page was not still
  // loading, it had already given up on some of them and said nothing.
  //
  // This is the KAL-232 class in its purest form. Now: each failure is COUNTED and NAMED, the
  // page states that some metrics did not load, and there is a retry.
  const loadToday = useCallback(async () => {
    try {
      const t = await withTimeout(api.get('/fitness/today'), LOAD_TIMEOUT_MS, 'fitness/today');
      setTodayData(t);
      setTodayFailed(false);
    } catch (e) {
      setTodayData(null);
      setTodayFailed(true);
      try { reportClientError('fitness/today ' + ((e && e.message) || 'failed'), null, 'vitals'); } catch (_) {}
    }
  }, []);

  const loadSeries = useCallback(async () => {
    setLoading(true);
    const failed = [];
    const results = await Promise.all(METRIC_KEYS.map(async (m) => {
      try {
        return await withTimeout(api.get(`/fitness/metric/${m}?range=${fetchRange}`),
                                 LOAD_TIMEOUT_MS, m);
      } catch (e) {
        failed.push(m);
        return null;      // null = FAILED, distinct from {data: []} = loaded-and-empty
      }
    }));
    const map = {};
    METRIC_KEYS.forEach((m, i) => { map[m] = results[i] ? ((results[i].data) || []) : []; });
    setSeries(map);
    setFailedMetrics(failed);
    if (failed.length) {
      try { reportClientError('fitness metrics failed: ' + failed.join(','), null, 'vitals'); } catch (_) {}
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRange, seriesAttempt]);

  useEffect(() => { loadToday(); }, [loadToday]);
  useEffect(() => { loadSeries(); }, [loadSeries]);

  // Once the trend charts have rendered (loading done), scroll the requested
  // section into view, then clear the request so it fires only once.
  useEffect(() => {
    if (loading || !pendingSection) return;
    const id = pendingSection;
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    setPendingSection(null);
    return () => clearTimeout(t);
  }, [loading, pendingSection]);

  const handleRefresh = useCallback(async () => {
    setSyncing2(true);
    await Promise.all([loadToday(), loadSeries()]);
    setSyncing2(false);
  }, [loadToday, loadSeries]);

  const { pulling, pullDistance } = useScreenSync(handleRefresh);

  if (loading && !todayData) return <LoadingSpinner message="Loading fitness data..." />;

  // --- Today's snapshot (activity rings) ---
  // KAL-259 — `|| 0` again. The ring's own `_noDataToday('steps')` guard already renders "—"
  // from provenance, so this coercion was masked here; it is removed anyway, because the next
  // reader of `stepsToday` will not know the guard exists.
  const stepsTodayRaw = todayData?.steps?.total;
  const stepGoal = 10000;
  const stepColor = '#C9973A';

  // (activeMins/activeGoal/activePct/activeColor removed 9 Aug 2026 with the "Active min" tile —
  // they fed nothing else. todayData.active_minutes itself is UNTOUCHED and still served by
  // /api/fitness/today; only the tile that rendered it is gone.)

  // KAL-266 — `|| 0` fabricated a zero for a metric the server correctly reported as absent.
  const cals = todayData?.calories;
  const calGoal = 2200;
  const calPct = (cals == null) ? null : Math.min(100, Math.round(cals / calGoal * 100));
  const calColor = '#C9973A';

  // KAL-266 — same fabrication.
  const distKm = (todayData?.distance_km == null) ? null
                 : Math.round(todayData.distance_km * 10) / 10;
  const distGoal = 5;
  const distPct = (distKm == null) ? null : Math.min(100, Math.round(distKm / distGoal * 100));
  const distColor = '#C9973A';

  // S2.2 — a field with NOTHING received today must not render as a confident zero.
  // "0 steps" and "we have not heard from your phone yet" are different statements, and
  // the second is usually the true one (the app hasn't been opened, so Health Connect
  // hasn't been re-read). Only the server's own `received_today` drives this — when the
  // payload carries no provenance (older backend) every field reads as before.
  const _provFields = (todayData && todayData.provenance && todayData.provenance.fields) || {};
  const _noDataToday = (k) => {
    const p = _provFields[k];
    return !!(p && p.received_today === false);
  };
  // ── KAL-265 — ONE ACCESSOR FOR "WHAT DO I SHOW IN THIS RING" ─────────────────────────────
  // Every ring now asks the same question in the same way, and gets today's figure OR the most
  // recent one WITH ITS DATE. A ring that goes blank while the database holds a figure — which
  // is what steps, calories and distance all did on NPL — is no longer expressible here.
  const _ringValue = (k) => {
    const today = { steps: stepsTodayRaw, calories: cals, distance_km: distKm }[k];
    if (today != null) return { value: today, asOf: _localToday, isToday: true };
    const lat = (todayData?.latest || {})[k];
    if (lat && lat.value != null) return { value: lat.value, asOf: lat.as_of_date, isToday: false };
    return { value: null, asOf: null, isToday: false };
  };
  // The sub-line states the day whenever it is not today — never a bare figure that reads as
  // current, and never silence where a real figure exists.
  const _ringSub = (k, fmt, goalTxt) => {
    const r = _ringValue(k);
    if (r.value == null) return _lastRecordedSub(k);
    if (r.isToday) return goalTxt;
    return `${fmt(r.value)} on ${formatDate(r.asOf)}`;
  };
  const _lastRecordedSub = (k) => {
    const p = _provFields[k];
    return (p && p.last_recorded_date)
      ? `last recorded ${formatDate(p.last_recorded_date)}`
      : 'nothing received yet';
  };

  // --- Period averages for the stat-card row ---
  const sleepHoursSeries = (series.sleep_duration || []).map(d => ({ date: d.date, value: d.value / 60.0 })); // minutes → hours
  const seriesByMetric = {
    steps: series.steps,
    active_minutes: series.active_minutes,
    calories: series.calories,
    sleep_duration: sleepHoursSeries,
    resting_hr: series.resting_hr,
    hrv: series.hrv,
    spo2: series.spo2,
  };

  // KAL-265 — the window boundary is the SERVER's instance-local today, declared before its
  // first use (it used to sit below these two, which is fine for a hoisted `const` in the same
  // scope only by luck of call order; the helpers now take it as an argument).
  const _localToday = todayData?.as_of_date || null;
  const avgCurrent = (key) => _periodAvg(seriesByMetric[key], periodDays, _localToday);
  const avgPrev = (key) => _prevPeriodAvg(seriesByMetric[key], periodDays, _localToday);
  // KAL-266 — the denominator, for the label. A mean whose basis is not stated is a mean the
  // reader cannot check: "90-day average" on NPL Calories is a mean of 84 days.
  const avgStats = (key) => _periodStats(seriesByMetric[key], periodDays, _localToday);

  // S2.2 — an average over a window whose source stopped reporting is not wrong, but it is
  // silently old. Name the newest reading that fed it once it is more than 2 days behind the
  // user's local today (the real case: UK Tim's Health Connect rows stop on 3 Jul while his
  // Fitbit keeps syncing daily — the SpO2/HRV averages were quoting a month-old sensor).
  // Basis is the SERVER's local today (as_of_date), not the browser's clock, so it agrees
  // with S4's day boundary.
  const _staleLatest = (key) => {
    const arr = seriesByMetric[key];
    const last = (Array.isArray(arr) && arr.length) ? arr[arr.length - 1].date : null;
    if (!last || !_localToday) return null;
    const dd = Math.round((new Date(_localToday + 'T00:00:00Z') - new Date(last + 'T00:00:00Z')) / 86400000);
    return (Number.isFinite(dd) && dd > 2) ? last : null;
  };

  const fmtNum = (v, decimals = 0) => (v === null || v === undefined || !Number.isFinite(v))
    ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals });

  function TrendArrow({ cur, prev, lowerBetter }) {
    if (cur === null || prev === null) return null;
    const diff = cur - prev;
    if (Math.abs(diff) / Math.abs(prev || 1) < 0.01) {
      return <span className="fit-trend" style={{ color: '#8B6914' }}>{'→'} flat</span>;
    }
    const up = diff > 0;
    const good = lowerBetter ? !up : up;
    return (
      <span className="fit-trend" style={{ color: good ? '#34d399' : '#ef4444' }}>
        {up ? '↑' : '↓'} {fmtNum(Math.abs(diff), Math.abs(prev) < 50 ? 1 : 0)} vs prev
      </span>
    );
  }

  // NPL has no Health Connect source for SpO₂ or Active Minutes yet, so those
  // cards would render permanently empty ("—") and read as broken. Hide them on
  // NPL until native capture lands (code stays gate-ready). Gate is instance-based,
  // same pattern as genetics.
  const _isNPL = FEATURES_GLOBAL.branding.instance === 'npl';
  const _hiddenOnNPL = new Set(_isNPL ? ['spo2', 'active_minutes'] : []);
  const STAT_CARDS = [
    { key: 'steps',          label: 'Daily Steps',     unit: '',    decimals: 0, color: '#C9973A' },
    // (#1377) This card averages the /api/fitness/metric/active_minutes series = day_points(), the
    // zone/step engine — so it is named for what it is, and its drawer states that rule.
    { key: 'active_minutes', label: 'Active Zone Minutes', unit: 'min', decimals: 0, color: '#EF4444' },
    { key: 'calories',       label: 'Calories',        unit: 'kcal',decimals: 0, color: '#F59E0B' },
    { key: 'sleep_duration', label: 'Sleep Duration',  unit: 'h',   decimals: 1, color: '#818CF8' },
    { key: 'resting_hr',     label: 'Resting HR',      unit: 'bpm', decimals: 0, color: '#ef4444', lowerBetter: true },
    { key: 'hrv',            label: 'HRV (RMSSD)',     unit: 'ms',  decimals: 0, color: '#fb7185' },
    { key: 'spo2',           label: 'SpO₂',            unit: '%',   decimals: 1, color: '#34d399' },
  ].filter(c => !_hiddenOnNPL.has(c.key));

  // --- Trend chart series (rendered with Recharts) ---
  // KAL-265 — the chart must plot the SAME dates the average describes; `slice(-periodDays)`
  // was a seventh window (element-count, and including today for active_minutes).
  const chartSlice = (key, transform) => {
    const arr = _windowByDate(seriesByMetric[key], periodDays, _localToday);
    return arr.map(d => ({ date: d.date, value: transform ? transform(d.value) : d.value }));
  };
  const stepsChart = chartSlice('steps');
  const hrvChart   = chartSlice('hrv');
  const rhrChart   = chartSlice('resting_hr');
  const sleepChart = chartSlice('sleep_duration');
  const spo2Chart  = chartSlice('spo2');

  // S3/S4 — Sleep + Resting-HR main-grid boxes. Sleep = last night (last sleep point,
  // in hours); RHR = latest reading. Rings: sleep fills toward an 8h target; RHR fills
  // MORE as it drops toward a fit resting rate (lower is better).
  const _lastVal = (arr) => (Array.isArray(arr) && arr.length ? arr[arr.length - 1].value : null);
  const lastNightSleepH = _lastVal(sleepChart);
  const avgSleepH = avgCurrent('sleep_duration');
  // null, NOT 0, when there is no last-night figure — the same false-zero class fixed on the
  // Steps and Distance rings on 14 Aug. Sleep happened to have data that day, so this one was
  // latent rather than visible: a user with no sleep record would have seen a confident “0%”
  // ring beside an “—”. RingGauge renders null as an absence.
  const sleepPct = lastNightSleepH != null ? Math.min(100, Math.round(lastNightSleepH / 8 * 100)) : null;
  const latestRHR = _lastVal(rhrChart);
  const avgRHR = avgCurrent('resting_hr');
  const rhrPct = latestRHR != null ? Math.max(0, Math.min(100, Math.round((100 - latestRHR) / (100 - 40) * 100))) : 0;

  const tickStyle = { fontSize: 10, fill: 'var(--text-muted, #8a9bb0)' };
  const xFmt = (d) => formatDate(d);
  const tooltipStyle = { fontSize: '12px', background: 'var(--bg-card, #112240)', color: 'var(--text-primary, #e2e8f0)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.4)' };

  // C — only claim an EXPIRED connection when one exists. fitbit_needs_reconnect is already
  // token-gated server-side, but fitbitSyncError is a client flag that a stray failed sync could
  // set for a user who has no Fitbit at all — which is how Daniel, on an Apple Watch, was told his
  // Fitbit connection had expired.
  const hasFitbit = todayData?.fitbit_connected !== false;
  const needsReconnect = hasFitbit && (fitbitSyncError || todayData?.fitbit_needs_reconnect);

  // ── KAL-081 (spec 5.3) — POSITION ON A RANGE, NOT A PROGRESS RING ──────────────────────────
  // Resting heart rate was rendered with RingGauge at `(100 - rhr) / (100 - 40) * 100`, i.e. 43%
  // for 74 bpm — VERIFIED against the code at App.js:17997, which confirms the spec's
  // reverse-engineering. That is position within an inverted 40-100 bpm band, not progress toward
  // a goal, and drawing it as a ring beside genuine target rings implies the user can "hit" their
  // resting heart rate. They cannot: it responds to behaviour over months.
  function RangeMarker({ value, low, high, color, size = 64 }) {
    if (value == null) return <div style={{ width: size, height: size }} />;
    const v = Math.max(low, Math.min(high, Number(value)));
    const pos = ((v - low) / (high - low)) * 100;
    return (
      <div style={{ width: size, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ position: 'relative', height: 8, borderRadius: 4,
                      background: 'linear-gradient(90deg, rgba(34,197,94,0.55), rgba(234,179,8,0.5), rgba(239,68,68,0.55))' }}>
          <div title={`${Math.round(value)} bpm within ${low}-${high}`}
               style={{ position: 'absolute', left: `calc(${pos}% - 5px)`, top: -3, width: 10, height: 14,
                        borderRadius: 3, background: color, border: '2px solid #0a0e1a',
                        boxShadow: '0 0 0 1px rgba(255,255,255,0.55)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-dim)' }}>
          <span>{low}</span><span>{high}</span>
        </div>
      </div>
    );
  }

  // pct === null|undefined means NO DATA, which is NOT the same as 0%. An absent ring draws
  // its track only and centres an em-dash; a zero ring draws an empty arc and says “0%”,
  // which is a claim about the user's day. Keeping the two distinguishable is the whole
  // point (KAL-125).
  function RingGauge({ pct, color, size = 64, stroke = 7, center }) {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const absent = (pct === null || pct === undefined || Number.isNaN(Number(pct)));
    const safePct = absent ? 0 : Math.max(0, Math.min(100, Number(pct) || 0));
    const dash = (safePct / 100) * circ;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block'}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke}/>
        {/* ⚠ THE VALUE ARC IS NOT DRAWN AT ALL WHEN THE FIGURE IS ABSENT. Leaving it in place
            with a zero-length dash is not neutral: strokeLinecap="round" paints a ROUND CAP
            even at length 0, so an absent ring showed a small coloured dot at 12 o'clock —
            visible in fitness-page-absence.png — which reads as a sliver of progress. Same
            false-zero class as the “0%” label it sat next to. */}
        {!absent && (
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`} style={{transition:'stroke-dasharray 0.6s ease'}}/>
        )}
        {center && <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle"
          fill={absent ? 'var(--text-dim)' : color} fontSize={size>60?11:9}
          fontWeight="700">{absent ? '—' : center}</text>}
      </svg>
    );
  }

  return (
    <div className="fitness-page" ref={containerRef}>
      {/* A7 / KAL-241 — SAY SO WHEN SOMETHING DID NOT LOAD.
          Previously a failed metric drew an empty chart, identical to a metric with no data,
          and the page looked merely sparse rather than broken. This names what is missing and
          offers a retry, so "arrives incomplete" becomes a stated outcome the user can act on
          rather than something they have to infer from an absence. */}
      {(failedMetrics.length > 0 || todayFailed) && (
        <div className="fit-loadfail" role="alert">
          <span>
            {todayFailed && failedMetrics.length === 0
              ? 'Couldn’t load today’s summary.'
              : `Couldn’t load ${failedMetrics.length === 1 ? '1 metric' : failedMetrics.length + ' metrics'}${todayFailed ? ' or today’s summary' : ''}.`}
            {failedMetrics.length > 0 && (
              <span className="fit-loadfail-which"> ({failedMetrics.map(m => m.replace(/_/g, ' ')).join(', ')})</span>
            )}
          </span>
          <button
            type="button"
            className="fit-loadfail-retry"
            onClick={() => { setTodayFailed(false); setFailedMetrics([]); loadToday(); setSeriesAttempt(a => a + 1); }}
          >
            Retry
          </button>
        </div>
      )}
      {/* Pull-to-refresh indicator */}
      {pulling && <div style={{textAlign:'center',padding:'8px',color:'#C9973A',fontSize:'0.8rem'}}>{pullDistance > 60 ? '↑ Release to refresh' : '↓ Pull to refresh'}</div>}
      {/* S5 — this page's own "↻ Syncing…" line was the THIRD indicator for one piece of state.
          Both of its triggers already reach the single GlobalSyncBar: pull-to-refresh goes
          through useScreenSync → triggerGlobalSync (which emits syncing/loading/ready), and the
          Fitbit background sync is reported by the same bar. Removed, not duplicated. */}

      {needsReconnect && (
        <div style={{background:'#F8F7F4',border:'1px solid #F59E0B',borderRadius:'8px',padding:'10px 14px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'10px',fontSize:'0.85rem'}}>
          <span style={{color:'#F59E0B'}}>⚠</span>
          <span style={{flex:1,color:'#112240'}}>Fitbit connection expired — data may be stale.</span>
          <button onClick={() => onNav && onNav('profile')} style={{background:'none',border:'none',color:'#112240',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>Reconnect →</button>
        </div>
      )}

      <div className="fitness-header">
        <h2><img className="title-mark" src="/kalpa_mark.png?v=2" alt="" style={{height:'24px',width:'24px',marginRight:'8px',verticalAlign:'middle',objectFit:'contain'}} />Fitness</h2>
        {/* KAL-261 — WAS: `Data from {as_of_date}`, compared against the UTC date.
            `as_of_date` is the day the server COUNTED, in the instance timezone - it is NOT the
            date the data came from. On 23 Aug 2026 it printed "Data from 24 Aug 2026" while the
            line immediately below correctly said the last data was 23 Aug. One of the two had to
            be wrong, and it was this one: there was no 24 Aug data at all.
            The per-metric dates in FitnessProvenance below are the real "where the data is
            from". This line now says only what it actually knows - which day is being counted -
            and stays silent unless that differs from the reader's own day. */}
        {/* KAL-268 — the header banner is GONE, not reworded. It said "Counting 24 Aug 2026"
            directly above "No data today — last 23 Aug 2026": two lines, two facts, one of them
            redundant. FitnessProvenance below now states the whole thing in one sentence (the
            approved 2.5 copy), and it is the surface that already owns per-metric freshness. */}
      </div>

      {/* S2.2 — what these numbers were read from, and when it last arrived. */}
      <FitnessProvenance prov={todayData?.provenance} received={lastReceived}
        asOfDate={todayData?.as_of_date} tzLabel={_tzShortLabel()} />

      {/* KAL-263 — THE OLD COPY NAMED THE WRONG CAUSE, AND ON TWO ACCOUNTS IT WAS SIMPLY FALSE.
          It said "Health Connect has not supplied a reading" and hard-coded "Health Connect" as
          the source, while the query behind it filtered `provider='health_connect'`. Measured on
          NPL 23 Aug 2026: 373 VO₂ Max rows exist, ALL of them from apple_health, none from
          health_connect - so Benjamin_Gocher (205 rows, to 2026-06-22) and npl_tester_daniel
          (114 rows, to 2026-08-02) were both told their reading did not exist while it sat in the
          table. The provider filter is gone; the source is now READ FROM THE ROW.
          The absence wording no longer blames a provider either, because on Android the app has
          never been able to supply this metric: `Vo2Max` is excluded from the runtime Health
          Connect permission request (react-native-health-connect 3.5.3 cannot map it and throws
          InvalidRecordType on a coroutine worker - an uncatchable hard crash). That is a pinned
          library limitation, tracked separately as KAL-264 and NOT fixed here. Saying "your
          device has not supplied one" would have been a second wrong cause. */}
      {_isNPL && (
        <div style={{fontSize:'0.72rem',lineHeight:1.45,color:'var(--text-muted, #8a9bb0)',margin:'4px 0 12px'}}>
          {todayData?.vo2_max_latest
            ? <>VO₂ max: {Number(todayData.vo2_max_latest.value).toFixed(1)} ml/kg/min · {todayData.vo2_max_latest.source || 'your phone'} last recorded {formatDate(todayData.vo2_max_latest.date)}. No newer VO₂ max was available at the last phone sync.</>
            : <>VO₂ max: no reading has reached Kalpa yet.</>}
        </div>
      )}

      {/* SECTION 1 — Today's activity (2×2 ring grid) */}
      <div className="fitness-section-label">Today's Activity</div>
      <div className="fitness-rings-grid">
        {/* Section B — AZM ring, continuous with the Today hero. Tap opens the SAME AZM detail
            drawer as the Today card + the other vitals (azmDrawerSpec → MetricDrawerBody); the old
            inline ⓘ popup is gone. */}
        {FEATURES_GLOBAL.two_loop && (() => {
          // ── KAL-267 — ROLLING 7 DAYS, THE SAME WINDOW TODAY USES ───────────────────────
          // WAS `azmWeek.week` (Monday-reset) with `pts = wk ? wk.points : 0`. Two faults in
          // three lines: the window disagreed with Today's rolling 7 (0 here vs 418 there,
          // measured 24 Aug 00:24 NPT — a Monday, so the week was 24 minutes old), and the
          // `: 0` fabricated a zero for a DISCONNECTED watch, printing a confident "0" beside
          // "connect your watch".
          // Now: rolling-7 from the server, and null — an absence — when there is none.
          const roll = (azmWeek && azmWeek.connected) ? (azmWeek.rolling || null) : null;
          const wk = (azmWeek && azmWeek.connected) ? azmWeek.week : null;
          const pts = roll ? roll.points : null;
          const azmTarget = (roll && roll.target) || (wk && wk.target) || 150;
          const frac = pts == null ? null : Math.min(1, pts / azmTarget);
          return (
            <div className="fitness-ring-card fitness-ring-azm fitness-clickable" role="button" tabIndex={0}
              onClick={() => (FEATURES_GLOBAL.today_v2
                ? openDrawer({ kind: 'tv2metric', metric: 'azm',
                               month: new Date().toISOString().slice(0, 10).slice(0, 7),
                               title: 'Active Zone Minutes' })
                : openDrawer(azmDrawerSpec()))}>
              {/* KAL-089 — AZM is one of the three SETTABLE targets, so its ring is a tap target
                  too. It sits OUTSIDE the tile array below (it is a two_loop card with its own
                  ring component), which is why the first pass missed it. */}
              {targetsAvailable ? (
                <button type="button" className="fitness-ring-tap"
                  aria-label="Edit your active zone minutes target"
                  onClick={(e) => { e.stopPropagation(); setTargetEdit('active_zone_minutes'); }}>
                  <_KalpaRing mode="progress" accent="#7F91DF" size={isMobile ? 64 : 72} stroke={11} pct={frac}>
                    {/* KAL-271 — length-driven size (the KAL-227 rule): 3 digits shrink, 4+ shrink
                        further, so "423" fits the circle at any Android text-zoom. */}
                    <span className={'kring-num' + (pts != null && String(pts).length >= 4 ? ' is-xwide'
                                     : pts != null && String(pts).length === 3 ? ' is-wide' : '')}
                      style={{ color: '#7F91DF' }}>{pts == null ? '—' : pts}</span>
                  </_KalpaRing>
                  <span className="fitness-ring-pencil" aria-hidden="true">{'✎'}</span>
                </button>
              ) : (
                <_KalpaRing mode="progress" accent="#7F91DF" size={isMobile ? 64 : 72} stroke={11} pct={frac}>
                  <span className={'kring-num' + (pts != null && String(pts).length >= 4 ? ' is-xwide'
                                   : pts != null && String(pts).length === 3 ? ' is-wide' : '')}
                    style={{ color: '#7F91DF' }}>{pts == null ? '—' : pts}</span>
                </_KalpaRing>
              )}
              <div className="fitness-ring-body">
                <div className="fitness-ring-label">Active Zone Min
                  {targetsAvailable && <span className="fitness-ring-hint">tap ring to edit</span>}
                </div>
                <div className="fitness-ring-val" style={{ color: '#7F91DF' }}>{pts == null ? '—' : pts}</div>
                {(() => { const q = _queuedTarget(cycleTargets, 'active_zone_minutes');
                  return q ? <div className="fitness-ring-queued">{q.now} now · {q.next} from {q.from}</div> : null; })()}
                {/* KAL-267 — the window is now NAMED. "this week" was the whole ambiguity:
                    it meant Monday-reset here and rolling-7 on Today. */}
                <div className="fitness-ring-sub">{pts != null
                  ? `${pts} / ${azmTarget} · rolling 7 days`
                  : 'connect your watch'}</div>
                {/* (#1377) State the rule NEXT TO the figure, not only one tap away in the drawer —
                    otherwise "moderate" and "vigorous" are unfalsifiable to the user. Compact form
                    of the one canonical definition; the drawer carries the full version. */}
                <div style={{ fontSize: 10.5, lineHeight: 1.35, color: 'var(--text-dim)', marginTop: 3 }}>
                  {AZM_ZONE_DEF_SHORT}
                </div>
              </div>
            </div>
          );
        })()}
        {[
          // S2.2 — val/sub fall back to "—  ·  last recorded <date>" when the server reports
          // that nothing arrived for today, rather than presenting an absence as a zero.
          // ⚠ AND FOR TWO OF THESE TILES IT DID EXACTLY THAT ANYWAY, until 14 Aug. `pct` was set
          //   to 0 on absence, and `pct` is what the RING renders — both its arc and its centre
          //   label. So Steps and Distance printed a confident “0%” beside a “—” and a
          //   “last recorded 13 Aug 2026”: the value admitted it had no data while the ring
          //   asserted the user had achieved none of their goal. MEASURED on master-test with
          //   zero fitness rows for 14 Aug: ring badges read “0%” with stroke-dasharray
          //   “0 166.5”. `pct` is now NULL on absence and RingGauge renders the absence.
          //   This is the KAL-125 rule applied to a percentage: a missing figure is missing,
          //   not a plausible-looking zero.
          // KAL-265 — WAS: blank whenever today's exact date had no row, which on this instance
          // is every evening. Now shows the resolved figure and NAMES its day.
          (() => { const r = _ringValue('steps'); const f = (v) => Math.round(v).toLocaleString('en-GB');
            return { key: 'steps', label: 'Steps',
                     val: r.value == null ? '—' : f(r.value),
                     pct: r.value == null ? null : Math.min(100, Math.round(r.value / stepGoal * 100)),
                     color: stepColor, stale: !r.isToday && r.value != null,
                     sub: _ringSub('steps', f, `${f(r.value == null ? 0 : r.value)} / ${stepGoal.toLocaleString('en-GB')}`) }; })(),
          // KAL-081 (spec 5.3) — CALORIES IS AN OBSERVATION, NOT A TARGET. The 2,200 kcal goal is
          // removed: basal metabolism dominates the figure, so on the reference device 1,783 of
          // 2,200 was reached by 20:14 largely through existing. A target cleared by being alive
          // carries no signal, and spec 5.2 states energy expenditure is reported, never targeted.
          // The FIGURE is untouched — only the goal, the /2200 denominator and the ring % are gone.
          (() => { const r = _ringValue('calories'); const f = (v) => Math.round(v).toLocaleString('en-GB');
            return { key: 'calories', label: 'Calories',
                     val: r.value == null ? '—' : f(r.value), observation: true,
                     color: calColor, stale: !r.isToday && r.value != null,
                     sub: _ringSub('calories', f, 'kcal burned today') }; })(),
          (() => { const r = _ringValue('distance_km'); const f = (v) => `${Number(v).toFixed(1)} km`;
            return { key: 'distance', label: 'Distance',
                     val: r.value == null ? '—' : f(r.value),
                     pct: r.value == null ? null : Math.min(100, Math.round(r.value / distGoal * 100)),
                     color: distColor, stale: !r.isToday && r.value != null,
                     sub: _ringSub('distance_km', f, `${Number(r.value == null ? 0 : r.value).toFixed(1)} / ${distGoal} km`) }; })(),
          // S3 — Sleep as a main box (last night); tapping opens the sleep-stage detail.
          { key: 'sleep_duration', label: 'Sleep',      val: lastNightSleepH != null ? _fmtHm(lastNightSleepH) : '—', pct: sleepPct, color: '#818CF8', sub: avgSleepH != null ? `7-day avg ${_fmtHm(avgSleepH)}` : 'last night', onClick: () => (FEATURES_GLOBAL.today_v2 ? showMetric('sleep_duration') : setSleepDetail(true)) },
          // ⚠ KAL-157 — the Sleep card carried its OWN onClick, which bypassed showMetric
          // entirely and opened the legacy sleep-detail overlay. So "Body opens the SAME drawer
          // as Today" held for steps/AZM/RHR but silently NOT for sleep — one metric with two
          // detail views, which is the exact divergence the universal drawer exists to remove.
          // Caught by the verification matrix, not by reading the code.
          // S4 — Resting HR as the 6th box (NOT HRV — HRV stays in the averages row).
          // KAL-081 (spec 5.3) — position on a range, never a progress ring. See RangeMarker.
          { key: 'resting_hr',     label: 'Resting HR', val: latestRHR != null ? `${Math.round(latestRHR)} bpm` : '—', range: { low: 40, high: 100, value: latestRHR }, color: '#ef4444', sub: avgRHR != null ? `7-day avg ${Math.round(avgRHR)} bpm` : 'latest' },
          // (#1377) The 7th "Active min" tile was REMOVED here (9 Aug 2026). It showed
          // /api/fitness/today's active_minutes — fitbit_activity.active_minutes, the device's own
          // fairly-active + very-active sum (no ×2, no HR zones) — which is a DIFFERENT quantity
          // from the Active Zone Minutes tile above it despite reading almost identically. Two
          // tiles labelled "Active …" with different rules, one of them usually 0/30, cost more in
          // confusion than the second number was worth, and it broke the 2-column grid at 7.
          // DISPLAY-ONLY REMOVAL: active_minutes is untouched in the DB and still returned by
          // /api/fitness/today and /api/fitness/metric/active_minutes; the Active Zone Minutes tile,
          // its ×2 tooltip, and the Active Minutes averages row all stay exactly as they were.
        ].filter(r => !_hiddenOnNPL.has(r.key)).map(({ key, label, val, pct, color, sub, note, onClick, observation, range }) => {
          const queued = _queuedTarget(cycleTargets, key);
          return (
          <div key={label} className="fitness-ring-card fitness-clickable" onClick={onClick || (() => showMetric(key))} role="button" tabIndex={0}>
            {range ? (
              <RangeMarker value={range.value} low={range.low} high={range.high} color={color} size={isMobile ? 64 : 72} />
            ) : observation ? (
              // No ring: there is no target to be a proportion of.
              // “observed today” is a statement of KIND (an observation, not a target). When
              // nothing was observed today it is simply false — measured 14 Aug, the Calories
              // tile read “observed today” beside “—  ·  last recorded 13 Aug 2026”.
              <div style={{ width: isMobile ? 64 : 72, textAlign: 'center', fontSize: 10.5,
                            color: 'var(--text-dim)', lineHeight: 1.3 }}>
                {/* KAL-266 — this said "nothing yet today" while the ring beside it showed
                    "2,527 · 2,527 on 23 Aug 2026". Both were literally true and the pair read as
                    a contradiction, which is the same defect one layer out. The badge now
                    describes WHAT IS SHOWN: today, or the day the shown figure belongs to, or a
                    genuine absence. */}
                {(() => { const r = _ringValue(key === 'calories' ? 'calories' : key);
                  if (r.value == null) return <>nothing<br />yet</>;
                  if (r.isToday) return <>observed<br />today</>;
                  return <>observed<br />{formatDate(r.asOf)}</>; })()}</div>
            ) : (TARGET_EDITABLE[key] && targetsAvailable) ? (
              /* KAL-089 (spec 5.4) — THE RING ITSELF IS THE TAP TARGET. The previous build put
                 an "Edit target" button beside the label, which is not what the spec asks for:
                 the pattern is direct manipulation of the thing you are looking at. The ring is
                 now a button opening the same sheet, with a persistent pencil affordance —
                 a bare tappable ring with no hint is undiscoverable for a new user, so the
                 affordance is not optional. stopPropagation keeps the card's own
                 open-the-metric-detail click working. */
              <button type="button" className="fitness-ring-tap"
                aria-label={pct == null
                  ? `Edit your ${label} target (no data recorded today)`
                  : `Edit your ${label} target (currently ${pct}% of target)`}
                onClick={(e) => { e.stopPropagation(); setTargetEdit(key); }}>
                <RingGauge pct={pct} color={color} size={isMobile ? 64 : 72} stroke={11} center={`${pct}%`} />
                <span className="fitness-ring-pencil" aria-hidden="true">{'✎'}</span>
              </button>
            ) : (
              <RingGauge pct={pct} color={color} size={isMobile ? 64 : 72} stroke={11} center={`${pct}%`} />
            )}
            <div className="fitness-ring-body">
              <div className="fitness-ring-label">
                {label}
                {TARGET_EDITABLE[key] && targetsAvailable && <span className="fitness-ring-hint">tap ring to edit</span>}
              </div>
              <div className="fitness-ring-val" style={{ color }}>{val}</div>
              <div className="fitness-ring-sub">{sub}</div>
              {/* KAL-090 — A QUEUED CHANGE MUST BE VISIBLE. Under the asymmetric rule only a
                  LOWER is ever queued, and it lands next cycle. Until now nothing on the ring
                  said so, so a user who lowered a target saw the old number and concluded the
                  save had failed. */}
              {queued && (
                <div className="fitness-ring-queued">
                  {queued.now} now · {queued.next} from {queued.from}
                </div>
              )}
              {note && (
                <div style={{ fontSize: 10.5, lineHeight: 1.35, color: 'var(--text-dim)', marginTop: 3 }}>{note}</div>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* SECTION 2 — Period averages (7d / 30d / 90d toggle) */}
      <div className="fitness-period-head">
        <div className="fitness-section-label" style={{ margin: 0 }}>Averages</div>
        <div className="fitness-period-toggle" role="tablist">
          {Object.keys(PERIODS).map(p => (
            <button key={p}
              className={`fit-pill ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
              role="tab"
              aria-selected={period === p}
            >{p}</button>
          ))}
        </div>
      </div>
      <div className="fitness-stats-grid">
        {STAT_CARDS.map(card => {
          const st = avgStats(card.key);
          const cur = st.mean;
          const prev = avgPrev(card.key);
          return (
            <div key={card.key} className="fit-stat-card fitness-clickable" onClick={() => showMetric(card.key)} role="button" tabIndex={0}>
              <div className="fit-stat-label">{card.label}</div>
              <div className="fit-stat-val" style={{ color: card.color }}>
                {card.key === 'sleep_duration'
                  ? (cur != null ? _fmtHm(cur) : '—')   /* "Xh Ym" — cur is in hours; no unit suffix */
                  : <>{fmtNum(cur, card.decimals)}<span className="fit-stat-unit">{card.unit}</span></>}
              </div>
              <TrendArrow cur={cur} prev={prev} lowerBetter={card.lowerBetter} />
              {/* KAL-266 — STATE THE DENOMINATOR. "90-day average" on NPL Calories is a mean of
                  EIGHTY-FOUR days, and nothing on the card said so. Shown only when the window
                  is not fully covered, so a complete window stays uncluttered. */}
              {cur != null && st.daysWithData < st.daysInWindow && (
                <div className="fit-stat-basis" data-testid={`fit-basis-${card.key}`}>
                  {st.daysWithData} of {st.daysInWindow} days with data
                </div>
              )}
              {/* 2.3 — the SAME sentence Today v2 uses, reused rather than reworded, and N here
                  describes the window actually averaged. */}
              {cur != null && st.daysWithData >= st.daysInWindow && (
                <div className="fit-stat-basis" data-testid={`fit-basis-${card.key}`}>
                  {st.daysWithData} complete days — today joins averages at midnight.
                </div>
              )}
              {/* S2.2 — freshness of the data behind the average, only when genuinely stale. */}
              {_staleLatest(card.key) && (
                <div className="fit-stat-stale" data-testid={`fit-stale-${card.key}`}>
                  latest {formatDate(_staleLatest(card.key))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* SECTION 2b — Recent Activities */}
      <ActivitiesSection onSelect={(logId) => setActivityDetail(logId)} />

      {/* SECTION 3 — Trend charts (period-aware) */}
      <div className="fitness-section-label">Trends — {period}</div>
      <div className="fitness-charts-grid">
        <FitTrendCard anchorId="steps" title="Steps" color="#C9973A" type="bar" data={stepsChart} unit="" yFmt={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} xFmt={xFmt} tickStyle={tickStyle} tooltipStyle={tooltipStyle} onClick={() => showMetric('steps')} />
        <FitTrendCard anchorId="hrv" title="HRV (RMSSD)" color="#fb7185" type="line" data={hrvChart} unit="ms" xFmt={xFmt} tickStyle={tickStyle} tooltipStyle={tooltipStyle} onClick={() => showMetric('hrv')} />
        <FitTrendCard anchorId="resting-hr" title="Resting Heart Rate" color="#ef4444" type="line" data={rhrChart} unit="bpm" xFmt={xFmt} tickStyle={tickStyle} tooltipStyle={tooltipStyle} onClick={() => showMetric('resting_hr')} />
        <FitTrendCard anchorId="sleep" title="Sleep Duration" color="#818CF8" type="bar" data={sleepChart} unit="h" yFmt={v => v.toFixed(1)} xFmt={xFmt} tickStyle={tickStyle} tooltipStyle={tooltipStyle} onClick={() => showMetric('sleep_duration')} />
        {!_hiddenOnNPL.has('spo2') && (
        <FitTrendCard title="SpO₂" color="#34d399" type="line" data={spo2Chart} unit="%" yFmt={v => v.toFixed(1)} xFmt={xFmt} tickStyle={tickStyle} tooltipStyle={tooltipStyle} onClick={() => showMetric('spo2')} />
        )}
      </div>

      {/* (#1377) ONE max-HR rule (azmMaxHrFromAge = the engine's 220 − age, 185 when no DOB).
          The old inline copy here defaulted to "age 55" — inventing a max HR of 165 for any user
          whose age hadn't loaded, which then drove the zone bands and the zone minutes shown. */}
      {activityDetail && <ActivityDetailPanel
        logId={activityDetail}
        maxHR={azmMaxHrFromAge(profile?.age ?? (profile?.dob
          ? (Date.now() - new Date(profile.dob).getTime()) / 3.15576e10 : null))}
        onClose={() => setActivityDetail(null)} />}
      {sleepDetail && <SleepDetailPanel onClose={() => setSleepDetail(false)} />}
      {targetEdit && (
        <TargetEditSheet metric={targetEdit} onClose={() => setTargetEdit(null)}
          onSaved={() => setTargetSavedAt(Date.now())} />
      )}
    </div>
  );
}

// S3 — Sleep-stage detail: last night's deep/light/REM/awake as a segmented bar +
// legend (from /api/fitness/sleep-stages, read-only). Graceful "stages not available"
// state when only total duration was recorded.
function SleepDetailPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get('/fitness/sleep-stages')
      .then(d => { if (alive) { setData(d || { available: false }); setLoading(false); } })
      .catch(() => { if (alive) { setData({ available: false }); setLoading(false); } });
    return () => { alive = false; };
  }, []);
  // INTERNAL-CONSISTENCY RULE: the bar shows ONLY what the headline counts. The headline is time
  // ASLEEP, so the segments are the asleep stages (+ unstaged) and they sum to it exactly; every
  // percentage divides by that same figure. Awake is real but it is NOT sleep, so it is reported on
  // its own line as time in bed rather than as a slice of a "total sleep" bar. Previously the
  // headline was SUM('Sleep Duration') while the bar summed stage rows and the percentages used a
  // third denominator that included Awake — three quantities in one card (NPL 1 Aug: a 3.13h
  // headline over 7.76h of bars; UK/US 3 Jul: 4.18h over 2.50h).
  const STAGES = [
    { key: 'deep', label: 'Deep', color: '#4338CA' },
    { key: 'rem', label: 'REM', color: '#818CF8' },
    { key: 'light', label: 'Light', color: '#A5B4FC' },
    { key: 'unstaged', label: 'Unstaged', color: '#94A3B8' },
  ];
  const stages = (data && data.stages) || {};
  const asleepH = STAGES.reduce((s, x) => s + (stages[x.key] || 0), 0);
  const awakeH = stages.awake || 0;
  return ReactDOM.createPortal(
    <div className="sleep-detail-backdrop" onClick={onClose}>
      <div className="sleep-detail" role="dialog" aria-label="Sleep detail" onClick={e => e.stopPropagation()}>
        <div className="sleep-detail-head">
          <span>Sleep{data && data.date ? ` · ${formatDate(data.date)}` : ''}</span>
          <button className="sleep-detail-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        {loading ? <p className="sleep-detail-note">Loading…</p> : (<>
          <div className="sleep-detail-total" data-total-min={data ? data.total_min : ''}>
            {asleepH > 0 ? _fmtHm(asleepH) : '—'}
            <span> total sleep</span></div>
          {data && data.session_count > 1 && (
            <div className="sleep-detail-sessions">
              across {data.session_count} sessions
              {Array.isArray(data.sessions)
                ? ` · ${data.sessions.map(s => `${s.start_local}–${s.end_local}`).join(', ')}`
                : ''}
            </div>
          )}
          {data && data.available && asleepH > 0 ? (<>
            <div className="sleep-stage-bar">
              {STAGES.map(x => (stages[x.key]
                ? <div key={x.key} className="sleep-stage-seg"
                    style={{ width: (stages[x.key] / asleepH * 100) + '%', background: x.color }}
                    title={`${x.label} ${_fmtHm(stages[x.key])}`} />
                : null))}
            </div>
            <div className="sleep-stage-legend">
              {STAGES.map(x => stages[x.key] ? (
                <div key={x.key} className="sleep-stage-leg">
                  <span className="sleep-stage-dot" style={{ background: x.color }} />
                  {x.label}: {_fmtHm(stages[x.key])} ({Math.round(stages[x.key] / asleepH * 100)}%)
                </div>
              ) : null)}
            </div>
            {awakeH > 0 && (
              <div className="sleep-detail-awake">
                Plus {_fmtHm(awakeH)} awake — {_fmtHm(asleepH + awakeH)} in bed.
              </div>
            )}
          </>) : (
            <p className="sleep-detail-note">Sleep-stage breakdown isn’t available for this night — only total duration was recorded.</p>
          )}
        </>)}
        <div className="sleep-detail-disc">Wellness guidance, not medical advice.</div>
      </div>
    </div>,
    document.body
  );
}

// ── Metric Detail Panel (slide-up overlay) ────────────────────────────────
// Opens when a user taps a Fitness ring/stat/trend card. Fetches the metric's
// full history for the selected range and renders a chart + Min/Max/Avg stats.
const METRIC_CONFIG = {
  // Colours chosen to read on the dark navy drawer background (Part 6):
  // navy fills (#112240) were invisible, so steps/calories/distance use the
  // accent gold and weight uses a light slate.
  steps:          { label: 'Steps',              unit: '',     yLabel: 'Steps',    chart: 'bar',  goal: 10000, goalLabel: 'Goal 10,000', goalWhite: true, color: '#C9973A', decimals: 0 },
  // (#1377) impact carries the zone definition so EVERY entry point states it — the Fitness
  // "Active min" tile and the "Active Minutes" averages card both open this via
  // showMetric()/metricSpecFromConfig, which previously showed the figure with no definition at
  // all (only azmDrawerSpec's override had one).
  // Title says "Active Zone Minutes" because that is what the SERIES is: /api/fitness/metric/
  // active_minutes serves active_minutes.day_points(), the zone/step engine — not the device's own
  // "active minutes" total that the Fitness tile and Today strip show. Titling it "Active Minutes"
  // while charting AZM under a zone definition was the last place the two quantities blurred.
  active_minutes: { label: 'Active Zone Minutes', unit: 'min',  yLabel: 'Minutes',  chart: 'bar',  goal: 30,    color: '#EF4444', decimals: 0, impact: AZM_ZONE_DEF_LONG },
  calories:       { label: 'Calories',           unit: 'kcal', yLabel: 'kcal',     chart: 'bar',  goal: 2200,  color: '#C9973A', decimals: 0 },
  distance:       { label: 'Distance',           unit: 'km',   yLabel: 'km',       chart: 'bar',  goal: 5,     color: '#C9973A', decimals: 1 },
  resting_hr:     { label: 'Resting Heart Rate', unit: 'bpm',  yLabel: 'bpm',      chart: 'line', color: '#EF4444', decimals: 0, impact: 'A lower resting heart rate is associated with better cardiovascular fitness and is linked with longer healthspan.' },
  hrv:            { label: 'HRV (RMSSD)',        unit: 'ms',   yLabel: 'ms',       chart: 'line', color: '#fb7185', decimals: 0, impact: 'Higher heart-rate variability is associated with better recovery and autonomic balance, and is linked with longevity.' },
  spo2:           { label: 'SpO₂',               unit: '%',    yLabel: '%',        chart: 'line', color: '#34d399', decimals: 1 },
  sleep_duration: { label: 'Sleep Duration',     unit: 'h',    yLabel: 'Hours',    chart: 'line', color: '#818CF8', decimals: 1, transform: v => v / 60, hm: true },
  // LOCALE (2d): METRIC_CONFIG is a MODULE-level const, so a plain `unit: weightUnit()` would be
  // evaluated at import — before /api/features has resolved — and would freeze 'kg' for the whole
  // session. Getters defer the call to metricSpecFromConfig()'s read, which happens at render.
  weight:         { label: 'Weight', get unit() { return weightUnit(); }, get yLabel() { return weightUnit(); }, chart: 'line', color: '#94a3b8', decimals: 1 },
};

// Bucket a daily [{date:'YYYY-MM-DD', value}] series into Monday-anchored weeks, newest first
// (index 0 = the most recent Monday week). Weekday is computed from the date STRING via UTC so
// it is timezone-agnostic (the dates are already local calendar days from the backend). Used by
// the AZM drawer's week navigation — slice client-side, no per-click fetch.
function _isoAddDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function _isoMonday(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  const wd = (d.getUTCDay() + 6) % 7;   // Mon=0 … Sun=6
  d.setUTCDate(d.getUTCDate() - wd);
  return d.toISOString().slice(0, 10);
}
function bucketAzmWeeks(arr) {
  const m = new Map();
  (Array.isArray(arr) ? arr : []).forEach(row => {
    if (!row || !row.date) return;
    const wk = _isoMonday(String(row.date).slice(0, 10));
    if (!m.has(wk)) m.set(wk, []);
    m.get(wk).push({ date: row.date, value: Number(row.value) || 0 });
  });
  const weeks = [...m.entries()].map(([start, days]) => {
    days.sort((a, b) => (a.date < b.date ? -1 : 1));
    return { start, end: _isoAddDays(start, 6), days, total: days.reduce((a, d) => a + d.value, 0) };
  });
  weeks.sort((a, b) => (a.start < b.start ? 1 : -1));   // newest first
  return weeks;
}

const METRIC_RANGES = [
  { key: '7d',   label: '1W', days: 7 },
  { key: '30d',  label: '1M', days: 30 },
  { key: '90d',  label: '3M', days: 90 },
  { key: '180d', label: '6M', days: 180 },
  { key: '1y',   label: '1Y', days: 365 },
  { key: 'all',  label: 'All', days: 100000 },
];

// ═══════════════════════════════════════════════════════════════════════
// DRAWER SYSTEM (Phase 2) — every Today card tap opens a detail drawer that
// overlays Today. The user never leaves Today via a card tap; only deliberate
// sidebar/bottom-bar nav causes full page navigation. The drawer is App-level
// React state (not a route). It pushes ONE history entry (#today-drawer) via
// the shared useModalHashRoute hook so browser/Android back closes it and
// returns to Today, never to an internal page.
// ═══════════════════════════════════════════════════════════════════════

// Dark-theme chart chrome shared by all drawers (Part 2 + Part 6 audit).
const DRAWER_TICK = { fontSize: 11, fill: 'var(--text-muted, #8a9bb0)' };
const DRAWER_TOOLTIP = {
  fontSize: '12px', background: 'var(--bg-card, #112240)', color: 'var(--text-primary, #e2e8f0)',
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
};
const _drawerXFmt = (range) => (d) => formatDate(d);

// Slide-in drawer chrome. Right-side panel on desktop, bottom sheet on mobile
// (CSS handles the direction). Close triggers: × button, backdrop tap, Escape,
// browser back — all routed through the canonical close handler so the
// #today-drawer history entry stays in sync.
function KalpaDrawer({ title, onClose, children }) {
  const close = useModalHashRoute('#today-drawer', onClose);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);
  return (
    <div className="kdrawer-backdrop" onClick={close}>
      <div className="kdrawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="kdrawer-head">
          <div className="kdrawer-title">{title}</div>
          <button className="kdrawer-close" onClick={close} aria-label="Close">×</button>
        </div>
        <div className="kdrawer-divider" />
        <div className="kdrawer-body">{children}</div>
      </div>
    </div>
  );
}

// Build a metric-drawer spec from the central METRIC_CONFIG, with optional
// overrides (hero/subtitle/baseline supplied by the Today payload).
// Format a value in HOURS as "Xh Ym" (round to nearest minute). Total minutes
// are rounded BEFORE the split so e.g. 6.999h → "7h 0m", never "6h 60m".
// Display-only: the underlying value/API/stored data stay unchanged.
function _fmtHm(hours) {          // 2b — delegates to the ONE canonical formatter
  return fmtDurationHours(hours);
}

function metricSpecFromConfig(key, overrides = {}) {
  const cfg = METRIC_CONFIG[key] || {};
  return {
    kind: 'metric', metricKey: key,
    title: cfg.label || key,
    color: cfg.color || 'var(--accent-gold)',
    chartType: cfg.chart || 'line',
    transform: cfg.transform,
    decimals: cfg.decimals != null ? cfg.decimals : 0,
    unit: cfg.unit || '',
    hm: cfg.hm,                  // display value as "Xh Ym" (Sleep Duration)
    yLabel: cfg.yLabel,
    goal: cfg.goal,
    goalLabel: cfg.goalLabel,
    goalWhite: cfg.goalWhite,
    impact: cfg.impact,
    ...overrides,
  };
}

// Shared AZM drawer spec — the SINGLE source of the AZM drawer's title/goal/impact, used by BOTH
// the Today hero card and the Fitness /#vitals card so the two entry points open an identical
// drawer and can never drift. (The numbers all come from /api/fitness/metric/active_minutes, which
// serves active_minutes.day_points() per day — MAX(HR-zone points, NHS step estimate), Monday-local.)
function azmDrawerSpec() {
  return metricSpecFromConfig('active_minutes', {
    title: 'Active Zone Minutes',
    initialRange: '7d',   // AZM is inherently weekly — open on 1W where the week-nav lives
    goal: 21,   // 150/week ÷ 7 ≈ the daily pace toward the weekly target
    goalLabel: 'Goal ≈21/day (150/wk)',
    // (#1377) definition now inherited from METRIC_CONFIG.active_minutes.impact
    // (= AZM_ZONE_DEF_LONG). The local copy that lived here said brisk-walking steps "are ADDED as
    // moderate minutes" — that stopped being true at commit 2e81ab5, which made the day total
    // MAX(zone, steps) rather than their sum. Two copies of a definition is how it went stale;
    // there is now one.
  });
}

// The standard metric drawer interior: hero number, today-vs-baseline
// subtitle, time-range tabs, trend chart with dashed baseline, MIN/AVG/MAX.
function MetricDrawerBody({ spec, onClose, onAskAI }) {
  const {
    metricKey, title, color = 'var(--accent-gold)', chartType = 'line',
    transform, decimals = 0, unit = '', hm, goal, goalLabel, goalWhite, yLabel, impact,
    hero, heroSub, heroState, baseline, extra,
  } = spec;
  const [range, setRange] = useState(spec.initialRange || '30d');
  // AZM week navigation: browse back through prior Monday-local weeks. 0 = current week.
  // Only active for the Active-Zone-Minutes drawer at the 1W range. Implemented by fetching a
  // 90-day daily series ONCE and slicing it into Monday-weeks client-side — instant week
  // switching, one request, and no per-click re-fetch race.
  const [weekOffset, setWeekOffset] = useState(0);
  // AZM week navigation: browse back through prior Monday-local weeks. 0 = current week.
  // The full daily history is loaded ONCE by the range=all effect below (into allData) and
  // sliced into Monday-weeks in render — instant week switching, no per-click fetch, and it
  // rides the single reliable range=all effect (deps [metricKey]) rather than the churny
  // range-driven effect.
  const azmWeekNav = metricKey === 'active_minutes' && range === '7d';
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState(null);         // full daily history (range=all), for AZM weeks
  const [loading, setLoading] = useState(true);
  // W4: distinguish a FAILED fetch from a legitimately-empty series (both used to
  // coalesce to [] → "—", hiding real errors — a loud-diagnostics violation).
  const [err, setErr] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // C — total available history span (days), fetched once, so ranges that exceed it
  // can be greyed/disabled with a tooltip instead of showing an identical window.
  const [fullSpanDays, setFullSpanDays] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/fitness/metric/${metricKey}?range=all`).then(d => {
      if (!alive) return;
      const t = transform || (v => v);
      const arr = Array.isArray(d?.data) ? d.data : [];
      setAllData(arr.map(r => ({ date: r.date, value: t(r.value) })));
      if (arr.length >= 1) {
        const first = new Date(String(arr[0].date).slice(0, 10)), last = new Date(String(arr[arr.length - 1].date).slice(0, 10));
        setFullSpanDays(Math.max(1, Math.round((last - first) / 86400000) + 1));
      } else setFullSpanDays(0);
    }).catch(() => { if (alive) { setFullSpanDays(null); setAllData([]); } });
    return () => { alive = false; };
  }, [metricKey, transform]);

  // Last-request-wins via a monotonic id (robust to double-invoke / out-of-order responses).
  // Skipped for AZM week-nav, which slices allData (from the range=all effect) in render.
  const reqIdRef = useRef(0);
  useEffect(() => {
    if (azmWeekNav) return;
    const myId = ++reqIdRef.current;
    setLoading(true); setErr(null);
    const ep = `/fitness/metric/${metricKey}?range=${range}`;
    api.get(ep)
      .then(d => {
        if (reqIdRef.current !== myId) return;   // superseded by a newer request
        const arr = Array.isArray(d?.data) ? d.data : [];
        const t = transform || (v => v);
        setData(arr.map(row => ({ date: row.date, value: t(row.value) })));
        setErr(null);
      })
      .catch(e => {
        if (reqIdRef.current !== myId) return;
        // W4: surface the cause loudly instead of a silent [] → "—".
        const msg = (e && e.message) || 'network error';
        console.error(`[fitness] metric fetch failed: GET ${ep} -> ${msg}`);
        try { reportClientError(`fitness metric fetch failed: GET ${ep} -> ${msg}`, e && e.stack, 'FitnessMetricDrawer'); } catch (_) {}
        setData([]); setErr({ ep, msg });
      })
      .finally(() => { if (reqIdRef.current === myId) setLoading(false); });
  }, [metricKey, range, transform, reloadKey, azmWeekNav]);

  // AZM week-nav: bucket the full daily history (allData, from the reliable range=all effect) into
  // Monday-weeks (newest first) IN RENDER — pure, no effect, no race. weekOffset indexes the array
  // (0 = most recent week). azmLoading = the range=all fetch hasn't landed yet.
  const azmWeeks = (azmWeekNav && Array.isArray(allData)) ? bucketAzmWeeks(allData) : null;
  const azmWeek = azmWeeks && azmWeeks.length ? (azmWeeks[weekOffset] || null) : null;
  const azmMaxOffset = azmWeeks && azmWeeks.length ? azmWeeks.length - 1 : 0;
  const azmLoading = azmWeekNav && allData === null;
  // KAL-265 — THE SERVER'S `range=7d` IS EIGHT DAYS. `since = date.today() - timedelta(days=7)`
  // with `date >= since` is inclusive at both ends (app.py:20339), so every MetricDrawerBody
  // drawer was one day wider than the card beside it. Measured on NPL 23 Aug: calories drawer
  // 2,631.9 over 16-23 Aug against a card showing 2,671.7 over 17-23 Aug; distance 6.8 vs 7.1;
  // hrv 27.9 vs 28.0. Re-window client-side by DATE so the drawer and the card select the same
  // dates. The AZM week-nav branch is left alone: it is an explicit WEEK navigator, not the
  // rolling headline, and it carries its own labelled week bounds.
  const _mdbToday = spec.asOfDate || null;
  const _mdbDays = (METRIC_RANGES.find(r => r.key === range) || {}).days || null;
  const _rangeWindowed = (!azmWeekNav && _mdbDays && Array.isArray(data) && data.length)
    ? _windowByDate(data, _mdbDays, _mdbToday) : data;
  const effData = azmWeekNav ? (azmWeek ? azmWeek.days : []) : _rangeWindowed;
  const showLoading = azmWeekNav ? azmLoading : loading;

  // KAL-266 — `filter(v > 0)` deleted genuine zeros. A rest day is data. Exclude null, include 0.
  const _mdbStats = _meanOver(effData || []);
  const values = (effData || []).map(d => d && d.value).filter(v => v != null && Number.isFinite(v));
  const minV = values.length ? Math.min(...values) : null;
  const maxV = values.length ? Math.max(...values) : null;
  const avgV = _mdbStats.mean;
  const fmt = v => (v == null || !Number.isFinite(v)) ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
  // hm metrics (Sleep Duration): display HOURS as "Xh Ym", no unit suffix. The
  // chart axis/tooltip keep numeric `fmt`; only header/avg/min/max use `fmtV`.
  const fmtV = hm ? _fmtHm : fmt;
  const unitSuffix = hm ? '' : (unit ? ' ' + unit : '');

  const heroText = hero != null
    ? hero
    : (azmWeekNav
        ? `${azmWeek ? azmWeek.total : 0}${unitSuffix}`   // AZM: hero = the selected week's total
        : (data.length ? `${fmtV(data[data.length - 1].value)}${unitSuffix}` : '—'));
  const subText = heroSub != null
    ? heroSub
    : (avgV != null ? `${fmtV(avgV)}${unitSuffix} avg · ${METRIC_RANGES.find(r => r.key === range)?.label || range}`
         + (_mdbDays && _mdbStats.n && _mdbStats.n < _mdbDays ? ` · ${_mdbStats.n} of ${_mdbDays} days with data` : '') : '');
  const refLine = baseline != null ? baseline : avgV;
  const refLabel = baseline != null ? 'baseline' : 'avg';
  // X-axis: format from the ACTUAL data span, not the range, so ~1 month of history
  // shows distinct DD/MM labels instead of the same "YYYY-MM" repeated (the old
  // d.slice(0,7) collapsed every same-month point to one label). Always en-GB / non-US.
  const _rangeDays = { '7d': 7, '30d': 30, '90d': 90, '180d': 180, '1y': 365, 'all': 3650 }[range] || 30;
  const _spanDays = effData.length >= 2
    ? Math.round((new Date(String(effData[effData.length - 1].date).slice(0, 10)) - new Date(String(effData[0].date).slice(0, 10))) / 86400000)
    : (effData.length ? 1 : 0);
  const xFmt = (d) => formatDate(d);
  const _limitedHistory = data.length > 0 && _spanDays < (_rangeDays - 2) && range !== '7d';
  const yAxisLabel = yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: 'var(--text-muted,#8a9bb0)', fontSize: 11, style: { textAnchor: 'middle' } } : undefined;
  const goalStroke = goalWhite ? 'rgba(255,255,255,0.7)' : 'var(--accent-gold)';
  // "X steps to goal" — only when this is the steps metric and we have today's value.
  const latestVal = effData.length ? effData[effData.length - 1].value : null;
  const stepsToGoal = (metricKey === 'steps' && goal && latestVal != null && latestVal < goal)
    ? Math.round(goal - latestVal) : null;

  return (
    <KalpaDrawer title={title} onClose={onClose}>
      <div className="kdrawer-hero">{heroText}</div>
      {subText && <div className={`kdrawer-sub${heroState ? ' state-' + heroState : ''}`}>{subText}</div>}
      {stepsToGoal != null && <div className="kdrawer-goal-note">{stepsToGoal.toLocaleString('en-GB')} steps to goal</div>}
      {extra}
      <div className="kdrawer-ranges">
        {METRIC_RANGES.map(r => {
          // Disable ranges longer than the history we actually have (keep 'all' + the
          // smallest window always enabled). Tooltip explains why.
          const beyond = fullSpanDays != null && r.key !== 'all' && r.key !== '7d' && r.days > fullSpanDays + 2;
          return (
            <button key={r.key}
              className={`kdrawer-range-btn${range === r.key ? ' active' : ''}`}
              onClick={() => { if (!beyond) { setRange(r.key); setWeekOffset(0); } }}
              disabled={beyond}
              title={beyond ? `Only ~${fullSpanDays} day${fullSpanDays === 1 ? '' : 's'} of history available yet` : undefined}
              style={beyond ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >{r.label}</button>
          );
        })}
      </div>
      {azmWeekNav && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, margin: '6px 2px 10px' }}>
          <button type="button" onClick={() => setWeekOffset(o => Math.min(azmMaxOffset, o + 1))}
            aria-label="Previous week" disabled={weekOffset >= azmMaxOffset}
            style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid var(--bg-card-border, rgba(255,255,255,0.18))',
                     background: 'transparent', color: 'inherit', cursor: weekOffset >= azmMaxOffset ? 'not-allowed' : 'pointer',
                     opacity: weekOffset >= azmMaxOffset ? 0.4 : 1, fontSize: 15, lineHeight: 1 }}>◀</button>
          <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--text-primary,#e2e8f0)' }}>
            {/* (#1376) The week RANGE is shown for every week including the current one — "This
                week" alone left the user unable to tell which Monday–Sunday window the figure
                covers, which is the same unfalsifiability problem as an undefined zone. */}
            <div style={{ fontWeight: 600 }}>
              {weekOffset === 0 ? 'This week'
                : azmWeek ? `${formatDate(azmWeek.start)} – ${formatDate(azmWeek.end)}`
                : `${weekOffset} week${weekOffset === 1 ? '' : 's'} ago`}
            </div>
            {azmWeek && weekOffset === 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {formatDate(azmWeek.start)} – {formatDate(azmWeek.end)}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              {azmWeek ? azmWeek.total : 0} / 150 min · target
            </div>
          </div>
          <button type="button" onClick={() => setWeekOffset(o => Math.max(0, o - 1))}
            aria-label="Next week" disabled={weekOffset === 0}
            style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid var(--bg-card-border, rgba(255,255,255,0.18))',
                     background: 'transparent', color: 'inherit', cursor: weekOffset === 0 ? 'not-allowed' : 'pointer',
                     opacity: weekOffset === 0 ? 0.4 : 1, fontSize: 15, lineHeight: 1 }}>▶</button>
        </div>
      )}
      {_limitedHistory && (
        <div style={{ fontSize: 11.5, color: 'var(--text-dim)', margin: '2px 2px 8px' }}>
          Showing all {_spanDays + 1} days available — limited history for this range.
        </div>
      )}
      <div className="kdrawer-chart">
        {showLoading ? (
          <div className="kdrawer-empty">Loading…</div>
        ) : err ? (
          <div className="kdrawer-empty" role="alert" style={{ color: 'var(--danger, #ef4444)' }}>
            Couldn’t load this metric ({err.msg}).
            <button type="button" onClick={() => setReloadKey(k => k + 1)}
              style={{ marginLeft: 8, padding: '4px 10px', borderRadius: 8, border: '1px solid var(--bg-card-border, rgba(255,255,255,0.18))', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        ) : effData.length === 0 ? (
          <div className="kdrawer-empty">No data for this period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            {chartType === 'bar' ? (
              <BarChart data={effData} margin={{ top: 8, right: 14, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false} tickFormatter={xFmt} interval="preserveStartEnd" />
                <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={48} label={yAxisLabel} />
                <Tooltip contentStyle={DRAWER_TOOLTIP} formatter={v => fmt(v)} />
                {goal && <ReferenceLine y={goal} stroke={goalStroke} strokeDasharray="4 3" strokeWidth={1.2} label={{ value: goalLabel || `Goal ${goal.toLocaleString('en-GB')}`, position: 'insideTopRight', fill: goalStroke, fontSize: 10 }} />}
                <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
              </BarChart>
            ) : (
              <LineChart data={effData} margin={{ top: 8, right: 14, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false} tickFormatter={xFmt} interval="preserveStartEnd" />
                <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={48} domain={['auto', 'auto']} label={yAxisLabel} />
                <Tooltip contentStyle={DRAWER_TOOLTIP} formatter={v => fmt(v)} />
                {refLine != null && <ReferenceLine y={refLine} stroke="var(--accent-gold)" strokeDasharray="4 3" strokeWidth={1.2} label={{ value: `${refLabel} ${fmt(refLine)}`, position: 'insideTopRight', fill: 'var(--accent-gold)', fontSize: 10 }} />}
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.4} dot={effData.length <= 30 ? { r: 2.5, fill: color } : false} activeDot={{ r: 5 }} />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
      {impact && (
        <div className="kdrawer-impact">
          <span className="kdrawer-impact-icon" aria-hidden="true">★</span>
          <span>{impact}</span>
        </div>
      )}
      <div className="kdrawer-stats">
        <div><div className="kdrawer-stat-label">Min</div><div className="kdrawer-stat-val">{fmtV(minV)}{!hm && <span className="kdrawer-stat-unit">{unit}</span>}</div></div>
        <div><div className="kdrawer-stat-label">Avg</div><div className="kdrawer-stat-val">{fmtV(avgV)}{!hm && <span className="kdrawer-stat-unit">{unit}</span>}</div></div>
        <div><div className="kdrawer-stat-label">Max</div><div className="kdrawer-stat-val">{fmtV(maxV)}{!hm && <span className="kdrawer-stat-unit">{unit}</span>}</div></div>
      </div>
      {onAskAI && (
        <button className="kdrawer-ask-btn" onClick={() => onAskAI(
          `Tell me about my ${title}. Latest ${heroText}${subText ? ` (${subText})` : ''}; `
          + `min ${fmtV(minV)}${unitSuffix}, average ${fmtV(avgV)}${unitSuffix}, max ${fmtV(maxV)}${unitSuffix} over the shown period. `
          + `What does this mean for my health and longevity, grounded in my data?`)}>
          ✦ Ask Kalpa about this
        </button>
      )}
    </KalpaDrawer>
  );
}

// Longevity drawer trend chart: dual line (bio gold solid + chrono dashed)
// with a fill under the bio line — green when below chronological (younger),
// red when above. 7D|30D|90D range tabs fetch dated composite snapshots.
const LONGEVITY_RANGES = [
  { key: '7d',  label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
];

function LongevityTrendChart({ seed, onMarkerTap }) {
  const [range, setRange] = useState('7d');
  const [series, setSeries] = useState(seed || []);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let alive = true;
    if (range === '7d' && seed && seed.length >= 2) { setSeries(seed); return; }
    setLoading(true);
    api.get(`/bio-age-trend?range=${range}`)
      .then(d => { if (alive) setSeries(Array.isArray(d?.series) ? d.series : []); })
      .catch(() => { if (alive) setSeries([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range, seed]);
  // Blood-test events fetched once at the widest window; the dot helper plots
  // only those dates present in the current series, so they track the range.
  useEffect(() => {
    let alive = true;
    api.get('/blood-panel-events?range=90d')
      .then(d => { if (alive) setEvents(Array.isArray(d?.events) ? d.events : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const evByDate = _bloodEventsByDate(events);
  const last = series.length ? series[series.length - 1] : null;
  const younger = last ? (last.chronological_age - last.biological_age) >= 0 : true;
  const fillColor = younger ? '#34d399' : '#f87171';
  const ages = series.flatMap(d => [d.biological_age, d.chronological_age]).filter(v => v != null);
  const min = ages.length ? Math.floor(Math.min(...ages) - 0.5) : 0;
  const max = ages.length ? Math.ceil(Math.max(...ages) + 0.5) : 1;

  // X-axis adapts to the range: weekday names for 7D (no repeats), dated
  // "12 May" ticks thinned to ~6 labels for 30D/90D so they never cram.
  const rangeDays = { '7d': 7, '30d': 30, '90d': 90 }[range] || 30;
  const tickFmt = range === '7d' ? _weekdayShort : _dayMonth;
  const tickInterval = range === '7d' ? 0 : Math.max(0, Math.ceil(series.length / 6) - 1);
  // History note: snapshots accrue daily, so a freshly-seeded account may have
  // fewer days than the selected window. Show what exists + flag it building.
  const buildingHistory = series.length > 0 && series.length < rangeDays - 2;

  return (
    <div className="kdrawer-chart" style={{ marginTop: 16 }}>
      <div className="kdrawer-section-label">Biological vs chronological age</div>
      <div className="kdrawer-ranges">
        {LONGEVITY_RANGES.map(r => (
          <button key={r.key}
            className={`kdrawer-range-btn${range === r.key ? ' active' : ''}`}
            onClick={() => setRange(r.key)}
          >{r.label}</button>
        ))}
      </div>
      {loading ? (
        <div className="kdrawer-empty">Loading…</div>
      ) : series.length < 1 ? (
        <div className="kdrawer-empty">No biological-age snapshots recorded for this period yet.</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={series} margin={{ top: 8, right: 14, left: 0, bottom: 4 }}>
            <defs>
              <linearGradient id="bioFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={fillColor} stopOpacity={0.28} />
                <stop offset="100%" stopColor={fillColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false}
              tickFormatter={tickFmt} interval={tickInterval} minTickGap={16} />
            <YAxis domain={[min, max]} tick={DRAWER_TICK} axisLine={false} tickLine={false} width={44}
              label={{ value: 'Age (years)', angle: -90, position: 'insideLeft', fill: 'var(--text-muted,#8a9bb0)', fontSize: 11, style: { textAnchor: 'middle' } }} />
            <Tooltip content={<_BioAgeTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
            <Area type="monotone" dataKey="biological_age" stroke="none" fill="url(#bioFill)" isAnimationActive={false} />
            <Line type="monotone" dataKey="chronological_age" stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} name="Chronological" />
            <Line type="monotone" dataKey="biological_age" stroke="#C9973A" strokeWidth={2}
              dot={(p) => {
                const row = series[p.index];
                const ev = row ? evByDate[row.date] : null;
                return (
                  <g key={p.key}>
                    <circle cx={p.cx} cy={p.cy} r={2.5} fill="#C9973A" />
                    {ev ? <_BloodMarker ev={ev} cx={p.cx} cy={p.cy} onTap={onMarkerTap} /> : null}
                  </g>
                );
              }}
              activeDot={{ r: 5 }} isAnimationActive={false} name="Biological" />
            {last && <ReferenceLine x={last.date} stroke="rgba(255,255,255,0.25)" strokeDasharray="2 2"
              label={{ value: 'Today', position: 'top', fill: 'var(--text-muted,#8a9bb0)', fontSize: 10 }} />}
          </ComposedChart>
        </ResponsiveContainer>
      )}
      {!loading && buildingHistory && (
        <div className="kdrawer-trend-note">History building — more data over time.</div>
      )}
    </div>
  );
}

// Genetics must not surface in the personal Longevity composite (regulatory —
// associated variants live on My Genome only). Backend already drops them; this
// is defence-in-depth against any genetics-tagged driver slipping through.
function _isGeneticsDriver(d) {
  const s = `${d.model_tag || ''} ${d.name || ''}`.toLowerCase();
  return /genetic|genome|dna|variant|\bsnp\b|apoe|polygenic|gwas/.test(s);
}

// Turn a driver into a self-contained, specific action sentence (Part 7) — no
// bare fragments, and each names the actual metric + current value.
function _longevityAction(d) {
  const n = (d.name || '').toLowerCase();
  const v = (d.value != null && d.value !== '') ? `${d.value}${d.unit ? ` ${d.unit}` : ''}` : null;
  const cur = v ? ` (currently ${v})` : '';
  if (n.includes('sleep')) return `Aim for 7–8h of sleep tonight${v ? ` (you average ${v})` : ''}.`;
  if (n.includes('bmi') || n.includes('body mass') || n.includes('weight')) return `Move your BMI toward 24${cur} with steady activity and a balanced diet.`;
  if (n.includes('resting') || n === 'rhr' || n.includes('resting heart')) return `Build an aerobic base to bring your resting heart rate toward 55 bpm${cur}.`;
  if (n.includes('hrv')) return `Protect recovery with consistent sleep and lower evening stress to raise HRV${cur}.`;
  if (n.includes('step')) return `Add a daily walk to reach 8,000+ steps${v ? ` (you average ${v})` : ''}.`;
  if (n.includes('vo2') || n.includes('cardio') || n.includes('fitness')) return `Add two zone-2 cardio sessions a week to lift your fitness${cur}.`;
  if (n.includes('ldl') || n.includes('apob') || n.includes('cholesterol') || n.includes('triglycer') || n.includes('lp(a)') || n.includes('lipo')) return `Consider discussing your ${d.name} with your GP, alongside diet and activity${cur}.`;
  if (n.includes('glucose') || n.includes('hba1c') || n.includes('a1c')) return T(`Favour whole foods and post-meal walks to steady your blood sugar${cur}.`);
  if (n.includes('crp') || n.includes('inflamm')) return T(`Prioritise sleep, regular activity and an anti-inflammatory diet${cur}.`);
  if (d.suggestion && /[.!?]$/.test(d.suggestion.trim()) && d.suggestion.trim().length > 24) return d.suggestion.trim();
  return `Focus on improving your ${d.name}${cur}.`;
}

// KLS ring → Longevity drawer (Part 3). Hero bio age + delta chip + narrative,
// dual-line bio-age trend with range tabs, 5 improvement drivers with model
// tags, "What I can do today" action cards, and a small link to the full page.
function LongevityDrawerBody({ spec, onClose, onNav, onAskAI, onOpenFull, onOpenDrawer }) {
  const kls = spec.kls || {};
  const seedTrend = spec.bioTrend || [];
  const narrative = spec.bioNarrative;
  const [score, setScore] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/longevity-score').then(d => { if (alive) setScore(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const bio = kls.biological_age;
  const delta = kls.delta_years;
  const bioProv = kls.data_provenance || (score && score.data_provenance) || null;
  const bioInsufficient = !!(kls.insufficient_data || (bioProv && bioProv.sufficient === false));
  const bioLimited = !!(bioProv && bioProv.sufficient && bioProv.label === 'limited');
  // THE completeness contract — same single-draw object the Today hero renders, so the drawer
  // you open from that hero cannot contradict it (see _phenoage_completeness in app.py).
  const kComp = kls.completeness || (score && score.completeness) || null;

  // Prefer drivers passed from Today (already reshaped); fall back to the
  // /longevity-score payload, normalising to the {name, model_tag, ...} shape.
  let drivers = (spec.drivers && spec.drivers.length) ? spec.drivers : null;
  if (!drivers) {
    drivers = (score?.drivers_older || []).map(d => ({
      name: d.metric || d.label || d.name || 'Marker',
      model_tag: d.model || '',
      impact_years: d.impact,
      value: d.value || '',
      unit: '',
      suggestion: d.detail || d.ref || '',
    }));
  }
  drivers = drivers.filter(d => !_isGeneticsDriver(d)).slice(0, 5);

  // "What I can do today" — up to 3 tappable actions from the strongest
  // drivers, each rewritten as a self-contained sentence (Part 7).
  const actions = drivers.slice(0, 3).map(d => ({ d, text: _longevityAction(d) }));

  return (
    <KalpaDrawer title="Longevity" onClose={onClose}>
      {bioInsufficient ? (
        <>
          <div className="kdrawer-hero" style={{ fontSize: 22, lineHeight: 1.3 }}>
            {(bioProv && bioProv.label === 'under_18') ? 'Not shown under 18' : 'Add lab results'}
          </div>
          <div className="kdrawer-sub">
            {(bioProv && bioProv.label === 'under_18')
              ? 'Biological age uses an adult model (PhenoAge). Your markers, activity and sleep are shown as normal.'
              : kComp && kComp.draw_date
              ? `${kComp.label}${(bioProv && bioProv.has_dob) ? '' : ' + date of birth'} — a biological age needs all ${kComp.total} from one draw.`
              : 'Upload a recent blood panel to calculate your biological age.'}
          </div>
        </>
      ) : (
        <>
          <div className="kdrawer-hero">{bio != null ? fmtYearsMonths(bio) : '—'}</div>
          <div className="kdrawer-sub">biological age{(bioLimited && kComp) ? ` · limited data (${kComp.present}/${kComp.total} from ${kComp.draw_date_display})` : ''}</div>
        </>
      )}
      {delta != null && (
        <div className="today-delta-chip" data-state={kls.state || 'good'} style={{ marginTop: 12 }}>
          {delta <= 0 ? '↓' : '↑'} {fmtYearsMonths(delta)} {delta <= 0 ? 'younger' : 'older'} than your age
        </div>
      )}
      {narrative && <p className="kdrawer-narrative">{narrative}</p>}

      <LongevityTrendChart
        seed={seedTrend}
        onMarkerTap={(ev) => { if (onOpenDrawer) onOpenDrawer({ kind: 'bloodpanel', event: ev, returnSpec: spec }); }}
      />

      {drivers.length > 0 && (
        <div className="kdrawer-drivers">
          <div className="kdrawer-section-label">Top improvement drivers</div>
          {drivers.map((d, i) => (
            <div key={i} className="kdrawer-driver-card">
              <div className="kdrawer-driver-head">
                <span className="kdrawer-driver-name">{d.name}</span>
                {d.model_tag && <span className="kdrawer-driver-tag">{d.model_tag}</span>}
                {d.impact_years != null && (
                  <span className="kdrawer-driver-impact">+{Math.abs(d.impact_years).toFixed(1)}y</span>
                )}
              </div>
              {(d.value || d.unit) && (
                <div className="kdrawer-driver-value">{d.value}{d.unit ? ` ${d.unit}` : ''}</div>
              )}
              {d.suggestion && <div className="kdrawer-driver-suggestion">{d.suggestion}</div>}
            </div>
          ))}
        </div>
      )}

      {actions.length > 0 && (
        <div className="kdrawer-actions">
          <div className="kdrawer-section-label">What I can do today</div>
          {actions.map((a, i) => (
            <button
              key={i}
              type="button"
              className="kdrawer-action-card"
              onClick={() => { if (onClose) onClose(); if (onAskAI) onAskAI(`How can I improve my ${a.d.name}? ${a.text}`); }}
            >
              <span className="kdrawer-action-text">{T(a.text)}</span>
              <span className="kdrawer-action-arrow" aria-hidden="true">›</span>
            </button>
          ))}
        </div>
      )}

      <button
        className="kdrawer-link"
        onClick={() => {
          if (onOpenFull) onOpenFull({ ...spec, kind: 'longevity' });
          else if (onNav) onNav('longevity');
        }}
      >
        Open full Longevity page
      </button>
    </KalpaDrawer>
  );
}

// Insight drawer — full insight text plus relevant data inline, and an
// "Ask Kalpa about this" action that opens the coach drawer pre-seeded.
// First sentence of an insight, trimmed for use as a short headline (so the
// drawer never just re-prints the whole card paragraph).
function _firstSentence(t) {
  if (!t) return '';
  const s = String(t).trim();
  const m = s.match(/^.*?[.!?](\s|$)/);
  let out = (m ? m[0] : s).trim();
  if (out.length > 120) out = out.slice(0, 117).trim() + '…';
  return out;
}

// Scan an insight's text for biomarker names it actually cites, matched against
// the real marker list (display name or internal name, word-boundary). Returns
// up to 3 markers in the order they appear in the text.
function _citedMarkers(text, markers) {
  if (!text || !markers || !markers.length) return [];
  const low = String(text).toLowerCase();
  const found = [];
  const seen = new Set();
  for (const m of markers) {
    if (seen.has(m.name)) continue;
    let bestIdx = -1;
    for (const cand of [m.displayName, m.name]) {
      const cl = String(cand || '').toLowerCase().trim();
      if (cl.length < 2) continue;
      const esc = cl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(^|[^a-z0-9])${esc}([^a-z0-9]|$)`, 'i');
      const idx = low.search(re);
      if (idx >= 0 && (bestIdx < 0 || idx < bestIdx)) bestIdx = idx;
    }
    if (bestIdx >= 0) { seen.add(m.name); found.push({ m, idx: bestIdx }); }
  }
  found.sort((a, b) => a.idx - b.idx);
  return found.slice(0, 3).map(f => f.m);
}

const _MARKER_STATUS_COLOR = { normal: '#34d399', high: '#ef4444', low: '#f59e0b' };

// Compact dark-themed history chart for one cited marker, with the lab
// reference range shaded. Falls back to a single-value line when there is only
// one reading (so a one-point marker never renders an empty chart).
function _InsightMarkerChart({ marker }) {
  const hist = (marker.history || []).map(h => ({ date: h.date, value: h.value })).filter(h => h.value != null);
  const color = _MARKER_STATUS_COLOR[marker.status] || '#C9973A';
  if (hist.length < 2) {
    const v = marker.latest?.value ?? (hist[0] && hist[0].value);
    return (
      <div className="kdrawer-marker-single">
        Latest: <strong style={{ color }}>{v != null ? v : '—'}{marker.unit ? ` ${marker.unit}` : ''}</strong>
        {marker.refLow != null && marker.refHigh != null && (
          <span className="kdrawer-marker-ref"> · range {marker.refLow}–{marker.refHigh}</span>
        )}
      </div>
    );
  }
  const vals = hist.map(d => d.value);
  let mn = Math.min(...vals), mx = Math.max(...vals);
  if (marker.refLow != null) mn = Math.min(mn, marker.refLow);
  if (marker.refHigh != null) mx = Math.max(mx, marker.refHigh);
  const pad = (mx - mn) * 0.15 || (mx * 0.1) || 1;
  return (
    <ResponsiveContainer width="100%" height={120}>
      <LineChart data={hist} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
        {marker.refLow != null && marker.refHigh != null && (
          <ReferenceArea y1={marker.refLow} y2={marker.refHigh} fill="#34d399" fillOpacity={0.08} stroke="none" />
        )}
        <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false}
          tickFormatter={d => (d ? String(d).slice(0, 7) : '')} interval="preserveStartEnd" />
        <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={40} domain={[mn - pad, mx + pad]} />
        <Tooltip contentStyle={DRAWER_TOOLTIP} formatter={v => `${v}${marker.unit ? ` ${marker.unit}` : ''}`} />
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.2} dot={hist.length <= 24 ? { r: 2, fill: color } : false} activeDot={{ r: 4 }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function InsightMiniMetric({ metricKey, color, unit }) {
  const [data, setData] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get(`/fitness/metric/${metricKey}?range=30d`)
      .then(d => { if (alive) setData((Array.isArray(d?.data) ? d.data : []).map(r => ({ date: r.date, value: r.value }))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [metricKey]);
  if (!data.length) return null;
  return (
    <ResponsiveContainer width="100%" height={120}>
      <LineChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
        <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false} tickFormatter={_drawerXFmt('30d')} interval="preserveStartEnd" />
        <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={40} />
        <Tooltip contentStyle={DRAWER_TOOLTIP} formatter={v => `${Number(v).toLocaleString('en-GB')} ${unit}`} />
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function InsightDrawerBody({ spec, onClose, onNav, onAskAI, onOpenDrawer }) {
  const { title = 'Insight', text, dataSource, askSeed } = spec;
  const [markers, setMarkers] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get('/clinical-timeline/panels')
      .then(d => {
        if (!alive) return;
        convertPanelsPayload(d);
        const panels = Array.isArray(d?.panels) ? d.panels : (Array.isArray(d) ? d : []);
        const flat = [];
        panels.forEach(p => (p.markers || []).forEach(m => flat.push(m)));
        setMarkers(flat);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const isFitness = ['fitness', 'wearable', 'activity'].includes((dataSource || '').toLowerCase());
  const cited = isFitness ? [] : _citedMarkers(text, markers);
  const headline = _firstSentence(text) || 'Here is the supporting data for this insight.';
  const primary = cited[0];

  const askWhy = () => {
    if (onClose) onClose();
    if (onAskAI) onAskAI(askSeed || (primary ? `Why is my ${primary.displayName} flagged in this insight?` : (text || 'Tell me more about this insight.')));
  };

  return (
    <KalpaDrawer title={title} onClose={onClose}>
      <p className="kdrawer-insight-headline">{headline}</p>

      {cited.length > 0 ? (
        cited.map(m => (
          <div key={m.name} className="kdrawer-marker-block">
            <div className="kdrawer-section-label">{m.displayName}{m.unit ? ` · ${m.unit}` : ''}</div>
            <_InsightMarkerChart marker={m} />
          </div>
        ))
      ) : isFitness ? (
        <div className="kdrawer-marker-block">
          <div className="kdrawer-section-label">Recent activity · steps</div>
          <InsightMiniMetric metricKey="steps" color="#C9973A" unit="steps" />
        </div>
      ) : null}

      <div className="kdrawer-insight-actions">
        {primary ? (
          <button className="kdrawer-cta" onClick={() => { if (onOpenDrawer) onOpenDrawer({ kind: 'marker', marker: primary, returnSpec: spec }); else { if (onClose) onClose(); if (onNav) onNav('timeline'); } }}>
            View {primary.displayName} in Health Record →
          </button>
        ) : isFitness ? (
          <button className="kdrawer-cta" onClick={() => { if (onClose) onClose(); if (onNav) onNav('vitals'); }}>
            Open Vital Profile →
          </button>
        ) : (
          <button className="kdrawer-cta" onClick={() => { if (onClose) onClose(); if (onNav) onNav('timeline'); }}>
            Open Health Record →
          </button>
        )}
        <button className="kdrawer-cta-secondary" onClick={askWhy}>Ask Kalpa why →</button>
      </div>
    </KalpaDrawer>
  );
}

// Lay-friendly, regulatory-safe context for common blood markers. Wellness
// framing only — describes what a marker is associated with, never diagnoses.
// Keyed by lowercased marker name fragment; first substring match wins. Falls
// back to the marker's own description, then a generic line.
const _MARKER_CONTEXT = [
  { k: 'vitamin b12', t: 'Vitamin B12 supports nerve function and red blood cell formation. Levels are influenced by diet (it comes mainly from animal foods), absorption, and some medications. Lower readings are commonly associated with fatigue.' },
  { k: 'vitamin d', t: 'Vitamin D supports bone health and immune function. Levels are associated with sun exposure, diet, and supplementation, and often dip in winter months.' },
  { k: 'ferritin', t: 'Ferritin reflects the body’s stored iron. Levels are associated with diet, blood loss, and inflammation. Trends over time are more informative than a single reading.' },
  { k: 'hba1c', t: 'HbA1c reflects average blood sugar over roughly the past three months. It is associated with diet, activity, and metabolic health, and is worth monitoring as a trend.' },
  { k: 'glucose', t: 'Glucose is your blood sugar at the time of the sample. It is influenced by recent meals, fasting status, activity, and stress.' },
  { k: 'cholesterol', t: 'Cholesterol is associated with cardiovascular health. The balance between HDL, LDL and total cholesterol — alongside diet and activity — gives more context than any single value.' },
  { k: 'hdl', t: 'HDL is often described as the “protective” cholesterol fraction. Higher levels are generally associated with better cardiovascular profiles, and are influenced by activity and diet.' },
  { k: 'ldl', t: 'LDL cholesterol is associated with cardiovascular risk profiles. It is influenced by diet, activity and, for some, medication.' },
  { k: 'triglyceride', t: 'Triglycerides are a blood fat associated with diet (especially refined carbohydrates and alcohol), activity, and metabolic health.' },
  { k: 'tsh', t: 'TSH is a pituitary hormone associated with thyroid function. Readings can shift with time of day, illness, and some medications.' },
  { k: 'creatinine', t: 'Creatinine is a waste product associated with kidney filtration and muscle mass. Hydration and recent exercise can affect a single reading.' },
  { k: 'crp', t: 'CRP is a marker associated with general inflammation. Transient rises can follow recent infection, injury, or intense exercise.' },
  { k: 'alt', t: 'ALT is an enzyme associated with liver health. Readings are influenced by alcohol, medication, and recent intense exercise.' },
  { k: 'ast', t: 'AST is an enzyme associated with liver and muscle. Recent strenuous activity can raise a single reading.' },
  { k: 'haemoglobin', t: 'Haemoglobin reflects the blood’s oxygen-carrying capacity. It is associated with iron status, hydration, and altitude.' },
  { k: 'hemoglobin', t: 'Hemoglobin reflects the blood’s oxygen-carrying capacity. It is associated with iron status, hydration, and altitude.' },
];
function _markerContext(marker) {
  const name = (marker.displayName || marker.name || '').toLowerCase();
  for (const e of _MARKER_CONTEXT) if (name.includes(e.k)) return e.t;
  if (marker.description && String(marker.description).trim().length > 0) return marker.description;
  return 'This is one of your tracked blood markers. Its trend over time, alongside your other results, gives more context than any single reading. Consider discussing with your GP if a value stays outside the typical reference range.';
}
const _MARKER_STATUS_LABEL = { normal: 'Within range', high: 'Above typical range', low: 'Below typical range' };

// Single-marker drawer: opened from an insight ("View X in Health Record →"),
// a Today feed blood-event row, or a longevity driver. Keeps the user in the
// Today loop instead of navigating to the full Health Record page. Reuses
// _InsightMarkerChart for the trend. Closing returns to wherever they were
// (spec.returnSpec re-opens the originating drawer, handled by DrawerHost).
function MarkerDrawerBody({ spec, onClose, onNav, onAskAI }) {
  const marker = spec.marker || {};
  const color = _MARKER_STATUS_COLOR[marker.status] || '#C9973A';
  const statusLabel = _MARKER_STATUS_LABEL[marker.status] || 'Tracked';
  const val = marker.latest?.value;
  const unit = marker.unit ? ` ${marker.unit}` : '';
  const title = marker.displayName || marker.name || 'Marker';

  const askAbout = () => {
    if (onClose) onClose();
    if (onAskAI) onAskAI(`Tell me about my ${title} result and what affects it.`);
  };

  return (
    <KalpaDrawer title={title} onClose={onClose}>
      <div className="kdrawer-marker-hero">
        <div className="kdrawer-marker-value" style={{ color }}>
          {val != null ? val : '—'}<span className="kdrawer-marker-unit">{unit}</span>
        </div>
        <span className="kdrawer-marker-status" style={{ color, borderColor: color }}>{statusLabel}</span>
      </div>
      {marker.refLow != null && marker.refHigh != null && (
        <div className="kdrawer-marker-refline">Typical reference range: {marker.refLow}–{marker.refHigh}{unit}</div>
      )}

      <div className="kdrawer-marker-block">
        <div className="kdrawer-section-label">History</div>
        <_InsightMarkerChart marker={marker} />
      </div>

      <div className="kdrawer-marker-context">{T(_markerContext(marker))}</div>

      <div className="kdrawer-insight-actions">
        <button className="kdrawer-cta" onClick={askAbout}>Ask Kalpa about this →</button>
        <button className="kdrawer-cta-secondary" onClick={() => { if (onClose) onClose(); if (onNav) onNav('timeline'); }}>
          Open full Health Record →
        </button>
      </div>
    </KalpaDrawer>
  );
}

// Tier metadata for the blood-panel summary. Regulatory-safe, wellness framing:
// "In range" / "Watch" / "Worth a look" — never "abnormal" or diagnostic.
// Lay-friendly status vocabulary (health-literacy research): plain language +
// colour + a distinct glyph so status never rides on colour alone. Directionality
// is decided server-side by marker_engine (a beneficial high is 'in_range', a
// suspected data error is 'check', never a red alarm).
// C4 (KAL-247) — colours come from theme-aware CSS variables, not hardcoded hex.
// Every state carries GLYPH + WORD + POSITION-on-track, so hue is reinforcement and never the
// signal (WCAG 1.4.1, already satisfied). What was NOT satisfied is 1.4.3 CONTRAST: measured
// against a light background these four hardcoded values scored 1.92 / 1.81 / 2.67 / 2.54 : 1
// where AA needs 4.5. The light-theme substitutes in App.css measure 4.99-6.54 : 1. See there.
const _BP_STATUS = {
  in_range: { color: 'var(--st-ok)',     label: 'In your typical range', glyph: '✓' },
  watch:    { color: 'var(--st-watch)',  label: 'Worth monitoring',      glyph: '~' },
  action:   { color: 'var(--st-action)', label: 'Outside typical range', glyph: '!' },
  check:    { color: 'var(--st-check)',  label: 'Worth re-checking',     glyph: '?' },
};
function _fmtPanelDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + (String(iso).length === 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return iso;
  return formatDate(d);
}

// Blood-panel summary popup (opened by tapping a marker on the bio-age trend).
// Stays in the Today/Longevity loop — closing returns to wherever the user was
// (spec.returnSpec). Positive-first: leads with the in-range count and a mostly
// green proportional bar, then a scannable colour-coded status grid (in-range
// cells first so green dominates), then a constructive "Worth a look" list of
// only the actionable markers (each taps through to its full marker drawer).
// If everything is in range, a positive message replaces the action list.
// ══════════════════════════════════════════════════════════════════════════════════════════
// PART C (KAL-247) — EVIDENCE-BASED LAB-RESULT PRESENTATION.  master-test ONLY.
// Gated on FEATURES_GLOBAL.lab_presentation_v2, which is false on UK, US and NPL. These change
// how a clinical value is READ, so they go in front of Dr Jeevan before any pilot user sees them.
// ══════════════════════════════════════════════════════════════════════════════════════════

// ── C1 — THE HARM ANCHOR ────────────────────────────────────────────────────────────────
// Zikmund-Fisher BJ, Scherer AM, Witteman HO, et al. "Effect of Harm Anchors in Visual Displays
// of Test Results on Patient Perceptions of Urgency About Near-Normal Values." J Med Internet
// Res 2018;20(3):e98. n=1,618. Adding a SECOND labelled reference point — the level at which a
// clinician would actually act — reduced perceived urgency and intent to contact a doctor for
// NEAR-NORMAL values, without blunting the response to genuinely extreme ones. That last clause
// is why this is safe: it does not make people ignore real problems, it stops a value one unit
// outside a reference range reading like an emergency.
//
// ⚠ EVERY ENTRY BELOW IS A PUBLISHED CLINICAL ACTION THRESHOLD, NOT A NUMBER I CHOSE.
// Where I could not find a defensible one, THE MARKER IS ABSENT from this map and no anchor is
// drawn — C1 says omit rather than invent, and an invented anchor would be worse than none
// because it carries the authority of a citation it does not have.
//
// Framing stays wellness-side (CLAUDE.md): the label describes what CLINICIANS typically do. It
// makes no claim about this user, names no condition, and recommends nothing.
const HARM_ANCHORS = {
  // ALT/AST: >3x upper limit of normal is the conventional threshold for investigating
  // hepatocellular injury rather than observing it. Kwo PY, Cohen SM, Lim JK. ACG Clinical
  // Guideline: Evaluation of Abnormal Liver Chemistries. Am J Gastroenterol 2017;112:18-35.
  'ALT':        { at: 120, unit: 'U/L', note: '3× the upper reference limit' },
  'AST':        { at: 120, unit: 'U/L', note: '3× the upper reference limit' },
  // CRP: >10 mg/L is the usual cut for clinically significant inflammation; below that is the
  // "low-grade" range used for cardiovascular risk, not for acute concern.
  // Pearson TA, et al. AHA/CDC Scientific Statement. Circulation 2003;107:499-511.
  'CRP':        { at: 10,  unit: 'mg/L', note: 'clinically significant inflammation' },
  // Fasting glucose >= 7.0 mmol/L (126 mg/dL) is the diagnostic threshold for diabetes.
  // WHO/IDF 2006; ADA Standards of Care 2024.
  'Glucose':    { at: 126, unit: 'mg/dL', note: 'the diabetes diagnostic threshold' },
  // Ferritin < 15 ug/L is the WHO threshold for depleted iron stores in adults.
  // WHO. Serum ferritin concentrations for the assessment of iron status. 2020 (WHO/NMH/NHD).
  'Ferritin':   { at: 15,  unit: 'ng/mL', note: 'depleted iron stores (WHO)', below: true },
  // ALP > 3x ULN, same ACG guideline reasoning as ALT/AST.
  'Alkaline Phosphatase': { at: 390, unit: 'U/L', note: '3× the upper reference limit' },
  // WBC: < 2.0 or > 20 x10^9/L are the conventional thresholds for urgent haematology review.
  // NICE NG12, suspected haematological cancer referral criteria.
  'WBC Count':  { at: 20,  unit: '10^3/uL', note: 'urgent haematology review threshold' },
  // Haemoglobin < 80 g/L is where transfusion is typically considered in a stable adult.
  // NICE NG24, Blood transfusion.
  'Haemoglobin': { at: 80, unit: 'g/L', note: 'where transfusion is considered', below: true },
  'Hemoglobin':  { at: 80, unit: 'g/L', note: 'where transfusion is considered', below: true },
  // Platelets < 50 x10^9/L is the usual threshold for bleeding precautions.
  // BSH guideline, Investigation and management of adult thrombocytopenia. Br J Haematol 2019.
  'Platelets':  { at: 50,  unit: '10^3/uL', note: 'bleeding-precaution threshold', below: true },
  // TSH > 10 mIU/L is the usual treat/refer threshold in subclinical hypothyroidism.
  // NICE NG145, Thyroid disease: assessment and management.
  'TSH':        { at: 10,  unit: 'mIU/L', note: 'where treatment is usually considered' },
};

// MARKERS DELIBERATELY WITHOUT AN ANCHOR — no defensible published action threshold exists for
// a single value, so none is drawn. This list is rendered in the UI (see below) rather than
// only living in a comment, because "we could not source one" is information the reader is
// entitled to. MCV, RDW-CV, Lymphocytes %, Albumin and Creatinine are all interpreted in
// CONTEXT (alongside other indices, or against eGFR/muscle mass) rather than against a level
// at which a clinician acts on that number alone.
const NO_HARM_ANCHOR = ['MCV', 'RDW-CV', 'Lymphocytes %', 'Albumin', 'Creatinine'];

function harmAnchorFor(name) {
  if (!name) return null;
  if (HARM_ANCHORS[name]) return HARM_ANCHORS[name];
  const hit = Object.keys(HARM_ANCHORS).find(k => k.toLowerCase() === String(name).toLowerCase());
  return hit ? HARM_ANCHORS[hit] : null;
}


// Visual range-bar for one marker: healthy band + the user's value positioned
// on it. Label leads with the reference range, then the result:
// "Range X–Y · You: Z". Wellness framing — never diagnostic.
function _BPRangeBar({ m }) {
  const lo = (m.refLow != null && m.refLow !== '') ? Number(m.refLow) : null;
  const hi = (m.refHigh != null && m.refHigh !== '') ? Number(m.refHigh) : null;
  const val = (m.value != null && m.value !== '') ? parseFloat(m.value) : null;
  const unit = m.unit ? ` ${m.unit}` : '';
  const meta = _BP_STATUS[m.status] || _BP_STATUS.in_range;
  const rangeLabel =
    (lo != null && hi != null) ? `Range ${lo}–${hi}${unit}` :
    (hi != null) ? `Range ≤ ${hi}${unit}` :
    (lo != null) ? `Range ≥ ${lo}${unit}` : 'No reference range';
  // C1 — the harm anchor. Only drawn when a PUBLISHED action threshold exists for this marker
  // (see HARM_ANCHORS) and it lies outside the reference range, which is the only place it adds
  // information: an anchor inside the range would say nothing.
  const anchor = FEATURES_GLOBAL.lab_presentation_v2 ? harmAnchorFor(m.displayName || m.marker) : null;
  let bandL = null, bandW = null, dotL = null, anchorL = null;
  if (lo != null && hi != null && hi > lo) {
    const span = hi - lo;
    // The scale must STRETCH to include the anchor, or the anchor would sit pinned at 0% or
    // 100% and imply the user's value is nearly at it — the exact false urgency C1 removes.
    let sMin = lo - span * 0.4, sMax = hi + span * 0.4;
    if (anchor) {
      if (anchor.below) sMin = Math.min(sMin, anchor.at - span * 0.15);
      else sMax = Math.max(sMax, anchor.at + span * 0.15);
    }
    const pct = (v) => Math.max(0, Math.min(100, ((v - sMin) / (sMax - sMin)) * 100));
    bandL = pct(lo); bandW = pct(hi) - bandL;
    if (val != null && !isNaN(val)) dotL = pct(val);
    if (anchor) anchorL = pct(anchor.at);
  }
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)' }}>
        {/* C2 — ONE RANGE PER VIEW. Scherer AM, Zikmund-Fisher BJ, et al. "Eliciting Medical
            Maximizing-Minimizing Preferences…" / and specifically: showing a clinical range AND
            an optimal range together WORSENED comprehension (JMIR 2018;20(10):e11027, n=6,776).
            This line renders exactly one: the lab's own reference range. The longevity-optimal
            range is deliberately NOT drawn beside it. */}
        <span>{rangeLabel}</span>
        {val != null && !isNaN(val) && (
          <span style={{ color: meta.color, fontWeight: 600 }}>{` · You: ${val}${unit}`}</span>
        )}
      </div>
      {bandL != null && (
        <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', marginTop: 4 }}>
          <div style={{ position: 'absolute', left: `${bandL}%`, width: `${bandW}%`, height: '100%', borderRadius: 3, background: 'rgba(74,222,128,0.35)' }} />
          {/* C1 — the anchor is a TICK, visually subordinate to the user's value dot. It is a
              reference point, not a second result, and must not compete for attention. */}
          {anchorL != null && (
            <div aria-hidden="true" style={{ position: 'absolute', left: `${anchorL}%`, top: -4, width: 2, height: 14, background: 'rgba(255,255,255,0.55)', transform: 'translateX(-50%)' }} />
          )}
          {dotL != null && (
            <div style={{ position: 'absolute', left: `${dotL}%`, top: -2, width: 10, height: 10, borderRadius: 5, background: meta.color, border: '1.5px solid #0d1626', transform: 'translateX(-50%)' }} />
          )}
        </div>
      )}
      {anchorL != null && (
        <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)', marginTop: 4, lineHeight: 1.45 }}>
          <span aria-hidden="true" style={{ display: 'inline-block', width: 2, height: 9, background: 'rgba(255,255,255,0.55)', marginRight: 6, verticalAlign: 'middle' }} />
          Doctors typically aren’t concerned until {anchor.below ? 'below ' : ''}{anchor.at}{anchor.unit ? ` ${anchor.unit}` : ''}
          <span style={{ opacity: 0.75 }}>{` — ${anchor.note}.`}</span>
        </div>
      )}
    </div>
  );
}

// One clickable marker row (used for both out-of-range and expanded in-range).
// Status label rides on text+glyph+colour (not colour alone) so it reads at a
// glance and stays legible for colour-blind users.
function _BPMarkerRow({ m, onClick }) {
  const meta = _BP_STATUS[m.status] || _BP_STATUS.in_range;
  return (
    <button onClick={onClick} style={{
      display: 'block', width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '9px 12px',
      marginTop: 8, cursor: 'pointer', color: 'inherit',
    }}>
      {/* C3 — VERDICT ABOVE VALUE. The plain-language status used to sit to the RIGHT of the
          marker name, on the same line as and competing with the number. Promoted to its own
          line above, so the first thing read is what the result MEANS rather than a figure the
          reader then has to interpret. The number is still immediately below, unchanged.
          C4 — the state carries ICON + TEXT + POSITION, never hue alone (WCAG 1.4.1): the
          glyph and the word both state it, and the dot on the track states it a third way. The
          coloured dot beside the name is decorative reinforcement, not the signal. */}
      {FEATURES_GLOBAL.lab_presentation_v2 ? (
        <>
          <div style={{
            fontSize: 11.5, fontWeight: 700, color: meta.color,
            display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 3,
          }}>
            <span aria-hidden="true">{meta.glyph}</span>{meta.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="kbp-dot" style={{ background: meta.color }} aria-hidden="true" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{m.displayName}</span>
            <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 'auto' }}>›</span>
          </div>
        </>
      ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="kbp-dot" style={{ background: meta.color }} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{m.displayName}</span>
        <span style={{
          marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: meta.color,
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <span aria-hidden="true">{meta.glyph}</span>{meta.label}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>›</span>
      </div>
      )}
      {m.status === 'check' && (
        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
          This value looks unusual — it may be a data or units issue. Worth confirming with a re-test.
        </div>
      )}
      <_BPRangeBar m={m} />
    </button>
  );
}

function BloodPanelDrawerBody({ spec, onClose, onNav, onOpenDrawer }) {
  const ev = spec.event || {};
  const markers = Array.isArray(ev.markers) ? ev.markers : [];
  const total = ev.marker_count != null ? ev.marker_count : markers.length;
  const inRange = ev.in_range_count != null ? ev.in_range_count : markers.filter(m => m.status === 'in_range').length;
  const watch = ev.watch_count != null ? ev.watch_count : markers.filter(m => m.status === 'watch').length;
  const action = ev.action_count != null ? ev.action_count : markers.filter(m => m.status === 'action').length;
  const check = ev.check_count != null ? ev.check_count : markers.filter(m => m.status === 'check').length;

  // Full marker objects (with history) for the tap-through, same source the
  // Health Record uses. Grid renders immediately from the event payload.
  const [richByName, setRichByName] = useState({});
  const [showInRange, setShowInRange] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get('/clinical-timeline/panels')
      .then(d => {
        if (!alive) return;
        convertPanelsPayload(d);
        const map = {};
        (d?.panels || []).forEach(p => (p.markers || []).forEach(m => { map[m.name] = m; }));
        setRichByName(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Out-of-range surfaced first, most-actionable first; suspected data errors
  // ('check') surfaced last (calmly, not hidden — health-literacy best practice).
  const lookOrder = { action: 0, watch: 1, check: 2 };
  const lookMarkers = markers
    .filter(m => m.status === 'action' || m.status === 'watch' || m.status === 'check')
    .sort((a, b) => (lookOrder[a.status] ?? 9) - (lookOrder[b.status] ?? 9));
  // In-range collapsed behind a tap (kept clickable) so 35 never force scroll.
  const inRangeMarkers = markers.filter(m => m.status === 'in_range');

  const openMarker = (m) => {
    const rich = richByName[m.name];
    let marker = rich;
    if (!marker) {
      // Fallback shape if the panels fetch hasn't resolved — enough to render.
      let st = 'normal';
      const val = parseFloat(m.value);
      if (m.status === 'check') {
        st = 'data_error';
      } else if (m.status === 'action' && !isNaN(val)) {
        if (m.refLow != null && val < m.refLow) st = 'low';
        else st = 'high';
      }
      marker = {
        name: m.name, displayName: m.displayName, unit: m.unit, status: st,
        refLow: m.refLow, refHigh: m.refHigh, latest: { value: m.value },
        history: !isNaN(val) ? [{ date: ev.test_date, value: val }] : [],
      };
    }
    if (onOpenDrawer) onOpenDrawer({ kind: 'marker', marker, returnSpec: spec });
  };

  const pct = (n) => total > 0 ? `${(n / total) * 100}%` : '0%';

  return (
    <KalpaDrawer title="Blood panel" onClose={onClose}>
      <div className="kbp-subhead">
        <span className="kbp-date">{_fmtPanelDate(ev.test_date)}</span>
        {ev.isNPL && <span className="kbp-npl-badge">NPL</span>}
      </div>

      {/* Positive-first headline + proportional status bar */}
      <div className="kbp-headline">
        <span className="kbp-headline-num">{inRange}</span> of {total} markers in range
      </div>
      <div className="kbp-bar" role="img" aria-label={`${inRange} in your typical range, ${watch} worth monitoring, ${action} outside typical range${check > 0 ? `, ${check} worth re-checking` : ''}`}>
        {inRange > 0 && <span className="kbp-bar-seg" style={{ width: pct(inRange), background: _BP_STATUS.in_range.color }} />}
        {watch > 0 && <span className="kbp-bar-seg" style={{ width: pct(watch), background: _BP_STATUS.watch.color }} />}
        {action > 0 && <span className="kbp-bar-seg" style={{ width: pct(action), background: _BP_STATUS.action.color }} />}
        {check > 0 && <span className="kbp-bar-seg" style={{ width: pct(check), background: _BP_STATUS.check.color }} />}
      </div>

      {/* Out-of-range first — each clickable, with a range bar */}
      {lookMarkers.length > 0 ? (
        <div className="kbp-section">
          <div className="kdrawer-section-label">Worth a look</div>
          {lookMarkers.map((m, i) => (
            <_BPMarkerRow key={`look-${m.name}-${i}`} m={m} onClick={() => openMarker(m)} />
          ))}
        </div>
      ) : (
        <div className="kbp-allclear">All markers in range — nicely done.</div>
      )}

      {/* In-range collapsed behind a tap so 35 markers never force scrolling;
          every one stays individually clickable once expanded. */}
      {inRangeMarkers.length > 0 && (
        <div className="kbp-section">
          <button
            onClick={() => setShowInRange(s => !s)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 10, padding: '10px 12px', cursor: 'pointer', color: 'inherit', fontSize: 13,
            }}
          >
            <span className="kbp-dot" style={{ background: _BP_STATUS.in_range.color }} />
            <span style={{ fontWeight: 600 }}>{inRangeMarkers.length} in range</span>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>— tap to {showInRange ? 'hide' : 'view'}</span>
            <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.4)' }}>{showInRange ? '▴' : '▾'}</span>
          </button>
          {showInRange && inRangeMarkers.map((m, i) => (
            <_BPMarkerRow key={`inr-${m.name}-${i}`} m={m} onClick={() => openMarker(m)} />
          ))}
        </div>
      )}

      <button className="kbp-footer-link" onClick={() => { if (onClose) onClose(); if (onNav) onNav('timeline'); }}>
        View full panel in Health Record →
      </button>
    </KalpaDrawer>
  );
}

// "HH:MM" from a Fitbit start/end timestamp (ISO, "HH:MM:SS", or "HH:MM").
function _clockTime(s) {
  if (!s) return '';
  const m = String(s).match(/(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return '';
}

// 2b — delegates. NOTE the old version printed "0h 42m" for a sub-hour value; the
// canonical formatter prints "42m", which is what the rest of the app already showed.
const _hrsMin = (mins) => fmtDurationMin(mins);

// 4-lane hypnogram (Awake / REM / Light / Deep, top→bottom). Renders the real
// stage-transition time-series when the sync captured it (coloured blocks
// positioned across the night, Fitbit/Google-Health style); otherwise falls
// back to a proportional stacked timeline bar from the stage-minute totals —
// labelled explicitly so the degraded mode is never silent.
const _HYP_LANES = [
  { key: 'wake',  label: 'Awake', color: '#E89BB0' },
  { key: 'rem',   label: 'REM',   color: '#7FB2F0' },
  { key: 'light', label: 'Light', color: '#4A78C2' },
  { key: 'deep',  label: 'Deep',  color: '#6C5CE7' },
  // Only rendered when non-zero (see _hypLanes) — sleep a source reported as a session total with
  // no stage split. It has to be drawable because it IS counted in the hero duration.
  { key: 'unstaged', label: 'Unstaged', color: '#94A3B8' },
];
// Deep/Light/REM/Awake always show (a 0m REM night is information); Unstaged is noise at 0.
const _hypLanes = (mins) => _HYP_LANES.filter(l => l.key !== 'unstaged' || (mins[l.key] || 0) > 0);
const _fmtDur = (mins) => fmtDurationMin(mins);   // 2b — delegates
function _fmtNightDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(d.getTime())) return iso;
  return formatDate(d);
}

// NAP LABELLING RULE (committed — do not change without changing this comment).
// A session is labelled "Nap" when ALL of the following hold:
//   1. it does NOT cross midnight,
//   2. it is asleep for LESS THAN 180 minutes (3 h), and
//   3. it both starts at/after 06:00 and ends at/before 22:00 local.
// Justification for each clause, because a bare duration threshold mislabels real sleep:
//   - 3 h: nap literature treats naps as typically 20-120 min; short overnight sleeps in this
//     pilot run 4-6 h (1 Aug's overnight was 312 min). 180 min sits in the empty gap between the
//     two populations, so it separates them without clipping either.
//   - the 06:00-22:00 daytime window is what stops a TRUNCATED OVERNIGHT sleep (e.g. a 2 h night
//     ending 04:00) being called a nap — duration alone would get that wrong.
//   - crosses_midnight is an explicit disqualifier for the same reason.
// Anything not matching all three is left unlabelled rather than guessed at.
const NAP_MAX_ASLEEP_MIN = 180;
function _sleepSessionIsNap(s) {
  if (!s || s.crosses_midnight) return false;
  if (!(s.asleep_min < NAP_MAX_ASLEEP_MIN)) return false;
  const startH = parseInt(String(s.start_local || '').slice(0, 2), 10);
  const endH = parseInt(String(s.end_local || '').slice(0, 2), 10);
  const endM = parseInt(String(s.end_local || '').slice(3, 5), 10);
  if (!isFinite(startH) || !isFinite(endH) || !isFinite(endM)) return false;
  return startH >= 6 && (endH < 22 || (endH === 22 && endM === 0));
}
// Efficiency is ALWAYS derived from the figures being displayed — never a constant.
// (A hard-coded 87% shipped once; that is why this is a function and not a literal.)
function _sleepEfficiency(asleepMin, inBedMin) {
  if (!inBedMin || inBedMin <= 0 || asleepMin == null) return null;
  // KAL-079 — the same honesty rule the server applies to the day figure, applied to the
  // per-session chips. Where a session has no independent time-in-bed (in-bed <= asleep, which
  // is how the resolver represents "no separate measurement") efficiency is 100% by
  // construction and means nothing, so it is withheld. And rounding must never manufacture a
  // flawless night: 99.6% is not 100%.
  if (inBedMin <= asleepMin) return null;
  const pct = Math.round((asleepMin / inBedMin) * 100);
  return pct >= 100 ? 99 : pct;
}

function SleepHypnogram({ night, session }) {
  if (!night) return null;
  // When one session is selected we render THAT session's own minutes. The stage-transition
  // series (levels_json) is recorded for a single session only, so it is used solely for the
  // whole-day view of a single-session day; on any multi-session day, or for a selected
  // session, we fall through to the proportional bar rather than draw one session's shape
  // under another session's label.
  const stages = session
    ? { deep_min: session.deep_min, rem_min: session.rem_min, light_min: session.light_min,
        awake_min: session.awake_min, unstaged_min: session.unstaged_min || 0 }
    : (night.stages || {});
  const fallbackMins = {
    wake: stages.awake_min || 0, rem: stages.rem_min || 0,
    light: stages.light_min || 0, deep: stages.deep_min || 0,
    // Sleep the source reported as a total with no stage split. Drawn as its own lane so the lanes
    // still account for every minute in the hero figure above them.
    unstaged: stages.unstaged_min || 0,
  };
  const hasLevels = !session && (night.session_count || 1) === 1
    && night.has_levels && Array.isArray(night.hypnogram) && night.hypnogram.length > 0;

  if (hasLevels) {
    const segs = night.hypnogram;
    const span = Math.max(...segs.map(s => s.start_s + s.seconds), 1);
    // SHAPE from levels_json, MINUTES from the canonical resolver. levels_json exists only for the
    // single Fitbit-recorded session, so on a multi-session day its own totals are a SUBSET of the
    // hero above: measured on Kalpa UK 29 Jun 2026, the hero read 6h54m (414m) over lanes summing
    // to 349m, and the lane said 26m awake while the summary line said 34m. Labelling the lanes
    // from night.stages keeps every number on this screen tied to the one resolver; the blocks
    // still come from the real transition series.
    const realMins = fallbackMins;
    return (
      <div className="hyp">
        {_hypLanes(realMins).map(lane => (
          <div className="hyp-lane" key={lane.key}>
            <div className="hyp-lane-label">{lane.label}<span> · {_fmtDur(realMins[lane.key])}</span></div>
            <div className="hyp-track">
              {segs.filter(s => s.level === lane.key).map((s, i) => (
                <div key={i} className="hyp-block"
                  style={{ left: `${(s.start_s / span) * 100}%`, width: `${Math.max(0.5, (s.seconds / span) * 100)}%`, background: lane.color }} />
              ))}
            </div>
          </div>
        ))}
        <div className="hyp-axis">
          <span>{_clockTime(night.start_time)}</span>
          <span>{_clockTime(night.end_time)}</span>
        </div>
      </div>
    );
  }

  // Fallback: proportional stacked timeline bar (no per-night transitions).
  const order = ['deep', 'light', 'rem', 'unstaged', 'wake'];
  return (
    <div className="hyp-fallback">
      <div className="hyp-fallback-bar">
        {order.map(k => fallbackMins[k] > 0 && (
          <div key={k} style={{ flex: fallbackMins[k], background: _HYP_LANES.find(l => l.key === k).color }}
            title={`${_HYP_LANES.find(l => l.key === k).label} ${_fmtDur(fallbackMins[k])}`} />
        ))}
      </div>
      <div className="hyp-fallback-legend">
        {_hypLanes(fallbackMins).map(lane => (
          <span key={lane.key}><span className="hyp-dot" style={{ background: lane.color }} />{lane.label} {_fmtDur(fallbackMins[lane.key])}</span>
        ))}
      </div>
      <div className="hyp-fallback-note">
        {session
          ? `Stage proportions for this session (${session.start_local} – ${session.end_local}).`
          : "Per-stage timing wasn't recorded for this night — showing stage proportions."}
      </div>
    </div>
  );
}

// Secondary: sleep-duration period trend (the old primary view, now demoted
// below the single-night detail and shown on demand).
function SleepTrend() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true; setLoading(true);
    api.get(`/fitness/sleep/history?range=${range}`)
      .then(d => { if (alive) setData(Array.isArray(d?.data) ? d.data : []); })
      .catch(() => { if (alive) setData([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);
  // KAL-266 — `(d.duration_min || 0)` then `filter(h > 0)`: the same cancelling pair. A
  // zero-minute sleep record does not exist, so null is the honest representation of absence.
  const hoursArr = data.map(d => (d.duration_min == null ? null : d.duration_min / 60))
                       .filter(h => h != null && Number.isFinite(h));
  const avgV = hoursArr.length ? hoursArr.reduce((a, b) => a + b, 0) / hoursArr.length : null;
  const chartData = data.map(d => ({ date: d.date, hours: +((d.duration_min || 0) / 60).toFixed(2) }));
  const fmtH = v => (v == null ? '—' : v.toFixed(1));
  return (
    <>
      <div className="kdrawer-ranges">
        {METRIC_RANGES.map(r => (
          <button key={r.key} className={`kdrawer-range-btn${range === r.key ? ' active' : ''}`}
            onClick={() => setRange(r.key)}>{r.label}</button>
        ))}
      </div>
      <div className="kdrawer-chart">
        {loading ? <div className="kdrawer-empty">Loading…</div>
          : chartData.length === 0 ? <div className="kdrawer-empty">No sleep data for this period.</div>
            : (
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={chartData} margin={{ top: 8, right: 14, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false} tickFormatter={_drawerXFmt(range)} interval="preserveStartEnd" />
                  <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={40}
                    label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: 'var(--text-muted,#8a9bb0)', fontSize: 11, style: { textAnchor: 'middle' } }} />
                  <Tooltip contentStyle={DRAWER_TOOLTIP} formatter={v => `${fmtH(v)} h`} />
                  {avgV != null && <ReferenceLine y={avgV} stroke="var(--accent-gold)" strokeDasharray="4 3" strokeWidth={1.2} label={{ value: `avg ${fmtH(avgV)}h`, position: 'insideTopRight', fill: 'var(--accent-gold)', fontSize: 10 }} />}
                  <Line type="monotone" dataKey="hours" stroke="#3b82f6" strokeWidth={2.2}
                    dot={(p) => <circle key={p.key} cx={p.cx} cy={p.cy} r={chartData.length <= 30 ? 2 : 0} fill="#3b82f6" />}
                    activeDot={{ r: 5 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
      </div>
    </>
  );
}

// Sleep drawer (Part 4): single-night PRIMARY detail (Google Health style) —
// hero duration, bed/wake times, summary, 4-lane hypnogram, date navigation;
// period trend demoted to a secondary on-demand toggle.
// 2h(iii) — one tooltip, one place. Wording is deliberately about WHY totals differ rather
// than an apology, because the cause is upstream of Kalpa entirely.
// KAL-078 — NAME THE REASON, do not merely disclaim.
// Diagnosed 10 Aug 2026 on npl_801054440's night of 2026-08-10, segment by segment: Health
// Connect's own stage records give deep 111.5 / light 140.5 / REM 15.0 = 267 min asleep with
// 70 min awake across 19 awake segments, ELEVEN of them 30 seconds long. Google's app showed
// 156 light / 54 awake for the identical session. The single rule that reconciles the two is
// that Google counts awake segments shorter than ~5 minutes as sleep; reclassifying exactly
// those reproduces its figures to within a minute (light 157 vs 156, awake 53.5 vs 54).
// We deliberately do NOT copy that rule (KAL-074 WONTFIX): it is an undocumented vendor
// presentation heuristic that can change without notice, and Health Connect is our declared
// source of truth. But a user comparing the two screens deserves the actual reason, not a
// shrug — so we state the definition we use and what follows from it.
// KAL-081 (spec 5.2) — the ONLY metrics a user may target: behaviours they control. Resting HR,
// HRV, calories and weight are deliberately absent. Keyed by the Fitness tile key so a tile can
// ask "am I editable" without knowing anything about the cycle module.
const TARGET_EDITABLE = { steps: true, sleep_duration: true, active_zone_minutes: true };

// KAL-090 — format a queued (next-cycle) target change for the ring badge and the cycle card.
// Returns null when nothing is queued, so the badge simply does not render.
function _fmtTargetValue(metric, n) {
  if (n == null) return '—';
  if (metric === 'sleep_duration') {
    const h = Math.floor(n / 60), m = Math.round(n % 60);
    return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
  }
  return Number(n).toLocaleString('en-GB');
}

function _queuedTarget(targets, metricKey) {
  if (!Array.isArray(targets)) return null;
  const t = targets.find(x => x.metric === metricKey);
  if (!t || !t.pending) return null;
  let from = t.pending.effective_from;
  try {
    from = new Date(from + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { /* keep the ISO string if the locale formatter is unavailable */ }
  return { now: _fmtTargetValue(metricKey, t.value),
           next: _fmtTargetValue(metricKey, t.pending.value), from };
}

const _SLEEP_SOURCE_TOOLTIP = (
  'We count only the time your device recorded you as asleep. Brief awakenings are counted as ' +
  'awake, so this can read a little lower than your watch app, which often folds short ' +
  'wake-ups back into sleep. Naps or late data may also arrive hours later, or not at all.'
);

// Resolve the provider from the night payload, falling back to the instance's wearable_route
// flag when a row predates source tagging. NEVER an instance-name gate.
function _sleepSourceLabel(night) {
  const raw = String((night && (night.source || night.provider)) || '').toLowerCase();
  if (raw.includes('health_connect') || raw.includes('healthconnect')) return 'from Health Connect';
  if (raw.includes('healthkit') || raw.includes('apple')) return 'from Apple Health';
  if (raw.includes('fitbit')) return 'from Fitbit';
  if (raw) return null;                       // tagged with something we do not recognise: say nothing
  return FEATURES_GLOBAL.wearable_route === 'health_connect'
    ? 'from Health Connect'
    : 'from Fitbit';
}

function SleepDrawerBody({ spec, onClose, onAskAI }) {
  const [night, setNight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [curDate, setCurDate] = useState(spec.date || null);
  const [showTrend, setShowTrend] = useState(false);
  const [selIdx, setSelIdx] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setSelIdx(null);   // a session index from the previous day would point at the wrong session
    const q = curDate ? `?date=${encodeURIComponent(curDate)}` : '';
    api.get(`/fitness/sleep/night${q}`)
      .then(d => { if (alive) setNight(d && d.available ? d : null); })
      .catch(() => { if (alive) setNight(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [curDate]);

  const mins = night ? night.duration_minutes : null;
  const eff = night ? night.efficiency : null;
  const tib = night ? night.time_in_bed : null;
  // KAL-079 — preserve null. `|| 0` would turn a WITHHELD awake figure into a confident zero,
  // which is the exact fabrication this item exists to remove.
  const awakeMin = night ? (night.stages?.awake_min ?? null) : null;
  const sessionList = (night && Array.isArray(night.sessions)) ? night.sessions : [];
  // selIdx === null means "whole day"; otherwise the index of the session being inspected.
  const selSession = (selIdx != null && sessionList[selIdx]) ? sessionList[selIdx] : null;

  return (
    <KalpaDrawer title="Sleep" onClose={onClose}>
      {loading ? (
        <div className="kdrawer-empty">Loading…</div>
      ) : !night ? (
        <div className="kdrawer-empty">No sleep recorded for this night.</div>
      ) : (
        <>
          <div className="kdrawer-daynav">
            <button className="kdrawer-daynav-btn" disabled={!night.prev_date}
              onClick={() => night.prev_date && setCurDate(night.prev_date)} aria-label="Previous night">←</button>
            <span className="kdrawer-daynav-date">{_fmtNightDate(night.date)}</span>
            {/* 2h(iii) — SAY WHERE THE NUMBER CAME FROM, and pre-empt the "Google says
                something different" question. The divergence is upstream and not ours to
                fix: some devices report more sleep detail to Google's own app than they
                expose to other apps, and late/nap data can arrive hours later or never.
                Provider-aware so NPL (Health Connect) and US (Fitbit) each name their own
                source rather than a generic one. */}
            {_sleepSourceLabel(night) && (
              <span className="kdrawer-source-chip" title={_SLEEP_SOURCE_TOOLTIP}>
                {_sleepSourceLabel(night)}
                <span className="kdrawer-source-info" aria-hidden="true">ⓘ</span>
              </span>
            )}
            <button className="kdrawer-daynav-btn" disabled={!night.next_date}
              onClick={() => night.next_date && setCurDate(night.next_date)} aria-label="Next night">→</button>
          </div>

          {/* Day total stays the headline whichever session is selected, so this figure always
              equals the Fitness card that opened the drawer (both come from _sleep_days). */}
          <div className="kdrawer-hero">{mins != null ? _hrsMin(mins) : '—'}</div>
          {sessionList.length === 1 && (night.start_time || night.end_time) && (
            <div className="kdrawer-sub">{night.start_time} – {night.end_time}</div>
          )}
          {sessionList.length > 1 && (
            <div className="kdrawer-sub">
              day total · {sessionList.length} sessions
            </div>
          )}
          {/* KAL-078 — ASLEEP and TIME IN BED side by side. The hero number is asleep time, but
              nothing on screen said so, and "8h in bed" vs "6h30 asleep" is exactly the gap a
              user notices against their watch. Naming both, and which one the headline is,
              removes the ambiguity before the explanation below has to do any work.
              KAL-079 — efficiency and awake are omitted from this line entirely when the server
              withheld them; a withheld figure must not appear as a blank or a zero. */}
          <div className="kdrawer-sleep-summary">
            {mins != null ? <><strong>{_hrsMin(mins)} asleep</strong> · </> : null}
            {_hrsMin(tib)} in bed{eff != null ? ` · ${eff}% efficiency` : ''}
            {awakeMin != null && awakeMin > 0 ? ` · ${_fmtDur(awakeMin)} awake` : ''}.
          </div>
          {/* KAL-079 — say WHY the efficiency is missing rather than leaving a gap. Two distinct
              causes, two distinct sentences: contradictory inputs (overlapping segments upstream,
              so asleep exceeds time in bed and any efficiency would be over 100%), and no
              independent time-in-bed measurement (efficiency would be 100% by construction).
              Same honest-labelling rule as KAL-078: name the reason, never a bare disclaimer. */}
          {night.efficiency_withheld === 'contradictory_inputs' && (
            <div className="kdrawer-sleep-note">
              Sleep efficiency isn’t shown for this night. Your device sent overlapping sleep
              segments, so the time asleep it reported ({_hrsMin(mins)}) is longer than the time
              you were in bed ({_hrsMin(tib)}). Those two can’t both be right, so we’ve left the
              efficiency and awake figures out rather than show a number we can’t stand behind.
              The asleep and in-bed times above are exactly as your device recorded them.
            </div>
          )}
          {night.efficiency_withheld === 'no_time_in_bed' && (
            <div className="kdrawer-sleep-note">
              Sleep efficiency isn’t shown for this night — your device didn’t report a separate
              “time in bed”, and efficiency is time asleep divided by time in bed. Without that
              second figure it would always come out at 100%, which would tell you nothing.
            </div>
          )}
          {/* The REASON for the awake figure, not a disclaimer. Only when awake is present AND
              non-zero — withheld or zero means there is nothing to explain. */}
          {awakeMin != null && awakeMin > 0 && (
            <div className="kdrawer-sleep-note">
              Your {_fmtDur(awakeMin)} awake is time your device recorded you as awake, including
              brief wake-ups. We count those as awake rather than sleep, so this total can read a
              little lower than your watch app, which often folds short wake-ups back into sleep.
            </div>
          )}

          {/* A day can hold more than one sleep (overnight + nap). Google Health lists them and
              lets you open each one, so we do the same: collapsing N sessions into a single total
              hid an entire session and made the headline look wrong even when it was right.
              Sessions are never merged into one hypnogram — selecting one shows only its own
              stages. */}
          {sessionList.length > 1 && (
            <>
              <div className="kdrawer-section-label">
                {sessionList.length} sleep sessions
              </div>
              <div className="kdrawer-sleep-sessions">
                {sessionList.map((s, i) => {
                  const sel = selIdx === i;
                  const sEff = _sleepEfficiency(s.asleep_min, s.time_in_bed_min);
                  return (
                    <button
                      type="button"
                      className={`kdrawer-sleep-session${sel ? ' is-selected' : ''}`}
                      key={i}
                      aria-pressed={sel}
                      onClick={() => setSelIdx(sel ? null : i)}
                    >
                      <span className="kdrawer-sleep-session-time">
                        {s.start_local} – {s.end_local}
                        {s.crosses_midnight ? <span className="kdrawer-sleep-session-xmid"> +1d</span> : null}
                        {_sleepSessionIsNap(s) ? <span className="kdrawer-sleep-session-nap">nap</span> : null}
                        {sEff != null ? <span className="kdrawer-sleep-session-eff">{sEff}% eff</span> : null}
                      </span>
                      <span className="kdrawer-sleep-session-dur">{_hrsMin(s.asleep_min)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="kdrawer-section-label">
            {selSession
              ? `Sleep stages · ${selSession.start_local} – ${selSession.end_local}`
              : 'Sleep stages'}
            {selSession && (
              <button type="button" className="kdrawer-sleep-clearsel" onClick={() => setSelIdx(null)}>
                show whole day
              </button>
            )}
          </div>
          <SleepHypnogram night={night} session={selSession} />

          <button className="kdrawer-trend-toggle" onClick={() => setShowTrend(v => !v)}>
            {showTrend ? 'Hide duration trend' : 'Show duration trend'}
          </button>
          {showTrend && <SleepTrend />}
          {onAskAI && (
            <button className="kdrawer-ask-btn" onClick={() => onAskAI(
              `Tell me about my sleep on ${night.date}: ${mins != null ? _hrsMin(mins) : '—'} asleep`
              + `${tib ? `, ${_hrsMin(tib)} in bed` : ''}${eff ? `, ${eff}% efficiency` : ''}. `
              + `How is my sleep affecting my health and longevity, and what should I focus on?`)}>
              ✦ Ask Kalpa about this
            </button>
          )}
        </>
      )}
    </KalpaDrawer>
  );
}

// Activity drawer (Part 5): workout header, hero stats, navigation through
// recent activities, same-type duration trend, and a longevity-impact line.
function _activityIcon(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('run')) return '🏃';
  if (n.includes('walk')) return '🚶';
  if (n.includes('bike') || n.includes('cycl')) return '🚴';
  if (n.includes('swim')) return '🏊';
  if (n.includes('weight') || n.includes('strength')) return '🏋️';
  if (n.includes('yoga')) return '🧘';
  return '💪';
}
function SleepStat({ label, value }) {
  return value != null ? (
    <div className="kdrawer-actstat"><div className="kdrawer-actstat-val">{value}</div><div className="kdrawer-actstat-label">{label}</div></div>
  ) : null;
}

// Dark-themed intraday BPM graph for one workout — HR zones shaded behind a
// zone-coloured line. Mirrors the light-mode ActivityDetailPanel chart but
// retuned for the Today drawer surface. `hr` is [{t,bpm}] (or [{bpm}]).
function _ActivitySessionHR({ hr: hrRaw, durationMin, maxHR = AZM_MAX_HR_FALLBACK }) {
  // P0 guard: finite-bpm samples only (a null/NaN bpm → NaN axis domain → white screen).
  const hr = (Array.isArray(hrRaw) ? hrRaw : []).filter(s => s && typeof s.bpm === 'number' && isFinite(s.bpm));
  if (hr.length < 2) {
    return <div className="kdrawer-empty">Heart-rate samples weren't recorded for this session.</div>;
  }
  const zones = computeHRZones(maxHR);
  const totalSeconds = (durationMin || 0) * 60 || (hr.length - 1);
  const stepSeconds = hr.length > 1 ? totalSeconds / (hr.length - 1) : 0;
  const hrChart = hr.map((s, i) => ({ elapsed: Math.round(i * stepSeconds), bpm: s.bpm }));
  const fmtElapsed = (sec) => {
    const s = Math.max(0, Math.round(sec || 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const tickStepSec = (totalSeconds / 60) < 60 ? 300 : 600;
  const hrTicks = [];
  for (let t = 0; t <= totalSeconds; t += tickStepSec) hrTicks.push(t);
  if (hrTicks.length && hrTicks[hrTicks.length - 1] !== Math.round(totalSeconds)) hrTicks.push(Math.round(totalSeconds));

  const _ext = _finiteExtent(hr.map(s => s.bpm), 60, (maxHR || AZM_MAX_HR_FALLBACK));
  const yMin = Math.max(40, Math.floor(_ext.min / 10) * 10 - 10);
  const yMax = Math.max((maxHR || AZM_MAX_HR_FALLBACK) + 15, Math.ceil(_ext.max / 10) * 10 + 10);

  const zoneFor = (bpm) => zones.find(z => bpm < z.hi) || zones[zones.length - 1];
  const minutesPerSample = (durationMin || 0) / hr.length;
  const zoneMinutes = Object.fromEntries(zones.map(z => [z.key, 0]));
  for (const s of hr) zoneMinutes[zoneFor(s.bpm).key] += minutesPerSample;

  // Split into runs of same-zone samples so each can be drawn in its colour.
  const segments = [];
  let cur = { zoneKey: zoneFor(hrChart[0].bpm).key, color: zoneFor(hrChart[0].bpm).color, points: [hrChart[0]] };
  for (let i = 1; i < hrChart.length; i++) {
    const z = zoneFor(hrChart[i].bpm);
    cur.points.push(hrChart[i]);
    if (z.key !== cur.zoneKey) { segments.push(cur); cur = { zoneKey: z.key, color: z.color, points: [hrChart[i - 1], hrChart[i]] }; }
  }
  segments.push(cur);
  const segmentDatasets = segments.map((seg, idx) => {
    const set = new Set(seg.points.map(p => p.elapsed));
    return { key: `${seg.zoneKey}-${idx}`, color: seg.color, data: hrChart.map(p => ({ elapsed: p.elapsed, bpm: set.has(p.elapsed) ? p.bpm : null })) };
  });
  const HRTooltip = ({ active, payload }) => {
    if (!active || !Array.isArray(payload)) return null;
    const hit = payload.find(p => p && p.value != null);
    if (!hit) return null;
    return <div style={{ ...DRAWER_TOOLTIP, padding: '6px 10px' }}>{fmtElapsed(hit.payload?.elapsed)} · {hit.value} bpm</div>;
  };
  return (
    <div className="kdrawer-chart">
      <ResponsiveContainer width="100%" height={210}>
        <ComposedChart data={hrChart} margin={{ top: 8, right: 56, left: 0, bottom: 16 }}>
          <XAxis dataKey="elapsed" type="number" domain={[0, totalSeconds || 'auto']} ticks={hrTicks}
            tickFormatter={fmtElapsed} tick={DRAWER_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={34} domain={[yMin, yMax]} />
          {zones.map(z => (
            <ReferenceArea key={`band-${z.key}`} y1={z.lo} y2={Math.min(z.hi, yMax)} fill={z.color} fillOpacity={0.12} ifOverflow="hidden" />
          ))}
          {zones.filter(z => z.pctTop != null).map(z => (
            <ReferenceLine key={`line-${z.key}`} y={z.hi} stroke={z.color} strokeWidth={1} strokeDasharray="3 3"
              label={{ value: `${z.label} ${z.hi}`, position: 'right', fill: z.color, fontSize: 9, fontWeight: 600 }} />
          ))}
          <Tooltip content={<HRTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1, strokeDasharray: '3 3' }} wrapperStyle={{ outline: 'none', pointerEvents: 'none' }} />
          {segmentDatasets.map(seg => (
            <Line key={seg.key} data={seg.data} type="monotone" dataKey="bpm" stroke={seg.color}
              strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} legendType="none" />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      {/* (#1377) The legend now carries the BAND as well as the minutes (flex-wrap, so it reflows on
          mobile) — the boundaries are the AZM engine's own, so a user can check the number. */}
      <div className="hyp-fallback-legend" style={{ marginTop: 6 }}>
        {zones.map(z => (
          <span key={z.key}>
            <span className="hyp-dot" style={{ background: z.color }} />
            {z.label} {z.lo <= 0 ? `<${z.hi}` : (z.pctTop != null ? `${z.lo}–${z.hi - 1}` : `${z.lo}+`)} bpm · {zoneMinutes[z.key] >= 0.1 ? `${zoneMinutes[z.key].toFixed(0)}m` : '0m'}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActivityDrawerBody({ spec, onClose }) {
  const initial = spec.activity || {};
  const initialId = spec.logId != null ? spec.logId : initial.log_id;
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(-1);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  // (#1377) The user's own max HR, so the zone boundaries this drawer DRAWS and COUNTS are the
  // engine's (220 − age) and not the old hard-coded 165 (an age-55 assumption). hrInfo =
  // {bpm, fromAge}; fromAge false means we are on the engine's 185 fallback and the caption says
  // "assumed" instead of claiming "220 − your age". Loud on failure, never a silent fallback.
  const [hrInfo, setHrInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/profile')
      .then(d => {
        if (!alive) return;
        const age = (d && d.age != null) ? d.age
          : (d && d.dob ? (Date.now() - new Date(d.dob).getTime()) / 3.15576e10 : null);
        const info = azmMaxHrInfoFromAge(age);
        if (!info.fromAge) {
          console.warn('[azm/#1377] /profile carried no usable age or dob — zone boundaries fall '
            + `back to max HR ${AZM_MAX_HR_FALLBACK} bpm (backend _MAX_HR_FALLBACK)`);
        }
        setHrInfo(info);
      })
      .catch(e => {
        if (!alive) return;
        console.warn('[azm/#1377] /profile fetch failed — zone boundaries fall back to max HR '
          + `${AZM_MAX_HR_FALLBACK} bpm:`, e);
        setHrInfo({ bpm: AZM_MAX_HR_FALLBACK, fromAge: false });
      });
    return () => { alive = false; };
  }, []);

  // Nav list (for prev/next + same-type trend) — lightweight, no HR series.
  useEffect(() => {
    let alive = true;
    setListLoading(true);
    api.get('/fitness/activities?limit=30')
      .then(d => {
        if (!alive) return;
        const arr = Array.isArray(d?.activities) ? d.activities : [];
        setList(arr);
        const start = arr.findIndex(a => String(a.log_id) === String(initialId));
        setSel(start >= 0 ? start : 0);
      })
      .catch(() => { if (alive) { setList([]); setSel(0); } })
      .finally(() => { if (alive) setListLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navAct = (sel >= 0 && list.length) ? list[sel] : initial;
  const curId = navAct.log_id != null ? navAct.log_id : initialId;

  // Per-session detail (hr_series) for the currently-selected activity.
  useEffect(() => {
    if (curId == null) { setDetail(null); setDetailLoading(false); return; }
    let alive = true;
    setDetailLoading(true);
    api.get(`/fitness/activities/${encodeURIComponent(curId)}`)
      .then(d => { if (alive) setDetail(d && !d.error ? d : null); })
      .catch(() => { if (alive) setDetail(null); })
      .finally(() => { if (alive) setDetailLoading(false); });
    return () => { alive = false; };
  }, [curId]);

  const src = detail || navAct || initial;
  const name = src.activity_name || src.name || navAct.activity_name || 'Activity';
  const icon = _activityIcon(name);
  const mins = src.duration_min != null ? src.duration_min : (navAct.duration_min || initial.duration_mins || 0);
  const avgHr = src.avg_heart_rate || navAct.avg_heart_rate || initial.avg_hr;
  const dist = src.distance != null ? src.distance : (navAct.distance != null ? navAct.distance : initial.distance_km);
  const distUnit = src.distance_unit || navAct.distance_unit || initial.distance_unit || 'km';
  const cals = src.calories || navAct.calories || initial.calories;
  const hr = (detail && Array.isArray(detail.hr_series)) ? detail.hr_series : [];

  // (#1377) Active Zone Minutes for this session, by the ONE canonical rule
  // (azmZoneMinutesFromHr → the same 50%/70%-of-max-HR boundaries and the same ×2 vigorous
  // doubling as backend active_minutes). It used to be computed here from a hard-coded max HR of
  // 165 bpm, with the counted band starting at 60% of it (the old HR_ZONES map) and NO doubling —
  // three ways adrift from the definition the app states, on a figure labelled "zone min".
  // Computed only once the max HR has RESOLVED (hrInfo set): a figure derived from an assumed
  // max HR, flashed for a moment and then silently replaced, is exactly the unfalsifiable number
  // this item is about. Until then the stat and its caption simply aren't shown.
  const azm = hrInfo ? azmZoneMinutesFromHr(hr, mins, hrInfo.bpm) : null;

  // Same-type trend: last 10 of the same activity name, oldest→newest (secondary).
  const sameType = list.filter(a => (a.activity_name || '') === name);
  const trend = sameType.slice(0, 10).reverse().map(a => ({ date: a.date, mins: a.duration_min || 0, _id: a.log_id }));

  // Rough longevity contribution: scales with active minutes. Display only.
  const impactY = Math.max(0.01, Math.min(0.12, (mins / 60) * 0.05));

  return (
    <KalpaDrawer title="Activity" onClose={onClose}>
      <div className="kdrawer-act-head">
        <span className="kdrawer-act-icon" aria-hidden="true">{icon}</span>
        <span className="kdrawer-act-name">{name}</span>
      </div>

      {list.length > 1 && (
        <div className="kdrawer-daynav">
          <button className="kdrawer-daynav-btn" disabled={sel >= list.length - 1}
            onClick={() => setSel(Math.min(list.length - 1, sel + 1))} aria-label="Older session">←</button>
          <span className="kdrawer-daynav-date">{_fmtNightDate(navAct.date)}</span>
          <button className="kdrawer-daynav-btn" disabled={sel <= 0}
            onClick={() => setSel(Math.max(0, sel - 1))} aria-label="Newer session">→</button>
        </div>
      )}

      <div className="kdrawer-actstats">
        <SleepStat label="Duration" value={mins ? `${mins} min` : null} />
        <SleepStat label="Avg HR" value={avgHr ? `${avgHr} bpm` : null} />
        <SleepStat label="Calories" value={cals ? `${cals}` : null} />
        <SleepStat label="Distance" value={dist != null ? `${dist} ${distUnit}` : null} />
        <SleepStat label="Active Zone Min" value={azm ? `${azm.points} min` : null} />
      </div>

      {/* (#1377) State the rule NEXT TO the figure, with this session's own arithmetic, so the
          number is checkable rather than asserted. */}
      {azm && (
        <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-dim,#8a9bb0)', marginTop: 8 }}>
          {azm.moderate} moderate min ×1 + {azm.vigorous} vigorous min ×2 = {azm.points}.{' '}
          Moderate = {Math.round(AZM_ZONE_FATBURN_FRAC * azm.maxHr)}–{Math.round(AZM_ZONE_CARDIO_FRAC * azm.maxHr) - 1} bpm,
          vigorous = {Math.round(AZM_ZONE_CARDIO_FRAC * azm.maxHr)} bpm and above (max HR {azm.maxHr} bpm
          {hrInfo && hrInfo.fromAge ? ' ≈ 220 − your age' : ', assumed — no date of birth on file'}).
        </div>
      )}

      {/* PRIMARY: intraday BPM graph with HR zones. Held until the max HR resolves so the shaded
          bands are never drawn at one set of boundaries and then redrawn at another. */}
      <div className="kdrawer-section-label" style={{ marginTop: 18 }}>Heart rate · this session</div>
      {(detailLoading || !hrInfo) ? (
        <div className="kdrawer-empty">Loading heart rate…</div>
      ) : (
        <_ActivitySessionHR hr={hr} durationMin={mins} maxHR={hrInfo.bpm} />
      )}

      <div className="kdrawer-impact">
        <span className="kdrawer-impact-icon" aria-hidden="true">★</span>
        <span>This session contributed approximately −{impactY.toFixed(2)}y to your biological age.</span>
      </div>

      {/* SECONDARY: recent same-type volume. */}
      {trend.length >= 2 && (
        <div className="kdrawer-chart" style={{ marginTop: 20 }}>
          <div className="kdrawer-section-label">Recent {name} sessions</div>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={trend} margin={{ top: 8, right: 14, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="date" tick={DRAWER_TICK} axisLine={false} tickLine={false} tickFormatter={formatDate} interval="preserveStartEnd" />
              <YAxis tick={DRAWER_TICK} axisLine={false} tickLine={false} width={40}
                label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: 'var(--text-muted,#8a9bb0)', fontSize: 11, style: { textAnchor: 'middle' } }} />
              <Tooltip contentStyle={DRAWER_TOOLTIP} formatter={v => `${v} min`} />
              <Bar dataKey="mins" radius={[3, 3, 0, 0]}>
                {trend.map((t, i) => (
                  <Cell key={i} fill={String(t._id) === String(curId) ? '#C9973A' : '#3b82f6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {listLoading && list.length === 0 && <div className="kdrawer-empty">Loading…</div>}
    </KalpaDrawer>
  );
}

// Top-level switch: renders the active drawer based on the App-level spec.
function DrawerHost({ spec, onClose, onNav, onAskAI, onOpenLongevityFull, onOpenDrawer }) {
  if (!spec) return null;
  // KAL-142 Phase 3 — the universal metric drawer. Same component from Today v2 tiles AND
  // Body/Fitness rows, so a metric has exactly ONE detail view.
  if (spec.kind === 'tv2metric') return <TV2MetricDrawer spec={spec} onClose={onClose} />;
  if (spec.kind === 'metric')    return <MetricDrawerBody spec={spec} onClose={onClose} onAskAI={onAskAI} />;
  if (spec.kind === 'longevity') return <LongevityDrawerBody spec={spec} onClose={onClose} onNav={onNav} onAskAI={onAskAI} onOpenFull={onOpenLongevityFull} onOpenDrawer={onOpenDrawer} />;
  if (spec.kind === 'sleep')     return <SleepDrawerBody spec={spec} onClose={onClose} onAskAI={onAskAI} />;
  if (spec.kind === 'activity')  return <ActivityDrawerBody spec={spec} onClose={onClose} />;
  if (spec.kind === 'insight')   return <InsightDrawerBody spec={spec} onClose={onClose} onNav={onNav} onAskAI={onAskAI} onOpenDrawer={onOpenDrawer} />;
  if (spec.kind === 'marker')    return <MarkerDrawerBody spec={spec} onNav={onNav} onAskAI={onAskAI} onClose={() => (spec.returnSpec && onOpenDrawer ? onOpenDrawer(spec.returnSpec) : onClose())} />;
  if (spec.kind === 'bloodpanel') return <BloodPanelDrawerBody spec={spec} onNav={onNav} onOpenDrawer={onOpenDrawer} onClose={() => (spec.returnSpec && onOpenDrawer ? onOpenDrawer(spec.returnSpec) : onClose())} />;
  return null;
}


// ── Activities (recent workout sessions) ──────────────────────────────────
// (#1377) HR zones, THE SAME boundaries the AZM engine counts on — the shared
// AZM_ZONE_*_FRAC constants (backend/active_minutes.py _ZONE_*_FRAC). Thresholds are % of max HR
// (AZM_MAX_HR_FALLBACK, or 220 − age where a DOB is on file). For age 55 → max HR 165 →
// 83/116/140 bpm.
// WAS WRONG until this commit: a 5-band map with "Light 50–60%" and "Fat Burn 60–70%", so the
// legend and the shaded bands told the user moderate began at 60% of max HR while the engine had
// been counting from 50% — the app displayed boundaries it did not use.
const HR_ZONES = [
  { key: 'below',   label: 'Below zone', pctTop: AZM_ZONE_FATBURN_FRAC, color: '#3B82F6' }, // <50% — doesn't count
  { key: 'fatburn', label: 'Fat Burn',   pctTop: AZM_ZONE_CARDIO_FRAC,  color: '#EAB308' }, // 50-69% — moderate ×1
  { key: 'cardio',  label: 'Cardio',     pctTop: AZM_ZONE_PEAK_FRAC,    color: '#F97316' }, // 70-84% — vigorous ×2
  { key: 'peak',    label: 'Peak',       pctTop: null,                  color: '#EF4444' }, // 85%+  — vigorous ×2
];
function computeHRZones(maxHR) {
  // Fallback = the ENGINE's fallback (185), not the old literal 165 (an age-55 assumption that
  // silently invented a max HR for everyone else).
  const mx = (typeof maxHR === 'number' && isFinite(maxHR) && maxHR > 0) ? maxHR : AZM_MAX_HR_FALLBACK;
  const out = [];
  let lo = 0;
  for (const z of HR_ZONES) {
    const hi = z.pctTop == null ? mx + 30 : Math.round(mx * z.pctTop);
    out.push({ ...z, lo, hi });
    lo = hi;
  }
  return out;
}
// Finite-only min/max WITHOUT the spread operator. P0 white-screen guard: a null/NaN bpm
// in an HR series made Math.min(...hr.map(s=>s.bpm)) return NaN → a NaN YAxis domain →
// Recharts threw in its async measure callback (NOT caught by the render-phase ErrorBoundary)
// → the whole workout view went white. Spread on a long series could also blow the call stack.
function _finiteExtent(nums, fallbackMin, fallbackMax) {
  let mn = Infinity, mx = -Infinity;
  for (const v of nums) { if (typeof v === 'number' && isFinite(v)) { if (v < mn) mn = v; if (v > mx) mx = v; } }
  return { min: isFinite(mn) ? mn : fallbackMin, max: isFinite(mx) ? mx : fallbackMax };
}

const ACTIVITY_ICONS = {
  walk: '🚶', run: '🏃', treadmill: '🏃', jog: '🏃',
  bike: '🚴', cycle: '🚴', cycling: '🚴', spin: '🚴',
  swim: '🏊', yoga: '🧘', meditation: '🧘',
  weights: '💪', strength: '💪', workout: '💪',
  hike: '🥾', elliptical: '⚙️', sport: '⚽',
};
function activityIcon(name) {
  const s = (name || '').toLowerCase();
  for (const key of Object.keys(ACTIVITY_ICONS)) if (s.includes(key)) return ACTIVITY_ICONS[key];
  return '🏋️';
}
function formatDuration(ms) {
  const min = Math.round((ms || 0) / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
function formatActivityDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return formatDate(d);
  } catch { return iso; }
}

// Phase 3 — interactive, keyless activity route map. Restores + improves the old
// SVG route line (no maps provider/key — none ever existed): aspect-correct
// projection (longitude scaled by cos(lat) so the route isn't stretched), start
// (green) / end (red) markers, distance/pace/altitude caption, and pan/zoom
// (scroll to zoom, drag to pan, double-click to reset). Gated on features.location
// by the caller. gps: array of [lat,lng] or [lat,lng,alt].
function _ActivityRouteMap({ gps, distance, distanceUnit, pace }) {
  const W = 600, H = 300, PAD = 16;
  const [vb, setVb] = useState(null);
  const dragRef = useRef(null);
  if (!Array.isArray(gps) || gps.length < 2) {
    return <div className="activity-gps-placeholder">GPS not recorded for this activity.</div>;
  }
  const lats = gps.map(p => p[0]), lngs = gps.map(p => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180) || 1; // aspect fix
  const rLat = (maxLat - minLat) || 1e-6;
  const rLng = ((maxLng - minLng) * kx) || 1e-6;
  const scale = Math.min((W - 2 * PAD) / rLng, (H - 2 * PAD) / rLat);
  const offX = PAD + ((W - 2 * PAD) - rLng * scale) / 2;
  const offY = PAD + ((H - 2 * PAD) - rLat * scale) / 2;
  const hasAlt = gps[0].length > 2 && gps[0][2] != null;
  let aMin = Infinity, aMax = -Infinity;
  if (hasAlt) for (const p of gps) if (p[2] != null) { aMin = Math.min(aMin, p[2]); aMax = Math.max(aMax, p[2]); }
  const xy = gps.map(([la, ln]) => [
    +(offX + (ln - minLng) * kx * scale).toFixed(1),
    +(H - (offY + (la - minLat) * scale)).toFixed(1),
  ]);
  const pts = xy.map(p => p.join(',')).join(' ');
  const start = xy[0], end = xy[xy.length - 1];
  const view = vb || { x: 0, y: 0, w: W, h: H };
  const r = view.w / W; // keep markers/strokes constant size as we zoom
  const onWheel = (e) => {
    e.preventDefault();
    const f = e.deltaY < 0 ? 0.85 : 1 / 0.85;
    const nw = Math.min(W, Math.max(W * 0.12, view.w * f));
    const nh = nw * (H / W);
    const cx = view.x + view.w / 2, cy = view.y + view.h / 2;
    setVb({ x: cx - nw / 2, y: cy - nh / 2, w: nw, h: nh });
  };
  const onDown = (e) => { dragRef.current = { px: e.clientX, py: e.clientY, vx: view.x, vy: view.y }; };
  const onMove = (e) => {
    if (!dragRef.current) return;
    setVb({ ...view,
      x: dragRef.current.vx - (e.clientX - dragRef.current.px) * (view.w / W),
      y: dragRef.current.vy - (e.clientY - dragRef.current.py) * (view.h / H) });
  };
  const onUp = () => { dragRef.current = null; };
  return (
    <div className="activity-route-map">
      <svg viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} className="activity-gps-svg"
        onWheel={onWheel} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
        onPointerLeave={onUp} onDoubleClick={() => setVb(null)}
        style={{ touchAction: 'none', cursor: 'grab' }}>
        <rect x={-W} y={-H} width={W * 3} height={H * 3} fill="var(--secondary-bg, #F8F7F4)" rx={8} />
        <polyline points={pts} fill="none" stroke="var(--accent, #112240)"
          strokeWidth={2.6 * r} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={start[0]} cy={start[1]} r={6 * r} fill="#22c55e" stroke="#fff" strokeWidth={1.6 * r} />
        <circle cx={end[0]} cy={end[1]} r={6 * r} fill="#ef4444" stroke="#fff" strokeWidth={1.6 * r} />
      </svg>
      <div className="activity-route-meta" style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {distance != null && <span>{distance} {distanceUnit || ''}</span>}
        {pace != null && <span>· {pace}</span>}
        {hasAlt && isFinite(aMin) && <span>· {Math.round(aMin)}–{Math.round(aMax)} m elev</span>}
        <span style={{ color: 'var(--text-dim)' }}>· scroll to zoom · drag to pan · double-click to reset</span>
      </div>
    </div>
  );
}

function ActivityDetailPanel({ logId, maxHR = AZM_MAX_HR_FALLBACK, onClose }) {
  // Encode the activity ID into the modal hash so a refresh while the
  // modal is open lands back on this exact activity. encodeURIComponent
  // guards against IDs containing special characters.
  const closeModal = useModalHashRoute(`#activity-${encodeURIComponent(logId)}`, onClose);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0); // bump to retry
  useEffect(() => {
    // Stable AbortController per fetch so a re-render doesn't cancel the
    // in-flight request. Component unmount and explicit retries DO cancel.
    const ctrl = new AbortController();
    setLoading(true);
    setData(null);
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    // 20s timeout — TCX parsing on cold cache + slow mobile network can
    // legitimately take 8–15s. 10s was too aggressive and was tripping
    // on real slow connections that would otherwise complete.
    const timeoutId = setTimeout(() => ctrl.abort(new Error('timeout')), 20000);
    const url = `${API_BASE}/fitness/activities/${encodeURIComponent(logId)}`;
    fetch(url, { credentials: 'include', signal: ctrl.signal })
      .then(async r => { if (!r.ok) throw await _apiError(r); return r.json(); })
      .then(d => {
        clearTimeout(timeoutId);
        if (process.env.NODE_ENV === 'development') {
          const ms = Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0));
          console.log(`[ActivityDetail] ${logId} loaded in ${ms}ms`);
        }
        if (!ctrl.signal.aborted) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(e => {
        clearTimeout(timeoutId);
        if (ctrl.signal.aborted && e?.name !== 'AbortError' && e?.message !== 'timeout') return;
        // 'aborted' from React StrictMode double-effect is harmless — only
        // surface a real error if the abort came from our timeout or fetch
        // failed for another reason.
        const msg = (e?.message === 'timeout' || (e && String(e).includes('timeout')))
          ? 'This activity is taking too long to load.'
          : (e?.message || 'Could not load activity.');
        if (!ctrl.signal.aborted || e?.message === 'timeout') {
          setData({ error: msg });
          setLoading(false);
        }
      });
    return () => {
      clearTimeout(timeoutId);
      ctrl.abort();
    };
  }, [logId, fetchKey]);
  const retry = () => setFetchKey(k => k + 1);

  if (loading) {
    return (
      <div className="metric-detail-overlay" onClick={closeModal}>
        <div className="metric-detail-panel" onClick={e => e.stopPropagation()}>
          <div className="metric-detail-head">
            <div><div className="metric-detail-label">Activity</div></div>
            <button className="metric-detail-close" onClick={closeModal}>×</button>
          </div>
          <div className="activity-loading-state">
            <div className="activity-loading-spinner" aria-hidden="true" />
            <div className="activity-loading-text">Loading activity…</div>
            <div className="activity-loading-sub">Fetching heart rate and GPS data</div>
          </div>
        </div>
      </div>
    );
  }
  if (!data || data.error) {
    return (
      <div className="metric-detail-overlay" onClick={closeModal}>
        <div className="metric-detail-panel" onClick={e => e.stopPropagation()}>
          <div className="metric-detail-head">
            <div><div className="metric-detail-label">Activity</div></div>
            <button className="metric-detail-close" onClick={closeModal}>×</button>
          </div>
          <div className="metric-detail-empty">
            {data?.error || 'Activity not available.'}
            <div style={{ marginTop: 12 }}>
              <button className="metric-range-btn" onClick={retry}>Retry</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // P0 guard: keep only samples with a finite numeric bpm — a null/NaN bpm downstream
  // produced a NaN axis domain that white-screened the whole view (see _finiteExtent).
  const hr = (Array.isArray(data.hr_series) ? data.hr_series : []).filter(s => s && typeof s.bpm === 'number' && isFinite(s.bpm));
  // RUN11.7 §2 — honest HR render tiers for NPL (HC-windowed series, often sparse):
  // ≥20 in-window samples → full zone curve; 1–19 → real points only (no implied
  // continuous trace); 0 → explicit "no HR recorded". MAIN (TCX) keeps its curve.
  const _isNPL = (typeof FEATURES_GLOBAL !== 'undefined') && FEATURES_GLOBAL.branding && FEATURES_GLOBAL.branding.instance === 'npl';
  const HR_DENSE = 20;
  const gps = Array.isArray(data.gps_track) ? data.gps_track : [];
  const pace = (data.distance && data.duration_min)
    ? (data.duration_min / data.distance)
    : null;
  // Elapsed time in seconds per HR sample (samples are evenly spaced through
  // duration_min). Used for the mm:ss X-axis and tooltip.
  const totalSeconds = (data.duration_min || 0) * 60;
  const stepSeconds = hr.length > 1 ? totalSeconds / (hr.length - 1) : 0;
  const hrChart = hr.map((s, i) => ({
    elapsed: Math.round(i * stepSeconds),
    bpm: s.bpm,
  }));
  const fmtElapsed = (sec) => {
    const s = Math.max(0, Math.round(sec || 0));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
  };
  // Tick every 5 min for sessions < 60 min, every 10 min for longer.
  const totalMinForTicks = totalSeconds / 60;
  const tickStepSec = totalMinForTicks < 60 ? 300 : 600;
  const hrTicks = [];
  if (totalSeconds > 0) {
    for (let t = 0; t <= totalSeconds; t += tickStepSec) hrTicks.push(t);
    if (hrTicks[hrTicks.length - 1] !== Math.round(totalSeconds)) {
      // include the final tick so the user can see total session length
      hrTicks.push(Math.round(totalSeconds));
    }
  }

  // Route rendering moved to the interactive <_ActivityRouteMap> (Phase 3).

  const tickStyle = { fontSize: 11, fill: '#8B6914' };
  const tooltipStyle = { fontSize: '12px', background: '#FFFFFF', color: '#112240', border: '1px solid #E5E2D8', borderRadius: '8px', boxShadow: 'none' };

  return (
    <div className="metric-detail-overlay" onClick={closeModal}>
      <div className="metric-detail-panel" onClick={e => e.stopPropagation()}>
        <div className="metric-detail-head">
          <div>
            <div className="metric-detail-label">{activityIcon(data.activity_name)} {data.activity_name}</div>
            <div className="metric-detail-current" style={{ fontSize: '1.4rem' }}>
              {formatActivityDate(data.start_time || data.date)}
              {data.start_time && data.start_time.includes('T') && (
                <span className="metric-detail-current-sub">
                  {new Date(data.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
          <button className="metric-detail-close" onClick={closeModal}>×</button>
        </div>

        {/* RUN11.8 §2 — on NPL (HC source that doesn't carry per-session distance/energy/steps)
            render ONLY the tiles that have data, so the card reads complete instead of a wall of
            "—". Duration always shows. MAIN (Fitbit-direct, fully populated) is unchanged: it
            still renders every tile (with "—" where null). No value is fabricated either way. */}
        <div className="activity-stats-grid">
          <div><div className="md-stat-label">Duration</div><div className="md-stat-val">{formatDuration(data.duration_ms)}</div></div>
          {(!_isNPL || data.distance != null) && <div><div className="md-stat-label">Distance</div><div className="md-stat-val">{data.distance != null ? `${data.distance}` : '—'}<span className="md-stat-unit">{data.distance != null ? data.distance_unit : ''}</span></div></div>}
          {(!_isNPL || !!data.avg_heart_rate) && <div><div className="md-stat-label">Avg HR</div><div className="md-stat-val">{data.avg_heart_rate || '—'}<span className="md-stat-unit">bpm</span></div></div>}
          {/* RUN11.9 P4 — high/low HR (Tim asked) + the greedy Tier-B scalars; each hidden on NPL when absent. */}
          {(!_isNPL || !!data.max_heart_rate) && <div><div className="md-stat-label">Max HR</div><div className="md-stat-val">{data.max_heart_rate || '—'}<span className="md-stat-unit">bpm</span></div></div>}
          {(!_isNPL || !!data.min_heart_rate) && <div><div className="md-stat-label">Min HR</div><div className="md-stat-val">{data.min_heart_rate || '—'}<span className="md-stat-unit">bpm</span></div></div>}
          {(!_isNPL || !!data.calories) && <div><div className="md-stat-label">Calories</div><div className="md-stat-val">{data.calories || '—'}<span className="md-stat-unit">kcal</span></div></div>}
          {(!_isNPL || !!data.active_calories) && <div><div className="md-stat-label">Active kcal</div><div className="md-stat-val">{data.active_calories || '—'}<span className="md-stat-unit">kcal</span></div></div>}
          {(!_isNPL || !!data.steps) && <div><div className="md-stat-label">Steps</div><div className="md-stat-val">{data.steps ? data.steps.toLocaleString('en-GB') : '—'}</div></div>}
          {(!_isNPL || !!data.elevation_gain) && <div><div className="md-stat-label">Elevation</div><div className="md-stat-val">{data.elevation_gain ? Math.round(data.elevation_gain) : '—'}<span className="md-stat-unit">m</span></div></div>}
          {(!_isNPL || !!data.floors) && <div><div className="md-stat-label">Floors</div><div className="md-stat-val">{data.floors || '—'}</div></div>}
          {(!_isNPL || !!data.avg_speed) && <div><div className="md-stat-label">Avg Speed</div><div className="md-stat-val">{data.avg_speed || '—'}<span className="md-stat-unit">km/h</span></div></div>}
          {(!_isNPL || !!data.avg_power) && <div><div className="md-stat-label">Avg Power</div><div className="md-stat-val">{data.avg_power || '—'}<span className="md-stat-unit">W</span></div></div>}
          {(!_isNPL || !!data.vo2max) && <div><div className="md-stat-label">VO₂ Max</div><div className="md-stat-val">{data.vo2max || '—'}</div></div>}
        </div>

        {/* RUN11.7 §2 — zero-HR: honest message, no chart. Ungated from _isNPL so an empty
            series ALWAYS shows the empty state (never a blank gap) even if FEATURES_GLOBAL
            hasn't resolved — part of the P0 white-screen guard. */}
        {hr.length === 0 && (
          <div className="activity-section">
            <div className="activity-section-title">Heart Rate</div>
            <div className="metric-detail-empty">No heart-rate recorded for this activity.</div>
          </div>
        )}
        {/* RUN11.7 §2 — NPL sparse HR (1–19): show the REAL points as dots only, never a
            connected line that would imply a continuous trace. */}
        {_isNPL && hr.length > 0 && hr.length < HR_DENSE && (
          <div className="activity-section">
            <div className="activity-section-title">Heart Rate</div>
            <ResponsiveContainer width="100%" height={160}>
              <ComposedChart data={hrChart} margin={{ top: 8, right: 20, left: 0, bottom: 16 }}>
                <XAxis dataKey="elapsed" type="number" domain={[0, totalSeconds || 'auto']} ticks={hrTicks}
                  tickFormatter={fmtElapsed} tick={tickStyle} axisLine={false} tickLine={false} />
                <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={34} />
                <Line dataKey="bpm" stroke="none" dot={{ r: 3, fill: '#E16A8F' }} isAnimationActive={false} legendType="none" />
              </ComposedChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 6 }}>
              {hr.length} heart-rate reading{hr.length === 1 ? '' : 's'} recorded during this activity — too few for a continuous trace.
            </div>
          </div>
        )}
        {hr.length > 0 && (!_isNPL || hr.length >= HR_DENSE) && (() => {
          const zones = computeHRZones(maxHR);
          // (#1377) The two counting bands, by KEY not index, for the definition caption below.
          const zFat = zones.find(z => z.key === 'fatburn');
          const zCar = zones.find(z => z.key === 'cardio');
          // Time per HR sample in minutes (samples are evenly spaced through duration)
          const minutesPerSample = (data.duration_min || 0) / hr.length;
          // Bucket each sample into its zone
          const zoneMinutes = Object.fromEntries(zones.map(z => [z.key, 0]));
          for (const s of hr) {
            const bpm = s.bpm;
            const z = zones.find(z => bpm < z.hi) || zones[zones.length - 1];
            zoneMinutes[z.key] += minutesPerSample;
          }
          const totalMin = Object.values(zoneMinutes).reduce((a, b) => a + b, 0) || 1;
          const _ext = _finiteExtent(hr.map(s => s.bpm), 60, (maxHR || AZM_MAX_HR_FALLBACK));
          const yMin = Math.max(40, Math.floor(_ext.min / 10) * 10 - 10);
          const yMax = Math.max((maxHR || AZM_MAX_HR_FALLBACK) + 15, Math.ceil(_ext.max / 10) * 10 + 10);
          // Build zone-coloured line segments. We walk through the HR points
          // and split into runs of consecutive samples in the same zone, so
          // each run can be rendered as a separate <Line> coloured by zone.
          // Where the zone changes between adjacent samples, both runs include
          // the shared point so the line stays visually connected.
          const zoneFor = (bpm) => zones.find(z => bpm < z.hi) || zones[zones.length - 1];
          const segments = [];
          if (hrChart.length > 0) {
            let cur = { zoneKey: zoneFor(hrChart[0].bpm).key, color: zoneFor(hrChart[0].bpm).color, points: [hrChart[0]] };
            for (let i = 1; i < hrChart.length; i++) {
              const z = zoneFor(hrChart[i].bpm);
              if (z.key === cur.zoneKey) {
                cur.points.push(hrChart[i]);
              } else {
                cur.points.push(hrChart[i]);          // close current at the transition point
                segments.push(cur);
                cur = { zoneKey: z.key, color: z.color, points: [hrChart[i - 1], hrChart[i]] };
              }
            }
            segments.push(cur);
          }
          // Recharts plots Lines against the full chart `data` array using the
          // x-axis dataKey; to render a partial line we pass the full dataset
          // and null out bpm for points outside this segment.
          const segmentDatasets = segments.map((seg, idx) => {
            const elapsedSet = new Set(seg.points.map(p => p.elapsed));
            return {
              key: `${seg.zoneKey}-${idx}`,
              color: seg.color,
              data: hrChart.map(p => ({ elapsed: p.elapsed, bpm: elapsedSet.has(p.elapsed) ? p.bpm : null })),
            };
          });
          // Custom tooltip content — works on touch (Android Chrome / iOS
          // Safari) as well as mouse. Recharts triggers content render on
          // both hover and click; touch events fire 'click' on mobile so
          // the tooltip appears on tap. We render a single 'mm:ss · NNN bpm'
          // line and skip the segment's null entries (multiple overlapping
          // lines each contribute a payload row, most of them null).
          const HRTooltip = ({ active, payload, label }) => {
            if (!active || !Array.isArray(payload) || payload.length === 0) return null;
            const hit = payload.find(p => p && p.value != null);
            if (!hit) return null;
            const elapsed = hit.payload?.elapsed ?? label;
            return (
              <div style={{ ...tooltipStyle, padding: '6px 10px' }}>
                {fmtElapsed(elapsed)} · {hit.value} bpm
              </div>
            );
          };
          return (
            <div className="activity-section">
              <div className="activity-section-title">Heart Rate Zones</div>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={hrChart} margin={{ top: 8, right: 64, left: 0, bottom: 18 }}>
                  <XAxis
                    dataKey="elapsed"
                    type="number"
                    domain={[0, totalSeconds || 'auto']}
                    ticks={hrTicks}
                    tickFormatter={fmtElapsed}
                    tick={tickStyle}
                    axisLine={false}
                    tickLine={false}
                    label={{ value: 'Elapsed time', position: 'insideBottom', offset: -6, style: { fontSize: 10, fill: '#8B6914' } }}
                  />
                  <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={36} domain={[yMin, yMax]} />
                  {/* Zone background bands (15% opacity) — colours match the line segments. */}
                  {zones.map(z => (
                    <ReferenceArea key={`band-${z.key}`} y1={z.lo} y2={Math.min(z.hi, yMax)} fill={z.color} fillOpacity={0.15} ifOverflow="hidden" />
                  ))}
                  {/* Threshold lines with zone labels */}
                  {zones.filter(z => z.pctTop != null).map(z => (
                    <ReferenceLine key={`line-${z.key}`} y={z.hi} stroke={z.color} strokeWidth={1} strokeDasharray="3 3" label={{ value: `${z.label} ${z.hi}`, position: 'right', fill: z.color, fontSize: 10, fontWeight: 600 }} />
                  ))}
                  {/* Default trigger ('hover') — recharts forwards
                      touchstart/touchmove as synthetic mouse events on iOS
                      Safari and Android Chrome, so the tap-to-inspect flow
                      works without a custom touch handler. wrapperStyle's
                      pointer-events:none keeps the floating tooltip from
                      swallowing subsequent taps. */}
                  <Tooltip
                    content={<HRTooltip />}
                    cursor={{ stroke: '#8B6914', strokeWidth: 1, strokeDasharray: '3 3' }}
                    wrapperStyle={{ outline: 'none', pointerEvents: 'none' }}
                  />
                  {segmentDatasets.map(seg => (
                    <Line
                      key={seg.key}
                      data={seg.data}
                      type="monotone"
                      dataKey="bpm"
                      stroke={seg.color}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls={false}
                      legendType="none"
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>

              {/* Zone legend */}
              <div className="hr-zone-legend">
                {zones.map(z => (
                  <span key={z.key} className="hr-zone-chip">
                    <span className="hr-zone-dot" style={{ background: z.color }} />
                    {z.label} <span className="hr-zone-range">({z.lo}{z.pctTop != null ? `–${z.hi}` : '+'} bpm)</span>
                  </span>
                ))}
              </div>

              {/* Time in zone — horizontal stacked bar + breakdown */}
              <div className="hr-zone-bar-wrap">
                <div className="hr-zone-bar">
                  {zones.map(z => {
                    const pct = (zoneMinutes[z.key] / totalMin) * 100;
                    if (pct <= 0) return null;
                    return <div key={z.key} className="hr-zone-bar-seg" style={{ background: z.color, width: `${pct}%` }} title={`${z.label}: ${zoneMinutes[z.key].toFixed(1)} min`} />;
                  })}
                </div>
                <div className="hr-zone-breakdown">
                  {zones.map(z => (
                    <div key={z.key} className="hr-zone-row">
                      <span className="hr-zone-dot" style={{ background: z.color }} />
                      <span className="hr-zone-row-label">{z.label}</span>
                      <span className="hr-zone-row-min">{zoneMinutes[z.key] >= 0.1 ? zoneMinutes[z.key].toFixed(1) : '0'} min</span>
                    </div>
                  ))}
                </div>
                {/* (#1377) Which of these bands count, and how much — the engine's rule, stated
                    where the bands are shown instead of left for the user to infer. Boundaries are
                    read off the SAME `zones` array that drew the bars, so the caption cannot drift
                    from the chart, and it asserts no derivation for maxHR it can't see. */}
                {zFat && zCar && (
                  <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)', marginTop: 8 }}>
                    Toward Active Zone Minutes: Fat Burn {zFat.lo}–{zFat.hi - 1} bpm counts ×1
                    (moderate); Cardio and Peak {zCar.lo} bpm and above count ×2 (vigorous). Bands are
                    set from a maximum heart rate of {Math.round(maxHR)} bpm.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {FEATURES_GLOBAL.location && (
          <div className="activity-section">
            <div className="activity-section-title">Route</div>
            <_ActivityRouteMap gps={gps} distance={data.distance} distanceUnit={data.distance_unit} pace={pace} />
          </div>
        )}

        {pace != null && (
          <div className="activity-section">
            <div className="activity-section-title">Pace</div>
            <div className="md-stat-val" style={{ fontSize: '1.1rem' }}>
              {Math.floor(pace)}:{String(Math.round((pace - Math.floor(pace)) * 60)).padStart(2, '0')}
              <span className="md-stat-unit"> min/{data.distance_unit || 'km'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivitiesSection({ onSelect }) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const limit = showAll ? 200 : 10;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/fitness/activities?limit=${limit}&offset=0`)
      .then(d => { if (!alive) return; setList(d.activities || []); setTotal(d.total || 0); })
      .catch(() => { if (alive) setList([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [limit]);

  return (
    <>
      <div className="fitness-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span>Recent Activities</span>
        {total > 10 && (
          <button className="activities-see-all" onClick={() => setShowAll(s => !s)}>
            {showAll ? 'Show less' : `See all ${total}`}
          </button>
        )}
      </div>
      <div className="activities-list">
        {loading && <div className="activities-empty">Loading…</div>}
        {!loading && list.length === 0 && <div className="activities-empty">No activities recorded yet.</div>}
        {list.map(a => (
          <div key={a.log_id} className="activity-row fitness-clickable" onClick={() => onSelect(a.log_id)} role="button" tabIndex={0}>
            <span className="activity-icon">{activityIcon(a.activity_name)}</span>
            <div className="activity-main">
              <div className="activity-name">{a.activity_name}</div>
              <div className="activity-meta">{formatActivityDate(a.start_time || a.date)} · {formatDuration(a.duration_ms)}{a.distance != null ? ` · ${a.distance} ${a.distance_unit}` : ''}</div>
            </div>
            <div className="activity-cals">{a.calories ? `${a.calories} kcal` : ''}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// Trend card used inside the new Fitness page. Tiny shared wrapper so the
// 5 charts stay visually consistent — same height, same axis/grid styling,
// same empty state.
function FitTrendCard({ title, color, type, data, unit, yFmt, xFmt, tickStyle, tooltipStyle, onClick, anchorId }) {
  const clickProps = onClick ? { onClick, role: 'button', tabIndex: 0, style: { cursor: 'pointer' } } : {};
  // anchorId lets the Today feed deep-link + scroll to a specific trend
  // (e.g. #steps). scroll-margin keeps it clear of the sticky header.
  const idProps = anchorId ? { id: anchorId, style: { ...(clickProps.style || {}), scrollMarginTop: '80px' } } : {};
  if (!data || data.length === 0) {
    return (
      <div className="fit-chart-card" {...clickProps} {...idProps}>
        <div className="fit-chart-title">{title}</div>
        <div className="fit-chart-empty">No data for this period.</div>
      </div>
    );
  }
  return (
    <div className="fit-chart-card" {...clickProps} {...idProps}>
      <div className="fit-chart-title">{title} <span className="fit-chart-unit">{unit}</span></div>
      <ResponsiveContainer width="100%" height={150}>
        {type === 'bar' ? (
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <XAxis dataKey="date" tick={tickStyle} axisLine={false} tickLine={false} tickFormatter={xFmt} interval="preserveStartEnd" />
            <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={36} tickFormatter={yFmt} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => yFmt ? yFmt(v) : v} />
            <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <XAxis dataKey="date" tick={tickStyle} axisLine={false} tickLine={false} tickFormatter={xFmt} interval="preserveStartEnd" />
            <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={36} tickFormatter={yFmt} domain={['auto','auto']} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => yFmt ? yFmt(v) : v} />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.2} dot={data.length <= 30 ? { r: 2.5 } : false} activeDot={{ r: 5 }} />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// KalpaChat — Claude.ai-style chat (replaces LiveHealthPage + BrainChat)
// ═══════════════════════════════════════════════════════════════════════

// Timezone-aware timestamp helpers. UTC stays in storage; rendering shifts
// into the user's home timezone so BST/GMT swap automatically via Intl.
function tzDayKey(ts, tz) {
  // 'YYYY-MM-DD' for the given timestamp, evaluated in tz. Used to group
  // messages into day buckets without leaking 23:00 UTC into the wrong day.
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date(ts));
    const o = {};
    for (const p of parts) o[p.type] = p.value;
    return `${o.year}-${o.month}-${o.day}`;
  } catch { return new Date(ts).toISOString().slice(0, 10); }
}
function tzDayLabel(dayKey, tz) {
  const today = tzDayKey(new Date(), tz);
  if (dayKey === today) return 'Today';
  // Compute "yesterday" by walking back one day in the same tz.
  const y = new Date(Date.now() - 24 * 3600 * 1000);
  if (dayKey === tzDayKey(y, tz)) return 'Yesterday';
  // Build a Date at noon UTC for the day, then format in tz.
  const d = new Date(`${dayKey}T12:00:00Z`);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, day: 'numeric', month: 'short', year: 'numeric'
    }).format(d);
  } catch { return dayKey; }
}
function tzTime(ts, tz) {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false
    }).format(new Date(ts));
  } catch { return ''; }
}

// Parse [[term|definition]] tokens into segments. Returns an array of
// either plain strings or {term, def} objects.
function parseKalpaTerms(text) {
  if (!text) return [];
  const out = [];
  const re = /\[\[([^\]|]+)\|([^\]]+)\]\]/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push({ term: m[1].trim(), def: m[2].trim() });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function KalpaTerm({ term, def }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);
  // Position the popover with FIXED viewport coords computed from the term's rect at
  // open time — the old position:absolute inside the chat bubble could be clipped by a
  // scroll/overflow ancestor (so on native it read as "didn't open"). Mirrors GlossaryTerm.
  const openPop = useCallback(() => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const maxw = Math.min(280, window.innerWidth - 16);
    let left = r.left + r.width / 2 - maxw / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - maxw - 8));   // clamp to viewport
    const above = r.top > 160;                                          // flip below if no room
    setPos({ left, top: above ? Math.round(r.top - 8) : Math.round(r.bottom + 8), maxw, above });
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return undefined;
    // pointerdown (NOT mousedown — mousedown doesn't fire on touch, so the popover never
    // dismissed on native) + scroll/resize/Escape.
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return (
    <span className="kc-term-wrap" ref={ref}>
      <span className="kc-term" onClick={(e) => { e.stopPropagation(); open ? close() : openPop(); }} role="button" tabIndex={0}>{term}</span>
      {open && pos && ReactDOM.createPortal(
        // PORTAL to <body>: rendered inline, this position:fixed popover was
        // positioned relative to the nearest TRANSFORMED ancestor — the Ask Kalpa
        // drawer carries transform: translate(-50%, …) at ALL times (slide-up +
        // centring), so the fixed coords resolved against the drawer's offset and
        // the popover mis-positioned/overlapped (and, at z-index 50 < the drawer's
        // 1201, painted behind drawer chrome — read as "semi-transparent"). Mirrors
        // GlossaryTerm (App.js:1906). node.contains() below still works across the
        // portal boundary (native DOM on the real body-mounted node). Opaque
        // var(--surface) + zIndex 4000 (above the drawer) match the proven pattern.
        <span className="kc-term-pop" ref={popRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, right: 'auto', bottom: 'auto',
                   transform: pos.above ? 'translateY(-100%)' : 'none', maxWidth: pos.maxw,
                   zIndex: 4000, background: 'var(--surface, #112240)' }}>{def}</span>,
        document.body)}
    </span>
  );
}

// Inline span-only renderer. Handles **bold**, *italic*, and newlines via <br>.
// Never emits block-level elements, so tooltips can sit inline mid-sentence
// without forcing a line break.
function KalpaInline({ text }) {
  if (!text) return null;
  // Split on **bold** and *italic*, keeping the delimiters as tokens.
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return (
    <>
      {tokens.map((tok, ti) => {
        if (!tok) return null;
        let inner;
        if (tok.startsWith('**') && tok.endsWith('**')) {
          inner = <strong>{tok.slice(2, -2)}</strong>;
        } else if (tok.startsWith('*') && tok.endsWith('*') && tok.length > 2) {
          inner = <em>{tok.slice(1, -1)}</em>;
        } else {
          // Plain text: render with explicit <br> for newlines so paragraphs
          // break where the model intends, but stay inline-flow otherwise.
          const lines = tok.split('\n');
          inner = lines.map((ln, li) => (
            <React.Fragment key={li}>
              {li > 0 && <br />}
              {ln}
            </React.Fragment>
          ));
        }
        return <React.Fragment key={ti}>{inner}</React.Fragment>;
      })}
    </>
  );
}

// Hide half-written inline markup at the tail of a streaming buffer so the user
// never sees raw [[, ]], ** or code fragments flash before the closing token
// arrives. Each cut is at the buffer tail and self-heals once the rest streams.
function _sanitizeStreamingMarkup(safe) {
  // Unclosed wikilink: drop from the last "[[" that has no matching "]]".
  const li = safe.lastIndexOf('[[');
  if (li >= 0 && safe.indexOf(']]', li) === -1) safe = safe.slice(0, li);
  // Unclosed fenced code block (odd count of ```).
  if (((safe.match(/```/g) || []).length) % 2 === 1) safe = safe.slice(0, safe.lastIndexOf('```'));
  // Unclosed inline code span (odd count of `).
  if (((safe.match(/`/g) || []).length) % 2 === 1) safe = safe.slice(0, safe.lastIndexOf('`'));
  // Dangling bold marker (odd count of **) — drop the trailing unmatched run.
  const boldParts = safe.split('**');
  if (boldParts.length % 2 === 0) safe = boldParts.slice(0, -1).join('**');
  // Dangling italic marker (odd count of a lone *) — drop from the last one.
  if (((safe.match(/(?<!\*)\*(?!\*)/g) || []).length) % 2 === 1) {
    const idx = safe.lastIndexOf('*');
    if (idx >= 0) safe = safe.slice(0, idx);
  }
  return safe;
}

// Renders content with [[term|def]] replaced by tooltips and the rest as
// markdown. When mixed segments are present we MUST stay inline — block-level
// MarkdownContent around each text segment would force HRV/RHR/SpO2 onto
// separate lines.
function KalpaContent({ content, streaming }) {
  // OrderPromptCard confirm state (FIX 1) — lives here so the styled card renders
  // regardless of which text-render branch we take below.
  const [confirmingPrompt, setConfirmingPrompt] = useState(null);
  // For streaming, strip half-written tokens so we don't flash raw [[ ]]
  // and chart/image JSON. We strip complete blocks first, then strip any
  // ORPHAN start markers without a matching end — those would otherwise
  // bleed the raw JSON on screen until the end marker streams in.
  let safe = content || '';
  safe = safe.replace(/===IMAGE_START===[\s\S]*?===IMAGE_END===/g, '')
             .replace(/===CHART_START===[\s\S]*?===CHART_END===/g, '');
  if (streaming) {
    safe = safe.replace(/===IMAGE_START===[\s\S]*$/g, '')
               .replace(/===CHART_START===[\s\S]*$/g, '');
    safe = _sanitizeStreamingMarkup(safe);
  }
  // Render any pipe-less [[term]] as plain text so stray double-brackets never
  // leak (the tooltip form requires [[term|explanation]]).
  safe = safe.replace(/\[\[([^\]|]+)\]\]/g, '$1');
  // FIX 1: parse ORDER_PROMPT lines into the styled OrderPromptCard BEFORE
  // segmenting — the multi-segment (tooltip) branch below used to render the raw
  // "ORDER_PROMPT: …" token as plain text. While streaming, just hide the
  // (possibly partial) line; render the card only once the reply is complete.
  let orderPrompts = [];
  if (streaming) {
    safe = safe.replace(/^\s*ORDER_PROMPT:.*$/gim, '').replace(/\n{3,}/g, '\n\n');
  } else {
    const ex = extractOrderPrompts(safe);
    safe = ex.stripped;
    orderPrompts = ex.prompts;
  }
  const stripped = safe;
  const segments = parseKalpaTerms(stripped);
  const body = (segments.length === 1 && typeof segments[0] === 'string')
    ? <MarkdownContent content={segments[0]} />
    : (
      <span className="kc-rich">
        {segments.map((seg, i) =>
          typeof seg === 'string'
            ? <KalpaInline key={i} text={seg} />
            : <KalpaTerm key={i} term={seg.term} def={seg.def} />
        )}
      </span>
    );
  if (!orderPrompts.length) return body;
  return (
    <>
      {body}
      {orderPrompts.map(p => (
        <OrderPromptCard key={p.key} prompt={p}
          onDismiss={() => {/* ephemeral chat prompt */}}
          onOrder={() => setConfirmingPrompt(p)} />
      ))}
      {confirmingPrompt && (
        <OrderConfirmModal prompt={confirmingPrompt}
          onClose={() => setConfirmingPrompt(null)}
          onConfirmed={() => setConfirmingPrompt(null)} />
      )}
    </>
  );
}

// Real-time "show your working" status. `log` is the ordered list of status
// strings the BACKEND emitted as each pipeline stage actually ran (blood-marker
// counts, years of wearable data, medicine counts, etc.). We render the real
// progression — completed stages dimmed, the latest one active with a pulsing
// dot — and never rotate on a timer. Before the first backend status arrives we
// show only the pulsing dots (no invented text).
function KalpaStatus({ log }) {
  const stages = Array.isArray(log) ? log : [];
  if (stages.length === 0) {
    return (
      <div className="kc-status">
        <span className="kc-status-dots"><span/><span/><span/></span>
      </div>
    );
  }
  // Show the last few stages so the area feels alive but stays compact.
  const shown = stages.slice(-4);
  const hiddenCount = stages.length - shown.length;
  return (
    <div className="kc-status kc-status-log">
      {shown.map((text, i) => {
        const isLast = i === shown.length - 1;
        return (
          <div key={hiddenCount + i} className={`kc-status-line ${isLast ? 'active' : 'done'}`}>
            {isLast
              ? <span className="kc-status-dots"><span/><span/><span/></span>
              : <span className="kc-status-tick" aria-hidden="true">✓</span>}
            <span className="kc-status-text">{text}</span>
          </div>
        );
      })}
    </div>
  );
}

function KalpaSidebar({ conversations, activeId, onSelect, onNew, onDelete, collapsed, onToggle }) {
  return (
    <aside className={`kc-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="kc-sidebar-head">
        <button className="kc-toggle" onClick={onToggle} title={collapsed ? 'Show chats' : 'Hide chats'}>
          {collapsed ? '☰' : '✕'}
        </button>
        {!collapsed && (
          <button className="kc-new" onClick={onNew}>
            <span style={{fontSize:'1.05rem',marginRight:6}}>+</span> New chat
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="kc-conv-list">
          {conversations.length === 0 && <div className="kc-conv-empty">No chats yet</div>}
          {conversations.map(c => {
            const title = c.title || (c.first_message ? c.first_message.slice(0, 38) : 'New chat');
            return (
              <div
                key={c.id}
                className={`kc-conv-item ${c.id === activeId ? 'active' : ''}`}
                onClick={() => onSelect(c.id)}
              >
                <div className="kc-conv-title">{title}</div>
                <button
                  className="kc-conv-del"
                  onClick={e => { e.stopPropagation(); onDelete(c.id); }}
                  title="Delete chat"
                >×</button>
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}

function KalpaAvatar() {
  return (
    <div className="kc-avatar">
      <img src="/kalpa_mark.png?v=3" alt="Kalpa" />
    </div>
  );
}

function KalpaMessageRow({ msg, tz, showTime, queryType }) {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';
  if (isSystem) {
    // Surface live_health_cards as small Kalpa-initiated insight cards.
    const card = msg.card || {};
    return (
      <div className="kc-row kc-row-system">
        <KalpaAvatar />
        <div className="kc-insight">
          <div className="kc-insight-head">
            <span className="kc-insight-spark">{card.icon || '✨'}</span>
            <span className="kc-insight-title">{card.headline || msg.content}</span>
          </div>
          {card.insight && <div className="kc-insight-body"><KalpaContent content={deIsoDates(card.insight)} /></div>}
          {card.data_sources && <div className="kc-insight-sources">Sources: {deIsoDates(card.data_sources)}</div>}
          {card.longevity_pts > 0 && FEATURES_GLOBAL.gamification && <span className="kc-insight-pts">+{card.longevity_pts} pts</span>}
        </div>
      </div>
    );
  }
  // While the assistant is streaming and no text has arrived yet, render the
  // KalpaStatus thinking sequence INSIDE this same bubble. Without this we
  // get two bubbles on screen — an empty cursor stub plus a separate status
  // row — which looks broken.
  const showThinking = !isUser && msg.streaming && !msg.content;
  return (
    <div className={`kc-row ${isUser ? 'kc-row-user' : 'kc-row-ai'}`}>
      {!isUser && <KalpaAvatar />}
      <div className={`kc-bubble ${isUser ? 'kc-bubble-user' : 'kc-bubble-ai'}`}>
        {isUser && Array.isArray(msg.imagePreviews) && msg.imagePreviews.length > 0 && (
          <div className="kc-user-images">
            {msg.imagePreviews.map((src, i) => (
              <img key={i} src={src} alt="" className="kc-user-image" />
            ))}
          </div>
        )}
        {isUser && msg.imagePreview && !msg.imagePreviews && (
          <img src={msg.imagePreview} alt="" className="kc-user-image" />
        )}
        {isUser
          ? <span className="kc-user-text">{msg.content}</span>
          : showThinking
            ? <KalpaStatus log={msg.statusLog} />
            : <KalpaContent content={deIsoDates(msg.content)} streaming={msg.streaming} />}
        {msg.charts && msg.charts.map((c, i) => <InlineChart key={i} chart={c} />)}
        {/* Image generation disabled — skip msg.images entirely. */}
        {msg.files && msg.files.length > 0 && !msg.imagePreview && !msg.imagePreviews && (
          <div className="kc-files">
            {msg.files.map((f, i) => <span key={i} className="kc-file-chip">{'📄'} {f}</span>)}
          </div>
        )}
        {msg.streaming && msg.content && <span className="kc-cursor">|</span>}
        {!isUser && !msg.streaming && msg.content && (
          <div style={{marginTop:6}}><AiThumbs surface="ask_kalpa" contextRef={msg.id} /></div>
        )}
        {msg.timestamp && !msg.streaming && showTime && (
          <div className="kc-time">{tzTime(msg.timestamp, tz)}</div>
        )}
      </div>
    </div>
  );
}

function KalpaWelcome({ onHint, cards }) {
  // Each hint declares the feature flag it depends on. When the flag is
  // off for this instance, the hint is filtered out — so the NPL pilot
  // (genetics=false) doesn't show "Genetic risks" as a starter, and any
  // instance with insurance=false hides insurance-tied starters.
  const isNPL = FEATURES_GLOBAL.branding.instance === 'npl';
  const logoSrc = isNPL ? (FEATURES_GLOBAL.branding.logo_url || '/static/npl_logo.png') : '/kalpa_logo.png';
  const appName = isNPL ? (FEATURES_GLOBAL.branding.app_name || 'NPL Health') : 'Kalpa';
  const subtitleText = isNPL
    ? 'Ask anything about your blood markers, fitness, or medicines.'
    : 'Ask anything about your blood markers, fitness, medicines, or genetics.';
  const allHints = [
    { icon: '🧪', label: 'Latest blood results', q: T('Summarise my latest blood results in plain English'), requires: null },
    { icon: '💊', label: 'Medicine check',       q: 'Are there any concerns with my current medicines?', requires: null },
    { icon: '💓', label: 'Heart trend',          q: 'How has my resting heart rate trended over the last 90 days?', requires: null },
    { icon: '🧬', label: 'Genetic risks',        q: 'What genetic risks should I be aware of?', requires: 'genetics' },
  ];
  const hints = allHints.filter(h => !h.requires || FEATURES_GLOBAL[h.requires]);
  return (
    <div className="kc-welcome">
      <div className="kc-welcome-logo">
        <img src={logoSrc} alt={appName} style={{height:'40px',width:'auto',objectFit:'contain'}} />
      </div>
      <h1 className="kc-welcome-title">How can I help with your health today?</h1>
      <p className="kc-welcome-sub">{subtitleText}</p>
      <div className="kc-welcome-hints">
        {hints.map((h, i) => (
          <button key={i} className="kc-hint" onClick={() => onHint(h.q)}>
            <span className="kc-hint-icon">{h.icon}</span>
            <span>{h.label}</span>
          </button>
        ))}
      </div>
      {cards && cards.length > 0 && (
        <div className="kc-daily">
          <div className="kc-daily-head">Your daily insights</div>
          {cards.slice(0, 3).map(c => (
            <div key={c.id} className="kc-daily-card" onClick={() => onHint(`Tell me more about: ${c.headline}`)}>
              <div className="kc-daily-icon">{c.icon || '✨'}</div>
              <div className="kc-daily-body">
                <div className="kc-daily-headline">{deIsoDates(c.headline)}</div>
                <div className="kc-daily-insight">{deIsoDates(c.insight)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Classify a query into a coarse type (blood/medicines/fitness/genetics/
// general). Retained for the backend meta event's query_type fallback.
function clientClassify(q) {
  const s = (q || '').toLowerCase();
  if (/blood|cholesterol|hba1c|ldl|hdl|triglycer|hemoglobin|vitamin d|iron|ferritin|crp|psa|tsh/.test(s)) return 'blood';
  if (/medicine|medication|drug|tablet|capsule|tamsulosin|statin|metformin|interact/.test(s)) return 'medicines';
  if (/sleep|step|hr |resting heart|hrv|spo2|fitness|exercise|cardio|vo2|workout/.test(s)) return 'fitness';
  if (/gene|genetic|variant|rs\d|snp|dna|allele|methyl|apoe|mthfr/.test(s)) return 'genetics';
  return 'general';
}

function KalpaChat({ prefill, tz = 'UTC' }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [queryType, setQueryType] = useState('general');
  const [showStatus, setShowStatus] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  // In-flight image attachments (camera/photo) that pair with a query and route
  // to /chat/upload (Gemini Vision) on Send. Documents (PDF/CSV) still go
  // through the immediate-upload path via uploadedFiles. Max 2 images per
  // message — see MAX_CHAT_IMAGES.
  const [pendingImages, setPendingImages] = useState([]); // [{file, name, previewUrl}]
  const [attachError, setAttachError] = useState(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  const [insightCards, setInsightCards] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const atBottomRef = useRef(true);
  // True while a stream is in flight. Used to suppress the messages-reload
  // effect when the stream's meta event flips activeConvId from null to a
  // newly-created conversation id (which would otherwise clobber the
  // optimistic user + assistant-placeholder bubbles).
  const streamingRef = useRef(false);

  // Apply prefill from "Ask AI" navigation. No deps array on purpose — the
  // ref is set just before navigation, so we check on every render to catch
  // it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (prefill && prefill.current) {
      setInput(prefill.current);
      prefill.current = null;
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  });

  const loadConversations = useCallback(async () => {
    try { setConversations(await api.get('/conversations')); }
    catch (e) { console.error('Failed to load conversations:', e); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Pull live_health_cards once for the welcome surface
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/live-health/feed');
        if (r && r.cards) setInsightCards(r.cards);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Load messages for active conversation. Skip when a stream is in flight —
  // the meta event sets activeConvId to a freshly-created id, and reloading
  // here would replace the optimistic user+placeholder bubbles with the
  // server-side state (which doesn't yet contain the streaming reply).
  useEffect(() => {
    if (!activeConvId) { setMessages([]); return; }
    if (streamingRef.current) return;
    (async () => {
      try {
        const msgs = await api.get(`/conversations/${activeConvId}/messages?limit=100`);
        setMessages(msgs);
        setTimeout(() => { const c = messagesContainerRef.current; if (c) c.scrollTop = c.scrollHeight; }, 50);
      } catch (e) { console.error('Load msgs failed:', e); setMessages([]); }
    })();
  }, [activeConvId]);

  const handleContainerScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    if (atBottomRef.current) {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const handleNewChat = () => {
    setActiveConvId(null);
    setMessages([]);
    setInput('');
    setUploadedFiles([]);
    setUploadedDocs([]);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleDeleteConv = async (id) => {
    try {
      await api.del(`/conversations/${id}`);
      if (activeConvId === id) { setActiveConvId(null); setMessages([]); }
      loadConversations();
    } catch (e) { console.error('Delete failed:', e); }
  };

  const MAX_CHAT_IMAGES = 2;

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setAttachError(null);
    // Snapshot current count so we can decide admission without setState races
    let imageCount = pendingImages.length;
    let rejected = false;
    const toAdd = [];
    for (const file of files) {
      const safeName = (file.name && file.name.trim()) || (file.type && file.type.startsWith('image/') ? 'photo.jpg' : 'file');
      const isImage = (file.type || '').startsWith('image/') || /\.(jpe?g|png|heic|heif|webp|gif)$/i.test(safeName);

      if (isImage) {
        if (imageCount >= MAX_CHAT_IMAGES) {
          rejected = true;
          continue;
        }
        let previewUrl = null;
        try { previewUrl = URL.createObjectURL(file); } catch {}
        toAdd.push({ file, name: safeName, previewUrl });
        imageCount += 1;
      } else {
        // Documents: immediate upload (existing behaviour).
        const tempId = Date.now() + '_' + safeName;
        setUploadedFiles(prev => [...prev, { name: safeName, tempId, uploading: true }]);
        const formData = new FormData();
        formData.append('file', file);
        try {
          const result = await api.upload('/chat/upload', formData);
          setUploadedFiles(prev => prev.map(f => f.tempId === tempId ? { name: safeName, ...result, uploading: false } : f));
          setUploadedDocs(prev => [...prev, result]);
        } catch (error) {
          setUploadedFiles(prev => prev.map(f => f.tempId === tempId ? { name: safeName, uploading: false, error: error.message } : f));
        }
      }
    }
    if (toAdd.length > 0) setPendingImages(prev => [...prev, ...toAdd]);
    if (rejected) setAttachError(`Maximum ${MAX_CHAT_IMAGES} images per message. Remove an existing image to add another.`);
    [fileInputRef, cameraInputRef, photoInputRef].forEach(r => { if (r.current) r.current.value = ''; });
  };

  const removePendingImage = (index) => {
    setPendingImages(prev => {
      const removed = prev[index];
      if (removed?.previewUrl) { try { URL.revokeObjectURL(removed.previewUrl); } catch {} }
      return prev.filter((_, i) => i !== index);
    });
    setAttachError(null);
  };

  const isUploading = uploadedFiles.some(f => f.uploading);

  const handleSend = async () => {
    if ((!input.trim() && uploadedFiles.length === 0 && pendingImages.length === 0) || isStreaming) return;
    const text = input.trim();

    // ── Branch: image(s) + question → /chat/upload (Gemini Vision) ─────────
    if (pendingImages.length > 0) {
      const imgs = pendingImages;
      const userMsg = {
        role: 'user',
        content: text,
        files: imgs.map(i => i.name),
        imagePreviews: imgs.map(i => i.previewUrl).filter(Boolean),
        imagePreview: imgs[0]?.previewUrl,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMsg]);
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      // Don't revoke previews yet — message bubble references them
      setPendingImages([]);
      setAttachError(null);

      const placeholderId = `vision-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: placeholderId, role: 'assistant', content: '', streaming: true,
        timestamp: new Date().toISOString(),
      }]);
      setIsStreaming(true);
      setShowStatus(true);
      try {
        const fd = new FormData();
        imgs.forEach(img => fd.append('file', img.file, img.name || 'photo.jpg'));
        if (text) fd.append('query', text);
        const resp = await fetch(`${API_BASE}/chat/upload`, {
          method: 'POST', body: fd, credentials: 'include',
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data.error) {
          const errText = (data && data.error) ? data.error : `Upload failed (HTTP ${resp.status})`;
          setMessages(prev => prev.map(m => m.id === placeholderId ? {
            ...m, content: `Sorry — I couldn't analyse that image: ${errText}`, streaming: false,
          } : m));
        } else {
          const analysis = data.analysis || data.summary || '';
          setMessages(prev => prev.map(m => m.id === placeholderId ? {
            ...m, content: analysis || 'No analysis returned.', streaming: false,
          } : m));
        }
      } catch (err) {
        setMessages(prev => prev.map(m => m.id === placeholderId ? {
          ...m, content: `Sorry — image upload failed: ${err.message}`, streaming: false,
        } : m));
      } finally {
        setIsStreaming(false);
        setShowStatus(false);
      }
      return;
    }

    const currentDocs = [...uploadedDocs];
    const currentFiles = uploadedFiles.map(f => f.name);
    const guess = clientClassify(text);
    setQueryType(guess);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setUploadedFiles([]);
    setUploadedDocs([]);

    setMessages(prev => [...prev, {
      role: 'user', content: text, files: currentFiles, timestamp: new Date().toISOString()
    }]);

    setIsStreaming(true);
    setShowStatus(true);
    streamingRef.current = true;
    const streamId = `stream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages(prev => [...prev, {
      id: streamId, role: 'assistant', content: '', streaming: true, timestamp: new Date().toISOString()
    }]);

    // Track whether the SSE stream actually delivered content. If the stream
    // closes (either normally or via timeout) without text and without a
    // `done` event, we fall back to the non-streaming /chat endpoint so the
    // first message in a fresh chat never silently produces an empty bubble.
    let gotAnyText = false;
    let gotDone = false;
    let streamedText = '';
    const abortCtl = new AbortController();
    // 60s hard timeout — typical first-token < 3s, full reply < 30s.
    const abortTimer = setTimeout(() => abortCtl.abort(), 60000);

    const FALLBACK_MSG = "Sorry, I couldn't generate a reply this time. Please send that again.";

    try {
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: text,
          includeContext: true,
          uploaded_docs: currentDocs,
          files: currentFiles,
          conversation_id: activeConvId || null,
          timezone: clientTimezone(),
        }),
        credentials: 'include',
        signal: abortCtl.signal,
      });
      if (!resp.ok) throw new Error(`API ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'meta') {
            if (evt.conversation_id) setActiveConvId(evt.conversation_id);
            if (evt.query_type) setQueryType(evt.query_type);
          } else if (evt.type === 'status') {
            // Real backend pipeline stage — append to this message's log so the
            // user sees genuine progression through their data.
            setMessages(prev => prev.map(m => m.id === streamId
              ? { ...m, statusLog: [...(m.statusLog || []), evt.text] } : m));
          } else if (evt.type === 'text') {
            gotAnyText = true;
            if (showStatus) setShowStatus(false);
            streamedText += evt.content;
            const snap = streamedText;
            setMessages(prev => prev.map(m => m.id === streamId ? { ...m, content: snap } : m));
          } else if (evt.type === 'done') {
            gotDone = true;
            const final = (evt.clean_text || streamedText || '').trim();
            setMessages(prev => prev.map(m => m.id === streamId ? {
              ...m, content: final || FALLBACK_MSG,
              charts: evt.charts || [], images: evt.images || [], streaming: false
            } : m));
            if (evt.conversation_id) setActiveConvId(evt.conversation_id);
            loadConversations();
          } else if (evt.type === 'error') {
            setMessages(prev => prev.map(m => m.id === streamId ? {
              ...m, content: `Sorry, an error occurred: ${evt.message}`, streaming: false
            } : m));
          }
        }
      }
      // Reader closed. If we never received text AND never saw a `done`
      // event, the SSE connection terminated empty — try the non-streaming
      // fallback so the user gets *some* reply on the first message.
      if (!gotAnyText && !gotDone) {
        console.warn('SSE closed without text or done — falling back to /chat');
        throw new Error('Empty SSE stream');
      }
    } catch (err) {
      // If the user-visible bubble already got rendered with text we don't
      // overwrite it. Otherwise, retry via the non-streaming endpoint.
      if (gotAnyText) {
        setMessages(prev => prev.map(m => m.id === streamId ? { ...m, streaming: false } : m));
      } else {
        try {
          const r = await _chatFallbackPost({   // KAL-366 — 90 s, not api.post's 20 s
            query: text, includeContext: true,
            uploaded_docs: currentDocs, files: currentFiles,
            conversation_id: activeConvId || null,
            timezone: clientTimezone(),
          });
          if (r.conversation_id) setActiveConvId(r.conversation_id);
          setMessages(prev => prev.map(m => m.id === streamId ? {
            ...m, content: (r.response || '').trim() || FALLBACK_MSG,
            charts: r.charts || [], images: r.images || [], streaming: false
          } : m));
          loadConversations();
        } catch (e2) {
          setMessages(prev => prev.map(m => m.id === streamId ? {
            ...m, content: FALLBACK_MSG, streaming: false
          } : m));
        }
      }
    } finally {
      clearTimeout(abortTimer);
      setIsStreaming(false);
      setShowStatus(false);
      streamingRef.current = false;
    }
  };

  return (
    <div className="kc-shell">
      <KalpaSidebar
        conversations={conversations}
        activeId={activeConvId}
        onSelect={(id) => setActiveConvId(id)}
        onNew={handleNewChat}
        onDelete={handleDeleteConv}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
      />
      <main className="kc-main">
        {sidebarCollapsed && (
          <button className="kc-mobile-toggle" onClick={() => setSidebarCollapsed(false)} title="Show chats">&#9776;</button>
        )}
        <div className="kc-thread" ref={messagesContainerRef} onScroll={handleContainerScroll}>
          {messages.length === 0 && (
            <KalpaWelcome
              cards={insightCards}
              onHint={q => { setInput(q); setTimeout(() => textareaRef.current?.focus(), 50); }}
            />
          )}
          {(() => {
            // Render messages with day separators (Today / Yesterday / DD MMM
            // YYYY in user's tz) and inline HH:mm only on the last message in
            // a consecutive sender group, or where the gap to the next
            // message exceeds 5 minutes.
            const out = [];
            let prevDayKey = null;
            for (let i = 0; i < messages.length; i++) {
              const m = messages[i];
              const next = messages[i + 1];
              const dayKey = m.timestamp ? tzDayKey(m.timestamp, tz) : null;
              if (dayKey && dayKey !== prevDayKey) {
                out.push(
                  <div key={`sep-${dayKey}-${i}`} className="kc-day-sep">
                    <span>{tzDayLabel(dayKey, tz)}</span>
                  </div>
                );
                prevDayKey = dayKey;
              }
              let showTime = true;
              if (next && m.timestamp && next.timestamp) {
                const sameSender = next.role === m.role;
                const gapMs = new Date(next.timestamp).getTime() - new Date(m.timestamp).getTime();
                if (sameSender && gapMs <= 5 * 60 * 1000) showTime = false;
              }
              out.push(<KalpaMessageRow key={m.id || i} msg={m} tz={tz} showTime={showTime} queryType={queryType} />);
            }
            return out;
          })()}
          <div ref={messagesEndRef} />
        </div>

        <div className="kc-composer">
          {(uploadedFiles.length > 0 || pendingImages.length > 0 || attachError) && (
            <div className="kc-files-preview">
              {pendingImages.map((img, idx) => (
                <div key={`img-${idx}`} className="kc-file-pill kc-image-pill" title={img.name}>
                  {img.previewUrl
                    ? <img src={img.previewUrl} alt="" className="kc-image-thumb" />
                    : <span>🖼️</span>}
                  <span className="kc-file-name">{img.name}</span>
                  <button onClick={() => removePendingImage(idx)} aria-label="Remove image">×</button>
                </div>
              ))}
              {uploadedFiles.map((f, i) => (
                <div key={f.tempId || i} className={`kc-file-pill ${f.error ? 'err' : ''}`}>
                  <span>{f.uploading ? '⏳' : f.error ? '⚠' : '📄'}</span>
                  <span className="kc-file-name">{f.name}</span>
                  <button onClick={() => {
                    setUploadedFiles(prev => prev.filter((_, idx) => idx !== i));
                    setUploadedDocs(prev => prev.filter((_, idx) => idx !== i));
                  }}>×</button>
                </div>
              ))}
              {attachError && (
                <div className="kc-attach-error" role="alert">{attachError}</div>
              )}
            </div>
          )}
          <div className="kc-input-bar">
            <input type="file" ref={cameraInputRef} onChange={handleFileUpload} accept="image/*" capture="environment" style={{display:'none'}} />
            <input type="file" ref={photoInputRef} onChange={handleFileUpload} multiple accept="image/*" style={{display:'none'}} />
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple accept=".pdf,.doc,.docx,.csv,.txt,image/*" style={{display:'none'}} />
            <div className="kc-attach-wrap">
              <button className="kc-attach" onClick={() => setShowAttachMenu(p => !p)} title="Attach">{'📎'}</button>
              {showAttachMenu && (
                <div className="kc-attach-menu" onClick={() => setShowAttachMenu(false)}>
                  <button onClick={() => photoInputRef.current?.click()}><span>{'🖼️'}</span> Photo Library</button>
                  <button onClick={() => cameraInputRef.current?.click()}><span>{'📷'}</span> Camera</button>
                  <button onClick={() => fileInputRef.current?.click()}><span>{'📄'}</span> File / Document</button>
                </div>
              )}
            </div>
            <textarea
              ref={textareaRef}
              className="kc-textarea"
              value={input}
              onChange={e => { setInput(e.target.value); autoGrowTextarea(e.target); }}
              onKeyDown={e => {
                // Enter SENDS (like the Claude chat app); Shift+Enter = newline.
                // Don't submit mid-IME-composition (e.g. Nepali input).
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
                  e.preventDefault();
                  if (!isStreaming && !isUploading && (input.trim() || uploadedFiles.length > 0)) handleSend();
                }
              }}
              placeholder="Ask me about your health…  (Enter to send, Shift+Enter for a new line)"
              rows={1}
              disabled={isStreaming}
            />
            <button
              className="kc-send"
              onClick={handleSend}
              disabled={isStreaming || isUploading || (!input.trim() && uploadedFiles.length === 0)}
              title="Send"
            >&#8593;</button>
          </div>
          {FEATURES_GLOBAL.nepali_language && <NepaliToggleButton />}
          <div className="kc-disclaimer">Kalpa provides wellness insights, not medical advice.</div>
        </div>
      </main>
    </div>
  );
}


// Tab/view IDs that the sidebar nav can switch between. Anything not in
// this set is invalid as a top-level view.
const KNOWN_VIEWS = new Set([
  // TODAY feed — the new default landing view on kalpa.health, replaces
  // chat-as-home. Renders TodayFeed at the root URL when present.
  'today',
  'longevity', 'vitals', 'genome', 'timeline',
  'medicines', 'vault', 'privacy', 'profile',
  // KAL-083 — the monthly cycle report. Without this the route silently no-ops:
  // onPop/onHash both drop any view not in this set, so the card's own link did nothing.
  'cycle-report',
  // Underwriting plane (Section 4) — insurance_export flag + superadmin only. The
  // component itself renders nothing unless the flag is on, and every figure comes
  // from server routes that re-check the flag, the role AND the subject's consent.
  'underwriting',
  // 'corporate'/'compliance' live in the avatar menu; NPL adds its own
  // corporate dashboard ('npl-corporate') and keeps the legacy 'chat' route.
  'corporate', 'compliance', 'chat',
  'npl-corporate', 'staff-lab-upload',
  // Ops Console — superadmin-only continuous-improvement console (NPL).
  // Route-guarded by OpsConsoleGuard; non-superadmins land on Today.
  'ops-console',
  // User Management — admin/superadmin roster + add/edit/deactivate; role +
  // Google allow-list management is superadmin-only (server-enforced).
  'user-management',
  // Longevity Flags View — flat list of flagged-marker action cards
  // triggered from the ⚑ button in Health Record. Has its own URL
  // (#longevity-flags) so reload + browser-back work the same way as
  // other top-level views.
  'longevity-flags',
  // KAL-235 — Bimal's Order Management screen (NPL local-admin). Its own URL so reload and
  // browser-back behave like every other top-level view.
  'order-management',
]);
// Legacy / alias hashes that no longer own a page. #home is the old
// chat-as-home landing; #chat and #assistant were the dedicated chat page,
// now retired in favour of the Ask Kalpa slide-up drawer. All resolve to the
// TODAY feed; the chat aliases additionally request the drawer be opened.
const CHAT_ALIASES = new Set(['chat', 'assistant']);
// Fixed (non-parameterised) modal sentinels. Dynamic ones — e.g.
// '#activity-{logId}' — are matched by prefix in isModalHash() so that a
// deep link can encode the specific record to reopen.
const FIXED_MODAL_HASHES = new Set([
  '#ecg-detail', '#eda-detail',
  '#finding-drawer', '#npl-order', '#bio-method', '#notifications',
  // Documents file-preview overlay (Phase A) — a nested sub-screen inside the
  // #vault view. Routing it through history means Back closes the preview and
  // returns to the Documents list, instead of popping the #vault entry and
  // skipping straight back to Health Record.
  '#vault-doc',
  // Phase: Class-C overlays brought under the one history model so back closes
  // them instead of skipping to the page beneath / exiting the app.
  '#ask-kalpa',      // Ask Kalpa slide-up drawer (global)
  '#marker-detail',  // blood-marker drill-down (Health Record)
  '#metric-detail',  // Fitbit metric drill-down (Fitness)
  '#npl-consent',    // NPL consent screen RE-OPENED from the deferred banner
]);
function isModalHash(hash) {
  if (!hash) return false;
  if (FIXED_MODAL_HASHES.has(hash)) return true;
  // Dynamic patterns — extend this list as more deep-linkable modals get
  // added. The leading '#activity-' includes the log ID so the page can
  // reopen the exact activity on refresh.
  if (/^#activity-/.test(hash)) return true;
  return false;
}
// For a modal hash, which top-level view should be active behind the modal?
// Used when the page loads directly into a modal hash on refresh so we
// pick the right parent tab + render the right page component.
function parentViewForModalHash(hash) {
  if (/^#activity-/.test(hash))             return 'vitals';
  if (hash === '#ecg-detail')               return 'vitals';
  if (hash === '#eda-detail')               return 'vitals';
  if (hash === '#finding-drawer')           return 'genome';
  if (hash === '#npl-order')                return 'timeline';
  if (hash === '#bio-method')               return 'longevity';
  if (hash === '#vault-doc')                return 'vault';
  if (hash === '#marker-detail')            return 'timeline';
  if (hash === '#metric-detail')            return 'vitals';
  // '#notifications', '#ask-kalpa' and '#npl-consent' are global overlays —
  // no specific parent view.
  return null;
}

function App() {
  // Resolve initial view from OAuth redirect (?view=), hash, or 'home'.
  // If the URL hash is a modal sentinel (refresh while a deep-linkable
  // modal was open), pick the modal's parent view so the right tab is
  // active behind the modal.
  const urlParams = new URLSearchParams(window.location.search);
  const rawHash = window.location.hash;
  // Strip any ?section=… query off the hash (e.g. #vitals?section=sleep) so
  // the bare view id resolves; FitnessPage reads the section from the hash.
  const rawInitialView = urlParams.get('view') || rawHash.slice(1).split('?')[0] || 'today';
  let initialView;
  if (rawInitialView === 'home' || CHAT_ALIASES.has(rawInitialView)) {
    // Legacy chat-as-home / dedicated chat routes now land on TODAY; the
    // chat aliases pop the Ask Kalpa drawer (handled below).
    initialView = 'today';
  } else if (KNOWN_VIEWS.has(rawInitialView)) {
    initialView = rawInitialView;
  } else if (isModalHash(rawHash)) {
    initialView = parentViewForModalHash(rawHash) || 'today';
  } else {
    initialView = 'today';
  }
  const [currentView, setCurrentView] = useState(initialView);
  // Ask Kalpa slide-up drawer — opens immediately if the user deep-linked to
  // a retired chat route (#chat / #assistant).
  const [chatDrawerOpen, setChatDrawerOpen] = useState(CHAT_ALIASES.has(rawInitialView));
  // Phase 2 metric/insight/longevity drawer — App-level state (NOT a route).
  // A Today card tap (or Fitness card tap) opens it as an overlay; the drawer
  // pushes a single #today-drawer history entry so browser/Android back closes
  // it and returns to Today. null = closed. Mounts fresh each open.
  const [drawer, setDrawer] = useState(null);
  const openDrawer = useCallback((spec) => setDrawer(spec), []);
  const closeDrawer = useCallback(() => setDrawer(null), []);
  // Deliberate page navigation (sidebar / bottom bar / account menu / the one
  // permitted Longevity escape from a drawer): clear any open drawer first so
  // it doesn't linger over the new page, then switch views.
  const navTo = useCallback((view) => { setDrawer(null); setCurrentView(view); }, []);

  // Part 3 returnToDrawer: when the user opens the full Longevity page from the
  // Longevity drawer, remember the drawer spec so pressing back returns to
  // Today with the same drawer re-opened. The ref is only set by this one
  // path and cleared on consume, so it can never affect ordinary navigation.
  const longevityReturnRef = useRef(null);
  const openLongevityFromDrawer = useCallback((spec) => {
    longevityReturnRef.current = spec;
    setDrawer(null);
    setCurrentView('longevity');
  }, []);

  // Native sync bridge. v26 has two deliberately distinct calls:
  //   __kalpaNativeSyncFinished(detail) — terminal ACK for an interactive waiter.
  //   __kalpaNativeDataUpdated()         — data-only historical/backfill refresh.
  // Keeping those separate prevents a backfill from falsely completing force_sync, while the
  // legacy __kalpaRefreshFitness alias remains data-only and cannot start another native run.
  useEffect(() => {
    window.__kalpaNativeSyncFinished = (detail, lastSynced, errorCode) => {
      const terminal = _normaliseNativeFinished(detail, lastSynced, errorCode);
      if (terminal.status === 'ready') usageAnalytics.track('fitness_synced', { source: 'native' });
      _handleNativeFinished(terminal);
    };
    window.__kalpaNativeSyncComplete = (completedAt) => {
      window.__kalpaNativeSyncFinished({ status: 'ready', lastSynced: completedAt, source: 'compat-complete' });
    };
    window.__kalpaNativeDataUpdated = () => {
      usageAnalytics.track('fitness_synced', { source: 'native_backfill' });
      void _refreshDataAndReceipt('native data update', {
        terminal: !_syncInFlight,
        dataEvent: 'kalpa:native-data-updated',
        receiptFirst: true,
      });
    };
    window.__kalpaRefreshFitness = () => {
      _handleLegacyNativeDataUpdate();
    };
    // v12 native emits explicit sync-lifecycle so the strip mirrors the real phase:
    // __kalpaSyncStatus('syncing'|'loading'|'ready'|'error', lastSyncedISO?).
    window.__kalpaSyncStatus = _acceptNativeLifecycle;
    window.addEventListener('kalpa:sync-progress-native', _acceptNativeProgress);
    return () => {
      window.removeEventListener('kalpa:sync-progress-native', _acceptNativeProgress);
      delete window.__kalpaNativeSyncFinished;
      delete window.__kalpaNativeSyncComplete;
      delete window.__kalpaNativeDataUpdated;
      delete window.__kalpaRefreshFitness;
      delete window.__kalpaSyncStatus;
    };
  }, []);
  useEffect(() => {
    const onPop = () => {
      if (!longevityReturnRef.current) return;
      const h = window.location.hash;
      if (isModalHash(h)) return;
      const raw = (h ? h.slice(1) : 'today').split('?')[0];
      if (raw === '' || raw === 'today') {
        const spec = longevityReturnRef.current;
        longevityReturnRef.current = null;
        setCurrentView('today');
        setDrawer(spec);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Save and restore the Health Record scroll position around a
  // round-trip through Longevity Flags View, so the user lands back
  // where they were instead of scrolled to the top. The .main-content
  // element owns the page scroll (the app shell uses overflow:hidden).
  const timelineScrollRef = useRef(0);
  const previousViewRef = useRef(initialView);
  useEffect(() => {
    const mainEl = document.querySelector('.main-content');
    // Leaving the timeline for Longevity Flags View: save scroll.
    if (previousViewRef.current === 'timeline' && currentView === 'longevity-flags' && mainEl) {
      timelineScrollRef.current = mainEl.scrollTop;
    }
    // Returning to timeline from Longevity Flags View: restore.
    if (previousViewRef.current === 'longevity-flags' && currentView === 'timeline' && mainEl) {
      // Defer one frame so the timeline content has mounted before we
      // try to scroll it. requestAnimationFrame after the React commit
      // is enough — no fallback needed.
      requestAnimationFrame(() => { mainEl.scrollTop = timelineScrollRef.current || 0; });
    }
    previousViewRef.current = currentView;
  }, [currentView]);

  // Strip any hash that is neither a known view nor a known modal sentinel.
  // Preserve modal hashes so deep-link auto-open (e.g. FitnessPage reopening
  // #activity-{id}) can still read them.
  useEffect(() => {
    const h = window.location.hash;
    // KAL-115 — SPLIT THE QUERY OFF BEFORE THE KNOWN_VIEWS TEST. The other two places that
    // resolve a view from the hash already do this (initialView at ~22470, onPop at ~22609);
    // this one did not, so ANY hash carrying a query was judged unknown and rewritten —
    // silently discarding the parameter. That broke `#vitals?section=sleep` (a shipped
    // deep-link) and `#cycle-report?cycle=YYYY-MM`, which is the only way to open a PAST
    // cycle's report.
    const candidate = h ? h.slice(1).split('?')[0] : '';
    if (h && !KNOWN_VIEWS.has(candidate) && !isModalHash(h)) {
      const expected = initialView === 'today' ? '' : `#${initialView}`;
      window.history.replaceState(null, '', expected || window.location.pathname);
    }
  }, [initialView]);

  // Sync currentView with the URL hash on browser navigation (Android back
  // button, browser back/forward). Modal hashes are handled by
  // useModalHashRoute's own popstate listener — skip them here so we don't
  // accidentally redirect to a parent view while a modal close is in flight.
  // Unknown hashes are ignored too (we don't blank the page).
  useEffect(() => {
    const onPop = () => {
      const h = window.location.hash;
      if (isModalHash(h)) return;
      // Strip any ?section=… query (deep-section links like #vitals?section=sleep)
      // so the bare view id is matched against KNOWN_VIEWS.
      const raw = (h ? h.slice(1) : 'today').split('?')[0];
      if (raw === 'home' || CHAT_ALIASES.has(raw)) {
        if (CHAT_ALIASES.has(raw)) setChatDrawerOpen(true);
        setCurrentView(prev => prev !== 'today' ? 'today' : prev);
        return;
      }
      if (!KNOWN_VIEWS.has(raw)) return;
      setCurrentView(prev => prev !== raw ? raw : prev);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ── Usage telemetry: app_opened once, route_view per SPA nav ──────────────
  // In-app navigation uses replaceState (no hashchange fires) so currentView is
  // the authoritative SPA route signal — this covers every programmatic nav +
  // the initial view. The hashchange listener additionally catches hash-only
  // changes (section deep-links, back/forward). Both feed the deduped tracker.
  useEffect(() => { usageAnalytics.track('app_opened'); }, []);
  useEffect(() => { usageAnalytics.trackRouteView(currentView); }, [currentView]);
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      if (isModalHash(h)) return;   // never emit a modal hash as a route
      const raw = (h ? h.slice(1) : 'today').split('?')[0];
      const known = KNOWN_VIEWS.has(raw) ? raw : (!raw ? 'today' : 'other');
      usageAnalytics.trackRouteView(known);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Android Chrome nav-bar fallback for browsers that don't support 100dvh.
  // Modern browsers (Chrome 108+, Safari 15.4+) handle the visible viewport
  // natively via dvh units, so this shim is a belt-and-braces for older
  // versions. We compare the layout viewport (window.innerHeight, which on
  // Android extends *behind* the nav bar with viewport-fit=cover) against
  // the visible viewport (visualViewport.height). The delta is whatever
  // chrome is obscuring at the moment — nav bar, on-screen keyboard, etc.
  //
  // IMPORTANT: do NOT use screen.height here. screen.height is the entire
  // device screen including the status bar at the top, which would inflate
  // the value by ~80px and push the composer way too far up.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const update = () => {
      const vv = window.visualViewport;
      const obscured = window.innerHeight - vv.height;
      // Filter out keyboard pop-ups (usually >150px) — only set the var
      // when the obscured area is plausibly a nav bar (0–96px).
      const navBar = obscured > 0 && obscured <= 96 ? Math.round(obscured) : 0;
      document.documentElement.style.setProperty('--nav-bar-height', `${navBar}px`);
    };
    update();
    window.visualViewport.addEventListener('resize', update);
    window.visualViewport.addEventListener('scroll', update);
    return () => {
      window.visualViewport.removeEventListener('resize', update);
      window.visualViewport.removeEventListener('scroll', update);
    };
  }, []);
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [toast, setToast] = useState(null);

  // KAL-200 — an underwriter session lands on the sandbox, not on Today.
  // Must be an effect, not part of `initialView`: the role arrives with /api/profile,
  // which has not resolved when useState runs. It also has to sit HERE, below the
  // `profile` declaration — placing it beside the currentView useState referenced
  // `profile` in its temporal dead zone and would have thrown at runtime.
  // Guarded on the current view so it fires once on arrival and never fights a
  // navigation the user made themselves.
  const _uwRole = profile && profile.admin_role;
  useEffect(() => {
    if (_uwRole === 'underwriter' && currentView === 'today') setCurrentView('underwriting');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_uwRole]);

  // Clean up URL params after reading them, then keep hash in sync with currentView
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Browser back from the very first page in this tab's history would
    // otherwise exit the site (to the search results / external referer).
    // Push a duplicate of the current URL so there's an in-app entry to
    // pop back to. Standard one-back-and-then-exit pattern.
    try { window.history.pushState({ kalpaInitialEntry: true }, '', window.location.href); } catch {}
  }, []);

  // First run flag for the hash-sync useEffect below. On mount, if the
  // URL has a modal hash (deep link to /#activity-{id} etc.), we MUST
  // leave it intact so the modal's parent view can read it and trigger
  // auto-open. Subsequent runs always reconcile URL to currentView.
  const hashSyncFirstRunRef = useRef(true);
  useEffect(() => {
    if (hashSyncFirstRunRef.current) {
      hashSyncFirstRunRef.current = false;
      if (isModalHash(window.location.hash)) {
        return; // preserve deep-link modal hash for auto-open
      }
    }
    // Persist navigation in URL hash so reload returns to the same page.
    // 'today' is the default landing view — leave the URL bare (no hash)
    // when we're on it. 'home' (legacy chat-as-home) gets a bare URL too
    // since some external links still target it.
    const hash = (currentView === 'today') ? '' : `#${currentView}`;
    // KAL-115 — NEVER discard a query that belongs to the view we are already on. This effect
    // rewrites the hash to `#${currentView}`, which silently deleted the parameter in
    // `#cycle-report?cycle=2026-07` (the only way to open a PAST cycle) and `#vitals?section=…`.
    // Fixing the strip effect above was necessary but not sufficient — THIS is what actually
    // rewrote the URL, because it reconciles on currentView alone and cannot see the query.
    const cur = window.location.hash || '';
    if (cur.indexOf('?') > -1 && cur.slice(1).split('?')[0] === currentView) return;
    if (window.location.hash !== (hash || '#')) {
      window.history.replaceState(null, '', hash || window.location.pathname);
    }
  }, [currentView]);

  // Auth check
  useEffect(() => {
    fetch(`${AUTH_BASE}/auth/user`, { credentials: 'include' })
      .then(r => { if (r.ok) return r.json(); throw new Error('Not authenticated'); })
      .then(u => {
        // KAL-322 — the per-profile key for IndexedDB last-known summaries.
        try { window.__kalpaProfileKey = (u && (u.profile_name || u.logged_in_name || u.email)) || ''; } catch (_) {}
        setUser(u); setAuthChecked(true);
      })
      .catch(() => { setUser(null); setAuthChecked(true); });
  }, []);

  // The sync store is module-scoped so every status consumer shares one snapshot. Reset that
  // snapshot on an in-SPA logout/login; otherwise user B could briefly inherit user A's receipt.
  const syncSessionKey = user ? (user.email || user.npl_patient_id || user.name || 'authenticated') : null;
  useEffect(() => {
    if (authChecked) _setSyncSession(syncSessionKey);
  }, [authChecked, syncSessionKey]);

  // Post-auth landing: always send the user to the home/Live Health
  // view on the first render after auth resolves, regardless of the
  // hash the page loaded with. This fixes the case where a stale
  // browser tab had #longevity-flags (or any other deep-link hash) in
  // the URL and a fresh login otherwise dropped the user straight into
  // that view instead of the dashboard. One-shot via a ref — later
  // in-app navigations are not affected.
  const postAuthLandingDoneRef = useRef(false);
  useEffect(() => {
    if (!authChecked || !user || postAuthLandingDoneRef.current) return;
    postAuthLandingDoneRef.current = true;
    // Land on TODAY regardless of stale hash (NPL home renamed to Today to
    // match main). Bare URL is fine — the hash-sync useEffect writes '#today'.
    if (window.location.hash && window.location.hash !== '#today') {
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch {}
    }
    setCurrentView('today');
  }, [authChecked, user]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstallBanner(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') { setShowInstallBanner(false); setInstallPrompt(null); }
  };

  const handleLogout = async () => {
    try { await fetch(`${AUTH_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    // Clear any deep-link hash so the next login lands on home rather
    // than restoring whatever the user was on. Re-arm the one-shot
    // post-auth landing ref so it fires again on the next sign-in
    // (same App instance, no full reload).
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {}
    postAuthLandingDoneRef.current = false;
    setCurrentView('today');
    setUser(null);
  };


  // App-level subscription to the global feature flags. Forces a
  // re-render when /api/features resolves so flag-gated logic (Fitbit
  // auto-sync, conditional nav items, etc.) sees the right values on
  // the next render instead of running once with the kalpa defaults.
  useFeatures();
  // NPL data-import consent state. null = not loaded yet; otherwise an
  // object from /api/npl/consent-status: { has_mapping, consent_given_at,
  // consent_deferred_at, ... }. Drives whether the full-screen
  // NplConsentScreen overlay shows, or just the small reminder banner.
  const [nplConsent, setNplConsent] = useState(null);
  const [forceConsentScreen, setForceConsentScreen] = useState(false);
  // NPL first-run onboarding wizard state (null = not loaded; otherwise the
  // /api/onboarding/state payload). Drives the full-screen NplOnboardingWizard.
  const [onboarding, setOnboarding] = useState(null);
  // Back-button parity for the consent screen RE-OPENED from the deferred
  // banner: gated on forceConsentScreen so it owns a #npl-consent history
  // entry only in that mode. The mandatory first-run consent (consentNeeded &&
  // !consentDeferred) runs with forceConsentScreen=false → no entry → it stays
  // non-dismissible by back, as intended. closeConsent routes accept/defer and
  // hardware/browser back through the same pop.
  const closeConsent = useModalHashRoute('#npl-consent', () => setForceConsentScreen(false), forceConsentScreen);
  // Local mirror of FEATURES_GLOBAL.ready so effects that depend on
  // features being loaded can list it in their dep array (mutating a
  // module-level object doesn't trigger React reactivity by itself).
  const [featuresReady, setFeaturesReady] = useState(FEATURES_GLOBAL.ready);
  useEffect(() => {
    if (FEATURES_GLOBAL.ready) { setFeaturesReady(true); return; }
    const tick = () => { if (FEATURES_GLOBAL.ready) setFeaturesReady(true); };
    FEATURES_LISTENERS.add(tick);
    return () => FEATURES_LISTENERS.delete(tick);
  }, []);

  // ── Fitbit sync state (App-level so it persists across page navigation) ──
  const [fitbitSyncing, setFitbitSyncing] = useState(false);
  const [fitbitSyncStatus, setFitbitSyncStatus] = useState(null);
  const syncPollRef = useRef(null);

  const [fitbitSyncError, setFitbitSyncError] = useState(null);
  const triggerFitbitSync = useCallback(async (full = false) => {
    setFitbitSyncing(true);
    setFitbitSyncError(null);
    // S5 — a Fitbit sync now reports itself on the SAME lifecycle every other sync uses, so the
    // single GlobalSyncBar covers it. It previously emitted nothing at all, which is why the
    // Fitness page had grown its own third "↻ Syncing…" line: without this the Fitbit-direct
    // users (Kalpa UK/US) would have lost that signal when that line was removed.
    emitSyncStatus('syncing');
    emitSyncProgress(0, null, 'Syncing with Fitbit…', true, 'provider');   // named, honestly unmeasurable
    try {
      await api.post('/fitbit/sync', { full });
      // All syncs now run in background — start polling
      if (!syncPollRef.current) {
        syncPollRef.current = setInterval(async () => {
          try {
            const s = await api.get('/fitbit/sync-status');
            setFitbitSyncStatus(s);
            if (!s.background_sync || !s.background_sync.running) {
              clearInterval(syncPollRef.current);
              syncPollRef.current = null;
              setFitbitSyncing(false);
              // Check if sync ended with an error
              if (s.background_sync?.error) {
                const err = s.background_sync.error;
                // S5 — clear the shared busy state, or the one bar would spin forever after a
                // failed Fitbit sync. The Fitbit-SPECIFIC remedy stays where it already was
                // (the toast below + the "Reconnect →" banner) rather than being duplicated here.
                emitSyncStatus('error', { error: err });
                setFitbitSyncError(err);
                setToast(err.includes('reconnect') || err.includes('Reconnect') || err.includes('refresh failed')
                  ? '\u26A0 Fitbit connection expired. Please reconnect on your Profile page.'
                  : '\u26A0 Fitbit sync error: ' + err.slice(0, 100));
                setTimeout(() => setToast(null), 8000);
              } else if (s.background_sync?.rate_limited) {
                // 429 after first batch = expected, not an error
                console.log('Fitbit rate limited — first batch loaded successfully');
                void _refreshDataAndReceipt('Fitbit rate-limited completion', {
                  terminal: true,
                  dataEvent: 'kalpa:fitbit-synced',
                });
              } else {
                void _refreshDataAndReceipt('Fitbit completion', {
                  terminal: true,
                  dataEvent: 'kalpa:fitbit-synced',
                });
              }
            }
          } catch (e) {
            console.warn('[sync] Fitbit status poll failed; retrying:', e);
          }
        }, 4000);
      }
    } catch (e) {
      console.error('Sync failed:', e);
      setFitbitSyncing(false);
      emitSyncStatus('error', { error: e.message || 'Sync request failed' });   // S5 — never leave the shared bar spinning
      setFitbitSyncError(e.message || 'Sync request failed');
    }
  }, []);

  // Auto-sync on app load: check if data is stale (>1 hour since last sync)
  const autoSyncTriggered = useRef(false);
  useEffect(() => {
    if (!user || !featuresReady || autoSyncTriggered.current) return;
    // On NPL Health, Fitbit is not part of the product. The /fitbit/*
    // endpoints will return errors for unconnected users which surface as
    // a toast at bottom: 24px — directly over the mobile bottom nav,
    // blocking taps until it self-dismisses 8s later. Skip the auto-sync
    // path entirely on this instance. Gating on featuresReady ensures the
    // instance value is the post-fetch value rather than the kalpa default.
    if (FEATURES_GLOBAL.branding.instance === 'npl') {
      autoSyncTriggered.current = true;
      return;
    }
    autoSyncTriggered.current = true;
    (async () => {
      try {
        const status = await api.get('/fitbit/sync-status');
        setFitbitSyncStatus(status);
        // KAL-208 — a user with NO Fitbit connection is not "stale", and must not be told a
        // sync failed. Without this, an unconnected account on a fitbit_direct instance got
        // `{}` here, fell through to the staleness branch below (newestSync null ⇒ stale),
        // fired triggerFitbitSync, received 400 "Fitbit not connected", and the shared
        // GlobalSyncBar rendered "Couldn't sync — tap to retry" across the whole app.
        // CAUGHT BY THE BROWSER CHECK, NOT THE API ONE: every payload field I had inspected
        // (fitbit_status.connected/needs_reconnect, providers.last_sync_at, last-received) was
        // correctly inert — this bar is driven by its own client-side lifecycle, so nothing in
        // the JSON could have revealed it. Only rendering the page did.
        // Explicit === false so an older server that omits the key keeps today's behaviour.
        if (status.connected === false) return;
        // Check if already running
        if (status.background_sync?.running) {
          setFitbitSyncing(true);
          emitSyncStatus('syncing');   // S5 — a sync already in flight at load reports too
          emitSyncProgress(0, null, 'Finishing an earlier Fitbit sync…', true, 'provider');
          if (!syncPollRef.current) {
            syncPollRef.current = setInterval(async () => {
              try {
                const s = await api.get('/fitbit/sync-status');
                setFitbitSyncStatus(s);
                if (!s.background_sync || !s.background_sync.running) {
                  clearInterval(syncPollRef.current);
                  syncPollRef.current = null;
                  setFitbitSyncing(false);
                  if (s.background_sync?.error) {
                    console.error('[sync] earlier Fitbit sync finished with error:', s.background_sync.error);
                    emitSyncStatus('error', { error: s.background_sync.error });
                  } else {
                    void _refreshDataAndReceipt('earlier Fitbit completion', {
                      terminal: true,
                      dataEvent: 'kalpa:fitbit-synced',
                    });
                  }
                }
              } catch (e) {
                console.warn('[sync] earlier Fitbit status poll failed; retrying:', e);
              }
            }, 4000);
          }
          return;
        }
        // If last sync had an auth error, show it but don't auto-retry
        if (status.background_sync?.error && (status.background_sync.error.includes('reconnect') || status.background_sync.error.includes('refresh failed'))) {
          setFitbitSyncError(status.background_sync.error);
          return;
        }
        // Check staleness: find most recent last_sync_at across endpoints
        let newestSync = null;
        Object.values(status).forEach(v => {
          if (v && typeof v === 'object' && v.last_sync_at) {
            const d = new Date(v.last_sync_at);
            if (!newestSync || d > newestSync) newestSync = d;
          }
        });
        const staleMs = 60 * 60 * 1000; // 1 hour
        if (!newestSync || (Date.now() - newestSync.getTime()) > staleMs) {
          triggerFitbitSync(false);
        }
      } catch (e) { /* Fitbit not connected or error — skip auto-sync */ }
    })();
    return () => { if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; } };
  }, [user, featuresReady, triggerFitbitSync]);

  useEffect(() => { if (user) { loadInitialData(); } }, [user]);

  // KAL-314 — THE PER-USER GATE IS LIVE, NOT LOGIN-TIME. loadInitialData ran once per login, so a
  // WebView that stays open for days kept the /api/profile answer it got on day one — including
  // order_eligible (KAL-303). The server evaluates eligibility per request; this makes the
  // CLIENT re-ask whenever the app comes back to the foreground or the phone finishes a sync,
  // and refreshes the profile alongside so nothing else goes stale for the same reason.
  //
  // SAME EVENT, SECOND JOB — THE STALE BUNDLE. The document is served no-cache, but a long-lived
  // WebView never re-requests it, so a pilot phone can run last week's bundle indefinitely
  // (memory: "old UI" is usually a stale client). On return from >= 10 min hidden, compare the
  // running main.js against /asset-manifest.json and reload ONCE if the fleet moved on.
  useEffect(() => {
    if (!user) return undefined;
    let hiddenAt = 0;
    const refreshGate = async (why) => {
      try {
        const p = await api.get('/profile');
        const was = FEATURES_GLOBAL.order_eligible;
        FEATURES_GLOBAL.order_eligible = !!(p && p.order_eligible);
        setProfile(p);
        if (was !== FEATURES_GLOBAL.order_eligible) {
          console.info(`[gate] order_eligible ${was} -> ${FEATURES_GLOBAL.order_eligible} (${why})`);
        }
      } catch (e) {
        console.warn(`[gate] profile refresh failed (${why}):`, e && e.message);
      }
    };
    const checkBundle = async () => {
      try {
        const running = (document.querySelector('script[src*="/static/js/main."]') || {}).src || '';
        if (!running) return;
        const r = await fetch('/asset-manifest.json', { cache: 'no-store', credentials: 'same-origin' });
        if (!r.ok) return;
        const j = await r.json();
        const served = (j && j.files && j.files['main.js']) || '';
        if (served && !running.endsWith(served)) {
          console.warn(`[bundle] stale: running ${running.split('/').pop()} vs served ${served.split('/').pop()} — reloading`);
          window.location.reload();
        }
      } catch (e) { console.warn('[bundle] manifest check failed:', e && e.message); }
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      refreshGate('visible');
      if (hiddenAt && Date.now() - hiddenAt >= 10 * 60 * 1000) checkBundle();
    };
    const onSynced = () => refreshGate('native-synced');
    window.addEventListener('visibilitychange', onVis);
    window.addEventListener('kalpa:native-synced', onSynced);
    return () => {
      window.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('kalpa:native-synced', onSynced);
    };
  }, [user]);

  // Pull NPL consent status once we know who is logged in and what
  // instance we're on. On Kalpa main this stays null and the consent
  // screen never renders.
  useEffect(() => {
    if (!user || !featuresReady) return;
    if (FEATURES_GLOBAL.branding.instance !== 'npl') return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get('/npl/consent-status');
        if (!cancelled) setNplConsent(d || { has_mapping: false });
      } catch {
        if (!cancelled) setNplConsent({ has_mapping: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user, featuresReady]);

  // Pull first-run onboarding state (NPL only). On Kalpa main this stays null
  // and the wizard never renders. Re-fetched whenever the user changes.
  useEffect(() => {
    if (!user || !featuresReady) return;
    if (FEATURES_GLOBAL.branding.instance !== 'npl') return;
    let cancelled = false;
    (async () => {
      try {
        // §O — send the detected platform so the server hides web-only cards
        // (connect_device) in the native WebView.
        const _plat = (typeof window !== 'undefined' && window.ReactNativeWebView) ? 'native' : 'web';
        const d = await api.get('/onboarding/state?platform=' + _plat);
        if (!cancelled) setOnboarding(d || null);
      } catch {
        // Fail open — never trap the user behind a broken gate fetch.
        if (!cancelled) setOnboarding({ intro_seen: true, outstanding: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [user, featuresReady]);

  // §O — the OnboardingGate shows while the Welcome splash is unseen OR any card is
  // still OUTSTANDING (server-computed for this platform). Auto-skipped/resolved cards
  // are absent from `outstanding`, so a fully-set-up returning user never sees the gate.
  // NEVER while a superadmin is view-as'ing another profile: every step in this wizard
  // (including "Skip") resolves itself with a POST, which the view-as read-only
  // chokepoint always refuses with 403 — so under impersonation this gate cannot be
  // advanced OR skipped, and its full-screen backdrop (zIndex 9999) sits above the
  // "Viewing as" exit banner, trapping the session. View-as is read-only and this
  // wizard can only fail here, so it must not render at all while impersonating.
  const showOnboarding = FEATURES_GLOBAL.branding.instance === 'npl'
    && !!onboarding
    && !profile?.viewing_as
    && (onboarding.intro_seen === false || (Array.isArray(onboarding.outstanding) && onboarding.outstanding.length > 0));
  const consentNeeded   = !!(nplConsent && nplConsent.has_mapping && !nplConsent.consent_given_at);
  const consentDeferred = !!(nplConsent && nplConsent.has_mapping && !nplConsent.consent_given_at && nplConsent.consent_deferred_at);
  // Suppress the lab-record consent overlay while the first-run wizard is up
  // (the wizard already gathers the lab-link consent) — avoid double overlays.
  // Also suppressed while view-as'ing, same reason as showOnboarding above.
  const showConsentOverlay = !showOnboarding && !profile?.viewing_as && consentNeeded && (!consentDeferred || forceConsentScreen);
  const loadInitialData = async () => {
    try {
      const p = await api.get('/profile');
      // KAL-303 — the instance flag says ordering EXISTS here; this says whether THIS user may
      // be offered it (Nepal device zone / +977 phone / operator override, resolved server-side).
      FEATURES_GLOBAL.order_eligible = !!(p && p.order_eligible);
      setProfile(p);
    } catch (e) {
      setProfileError(e.message);
      // Do NOT fabricate an identity here. A hardcoded 'Tim' is a main-only
      // relic that would render a fake profile on NPL. Orphaned/purged sessions
      // are already forced to logout upstream (auth_user → 401 → LoginPage);
      // a genuine fetch error just leaves a neutral, render-safe blank.
      setProfile({ displayName: 'User', name: 'User', age: null });
    }
  };
  // Notification bell + panel removed (dead control). loadUnreadCount /
  // openNotifications and their state are gone with it.

  // Ask AI: stash the question and pop the Ask Kalpa drawer, which auto-sends
  // it. Replaces the old navigate-to-chat-page behaviour.
  const chatPrefillRef = useRef(null);
  const handleAskAI = (question) => {
    chatPrefillRef.current = question;
    setChatDrawerOpen(true);
  };

  // Deep-section ref the Fitness page reads on mount to scroll to a specific
  // trend. In Phase 2 the Today vital cards open drawers rather than navigating
  // to a Fitness section, so this stays null in normal use — the ref is kept
  // for the FitnessPage deep-link (#vitals?section=…) path.
  const fitnessSectionRef = useRef(null);

  if (!authChecked) return <div className="app"><LoadingSpinner message="Loading..." /></div>;
  if (!user) return <LoginPage />;

  return (
    <div className="app">
      {/* §B — single global glossary popover host: makes every .auto-glossary
          span (chat + any HTML prose) tap-to-define, app-wide. */}
      <GlossaryPopover />
      <div className={`mobile-header ${FEATURES_GLOBAL.branding.instance === 'npl' ? 'mobile-header-npl' : ''}`}>
        {FEATURES_GLOBAL.branding.instance === 'npl' ? (
          <>
            {/* §C — the ACTUAL top-left masthead the user taps on phone/app is this
                mobile-header logo group (§2 only wired the desktop .npl-header-logo,
                which is why the logo was dead on device). Wire it to Today. */}
            <div className="mobile-header-left" role="button" tabIndex={0} aria-label="Go to Today"
              style={{cursor:'pointer'}}
              onClick={() => navTo('today')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navTo('today'); } }}>
              {/* Square icon at 28×28 — more legible than the full horizontal
                  mark at this size. The PNG is transparent + multi-colour, so it
                  sits on an intentional white badge (see .mobile-header-logo-square
                  in App.css) — consistent in light + dark, never a raw slab. */}
              <img
                className="mobile-header-logo mobile-header-logo-square"
                src="/static/npl_logo_square.png"
                alt="NPL"
                width="28"
                height="28"
              />
              <span className="mobile-header-name">{FEATURES_GLOBAL.branding.app_name || 'NPL Health'}</span>
            </div>
            <div className="mobile-header-actions">
              <span className="mobile-header-powered-by">Powered by {FEATURES_GLOBAL.branding.powered_by || 'Kalpa'}</span>
              <NplThemeToggle />
              {/* RUN9 §6 — strapline to the RIGHT of the theme badge, inline (no new row). */}
              <span className="npl-strapline">Health you can measure</span>
              {/* Dead notification bell removed — its panel was retired in the v11
                  merge, so it only re-rendered/reloaded with no panel behind it. */}
              <AccountMenu
                user={user}
                profile={profile}
                currentView={currentView}
                onNav={navTo}
                onLogout={handleLogout}
              />
            </div>
          </>
        ) : (
          <>
            {/* Phase 3 (main): the standalone Health Insights bell + panel were
                removed on Kalpa — insights surface as Today entries. */}
            <img className="mobile-header-logo" src="/kalpa_logo.png" alt="Kalpa" style={{height:'28px',width:'auto',objectFit:'contain',verticalAlign:'middle',marginLeft:'8px'}} />
            <div className="mobile-header-actions">
              <AccountMenu
                user={user}
                profile={profile}
                currentView={currentView}
                onNav={navTo}
                onLogout={handleLogout}
              />
            </div>
          </>
        )}
      </div>
      <nav className="sidebar">
        <div className="logo">
          {/* §2 — the masthead logo is a shortcut to Today (the home view). Keyboard-
              accessible (Enter/Space) via the existing navTo handler. No restyle. */}
          {FEATURES_GLOBAL.branding.instance === 'npl' ? (
            <div className="npl-header-logo" role="button" tabIndex={0} aria-label="Go to Today"
              style={{cursor:'pointer'}}
              onClick={() => navTo('today')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navTo('today'); } }}>
              <img src={FEATURES_GLOBAL.branding.logo_url || '/static/npl_logo.png'} alt={FEATURES_GLOBAL.branding.app_name || 'NPL Health'} />
              {FEATURES_GLOBAL.branding.powered_by && (
                <span className="npl-powered-by">Powered by {FEATURES_GLOBAL.branding.powered_by}</span>
              )}
            </div>
          ) : (
            <img src="/kalpa_logo.png" alt="Kalpa" className="sidebar-logo-img" role="button" tabIndex={0} aria-label="Go to Today"
              style={{cursor:'pointer'}}
              onClick={() => navTo('today')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navTo('today'); } }} />
          )}
          <LongevityPointsPill />
          {/* NplThemeToggle self-gates (renders null off NPL). The NPL notification
              bell was removed: its panel was retired in the v11 merge, leaving a
              dead control that only re-rendered/reloaded the page with no panel. */}
          <NplThemeToggle />
        </div>
        <div className="nav-items">
          {(profile?.admin_role === 'underwriter' ? [
            // KAL-200 — an UNDERWRITER gets one destination and no consumer tabs.
            // Not cosmetic: every consumer route is refused by the before_request wall, so
            // leaving Today/Longevity/Fitness in the rail would offer an external user a row
            // of controls that all fail. The wall already makes them safe; this makes them
            // absent, which is what "this account is only for the sandbox" should look like.
            { id: 'underwriting', icon: '📄', label: 'Underwriting' },
          ] : [
            // TODAY is the default landing view — replaces chat-as-home.
            // Sun icon visually echoes the morning-greeting masthead.
            { id: 'today', icon: '☀️', label: 'Today' },
            { id: 'longevity', icon: '\uD83D\uDCCA', label: 'Longevity', shortLabel: 'Longevity' },
            { id: 'vitals', icon: '\u2764\uFE0F', label: 'Fitness' },
            // genetics flag \u2014 hide My Genome on instances where genetics
            // hasn't been turned on (e.g. NPL pilot phase one).
            // KAL-213 \u2014 and hidden for a SYNTHETIC DEMO SUBJECT too, whatever the flag says.
            // Demo profiles carry no genetic data BY DESIGN and will not be given any: a real
            // genome is identifiable data that would break the "no real person is represented"
            // attestation, and synthetic genotypes are not defensible from published
            // aggregates. That absence is the ABI point, not a gap \u2014 so the nav entry is
            // removed rather than left to render an empty state that reads as missing data.
            ...(FEATURES_GLOBAL.genetics && !profile?.is_demo
              ? [{ id: 'genome', icon: '\uD83E\uDDEC', label: 'My Genome', shortLabel: 'Genome', accent: 'var(--accent-violet)' }]
              // KAL-364 — NO LONGER mobileHidden. On a phone the only other entries were the last
              // card on Longevity and the account menu, whose avatar the Feedback pill half-covers
              // inside the Kalpa app (Tim, 28 Sep 2026: "no genome button"). Genetics-gated, so
              // NPL's bar (genetics=0) keeps its six items.
              : []),
            { id: 'timeline', icon: '\uD83D\uDCCB', label: 'Health Record', shortLabel: 'Record' },   // S7: 'Health...' truncated at 6 tabs
            { id: 'medicines', icon: '\uD83D\uDC8A', label: 'Medicines', shortLabel: 'Meds' },        // S7: 'Medici...' truncated at 6 tabs
            // Ask Kalpa \u2014 a NAV ITEM ON EVERY VIEWPORT (committee #1380). Opens the coach
            // drawer rather than navigating a view (handled in onClick below).
            // It used to carry mobileHidden:true, so `.nav-items .nav-mobile-hidden{display:none}`
            // removed it from the mobile bottom bar and mobile got the floating diamond FAB
            // instead \u2014 i.e. the committee's nav placement rendered on DESKTOP ONLY. The FAB is
            // now gone and this item renders everywhere, so Ask is reached the same way on both.
            // shortLabel: the bottom-bar siblings are single words, so mobile shows "Ask" while
            // the desktop left-nav keeps the full committee label (see .nav-label-short in CSS).
            { id: 'ask', icon: '\uD83D\uDCAC', label: 'Ask about my health', shortLabel: 'Ask', accent: 'var(--accent-teal, #5EC8D8)' },
            // "Documents" (formerly the standalone "Medical Vault" item) is no
            // longer a top-level nav entry \u2014 it's re-parented as a section
            // INSIDE Health Record (reachable via the \uD83D\uDCC4 Documents button on
            // that page), so it surfaces on every surface incl. the 5-item
            // mobile bottom bar without adding a sixth item. Route #vault still
            // exists in KNOWN_VIEWS for deep-linking.
            // Profile, Privacy & Data, and (admin-only) corporate dashboards now
            // live in the avatar/profile menu (AccountMenu) on every instance
            // (NPL's Corporate Health too \u2014 moved off the bottom nav so 7 items
            // don't overflow Bimal's small-screen Android). The hash routes
            // (#profile / #privacy / #corporate / #compliance / #npl-corporate)
            // still work for deep-linking via KNOWN_VIEWS. Mobile bottom bar shows
            // the 5 non-mobileHidden items: Today, Longevity, Fitness, Health
            // Record, Medicines (My Genome is mobileHidden + genetics-gated).
          ]).map(item => (
            <button key={item.id} className={`${currentView === item.id ? 'active' : ''} ${item.accent ? 'nav-violet' : ''} ${item.mobileHidden ? 'nav-mobile-hidden' : ''} ${item.shortLabel ? 'has-short-label' : ''}`} onClick={() => item.id === 'ask' ? setChatDrawerOpen(true) : navTo(item.id)} style={currentView===item.id&&item.accent?{'--nav-accent':item.accent}:undefined}>
              {/* Both labels are rendered and CSS picks one per viewport, so the switch happens
                  at the real breakpoint — no JS resize listener, no hydration/width guess. */}
              <span className="icon">{item.icon}</span><span className="nav-label">{item.label}</span>
              {item.shortLabel && <span className="nav-label nav-label-short">{item.shortLabel}</span>}
            </button>
          ))}
        </div>
        {/* S5 — the sidebar's own sync panel is GONE. It was the only one with a progress bar
            and CSS hid it below 480px, so phones never saw it while desktop saw two indicators.
            There is now exactly one <GlobalSyncBar /> at the top of the content column. */}
        <div className="sidebar-account-block" style={{padding:'8px 16px', borderTop:'1px solid var(--border-color)'}}>
          {/* Desktop avatar trigger — moved out of .sidebar-footer so
              it sits ABOVE the install / sync / version block and is
              the first thing the eye lands on at the bottom of the
              nav. Uses the labelled variant (avatar + name + caret)
              so a desktop user can't mistake a 32px circle for chrome
              decoration. The dropdown's name+email/Profile Settings/
              Data & Privacy/Log out items live in AccountMenu. */}
          <AccountMenu
            user={user}
            profile={profile}
            currentView={currentView}
            onNav={navTo}
            onLogout={handleLogout}
            variant="labelled"
          />
        </div>
        <div className="sidebar-footer">
          {showInstallBanner && (
            <div className="install-banner" onClick={handleInstall}>
              <span>Install {FEATURES_GLOBAL.branding.app_name || 'NPL Health'}</span>
              <button onClick={(e) => { e.stopPropagation(); setShowInstallBanner(false); }}>&times;</button>
            </div>
          )}
          {/* Sync state is owned entirely by the single <GlobalSyncBar /> at the top of the content
              column (S5). This keeps only the Fitbit-DISCONNECTED affordance, which is a distinct
              thing — an account state needing a reconnect, not the progress of a sync. */}
          {!fitbitSyncing && fitbitSyncError && <div className="sidebar-sync-indicator" style={{color:'#F59E0B',cursor:'pointer',animation:'none'}} onClick={()=>setCurrentView('profile')}>⚠ Fitbit disconnected</div>}
          <div style={{fontSize:'9px',color:'#555555',marginTop:'4px',textAlign:'center'}}>Kalpa Health v8.0</div>
        </div>
      </nav>
      <main className={`main-content${(currentView === 'chat' || currentView === 'home') ? ' chat-active' : ''}${currentView === 'today' ? ' today-active' : ''}`}>
        {consentDeferred && !showConsentOverlay && (
          <div style={{padding:'10px 16px 0 16px'}}>
            <NplConsentDeferredBanner onOpen={() => setForceConsentScreen(true)} />
          </div>
        )}
        {/* S5 — THE one sync surface, ABOVE the router: rendered here, before the currentView
            switch and UNGATED, so it does not unmount when the view changes. The old strip was
            gated on a hard-coded five-view list, which both hid it on Documents/My Genome and
            threw away an in-flight sync's progress the moment you navigated. */}
        <GlobalSyncBar />
        {/* KAL-142 — TODAY v2 PRECEDENCE, MADE EXPLICIT AT THE VIEW DISPATCH.
            today_v2 replaces the WHOLE Today screen, so when it is on TodayFeed is never
            constructed — and neither is the CycleCard inside it, whose own cycle_ui_v2 gate
            therefore cannot be reached. That matters because both flags would otherwise claim
            the August report card, which is the two-owners-one-surface divergence the canonical
            rule exists to prevent. Deciding it HERE rather than inside CycleCard also makes the
            flag-OFF proof trivial: with today_v2 off this line is character-identical to what
            it was, so nothing on the old path can have changed. */}
        {/* ⚠ KAL-251 — `featuresReady &&` IS LOAD-BEARING. DO NOT REMOVE IT.
            `today_v2` defaults FALSE until /api/features resolves, so without this gate the
            first render of Today mounts the WRONG branch. Every other render-time read of
            FEATURES_GLOBAL self-corrects when hydration re-renders the tree (the root App
            subscribes via useFeatures(), and nothing is memoised) — but a MOUNT is not a
            display. TodayFeed's effects run, it fires the legacy `/api/today`, and the
            subsequent correct render does not recall that request.
            MEASURED: since 19 Aug every legacy /api/today call co-occurred with a v2 call —
            UK 1 of 1 at 29.394 s to a real user, NPL 3 of 3 at 14.347/14.590/0.295 s. Rare,
            because /api/features usually wins at ~110 ms; it loses precisely when the box is
            slow, so the contention causes the wasted request which worsens the contention.
            Rendering NOTHING for that ~110 ms is correct: the alternative is doing expensive
            work for a screen we are about to throw away. `featuresReady` is already the gate
            on the sync effects at 25779/25865/25882 — this just applies it where a wrong
            answer costs a request rather than a repaint. */}
        {/* KAL-255 — TodayFeed is DELETED, so there is no longer a branch here: TodayV2 is
            the only Today screen. `today_v2` remains a console-owned flag but no longer
            selects between two implementations at this dispatch; turning it off would
            previously have fallen back to TodayFeed, and that fallback no longer exists.
            `featuresReady` is KEPT deliberately even though the branch is gone. TodayV2's
            subtree still reads flags at render time (order_lab gates a request in
            OrderPanelPrompt, two_loop and cycle_ui_v2 gate sub-surfaces), and one paint's
            delay is a cheaper price than mounting that subtree against default values. */}
        {currentView === 'today' && featuresReady &&
          <TodayV2 onNav={navTo} onAskAI={handleAskAI} onOpenDrawer={openDrawer} />}
        {/* KAL-299 — chat timestamps render in the VIEWER's zone (the browser knows it), never a
            hard-coded London default: `profile.timezone` is the migration-poisoned column that
            reads Europe/London for every Nepali pilot. */}
        {(currentView === 'home' || currentView === 'chat') && <KalpaChat prefill={chatPrefillRef} tz={(_TZ_HEADER['X-Kalpa-Timezone']) || profile?.device_tz || 'UTC'} />}
        {currentView === 'genome' && FEATURES_GLOBAL.genetics && <GenomePage />}
        {currentView === 'longevity' && <LongevityPage onNav={navTo} />}
        {currentView === 'vitals' && (profile ? <FitnessPage profile={profile} fitbitSyncError={fitbitSyncError} onNav={navTo} sectionRef={fitnessSectionRef} syncing={fitbitSyncing} triggerFitbitSync={triggerFitbitSync} openDrawer={openDrawer} /> : <LoadingSpinner message="Loading profile..." />)}
        {currentView === 'timeline' && <ClinicalTimeline onAskAI={handleAskAI} onNav={setCurrentView} />}
        {currentView === 'longevity-flags' && <LongevityFlagsView onNav={setCurrentView} />}
        {/* KAL-083 — monthly cycle report, IN-APP VIEW only. No PDF and no archive: those need
            a renderer decision (KAL-015) that has not been made. */}
        {currentView === 'cycle-report' && <CycleReportView onNav={setCurrentView} />}
        {currentView === 'medicines' && <MedicinesPage onNav={setCurrentView} />}
        {currentView === 'vault' && <MedicalVault onBack={() => navTo('timeline')} />}
        {currentView === 'privacy' && <PrivacyPage />}
        {currentView === 'underwriting' && <UnderwritingPage profile={profile} />}
        {currentView === 'corporate' && profile?.is_admin === true && <CorporatePage />}
        {currentView === 'compliance' && profile?.is_admin === true && <ComplianceDashboard />}
        {/* NPL corporate dashboard (anonymised pilot metrics) — NPL instance + admin only. */}
        {currentView === 'npl-corporate' && FEATURES_GLOBAL.branding.instance === 'npl' && (profile?.is_admin || profile?.admin_role === 'admin' || profile?.admin_role === 'superadmin') && <NPLCorporateDashboard />}
        {/* 2g — role gate only; the route must not be instance-name-gated either, or the
            menu item would lead to a blank screen on US. Server enforces require_lab_staff. */}
        {currentView === 'staff-lab-upload' && (profile?.is_admin || ['admin', 'superadmin', 'lab_staff'].includes(profile?.admin_role)) && <StaffLabUpload />}
        {/* Ops Console — superadmin-only; guard redirects everyone else to Today. */}
        {currentView === 'ops-console' && <OpsConsoleGuard profile={profile} navTo={navTo} />}
        {/* User Management — admin/superadmin (server enforces the role split). */}
        {/* KAL-235 — gated on the FLAG and the ROLE. lab_admin is the narrow role Bimal holds:
            it reaches the order catalogue and nothing else, so managing prices never implies
            access to biomarkers (the same GDPR Art. 25 separation that already divides
            `admin` from `superadmin`). */}
        {currentView === 'order-management' && FEATURES_GLOBAL.order_lab
          && (profile?.is_admin || ['lab_admin', 'admin', 'superadmin'].includes(profile?.admin_role))
          && <OrderManagementPage />}
        {currentView === 'user-management' && (profile?.is_admin || ['admin', 'superadmin'].includes(profile?.admin_role)) && <UserManagementPage profile={profile} onChanged={() => { loadInitialData(); }} onViewAs={async () => {
          // HARD RELOAD, deliberately — not loadInitialData()+navTo('today').
          // Identity just changed for the whole session. An in-place SPA transition only
          // refreshed /api/profile, so the banner correctly said "Viewing as Pawan Joshi"
          // while the Today feed still showed Tim's greeting and Tim's 50y Kalpa Age from
          // state fetched before the switch — the most dangerous possible outcome: a
          // superadmin reading one patient's screen labelled as another's. Rebooting the app
          // guarantees EVERY component refetches under the new session instead of us trying
          // to enumerate and invalidate each cache. Lands on Today (the default view).
          window.location.assign('/');
        }} />}
        {currentView === 'profile' && <ProfilePage onProfileSwitch={() => { loadInitialData(); }} />}
      </main>
      {/* Phase 3: standalone NotificationPanel ("Health Insights" list) removed —
          insights are now Today entries. NotificationPanel/Bell defs are retained
          (unused) to avoid churn; the hash route no longer opens them. */}
      {toast && <div className="toast-notification">{toast}</div>}
      {/* NPL first-run onboarding wizard \u2014 NPL instance only, until web steps done. */}
      {showOnboarding && (
        <NplOnboardingWizard
          state={onboarding}
          profile={profile}
          onComplete={() => setOnboarding(o => ({ ...(o || {}), intro_seen: true, outstanding: [] }))}
        />
      )}
      {/* NPL consent overlay \u2014 NPL instance only (gated by showConsentOverlay). */}
      {showConsentOverlay && (
        <NplConsentScreen
          onAccepted={() => {
            setNplConsent(c => ({ ...(c || {}), consent_given_at: new Date().toISOString() }));
            setToast('NPL results are being imported into your dashboard.');
            setTimeout(() => setToast(null), 4000);
            closeConsent();
          }}
          onDeferred={() => {
            setNplConsent(c => ({ ...(c || {}), consent_deferred_at: new Date().toISOString() }));
            closeConsent();
          }}
        />
      )}
      {/* Ask Kalpa's floating diamond FAB was REMOVED (committee #1380). Ask is now a nav item
          on EVERY viewport, so a second, differently-placed mobile-only entry point was an
          inconsistency rather than an affordance. The persistent slide-up drawer below is
          unchanged - the nav item opens it. */}
      {/* Persistent Feedback entry point — every page. TOP-RIGHT below the header
          bar (clears the avatar/theme toggle above; bottom-right is now free since the
          Ask FAB was removed; no longer overlaps Today insight text / sidebar name). */}
      {/* Superadmin view-as indicator — shell-level so NO view can render without it
          while impersonation is active. Exit returns to the admin roster. */}
      {/* Exit reloads for the same reason the entry does: the session's identity changes back,
          so every view must refetch rather than keep the viewed user's data on screen. */}
      <ViewingAsBanner profile={profile} onExit={async () => { window.location.assign('/'); }} />
      <FeedbackWidget />
      <AskKalpaDrawer
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        currentView={currentView}
        prefillRef={chatPrefillRef}
      />
      {/* Phase 2 metric / longevity / insight drawer — overlays the current
          page (always Today for card taps). Closing returns to Today with no
          page navigation. */}
      <DrawerHost
        spec={drawer}
        onClose={closeDrawer}
        onNav={navTo}
        onAskAI={handleAskAI}
        onOpenLongevityFull={openLongevityFromDrawer}
        onOpenDrawer={openDrawer}
      />
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(error, info) {
    // Report the crash to /api/client-error (keeps the display fallback below).
    try { reportClientError('ErrorBoundary: ' + String((error && error.message) || error),
      (error && error.stack) || (info && info.componentStack), 'ErrorBoundary'); } catch (e) {}
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:'40px',fontFamily:'monospace',background:'#FFFFFF',color:'#112240',minHeight:'100vh'}}>
          <h2 style={{color:'#ef4444',marginBottom:'16px'}}>App Error (send this to support)</h2>
          <pre style={{whiteSpace:'pre-wrap',color:'#fca5a5',fontSize:'13px'}}>{String(this.state.error)}{'\n\n'}{this.state.error?.stack}</pre>
          <button onClick={()=>window.location.reload()} style={{marginTop:'24px',padding:'8px 16px',background:'#112240',color:'#FFFFFF',border:'none',borderRadius:'8px',cursor:'pointer'}}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const WrappedApp = () => <ErrorBoundary><App /></ErrorBoundary>;
export default WrappedApp;
